import { t as NoOpSearchAdapter } from "./noop-search-adapter-Cwic19bX.js";
import { action, comparer, computed, flow, isFlowCancellationError, makeAutoObservable, makeObservable, observable, observe, override, reaction, runInAction } from "mobx";
import { v4, v5 } from "uuid";
import { generateText } from "@tiptap/core";
import { notesToText, stripAskUserBlocks, stripMarkdown, stripMeetingIntentPrompt, stripMsgChannelTags, stripRoutinePreamble } from "@littlebird/markdown-utils";
import { z } from "zod";
import { DEFAULT_ROUTINE_INTERVAL_COUNT, DEFAULT_ROUTINE_INTERVAL_UNIT, DEFAULT_ROUTINE_SCHEDULE, MIN_ROUTINE_INTERVAL_MINUTES, PeriodicCron, buildMeetingNotesSendContext, fromRoutineDateTimeValue, hashString, isCanonicalRecurrenceRule, opensTurn, parseBackendName, parseRoutineSchedule, routineIntervalMinutes } from "@littlebird/ui-utils";
import { escapeRegExp, isEqual, lowerFirst, mapValues, orderBy, pick } from "lodash";
import { format } from "date-fns/format";
import { isSameWeek } from "date-fns/isSameWeek";
import { isToday } from "date-fns/isToday";
import { isYesterday } from "date-fns/isYesterday";
import { dismissalQuestionOf } from "@littlebird/action-cards-view";
import { EventNames, globalEventBus } from "@littlebird/event-bus";
import { Duration, Effect, Schedule } from "effect";
import { describeError, isSuppressibleNetworkMessage } from "@littlebird/logger/error";
import invariant from "invariant";
import pluralize from "pluralize";
import { CATALOG_VERSION } from "@littlebird/action-cards-view/version";
import { computedFn } from "mobx-utils";
import { createContext, useContext } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/core/sync/models/base/model.ts
var Model = class Model {
	static modelName;
	/** Runtime-only fields excluded from the manifest and `toJSON`. */
	static NON_PERSISTED_FIELDS = /* @__PURE__ */ new Set([
		"store",
		"logger",
		"isSaving",
		"isNew"
	]);
	store;
	logger;
	id = "";
	isSaving = false;
	isNew = false;
	createdAt = /* @__PURE__ */ new Date();
	updatedAt = /* @__PURE__ */ new Date();
	deletedAt = void 0;
	constructor(fields = {}, store, logger) {
		makeObservable(this, {
			logger: false,
			id: observable,
			isSaving: observable,
			createdAt: observable,
			updatedAt: observable,
			deletedAt: observable,
			updateData: action,
			delete: action
		});
		this.isNew = !fields.id;
		this.updateData(fields);
		this.store = store;
		this.logger = logger;
	}
	/** Sorted fields before filtering, including those with `undefined` values. */
	getPersistedFieldNames() {
		return Object.keys(this).filter((key) => !Model.NON_PERSISTED_FIELDS.has(key)).sort();
	}
	updateData(fields) {
		Object.assign(this, fields);
	}
	toJSON() {
		const json = {};
		for (const key in this) {
			if (!Object.prototype.hasOwnProperty.call(this, key)) continue;
			if (Model.NON_PERSISTED_FIELDS.has(key)) continue;
			const value = this[key];
			if (typeof value === "function" || typeof value === "undefined") continue;
			if (value instanceof Date) {
				json[key] = value.toISOString();
				continue;
			}
			if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
				json[key] = value;
				continue;
			}
			if (Array.isArray(value)) {
				json[key] = value.map((item) => this.serializeJSONValue(item));
				continue;
			}
			if (typeof value === "object") json[key] = this.serializeJSONObject(value);
		}
		return json;
	}
	serializeJSONValue(value) {
		if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
		if (value instanceof Date) return value.toISOString();
		if (Array.isArray(value)) return value.map((v) => this.serializeJSONValue(v));
		if (typeof value === "object") return this.serializeJSONObject(value);
		return null;
	}
	serializeJSONObject(obj) {
		const output = {};
		for (const key in obj) {
			if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
			const value = obj[key];
			const serialized = this.serializeJSONValue(value);
			if (typeof serialized !== "undefined") output[key] = serialized;
		}
		return output;
	}
	async delete() {
		try {
			this.isSaving = true;
			await this.store.delete(this);
		} catch (error) {
			this.logger.error(`Failed to delete ${this.constructor.name}`, {
				context: "Model:delete",
				metadata: {
					message: `Failed to delete ${this.constructor.name}`,
					error
				}
			});
			throw error;
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	}
};
//#endregion
//#region src/core/sync/models/arena.ts
var ArenaAgentResponse = class {
	agentName = "";
	messageId = "";
	threadId = "";
	constructor(fields = {}) {
		Object.assign(this, fields);
		makeObservable(this, {
			agentName: observable,
			messageId: observable,
			threadId: observable
		});
	}
};
var Exchange = class {
	id = v4();
	userMessageIds = {};
	agentResponses = {};
	constructor(fields = {}) {
		Object.assign(this, fields);
		makeObservable(this, {
			id: observable,
			userMessageIds: observable,
			agentResponses: observable
		});
	}
};
var Arena = class extends Model {
	static modelName = "Arena";
	agents = [];
	threadIds = {};
	exchanges = [];
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			agents: observable,
			exchanges: observable,
			threadIds: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/calendar-event.ts
var CalendarEvent = class extends Model {
	static modelName = "CalendarEvent";
	status = "confirmed";
	summary = "";
	description = null;
	startDatetime = "";
	endDatetime = "";
	htmlLink = null;
	attendees = [];
	hangoutLink = null;
	conferenceData = null;
	eventType = null;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			status: observable,
			summary: observable,
			description: observable,
			startDatetime: observable,
			endDatetime: observable,
			htmlLink: observable,
			attendees: observable,
			hangoutLink: observable,
			conferenceData: observable,
			eventType: observable
		});
	}
};
//#endregion
//#region src/core/utils/constants.ts
const STOP_CHAT_WITH_CONTEXT_PAYLOAD = "text:<system>Note to assistant: user requested early stop of your above message before you fully generated it via button in UI. This is completely normal feature, nothing wrong</system>";
const SYNC_INTERVAL_MS = 3e4;
const BOOTSTRAP_TIMEOUT_MS = 18e4;
const BOOTSTRAP_JOIN_GRACE_MS = 3e4;
const BOOTSTRAP_BODY_IDLE_TIMEOUT_MS = 18e4;
const SYNC_RETRY_INITIAL_DELAY_MS = 1e3;
const SYNC_RETRY_MAX_DELAY_MS = 3e4;
const MAX_TOTAL_ATTACHMENT_BYTES = 31457280;
const MAX_VIDEO_FILE_BYTES = 104857600;
const MAX_SPEECH_TO_TEXT_AUDIO_BYTES = 20971520;
const MAX_ATTACHMENTS_PER_MESSAGE = 10;
const WEBSOCKET_CONFIG = {
	PING_INTERVAL_MS: 3e4,
	PING_TIMEOUT_MS: 3e4,
	CONNECTION_STALE_THRESHOLD_MS: 6e4,
	HANDSHAKE_TIMEOUT_MS: 12e4,
	CLOSE_TIMEOUT_MS: 1e4,
	DEFAULT_INITIAL_BACKOFF_MS: 100,
	MAX_BACKOFF_MS: 16e3,
	PERSISTENT_FAILURE_RETRIES: 5,
	PERSISTENT_FAILURE_ELAPSED_MS: 6e4,
	MAX_AUTH_REJECTIONS: 3
};
function nextBackoff(retries) {
	const base = WEBSOCKET_CONFIG.DEFAULT_INITIAL_BACKOFF_MS * Math.pow(2, retries);
	const actual = Math.min(base, WEBSOCKET_CONFIG.MAX_BACKOFF_MS);
	return actual + actual * (Math.random() - .5);
}
//#endregion
//#region src/core/utils/draft.ts
const VIDEO_FILE_EXTENSIONS = /* @__PURE__ */ new Set([
	"mp4",
	"m4v",
	"mov",
	"webm",
	"mpeg",
	"mpg",
	"3gp",
	"avi",
	"wmv",
	"flv"
]);
function isVideoFileName(fileName) {
	const dot = fileName.lastIndexOf(".");
	return dot !== -1 && VIDEO_FILE_EXTENSIONS.has(fileName.slice(dot + 1).toLowerCase());
}
function isVideoUpload(fileName, mimeType) {
	const normalizedMimeType = mimeType.split(";", 1)[0]?.trim().toLowerCase();
	return isVideoFileName(fileName) && (normalizedMimeType?.startsWith("video/") === true || normalizedMimeType === "" || normalizedMimeType === "application/octet-stream" || normalizedMimeType === "binary/octet-stream");
}
const DRAFT_SENDABILITY_MESSAGES = {
	empty: "Cannot send empty message",
	files_uploading: "Some files are still uploading",
	files_failed: "Some files failed to upload"
};
/** The bytes a file adds toward MAX_TOTAL_ATTACHMENT_BYTES; a video is capped on its own instead. */
function attachmentTotalBytes(file) {
	const mimeType = "mimeType" in file ? file.mimeType : file.type;
	return isVideoUpload(file.name, mimeType) ? 0 : file.size;
}
var DraftAttachmentLimitError = class extends Error {
	code;
	constructor(code, message) {
		super(message);
		this.name = "DraftAttachmentLimitError";
		this.code = code;
	}
};
function getDraftSendabilityError(draft, options = {}) {
	if (draft.files?.some((f) => f.uploadState === "pending" || f.uploadState === "uploading")) return "files_uploading";
	const hasContent = !!draft.content?.trim();
	const hasSelectedText = (draft.selectedTexts?.length ?? 0) > 0;
	const hasUploadedFiles = draft.files?.some((f) => f.uploadState === "uploaded") ?? false;
	if (hasContent || hasSelectedText || hasUploadedFiles || options.hasAskUserAnswers) return null;
	if (draft.files?.some((f) => f.uploadState === "failed")) return "files_failed";
	return "empty";
}
function getDraftSendabilityMessage(error) {
	if (!error) return null;
	return DRAFT_SENDABILITY_MESSAGES[error];
}
const isDraftSendable = (draft) => getDraftSendabilityError(draft) === null;
const isDraftEmpty = (draft) => getDraftSendabilityError(draft) === "empty";
/**
* Choose the content source for one send: caller-supplied text (a voice
* transcript) or the composer draft. Text sends tolerate a missing draft;
* draft sends require one.
*/
function resolveOutgoingSendInput(content, getDraft, hasAskUserAnswers = false) {
	if (content !== void 0) {
		const trimmedContent = content.trim();
		if (!trimmedContent && !hasAskUserAnswers) throw new Error("Cannot send empty message");
		return { content: trimmedContent };
	}
	const draft = getDraft();
	if (!draft) throw new Error("Draft not found");
	const sendabilityError = getDraftSendabilityError(draft, { hasAskUserAnswers });
	if (sendabilityError) throw new Error(DRAFT_SENDABILITY_MESSAGES[sendabilityError]);
	return {
		content: composeDraftMessageContent(draft.content, draft.selectedTexts),
		draft
	};
}
function composeDraftMessageContent(content, selectedTexts) {
	const quotes = selectedTexts.map((s) => s.text.trim().replace(/\n/g, " ")).filter(Boolean);
	if (quotes.length === 0) return content;
	const hasBody = content.trim().length > 0;
	const prefix = hasBody ? "Context" : "Tell me more about";
	const quoteLines = quotes.map((q) => `${prefix}: "${q}"`).join("\n");
	return hasBody ? `${quoteLines}\n\n${content}` : quoteLines;
}
//#endregion
//#region src/core/sync/models/draft.ts
var Draft = class extends Model {
	static modelName = "Draft";
	draftId = "";
	content = "";
	draftDocument = void 0;
	files = [];
	selectedTexts = [];
	projectId = void 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			draftId: observable,
			content: observable,
			draftDocument: observable.ref,
			files: observable,
			selectedTexts: observable,
			projectId: observable,
			canAttachFile: computed,
			attachmentsSize: computed
		});
	}
	/** The bytes counted toward MAX_TOTAL_ATTACHMENT_BYTES, which leave videos out. */
	get attachmentsSize() {
		return this.files.reduce((totalSize, file) => totalSize + attachmentTotalBytes({
			name: file.fileName,
			size: file.size,
			mimeType: file.contentType
		}), 0);
	}
	get canAttachFile() {
		if (!this.files) return true;
		return this.files.length < 10;
	}
};
//#endregion
//#region src/core/sync/models/file.ts
var File$1 = class extends Model {
	static modelName = "File";
	filename = "";
	contentType = "";
	size = 0;
	width = void 0;
	height = void 0;
	syncId = 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			filename: observable,
			contentType: observable,
			size: observable,
			width: observable,
			height: observable,
			syncId: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/journal.ts
var Journal = class extends Model {
	static modelName = "Journal";
	text = "";
	description = "";
	document = void 0;
	sections = void 0;
	date = /* @__PURE__ */ new Date();
	viewedAt = void 0;
	isEdited = false;
	restoreCount = 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			text: observable,
			sections: observable,
			date: observable,
			viewedAt: observable,
			description: observable,
			document: observable,
			isEdited: observable,
			restoreCount: observable
		});
	}
	toMarkdown() {
		if (this.document) try {
			return generateText(this.document, []);
		} catch {
			return this.text;
		}
		return this.text;
	}
};
//#endregion
//#region src/core/sync/types/common.ts
const brand = (schema) => schema;
const ThreadIdSchema = brand(z.string());
const MessageIdSchema = brand(z.string());
const MeetingIdSchema = brand(z.string());
const ProjectIdSchema = brand(z.string());
const AgentIdSchema = brand(z.string());
const FileIdSchema = brand(z.string());
const JournalIdSchema = brand(z.string());
const CalendarEventIdSchema = brand(z.string());
const MeetingPrepIdSchema = brand(z.string());
const McpClientGrantIdSchema = brand(z.string());
const ActionCardIdSchema = brand(z.string());
const EntityIdSchema = brand(z.string());
const UserIdSchema = brand(z.number());
const RoutineIdSchema = brand(z.number());
const ReportIdSchema = brand(z.number());
const ReportTemplateIdSchema = brand(z.number());
const TodoReferenceIdSchema = brand(z.number());
const SyncIdSchema = brand(z.number());
//#endregion
//#region src/core/sync/types/chat.ts
const MAIN_CHAT_MODE = "default";
const FAST_CHAT_MODE = "fast";
const HUMMINGBIRD_CHAT_MODE = "fast";
const RESEARCH_CHAT_MODE = "research";
const DASH_CHAT_MODE = "dash";
const ULTRA_CHAT_MODE = "ultra";
const GOD_CHAT_MODE = "god";
const ARGON_CHAT_MODE = "argon";
let ChatStatus = /* @__PURE__ */ function(ChatStatus) {
	ChatStatus["NotSubmitted"] = "not_submitted";
	ChatStatus["Thinking"] = "thinking";
	ChatStatus["Streaming"] = "streaming";
	ChatStatus["Done"] = "done";
	ChatStatus["Errored"] = "errored";
	ChatStatus["Stopped"] = "stopped";
	ChatStatus["WaitingForClientTools"] = "waiting_for_client_tools";
	return ChatStatus;
}({});
const isChatStatusBusy = (status) => status === "streaming" || status === "thinking" || status === "waiting_for_client_tools";
//#endregion
//#region src/core/sync/types/ask-user.ts
const ASK_USER_FREE_TEXT_PLACEHOLDER = "Something else";
/**
* The answer after the user picks an option. A pick toggles the option, and
* when the question allows one answer, the pick replaces the user's own words.
*/
const withAskUserPick = (answer, option, allowMultiple) => {
	const picked = answer.selected.includes(option);
	if (allowMultiple) return {
		...answer,
		selected: picked ? answer.selected.filter((value) => value !== option) : [...answer.selected, option]
	};
	const selected = picked ? [] : [option];
	return {
		selected,
		freeText: selected.length > 0 ? "" : answer.freeText
	};
};
/**
* The answer after the user types. When the question allows one answer, the
* user's own words replace the pick.
*/
const withAskUserFreeText = (answer, freeText, allowMultiple) => ({
	freeText,
	selected: allowMultiple || !freeText ? answer.selected : []
});
//#endregion
//#region src/core/sync/types/modality.ts
let ModalityType = /* @__PURE__ */ function(ModalityType) {
	ModalityType["GetScreenContext"] = "get_screen_context";
	ModalityType["ChatWithWebSearch"] = "chat_with_web_search";
	ModalityType["ChatWithWebSearchMulti"] = "chat_with_web_search_multi";
	ModalityType["ChatWithWebNavigation"] = "chat_with_web_navigation";
	ModalityType["ChatWithUserContext"] = "chat_with_user_context";
	ModalityType["ChatWithPreviousContext"] = "chat_with_previous_context";
	ModalityType["ChatWithNotes"] = "chat_with_memory";
	ModalityType["EditAssistantNotes"] = "edit_assistant_notes";
	ModalityType["Memory"] = "memory";
	ModalityType["Todo"] = "chat_with_todo";
	ModalityType["SelfKnowledge"] = "self_knowledge";
	ModalityType["ImageGeneration"] = "generate_image";
	ModalityType["WorldModel"] = "world_model";
	ModalityType["TodoWrite"] = "todo_write";
	ModalityType["Route"] = "route";
	ModalityType["ReadFileGemini"] = "read_file_gemini";
	ModalityType["ReadFileCodex"] = "read_file_codex";
	ModalityType["ReadFileClaude"] = "read_file_claude";
	ModalityType["EditFile"] = "edit_file";
	ModalityType["EditFileClaude"] = "edit_file_claude";
	ModalityType["ApplyPatch"] = "apply_patch";
	ModalityType["ListDirectory"] = "list_directory";
	ModalityType["ListDir"] = "list_dir";
	ModalityType["SearchFileContent"] = "search_file_content";
	ModalityType["GrepFiles"] = "grep_files";
	ModalityType["RunShellCommand"] = "run_shell_command";
	ModalityType["ShellCommand"] = "shell_command";
	ModalityType["McpToolCall"] = "mcp_tool_call";
	ModalityType["ReadFile"] = "read_file";
	ModalityType["WriteFile"] = "write_file";
	ModalityType["RunShell"] = "run_shell";
	ModalityType["Bash"] = "bash";
	ModalityType["RunSandbox"] = "run_sandbox";
	ModalityType["PresentFile"] = "present_file";
	ModalityType["ToolSearch"] = "tool_search";
	ModalityType["ToolExecute"] = "tool_execute";
	ModalityType["HivemindIncoming"] = "hivemind_incoming";
	ModalityType["TeamChatPost"] = "team_chat_post";
	ModalityType["RunSubagent"] = "run_subagent";
	/**
	* @deprecated Integration sub-agent flow was replaced by ToolSearch + ToolExecute.
	* TODO(integration-subagent-cleanup): remove once historical chats / locally persisted sync
	* messages holding these values have rotated out. Kept read-only so old reasoning panels
	* decode into a known enum value instead of `undefined` (which would crash consumers that
	* `assert_never` on ModalityType).
	*/
	ModalityType["ChatWithIntegrations"] = "chat_with_integrations";
	/** @deprecated see ChatWithIntegrations. */
	ModalityType["InternalIntegrations"] = "internal_integrations";
	return ModalityType;
}({});
/**
* @deprecated Legacy sub-agent action enum. Only retained so old persisted `option` values
* decode to something meaningful. No writers should reference this.
*/
let IntegrationActionType = /* @__PURE__ */ function(IntegrationActionType) {
	IntegrationActionType["ExecuteIntegrationTool"] = "execute_integration_tool";
	return IntegrationActionType;
}({});
//#endregion
//#region src/core/sync/types/todo.ts
let TodoStatus = /* @__PURE__ */ function(TodoStatus) {
	TodoStatus["suggestion"] = "suggestion";
	TodoStatus["accepted"] = "accepted";
	TodoStatus["rejected"] = "rejected";
	TodoStatus["completed"] = "completed";
	TodoStatus["deleted"] = "deleted";
	TodoStatus["stale_suggestion"] = "stale_suggestion";
	return TodoStatus;
}({});
let TodoAction = /* @__PURE__ */ function(TodoAction) {
	TodoAction["GetTodos"] = "get_todos";
	TodoAction["CreateTodo"] = "create_todo";
	TodoAction["UpdateTodo"] = "update_todo";
	TodoAction["DeleteTodo"] = "delete_todo";
	return TodoAction;
}({});
//#endregion
//#region src/core/sync/types/file.ts
var DraftFile = class {
	id;
	/**
	* Stable client-side render identity. Unlike `id` (the server file id),
	* it survives replaceFileAndUpload, so UI keyed on it does not remount.
	*/
	localKey;
	fileName;
	contentType;
	size;
	file;
	uploadState;
	progress;
	error;
	height;
	width;
	constructor(init = {}) {
		this.id = init.id ?? v4();
		this.localKey = this.id;
		this.fileName = init.fileName;
		this.contentType = init.contentType;
		this.size = init.size;
		this.file = init.file;
		this.uploadState = init.uploadState ?? "pending";
		this.progress = init.progress ?? 0;
		this.error = init.error;
		this.height = init.height;
		this.width = init.width;
		makeAutoObservable(this);
	}
	/** Snapshot of the mutable fields, for rolling back an in-place swap. */
	captureState() {
		return {
			id: this.id,
			file: this.file,
			fileName: this.fileName,
			contentType: this.contentType,
			size: this.size,
			height: this.height,
			width: this.width,
			uploadState: this.uploadState,
			progress: this.progress,
			error: this.error
		};
	}
	restoreState(state) {
		Object.assign(this, state);
	}
};
//#endregion
//#region src/core/sync/types/api-models.ts
let ReportStatus = /* @__PURE__ */ function(ReportStatus) {
	ReportStatus["GENERATING"] = "GENERATING";
	ReportStatus["NO_DATA"] = "NO DATA";
	return ReportStatus;
}({});
//#endregion
//#region src/core/sync/types/bootstrap.ts
const EMPTY_SYNC_PERSIST_RESULT = {
	deletedSearchDocIds: [],
	upsertedRecords: []
};
const REQUIRED_ENTITY_TYPES = [
	"threads",
	"messages",
	"files",
	"journals",
	"meetings",
	"mcp_client_grants"
];
//#endregion
//#region src/core/sync/types/meeting-share.ts
const MeetingShareVisibilitySchema = z.enum([
	"private",
	"team",
	"public",
	"people"
]);
const MeetingShareResponseSchema = z.object({
	meeting_id: MeetingIdSchema,
	public_token: z.string().nullable(),
	visibility: MeetingShareVisibilitySchema,
	shared_team_id: z.string().nullable().optional()
});
const MeetingShareStatusSchema = z.object({
	is_shared: z.boolean(),
	public_token: z.string().nullable(),
	visibility: MeetingShareVisibilitySchema,
	shared_team_id: z.string().nullable().optional(),
	invited_emails: z.array(z.string()).default([])
});
const MeetingShareInvitesSchema = z.object({ invited_emails: z.array(z.string()) });
//#endregion
//#region src/core/sync/types/routine.ts
const ROUTINE_TRIGGER_GMAIL = "gmail_message";
/** Fires on mail arriving in the inbox of one connected Outlook account. */
const ROUTINE_TRIGGER_OUTLOOK = "outlook_message";
const ROUTINE_TRIGGER_WEBHOOK = "generic_webhook";
/** Fires once, when the first words of a meeting are transcribed. */
const ROUTINE_TRIGGER_MEETING_STARTED = "meeting_started";
/** Fires each time a meeting's summary is ready. */
const ROUTINE_TRIGGER_MEETING_SUMMARIZED = "meeting_summarized";
const MEETING_TRIGGER_KINDS = [ROUTINE_TRIGGER_MEETING_STARTED, ROUTINE_TRIGGER_MEETING_SUMMARIZED];
const ROUTINE_TRIGGER_KINDS = [
	ROUTINE_TRIGGER_GMAIL,
	ROUTINE_TRIGGER_OUTLOOK,
	ROUTINE_TRIGGER_WEBHOOK,
	...MEETING_TRIGGER_KINDS
];
const WEBHOOK_CONDITION_OPERATORS = [
	"equals",
	"contains",
	"exists",
	"regex"
];
/** Gmail labels a trigger can watch: `INBOX` for received mail, `SENT` for mail the user sends. */
const GMAIL_TRIGGER_LABELS = ["INBOX", "SENT"];
/** Graph's folders an Outlook trigger can watch: `inbox` for received mail, `sentitems` for mail the user sends. */
const OUTLOOK_TRIGGER_FOLDERS = ["inbox", "sentitems"];
//#endregion
//#region src/core/sync/models/project-icon.ts
const ICON_COLOR_KEYS = [
	"gray",
	"violet",
	"blue",
	"green",
	"yellow",
	"amber",
	"red",
	"crimson"
];
const ICON_CODE_PATTERN = /^[a-z0-9-]+$/;
const ICON_CODE_MAX_LENGTH = 64;
function isIconColorKey(value) {
	return ICON_COLOR_KEYS.includes(value);
}
function parseIconConfig(value) {
	if (!value || typeof value !== "object") return null;
	const { type, code, color } = value;
	if (type !== "icon" && type !== "emoji") return null;
	if (code !== void 0 && code !== null && typeof code !== "string") return null;
	if (typeof code === "string" && code.length > 64) return null;
	if (color !== void 0 && color !== null && !isIconColorKey(color)) return null;
	const parsedColor = isIconColorKey(color) ? color : null;
	if (type === "emoji") return typeof code === "string" && code.length > 0 ? {
		type: "emoji",
		code,
		color: parsedColor
	} : null;
	if (typeof code === "string" && !ICON_CODE_PATTERN.test(code)) return null;
	return {
		type: "icon",
		code: code ?? null,
		color: parsedColor
	};
}
function isSameIconConfig(a, b) {
	return a === b || a !== null && b !== null && a.type === b.type && (a.code ?? null) === (b.code ?? null) && (a.color ?? null) === (b.color ?? null);
}
//#endregion
//#region src/core/sync/models/project-icon-catalog.ts
const ENTITY_ICON_CATALOG = [
	{
		slug: "folder",
		keywords: "directory"
	},
	{
		slug: "folder-open",
		keywords: "directory"
	},
	{
		slug: "file",
		keywords: "document"
	},
	{
		slug: "file-text",
		keywords: "document notes data txt pdf"
	},
	{
		slug: "notepad",
		keywords: "notes notebook pages paper stationery sketchbook organizer organiser"
	},
	{
		slug: "sticky-note",
		keywords: "memo comment annotation reaction reminder todo task idea"
	},
	{
		slug: "book",
		keywords: "reading library pages booklet magazine leaflet pamphlet writing"
	},
	{
		slug: "archive",
		keywords: "box storage index backup records"
	},
	{
		slug: "inbox",
		keywords: "mail tray email"
	},
	{
		slug: "tag",
		keywords: "label badge ticket mark"
	},
	{
		slug: "pin",
		keywords: "save map lock fix"
	},
	{
		slug: "link",
		keywords: "url chain"
	},
	{
		slug: "lock",
		keywords: "private secure security password admin"
	},
	{
		slug: "layout-grid",
		keywords: "dashboard app home start"
	},
	{
		slug: "briefcase",
		keywords: "work job business bag baggage folder"
	},
	{
		slug: "building",
		keywords: "office company business enterprise skyscraper organisation organization city"
	},
	{
		slug: "presentation",
		keywords: "slides deck screen whiteboard markers blackboard chalk easel"
	},
	{
		slug: "chart-bar",
		keywords: "analytics stats statistics diagram graph"
	},
	{
		slug: "chart-line",
		keywords: "analytics trend statistics diagram graph"
	},
	{
		slug: "chart-pie",
		keywords: "analytics share statistics diagram presentation"
	},
	{
		slug: "target",
		keywords: "goal aim objective logo bullseye deadline projects overview"
	},
	{
		slug: "trophy",
		keywords: "win award prize sports winner achievement champion celebration"
	},
	{
		slug: "award",
		keywords: "badge medal achievement rosette prize winner"
	},
	{
		slug: "flag",
		keywords: "milestone country report marker notification warning goal notice"
	},
	{
		slug: "rocket",
		keywords: "launch startup ship release boost space version"
	},
	{
		slug: "crown",
		keywords: "king premium diadem tiara circlet corona ruler winner"
	},
	{
		slug: "gem",
		keywords: "diamond premium crystal ruby jewellery price special present"
	},
	{
		slug: "handshake",
		keywords: "deal partner agreement partnership business assistance cooperation friendship"
	},
	{
		slug: "megaphone",
		keywords: "marketing announce advertisement announcement attention alert loudspeaker notification"
	},
	{
		slug: "wallet",
		keywords: "money finance pocket"
	},
	{
		slug: "graduation-cap",
		keywords: "school study learning university learn mortarboard education ceremony"
	},
	{
		slug: "code",
		keywords: "developer programming source html xml"
	},
	{
		slug: "terminal",
		keywords: "console shell code prompt"
	},
	{
		slug: "bug",
		keywords: "issue defect error testing troubleshoot problem report debug"
	},
	{
		slug: "database",
		keywords: "storage sql memory container tin pot bytes servers"
	},
	{
		slug: "server",
		keywords: "backend hosting cloud storage"
	},
	{
		slug: "cpu",
		keywords: "chip hardware processor cores technology computer circuit memory"
	},
	{
		slug: "laptop",
		keywords: "computer screen remote"
	},
	{
		slug: "monitor",
		keywords: "screen display tv vm"
	},
	{
		slug: "smartphone",
		keywords: "mobile cellphone device screen"
	},
	{
		slug: "cloud",
		keywords: "hosting storage weather"
	},
	{
		slug: "wifi",
		keywords: "network internet connection signal wireless"
	},
	{
		slug: "bot",
		keywords: "robot ai agent chat assistant"
	},
	{
		slug: "brain",
		keywords: "mind thinking medical mental intellect cerebral consciousness genius"
	},
	{
		slug: "zap",
		keywords: "energy power speed flash camera lightning electricity quick"
	},
	{
		slug: "settings",
		keywords: "config preferences cog edit gear slider"
	},
	{
		slug: "wrench",
		keywords: "fix repair tools account settings spanner diy toolbox"
	},
	{
		slug: "hammer",
		keywords: "build construction mallet nails diy toolbox"
	},
	{
		slug: "shield",
		keywords: "security protection cybersecurity secured safety protected guardian guarded"
	},
	{
		slug: "scissors",
		keywords: "cut trim snip chop stationery crafts"
	},
	{
		slug: "palette",
		keywords: "design color art colors colours theme scheme paint"
	},
	{
		slug: "key",
		keywords: "access password login authentication secure unlock"
	},
	{
		slug: "users",
		keywords: "team group people"
	},
	{
		slug: "user",
		keywords: "person profile account contact"
	},
	{
		slug: "chat",
		keywords: "messages conversation comment dialog feedback copy multiple discussion"
	},
	{
		slug: "speech",
		keywords: "talk comment chat conversation dialog feedback"
	},
	{
		slug: "mail",
		keywords: "email inbox message letter unread"
	},
	{
		slug: "send",
		keywords: "share deliver email message mail submit"
	},
	{
		slug: "phone",
		keywords: "call"
	},
	{
		slug: "video",
		keywords: "camera meeting movie film recording camcorder reel"
	},
	{
		slug: "mic",
		keywords: "audio record voice sound listen radio podcast microphone"
	},
	{
		slug: "headphones",
		keywords: "audio listen music sound"
	},
	{
		slug: "home",
		keywords: "house personal living building residence architecture"
	},
	{
		slug: "heart",
		keywords: "love favorite health like emotion suit playing cards"
	},
	{
		slug: "star",
		keywords: "favorite bookmark like review rating"
	},
	{
		slug: "smile",
		keywords: "happy fun mood face"
	},
	{
		slug: "coffee",
		keywords: "cafe break drink cup mug tea hot beverage"
	},
	{
		slug: "utensils",
		keywords: "food dining fork knife cutlery flatware tableware silverware"
	},
	{
		slug: "pizza",
		keywords: "food pie quiche"
	},
	{
		slug: "cake",
		keywords: "birthday celebration birthdate party surprise gateaux dessert fondant"
	},
	{
		slug: "gift",
		keywords: "present birthday box party"
	},
	{
		slug: "shopping-bag",
		keywords: "retail ecommerce cart purchase store"
	},
	{
		slug: "shopping-cart",
		keywords: "groceries trolley basket store purchase products items ingredients"
	},
	{
		slug: "shirt",
		keywords: "clothes fashion shopping store clothing"
	},
	{
		slug: "dumbbell",
		keywords: "gym fitness workout barbell weight"
	},
	{
		slug: "bike",
		keywords: "cycling exercise bicycle transport trip"
	},
	{
		slug: "music",
		keywords: "song audio note quaver"
	},
	{
		slug: "gamepad",
		keywords: "games gaming console"
	},
	{
		slug: "camera",
		keywords: "photo picture photography lens focus capture shot visual"
	},
	{
		slug: "film",
		keywords: "movie video cinema reel camera entertainment"
	},
	{
		slug: "ticket",
		keywords: "event booking entry pass voucher concert show perforated"
	},
	{
		slug: "leaf",
		keywords: "plant nature eco sustainability energy autumn"
	},
	{
		slug: "flower",
		keywords: "garden bloom sustainability nature plant"
	},
	{
		slug: "sprout",
		keywords: "growth seedling eco green leaf nature plant seed"
	},
	{
		slug: "tree",
		keywords: "forest nature pine park"
	},
	{
		slug: "sun",
		keywords: "weather day summer brightness light"
	},
	{
		slug: "moon",
		keywords: "night sleep dark"
	},
	{
		slug: "snowflake",
		keywords: "winter cold weather freeze"
	},
	{
		slug: "flame",
		keywords: "fire hot streak heat burn light glow ignite"
	},
	{
		slug: "droplet",
		keywords: "water rain weather liquid fluid wet moisture damp"
	},
	{
		slug: "waves",
		keywords: "ocean sea beach water"
	},
	{
		slug: "mountain",
		keywords: "hiking peak climb hike rock"
	},
	{
		slug: "dog",
		keywords: "pet animal puppy hound canine"
	},
	{
		slug: "compass",
		keywords: "navigate explore direction north east south west browser"
	},
	{
		slug: "map",
		keywords: "navigation location travel"
	},
	{
		slug: "map-pin",
		keywords: "location place waypoint marker drop"
	},
	{
		slug: "globe",
		keywords: "world international web browser language translate"
	},
	{
		slug: "plane",
		keywords: "flight travel trip airplane"
	},
	{
		slug: "car",
		keywords: "drive vehicle trip journey"
	},
	{
		slug: "truck",
		keywords: "delivery shipping van haulage lorry"
	},
	{
		slug: "ship",
		keywords: "boat sea cruise knots maritime sailing yacht tanker"
	},
	{
		slug: "stethoscope",
		keywords: "health medical doctor phonendoscope heart lungs sound"
	},
	{
		slug: "microscope",
		keywords: "research lab medical education science imaging"
	},
	{
		slug: "flask",
		keywords: "lab experiment chemistry beaker erlenmeyer test"
	},
	{
		slug: "atom",
		keywords: "physics science atomic nuclear particle element molecule electricity"
	},
	{
		slug: "telescope",
		keywords: "discover space astronomy discovery exploration explore vision perspective"
	},
	{
		slug: "lightbulb",
		keywords: "idea inspiration bright lights"
	},
	{
		slug: "clock",
		keywords: "time schedule watch alarm"
	},
	{
		slug: "calendar",
		keywords: "date schedule month year event birthday birthdate"
	},
	{
		slug: "calendar-days",
		keywords: "month date year event"
	},
	{
		slug: "alarm-clock",
		keywords: "reminder wake morning"
	},
	{
		slug: "bell",
		keywords: "notification reminder alarm sound"
	},
	{
		slug: "history",
		keywords: "past recent recently"
	},
	{
		slug: "sparkles",
		keywords: "stars effect filter night magic"
	},
	{
		slug: "pencil",
		keywords: "rubber edit create draw sketch draft writer writing"
	},
	{
		slug: "play",
		keywords: "music audio video start run"
	},
	{
		slug: "image",
		keywords: "picture photo"
	},
	{
		slug: "hand-coins",
		keywords: "savings banking money finance offers mortgage payment received"
	},
	{
		slug: "chart-no-axes-combined",
		keywords: "statistics analytics diagram graph"
	},
	{
		slug: "landmark",
		keywords: "bank building capitol finance money museum hall institute"
	},
	{
		slug: "briefcase-business",
		keywords: "work bag baggage folder portfolio"
	},
	{
		slug: "package",
		keywords: "box container storage sealed delivery undelivered unopened packed"
	},
	{
		slug: "banknote",
		keywords: "currency money payment"
	},
	{
		slug: "scale",
		keywords: "balance legal license right rule law justice weight"
	},
	{
		slug: "gauge",
		keywords: "dashboard dial meter speed pressure measure level"
	},
	{
		slug: "list-checks",
		keywords: "todo done tick complete tasks items pending"
	},
	{
		slug: "shopping-basket",
		keywords: "cart store purchase products items ingredients"
	},
	{
		slug: "wand-sparkles",
		keywords: "magic wizard magician"
	},
	{
		slug: "party-popper",
		keywords: "emoji congratulations celebration tada excitement exciting excites confetti"
	},
	{
		slug: "hat-glasses",
		keywords: "incognito disguise costume masked anonymous anonymity privacy stealth"
	},
	{
		slug: "pen-line",
		keywords: "pencil change create draw writer writing biro ink"
	},
	{
		slug: "clock-fading",
		keywords: "time watch alarm"
	},
	{
		slug: "piggy-bank",
		keywords: "money savings"
	},
	{
		slug: "swords",
		keywords: "battle challenge game war weapon"
	},
	{
		slug: "paw-print",
		keywords: "pets vets veterinarian domesticated cat dog bear"
	},
	{
		slug: "puzzle",
		keywords: "component module part piece"
	},
	{
		slug: "chef-hat",
		keywords: "cooking food kitchen restaurant"
	},
	{
		slug: "biceps-flexed",
		keywords: "arm muscle strong athletic toned muscular forelimb curled"
	},
	{
		slug: "life-buoy",
		keywords: "preserver lifesaver help rescue ship ring raft inflatable"
	},
	{
		slug: "tree-palm",
		keywords: "vacation leisure island"
	},
	{
		slug: "cookie",
		keywords: "biscuit privacy legal food"
	},
	{
		slug: "trees",
		keywords: "forest park nature"
	},
	{
		slug: "ghost",
		keywords: "spooky"
	},
	{
		slug: "fish",
		keywords: "dish restaurant course meal seafood pet sea marine"
	},
	{
		slug: "tent-tree",
		keywords: "camping campsite holiday retreat nomadic wilderness outdoors"
	},
	{
		slug: "chess-knight",
		keywords: "piece horse"
	},
	{
		slug: "git-pull-request",
		keywords: "code open"
	},
	{
		slug: "guitar",
		keywords: "acoustic instrument strings riff rock band country concert"
	},
	{
		slug: "piano",
		keywords: "music audio sound noise notes chord keys octave"
	},
	{
		slug: "feather",
		keywords: "quill plume writing author literature poetry manuscript bird"
	},
	{
		slug: "bird",
		keywords: "peace freedom wing avian tweet"
	},
	{
		slug: "birdhouse",
		keywords: "garden home woodwork"
	},
	{
		slug: "pencil-sparkles",
		keywords: "edit ai tools smart create draw sketch draft"
	},
	{
		slug: "face-slightly-smiling",
		keywords: "smile happy happiness good positive friendly patronizing ironic"
	},
	{
		slug: "mic-audio-lines",
		keywords: "podcast waveform microphone talk voice speech stream recording"
	}
];
const ENTITY_ICON_WORDS = ENTITY_ICON_CATALOG.map((entry) => ({
	slug: entry.slug,
	words: /* @__PURE__ */ new Set([...entry.slug.split("-"), ...entry.keywords.split(" ")])
}));
const suggestEntityIconSlug = (title) => {
	const titleWords = title.toLowerCase().match(/[a-z0-9]+/g) ?? [];
	return ENTITY_ICON_WORDS.find((entry) => titleWords.some((word) => entry.words.has(word)))?.slug ?? null;
};
const suggestIconForTitle = (title, currentIcon) => {
	const code = suggestEntityIconSlug(title);
	const color = currentIcon?.color ?? null;
	return code || color ? {
		type: "icon",
		code,
		color
	} : null;
};
const ICON_COLOR_LABELS = {
	gray: "Gray",
	violet: "Midnight Blue",
	blue: "Jordy Blue",
	green: "Emerald Green",
	yellow: "Yellow",
	amber: "Amber",
	red: "Red",
	crimson: "Plum"
};
//#endregion
//#region src/core/sync/models/routine-email-trigger-draft.ts
const GMAIL = {
	kind: ROUTINE_TRIGGER_GMAIL,
	sameMail: (a, b) => a.query.trim() === b.query.trim(),
	fields: [
		"query",
		"labelIds",
		"connectedAccountId"
	],
	isPlaceholder: (trigger) => !trigger.query,
	adopt: (saved, placeholder) => ({
		...saved,
		labelIds: isEqual(placeholder.labelIds, ["INBOX", "SENT"]) ? saved.labelIds : placeholder.labelIds
	})
};
const OUTLOOK_FILTERS = [
	"addresses",
	"domains",
	"subjectContains",
	"excludedAddresses",
	"excludedDomains",
	"excludedSubjectContains"
];
const OUTLOOK = {
	kind: ROUTINE_TRIGGER_OUTLOOK,
	sameMail: (a, b) => isEqual(a.folders, b.folders) && OUTLOOK_FILTERS.every((filter) => isEqual(a[filter], b[filter])),
	fields: [
		"connectedAccountId",
		"folders",
		...OUTLOOK_FILTERS
	],
	isPlaceholder: (trigger) => OUTLOOK_FILTERS.every((filter) => trigger[filter].length === 0),
	adopt: (saved, placeholder) => ({
		...saved,
		folders: isEqual(placeholder.folders, OUTLOOK_TRIGGER_FOLDERS) ? saved.folders : placeholder.folders
	})
};
const sameAccount = (a, b) => a.connectedAccountId === b.connectedAccountId;
function matchSavedTriggers(spec, baseline, saved) {
	const matches = /* @__PURE__ */ new Map();
	const claimed = /* @__PURE__ */ new Set();
	const match = (predicate) => {
		for (const trigger of baseline) {
			if (matches.has(trigger)) continue;
			const candidates = saved.filter((candidate) => !claimed.has(candidate) && predicate(trigger, candidate));
			if (candidates.length !== 1) continue;
			if (baseline.filter((candidate) => !matches.has(candidate) && predicate(candidate, candidates[0])).length !== 1) continue;
			matches.set(trigger, candidates[0]);
			claimed.add(candidates[0]);
		}
	};
	match((a, b) => !!a.id && a.id === b.id);
	match((a, b) => sameAccount(a, b) && spec.sameMail(a, b));
	match(spec.sameMail);
	match(sameAccount);
	match((a, b) => sameAccount(a, b) && baseline.indexOf(a) === saved.indexOf(b));
	return matches;
}
function mergeTrigger(spec, local, baseline, saved) {
	const merged = {
		...local,
		id: saved.id
	};
	for (const field of spec.fields) if (isEqual(local[field], baseline[field])) merged[field] = saved[field];
	return merged;
}
function adoptNewTrigger(spec, saved, local, additions) {
	const placeholders = additions.filter((trigger) => local.includes(trigger) && spec.isPlaceholder(trigger) && sameAccount(trigger, saved));
	if (placeholders.length !== 1) {
		local.push(saved);
		return;
	}
	const placeholder = placeholders[0];
	local[local.indexOf(placeholder)] = spec.adopt(saved, placeholder);
}
const isChatSlot = (entry) => "chatSlot" in entry;
const withoutChatMark$1 = (trigger) => {
	const { sentToChat: _sentToChat, ...row } = trigger;
	return row;
};
function rebaseKind(spec, local, baseline, saved, triggersSaved) {
	const ofKind = (triggers) => triggers.filter((trigger) => trigger.kind === spec.kind);
	const previousTriggers = ofKind(baseline);
	const savedTriggers = ofKind(saved);
	const matches = matchSavedTriggers(spec, previousTriggers, savedTriggers);
	const claimed = /* @__PURE__ */ new Set();
	const additions = [];
	const result = local.flatMap((entry) => {
		if (entry.kind !== spec.kind) return [entry];
		const trigger = entry;
		const previous = previousTriggers.find((candidate) => !claimed.has(candidate) && (trigger.id ? trigger.id === candidate.id : !candidate.id && sameAccount(trigger, candidate) && spec.sameMail(trigger, candidate)));
		if (previous) claimed.add(previous);
		const next = previous && matches.get(previous);
		if (trigger.sentToChat && triggersSaved) {
			if (next) return [next];
			return [{
				chatSlot: true,
				row: trigger,
				wasSaved: !!previous
			}];
		}
		if (!previous) {
			additions.push(trigger);
			return [trigger];
		}
		if (next) return [mergeTrigger(spec, trigger, previous, next)];
		return isEqual(trigger, previous) ? [] : [trigger];
	});
	const matchedSaved = new Set(matches.values());
	const newSaved = savedTriggers.filter((trigger) => !matchedSaved.has(trigger));
	const slots = result.filter(isChatSlot);
	if (slots.length === 0) {
		const rows = result;
		for (const trigger of newSaved) adoptNewTrigger(spec, trigger, rows, additions);
		return rows;
	}
	return result.flatMap((entry) => {
		if (!isChatSlot(entry)) return [entry];
		if (newSaved.length > 0) return entry === slots[0] ? newSaved : [];
		return entry.wasSaved ? [] : [withoutChatMark$1(entry.row)];
	});
}
/**
* Carries the email triggers of a kept draft onto a newer save of the routine, such as one the agent made in
* chat. A trigger matches its saved version even though the backend gave it a new id. A field the user did not
* edit takes the saved value, and an edited field keeps the user's value. A row handed to chat is the
* exception: once the triggers were saved, the agent's version replaces it, edits and all.
*/
function rebaseEmailTriggerDrafts(local, baseline, saved) {
	const triggersSaved = !isEqual(baseline, saved);
	return rebaseKind(OUTLOOK, rebaseKind(GMAIL, local, baseline, saved, triggersSaved), baseline, saved, triggersSaved);
}
//#endregion
//#region src/core/sync/models/routine-draft.ts
const IconConfigSchema$1 = z.custom((value) => parseIconConfig(value) !== null, "Select a valid icon");
const DEFAULT_ROUTINE_ICON = {
	type: "icon",
	code: "clock",
	color: null
};
const EVERY_MONTH = Array.from({ length: 12 }, (_, index) => index + 1);
const hasPossibleMonthDay = (months, days) => months.some((month) => days.some((day) => day <= new Date(Date.UTC(2e3, month, 0)).getUTCDate()));
const WebhookConditionDraftSchema = z.object({
	path: z.string(),
	operator: z.enum(WEBHOOK_CONDITION_OPERATORS),
	value: z.string().nullable().default(null),
	negate: z.boolean().default(false)
});
/** A new Gmail trigger watches both mail the user receives and mail the user sends. */
const DEFAULT_GMAIL_TRIGGER_LABELS = ["INBOX", "SENT"];
/**
* Set on an email trigger row the user handed to chat with "Configure with Littlebird". When the routine is
* saved before the form reopens, the trigger the agent saved takes this row's place. Never sent to the backend.
*/
const SentToChatSchema = z.boolean().optional();
const GmailTriggerDraftSchema = z.object({
	id: z.string().optional(),
	sentToChat: SentToChatSchema,
	kind: z.literal(ROUTINE_TRIGGER_GMAIL),
	/** Gmail search query; empty matches every new message in `labelIds`. */
	query: z.string(),
	/** Absent on a library entry, whose trigger becomes a new one for the user. */
	labelIds: z.array(z.enum(GMAIL_TRIGGER_LABELS)).min(1).default(() => [...DEFAULT_GMAIL_TRIGGER_LABELS]),
	/** Mailbox to watch; null means any connected Gmail account. */
	connectedAccountId: z.string().nullable().default(null)
});
/** An empty filter list matches every message in `folders`; an empty `excluded` list leaves none out. */
const OutlookTriggerDraftSchema = z.object({
	id: z.string().optional(),
	sentToChat: SentToChatSchema,
	kind: z.literal(ROUTINE_TRIGGER_OUTLOOK),
	/** Null while the user is still choosing; submitting requires one. */
	connectedAccountId: z.string().nullable().default(null),
	folders: z.array(z.enum(OUTLOOK_TRIGGER_FOLDERS)).min(1).default(() => [...OUTLOOK_TRIGGER_FOLDERS]),
	addresses: z.array(z.string()).default([]),
	domains: z.array(z.string()).default([]),
	subjectContains: z.array(z.string()).default([]),
	excludedAddresses: z.array(z.string()).default([]),
	excludedDomains: z.array(z.string()).default([]),
	excludedSubjectContains: z.array(z.string()).default([])
});
/** An empty filter list matches every meeting. */
const MeetingTriggerDraftSchema = z.object({
	kind: z.enum(MEETING_TRIGGER_KINDS),
	titleContains: z.array(z.string()),
	attendeeEmails: z.array(z.string()),
	attendeeDomains: z.array(z.string())
});
const WebhookTriggerDraftSchema = z.object({
	kind: z.literal(ROUTINE_TRIGGER_WEBHOOK),
	conditions: z.array(WebhookConditionDraftSchema).default([]),
	/** Null while the user is still choosing; submitting requires one. */
	webhookEndpointId: z.string().nullable().default(null)
});
const RoutineTriggerDraftSchema = z.discriminatedUnion("kind", [
	GmailTriggerDraftSchema,
	OutlookTriggerDraftSchema,
	MeetingTriggerDraftSchema,
	WebhookTriggerDraftSchema
]);
/**
* A new Outlook trigger watches mail the user receives and sends, in the
* user's only Outlook account, or in none yet when they have several.
*/
const createOutlookTriggerDraft = (connectedAccountId) => ({
	kind: ROUTINE_TRIGGER_OUTLOOK,
	connectedAccountId,
	folders: [...OUTLOOK_TRIGGER_FOLDERS],
	addresses: [],
	domains: [],
	subjectContains: [],
	excludedAddresses: [],
	excludedDomains: [],
	excludedSubjectContains: []
});
const createMeetingTriggerDraft = (kind) => ({
	kind,
	titleContains: [],
	attendeeEmails: [],
	attendeeDomains: []
});
const RoutineFormDraftSchema = z.object({
	title: z.string(),
	icon: IconConfigSchema$1.nullable().optional().default(() => ({ ...DEFAULT_ROUTINE_ICON })),
	iconSuggestionsEnabled: z.boolean().default(false),
	prompt: z.string(),
	recurrenceType: z.enum([
		"Advanced",
		"Hourly",
		"Interval",
		"Daily",
		"Weekly",
		"Monthly"
	]),
	triggerTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Select a valid time"),
	hourlyEndTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Select a valid end time").default("18:00"),
	advancedMonths: z.array(z.number().int().min(1).max(12)).default(() => [...EVERY_MONTH]),
	advancedDayMode: z.enum(["weekDays", "monthDays"]).default("weekDays"),
	advancedMonthDays: z.array(z.number().int().min(1).max(31)).default(() => [1]),
	advancedTimes: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Select a valid time")).default(() => ["09:00"]),
	intervalCount: z.number().int().min(1).default(DEFAULT_ROUTINE_INTERVAL_COUNT),
	intervalUnit: z.enum([
		"minutes",
		"hours",
		"days"
	]).default(DEFAULT_ROUTINE_INTERVAL_UNIT),
	intervalStartAt: z.string().nullable().default(null),
	intervalEndAt: z.string().nullable().default(null),
	preservedRecurrenceRule: z.string().nullable().default(null),
	weekDays: z.array(z.string()),
	monthDay: z.number().int().min(1).max(31),
	/** Whether the routine runs on its schedule; false leaves only event triggers. */
	scheduleEnabled: z.boolean().default(true),
	/** Event triggers that run the routine alongside (or instead of) the schedule. */
	triggers: z.array(RoutineTriggerDraftSchema).default([]),
	kind: z.enum([
		"digest",
		"watcher",
		"reminder"
	]).optional(),
	notificationsEnabled: z.boolean(),
	emailNotificationsEnabled: z.boolean(),
	deliveryChannel: z.enum([
		"off",
		"imessage",
		"whatsapp_business"
	]),
	autoPauseEnabled: z.boolean(),
	mayReachOut: z.boolean().default(true),
	agentMode: z.enum([
		"lite",
		"regular",
		"max",
		"ultra",
		"god"
	])
});
const addAdvancedIssues = (draft, addIssue) => {
	if (draft.advancedMonths.length === 0) addIssue("Select at least one month", "advancedMonths");
	if (draft.advancedDayMode === "monthDays") {
		if (draft.advancedMonthDays.length === 0) addIssue("Select at least one day", "advancedMonthDays");
		else if (draft.advancedMonths.length > 0 && !hasPossibleMonthDay(draft.advancedMonths, draft.advancedMonthDays)) addIssue("Select a valid day for the selected months", "advancedMonthDays");
	}
	if (draft.advancedTimes.length === 0) addIssue("Add at least one time", "advancedTimes");
	if (new Set(draft.advancedTimes.map((time) => time.split(":")[1])).size > 1) addIssue("Advanced times must use the same minutes", "advancedTimes");
};
const addIntervalIssues = (draft, addIssue) => {
	if (routineIntervalMinutes(draft.intervalCount, draft.intervalUnit) < MIN_ROUTINE_INTERVAL_MINUTES) addIssue(`Routines can run at most once every ${MIN_ROUTINE_INTERVAL_MINUTES} minutes`, "intervalCount");
	const end = fromRoutineDateTimeValue(draft.intervalEndAt);
	if (draft.intervalEndAt !== null && end === null) addIssue("Select a valid end date and time", "intervalEndAt");
	const start = fromRoutineDateTimeValue(draft.intervalStartAt);
	if (end && start && end.getTime() <= start.getTime()) addIssue("End must be after the schedule starts", "intervalEndAt");
};
/** Matches the backend's limit: every delivery checks every condition, inline. */
const MAX_WEBHOOK_CONDITIONS = 10;
/** Operators whose empty value would match every present field, which the backend refuses. */
const OPERATORS_NEEDING_NON_EMPTY_VALUE = /* @__PURE__ */ new Set(["contains", "regex"]);
/** The backend's condition rules, so a save fails here with a message instead of as a 422. */
const addWebhookConditionIssues = (conditions, addIssue) => {
	if (conditions.length > 10) addIssue(`A webhook trigger can have at most 10 conditions`, []);
	conditions.forEach((condition, index) => {
		const label = `Condition ${index + 1}`;
		if (!condition.path.trim()) addIssue(`${label} needs a field path`, [index, "path"]);
		if (condition.operator === "exists") return;
		if (condition.value === null) addIssue(`${label} needs a value`, [index, "value"]);
		else if (!condition.value && OPERATORS_NEEDING_NON_EMPTY_VALUE.has(condition.operator)) addIssue(`${label} needs a value, since an empty one matches every payload`, [index, "value"]);
	});
};
const RoutineFormSubmissionSchema = RoutineFormDraftSchema.extend({
	title: z.string().trim().min(1, "Title is required"),
	prompt: z.string().trim().min(1, "Instructions are required")
}).superRefine((draft, context) => {
	const addIssue = (message, path) => context.addIssue({
		code: z.ZodIssueCode.custom,
		message,
		path: [path]
	});
	if (draft.scheduleEnabled && (draft.recurrenceType === "Weekly" || draft.recurrenceType === "Hourly" || draft.recurrenceType === "Advanced" && draft.preservedRecurrenceRule === null && draft.advancedDayMode === "weekDays") && draft.weekDays.length === 0) addIssue("Select at least one day", "weekDays");
	if (draft.scheduleEnabled && draft.recurrenceType === "Advanced" && draft.preservedRecurrenceRule === null) addAdvancedIssues(draft, addIssue);
	if (draft.scheduleEnabled && draft.recurrenceType === "Hourly" && draft.hourlyEndTime < draft.triggerTime) addIssue("End time cannot be before start time", "hourlyEndTime");
	if (draft.scheduleEnabled && draft.recurrenceType === "Interval" && draft.preservedRecurrenceRule === null) addIntervalIssues(draft, addIssue);
	if (!draft.scheduleEnabled && draft.triggers.length === 0) addIssue("Add a trigger, or turn the schedule back on", "triggers");
	draft.triggers.forEach((trigger, index) => addTriggerIssues(trigger, (message, triggerPath) => context.addIssue({
		code: z.ZodIssueCode.custom,
		message,
		path: [
			"triggers",
			index,
			...triggerPath
		]
	})));
});
/**
* Checked only on submit: a webhook trigger has no endpoint, and an Outlook
* trigger no account, while the user is still choosing one.
*/
const addTriggerIssues = (trigger, addIssue) => {
	if (trigger.kind === "outlook_message" && trigger.connectedAccountId === null) addIssue("Choose an Outlook account", ["connectedAccountId"]);
	if (trigger.kind !== "generic_webhook") return;
	if (trigger.webhookEndpointId === null) addIssue("Choose a webhook endpoint", ["webhookEndpointId"]);
	addWebhookConditionIssues(trigger.conditions, (message, conditionPath) => addIssue(message, ["conditions", ...conditionPath]));
};
const ROUTINE_FORM_DRAFT_FIELDS = Object.keys(RoutineFormDraftSchema.shape);
const draftValuesEqual = (a, b) => {
	if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((value) => b.includes(value));
	if (a && b && typeof a === "object" && typeof b === "object") return JSON.stringify(a) === JSON.stringify(b);
	return a === b;
};
/** A trigger as the routine would save it: handing its row to chat changes nothing saved. */
const withoutChatMark = (trigger) => {
	if (!("sentToChat" in trigger)) return trigger;
	const { sentToChat: _sentToChat, ...saved } = trigger;
	return saved;
};
const triggersEqual = (a, b) => a.length === b.length && a.every((trigger) => b.some((other) => isEqual(withoutChatMark(trigger), withoutChatMark(other))));
const routineFormDraftsEqual = (a, b) => ROUTINE_FORM_DRAFT_FIELDS.every((field) => field === "triggers" ? triggersEqual(a.triggers, b.triggers) : draftValuesEqual(a[field], b[field]));
const ROUTINE_INTERVAL_END_ERROR = "End must be at or after the next scheduled run";
/** Reject only new or edited schedules with no run by their end. */
const hasNoUpcomingIntervalOccurrence = (draft, now = /* @__PURE__ */ new Date()) => {
	if (draft.recurrenceType !== "Interval" || draft.preservedRecurrenceRule) return false;
	const end = fromRoutineDateTimeValue(draft.intervalEndAt);
	if (!end) return false;
	const explicitStart = fromRoutineDateTimeValue(draft.intervalStartAt);
	const start = explicitStart ?? new Date(now);
	if (!explicitStart) start.setSeconds(0, 0);
	const everyMs = routineIntervalMinutes(draft.intervalCount, draft.intervalUnit) * 6e4;
	if (!Number.isFinite(everyMs) || everyMs <= 0) return true;
	const milliseconds = draft.intervalUnit === "minutes" ? (date) => date.getTime() : (date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
	const startMs = milliseconds(start);
	return startMs + (explicitStart ? Math.max(0, Math.floor((milliseconds(now) - startMs) / everyMs) + 1) : 1) * everyMs > milliseconds(end);
};
const getRoutineTitleUpdate = (draft, title) => {
	if (!draft.iconSuggestionsEnabled) return { title };
	return {
		title,
		icon: {
			type: "icon",
			code: suggestEntityIconSlug(title) ?? DEFAULT_ROUTINE_ICON.code,
			color: draft.icon?.color ?? null
		}
	};
};
/** Returns HH:mm for the next local 15-minute boundary, preserving an exact boundary. */
const getDefaultRoutineTriggerTime = (now = /* @__PURE__ */ new Date()) => {
	const millisecondsIntoHour = now.getMinutes() * 6e4 + now.getSeconds() * 1e3 + now.getMilliseconds();
	const roundedMinutes = Math.ceil(millisecondsIntoHour / 9e5) * 15;
	const hours = roundedMinutes === 60 ? now.getHours() + 1 : now.getHours();
	const minutes = roundedMinutes === 60 ? 0 : roundedMinutes;
	return `${(hours % 24).toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};
const createDefaultRoutineFormDraft = (now = /* @__PURE__ */ new Date()) => ({
	title: "",
	icon: { ...DEFAULT_ROUTINE_ICON },
	iconSuggestionsEnabled: true,
	prompt: "",
	recurrenceType: "Daily",
	triggerTime: getDefaultRoutineTriggerTime(now),
	hourlyEndTime: "18:00",
	advancedMonths: [...EVERY_MONTH],
	advancedDayMode: "weekDays",
	advancedMonthDays: [1],
	advancedTimes: ["09:00"],
	intervalCount: DEFAULT_ROUTINE_INTERVAL_COUNT,
	intervalUnit: DEFAULT_ROUTINE_INTERVAL_UNIT,
	intervalStartAt: null,
	intervalEndAt: null,
	preservedRecurrenceRule: null,
	weekDays: ["MO"],
	monthDay: 1,
	scheduleEnabled: true,
	triggers: [],
	notificationsEnabled: true,
	emailNotificationsEnabled: true,
	deliveryChannel: "off",
	autoPauseEnabled: true,
	mayReachOut: true,
	agentMode: "regular"
});
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
/**
* Routine parsers are looser than the draft schema. Replace only values that
* would make opening the form throw; the controls handle other warnings.
*/
const toSchemaValidTime = (time, fallback) => TIME_PATTERN.test(time) ? time : fallback;
const toSchemaValidMonthDay = (monthDay) => Number.isInteger(monthDay) && monthDay >= 1 && monthDay <= 31 ? monthDay : DEFAULT_ROUTINE_SCHEDULE.monthDay;
const createRoutineFormDraftFromRoutine = (routine) => {
	const schedule = parseRoutineSchedule(routine.recurrenceRule);
	const isUnrepresentableCanonical = !(schedule.frequency === "Advanced" || schedule.frequency === "Hourly" || schedule.frequency === "Interval") && !schedule.isValid && isCanonicalRecurrenceRule(routine.recurrenceRule);
	return {
		title: routine.title,
		icon: routine.icon ?? { ...DEFAULT_ROUTINE_ICON },
		iconSuggestionsEnabled: false,
		prompt: routine.prompt,
		recurrenceType: isUnrepresentableCanonical ? "Advanced" : schedule.frequency,
		triggerTime: toSchemaValidTime(schedule.triggerTime, DEFAULT_ROUTINE_SCHEDULE.triggerTime),
		hourlyEndTime: toSchemaValidTime(schedule.hourlyEndTime, DEFAULT_ROUTINE_SCHEDULE.hourlyEndTime),
		advancedMonths: schedule.advancedMonths,
		advancedDayMode: schedule.advancedDayMode,
		advancedMonthDays: schedule.advancedMonthDays,
		advancedTimes: schedule.advancedTimes,
		intervalCount: schedule.intervalCount,
		intervalUnit: schedule.intervalUnit,
		intervalStartAt: schedule.intervalStartAt,
		intervalEndAt: schedule.intervalEndAt,
		preservedRecurrenceRule: schedule.isValid || !routine.recurrenceRule ? null : routine.recurrenceRule,
		weekDays: schedule.weekDays,
		monthDay: toSchemaValidMonthDay(schedule.monthDay),
		scheduleEnabled: routine.scheduleEnabled ?? routine.recurrenceRule !== null,
		triggers: routine.triggers ?? [],
		notificationsEnabled: routine.notificationsEnabled ?? false,
		emailNotificationsEnabled: routine.emailNotificationsEnabled ?? false,
		deliveryChannel: routine.deliveryChannel ?? "off",
		autoPauseEnabled: routine.autoPauseEnabled ?? true,
		mayReachOut: routine.mayReachOut ?? true,
		agentMode: routine.agentMode ?? "regular"
	};
};
const isSameDraftValue = (a, b) => {
	if (Array.isArray(a) && Array.isArray(b)) return isEqual(a, b);
	if (typeof a === "object" && a !== null && typeof b === "object" && b !== null) return isSameIconConfig(a, b);
	return a === b;
};
/**
* Carries a kept draft onto a newer version of the routine: a field the user
* never moved off its baseline adopts the fresh value, an edited one survives.
*/
const rebaseRoutineFormDraft = (draft, baseline, next) => {
	const rebased = { ...draft };
	for (const [key, value] of Object.entries(next)) if (isSameDraftValue(rebased[key], baseline[key])) rebased[key] = value;
	const rebasedDraft = rebased;
	if (!isEqual(draft.triggers, baseline.triggers)) rebasedDraft.triggers = rebaseEmailTriggerDrafts(draft.triggers, baseline.triggers, next.triggers);
	return rebasedDraft;
};
const isSameRoutineFormDraft = (a, b) => a.title === b.title && a.prompt === b.prompt && a.recurrenceType === b.recurrenceType && a.triggerTime === b.triggerTime && a.hourlyEndTime === b.hourlyEndTime && a.advancedDayMode === b.advancedDayMode && a.intervalCount === b.intervalCount && a.intervalUnit === b.intervalUnit && a.intervalStartAt === b.intervalStartAt && a.intervalEndAt === b.intervalEndAt && a.preservedRecurrenceRule === b.preservedRecurrenceRule && a.monthDay === b.monthDay && a.notificationsEnabled === b.notificationsEnabled && a.emailNotificationsEnabled === b.emailNotificationsEnabled && a.deliveryChannel === b.deliveryChannel && a.autoPauseEnabled === b.autoPauseEnabled && a.mayReachOut === b.mayReachOut && a.agentMode === b.agentMode && a.advancedMonths.length === b.advancedMonths.length && a.advancedMonths.every((month, index) => month === b.advancedMonths[index]) && a.advancedMonthDays.length === b.advancedMonthDays.length && a.advancedMonthDays.every((day, index) => day === b.advancedMonthDays[index]) && a.advancedTimes.length === b.advancedTimes.length && a.advancedTimes.every((time, index) => time === b.advancedTimes[index]) && a.weekDays.length === b.weekDays.length && a.weekDays.every((day, index) => day === b.weekDays[index]) && isSameIconConfig(a.icon, b.icon);
var RoutineDraft = class extends Model {
	static modelName = "RoutineDraft";
	ownerId = "";
	draftKey = "create";
	draft;
	/** What the draft held when the form first opened. */
	baselineDraft;
	customBackup;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		this.ownerId = fields.ownerId ?? "";
		this.draftKey = fields.draftKey ?? "create";
		this.draft = RoutineFormDraftSchema.parse(fields.draft);
		this.baselineDraft = fields.baselineDraft ? RoutineFormDraftSchema.parse(fields.baselineDraft) : void 0;
		this.customBackup = fields.customBackup ? RoutineFormDraftSchema.parse(fields.customBackup) : void 0;
		makeObservable(this, {
			ownerId: observable,
			draftKey: observable,
			draft: observable,
			baselineDraft: observable,
			customBackup: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/routine-trigger-mapper.ts
/** The fields of a meeting trigger as the backend sends and receives them. */
const ApiMeetingTriggerFieldsSchema = z.object({
	kind: z.enum(MEETING_TRIGGER_KINDS),
	title_contains: z.array(z.string()),
	attendee_emails: z.array(z.string()),
	attendee_domains: z.array(z.string())
});
const meetingTriggerFieldsFromApi = (trigger) => ({
	kind: trigger.kind,
	titleContains: trigger.title_contains,
	attendeeEmails: trigger.attendee_emails,
	attendeeDomains: trigger.attendee_domains
});
/** Reads what a saved trigger watches. Its id, enabled state, and last fire time are left out. */
const routineTriggerInputFromApi = (trigger) => {
	switch (trigger.kind) {
		case ROUTINE_TRIGGER_GMAIL: return {
			kind: trigger.kind,
			query: trigger.query,
			labelIds: trigger.label_ids ?? ["INBOX"],
			connectedAccountId: trigger.connected_account_id
		};
		case ROUTINE_TRIGGER_OUTLOOK: return {
			kind: trigger.kind,
			connectedAccountId: trigger.connected_account_id,
			folders: trigger.folders,
			addresses: trigger.addresses,
			domains: trigger.domains,
			subjectContains: trigger.subject_contains,
			excludedAddresses: trigger.excluded_addresses,
			excludedDomains: trigger.excluded_domains,
			excludedSubjectContains: trigger.excluded_subject_contains
		};
		case ROUTINE_TRIGGER_WEBHOOK: return {
			kind: trigger.kind,
			conditions: trigger.conditions,
			webhookEndpointId: trigger.webhook_endpoint_id
		};
		case ROUTINE_TRIGGER_MEETING_STARTED:
		case ROUTINE_TRIGGER_MEETING_SUMMARIZED: return meetingTriggerFieldsFromApi(trigger);
	}
};
const routineTriggerInputToApi = (trigger) => {
	switch (trigger.kind) {
		case ROUTINE_TRIGGER_GMAIL: return {
			kind: trigger.kind,
			query: trigger.query,
			label_ids: trigger.labelIds,
			connected_account_id: trigger.connectedAccountId
		};
		case ROUTINE_TRIGGER_OUTLOOK: return {
			kind: trigger.kind,
			connected_account_id: trigger.connectedAccountId,
			folders: trigger.folders,
			addresses: trigger.addresses,
			domains: trigger.domains,
			subject_contains: trigger.subjectContains,
			excluded_addresses: trigger.excludedAddresses,
			excluded_domains: trigger.excludedDomains,
			excluded_subject_contains: trigger.excludedSubjectContains
		};
		case ROUTINE_TRIGGER_WEBHOOK: return {
			kind: trigger.kind,
			conditions: trigger.conditions,
			webhook_endpoint_id: trigger.webhookEndpointId
		};
		case ROUTINE_TRIGGER_MEETING_STARTED:
		case ROUTINE_TRIGGER_MEETING_SUMMARIZED: return {
			kind: trigger.kind,
			title_contains: trigger.titleContains,
			attendee_emails: trigger.attendeeEmails,
			attendee_domains: trigger.attendeeDomains
		};
		default: throw new Error(`Unknown routine trigger: ${JSON.stringify(trigger)}`);
	}
};
//#endregion
//#region src/core/sync/types/routine-library.ts
const IconConfigSchema = z.custom((value) => parseIconConfig(value) !== null);
/** A library entry's trigger as the backend sends it, read into a draft trigger. */
const LibraryTriggerSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal(ROUTINE_TRIGGER_GMAIL),
	query: z.string()
}), ApiMeetingTriggerFieldsSchema]).transform((trigger) => trigger.kind === "gmail_message" ? trigger : meetingTriggerFieldsFromApi(trigger)).pipe(RoutineTriggerDraftSchema);
/**
* The curated catalog of ready-made routines the backend serves from
* `routine_library`. Read-only: nothing here is a synced entity, so these are
* plain values rather than models.
*/
const RoutineLibraryItemSchema = z.object({
	id: z.string(),
	title: z.string(),
	icon: IconConfigSchema.nullable().optional(),
	recurrence_type: z.enum(["time_interval", "context_trigger"]),
	recurrence_rule: z.string(),
	prompt: z.string(),
	description: z.string(),
	tags: z.array(z.string()).default([]),
	integrations: z.array(z.string()).default([]),
	frequency: z.enum([
		"Daily",
		"Weekly",
		"Monthly"
	]).nullable(),
	trigger_time: z.string(),
	week_day: z.string().nullable(),
	month_day: z.number().nullable(),
	agent_mode: z.enum([
		"lite",
		"regular",
		"max",
		"ultra",
		"god"
	]).optional(),
	kind: z.enum([
		"digest",
		"watcher",
		"reminder"
	]).optional(),
	triggers: z.array(LibraryTriggerSchema).optional(),
	source: z.enum(["system", "team"]).optional(),
	shared_by: z.string().nullable().optional(),
	team_template_id: z.string().optional(),
	aliases: z.array(z.string()).optional(),
	schedule_label: z.string().optional(),
	setup_details: z.string().optional(),
	requires_setup_chat: z.boolean().optional()
});
const RoutineLibraryTabSchema = z.object({
	slug: z.string(),
	label: z.string(),
	count: z.number()
});
const RoutineLibraryResponseSchema = z.object({
	items: z.array(RoutineLibraryItemSchema),
	tabs: z.array(RoutineLibraryTabSchema)
});
/**
* The envelope only. Items are parsed one at a time so a template the schema
* does not recognize costs its own entry rather than the whole catalog.
*/
const RoutineLibraryEnvelopeSchema = z.object({
	items: z.array(z.unknown()),
	tabs: z.array(RoutineLibraryTabSchema)
});
/** Pre-tabs deployments answered with a bare array of items. */
const LegacyRoutineLibraryResponseSchema = z.array(z.unknown());
function parseItems(items) {
	const parsed = items.map((item) => RoutineLibraryItemSchema.safeParse(item)).filter((result) => result.success);
	return {
		items: parsed.map((result) => result.data),
		droppedCount: items.length - parsed.length
	};
}
/**
* Accepts either response shape and normalizes to the current one, so callers
* never branch on which backend they reached.
*/
function parseRoutineLibraryResponse(data) {
	const current = RoutineLibraryEnvelopeSchema.safeParse(data);
	if (current.success) return {
		...parseItems(current.data.items),
		tabs: current.data.tabs
	};
	const legacy = LegacyRoutineLibraryResponseSchema.safeParse(data);
	if (legacy.success) return {
		...parseItems(legacy.data),
		tabs: []
	};
	throw current.error;
}
//#endregion
//#region src/core/sync/types/sync-engine.ts
/**
* BootstrapPhase tracks the lifecycle of the bootstrap process.
* Exposed through BootstrapManager.phase (MobX observable) and
* surfaced to React via useSDKState().
*/
let BootstrapPhase = /* @__PURE__ */ function(BootstrapPhase) {
	BootstrapPhase["Idle"] = "idle";
	BootstrapPhase["CreatingStores"] = "creatingStores";
	BootstrapPhase["ConnectingDatabase"] = "connectingDatabase";
	BootstrapPhase["HydratingFromCache"] = "hydratingFromCache";
	BootstrapPhase["FetchingFromServer"] = "fetchingFromServer";
	BootstrapPhase["WritingToDatabase"] = "writingToDatabase";
	BootstrapPhase["Hydrating"] = "hydrating";
	BootstrapPhase["ConnectingSync"] = "connectingSync";
	BootstrapPhase["Ready"] = "ready";
	BootstrapPhase["Errored"] = "errored";
	return BootstrapPhase;
}({});
//#endregion
//#region src/core/sync/types/pagination-helpers.ts
const DEFAULT_ENTITY_PAGE_LIMIT = 50;
function resolvePageLimit(options, fallback) {
	return Math.max(1, options?.limit ?? fallback ?? 50);
}
function buildEntityPageFromOverfetch(records, limit, getCursorValue) {
	const hasMore = records.length > limit;
	const pageRecords = hasMore ? records.slice(0, limit) : records;
	const lastRecord = pageRecords[pageRecords.length - 1];
	return {
		records: pageRecords,
		nextCursor: hasMore && lastRecord ? {
			value: getCursorValue(lastRecord),
			id: lastRecord.id
		} : void 0,
		hasMore
	};
}
//#endregion
//#region src/core/sync/types/pagination-drain.ts
const DEFAULT_DRAIN_PAGE_SIZE = 500;
function throwIfAborted(signal) {
	if (signal?.aborted) throw new DOMException("The operation was aborted", "AbortError");
}
/** Yields a page at a time, so a caller can process and discard each instead of holding all of them */
async function* streamEntityPages(listPage, listPageAfter, options) {
	const pageOptions = { limit: Math.max(1, options?.pageSize ?? DEFAULT_DRAIN_PAGE_SIZE) };
	let cursor = options?.startCursor;
	let hasMore = true;
	while (hasMore) {
		throwIfAborted(options?.signal);
		const page = cursor ? await listPageAfter(cursor, pageOptions) : await listPage(pageOptions);
		yield page;
		cursor = page.nextCursor;
		hasMore = page.hasMore && cursor !== void 0;
		if (hasMore) await options?.onYield?.();
	}
}
async function drainEntityPages(listPage, listPageAfter, options) {
	const allRecords = [];
	for await (const page of streamEntityPages(listPage, listPageAfter, options)) allRecords.push(...page.records);
	return allRecords;
}
//#endregion
//#region src/core/sync/types/run-report.ts
const NO_APPLY_TIMING = {
	applyMs: 0,
	yieldMs: 0,
	maxSliceMs: 0,
	parseMs: 0,
	persistMs: 0,
	searchMs: 0,
	maxPageApplyMs: 0
};
function addPageApplyTiming(run, page) {
	return {
		applyMs: run.applyMs + page.applyMs,
		yieldMs: run.yieldMs + page.yieldMs,
		maxSliceMs: Math.max(run.maxSliceMs, page.maxSliceMs),
		parseMs: run.parseMs + page.parseMs,
		persistMs: run.persistMs + page.persistMs,
		searchMs: run.searchMs + page.searchMs,
		maxPageApplyMs: Math.max(run.maxPageApplyMs, page.applyMs)
	};
}
function startRunRecord(trigger, previous, resumed, now = Date.now()) {
	return {
		trigger: previous?.trigger ?? trigger,
		attempt: previous ? previous.attempt + 1 : 1,
		resumed,
		priorMs: previous ? previous.priorMs + attemptDurationMs(previous) : 0,
		attemptStartedAtMs: now,
		updatedAtMs: now,
		open: true,
		pages: resumed ? previous?.pages ?? 0 : 0,
		rows: resumed ? previous?.rows ?? 0 : 0
	};
}
function attemptDurationMs(run) {
	return Math.max(0, run.updatedAtMs - run.attemptStartedAtMs);
}
//#endregion
//#region src/core/sync/models/message.ts
const QUOTE_LINE = /^(?:Context|Tell me more about):\s*"(.*)"\s*$/;
var Message = class extends Model {
	static modelName = "Message";
	threadId = "";
	role = "user";
	content = "";
	thinkingContent = "";
	hiddenContent = "";
	status = "not_submitted";
	mode = MAIN_CHAT_MODE;
	fileIds = [];
	modalitiesUsed = null;
	todoResults = [];
	toolCalls = [];
	contextAppsUsed = [];
	collaboratorIds = [];
	askUserQuestions = null;
	askUserAnswers = null;
	/** The user sent this message while a turn was running, and it joined that turn */
	joinedTurn = false;
	/**
	* The server refused this message because a reply held the chat, so the server never stored it.
	* Set only on this device and stored with the message. The schema manifest leaves it out: no
	* server row carries it, so a change to it needs no refill. The schema-versioning snapshot fails
	* if it enters the manifest
	*/
	refusedAsBusy = false;
	versionIds = [];
	replacesMessageId = null;
	replacedMessageIds = [];
	agent = "";
	isExpanded = false;
	needsTruncation = void 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			threadId: observable,
			role: observable,
			content: observable,
			thinkingContent: observable,
			hiddenContent: observable,
			status: observable,
			mode: observable,
			fileIds: observable,
			modalitiesUsed: observable,
			todoResults: observable,
			toolCalls: observable,
			contextAppsUsed: observable,
			collaboratorIds: observable,
			askUserQuestions: observable,
			askUserAnswers: observable,
			joinedTurn: observable,
			refusedAsBusy: observable,
			versionIds: observable,
			replacesMessageId: observable,
			replacedMessageIds: observable,
			agent: observable,
			isExpanded: observable,
			needsTruncation: observable,
			displayContent: computed,
			parsedContent: computed,
			setIsExpanded: action,
			setNeedsTruncation: action
		});
	}
	getPersistedFieldNames() {
		return super.getPersistedFieldNames().filter((key) => key !== "refusedAsBusy");
	}
	get displayContent() {
		const content = stripMsgChannelTags(stripMeetingIntentPrompt(stripRoutinePreamble(stripAskUserBlocks(this.content))));
		return this.status === "stopped" && content ? `${content}…` : content;
	}
	get parsedContent() {
		const lines = this.displayContent.split("\n");
		const quotes = [];
		let i = 0;
		while (i < lines.length) {
			const match = QUOTE_LINE.exec(lines[i]);
			if (!match) break;
			quotes.push(match[1]);
			i++;
		}
		if (quotes.length === 0) return {
			quotes: [],
			body: this.displayContent
		};
		if (lines[i] === "") i++;
		return {
			quotes,
			body: lines.slice(i).join("\n")
		};
	}
	get quotes() {
		return this.parsedContent.quotes;
	}
	get body() {
		return this.parsedContent.body;
	}
	setIsExpanded(value) {
		this.isExpanded = value;
	}
	setNeedsTruncation(value) {
		this.needsTruncation = value;
	}
	toJSON() {
		const json = super.toJSON();
		delete json.isExpanded;
		delete json.needsTruncation;
		return json;
	}
};
//#endregion
//#region src/core/sync/models/report.ts
var Report = class extends Model {
	static modelName = "Report";
	referenceId = -1;
	title = "";
	date = /* @__PURE__ */ new Date();
	scheduledAt = /* @__PURE__ */ new Date();
	text = "";
	reportTemplateId = -1;
	chatId = null;
	lastViewed = null;
	artifactFileIds = [];
	detailUpdatedAt = null;
	waitingOn = [];
	ranWithout = [];
	reachOut = null;
	backend = null;
	runContext = null;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		if (fields.scheduledAt === void 0) this.scheduledAt = this.date;
		makeObservable(this, {
			referenceId: observable,
			title: observable,
			date: observable,
			scheduledAt: observable,
			text: observable,
			reportTemplateId: observable,
			chatId: observable,
			lastViewed: observable,
			artifactFileIds: observable,
			detailUpdatedAt: observable,
			waitingOn: observable,
			ranWithout: observable,
			reachOut: observable.ref,
			backend: observable,
			runContext: observable.ref,
			hasDetailText: computed
		});
	}
	/**
	* Whether `text` is the full report rather than the short preview the list
	* endpoints return. False also once a newer version arrives, because the
	* detail we hold is then stale.
	*/
	get hasDetailText() {
		return this.detailUpdatedAt !== null && this.detailUpdatedAt.getTime() >= this.updatedAt.getTime();
	}
	parseStatus = (reportText) => {
		if (reportText === "NO DATA") return "NO DATA";
		if (reportText === "GENERATING") return "GENERATING";
		return null;
	};
	formatDate = (date) => {
		const d = new Date(date);
		if (isToday(d)) return "Today, " + format(d, "h:mm a");
		if (isYesterday(d)) return "Yesterday, " + format(d, "h:mm a");
		if (isSameWeek(d, /* @__PURE__ */ new Date())) return format(d, "E, MMM d, h:mm a");
		return format(d, "MMM d, h:mm a");
	};
};
//#endregion
//#region src/core/sync/models/routine.ts
var Routine = class extends Model {
	static modelName = "Routine";
	referenceId = -1;
	title = "";
	kind = "digest";
	completedAt = null;
	icon = null;
	projectId = null;
	createdInChatId = null;
	recurrenceType = "";
	recurrenceRule = null;
	prompt = "";
	isPaused = false;
	isAdminPaused = false;
	isExperimental = false;
	origin = "user";
	hiddenFromUser = false;
	starred = false;
	/** Null when the server is too old to send them */
	permissions = null;
	pauseReason = null;
	autoPauseEnabled = true;
	mayReachOut = true;
	/** The routine's newest run that waits on its user */
	waitingOnYou = null;
	notificationsEnabled = true;
	emailNotificationsEnabled = false;
	deliveryChannel = "off";
	deliveryAgentId = null;
	agentMode = "regular";
	lastManualGenerationAt = null;
	teamShare = null;
	triggers = [];
	get displayIcon() {
		return this.icon ?? DEFAULT_ROUTINE_ICON;
	}
	/** Littlebird runs it for its own work, such as a card watcher, so it is not one of the user's routines */
	get isSystem() {
		return this.origin === "system";
	}
	/**
	* Littlebird set the routine up and decides when it runs, so the user cannot
	* run it now. Only for copy: what the user may do comes from `permissions`.
	* IM-channel routines carry no `permissions` and have their own copy.
	*/
	get isManaged() {
		return this.permissions !== null && !this.permissions.canRun;
	}
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			referenceId: observable,
			title: observable,
			kind: observable,
			completedAt: observable,
			icon: observable,
			projectId: observable,
			createdInChatId: observable,
			recurrenceType: observable,
			recurrenceRule: observable,
			prompt: observable,
			isPaused: observable,
			isAdminPaused: observable,
			isExperimental: observable,
			origin: observable,
			hiddenFromUser: observable,
			starred: observable,
			permissions: observable,
			pauseReason: observable,
			autoPauseEnabled: observable,
			mayReachOut: observable,
			waitingOnYou: observable.ref,
			notificationsEnabled: observable,
			emailNotificationsEnabled: observable,
			deliveryChannel: observable,
			deliveryAgentId: observable,
			agentMode: observable,
			lastManualGenerationAt: observable,
			teamShare: observable,
			triggers: observable,
			displayIcon: computed,
			isSystem: computed,
			isManaged: computed
		});
	}
};
//#endregion
//#region src/core/sync/models/routine-run-source.ts
/**
* The ui-utils source contracts are structural camel case, so the assignment
* below is the check.
*
* Thread `TIcon` through explicitly, or every downstream view model degrades
* to `RoutineViewModel<unknown>`.
*/
const adaptRoutineModelToSource = (routine) => routine;
const adaptReportModelToSource = (report) => report;
//#endregion
//#region src/core/sync/models/thread.ts
function isRoutineRunThread(thread) {
	return "reportTemplateId" in thread && typeof thread.reportTemplateId === "number";
}
function isAgentThread(thread) {
	return "agentId" in thread && typeof thread.agentId === "string";
}
var Thread = class extends Model {
	static modelName = "Thread";
	title = "";
	lastMessageAt = /* @__PURE__ */ new Date();
	starred = false;
	archived = false;
	status = "not_submitted";
	model = "";
	lastMessageId = void 0;
	errorMessage = void 0;
	finished = void 0;
	/** Local reply completion time in milliseconds; metadata updates must not restart feed retention */
	completedAt = void 0;
	/**
	* The sync id of the last server row this client took for the chat: one that synced, the row `new_chat`
	* created, or the finished row that a done or error event or Stop named before it synced
	* (`ThreadStore.recordFinishSyncId`). `ThreadStore.buildThreadDataFromApi` spreads each server row into the
	* chat, so storage holds it. See `getPersistedFieldNames`.
	*/
	sync_id = void 0;
	/**
	* The sync id this device held when the chat last turned busy on it, the start of the reply shown; undefined
	* when it held none. Every finish takes a new sync id, so only a finished row newer than this ends the
	* reply (`ThreadStore.isAfterReplyStart`, Android's `replySyncId`). Set only on this
	* device. See `getPersistedFieldNames`.
	*/
	replyStartSyncId = void 0;
	synced = false;
	isLegacyChat = false;
	projectId = null;
	meetingId = null;
	reportTemplateId = null;
	agentId = null;
	lastImportedMessageId = null;
	origin = null;
	hiddenFromUser = false;
	isMessagingChannel = false;
	suggestedProject = null;
	backend = null;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			title: observable,
			lastMessageAt: observable,
			starred: observable,
			archived: observable,
			status: observable,
			model: observable,
			lastMessageId: observable,
			errorMessage: observable,
			finished: observable,
			completedAt: observable,
			synced: observable,
			isLegacyChat: observable,
			projectId: observable,
			meetingId: observable,
			reportTemplateId: observable,
			agentId: observable,
			lastImportedMessageId: observable,
			origin: observable,
			hiddenFromUser: observable,
			isMessagingChannel: observable,
			suggestedProject: observable,
			backend: observable,
			isAvailableForInteraction: computed
		});
	}
	/**
	* Storage holds reply metadata, but the schema manifest leaves its names out: a new name in the manifest
	* refills the table on every device, and no column reads them.
	*/
	getPersistedFieldNames() {
		return super.getPersistedFieldNames().filter((key) => key !== "sync_id" && key !== "replyStartSyncId" && key !== "completedAt");
	}
	/** Whether the thread may own ongoing interaction rather than being archived or deleted */
	get isAvailableForInteraction() {
		return !this.archived && !this.deletedAt;
	}
};
//#endregion
//#region src/core/sync/models/meeting.ts
var Meeting = class extends Model {
	static modelName = "Meeting";
	name = null;
	summary = null;
	tldr = null;
	personifiedTranscript = null;
	notes = null;
	prep = null;
	prepId = null;
	preps = [];
	prepStatus = null;
	sourceApp = null;
	externalSource = null;
	eventId = null;
	calendarSeriesId = null;
	eventStartAt = null;
	status = "active";
	summaryStatus = "initial";
	attendees = null;
	speakers = null;
	endedAt = null;
	firstChunkTimestamp = null;
	lastTranscribedAt = null;
	firstTranscriptAt = null;
	transcriptDeletedAt = null;
	projectId = null;
	customVocabulary = null;
	recordingDeviceId = null;
	recordingPlatform = null;
	hasNotification = false;
	prepContentCollapsed = true;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			name: observable,
			summary: observable,
			tldr: observable,
			personifiedTranscript: observable,
			notes: observable,
			notesText: computed,
			prep: observable,
			prepId: observable,
			preps: observable,
			prepStatus: observable,
			sourceApp: observable,
			externalSource: observable,
			eventId: observable,
			calendarSeriesId: observable,
			eventStartAt: observable,
			status: observable,
			summaryStatus: observable,
			attendees: observable,
			speakers: observable,
			endedAt: observable,
			firstChunkTimestamp: observable,
			lastTranscribedAt: observable,
			firstTranscriptAt: observable,
			transcriptDeletedAt: observable,
			projectId: observable,
			customVocabulary: observable,
			recordingDeviceId: observable,
			recordingPlatform: observable,
			hasNotification: observable,
			prepContentCollapsed: observable,
			markNotificationViewed: action.bound,
			togglePrepContentCollapsed: action.bound
		});
	}
	get notesText() {
		return this.store.getNotesText(this.notes);
	}
	markNotificationViewed() {
		this.hasNotification = false;
	}
	togglePrepContentCollapsed() {
		this.prepContentCollapsed = !this.prepContentCollapsed;
	}
};
//#endregion
//#region src/core/sync/models/agent.ts
var Agent = class extends Model {
	static modelName = "Agent";
	name = "";
	label = null;
	description = null;
	icon = null;
	agentSlug = null;
	chatId = null;
	pinnedAt = null;
	archivedAt = null;
	syncId = 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			name: observable,
			label: observable,
			description: observable,
			icon: observable,
			agentSlug: observable,
			chatId: observable,
			pinnedAt: observable,
			archivedAt: observable,
			syncId: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/project.ts
var Project = class extends Model {
	static modelName = "Project";
	name = "";
	instructions = null;
	icon = null;
	starred = false;
	limitChatContextToProject = true;
	disableScreenContextSearch = false;
	syncId = 0;
	role = "owner";
	hasShareLink = false;
	capabilities = {
		canEditContent: true,
		canInvite: false,
		canManageMembers: false,
		canManageLink: false,
		canRename: true,
		canDelete: true,
		canLeave: false
	};
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			name: observable,
			instructions: observable,
			icon: observable,
			starred: observable,
			limitChatContextToProject: observable,
			disableScreenContextSearch: observable,
			syncId: observable,
			role: observable,
			hasShareLink: observable,
			capabilities: observable
		});
	}
};
//#endregion
//#region src/core/projects/project-members.ts
async function fetchMembers(sdk, projectId) {
	return (await sdk.apiClient.fetch(`projects/${projectId}/members`)).members;
}
function addMember(sdk, projectId, input) {
	return sdk.apiClient.fetch(`projects/${projectId}/members`, {
		method: "POST",
		body: JSON.stringify(input)
	});
}
function updateMemberRole(sdk, projectId, membershipId, role) {
	return sdk.apiClient.fetch(`projects/${projectId}/members/${membershipId}`, {
		method: "PATCH",
		body: JSON.stringify({ role })
	});
}
function removeMember(sdk, projectId, membershipId) {
	return sdk.apiClient.fetch(`projects/${projectId}/members/${membershipId}`, { method: "DELETE" });
}
function leaveProject(sdk, projectId) {
	return sdk.apiClient.fetch(`projects/${projectId}/members/me`, { method: "DELETE" });
}
async function fetchShareLink(sdk, projectId) {
	return (await sdk.apiClient.fetch(`projects/${projectId}/share-link`)).token;
}
async function mintShareLink(sdk, projectId) {
	return (await sdk.apiClient.fetch(`projects/${projectId}/share-link`, { method: "POST" })).token;
}
function revokeShareLink(sdk, projectId) {
	return sdk.apiClient.fetch(`projects/${projectId}/share-link`, { method: "DELETE" });
}
function fetchShareLinkPreview(sdk, token) {
	return sdk.apiClient.fetch(`projects/join/${encodeURIComponent(token)}`);
}
function redeemShareLink(sdk, token) {
	return sdk.apiClient.fetch(`projects/join/${encodeURIComponent(token)}`, { method: "POST" });
}
//#endregion
//#region src/core/projects/project-shared-content.ts
function page(sdk, path, cursor) {
	const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
	return sdk.apiClient.fetch(`${path}${query}`);
}
function fetchProjectChats(sdk, projectId, cursor) {
	return page(sdk, `projects/${projectId}/chats`, cursor);
}
function fetchProjectChat(sdk, projectId, chatId) {
	return sdk.apiClient.fetch(`projects/${projectId}/chats/${chatId}`);
}
function fetchProjectChatFile(sdk, projectId, chatId, fileId) {
	return sdk.apiClient.fetch(`projects/${projectId}/chats/${chatId}/files/${fileId}`);
}
function fetchProjectMeetings(sdk, projectId, cursor) {
	return page(sdk, `projects/${projectId}/meetings`, cursor);
}
function fetchProjectMeeting(sdk, projectId, meetingId) {
	return sdk.apiClient.fetch(`projects/${projectId}/meetings/${meetingId}`);
}
function fetchProjectMeetingFile(sdk, projectId, meetingId, fileId) {
	return sdk.apiClient.fetch(`projects/${projectId}/meetings/${meetingId}/files/${fileId}`);
}
//#endregion
//#region src/core/projects/team-chats.ts
const chatPath = (projectId, chatId) => `projects/${projectId}/chats/${chatId}`;
function postToTeamChat(sdk, projectId, chatId, post) {
	return sdk.apiClient.fetch(`${chatPath(projectId, chatId)}/posts`, {
		method: "POST",
		body: JSON.stringify(post)
	});
}
async function fetchTeamChatDraft(sdk, projectId, chatId) {
	return (await sdk.apiClient.fetch(`${chatPath(projectId, chatId)}/draft`)).draft;
}
async function requestTeamChatDraft(sdk, projectId, chatId, instruction) {
	return (await sdk.apiClient.fetch(`${chatPath(projectId, chatId)}/draft`, {
		method: "POST",
		body: JSON.stringify(instruction === null ? {} : { instruction })
	})).draft;
}
function discardTeamChatDraft(sdk, projectId, chatId) {
	return sdk.apiClient.fetch(`${chatPath(projectId, chatId)}/draft`, { method: "DELETE" });
}
function markTeamChatRead(sdk, projectId, chatId) {
	return sdk.apiClient.fetch(`${chatPath(projectId, chatId)}/read`, { method: "POST" });
}
//#endregion
//#region src/core/projects/project-files.ts
function toProjectFile(raw) {
	return {
		id: raw.id,
		filename: raw.filename,
		contentType: raw.content_type,
		size: raw.size,
		status: raw.status,
		ingestionStatus: raw.ingestion_status ?? null,
		summary: raw.summary ?? null,
		createdAt: raw.created_at,
		updatedAt: raw.updated_at
	};
}
async function fetchProjectFiles(sdk, projectId) {
	return ((await sdk.apiClient.fetch(`projects/${projectId}/files`)).files ?? []).map(toProjectFile);
}
async function createProjectFileUploadUrl(sdk, projectId, input) {
	return (await sdk.apiClient.fetch(`projects/${projectId}/files/upload-url`, {
		method: "POST",
		body: JSON.stringify({ payload: {
			id: input.id,
			filename: input.filename,
			content_type: input.contentType,
			size: input.size
		} })
	})).url;
}
async function finalizeProjectFile(sdk, projectId, fileId) {
	return toProjectFile(await sdk.apiClient.fetch(`projects/${projectId}/files/${fileId}/finalize`, { method: "POST" }));
}
function deleteProjectFile(sdk, projectId, fileId) {
	return sdk.apiClient.fetch(`projects/${projectId}/files/${fileId}`, { method: "DELETE" });
}
//#endregion
//#region src/core/projects/project-team.ts
async function fetchInviteTeam(sdk) {
	const response = await sdk.apiClient.fetch("team/dashboard");
	if (!response) return null;
	return {
		team: {
			id: response.team.id,
			name: response.team.name
		},
		members: response.members.map((member) => ({
			email: member.email,
			name: member.name
		}))
	};
}
//#endregion
//#region src/core/action-card-delegations/action-card-delegations.ts
const toSentDelegation = (raw) => ({
	id: raw.id,
	sourceCardId: raw.source_card_id,
	recipientCardId: raw.recipient_card_id,
	delegatedTo: raw.delegated_to,
	note: raw.note,
	status: raw.status,
	createdAt: raw.created_at,
	updatedAt: raw.updated_at
});
const delegationPersonName = (person) => person.name || person.email;
function fetchDelegationTeammates(sdk) {
	return sdk.apiClient.fetch("action-card-delegations/teammates");
}
async function fetchSentDelegations(sdk) {
	return (await sdk.apiClient.fetch("action-card-delegations/sent")).map(toSentDelegation);
}
async function fetchReceivedDelegations(sdk) {
	return (await sdk.apiClient.fetch("action-card-delegations/received")).map((raw) => ({
		id: raw.id,
		recipientCardId: raw.recipient_card_id,
		delegatedBy: raw.delegated_by,
		note: raw.note,
		createdAt: raw.created_at
	}));
}
async function delegateActionCard(sdk, input) {
	const response = await sdk.apiClient.fetch("action-card-delegations", {
		method: "POST",
		body: JSON.stringify({
			card_id: input.cardId,
			delegated_to_id: input.delegatedToId,
			note: input.note || null
		})
	});
	return toSentDelegation(response);
}
//#endregion
//#region src/core/sync/models/mcp-client-grant.ts
var McpClientGrant = class extends Model {
	static modelName = "McpClientGrant";
	clientId = "";
	clientName = "";
	authorizedAt = /* @__PURE__ */ new Date();
	syncId = 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			clientId: observable,
			clientName: observable,
			authorizedAt: observable,
			syncId: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/user-config.ts
var UserConfigModel = class extends Model {
	static modelName = "UserConfig";
	meetings = {};
	menubar = {};
	calendar = {};
	chat = {};
	privacy = {};
	notifications = {};
	proactiveAgents = {};
	feed = {};
	autoReview = {};
	syncId = 0;
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			meetings: observable,
			menubar: observable,
			calendar: observable,
			chat: observable,
			privacy: observable,
			notifications: observable,
			proactiveAgents: observable,
			feed: observable,
			autoReview: observable,
			syncId: observable
		});
	}
};
//#endregion
//#region src/core/sync/models/action-card.ts
var ActionCard = class extends Model {
	static modelName = "ActionCard";
	localId = 0;
	chatId = null;
	sourceMessagingChannelType = null;
	sourceMeetingId = null;
	assistantMessageId = null;
	toolUseIndex = null;
	entityIndex = 0;
	origin = "chat";
	actionType = "";
	status = "draft";
	content = {};
	result = null;
	externalRef = null;
	context = null;
	importance = null;
	expiresAt = null;
	dueAt = null;
	dueAtEstimated = false;
	autoReview = null;
	resolvedAt = null;
	dismissedAt = null;
	pinnedAt = null;
	snoozedUntil = null;
	followUpChatId = null;
	followUpRequestId = null;
	followUpRepliedAt = null;
	followUpReplyKind = null;
	reportId = null;
	reportTemplateId = null;
	bootstrapDecision = null;
	watch = null;
	parentId = null;
	dismissedWithParent = false;
	backend = null;
	syncId = 0;
	computed = null;
	get title() {
		return this.computed?.title ?? "";
	}
	get summary() {
		return this.computed?.summary ?? "";
	}
	get analytics() {
		return this.computed?.analytics ?? {};
	}
	get facts() {
		return this.computed?.facts ?? {};
	}
	get dismissalQuestion() {
		return dismissalQuestionOf(this.computed?.facts);
	}
	get views() {
		return this.computed ? this.store.templatesFor(this.actionType) : null;
	}
	get row() {
		const { type: _type, ...storedFields } = this.content;
		return {
			id: this.id,
			action_type: this.actionType,
			status: this.status,
			content: this.content,
			result: this.result,
			external_ref: this.externalRef,
			context: this.context,
			title: this.title,
			summary: this.summary,
			views: this.views,
			view_state: this.computed ? {
				...storedFields,
				...this.computed.fields
			} : null,
			facts: this.computed?.facts ?? null,
			error: this.computed?.error ?? null,
			created_at: this.createdAt.toISOString(),
			updated_at: this.updatedAt.toISOString(),
			resolved_at: this.resolvedAt?.toISOString(),
			sync_id: this.syncId
		};
	}
	constructor(fields = {}, store, logger) {
		super(fields, store, logger);
		Object.assign(this, fields);
		makeObservable(this, {
			localId: observable,
			chatId: observable,
			sourceMessagingChannelType: observable,
			sourceMeetingId: observable,
			assistantMessageId: observable,
			toolUseIndex: observable,
			entityIndex: observable,
			origin: observable,
			actionType: observable,
			status: observable,
			content: observable,
			result: observable,
			externalRef: observable,
			context: observable,
			importance: observable,
			expiresAt: observable,
			dueAt: observable,
			dueAtEstimated: observable,
			autoReview: observable,
			resolvedAt: observable,
			dismissedAt: observable,
			pinnedAt: observable,
			snoozedUntil: observable,
			followUpChatId: observable,
			followUpRequestId: observable,
			followUpRepliedAt: observable,
			followUpReplyKind: observable,
			reportId: observable,
			reportTemplateId: observable,
			bootstrapDecision: observable.ref,
			watch: observable,
			parentId: observable,
			dismissedWithParent: observable,
			backend: observable,
			syncId: observable,
			computed: observable,
			title: computed,
			summary: computed,
			analytics: computed,
			facts: computed,
			views: computed,
			row: computed
		});
	}
};
var RequestTimeoutError = class extends Error {
	status;
	constructor(message) {
		super(message);
		this.name = "RequestTimeoutError";
		this.status = 408;
	}
};
var FreeLimitExceededError = class extends Error {
	errorCode;
	nextAllowedTime;
	messageLimit;
	chatId;
	constructor(messageOrMetadata) {
		const resolvedMetadata = typeof messageOrMetadata === "string" ? void 0 : messageOrMetadata;
		const resolvedMessage = typeof messageOrMetadata === "string" ? messageOrMetadata : messageOrMetadata.errorMessage ?? "Free limit exceeded";
		super(resolvedMessage);
		this.name = "FreeLimitExceededError";
		this.errorCode = resolvedMetadata?.errorCode;
		this.nextAllowedTime = resolvedMetadata?.nextAllowedTime;
		this.messageLimit = resolvedMetadata?.messageLimit;
		this.chatId = resolvedMetadata?.chatId;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var AuthenticationError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "AuthenticationError";
	}
};
/**
* The server refused a chat turn because another reply holds the chat, so the message was not accepted. It carries
* the id the chat's lease is held under, as the busy refusal over the socket does
*/
var ChatBusyError = class extends Error {
	conflictOwnerMessageId;
	constructor(conflictOwnerMessageId) {
		super("Another reply holds the chat");
		this.conflictOwnerMessageId = conflictOwnerMessageId;
		this.name = "ChatBusyError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/** The steer endpoint refused this request, so it stored nothing; an earlier request with the same id may have */
var SteerRefusedError = class extends Error {
	constructor(status) {
		super(`Steer request refused with ${status}`);
		this.name = "SteerRefusedError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var RateLimitError = class extends Error {
	retryAfter;
	constructor(message, retryAfter) {
		super(message);
		this.name = "RateLimitError";
		this.retryAfter = retryAfter;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var MeetingQuotaExceededError = class extends Error {
	errorCode;
	errorMessage;
	constructor(errorCode, errorMessage) {
		super(errorMessage);
		this.name = "MeetingQuotaExceededError";
		this.errorCode = errorCode;
		this.errorMessage = errorMessage;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var MeetingCreationCancelledError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "MeetingCreationCancelledError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var StreamCancellationError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "StreamCancellationError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/**
* A bootstrap/sync stream was cut short by a transient condition (network drop,
* timeout, app backgrounded) rather than genuinely completing. Callers treat it
* as recoverable and do not report it to Sentry as a hard error.
*/
var BootstrapInterruptedError = class extends Error {
	constructor(message) {
		super(message);
		this.name = "BootstrapInterruptedError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/**
* The server returned 409 `sync-reset-required`, meaning the local cursor is no
* longer usable (client ahead of server, expired, wrong scope, or unsupported
* projection/protocol). The bootstrap manager must clear the local checkpoint
* and force a full bootstrap instead of silently retrying the same impossible
* cursor. Recovery re-enters cold bootstrap immediately on both the warm
* (background) and cold (foreground) paths.
*/
var SyncResetRequiredError = class extends Error {
	reason;
	constructor(message, reason) {
		super(message);
		this.name = "SyncResetRequiredError";
		this.reason = reason;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/**
* A bootstrap hard error already logged at its origin with a specific, distinct
* message. Callers record the failure but must NOT log it again, so one incident
* produces exactly one Sentry issue instead of a specific + a generic duplicate.
*/
var ReportedBootstrapError = class extends Error {
	constructor(message, cause) {
		super(message);
		this.name = "ReportedBootstrapError";
		if (cause !== void 0) this.cause = cause;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var MissingEntityQueriesError = class extends Error {
	constructor(table) {
		super(`Missing entity queries for ${table}`);
		this.name = "MissingEntityQueriesError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
function parseRetryAfter(retryAfterHeader) {
	if (!retryAfterHeader) return 90;
	const seconds = Number.parseFloat(retryAfterHeader);
	if (!Number.isNaN(seconds)) {
		if (seconds <= 0) return 0;
		return Math.min(seconds, 900);
	}
	const retryDate = new Date(retryAfterHeader).getTime();
	if (!isNaN(retryDate)) {
		const delayMs = Math.max(0, retryDate - Date.now());
		const delaySeconds = Math.ceil(delayMs / 1e3);
		if (delaySeconds > 0) return Math.min(delaySeconds, 900);
	}
	return 90;
}
var HttpError = class extends Error {
	status;
	detail;
	/**
	* Copy the server explicitly marked as user-facing. `message` is whatever the
	* response carried — a framework detail string, or the raw body when an
	* intermediary answered with HTML — so only this is safe to show a user.
	*/
	userErrorMessage;
	constructor(status, message, userErrorMessage, detail) {
		super(message);
		this.name = "HttpError";
		this.status = status;
		this.userErrorMessage = userErrorMessage;
		this.detail = detail;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var ProjectNotEmptyError = class extends Error {
	projectId;
	constructor(projectId, message) {
		super(message ?? "Project still contains chats or meetings");
		this.name = "ProjectNotEmptyError";
		this.projectId = projectId;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var ProjectForbiddenError = class extends HttpError {
	constructor(message) {
		super(403, message);
		this.name = "ProjectForbiddenError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
const CHECK_NETWORK = "Unable to connect to Littlebird. Check your network connection and try again.";
const CHECK_NETWORK_OR_PROXY = "Unable to establish a connection to Littlebird. Check your network or proxy settings and try again.";
const CHAT_CONNECTION_USER_MESSAGES = {
	TLS_ERROR: "Unable to establish a secure connection. Your network or proxy may be intercepting HTTPS traffic. Contact your IT team or try a different network.",
	ENOTFOUND: "Unable to reach Littlebird servers. Check your network connection and try again.",
	CONNECTION_ERROR: CHECK_NETWORK,
	UPGRADE_REJECTED: CHECK_NETWORK_OR_PROXY,
	HANDSHAKE_TIMEOUT: CHECK_NETWORK_OR_PROXY,
	UNKNOWN: CHECK_NETWORK
};
var ChatConnectionError = class extends Error {
	reason;
	constructor(reason, options) {
		super(options?.message ?? CHAT_CONNECTION_USER_MESSAGES[reason], { cause: options?.cause });
		this.name = "ChatConnectionError";
		this.reason = reason;
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
const MAX_ERROR_CAUSE_DEPTH = 3;
function flattenErrorCause(error, maxDepth = MAX_ERROR_CAUSE_DEPTH) {
	if (!(error instanceof Error) || error.cause === void 0) return void 0;
	const parts = [];
	let current = error.cause;
	let depth = 0;
	while (current !== void 0 && depth < maxDepth) {
		if (current instanceof Error) {
			parts.push(current.message);
			current = current.cause;
		} else {
			parts.push(String(current));
			break;
		}
		depth += 1;
	}
	return parts.length > 0 ? parts.join(" <- ") : void 0;
}
function messageErrorAnalyticsFields(error, fallbackMessage = "Unknown error") {
	const fields = { error: error instanceof Error ? error.message : String(error ?? "") || fallbackMessage };
	const cause = flattenErrorCause(error);
	if (cause) fields.error_cause = cause;
	if (error instanceof ChatConnectionError) fields.ws_reason = error.reason;
	return fields;
}
/** Track non-network failures, plus ChatConnectionError (TLS/proxy diagnosis). */
function shouldTrackMessageError(error) {
	return !isNetworkError(error) || error instanceof ChatConnectionError;
}
function isTlsCertificateMessage(msg) {
	return msg.includes("unable to get local issuer certificate") || msg.includes("unable_to_verify_leaf_signature") || msg.includes("self_signed_cert_in_chain") || msg.includes("self-signed certificate") || msg.includes("certificate has expired") || /cert_|err_tls|unable_to_get_issuer/.test(msg);
}
function isNetworkError(error) {
	if (error instanceof ChatConnectionError) return true;
	const msg = (error instanceof Error ? error.message : String(error ?? "")).toLowerCase();
	if (typeof DOMException !== "undefined" && error instanceof DOMException) return error.name === "AbortError" || error.name === "NetworkError" || msg.includes("network") || msg.includes("load failed") || msg.includes("offline");
	if (error instanceof TypeError) return msg.includes("failed to fetch") || msg.includes("networkerror") || msg.includes("network error") || msg.includes("load failed") || msg.includes("the internet connection appears to be offline") || msg.includes("network request failed") || msg.includes("fetch failed") || msg.includes("econnrefused") || msg.includes("socket hang up") || msg.includes("timeout") || isTlsCertificateMessage(msg);
	if (error instanceof Error) return msg.includes("network request failed") || msg.includes("cannot connect to server") || msg.includes("internet connection appears to be offline") || msg.includes("websocket not connected") || isTlsCertificateMessage(msg);
	const code = error?.code ?? error?.errno;
	if (typeof code === "string") return [
		"ECONNRESET",
		"ECONNREFUSED",
		"ENOTFOUND",
		"EAI_AGAIN",
		"ETIMEDOUT",
		"ENETUNREACH",
		"EHOSTUNREACH",
		"UNABLE_TO_GET_ISSUER_CERT_LOCALLY",
		"UNABLE_TO_VERIFY_LEAF_SIGNATURE",
		"CERT_HAS_EXPIRED",
		"DEPTH_ZERO_SELF_SIGNED_CERT",
		"SELF_SIGNED_CERT_IN_CHAIN"
	].includes(code.toUpperCase());
	return false;
}
function isTransientFetchError(error) {
	if (error instanceof RequestTimeoutError) return true;
	if (error instanceof BootstrapInterruptedError) return true;
	if (error instanceof Error && error.name === "AbortError") return true;
	if (isNetworkError(error)) return true;
	const message = error instanceof Error ? error.message : String(error ?? "");
	return isSuppressibleNetworkMessage(message);
}
//#endregion
//#region src/core/sync/api-client.ts
const REQUEST_TIMEOUT_SECONDS = 180;
const BODY_IDLE_TIMEOUT_MESSAGE = "Response body idle timeout";
const DEFAULT_BACKOFF_OPTIONS = {
	numOfAttempts: 10,
	startingDelay: 2e3,
	maxDelay: 3e4,
	jitter: "full"
};
const STREAM_BACKOFF_OPTIONS = {
	...DEFAULT_BACKOFF_OPTIONS,
	numOfAttempts: 7
};
const NO_BACKOFF = {
	numOfAttempts: 1,
	startingDelay: 0,
	maxDelay: 0
};
const retryWithBackoff = (attempt, backoff, retry) => {
	let attemptNumber = 0;
	return Effect.runPromise(Effect.retry(Effect.tryPromise({
		try: () => {
			attemptNumber++;
			return attempt();
		},
		catch: (error) => error
	}), {
		schedule: Schedule.modifyDelay(Schedule.exponential(backoff.startingDelay), ({ duration }) => Effect.succeed(Math.random() * Math.min(Duration.toMillis(duration), backoff.maxDelay))),
		while: (error) => Effect.promise(async () => {
			return await retry(error, attemptNumber) && attemptNumber < backoff.numOfAttempts;
		})
	}));
};
var ApiClient = class ApiClient {
	logger;
	sdk;
	constructor(options, sdk) {
		this.logger = options.logger;
		this.sdk = sdk;
	}
	getBaseUrl() {
		return this.sdk.options.apiBaseUrl;
	}
	async handleAuthError() {
		globalEventBus.emit(EventNames.AUTH_ERROR, { data: {
			error: "Unauthorized",
			code: "401"
		} });
	}
	/**
	* Fetch and decode one JSON response, with retries. `timeoutMs` is a total
	* deadline across all retries; without it each attempt gets the default
	* per-attempt deadline instead.
	*/
	async fetch(endpoint, init) {
		if (init?.timeoutMs === void 0) return this.fetchAndDecode(endpoint, init);
		return this.withTimeout((signal) => this.fetchAndDecode(endpoint, {
			...init,
			signal
		}), init.signal, init.timeoutMs);
	}
	/**
	* Fetch one request through `fetchResponseWithBackoff` and decode its JSON
	* or text body, publishing final authentication failure.
	*/
	async fetchAndDecode(endpoint, init) {
		let response;
		try {
			response = await this.fetchResponseWithBackoff(endpoint, init);
		} catch (error) {
			if (error instanceof AuthenticationError) {
				this.logger.error(error, {
					context: "fetch_authentication_final_failure",
					metadata: {
						message: "Authentication failed after all retries",
						endpoint,
						method: init?.method || "GET"
					}
				});
				await this.handleAuthError();
			}
			throw error;
		}
		if (response.status === 204) return void 0;
		if (!response.ok) await this.handleErrorResponse(response, this.extractChatIdFromBody(init?.body));
		const ct = response.headers.get("content-type")?.toLowerCase();
		if (!ct || !ct.includes("application/json")) return await response.text();
		return response.json();
	}
	/**
	* Retry transport and retryable HTTP failures, rebuilding the authenticated
	* request each time, and return the raw response. This is the request half
	* shared by `fetchAndDecode` and `fetchNdjson`. The default timeout applies
	* per attempt; a caller-supplied total deadline wraps all retries. The first
	* 401 calls the `refreshAccessToken` option and is retried at once.
	*/
	async fetchResponseWithBackoff(endpoint, init) {
		const { backoff, timeoutMs, ...requestInit } = init ?? {};
		const retryPredicate = this.makeRetryPredicate();
		let haveRetriesEnded = false;
		const backoffConfig = {
			...DEFAULT_BACKOFF_OPTIONS,
			...backoff
		};
		const send = async () => {
			const request = async (signal) => {
				const req = await this.composeRequest(endpoint, {
					...requestInit,
					signal
				});
				const response = await fetch(req.url, {
					...requestInit,
					method: req.method,
					headers: req.headers,
					body: req.body,
					signal: req.signal
				});
				const detail = response.status === 401 ? await this.readErrorDetail(response) : void 0;
				signal?.throwIfAborted();
				return {
					response,
					sentToken: req.accessToken,
					detail
				};
			};
			return timeoutMs === void 0 ? this.withTimeout(request, requestInit.signal, REQUEST_TIMEOUT_SECONDS * 1e3) : request(requestInit.signal ?? void 0);
		};
		const throwIfRetryableResponse = (response) => this.throwIfRetryable(response, String(endpoint), requestInit.method, backoffConfig.numOfAttempts > 1);
		let hasRefreshedToken = false;
		return retryWithBackoff(async () => {
			const { response, sentToken, detail } = await send();
			const { refreshAccessToken } = this.sdk.options;
			if (response.status !== 401 || hasRefreshedToken || !refreshAccessToken || requestInit.signal?.aborted) return throwIfRetryableResponse(response);
			hasRefreshedToken = true;
			let refreshed;
			try {
				refreshed = await this.refreshTokens(refreshAccessToken, sentToken, detail, requestInit.signal);
			} catch (refreshError) {
				haveRetriesEnded = true;
				throw refreshError;
			}
			if (refreshed === "session_ended") {
				haveRetriesEnded = true;
				return throwIfRetryableResponse(response);
			}
			return throwIfRetryableResponse((await send()).response);
		}, backoffConfig, async (error) => !requestInit.signal?.aborted && !haveRetriesEnded && retryPredicate(error));
	}
	/** The `detail` field of an error response's JSON body, or undefined when the body has none */
	async readErrorDetail(response) {
		const body = await response.json().catch(() => null);
		return this.asRecord(body)?.detail;
	}
	/** Thrown by a chat send's 401 after a token refresh, to re-send in the same attempt */
	static TOKEN_REFRESHED = Symbol("token refreshed");
	/** Bounded and cancelled as the request is, so a stalled refresh cannot hold it */
	refreshTokens(refreshAccessToken, rejectedToken, detail, signal) {
		return this.withTimeout(() => refreshAccessToken({
			rejectedToken,
			detail
		}), signal, REQUEST_TIMEOUT_SECONDS * 1e3, "Token refresh timed out");
	}
	/** Throw for retryable statuses; let callers classify endpoint-specific failures. */
	async throwIfRetryable(res, endpoint, method, delayForRetry = true) {
		if (res.status === 401) throw new AuthenticationError("Unauthorized");
		if (res.status === 429) {
			const retryAfter = parseRetryAfter(res.headers.get("Retry-After"));
			const err = new RateLimitError("Too many requests", retryAfter);
			this.logger.error(err, {
				context: "fetch_rate_limit",
				metadata: {
					endpoint,
					retryAfter,
					method: method || "GET"
				}
			});
			if (delayForRetry && err.retryAfter > 0) await new Promise((r) => setTimeout(r, err.retryAfter * 1e3));
			throw err;
		}
		if (!res.ok && (res.status >= 500 || res.status === 408)) throw new HttpError(res.status, res.statusText || `HTTP ${res.status}`);
		return res;
	}
	/**
	* Stream newline-delimited JSON in order. Cancellation remains active while
	* reading the response body.
	*/
	async fetchNdjson(endpoint, options) {
		const { onLine, parseLine, onBodyBytes, requestTimeoutMs, bodyIdleTimeoutMs, ...requestInit } = options;
		const callerSignal = requestInit.signal;
		const requestController = new AbortController();
		const abortFromCaller = () => requestController.abort(callerSignal?.reason ?? /* @__PURE__ */ new Error("Request aborted"));
		if (callerSignal?.aborted) abortFromCaller();
		else callerSignal?.addEventListener("abort", abortFromCaller, { once: true });
		const effectiveRequestTimeoutMs = requestTimeoutMs ?? REQUEST_TIMEOUT_SECONDS * 1e3;
		try {
			let response;
			try {
				response = await this.withTimeout(() => this.fetchResponseWithBackoff(endpoint, {
					...requestInit,
					timeoutMs: effectiveRequestTimeoutMs,
					signal: requestController.signal
				}), requestController.signal, effectiveRequestTimeoutMs);
			} catch (error) {
				const requestError = requestController.signal.aborted ? requestController.signal.reason ?? error : error;
				if (requestError instanceof AuthenticationError) {
					this.logger.error(requestError, {
						context: "fetch_ndjson_authentication_final_failure",
						metadata: {
							message: "Authentication failed after all retries",
							endpoint
						}
					});
					await this.handleAuthError();
				}
				throw requestError;
			}
			if (!response.ok) await this.handleErrorResponse(response, this.extractChatIdFromBody(requestInit.body));
			await this.consumeNdjsonStream(response, {
				onLine,
				parseLine,
				onBodyBytes,
				signal: requestController.signal,
				bodyIdleTimeoutMs
			});
		} catch (error) {
			if (!requestController.signal.aborted) requestController.abort(error);
			throw requestController.signal.reason ?? error;
		} finally {
			callerSignal?.removeEventListener("abort", abortFromCaller);
		}
	}
	async consumeNdjsonStream(response, options) {
		if (response.body) await this.readNdjsonFromStream(response.body, options);
	}
	async readNdjsonFromStream(body, options) {
		const reader = body.getReader();
		const decoder = new TextDecoder();
		let buffer = "";
		try {
			while (true) {
				const { done, value } = await this.withTimeout(() => reader.read(), options.signal, options.bodyIdleTimeoutMs, BODY_IDLE_TIMEOUT_MESSAGE);
				if (done) break;
				options.onBodyBytes?.(value.byteLength);
				buffer += decoder.decode(value, { stream: true });
				buffer = await this.drainNdjsonBuffer(buffer, options);
			}
			buffer += decoder.decode();
			if (buffer.trim()) await this.parseNdjsonLine(buffer, options);
		} catch (error) {
			await reader.cancel(error).catch(() => void 0);
			throw error;
		} finally {
			reader.releaseLock();
		}
	}
	async drainNdjsonBuffer(buffer, options) {
		let newlineIdx;
		while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
			const line = buffer.slice(0, newlineIdx);
			buffer = buffer.slice(newlineIdx + 1);
			if (line.trim()) await this.parseNdjsonLine(line, options);
		}
		return buffer;
	}
	async parseNdjsonLine(line, options) {
		let parsed;
		const parseStartedAt = options.onParseMs ? performance.now() : 0;
		if (options.parseLine) parsed = options.parseLine(line);
		else try {
			parsed = JSON.parse(line);
		} catch {
			this.logger.warn("NDJSON line failed to parse, skipping", {
				context: "fetch_ndjson_parse_error",
				metadata: { line: line.slice(0, 200) }
			});
			return;
		}
		options.onParseMs?.(performance.now() - parseStartedAt);
		await options.onLine(parsed);
		if (options.signal?.aborted) throw options.signal.reason ?? /* @__PURE__ */ new Error("Request aborted");
	}
	/**
	* Send one chat turn. The server answers 202 once it accepts the turn, and
	* the reply arrives elsewhere: over the websocket, or through sync while the
	* socket is down. Only a failure before that answer is retried
	*/
	async postChatTurn(endpoint, options) {
		const { body, headers: callerHeaders, baseUrl = this.getBaseUrl(), backoff, signal: callerSignal } = options;
		const method = "POST";
		const chatId = this.extractChatIdFromBody(body);
		const retryPredicate = this.makeRetryPredicate();
		const { refreshAccessToken } = this.sdk.options;
		let hasRefreshedToken = false;
		let haveRetriesEnded = false;
		const send = async () => {
			if (callerSignal?.aborted) throw new StreamCancellationError("Stream was cancelled by caller");
			const { headers, accessToken } = await this.composeRequest(endpoint, {
				method,
				body,
				headers: callerHeaders
			});
			try {
				await this.withTimeout(async (signal) => {
					const response = await fetch(`${baseUrl}/${endpoint}`, {
						method,
						body,
						headers,
						signal
					});
					if (response.status !== 401) {
						await this.throwIfChatTurnRefused(response, endpoint, chatId);
						return;
					}
					if (hasRefreshedToken || !refreshAccessToken || callerSignal?.aborted) throw new AuthenticationError("Authentication failed");
					hasRefreshedToken = true;
					let refreshed;
					try {
						refreshed = await this.refreshTokens(refreshAccessToken, accessToken, await this.readErrorDetail(response), callerSignal);
					} catch (refreshError) {
						haveRetriesEnded = true;
						throw refreshError;
					}
					if (refreshed === "session_ended") {
						haveRetriesEnded = true;
						throw new AuthenticationError("Authentication failed");
					}
					throw ApiClient.TOKEN_REFRESHED;
				}, callerSignal, REQUEST_TIMEOUT_SECONDS * 1e3);
			} catch (error) {
				if (callerSignal?.aborted) throw new StreamCancellationError("Stream was cancelled by caller");
				throw error;
			}
		};
		try {
			await retryWithBackoff(async () => {
				try {
					await send();
				} catch (error) {
					if (error !== ApiClient.TOKEN_REFRESHED) throw error;
					await send();
				}
			}, {
				...STREAM_BACKOFF_OPTIONS,
				...backoff
			}, async (error, attemptNumber) => {
				if (callerSignal?.aborted || haveRetriesEnded) return false;
				const shouldRetry = await retryPredicate(error);
				if (shouldRetry) {
					const errorName = error instanceof Error ? error.constructor.name : typeof error;
					const statusCode = error instanceof HttpError ? error.status : void 0;
					this.logger.warn(`Stream will retry (attempt ${attemptNumber}): ${errorName}`, {
						context: "stream_retry_decision",
						metadata: {
							endpoint,
							chatId,
							attempt: attemptNumber,
							errorType: errorName,
							statusCode
						}
					});
				}
				return shouldRetry;
			});
		} catch (error) {
			if (error instanceof AuthenticationError) {
				this.logger.error(error, {
					message: "Authentication failed after all retries",
					context: "stream_authentication_final_failure",
					metadata: {
						endpoint,
						method,
						hasBody: !!body
					}
				});
				await this.handleAuthError();
			}
			throw error;
		}
	}
	/** Throw for every answer to a chat turn but 202, the server's acceptance, mapping each status to its error */
	async throwIfChatTurnRefused(response, endpoint, chatId) {
		if (response.status === 202) {
			await response.body?.cancel().catch(() => void 0);
			return;
		}
		if (response.status === 429) {
			const retryAfter = parseRetryAfter(response.headers.get("Retry-After"));
			const err = new RateLimitError("Too many requests", retryAfter);
			this.logger.error(err, {
				context: "stream_rate_limit",
				metadata: {
					endpoint,
					message: "Rate limit error during stream",
					retryAfter,
					method: "POST"
				}
			});
			if (err.retryAfter > 0) await new Promise((r) => setTimeout(r, err.retryAfter * 1e3));
			throw err;
		}
		if (response.status === 408) throw new RequestTimeoutError("Request timeout");
		if (response.status === 400 || response.status === 409) {
			let data = null;
			try {
				data = await response.json();
			} catch {
				throw new HttpError(response.status, response.statusText || `HTTP ${response.status}`);
			}
			const rateLimitDetail = response.status === 400 ? this.parseRateLimitDetail(data, chatId) : null;
			if (rateLimitDetail) {
				this.emitRateLimitPaywall(rateLimitDetail);
				throw this.createFreeLimitExceededError(rateLimitDetail);
			}
			const busyRefusal = response.status === 409 ? this.parseBusyRefusal(data) : null;
			if (busyRefusal) throw busyRefusal;
			throw new HttpError(response.status, this.extractErrorMessage(data), this.extractUserErrorMessage(data));
		}
		if (response.status === 404) {
			const data = await response.json().catch(() => null);
			const userErrorMessage = this.extractUserErrorMessage(data);
			if (userErrorMessage) throw new HttpError(response.status, userErrorMessage, userErrorMessage);
		}
		await response.body?.cancel().catch(() => void 0);
		throw new HttpError(response.status, response.statusText || `HTTP ${response.status}`);
	}
	uploadFileWithProgress(uploadUrl, file, onProgress) {
		let cancelled = false;
		let currentXhr = null;
		const retryPredicate = this.makeRetryPredicate();
		const attemptUpload = async () => new Promise((resolve, reject) => {
			if (cancelled) return reject(/* @__PURE__ */ new Error("Upload cancelled"));
			const xhr = new XMLHttpRequest();
			currentXhr = xhr;
			xhr.open("PUT", uploadUrl, true);
			xhr.setRequestHeader("Content-Type", file.type);
			xhr.upload.onprogress = (e) => {
				if (e.lengthComputable && e.total > 0) onProgress({
					loaded: e.loaded,
					total: e.total
				});
			};
			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					currentXhr = null;
					resolve(new Response(null, {
						status: xhr.status,
						statusText: xhr.statusText
					}));
				} else if (xhr.status === 401) reject(new AuthenticationError("Unauthorized"));
				else if (xhr.status === 429) reject(new RateLimitError("Rate limited", parseRetryAfter(xhr.getResponseHeader("Retry-After"))));
				else if (xhr.status >= 500 && xhr.status < 600 || xhr.status === 408) reject(new HttpError(xhr.status, xhr.statusText || `HTTP ${xhr.status}`));
				else {
					currentXhr = null;
					reject(/* @__PURE__ */ new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
				}
			};
			xhr.onerror = () => {
				currentXhr = null;
				reject(/* @__PURE__ */ new TypeError("Network error during upload"));
			};
			xhr.onabort = () => {
				currentXhr = null;
				reject(/* @__PURE__ */ new Error("Upload cancelled"));
			};
			xhr.send(file);
		});
		return {
			promise: retryWithBackoff(attemptUpload, DEFAULT_BACKOFF_OPTIONS, async (error) => {
				if (cancelled) return false;
				if (error instanceof RateLimitError) {
					if (error.retryAfter > 0) await new Promise((r) => setTimeout(r, error.retryAfter * 1e3));
					return true;
				}
				return await retryPredicate(error);
			}).catch(async (error) => {
				if (error instanceof AuthenticationError) {
					this.logger.error(error, {
						context: "upload_authentication_final_failure",
						metadata: {
							message: "Authentication failed after all retries during upload",
							uploadUrl,
							fileSize: file.size,
							fileType: file.type,
							fileName: file.name
						}
					});
					await this.handleAuthError();
				}
				throw error;
			}),
			cancel: () => {
				cancelled = true;
				if (currentXhr) currentXhr.abort();
			}
		};
	}
	/** The headers every request sends, and the access token they carry, if any */
	async buildCommonHeaders() {
		const { getVersion, getAccessToken, getUserHeader } = this.sdk.options;
		const version = await getVersion();
		const accessToken = await getAccessToken() || void 0;
		const user = await getUserHeader?.() ?? {};
		return {
			headers: {
				...accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
				...user,
				"x-client-version": version,
				"x-platform": this.sdk.options.platform,
				...this.sdk.options.capabilities?.addCompressionHeader ? { "Accept-Encoding": "gzip" } : {}
			},
			accessToken
		};
	}
	withJsonBody(init) {
		if (!init?.body) return {
			headers: {},
			body: void 0
		};
		if (typeof init.body === "string" || init.body instanceof FormData) return {
			headers: typeof init.body === "string" ? { "Content-Type": "application/json" } : {},
			body: init.body
		};
		return {
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(init.body)
		};
	}
	async composeRequest(endpoint, init) {
		const { headers: common, accessToken } = await this.buildCommonHeaders();
		const callerHeaders = init?.headers instanceof Headers ? Object.fromEntries(init.headers.entries()) : Array.isArray(init?.headers) ? Object.fromEntries(init.headers) : init?.headers ?? {};
		const { headers: jsonHeaders, body } = this.withJsonBody(init);
		return {
			url: endpoint instanceof URL ? endpoint.href : `${this.getBaseUrl()}/${endpoint}`,
			method: init?.method ?? "GET",
			headers: {
				...common,
				...jsonHeaders,
				...callerHeaders
			},
			body,
			signal: init?.signal,
			accessToken
		};
	}
	async handleErrorResponse(res, chatId) {
		const data = await res.clone().json().catch(() => null);
		if (data !== null) {
			const rateLimitDetail = this.parseRateLimitDetail(data, chatId);
			if (rateLimitDetail) {
				this.emitRateLimitPaywall(rateLimitDetail);
				throw this.createFreeLimitExceededError(rateLimitDetail);
			}
			const quotaError = this.parseMeetingQuotaError(data);
			if (quotaError) throw quotaError;
			throw new HttpError(res.status, this.extractErrorMessage(data), this.extractUserErrorMessage(data), this.asRecord(this.asRecord(data)?.detail) ?? void 0);
		}
		const text = await res.text();
		throw new HttpError(res.status, text || "Unknown error");
	}
	/**
	* Run `operation` with the caller's signal linked and an optional deadline.
	* Rejects as soon as either fires, even if the operation never settles.
	*/
	async withTimeout(operation, signal, timeoutMs, timeoutMessage = "Request timed out") {
		if (signal?.aborted) throw signal.reason ?? /* @__PURE__ */ new Error("Request aborted");
		const controller = new AbortController();
		const attempt = Effect.tryPromise({
			try: () => operation(controller.signal),
			catch: (error) => error
		});
		const deadline = timeoutMs === void 0 ? attempt : Effect.timeoutOrElse(attempt, {
			duration: timeoutMs,
			orElse: () => {
				const timeout = new RequestTimeoutError(timeoutMessage);
				controller.abort(timeout);
				return Effect.fail(timeout);
			}
		});
		if (!signal) return Effect.runPromise(deadline);
		const callerSignal = signal;
		return Effect.runPromise(Effect.raceFirst(deadline, this.failWhenAborted(callerSignal, () => {
			const reason = callerSignal.reason ?? /* @__PURE__ */ new Error("Request aborted");
			controller.abort(reason);
			return reason;
		})));
	}
	/** Fails with `failure` the moment `signal` aborts */
	failWhenAborted(signal, failure) {
		if (!signal) return Effect.never;
		return Effect.callback((resume) => {
			const onAbort = () => resume(Effect.fail(failure()));
			signal.addEventListener("abort", onAbort, { once: true });
			return Effect.sync(() => signal.removeEventListener("abort", onAbort));
		});
	}
	isAbortError(error) {
		if (typeof DOMException !== "undefined" && error instanceof DOMException) return error.name === "AbortError";
		if (error instanceof Error) return error.name === "AbortError" || error.message.toLowerCase().includes("aborted");
		return false;
	}
	emitRateLimitPaywall(detail) {
		if (detail.errorCode === "paid_rate_limit") return;
		globalEventBus.emit(EventNames.PAYWALL_SHOW_BANNER, { data: {
			kind: "rate_limit_reset",
			nextAllowedTime: detail.nextAllowedTime,
			errorCode: detail.errorCode,
			errorMessage: detail.errorMessage,
			messageLimit: detail.messageLimit,
			chatId: detail.chatId
		} });
	}
	createFreeLimitExceededError(detail) {
		return new FreeLimitExceededError({
			errorCode: detail.errorCode,
			nextAllowedTime: detail.nextAllowedTime,
			errorMessage: detail.errorMessage,
			messageLimit: detail.messageLimit,
			chatId: detail.chatId
		});
	}
	parseRateLimitDetail(payload, chatId) {
		const data = this.asRecord(payload);
		const detail = this.asRecord(data?.detail);
		if (!detail) return null;
		const nextAllowedTime = this.asNonEmptyString(detail.next_allowed_time);
		if (!nextAllowedTime) return null;
		const errorCode = this.asNonEmptyString(detail.error_code);
		const rawErrorMessage = this.asOptionalString(detail.user_error_message);
		const messageLimit = this.asOptionalFiniteNumber(detail.message_limit);
		const hasErrorCodeField = Object.prototype.hasOwnProperty.call(detail, "error_code");
		if (errorCode) return {
			errorCode,
			nextAllowedTime,
			errorMessage: this.resolveRateLimitErrorMessage(errorCode, rawErrorMessage),
			messageLimit,
			chatId
		};
		if (hasErrorCodeField) return null;
		if (messageLimit === void 0) return null;
		const legacyErrorCode = this.inferLegacyRateLimitErrorCode(rawErrorMessage);
		return {
			errorCode: legacyErrorCode,
			nextAllowedTime,
			errorMessage: this.resolveRateLimitErrorMessage(legacyErrorCode, rawErrorMessage),
			messageLimit,
			chatId
		};
	}
	inferLegacyRateLimitErrorCode(errorMessage) {
		if (errorMessage?.toLowerCase().includes("research")) return "research_daily_limit";
		return "chat_daily_limit";
	}
	resolveRateLimitErrorMessage(errorCode, errorMessage) {
		if (errorMessage) return errorMessage;
		if (errorCode === "research_daily_limit") return "Max daily limit reached. Upgrade to Pro for more Max messages.";
		if (errorCode === "image_generation_daily_limit") return "You've reached your daily image generation limit. Your limit resets at midnight.";
		return "You've used the daily chats included in your free plan. Upgrade now for expanded chat access.";
	}
	/** Only the copy the server marked user-facing, never a raw or framework detail */
	extractUserErrorMessage(payload) {
		const data = this.asRecord(payload);
		const detail = this.asRecord(data?.detail);
		return this.asOptionalString(detail?.user_error_message);
	}
	extractErrorMessage(payload) {
		const data = this.asRecord(payload);
		if (!data) return "Unknown error";
		const detail = data.detail;
		if (typeof detail === "string") return detail;
		const detailRecord = this.asRecord(detail);
		const userError = this.asOptionalString(detailRecord?.user_error_message);
		if (userError) return userError;
		return this.asOptionalString(data.message) ?? "Unknown error";
	}
	parseMeetingQuotaError(payload) {
		const data = this.asRecord(payload);
		const detail = this.asRecord(data?.detail);
		if (!detail) return null;
		const errorCode = this.asNonEmptyString(detail.error_code);
		if (errorCode !== "meeting_quota_exceeded") return null;
		return new MeetingQuotaExceededError(errorCode, this.asOptionalString(detail.user_error_message) ?? "Meeting limit reached.");
	}
	/** A chat turn's busy refusal, whose 409 `detail` holds the fields of the socket's busy refusal */
	parseBusyRefusal(payload) {
		const detail = this.asRecord(this.asRecord(payload)?.detail);
		if (detail?.chat_busy !== true) return null;
		return new ChatBusyError(this.asOptionalString(detail.conflict_owner_message_id));
	}
	asRecord(value) {
		if (!value || typeof value !== "object" || Array.isArray(value)) return null;
		return value;
	}
	asNonEmptyString(value) {
		if (typeof value !== "string") return null;
		const trimmed = value.trim();
		return trimmed.length > 0 ? trimmed : null;
	}
	asOptionalString(value) {
		return typeof value === "string" ? value : void 0;
	}
	asOptionalFiniteNumber(value) {
		return typeof value === "number" && Number.isFinite(value) ? value : void 0;
	}
	extractChatIdFromBody(body) {
		if (body instanceof FormData) {
			const chatId = body.get("chat_id");
			return typeof chatId === "string" ? chatId : void 0;
		}
		if (typeof body === "string") try {
			const parsed = JSON.parse(body);
			if (typeof parsed?.chat_id === "string") return parsed.chat_id;
		} catch {}
	}
	makeRetryPredicate() {
		return async (error) => {
			if (error instanceof FreeLimitExceededError) return false;
			if (error instanceof StreamCancellationError) return false;
			if (error instanceof MeetingQuotaExceededError) return false;
			if (error instanceof RequestTimeoutError) return true;
			if (error instanceof AuthenticationError) return true;
			if (error instanceof RateLimitError) return true;
			if (error instanceof HttpError) return error.status >= 500 && error.status < 600 || error.status === 408;
			if (isNetworkError(error)) {
				try {
					this.sdk.options.onNetworkIssue?.();
				} catch {}
				return true;
			}
			if (this.isAbortError(error)) return true;
			return false;
		};
	}
};
//#endregion
//#region src/core/api-error.ts
function extractBackendErrorCode(body) {
	if (typeof body !== "object" || body === null) return void 0;
	const detail = body.detail;
	if (typeof detail !== "object" || detail === null) return void 0;
	const errorCode = detail.error_code;
	return typeof errorCode === "string" ? errorCode : void 0;
}
//#endregion
//#region src/core/connection-card-completion.ts
const CONNECTION_CARD_COMPLETION_NAMESPACE = "e0ac2bc3-9b4f-43c0-a593-c94a32ff2731";
/**
* Stable message identity doubles as a synced receipt for one card's continuation.
* Every client must land on the same id, so this derivation is a cross-platform contract.
*/
function connectionCardCompletionMessageId(threadId, cardKey) {
	return v5(`${threadId}:${cardKey}`, CONNECTION_CARD_COMPLETION_NAMESPACE);
}
//#endregion
//#region src/core/file-links.ts
const FileLinkMetaSchema = z.object({
	file_id: z.string().uuid(),
	filename: z.string(),
	content_type: z.string(),
	size: z.number().int().nonnegative(),
	url: z.string().url(),
	public_until: z.string().datetime()
});
const resolutionCaches = /* @__PURE__ */ new WeakMap();
function resolutionCacheFor(sdk) {
	const existing = resolutionCaches.get(sdk);
	if (existing) return existing;
	const cache = /* @__PURE__ */ new Map();
	resolutionCaches.set(sdk, cache);
	return cache;
}
async function fetchFileLinkMeta(sdk, token) {
	try {
		const raw = await sdk.apiClient.fetch(`f/${token}/meta`);
		const meta = FileLinkMetaSchema.parse(raw);
		return {
			status: "ready",
			fileId: meta.file_id,
			filename: meta.filename,
			contentType: meta.content_type,
			size: meta.size
		};
	} catch (error) {
		if (error instanceof AuthenticationError || error instanceof HttpError && [
			401,
			403,
			404
		].includes(error.status)) return { status: "unavailable" };
		throw error;
	}
}
/** Resolve each immutable link token once per SDK instance. */
function resolveFileLinkMeta(sdk, token) {
	const cache = resolutionCacheFor(sdk);
	const existing = cache.get(token);
	if (existing) return existing;
	const pending = fetchFileLinkMeta(sdk, token);
	cache.set(token, pending);
	pending.then(void 0, () => {
		if (cache.get(token) === pending) cache.delete(token);
	});
	return pending;
}
//#endregion
//#region src/core/connection-lifecycle/connection-lifecycle.ts
function toError(value) {
	return value instanceof Error ? value : new Error(String(value));
}
/**
* ConnectionLifecycle owns app suspend/resume orchestration for the SDK.
*
* ### States
*
* | State         | Description                                                     |
* |---------------|-----------------------------------------------------------------|
* | Uninitialized | Services not up yet; app signals are ignored.                   |
* | ServicesReady | Services initialized, transport not yet healthy.                |
* | Connecting    | First connect or full reconnect in progress (bootstrap + WS).   |
* | Ready         | Polling allowed; WebSocket healthy enough for product use.      |
* | Suspended     | App away; polling stopped and transport torn down.              |
* | Recovering    | Resume in progress: drain, refresh, WS, then bootstrap.         |
* | Degraded      | Soft failure; realtime is up and sync keeps retrying.           |
* | Failed        | Hard failure; needs a deliberate reconnect.                     |
*
* `inactive` (Control Center, dialogs, Face ID) is not time away. The app is
* still on screen, so polling continues. Only `background` suspends.
*
* ### Guarantees
*
* - Transitions are serialized, so suspend and resume can never interleave.
* - Public methods never reject for domain failures; callers read outcomes off
*   `snapshot` instead.
* - `startPolling` is the only dependency allowed to decide an outcome. Every
*   other dependency failure is masked, so no throw can strand the machine in
*   `Connecting` or `Recovering`.
* - Every settled outcome writes SdkState, so a caller that flipped it to
*   `connecting` is never left waiting on a transition that no-ops.
* - Partial sessions opt out of the FSM's connect/suspend/resume model.
*   `notifyLogout` still runs (logout ends the session) and always stops
*   transport, even from `Uninitialized`. Transport for a partial session
*   comes up via `startPollingAndWebSocket` and down via
*   `stopPollingAndWebSocket` or `notifyLogout`; the FSM never tracks it as a state.
*/
var ConnectionLifecycle = class {
	#deps;
	#state = "Uninitialized";
	#stateEnteredAtMs = Date.now();
	#lastTransitionReason = "init";
	#lastError;
	#queue = Promise.resolve();
	constructor(deps) {
		this.#deps = deps;
	}
	notifyServicesReady() {
		if (this.#deps.isPartialMode()) return;
		if (this.#state !== "Uninitialized") return;
		this.transition("ServicesReady", "init", "services_ready");
	}
	beginConnect() {
		return this.run("init", () => this.beginConnectInner());
	}
	onBackground() {
		this.abortInFlightBootstrap();
		return this.run("background", () => this.onBackgroundInner());
	}
	onActive(context) {
		return this.run("active", () => this.onActiveInner(context));
	}
	notifyLogout() {
		this.abortInFlightBootstrap();
		return this.runUngated("none", () => this.notifyLogoutInner());
	}
	notifySyncRecovered() {
		if (this.#deps.isPartialMode()) return;
		if (this.#state !== "Degraded") return;
		this.transition("Ready", "none", "sync_recovered");
	}
	startPollingAndWebSocket() {
		return this.runUngated("init", async () => {
			await this.#deps.startPolling();
			await this.runDependency("connectWebSocket", () => this.#deps.connectWebSocket());
		});
	}
	stopPollingAndWebSocket() {
		return this.runUngated("none", async () => {
			this.#deps.stopPolling();
			await this.runDependency("disconnectWebSocket", () => this.#deps.disconnectWebSocket());
		});
	}
	get snapshot() {
		const bootstrapMode = this.#deps.getBootstrapMode();
		return {
			state: this.#state,
			stateEnteredAtMs: this.#stateEnteredAtMs,
			lastTransitionReason: this.#lastTransitionReason,
			...this.#lastError ? { lastError: this.#lastError } : {},
			...bootstrapMode !== "unknown" ? { bootstrapMode } : {}
		};
	}
	async beginConnectInner() {
		if (this.#state === "Ready" || this.#state === "Degraded") {
			this.#deps.setSdkReady();
			return;
		}
		if (this.#state !== "ServicesReady" && this.#state !== "Failed") return;
		this.#deps.setSdkConnecting();
		this.transition("Connecting", "init", "begin_connect");
		try {
			await this.#deps.startPolling({ surfaceSoftInterrupt: true });
			await this.runDependency("connectWebSocket", () => this.#deps.connectWebSocket());
			this.transition("Ready", "init", "connect_success");
			this.#deps.setSdkReady();
		} catch (error) {
			await this.settleConnectFailure(error, "init", "connect_failed");
		}
	}
	async onBackgroundInner() {
		if (!(this.#state === "Ready" || this.#state === "Degraded" || this.#state === "Connecting")) return;
		this.#deps.stopPolling();
		await this.tearDownTransport();
		this.transition("Suspended", "background", "app_backgrounded");
	}
	async onActiveInner(context) {
		if (!(this.#state === "Suspended" || this.#state === "Failed" || this.#state === "Degraded")) {
			this.#deps.logger.debug("lifecycle.onActive.ignored", {
				state: this.#state,
				inactiveMs: context.inactiveMs
			});
			return;
		}
		await this.resume(context);
	}
	async notifyLogoutInner() {
		this.#deps.stopPolling();
		await this.tearDownTransport();
		if (this.#state === "Uninitialized") return;
		this.transition("ServicesReady", "none", "logout");
	}
	async resume(context) {
		this.#deps.setSdkResuming(true);
		try {
			this.transition("Recovering", "active", "app_active", { inactiveMs: context.inactiveMs });
			await this.runDependency("cancelInFlightBootstrap", () => this.#deps.cancelInFlightBootstrap());
			const onLongInactive = this.#deps.onLongInactive;
			if (context.inactiveMs >= 18e4 && onLongInactive) await this.runDependency("onLongInactive", onLongInactive, { inactiveMs: context.inactiveMs });
			await this.runDependency("ensureWebSocketConnected", () => this.#deps.ensureWebSocketConnected());
			await this.settleResume("resume_success", "resume_failed");
		} finally {
			this.#deps.setSdkResuming(false);
		}
	}
	async settleResume(successReason, failureReason) {
		try {
			await this.#deps.startPolling({ surfaceSoftInterrupt: true });
			this.transition("Ready", "active", successReason);
			this.#deps.setSdkReady();
		} catch (error) {
			await this.settleConnectFailure(error, "active", failureReason);
		}
	}
	async settleConnectFailure(error, event, reason) {
		const err = toError(error);
		if (this.#deps.classifyBootstrapFailure(error) === "hard") {
			this.failHard(err, event, reason);
			return;
		}
		await this.runDependency("connectWebSocket", () => this.#deps.connectWebSocket());
		this.#lastError = {
			name: err.name,
			message: err.message
		};
		this.transition("Degraded", event, reason);
		this.#deps.setSdkReady();
		this.#deps.logger.warn("lifecycle.bootstrap.soft_failure", {
			error: this.#lastError,
			tags: this.failureTags("Degraded", reason),
			metadata: { event }
		});
	}
	failHard(err, event, reason) {
		this.#lastError = {
			name: err.name,
			message: err.message
		};
		this.transition("Failed", event, reason);
		this.#deps.logger.error(err, {
			tags: this.failureTags("Failed", reason),
			metadata: { event }
		});
		this.#deps.setSdkError(err);
	}
	failureTags(state, reason) {
		return {
			lifecycle_state: state,
			lifecycle_reason: reason,
			bootstrap_mode: this.#deps.getBootstrapMode()
		};
	}
	transition(to, event, reason, extraMeta) {
		const bootstrapMode = this.#deps.getBootstrapMode();
		this.#deps.logger.info("lifecycle.transition", {
			from: this.#state,
			to,
			event,
			reason,
			...bootstrapMode !== "unknown" ? { bootstrapMode } : {},
			...extraMeta
		});
		this.#state = to;
		this.#stateEnteredAtMs = Date.now();
		this.#lastTransitionReason = reason;
		if (to === "Ready") this.#lastError = void 0;
	}
	run(event, step) {
		return this.runUngated(event, async () => {
			if (this.#deps.isPartialMode()) return;
			await step();
		});
	}
	runUngated(event, step) {
		const guarded = async () => {
			try {
				await step();
			} catch (error) {
				this.failHard(toError(error), event, "unexpected_error");
			}
		};
		const next = this.#queue.then(guarded, guarded);
		this.#queue = next.then(() => void 0, () => void 0);
		return next;
	}
	async tearDownTransport() {
		await this.runDependency("disconnectWebSocket", () => this.#deps.disconnectWebSocket());
		await this.runDependency("cancelInFlightBootstrap", () => this.#deps.cancelInFlightBootstrap());
	}
	abortInFlightBootstrap() {
		this.#deps.cancelInFlightBootstrap().catch(() => void 0);
	}
	async runDependency(label, call, extraMeta) {
		try {
			await call();
		} catch (error) {
			const err = toError(error);
			this.#deps.logger.warn(`lifecycle.${label}.error`, {
				error: {
					name: err.name,
					message: err.message
				},
				...extraMeta
			});
		}
	}
};
//#endregion
//#region src/core/search/search-adapter.ts
const INDEXABLE_TABLES = /* @__PURE__ */ new Set([
	"threads",
	"messages",
	"journals",
	"meetings",
	"files"
]);
function isIndexableTable(table) {
	return INDEXABLE_TABLES.has(table);
}
//#endregion
//#region src/core/search/search-store.ts
const CACHE_SIZE = 10;
var SearchStore = class {
	query = "";
	results = [];
	isLoading = false;
	isIndexing = false;
	hasIndexed = false;
	/** A rebuild is owed, so whatever is indexed now is partial */
	isRebuildPending = false;
	error = null;
	cache = /* @__PURE__ */ new Map();
	adapter;
	logger;
	searchFlow = null;
	constructor(adapter, options) {
		this.adapter = adapter;
		this.logger = options?.logger;
		makeObservable(this, {
			query: observable,
			results: observable.shallow,
			isLoading: observable,
			isIndexing: observable,
			hasIndexed: observable,
			isRebuildPending: observable,
			error: observable.ref,
			cache: observable.shallow,
			hasResults: computed,
			isEmpty: computed,
			cacheSize: computed,
			isCached: computed,
			isSearchReady: computed,
			search: action,
			clear: action,
			setQuery: action,
			clearCache: action,
			clearAll: action,
			initIndexedState: action
		});
	}
	get hasResults() {
		return this.results.length > 0;
	}
	get isEmpty() {
		return !this.isLoading && this.results.length === 0 && this.query.length > 0;
	}
	get cacheSize() {
		return this.cache.size;
	}
	get isCached() {
		const normalizedQuery = this.query.trim().toLowerCase();
		return this.cache.has(normalizedQuery);
	}
	get isSearchReady() {
		return this.hasIndexed && !this.isIndexing && !this.isRebuildPending;
	}
	getCached(query) {
		const normalizedQuery = query.trim().toLowerCase();
		if (!this.cache.has(normalizedQuery)) return;
		const results = this.cache.get(normalizedQuery);
		this.cache.delete(normalizedQuery);
		this.cache.set(normalizedQuery, results);
		return results;
	}
	setCache(query, results) {
		const normalizedQuery = query.trim().toLowerCase();
		if (this.cache.has(normalizedQuery)) this.cache.delete(normalizedQuery);
		if (this.cache.size >= CACHE_SIZE) {
			const oldestKey = this.cache.keys().next().value;
			if (oldestKey) this.cache.delete(oldestKey);
		}
		this.cache.set(normalizedQuery, results);
	}
	clearCache() {
		this.cache.clear();
	}
	hasCached(query) {
		return this.cache.has(query.trim().toLowerCase());
	}
	async initIndexedState() {
		try {
			const [lastIndexedAt, rebuildPending] = await Promise.all([this.adapter.getLastIndexedAt(), this.adapter.isFullReindexPending()]);
			runInAction(() => {
				this.isRebuildPending = rebuildPending;
				if (lastIndexedAt) this.hasIndexed = true;
			});
		} catch (err) {
			this.logger?.debug("Failed to read search index metadata", {
				metadata: { error: err },
				context: "SearchStore:initIndexedState"
			});
		}
	}
	setQuery(query) {
		this.query = query;
	}
	search(query) {
		if (this.searchFlow) this.searchFlow.cancel();
		this.query = query;
		this.error = null;
		if (!query.trim()) {
			this.results = [];
			this.isLoading = false;
			return;
		}
		const cached = this.getCached(query);
		if (cached) {
			this.results = cached;
			this.isLoading = false;
			return;
		}
		const search = this.performSearch(query);
		this.searchFlow = search;
		search.catch((error) => {
			if (error instanceof Error && isFlowCancellationError(error)) return;
			this.logger?.error(error instanceof Error ? error : String(error), { context: "SearchStore:search" });
		});
	}
	clear() {
		if (this.searchFlow) {
			this.searchFlow.cancel();
			this.searchFlow = null;
		}
		this.query = "";
		this.results = [];
		this.error = null;
		this.isLoading = false;
	}
	clearAll() {
		this.clear();
		this.clearCache();
		this.hasIndexed = false;
	}
	async reindexItems(input) {
		const { removeIds, addItems } = input;
		try {
			if (addItems.length > 0) await this.adapter.add(addItems);
			const addIds = new Set(addItems.map((item) => item.id));
			const idsToRemove = removeIds.filter((id) => !addIds.has(id));
			if (idsToRemove.length > 0) await this.adapter.remove(idsToRemove);
			if (addItems.length > 0 || idsToRemove.length > 0) runInAction(() => {
				this.hasIndexed = true;
			});
		} finally {
			this.clearCache();
		}
	}
	async syncChanges(options) {
		try {
			await this.adapter.syncChanges(options);
			runInAction(() => {
				this.hasIndexed = true;
			});
		} finally {
			this.clearCache();
		}
	}
	performSearch = flow(function* (query) {
		this.isLoading = true;
		this.error = null;
		try {
			const results = yield this.adapter.search(query);
			if (this.query === query) {
				this.results = results;
				this.setCache(query, results);
			}
		} catch (e) {
			if (e instanceof Error) this.error = e;
		} finally {
			this.isLoading = false;
			this.searchFlow = null;
		}
	});
	startIndexing = flow(function* (options) {
		this.isIndexing = true;
		if (options?.fullReindex) this.isRebuildPending = true;
		try {
			yield this.adapter.indexData(options);
			this.hasIndexed = true;
			this.isRebuildPending = false;
			this.clearCache();
		} catch (e) {
			if (e instanceof Error) this.error = e;
			throw e;
		} finally {
			this.isIndexing = false;
		}
	});
};
//#endregion
//#region src/core/services/stream-sequence.ts
const MAX_TRACKED_STREAMS = 64;
var StreamSequenceTracker = class {
	lastSeq = /* @__PURE__ */ new Map();
	reported = /* @__PURE__ */ new Set();
	observe(stream, seq) {
		if (typeof stream !== "string" || typeof seq !== "number" || !Number.isInteger(seq)) return { drop: false };
		const previous = this.lastSeq.get(stream);
		if (previous === void 0) return this.trackNewStream(stream, seq);
		const expectedSeq = previous + 1;
		const duplicate = seq < expectedSeq;
		if (!duplicate) {
			this.lastSeq.delete(stream);
			this.lastSeq.set(stream, seq);
		}
		if (seq === expectedSeq) return { drop: false };
		return {
			drop: duplicate,
			anomaly: duplicate ? "duplicate" : "gap",
			expectedSeq,
			report: this.markReported(stream)
		};
	}
	end(stream) {
		if (stream === void 0) return;
		this.lastSeq.delete(stream);
		this.reported.delete(stream);
	}
	trackNewStream(stream, seq) {
		if (this.lastSeq.size >= MAX_TRACKED_STREAMS) this.end(this.lastSeq.keys().next().value);
		this.lastSeq.set(stream, seq);
		if (seq === 0) return { drop: false };
		return {
			drop: false,
			anomaly: "late_join",
			expectedSeq: 0,
			report: this.markReported(stream)
		};
	}
	markReported(stream) {
		if (this.reported.has(stream)) return false;
		this.reported.add(stream);
		return true;
	}
};
//#endregion
//#region src/core/services/transport.ts
const RECONCILE_DELAY_MS = 1e3;
const ANALYTICS_TYPE_BY_ANOMALY = {
	gap: "chunk_gap",
	duplicate: "chunk_duplicate",
	late_join: "chunk_late_join"
};
function classifyWsErrorReason(message) {
	if (!message) return void 0;
	if (/ENOTFOUND/.test(message)) return "ENOTFOUND";
	if (/ECONNREFUSED|ETIMEDOUT|ECONNRESET|EHOSTUNREACH|ENETUNREACH/.test(message)) return "CONNECTION_ERROR";
	if (/Unexpected server response: \d{3}/.test(message)) return "UPGRADE_REJECTED";
	if (/(?:handshake|WebSocket was closed during handshake)/i.test(message)) return "HANDSHAKE_TIMEOUT";
	if (/(?:CERT_|UNABLE_TO_VERIFY|UNABLE_TO_GET_ISSUER_CERT|SELF_SIGNED_CERT_IN_CHAIN|ERR_TLS|unable to get (?:local )?issuer certificate)/i.test(message)) return "TLS_ERROR";
}
function classifyReasonFromError(error) {
	let current = error;
	for (let depth = 0; depth < 4 && current !== void 0; depth += 1) {
		const reason = classifyWsErrorReason(current instanceof Error ? current.message : String(current ?? ""));
		if (reason) return reason;
		current = current instanceof Error ? current.cause : void 0;
	}
}
var TransportManager = class {
	sdk;
	wsAvailable = true;
	streamsNeedingReconcile = /* @__PURE__ */ new Map();
	streamTracker = new StreamSequenceTracker();
	reconcileTimer = null;
	disposer = null;
	lastWsUnavailableReason;
	constructor(sdk) {
		this.sdk = sdk;
		makeObservable(this, {
			wsAvailable: observable,
			setWsAvailability: action.bound
		});
	}
	start() {
		if (this.disposer) return;
		this.disposer = reaction(() => this.collectTerminalThreadIds(), (terminalIds) => {
			for (const id of terminalIds) for (const [stream, chatId] of this.streamsNeedingReconcile) if (chatId === id) this.streamsNeedingReconcile.delete(stream);
		}, {
			fireImmediately: true,
			name: "transport.cleanup-on-terminal",
			equals: comparer.structural
		});
	}
	collectTerminalThreadIds() {
		const ids = [];
		for (const t of this.sdk.stores.threads.data.values()) if (t.status === "done" || t.status === "errored" || t.status === "stopped") ids.push(t.id);
		return ids;
	}
	setWsAvailability(available, errorMessage) {
		if (this.wsAvailable === available) return;
		this.wsAvailable = available;
		const reason = available ? void 0 : classifyWsErrorReason(errorMessage);
		this.lastWsUnavailableReason = reason;
		if (!available) this.sdk.logger.warn("WS unavailable, chat replies will arrive once WS recovers or through sync", {
			reason,
			errorMessage
		});
		else this.sdk.logger.info("WS recovered");
	}
	/**
	* Send one chat turn. The server answers 202 and delivers the reply over the
	* websocket. With the websocket down, the server still runs the turn, and
	* the reply arrives once the socket reconnects or through sync. While the
	* websocket is down, a `ChatConnectionError` wraps every failure but a
	* cancel, so the server's answer, such as `ChatBusyError` for a busy chat,
	* is its cause
	*/
	async sendChat({ endpoint, body, headers, signal, baseUrl }) {
		if (this.wsAvailable) await this.ensureWsConnection();
		try {
			await this.sdk.apiClient.postChatTurn(endpoint, {
				body,
				headers,
				baseUrl,
				signal
			});
		} catch (err) {
			if (!this.wsAvailable && !(err instanceof StreamCancellationError)) throw new ChatConnectionError(this.lastWsUnavailableReason ?? classifyReasonFromError(err) ?? "UNKNOWN", { cause: err });
			throw err;
		}
	}
	/**
	* Sequence-check one WS chunk. Returns true for a duplicate to drop.
	* Any anomaly marks the stream for reconciliation once it ends; syncing
	* earlier is wasted because incoming rows are skipped while the streamed
	* message is unfinished.
	*/
	observeWsChunk(event) {
		const result = this.streamTracker.observe(event.stream, event.seq);
		if (result.anomaly && event.stream !== void 0) this.streamsNeedingReconcile.set(event.stream, event.chat_id);
		if (result.report && result.anomaly) {
			this.sdk.logger.warn("WS chat stream sequence anomaly", {
				context: "StreamSequence",
				metadata: {
					chatId: event.chat_id,
					stream: event.stream,
					expectedSeq: result.expectedSeq,
					receivedSeq: event.seq,
					kind: result.anomaly
				}
			});
			globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_RESPONSE_EVENT, { data: {
				chat_context_type: ANALYTICS_TYPE_BY_ANOMALY[result.anomaly],
				chat_id: event.chat_id,
				message_id: event.message_id ?? ""
			} });
		}
		if (result.drop) return true;
		if ((event.event_type === "done" || event.event_type === "error") && event.stream !== void 0) {
			this.streamTracker.end(event.stream);
			if (this.streamsNeedingReconcile.delete(event.stream)) this.scheduleReconcile();
		}
		return false;
	}
	scheduleReconcile() {
		if (this.reconcileTimer) return;
		this.reconcileTimer = setTimeout(() => {
			this.reconcileTimer = null;
			this.sdk.syncNow().catch(() => {});
		}, RECONCILE_DELAY_MS);
	}
	stop() {
		this.disposer?.();
		this.disposer = null;
		if (this.reconcileTimer) clearTimeout(this.reconcileTimer);
		this.reconcileTimer = null;
	}
	async ensureWsConnection() {
		try {
			return await this.sdk.wsManager.ensureConnection(5e3);
		} catch {
			return false;
		}
	}
};
//#endregion
//#region src/core/utils/timer.ts
const sleep = async (timeMs) => {
	return new Promise((resolve) => setTimeout(resolve, timeMs));
};
const worker = `(function workerSetup() {
      function ticker() {
        self.postMessage('tick');
      }
      self.onmessage = function (event) {
        const [data, time] = event.data;
        switch (data) {
          case 'start':
            setTimeout(ticker, time);
            break;
          default:
            break;
        }
      };
    })()`;
/**
* worker sleep to not get throttled by the browser, when the
* tab is inactive
*/
const workerSleep = async (ms) => {
	if (ms < 0) throw Error("`ms` should be a positive integer");
	if (typeof Worker === "undefined") return sleep(ms);
	const url = URL.createObjectURL(new Blob([worker], { type: "application/javascript" }));
	return new Promise((resolve) => {
		const WorkerThread = new Worker(url);
		WorkerThread.postMessage(["start", ms]);
		WorkerThread.onmessage = (event) => {
			if (event.data === "tick") {
				resolve();
				WorkerThread.terminate();
			}
		};
	}).finally(() => URL.revokeObjectURL(url));
};
//#endregion
//#region src/core/services/ws-message-handlers.ts
function parseChunk(chunk) {
	const rawData = typeof chunk === "object" && chunk !== null && "data" in chunk ? chunk.data : chunk;
	return typeof rawData === "string" ? JSON.parse(rawData) : rawData;
}
function processChunk(chunk, stores, transport) {
	const parsed = parseChunk(chunk);
	if (parsed.chat_id && transport.observeWsChunk(parsed)) return;
	stores.messages.onMessage({
		threadId: parsed.chat_id,
		isMetaSlashCommand: !!parsed.is_command,
		response: parsed
	});
}
function handleChatMessage(chunk, stores, transport, logger) {
	try {
		processChunk(chunk, stores, transport);
	} catch (err) {
		logger.error("WebSocket: Error parsing chat message", {
			error: err,
			chunk
		});
	}
}
function handleChatMessageBatch(data, stores, transport, logger) {
	if (!data?.chunks || !Array.isArray(data.chunks)) {
		logger.warn("WebSocket: Invalid batch payload", { data });
		return;
	}
	runInAction(() => {
		for (const chunk of data.chunks) try {
			processChunk(chunk, stores, transport);
		} catch (err) {
			logger.error("WebSocket: Error parsing batch chunk", {
				error: err,
				chunk
			});
		}
	});
}
function handleServerError(error, logger) {
	let errorMessage = "Unknown error";
	if (typeof error === "object" && error !== null && "error" in error) errorMessage = String(error.error);
	logger.error("WebSocket: Server error", { error: errorMessage });
	const isTokenError = errorMessage === "MISSING_TOKEN" || errorMessage === "INVALID_TOKEN";
	if (isTokenError) logger.error("WebSocket: Invalid token, triggering reconnect");
	return { shouldReconnect: isTokenError };
}
function parseActionCardsChangedPayload(data) {
	const cardId = typeof data === "object" && data !== null ? data.card_id : void 0;
	return typeof cardId === "string" ? { card_id: cardId } : {};
}
//#endregion
//#region src/core/services/websocket.ts
const WS_CLOSE_ABNORMAL = 1006;
const WS_CLOSE_GOING_AWAY = 1002;
const WS_CLOSE_UNSUPPORTED = 1003;
let WSMethods = /* @__PURE__ */ function(WSMethods) {
	WSMethods["PING"] = "ping";
	WSMethods["CHAT_MESSAGE"] = "chat_message";
	WSMethods["CHAT_MESSAGE_BATCH"] = "chat_message_batch";
	WSMethods["PUSH_NOTIFICATION"] = "push_notification";
	WSMethods["CHAT_COST_STEP"] = "chat.cost_step";
	WSMethods["ACTION_CARDS_CHANGED"] = "action_cards:changed";
	WSMethods["CHATS_CHANGED"] = "chats:changed";
	WSMethods["PROJECTS_CHANGED"] = "projects:changed";
	WSMethods["PEOPLE_WIKI_CHANGED"] = "people_wiki:changed";
	WSMethods["TEAM_CHATS_CHANGED"] = "team_chats:changed";
	WSMethods["ROUTINES_UPDATED"] = "routines_updated";
	WSMethods["BILLING_UPDATED"] = "billing_updated";
	WSMethods["MEETING_INTENT_DETECTED"] = "meeting_intent:detected";
	WSMethods["ERROR"] = "error";
	return WSMethods;
}({});
const DOMAIN_CLASSIFIED_PUSH_TYPE = "domain_classified";
const TEAM_POLICY_CHANGED_PUSH_TYPE = "team_policy_changed";
const AXON_PUSH_TYPE_PREFIX = "axon:";
const MAX_WARNED_UNKNOWN_PUSH_TYPES = 50;
function isPushTypeUnhandledOnMobile(type) {
	return type === DOMAIN_CLASSIFIED_PUSH_TYPE || type === TEAM_POLICY_CHANGED_PUSH_TYPE || type === "meeting_intent:detected" || type.startsWith(AXON_PUSH_TYPE_PREFIX);
}
const CLOSE_REASON_MODES = {
	PingTimeout: "PingTimeout",
	InactiveServer: "InactiveServer",
	AuthFailed: "AuthFailed",
	InvalidToken: "InvalidToken"
};
const SERVER_CLOSE_CODES = /* @__PURE__ */ new Set([
	1001,
	1011,
	1012,
	1013
]);
function classifyFailureMode(code, reason) {
	if (reason && CLOSE_REASON_MODES[reason]) return CLOSE_REASON_MODES[reason];
	if (code === null) return "Unknown";
	if (code === 1006) return "NetworkUnreachable";
	if (SERVER_CLOSE_CODES.has(code)) return "ServerClosed";
	return "Unknown";
}
var WebsocketManager = class {
	socket = { state: "disconnected" };
	sdk;
	connectionCount = 0;
	lastCloseReason = "InitialConnect";
	connectingPromise = null;
	ensureConnectionPromise = null;
	/**
	* For tracking liveness of the connection whether we can communicate
	* over same connection or need to create a new one.
	*
	* AWS API Gateway websocket does not have a keep-alive mechanism and terminates after 2 minutes of inactivity.
	*/
	lastKnownConnectedAt = /* @__PURE__ */ new Date(0);
	callbacks = /* @__PURE__ */ new Map();
	retries = 0;
	authRejections = 0;
	lastErrorMessage = null;
	lastCloseCode = null;
	persistentFailureReported = false;
	retriesAtFailure = 0;
	serverInactivityTimer = null;
	reconnectTimer = null;
	pingLoopActive = false;
	isOnline = true;
	reconnectWhenOnline = false;
	reachabilityUnsubscribe = null;
	disposed = false;
	cancellationGeneration = 0;
	disconnectedSince = null;
	warnedUnknownPushTypes = /* @__PURE__ */ new Set();
	connectionResolvers = [];
	constructor(sdk) {
		this.sdk = sdk;
		this.handleMessage = this.handleMessage.bind(this);
		this.handlePushMessage = this.handlePushMessage.bind(this);
		this.reachabilityUnsubscribe = sdk.options.subscribeToReachability?.((isOnline) => this.handleReachabilityChange(isOnline)) ?? null;
	}
	dispose() {
		if (this.disposed) return;
		this.disposed = true;
		this.reconnectWhenOnline = false;
		this.reachabilityUnsubscribe?.();
		this.reachabilityUnsubscribe = null;
		this.resolveAllConnectionResolvers(false);
		this.close();
		this.setSocketState({ state: "disconnected" });
	}
	handleReachabilityChange(isOnline) {
		if (this.disposed) return;
		if (isOnline === this.isOnline) return;
		this.isOnline = isOnline;
		this.sdk.logger.info(`WebSocket: network ${isOnline ? "online" : "offline"}`);
		if (!isOnline) {
			if (this.reconnectTimer) {
				this.clearReconnectTimer();
				this.reconnectWhenOnline = true;
			}
			return;
		}
		if (this.reconnectWhenOnline && !this.connectingPromise) {
			this.reconnectWhenOnline = false;
			this.connect();
		}
	}
	async connect() {
		if (this.disposed) return;
		if (!this.sdk.options.getAccessToken) {
			this.sdk.logger.warn("No getAccessToken provided, skipping websocket connect");
			return;
		}
		if (this.socket.state !== "disconnected") {
			this.sdk.logger.info(`WebSocket already ${this.socket.state}`);
			return;
		}
		if (this.connectingPromise) {
			this.sdk.logger.info("WebSocket connection already in progress");
			return this.connectingPromise;
		}
		this.clearReconnectTimer();
		const generation = this.cancellationGeneration;
		this.connectingPromise = this.doConnect(generation).finally(() => {
			if (this.cancellationGeneration === generation) this.connectingPromise = null;
		});
		return this.connectingPromise;
	}
	disconnect() {
		this.sdk.logger.info("WebSocket: disconnect() called");
		this.restartConnection("Disconnected");
	}
	isConnected() {
		return this.socket.state === "ready";
	}
	async ensureConnection(timeoutMs = 5e3) {
		if (this.isConnected()) return true;
		if (this.ensureConnectionPromise) return this.ensureConnectionPromise;
		const generation = this.cancellationGeneration;
		this.ensureConnectionPromise = this.doEnsureConnection(timeoutMs, generation).finally(() => {
			if (this.cancellationGeneration === generation) this.ensureConnectionPromise = null;
		});
		return this.ensureConnectionPromise;
	}
	async waitForConnection(timeoutMs) {
		if (this.isConnected()) return true;
		if (this.socket.state === "disconnected") {
			const connectionAttempt = this.connect();
			if (!this.connectingPromise && this.socket.state === "disconnected") {
				try {
					await connectionAttempt;
				} catch {
					return false;
				}
				return this.isConnected();
			}
			const generation = this.cancellationGeneration;
			connectionAttempt.catch(() => {
				if (this.cancellationGeneration === generation) this.resolveAllConnectionResolvers(false);
			});
		}
		return this.awaitConnectionResolution(timeoutMs);
	}
	async resetIfStale() {
		const timeSinceLastActivity = Date.now() - this.lastKnownConnectedAt.getTime();
		if (timeSinceLastActivity < WEBSOCKET_CONFIG.CONNECTION_STALE_THRESHOLD_MS) {
			this.sdk.logger.info("WebSocket still active, skipping reconnect", { timeSinceLastActivity: `${Math.round(timeSinceLastActivity / 1e3)}s` });
			return;
		}
		this.sdk.logger.info("Reconnecting stale WebSocket...", { timeSinceLastActivity: `${Math.round(timeSinceLastActivity / 1e3)}s` });
		this.restartConnection("StaleConnection");
		await this.connect();
	}
	awaitConnectionResolution(timeoutMs) {
		return new Promise((resolve) => {
			const timeoutId = timeoutMs ? setTimeout(() => {
				const idx = this.connectionResolvers.findIndex((r) => r.resolve === resolve);
				if (idx !== -1) this.connectionResolvers.splice(idx, 1);
				resolve(false);
			}, timeoutMs) : null;
			this.connectionResolvers.push({
				resolve,
				timeoutId
			});
		});
	}
	resolveAllConnectionResolvers(connected) {
		for (const resolver of this.connectionResolvers) {
			if (resolver.timeoutId) clearTimeout(resolver.timeoutId);
			resolver.resolve(connected);
		}
		this.connectionResolvers = [];
	}
	resetState(lastCloseReason = "InitialConnect") {
		this.clearServerInactivityTimeout();
		this.clearReconnectTimer();
		this.socket = { state: "disconnected" };
		this.lastCloseReason = lastCloseReason;
		this.lastKnownConnectedAt = /* @__PURE__ */ new Date(0);
		this.callbacks.clear();
		this.retries = 0;
		this.authRejections = 0;
		this.lastErrorMessage = null;
		this.lastCloseCode = null;
		this.persistentFailureReported = false;
		this.retriesAtFailure = 0;
		this.reconnectWhenOnline = false;
		this.disconnectedSince = null;
		this.serverInactivityTimer = null;
		this.connectingPromise = null;
		this.ensureConnectionPromise = null;
		this.connectionResolvers = [];
		this.cancellationGeneration += 1;
	}
	restartConnection(reason) {
		this.sdk.logger.info("WebSocket: restarting connection", {
			reason,
			state: this.socket.state
		});
		this.resolveAllConnectionResolvers(false);
		if (this.socket.state === "connecting" || this.socket.state === "authenticating" || this.socket.state === "ready") this.close();
		this.resetState(reason);
	}
	async doEnsureConnection(timeoutMs, generation) {
		const connected = await this.waitForConnection(timeoutMs);
		if (this.cancellationGeneration !== generation) return false;
		if (connected || this.isConnected()) return true;
		this.sdk.logger.warn("WebSocket: ensureConnection forcing reconnect", { state: this.socket.state });
		this.restartConnection("EnsureConnectionTimeout");
		return this.waitForConnection(timeoutMs);
	}
	async doConnect(generation) {
		if (this.socket.state !== "disconnected") throw new Error(`Cannot connect from state: ${this.socket.state}`);
		this.sdk.logger.info("WebSocket: Connecting...");
		const token = await this.sdk.options.getAccessToken();
		if (this.disposed || this.cancellationGeneration !== generation) return;
		const wsBaseUrl = typeof this.sdk.options.wsBaseUrl === "function" ? this.sdk.options.wsBaseUrl() : this.sdk.options.wsBaseUrl;
		const deviceId = await this.sdk.options.getDeviceId?.() ?? void 0;
		if (this.disposed || this.cancellationGeneration !== generation) return;
		const params = new URLSearchParams({ supports_batching: "true" });
		if (deviceId) params.set("device_id", deviceId);
		const url = `${wsBaseUrl}?${params.toString()}`;
		const ws = new WebSocket(url);
		this.setSocketState({
			state: "connecting",
			ws,
			token
		});
		this.bindWebSocketHandlers(ws, deviceId);
	}
	bindWebSocketHandlers(ws, deviceId) {
		ws.onopen = () => {
			this.sdk.logger.info("WebSocket: Open, sending auth...");
			if (this.socket.state !== "connecting") {
				this.sdk.logger.warn("WebSocket: onopen called in unexpected state");
				return;
			}
			const { token: authToken } = this.socket;
			ws.send(JSON.stringify({
				type: "auth",
				token: authToken,
				supports_batching: true,
				device_id: deviceId
			}));
			this.setSocketState({
				state: "authenticating",
				ws
			});
		};
		ws.onerror = (event) => {
			const message = event.message || null;
			this.sdk.transport.setWsAvailability(false, message ?? void 0);
			this.handleSocketError(message);
		};
		ws.onmessage = (event) => {
			this.resetServerInactivityTimeout();
			this.retries = 0;
			this.lastKnownConnectedAt = /* @__PURE__ */ new Date();
			this.handleMessage(event).catch((err) => {
				this.sdk.logger.error("WebSocket: Error handling message", { error: err });
			});
		};
		ws.onclose = (event) => {
			this.sdk.logger.info(`WebSocket: Closed (code=${event.code}, reason=${event.reason})`);
			this.lastCloseCode = event.code;
			if (this.lastCloseReason === null) this.lastCloseReason = event.reason || `code ${event.code}`;
			this.pingLoopActive = false;
			this.resolveAllConnectionResolvers(false);
			if ((event.code === WS_CLOSE_ABNORMAL || event.code === WS_CLOSE_GOING_AWAY || event.code === WS_CLOSE_UNSUPPORTED) && this.socket.state !== "ready") this.sdk.transport.setWsAvailability(false, event.reason || `code ${event.code}`);
			this.scheduleReconnect();
		};
	}
	setSocketState(state) {
		this.socket = state;
		this.sdk.logger.info(`WebSocket: State changed to ${state.state}`);
	}
	handleSocketError(message) {
		this.lastErrorMessage = message;
		this.sdk.logger.info("WebSocket: transient socket error", { message });
	}
	close() {
		this.pingLoopActive = false;
		this.clearServerInactivityTimeout();
		this.clearReconnectTimer();
		this.rejectAllCallbacks(/* @__PURE__ */ new Error("WebSocket disconnected"));
		if (this.socket.state === "connecting" || this.socket.state === "authenticating" || this.socket.state === "ready") {
			const ws = this.socket.ws;
			ws.onopen = null;
			ws.onclose = null;
			ws.onerror = null;
			ws.onmessage = null;
			try {
				ws.close();
			} catch {}
		}
	}
	closeAndReconnect(reason) {
		this.sdk.logger.info(`WebSocket: closeAndReconnect (${reason})`);
		if (this.socket.state === "connecting" || this.socket.state === "authenticating" || this.socket.state === "ready") {
			this.lastCloseReason = reason;
			this.close();
			this.setSocketState({ state: "disconnected" });
		}
		this.scheduleReconnect();
	}
	scheduleReconnect() {
		this.rejectAllCallbacks(/* @__PURE__ */ new Error("WebSocket reconnecting"));
		this.clearServerInactivityTimeout();
		if (this.socket.state === "connecting" || this.socket.state === "authenticating" || this.socket.state === "ready") this.close();
		this.setSocketState({ state: "disconnected" });
		if (this.disconnectedSince === null) this.disconnectedSince = Date.now();
		if (!this.isOnline && this.retries > 0) {
			this.reconnectWhenOnline = true;
			this.sdk.logger.info("WebSocket: offline, deferring reconnect until network returns");
			this.clearReconnectTimer();
			return;
		}
		const backoff = this.nextBackoff();
		this.maybeReportPersistentFailure();
		this.sdk.logger.info(`WebSocket: Attempting reconnect in ${Math.round(backoff)}ms`);
		const generation = this.cancellationGeneration;
		this.clearReconnectTimer();
		this.reconnectTimer = setTimeout(async () => {
			try {
				await this.connect();
			} catch (err) {
				this.sdk.logger.error("WebSocket: Reconnect failed", { error: err });
				if (this.cancellationGeneration === generation) this.scheduleReconnect();
			}
		}, backoff);
	}
	nextBackoff() {
		const backoff = nextBackoff(this.retries);
		this.retries++;
		return backoff;
	}
	maybeReportPersistentFailure() {
		if (this.persistentFailureReported) return;
		const disconnectedForMs = this.disconnectedSince === null ? 0 : Date.now() - this.disconnectedSince;
		const elapsedExceeded = disconnectedForMs > WEBSOCKET_CONFIG.PERSISTENT_FAILURE_ELAPSED_MS;
		if (this.retries < WEBSOCKET_CONFIG.PERSISTENT_FAILURE_RETRIES && !elapsedExceeded) return;
		this.persistentFailureReported = true;
		this.retriesAtFailure = this.retries;
		const mode = classifyFailureMode(this.lastCloseCode, this.lastCloseReason);
		this.sdk.logger.error(`WebSocket: persistent connection failure (${mode})`, {
			context: "WebsocketManager",
			metadata: {
				mode,
				code: this.lastCloseCode,
				reason: this.lastCloseReason,
				retries: this.retries,
				disconnectedForMs,
				lastErrorMessage: this.lastErrorMessage,
				state: this.socket.state,
				platform: this.sdk.options.platform
			}
		});
	}
	async startPingLoop() {
		if (this.pingLoopActive) return;
		this.pingLoopActive = true;
		while (this.pingLoopActive && this.socket.state === "ready") {
			const connectionId = this.connectionCount;
			this.sdk.logger.debug("sending ping");
			const gotPong = await this.ping();
			if (!this.pingLoopActive || this.connectionCount !== connectionId) break;
			if (gotPong) await workerSleep(WEBSOCKET_CONFIG.PING_INTERVAL_MS);
			else {
				this.sdk.logger.info("WebSocket: Ping failed, triggering reconnect");
				this.closeAndReconnect("PingTimeout");
				break;
			}
		}
	}
	ping() {
		let timeoutId;
		const timeout = new Promise((resolve) => {
			timeoutId = setTimeout(() => {
				this.sdk.logger.info("WebSocket: Ping timeout");
				resolve(false);
			}, WEBSOCKET_CONFIG.PING_TIMEOUT_MS);
		});
		const pong = this.internalCall("ping").then(() => {
			if (timeoutId) clearTimeout(timeoutId);
			this.lastKnownConnectedAt = /* @__PURE__ */ new Date();
			return true;
		}).catch((err) => {
			this.sdk.logger.info("WebSocket: Ping failed", { error: err });
			if (timeoutId) clearTimeout(timeoutId);
			return false;
		});
		return Promise.race([timeout, pong]);
	}
	async internalCall(method, params) {
		if (this.socket.state !== "ready") throw new Error("WebSocket not connected");
		const messageId = v4();
		const message = {
			type: method,
			id: messageId,
			...params && { data: params }
		};
		try {
			this.socket.ws.send(JSON.stringify(message));
		} catch (err) {
			this.sdk.logger.error("WebSocket: Failed to send message", { error: err });
			this.closeAndReconnect("FailedToSend");
			throw err;
		}
		return new Promise((resolve, reject) => {
			this.callbacks.set(messageId, {
				resolve,
				reject,
				method,
				createdAt: Date.now()
			});
		});
	}
	rejectAllCallbacks(error) {
		for (const callback of this.callbacks.values()) callback.reject(error);
		this.callbacks.clear();
	}
	async handleMessage(event) {
		const data = JSON.parse(event.data);
		if (data.id) this.handleResponse(data);
		else if (data.type) this.handlePushMessage(data);
		else this.sdk.logger.warn("WebSocket: Unknown message format", { data });
	}
	handleResponse(response) {
		const callback = this.callbacks.get(response.id);
		if (!callback) {
			this.sdk.logger.warn("WebSocket: No callback for response", { id: response.id });
			return;
		}
		this.callbacks.delete(response.id);
		if (response.type === "error") {
			const msg = response.data?.error || "Unknown error";
			callback.reject(/* @__PURE__ */ new Error(`WebSocket request failed: ${msg}`));
		} else callback.resolve(response.data ?? response);
	}
	handlePushMessage(data) {
		switch (data.type) {
			case "auth_ack":
				this.handleAuthAck(data.data);
				break;
			case "chat_message":
				this.handleChatMessage(data.data);
				break;
			case "chat_message_batch":
				this.handleChatMessageBatch(data.data);
				break;
			case "push_notification":
				globalEventBus.emit(EventNames.PUSH_NOTIFICATION, { data: data.data });
				break;
			case "chat.cost_step":
				globalEventBus.emit(EventNames.CHAT_COST_STEP, { data: data.data });
				break;
			case "action_cards:changed":
				globalEventBus.emit(EventNames.ACTION_CARDS_CHANGED, { data: parseActionCardsChangedPayload(data.data) });
				break;
			case "projects:changed":
			case "chats:changed":
			case "people_wiki:changed":
			case "team_chats:changed":
				globalEventBus.emit(data.type, { data: data.data });
				break;
			case "routines_updated":
				globalEventBus.emit(EventNames.ROUTINES_UPDATED, { data: data.data });
				break;
			case "billing_updated":
				globalEventBus.emit(EventNames.BILLING_UPDATED, { data: {} });
				break;
			case "error":
				if (this.socket.state === "authenticating") this.handleAuthError(data.error || data);
				else this.handleServerError(data.error);
				break;
			default: this.reportUnhandledPushType(String(data.type));
		}
	}
	reportUnhandledPushType(pushMessageType) {
		if (isPushTypeUnhandledOnMobile(pushMessageType)) {
			this.sdk.logger.debug("WebSocket: Ignoring push type not handled on mobile yet", { pushMessageType });
			return;
		}
		if (!this.warnedUnknownPushTypes.has(pushMessageType) && this.warnedUnknownPushTypes.size < MAX_WARNED_UNKNOWN_PUSH_TYPES) {
			this.warnedUnknownPushTypes.add(pushMessageType);
			this.sdk.logger.warn("WebSocket: Unknown push message type", { pushMessageType });
		}
	}
	handleAuthAck(_data) {
		if (this.socket.state !== "authenticating") {
			this.sdk.logger.warn("WebSocket: Received auth_ack in unexpected state", { state: this.socket.state });
			return;
		}
		const ws = this.socket.ws;
		this.setSocketState({
			state: "ready",
			ws,
			initialized: true
		});
		this.resetServerInactivityTimeout();
		this.lastKnownConnectedAt = /* @__PURE__ */ new Date();
		this.sdk.transport.setWsAvailability(true);
		if (this.persistentFailureReported) this.sdk.logger.info(`WebSocket: recovered after persistent failure (${this.retriesAtFailure} retries)`);
		this.retries = 0;
		this.persistentFailureReported = false;
		this.retriesAtFailure = 0;
		this.reconnectWhenOnline = false;
		this.disconnectedSince = null;
		this.authRejections = 0;
		this.lastErrorMessage = null;
		this.lastCloseCode = null;
		const reconnectReason = this.lastCloseReason !== "InitialConnect" && this.lastCloseReason ? this.lastCloseReason : null;
		if (reconnectReason) {
			this.sdk.logger.info(`WebSocket: Reconnected after: ${reconnectReason}`);
			globalEventBus.emit(EventNames.WS_RECONNECTED, { data: { reason: reconnectReason } });
		}
		this.connectionCount++;
		this.lastCloseReason = null;
		this.resolveAllConnectionResolvers(true);
		this.startPingLoop().catch((error) => {
			this.sdk.logger.warn("WebSocket: ping loop failed", { metadata: { error: error instanceof Error ? error.message : String(error) } });
		});
	}
	handleAuthError(_error) {
		this.authRejections++;
		this.sdk.logger.info("WebSocket: auth rejected", { metadata: { authRejections: this.authRejections } });
		if (this.authRejections < WEBSOCKET_CONFIG.MAX_AUTH_REJECTIONS) {
			this.closeAndReconnect("AuthFailed");
			return;
		}
		this.sdk.logger.error("WebSocket: persistent auth failure", {
			context: "WebsocketManager",
			metadata: {
				retries: this.retries,
				authRejections: this.authRejections,
				platform: this.sdk.options.platform
			}
		});
		this.resolveAllConnectionResolvers(false);
		this.close();
		this.resetState();
	}
	handleServerError(error) {
		const { shouldReconnect } = handleServerError(error, this.sdk.logger);
		if (shouldReconnect) this.closeAndReconnect("InvalidToken");
	}
	handleChatMessage(data) {
		handleChatMessage(data, this.sdk.stores, this.sdk.transport, this.sdk.logger);
	}
	handleChatMessageBatch(data) {
		handleChatMessageBatch(data, this.sdk.stores, this.sdk.transport, this.sdk.logger);
	}
	resetServerInactivityTimeout() {
		this.clearServerInactivityTimeout();
		this.serverInactivityTimer = setTimeout(() => {
			this.sdk.logger.info("WebSocket: Server inactivity timeout");
			this.closeAndReconnect("InactiveServer");
		}, WEBSOCKET_CONFIG.CONNECTION_STALE_THRESHOLD_MS);
	}
	clearServerInactivityTimeout() {
		if (this.serverInactivityTimer) {
			clearTimeout(this.serverInactivityTimer);
			this.serverInactivityTimer = null;
		}
	}
	clearReconnectTimer() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}
};
//#endregion
//#region src/core/sync/chat-tail.ts
function olderOf(current, next) {
	return current && Date.parse(current.createdAt) <= Date.parse(next.createdAt) ? current : next;
}
/** The range of a slice fetched before `before` */
function chatTailOf(thread, messages, before) {
	const collector = new ChatTailCollector();
	collector.addThreads([thread], before);
	collector.addMessages(messages);
	return collector.take()[0];
}
/** Gathers the tails of one stream: a thread row names the anchor, and the message rows that follow fill the range */
var ChatTailCollector = class {
	tails = /* @__PURE__ */ new Map();
	addThreads(threads, before) {
		for (const thread of threads) {
			const anchor = thread.messages_after;
			if (anchor === void 0) continue;
			this.tails.set(thread.id, {
				after: anchor ? {
					createdAt: anchor.created_at,
					id: anchor.message_id
				} : null,
				before,
				keptIds: /* @__PURE__ */ new Set(),
				rowUnfinished: thread.finished === false
			});
		}
	}
	addMessages(messages) {
		for (const message of messages) {
			const tail = this.tails.get(message.thread_id);
			if (!tail || !message.id || message.deleted_at) continue;
			tail.keptIds.add(message.id);
			if (message.created_at) tail.rangeOldest = olderOf(tail.rangeOldest, {
				createdAt: message.created_at,
				id: message.id
			});
		}
	}
	take() {
		const tails = [...this.tails].map(([threadId, tail]) => ({
			threadId,
			...tail
		}));
		this.tails.clear();
		return tails;
	}
};
/**
* Apply a range: delete the local messages that the server no longer has,
* through the sync processor so search follows. Returns whether the device
* holds the range's anchor.
*/
async function applyChatTail(sdk, tail) {
	const { ghostIds, anchorHeld } = await sdk.stores.messages.findChatTailGhosts(tail);
	if (ghostIds.length > 0) {
		const deletedAt = (/* @__PURE__ */ new Date()).toISOString();
		sdk.syncProcessor.enqueue({
			table: "messages",
			syncId: 0,
			rows: ghostIds.map((id) => ({
				id,
				thread_id: tail.threadId,
				deleted_at: deletedAt
			})),
			source: "live"
		});
		await sdk.syncProcessor.waitForIdle();
	}
	return anchorHeld;
}
/**
* Returns a gap that starts at `start`. Returns none when `start` is unset, or
* when, under the chat window (`syncChatWindow`), the device holds no message
* older than `start`: holding only a chat's newest messages is the window's
* purpose.
*/
async function chatGapBefore(sdk, threadId, start) {
	if (!start) return void 0;
	if ((sdk.options.syncChatWindow?.() ?? false) && !await sdk.stores.messages.holdsMessageBefore(threadId, start)) return;
	return {
		threadId,
		createdAt: start.createdAt,
		id: start.id
	};
}
//#endregion
//#region src/core/sync/chat-fetch.ts
/** One chat's slice from the server, or undefined when the server no longer has the chat */
async function requestChatSlice(sdk, threadId, { before, init } = {}) {
	const params = new URLSearchParams();
	if (before) {
		params.set("before_created_at", before.createdAt);
		params.set("before_message_id", before.id);
	}
	const query = params.toString();
	try {
		return await sdk.apiClient.fetch(`bootstrap/chat/${threadId}${query ? `?${query}` : ""}`, init);
	} catch (error) {
		if (error instanceof HttpError && error.status === 404) return void 0;
		throw error;
	}
}
/**
* Applies one chat's rows like a delta, without the tail cleanup, which deletes local messages the server lacks,
* such as an error shown here. The messages reach memory only for a chat whose messages are loaded; otherwise
* they are only written to storage.
*/
async function applyChatRows(sdk, rows) {
	sdk.syncProcessor.enqueue({
		table: "threads",
		syncId: 0,
		rows: [rows.thread],
		source: "live"
	});
	sdk.syncProcessor.enqueue({
		table: "messages",
		syncId: 0,
		rows: rows.messages,
		source: "live"
	});
	await sdk.syncProcessor.waitForIdle();
}
/** How long `readChatRows` waits for the server */
const READ_CHAT_ROWS_TIMEOUT_MS = 1e4;
/**
* Reads one chat's thread row and newest messages from the server without applying them, in one attempt
* that times out after `READ_CHAT_ROWS_TIMEOUT_MS` or ends with `signal`. Undefined when the server no longer has the
* chat.
*/
async function readChatRows(sdk, threadId, signal) {
	return requestChatSlice(sdk, threadId, { init: {
		backoff: NO_BACKOFF,
		timeoutMs: READ_CHAT_ROWS_TIMEOUT_MS,
		signal
	} });
}
/** The most requests one fill run makes across all gaps; the rules are in ai-docs/architecture/frontend/sync.md */
const GAP_REQUESTS_PER_RUN = 10;
/** How long one fill run may make requests; the rules are in ai-docs/architecture/frontend/sync.md */
const GAP_FILL_RUN_MS = 6e4;
const gapKey = (gap) => `${gap.threadId}/${gap.id}`;
/**
* Remove, then add, gaps in one `_meta` write, keyed by thread and message.
* An added gap whose key is still recorded keeps its place; any other added
* gap goes to the end of the list, so removing and adding the same gap moves
* it to the end.
*/
async function writeChatGaps(sdk, { add = [], remove = [] }) {
	await sdk.storage.updateMetaData((meta) => {
		const gapsByKey = new Map((meta?.chatGaps ?? []).map((gap) => [gapKey(gap), gap]));
		for (const gap of remove) gapsByKey.delete(gapKey(gap));
		for (const gap of add) gapsByKey.set(gapKey(gap), gap);
		const chatGaps = [...gapsByKey.values()];
		const { chatGaps: _previous, ...rest } = meta ?? {};
		return chatGaps.length > 0 ? {
			...rest,
			chatGaps
		} : rest;
	});
}
/**
* Fetch and apply the slice of a chat before a gap. Returns where the gap
* stands now: an older gap where the missing messages continue, or none when
* the gap closed. The gap closes when the slice starts the chat, the device
* holds the message before it, the chat window finds no older local message,
* or the server no longer has the chat (a 404).
*/
async function fetchSliceBeforeGap(sdk, gap, init) {
	const response = await requestChatSlice(sdk, gap.threadId, {
		before: gap,
		init: {
			...init,
			backoff: NO_BACKOFF
		}
	});
	if (!response) return void 0;
	await applyChatRows(sdk, response);
	const tail = chatTailOf(response.thread, response.messages, gap);
	if (tail ? await applyChatTail(sdk, tail) : false) return void 0;
	const serverOldest = response.has_older && response.oldest ? {
		createdAt: response.oldest.created_at,
		id: response.oldest.message_id
	} : void 0;
	return chatGapBefore(sdk, gap.threadId, serverOldest);
}
/** Fetch the slices before recorded gaps, in list order; the rules are in ai-docs/architecture/frontend/sync.md */
async function fillChatGaps(sdk, gaps, signal) {
	const deadline = Date.now() + GAP_FILL_RUN_MS;
	let requestsLeft = GAP_REQUESTS_PER_RUN;
	const queue = [...gaps];
	const requested = /* @__PURE__ */ new Set();
	while (queue.length > 0 && requestsLeft > 0 && Date.now() < deadline && !signal.aborted) {
		const gap = queue.shift();
		if (requested.has(gapKey(gap))) continue;
		requested.add(gapKey(gap));
		requestsLeft -= 1;
		let nextGap = gap;
		try {
			nextGap = await fetchSliceBeforeGap(sdk, gap, {
				signal,
				timeoutMs: deadline - Date.now()
			});
		} catch (error) {
			if (signal.aborted) return;
			sdk.logger.warn("Could not fill a gap", {
				metadata: {
					error,
					threadId: gap.threadId
				},
				context: "fillChatGaps"
			});
		}
		await writeChatGaps(sdk, {
			remove: [gap],
			add: nextGap ? [nextGap] : []
		});
		if (nextGap && gapKey(nextGap) !== gapKey(gap)) queue.unshift(nextGap);
	}
}
//#endregion
//#region src/core/sync/prune-check.ts
/** Threads first: a pruned thread takes its messages with it, so they are never uploaded */
const PRUNE_TABLES = [
	"threads",
	"messages",
	"meetings",
	"journals",
	"action_cards"
];
/** Matches the server's ABSENT_IDS_MAX */
const PRUNE_IDS_PER_REQUEST = 2e3;
/** A row this young may be one the server has not written yet */
const PRUNE_MIN_AGE_MS = 6e5;
const BRAKE_MIN_ABSENT = 100;
/** A small table fits in one chunk, so a wrong id mapping would otherwise empty it */
const BRAKE_MIN_ALL_ABSENT = 10;
function createdAtMs(row) {
	const value = row.createdAt;
	return value instanceof Date ? value.getTime() : Date.parse(String(value));
}
function isSettled(table, row) {
	switch (table) {
		case "threads": return row.synced !== false;
		case "messages": return row.role === "assistant" && row.status === "done" && !(row.toolCalls ?? []).some((toolCall) => toolCall.status === "pending");
		case "meetings": return row.status === "ended";
		case "journals":
		case "action_cards": return true;
	}
}
/** The server's "absent" is kept for any row this device may hold before the server does */
function prunableIds(table, absentIds, localRows, now, hasTransaction) {
	return absentIds.filter((id) => {
		const row = localRows.get(id);
		if (!row || hasTransaction(id)) return false;
		if (!(now - createdAtMs(row) >= PRUNE_MIN_AGE_MS)) return false;
		return isSettled(table, row);
	});
}
/** A mostly absent chunk points at an id mismatch, not at deletes, so nothing in it is removed */
function isPruneBraked(checked, absent) {
	return absent > BRAKE_MIN_ABSENT && absent * 2 > checked || checked >= BRAKE_MIN_ALL_ABSENT && absent === checked;
}
function isPruneDue(serverEpoch, storedEpoch) {
	return serverEpoch !== void 0 && (storedEpoch ?? 0) < serverEpoch;
}
//#endregion
//#region src/core/feed/feed-types.ts
const feedStatusSchema = z.enum([
	"waiting",
	"in_progress",
	"done",
	"dismissed"
]);
const feedTypeSchema = z.enum([
	"action",
	"update",
	"todo",
	"chat_prompt",
	"teammate"
]);
const feedConfigurationSchema = z.object({
	statuses: z.array(feedStatusSchema).default([]),
	types: z.array(feedTypeSchema).default([]),
	apps: z.array(z.string()).default([]),
	groupBy: z.enum([
		"none",
		"type",
		"app",
		"source",
		"people"
	]).default("none"),
	sort: z.enum(["newest", "oldest"]).default("newest")
});
const feedTabSchema = z.object({
	id: z.string().min(1),
	builtin: z.enum([
		"for_you",
		"in_progress",
		"todos",
		"history"
	]).nullable().default(null),
	name: z.string().trim().min(1),
	configuration: feedConfigurationSchema
});
const DEFAULT_FEED_TABS = [
	{
		id: "for_you",
		builtin: "for_you",
		name: "For you",
		configuration: feedConfigurationSchema.parse({ statuses: ["waiting"] })
	},
	{
		id: "in_progress",
		builtin: "in_progress",
		name: "In progress",
		configuration: feedConfigurationSchema.parse({ statuses: ["in_progress"] })
	},
	{
		id: "todos",
		builtin: "todos",
		name: "Todos",
		configuration: feedConfigurationSchema.parse({
			statuses: ["waiting"],
			types: ["todo"],
			groupBy: "none"
		})
	},
	{
		id: "history",
		builtin: "history",
		name: "History",
		configuration: feedConfigurationSchema.parse({ statuses: ["done", "dismissed"] })
	}
];
const feedConfigSchema = z.object({
	tabs: z.array(feedTabSchema).default(DEFAULT_FEED_TABS),
	gmail_auto_archive: z.enum([
		"off",
		"reply",
		"reply_and_dismiss"
	]).optional()
}).prefault({});
const PINNED_FEED_GROUP = "Pinned";
const WORKING_FEED_GROUP = "Working";
const SNOOZED_FEED_GROUP = "Snoozed";
const UNPINNED_FEED_GROUP = "Everything else";
const FEED_TYPE_LABELS = {
	action: "Ready to go",
	update: "Updates",
	todo: "Needs you",
	chat_prompt: "Littlebird",
	teammate: "Messages"
};
const READY_FEED_SECTION = FEED_TYPE_LABELS.action;
const NEEDS_YOU_FEED_SECTION = FEED_TYPE_LABELS.todo;
const UPDATES_FEED_SECTION = FEED_TYPE_LABELS.update;
const FEED_SECTION_OF = {
	action: READY_FEED_SECTION,
	todo: NEEDS_YOU_FEED_SECTION,
	teammate: NEEDS_YOU_FEED_SECTION,
	chat_prompt: NEEDS_YOU_FEED_SECTION,
	update: UPDATES_FEED_SECTION
};
const FEED_SECTION_ORDER = {
	[READY_FEED_SECTION]: 0,
	[NEEDS_YOU_FEED_SECTION]: 1,
	[UPDATES_FEED_SECTION]: 2
};
const feedItemSection = (metadata, options) => {
	if (metadata.snoozed) return SNOOZED_FEED_GROUP;
	if (options.pinned) return PINNED_FEED_GROUP;
	if (options.working) return WORKING_FEED_GROUP;
	return FEED_SECTION_OF[metadata.type];
};
const feedItemSubgroup = (metadata, configuration, section) => {
	if (!(section in FEED_SECTION_ORDER)) return void 0;
	if (configuration.groupBy === "none" || configuration.groupBy === "type") return void 0;
	if (configuration.groupBy === "people") return metadata.people[0] ?? "No people";
	return metadata[configuration.groupBy];
};
const groupBySection = (selected, placeOf) => {
	const sections = /* @__PURE__ */ new Map();
	for (const { item, metadata } of selected) {
		const [section, subgroup] = placeOf(metadata);
		let groups = sections.get(section);
		if (!groups) {
			groups = /* @__PURE__ */ new Map();
			sections.set(section, groups);
		}
		const members = groups.get(subgroup);
		if (members) members.push(item);
		else groups.set(subgroup, [item]);
	}
	return sections;
};
const selectFeedItems = (items, configuration, getMetadata, people = []) => {
	const selected = items.map((item) => ({
		item,
		metadata: getMetadata(item)
	})).filter(({ metadata }) => (!configuration.statuses.length || configuration.statuses.includes(metadata.status)) && (!configuration.types.length || configuration.types.includes(metadata.type)) && (!configuration.apps.length || configuration.apps.includes(metadata.app)) && (!people.length || metadata.people.some((id) => people.includes(id))));
	const isPinned = (metadata) => metadata.pinned && !metadata.snoozed;
	const isWorking = (metadata) => !metadata.snoozed && !isPinned(metadata) && (metadata.working ?? metadata.status === "in_progress");
	const sectionOf = (metadata) => feedItemSection(metadata, {
		pinned: isPinned(metadata),
		working: isWorking(metadata)
	});
	const sectionOrder = (metadata) => FEED_SECTION_ORDER[sectionOf(metadata)] ?? 0;
	selected.sort((left, right) => {
		const difference = left.metadata.timestamp - right.metadata.timestamp;
		return Number(!!left.metadata.snoozed) - Number(!!right.metadata.snoozed) || Number(isPinned(right.metadata)) - Number(isPinned(left.metadata)) || Number(isWorking(right.metadata)) - Number(isWorking(left.metadata)) || sectionOrder(left.metadata) - sectionOrder(right.metadata) || (configuration.sort === "oldest" ? difference : -difference) || left.metadata.id.localeCompare(right.metadata.id);
	});
	return [...groupBySection(selected, (metadata) => {
		const section = sectionOf(metadata);
		return [section, feedItemSubgroup(metadata, configuration, section)];
	})].flatMap(([section, groups]) => [...groups].flatMap(([subgroup, members], groupIndex) => members.map((item, index) => ({
		item,
		group: groupIndex === 0 && index === 0 ? section : void 0,
		subgroup: index === 0 ? subgroup : void 0
	}))));
};
//#endregion
//#region src/core/user-config/user-config-types.ts
const DEFAULT_EXCLUDED_EVENT_TYPES = [
	"workingLocation",
	"outOfOffice",
	"birthday",
	"focusTime"
];
const meetingsConfigSchema = z.object({
	notification_filter: z.number().default(0),
	reminder_lead_time: z.number().default(1),
	disable_reminders: z.boolean().default(false),
	exclude_solo_events: z.boolean().default(true),
	disable_detection_notification: z.boolean().default(false),
	auto_start_on_detection: z.boolean().default(false),
	auto_stop_timeout: z.number().default(15),
	status_window_option: z.number().default(1),
	automatic_meeting_prep_enabled: z.boolean().nullable().default(null),
	meeting_prep_lead_time: z.number().default(720),
	meeting_prep_email_enabled: z.boolean().default(true),
	meeting_summary_email_enabled: z.boolean().default(true),
	meeting_prep_prompt: z.string().default(""),
	meeting_summary_prompt: z.string().default(""),
	meeting_assist_enabled: z.boolean().nullable().default(null),
	meeting_assist_prompt: z.string().default(""),
	meeting_intelligence_enabled: z.boolean().nullable().default(null),
	transcription_engine_preference_source: z.enum(["default_policy", "manual"]).default("default_policy"),
	preferred_transcription_engine: z.enum(["remote", "local"]).default("remote"),
	disabled_note_sources: z.array(z.string()).optional(),
	hidden_circles: z.array(z.string()).optional(),
	show_summary_after_auto_stop: z.boolean().optional()
}).prefault({});
const menubarConfigSchema = z.object({
	calendar_enabled: z.boolean().default(true),
	include_events: z.string().default("today-and-tomorrow"),
	preview_lead_time: z.string().default("3-hours")
}).prefault({});
const calendarConfigSchema = z.object({
	days_ahead: z.number().default(10),
	max_events: z.number().default(20),
	excluded_event_types: z.array(z.string()).default(DEFAULT_EXCLUDED_EVENT_TYPES),
	excluded_statuses: z.array(z.string()).default([])
}).prefault({});
const chatConfigSchema = z.object({
	require_mod_enter_to_send: z.boolean().default(false),
	confirm_external_links: z.boolean().default(true),
	show_thoughts: z.boolean().default(false),
	show_messaging_channel_chats: z.boolean().default(false),
	previous_turn_preview: z.boolean().default(false),
	disable_screen_context_search: z.boolean().default(false),
	project_suggestions_enabled: z.boolean().default(true),
	hummingbird_always_new_chat: z.boolean().default(false),
	usage_pace_warnings: z.boolean().default(true),
	sandbox_allowed_domains: z.array(z.string()).default([]),
	email_attribution_footer_enabled: z.boolean().nullable().default(null)
}).prefault({});
const privacyConfigSchema = z.object({
	hide_hummingbird_from_screen_share: z.boolean().optional(),
	hide_meeting_widget_from_screen_share: z.boolean().default(true)
}).prefault({});
const notificationsConfigSchema = z.object({
	sound_enabled: z.boolean().default(true),
	sound: z.string().default("default"),
	routine_suggestions_email_enabled: z.boolean().optional()
}).prefault({});
const proactiveAgentVariantSchema = z.object({
	base_agent: z.string().min(1).max(100),
	initial_prompt: z.string().min(1).max(1e5).refine((prompt) => prompt.trim().length > 0)
});
const proactiveAgentsConfigSchema = z.object({
	variants: z.record(z.string().min(1).max(100), proactiveAgentVariantSchema).default({}),
	min_card_importance: z.number().int().min(0).max(10).default(0),
	borderline_cards: z.number().int().min(0).max(100).optional(),
	card_delivery: z.object({
		channel: z.enum([
			"off",
			"imessage",
			"whatsapp_business"
		]).nullable().default(null),
		min_importance: z.number().int().min(0).max(10).default(8)
	}).prefault({}),
	coding_only_started_by_littlebird: z.boolean().default(true)
}).prefault({});
const MAX_AUTO_REVIEW_RULES = 50;
const MAX_AUTO_REVIEW_RULE_CHARS = 300;
const autoReviewRuleBehaviorSchema = z.enum(["auto_run", "ask_first"]);
const AUTO_REVIEW_RULE_BEHAVIORS = autoReviewRuleBehaviorSchema.options;
const autoReviewRuleSchema = z.object({
	id: z.string(),
	text: z.string(),
	behavior: autoReviewRuleBehaviorSchema.catch("ask_first")
});
const autoReviewConfigSchema = z.object({ rules: z.array(autoReviewRuleSchema).default([]) }).prefault({});
const UserConfigSchema = z.object({
	meetings: meetingsConfigSchema,
	menubar: menubarConfigSchema,
	calendar: calendarConfigSchema,
	chat: chatConfigSchema,
	privacy: privacyConfigSchema,
	notifications: notificationsConfigSchema,
	proactive_agents: proactiveAgentsConfigSchema,
	feed: feedConfigSchema,
	auto_review: autoReviewConfigSchema
});
/** Fully-defaulted config used as the read fallback when a value is unset. */
const DEFAULT_USER_CONFIG = UserConfigSchema.parse({});
//#endregion
//#region src/core/user-config/user-config-store.ts
const USER_CONFIG_TABLE = "user_configs";
const STORAGE_TABLE = "userConfigs";
const USER_CONFIG_ENDPOINT = "user_configs";
const USER_CONFIG_ID = "user_config";
var UserConfigNotConfirmedError = class extends Error {
	constructor() {
		super("User config is waiting for the server copy");
		this.name = "UserConfigNotConfirmedError";
	}
};
const noopConfigStore = {
	data: /* @__PURE__ */ new Map(),
	isFetching: false,
	isSaving: false,
	isLoaded: false,
	add() {
		throw new Error("UserConfigModel noop store: add not supported");
	},
	remove() {},
	get() {},
	clear() {},
	async delete() {},
	async save() {
		throw new Error("UserConfigModel noop store: save not supported");
	},
	async hydrate() {},
	async ensureListLoadedFromStorage() {}
};
var ConfigColumn = class {
	store;
	column;
	constructor(store, column) {
		this.store = store;
		this.column = column;
	}
	get(key) {
		return this.store.config[this.column][key];
	}
	get values() {
		return this.store.config[this.column];
	}
	set(key, value) {
		const partial = {};
		partial[key] = value;
		this.store.patchColumn(this.column, partial);
	}
	patch(partial) {
		this.store.patchColumn(this.column, partial);
	}
	/** Changes `fields` of the object at `key`; its other fields come from the settings the write lands on */
	setFields(key, fields) {
		this.store.patchColumnFields(this.column, key, fields);
	}
};
var UserConfigStore = class {
	sdk;
	static schemaVersion = 5;
	confirmedConfig = DEFAULT_USER_CONFIG;
	pendingWrites = /* @__PURE__ */ new Map();
	heldChanges = [];
	syncId = 0;
	/**
	* True while a refill still owes the server's copy. Writes wait for that
	* copy, because some are built on the settings shown: a list or object
	* setting is sent whole, and a feed tabs write sends the tabs it started from.
	*/
	awaitingServerCopy = false;
	meetings = new ConfigColumn(this, "meetings");
	menubar = new ConfigColumn(this, "menubar");
	calendar = new ConfigColumn(this, "calendar");
	chat = new ConfigColumn(this, "chat");
	privacy = new ConfigColumn(this, "privacy");
	notifications = new ConfigColumn(this, "notifications");
	proactiveAgents = new ConfigColumn(this, "proactive_agents");
	feed = new ConfigColumn(this, "feed");
	autoReview = new ConfigColumn(this, "auto_review");
	constructor(sdk) {
		this.sdk = sdk;
		makeAutoObservable(this, {
			meetings: false,
			menubar: false,
			calendar: false,
			chat: false,
			privacy: false,
			notifications: false,
			proactiveAgents: false,
			feed: false,
			autoReview: false
		});
	}
	get config() {
		let config = this.confirmedConfig;
		for (const write of this.pendingWrites.values()) for (const column of Object.keys(write.changes)) config = {
			...config,
			[column]: {
				...config[column],
				...write.changes[column]
			}
		};
		for (const { column, partialFor } of this.heldChanges) config = {
			...config,
			[column]: {
				...config[column],
				...partialFor(config)
			}
		};
		return config;
	}
	/** Model fields used by the runtime schema manifest, as `Store` exposes. */
	getPersistedFieldNames() {
		return new UserConfigModel({ id: "__schema_manifest__" }, noopConfigStore, this.sdk.logger).getPersistedFieldNames();
	}
	async hydrateFromStorage() {
		const awaitingServerCopy = (await this.sdk.storage.getMetaData())?.userConfigRefillPending === true;
		runInAction(() => {
			this.awaitingServerCopy = awaitingServerCopy;
		});
		const row = await this.sdk.storage.get(STORAGE_TABLE, USER_CONFIG_ID);
		if (!row) return;
		const parsed = UserConfigSchema.safeParse({
			meetings: row.meetings,
			menubar: row.menubar,
			calendar: row.calendar,
			chat: row.chat,
			privacy: row.privacy,
			notifications: row.notifications,
			proactive_agents: row.proactiveAgents,
			feed: row.feed,
			auto_review: row.autoReview
		});
		runInAction(() => {
			this.confirmedConfig = parsed.success ? parsed.data : DEFAULT_USER_CONFIG;
			this.syncId = row.syncId ?? 0;
			this.retireConfirmedWrites();
		});
	}
	async persistAndHydrate(rows) {
		const row = rows.reduce((latest, candidate) => latest === void 0 || candidate.sync_id > latest.sync_id ? candidate : latest, void 0);
		if (!row || row.sync_id < this.syncId) return;
		const parsed = UserConfigSchema.safeParse({
			meetings: row.meetings,
			menubar: row.menubar,
			calendar: row.calendar,
			chat: row.chat,
			privacy: row.privacy,
			notifications: row.notifications,
			proactive_agents: row.proactive_agents,
			feed: row.feed,
			auto_review: row.auto_review
		});
		if (!parsed.success) {
			this.sdk.logger.warn("Invalid user config row from sync; skipping", { context: "user_config_hydrate_invalid" });
			return;
		}
		runInAction(() => {
			this.confirmedConfig = parsed.data;
			this.syncId = row.sync_id;
			this.retireConfirmedWrites();
		});
		await this.persistToStorage();
		await this.confirmServerCopy();
	}
	/** The server's copy arrived, or the refill showed there is none: send the held changes on top of it */
	async confirmServerCopy() {
		if (!this.awaitingServerCopy) return;
		const heldChanges = this.heldChanges;
		runInAction(() => {
			this.awaitingServerCopy = false;
			this.heldChanges = [];
		});
		for (const { column, partialFor } of heldChanges) this.patchColumn(column, partialFor(this.config));
		await this.sdk.storage.updateMetaData((meta) => {
			if (!meta?.userConfigRefillPending) return void 0;
			const { userConfigRefillPending: _, ...rest } = meta;
			return rest;
		});
	}
	patchColumn(column, partial) {
		if (this.awaitingServerCopy) {
			runInAction(() => {
				this.heldChanges.push({
					column,
					partialFor: () => partial
				});
			});
			return;
		}
		const transactionId = v4();
		const feedTabsBase = column === "feed" && "tabs" in partial ? this.config.feed.tabs : void 0;
		const changes = { [column]: partial };
		runInAction(() => {
			this.pendingWrites.set(transactionId, { changes });
		});
		this.sdk.transactionQueue.enqueue({
			id: transactionId,
			table: USER_CONFIG_TABLE,
			op: "update",
			modelId: USER_CONFIG_ID,
			payload: {
				config: changes,
				...feedTabsBase && { feed_tabs_base: feedTabsBase }
			},
			handlerOverride: {
				endpoint: USER_CONFIG_ENDPOINT,
				method: "PUT"
			}
		});
	}
	patchColumnFields(column, key, fields) {
		const partialFor = (config) => {
			const partial = {};
			partial[key] = {
				...config[column][key],
				...fields
			};
			return partial;
		};
		if (this.awaitingServerCopy) {
			runInAction(() => {
				this.heldChanges.push({
					column,
					partialFor
				});
			});
			return;
		}
		this.patchColumn(column, partialFor(this.config));
	}
	async rollback(tx) {
		runInAction(() => {
			this.pendingWrites.delete(tx.id);
		});
	}
	restoreTransaction(tx) {
		const parsed = UserConfigSchema.partial().safeParse(tx.payload.config);
		if (!parsed.success) return;
		const sent = tx.payload.config;
		const changes = mapValues(sent, (settings, column) => pick(parsed.data[column], Object.keys(settings)));
		runInAction(() => {
			this.pendingWrites.set(tx.id, {
				changes,
				syncId: tx.syncIdNeededForCompletion
			});
			this.retireConfirmedWrites();
		});
	}
	acknowledgeTransaction(tx) {
		const write = this.pendingWrites.get(tx.id);
		if (!write) return;
		runInAction(() => {
			write.syncId = tx.syncIdNeededForCompletion;
			this.retireConfirmedWrites();
		});
	}
	retireConfirmedWrites() {
		for (const [transactionId, write] of this.pendingWrites) if (write.syncId !== void 0 && write.syncId <= this.syncId) this.pendingWrites.delete(transactionId);
	}
	reset() {
		runInAction(() => {
			this.confirmedConfig = DEFAULT_USER_CONFIG;
			this.pendingWrites.clear();
			this.heldChanges = [];
			this.awaitingServerCopy = false;
			this.syncId = 0;
		});
		this.clearStorage();
	}
	migrateLegacyConfig(partial) {
		if (this.syncId > 0) return;
		if (this.awaitingServerCopy) throw new UserConfigNotConfirmedError();
		const columns = Object.keys(partial);
		if (!columns.some((column) => {
			const columnPartial = partial[column];
			return columnPartial !== void 0 && Object.keys(columnPartial).length > 0;
		})) return;
		for (const column of columns) {
			const columnPartial = partial[column];
			if (!columnPartial || Object.keys(columnPartial).length === 0) continue;
			this.patchColumn(column, columnPartial);
		}
	}
	async persistToStorage() {
		const now = /* @__PURE__ */ new Date();
		const model = new UserConfigModel({
			id: USER_CONFIG_ID,
			meetings: this.confirmedConfig.meetings,
			menubar: this.confirmedConfig.menubar,
			calendar: this.confirmedConfig.calendar,
			chat: this.confirmedConfig.chat,
			privacy: this.confirmedConfig.privacy,
			notifications: this.confirmedConfig.notifications,
			feed: this.confirmedConfig.feed,
			proactiveAgents: this.confirmedConfig.proactive_agents,
			autoReview: this.confirmedConfig.auto_review,
			syncId: this.syncId,
			createdAt: now,
			updatedAt: now
		}, noopConfigStore, this.sdk.logger);
		await this.sdk.storage.set(STORAGE_TABLE, model);
	}
	async clearStorage() {
		await this.sdk.storage.remove(STORAGE_TABLE, USER_CONFIG_ID);
	}
};
//#endregion
//#region src/core/utils/string.ts
const getTableName = (modelName) => {
	return pluralize(lowerFirst(modelName));
};
function normalizeFilename(filename) {
	const timestamp = Math.floor(Date.now() / 1e3);
	const extension = filename.slice(filename.lastIndexOf("."));
	return `${timestamp}_${filename.slice(0, filename.lastIndexOf(".")).trim().replace(/[^a-zA-Z0-9.]/g, "_").replace(/\s+/g, "_").slice(0, 500 - extension.length - timestamp.toString().length - 2)}${extension}`;
}
//#endregion
//#region src/core/sync/stores/base/store.ts
/** Splits ids into reads of at most 200, so no single storage read grows without limit */
const chunkIds = (ids) => {
	const chunks = [];
	for (let i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
	return chunks;
};
var Store = class {
	modelName = "";
	tableName = "";
	apiEndpoint = "";
	sdk;
	data = /* @__PURE__ */ new Map();
	isFetching = false;
	isSaving = false;
	isLoaded = false;
	hasMorePages = true;
	currentPageCursor;
	pendingReload = false;
	/** Where the next page read from storage starts, so a screen can stop reading at a date */
	get pageCursor() {
		return this.currentPageCursor;
	}
	refreshInFlight = null;
	refreshQueued = false;
	static persisted = true;
	static loadStrategy = "lazy";
	/**
	* Per-store schema version. Bump this when the shape of data in
	* storage changes.
	* The SDK's init flow compares stored versions against current
	* values and clears only the affected table.
	*/
	static schemaVersion = 1;
	runtimeLoadStrategy;
	constructor(sdk) {
		this.sdk = sdk;
		makeObservable(this, {
			modelName: false,
			tableName: false,
			apiEndpoint: false,
			sdk: false,
			data: observable,
			isFetching: observable,
			isSaving: observable,
			isLoaded: observable,
			hasMorePages: observable,
			add: action,
			remove: action,
			clear: action,
			reset: action,
			create: action,
			update: action,
			loadInitialPage: action,
			loadNextPage: action,
			ensureLoaded: action,
			ensureListLoadedFromStorage: action,
			orderedData: computed
		});
	}
	/**
	* always call this mehod in the constructor of the subclass
	*/
	initializeMetadata(apiEndpoint) {
		this.modelName = this.ModelClass.modelName;
		this.tableName = getTableName(this.modelName);
		this.apiEndpoint = apiEndpoint ?? this.tableName;
	}
	updateExistingModel(existingModel, item) {
		existingModel.updateData(item);
		return existingModel;
	}
	addRecord(record) {
		return this.add({
			...record,
			deletedAt: record.deletedAt ? record.deletedAt : void 0
		});
	}
	add = (item) => {
		if (!(item instanceof this.ModelClass)) {
			const existingModel = this.data.get(item.id);
			if (existingModel) return this.updateExistingModel(existingModel, item);
			const newModel = new this.ModelClass(item, this, this.sdk.logger);
			this.data.set(newModel.id, newModel);
			return newModel;
		}
		this.data.set(item.id, item);
		return item;
	};
	remove(item) {
		this.data.delete(item.id);
	}
	get(id) {
		return this.data.get(id);
	}
	/** Model fields used by the runtime schema manifest. */
	getPersistedFieldNames() {
		return new this.ModelClass({ id: "__schema_manifest__" }, this, this.sdk.logger).getPersistedFieldNames();
	}
	async persistMany(records) {
		if (records.length === 0) return;
		const serializable = records.map((record) => new this.ModelClass(record, this, this.sdk.logger));
		await this.sdk.storage.bulkSet(this.tableName, serializable);
	}
	clear() {
		this.data.clear();
		this.isLoaded = false;
		this.hasMorePages = true;
		this.currentPageCursor = void 0;
		this.generation += 1;
	}
	/**
	* Bumped by every `clear()`, which logout performs. A fetch that captures
	* this before awaiting can tell whether the store it is about to hydrate is
	* still the one it was asked for: applying a response that outlived a logout
	* would refill the next account's store with the previous account's rows.
	*/
	generation = 0;
	isCurrentGeneration(captured) {
		return captured === this.generation;
	}
	reset() {
		this.pendingReload = this.isLoaded || this.pendingReload;
		this.clear();
	}
	get effectiveLoadStrategy() {
		return this.runtimeLoadStrategy ?? this.constructor.loadStrategy;
	}
	get orderedData() {
		return orderBy(Array.from(this.data.values()).filter((item) => !item.deletedAt), "createdAt", "desc");
	}
	async hydrate() {
		if (this.effectiveLoadStrategy !== "eager") return;
		if (!this.constructor.persisted) return;
		await this.ensureListLoadedFromStorage();
	}
	async ensureListLoadedFromStorage() {
		if (this.isLoaded) return;
		await this.loadInitialPage();
	}
	async loadInitialPage(options) {
		const entityQueries = this.requireEntityQueries();
		this.clear();
		const generation = this.generation;
		const page = await entityQueries.listPage(options);
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of page.records) this.addRecord(record);
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
			this.isLoaded = true;
		});
	}
	async loadNextPage(options) {
		const entityQueries = this.requireEntityQueries();
		const generation = this.generation;
		if (!this.currentPageCursor || !this.hasMorePages) return;
		const page = await entityQueries.listPageAfter(this.currentPageCursor, options);
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of page.records) this.addRecord(record);
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
		});
	}
	async loadAllPages(options) {
		await this.ensureListLoadedFromStorage();
		while (this.hasMorePages && this.currentPageCursor) {
			const cursorIdBeforeLoad = this.currentPageCursor.id;
			await this.loadNextPage(options);
			if (this.currentPageCursor?.id === cursorIdBeforeLoad) break;
		}
	}
	async fetchRecordsByIdChunks(getByIds, ids) {
		if (ids.length === 0) return [];
		return (await Promise.all(chunkIds(ids).map((chunk) => getByIds(chunk)))).flat();
	}
	shouldRetainLocal(_record) {
		return false;
	}
	applyRefreshedRecord(record) {
		const existing = this.data.get(record.id);
		if (existing && record.updatedAt instanceof Date && existing.updatedAt instanceof Date && record.updatedAt.getTime() <= existing.updatedAt.getTime()) return;
		this.addRecord(record);
	}
	async refreshLoadedFromStorage() {
		return this.coalesceRefresh(() => this.runRefreshFromStorage());
	}
	async refreshOrReloadFromStorage() {
		if (this.pendingReload && !this.isLoaded) runInAction(() => {
			this.isLoaded = true;
		});
		this.pendingReload = false;
		if (!this.isLoaded) return;
		await this.refreshLoadedFromStorage();
	}
	async runRefreshFromStorage() {
		if (!this.isLoaded) return;
		const entityQueries = this.requireEntityQueries();
		const residentBefore = new Set(this.data.keys());
		const generation = this.generation;
		const page = await entityQueries.listPage();
		if (!this.isCurrentGeneration(generation)) return;
		const freshIds = /* @__PURE__ */ new Set();
		const fresh = [];
		for (const record of page.records) {
			fresh.push(record);
			freshIds.add(record.id);
		}
		const detailOnlyIds = Array.from(residentBefore).filter((id) => !freshIds.has(id));
		if (detailOnlyIds.length > 0) {
			const detailRecords = await this.fetchRecordsByIdChunks(entityQueries.getByIds.bind(entityQueries), detailOnlyIds);
			if (!this.isCurrentGeneration(generation)) return;
			for (const record of detailRecords) {
				fresh.push(record);
				freshIds.add(record.id);
			}
		}
		if (freshIds.size === 0 && residentBefore.size > 0) {
			this.sdk.logger.warn("Skipping refresh wipe: storage returned no rows for non-empty resident set", {
				context: "Store:runRefreshFromStorage",
				metadata: {
					store: this.modelName,
					residentBefore: residentBefore.size
				}
			});
			return;
		}
		runInAction(() => {
			for (const record of fresh) this.applyRefreshedRecord(record);
			for (const id of residentBefore) {
				if (freshIds.has(id)) continue;
				const existing = this.data.get(id);
				if (!existing) continue;
				if (this.shouldRetainLocal(existing)) continue;
				this.data.delete(id);
			}
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
		});
	}
	coalesceRefresh(run) {
		if (this.refreshInFlight) {
			this.refreshQueued = true;
			return this.refreshInFlight;
		}
		const promise = run().finally(() => {
			this.refreshInFlight = null;
			if (this.refreshQueued) {
				this.refreshQueued = false;
				this.refreshLoadedFromStorage();
			}
		});
		this.refreshInFlight = promise;
		return promise;
	}
	async ensureLoaded(id) {
		const existingModel = this.data.get(id);
		if (existingModel) return existingModel;
		const generation = this.generation;
		const entityQueries = this.getEntityQueries();
		if (entityQueries) {
			const entityRecord = await entityQueries.getById(id);
			if (!this.isCurrentGeneration(generation)) return void 0;
			if (entityRecord) return this.addRecord(entityRecord);
		}
		const record = await this.sdk.storage.get(this.tableName, id);
		if (!this.isCurrentGeneration(generation)) return void 0;
		if (!record) return void 0;
		return this.addRecord(record);
	}
	getEntityQueries() {
		return this.sdk.storage.entities[this.tableName];
	}
	requireEntityQueries() {
		const entityQueries = this.getEntityQueries();
		if (!entityQueries) throw new MissingEntityQueriesError(this.tableName);
		return entityQueries;
	}
	async create(params, options) {
		this.isSaving = true;
		try {
			const res = await this.sdk.apiClient.fetch(this.apiEndpoint, {
				method: "POST",
				body: JSON.stringify({
					...params,
					...options
				})
			});
			invariant(res, "Response should be available");
			return this.add(res);
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	}
	async update(params, options) {
		this.isSaving = true;
		try {
			const res = await this.sdk.apiClient.fetch(this.apiEndpoint, {
				method: "PUT",
				body: JSON.stringify({
					...params,
					...options
				})
			});
			invariant(res, "Response should be available");
			const model = this.add(res);
			if (this.constructor.persisted) await this.sdk.storage.set(this.tableName, model);
			return model;
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	}
	save(params, options = {}) {
		const { isNew, ...rest } = options;
		if (isNew || !("id" in params) || !params.id) return this.create(params, rest);
		return this.update(params, rest);
	}
	async delete(item, options = {}) {
		runInAction(() => {
			this.isSaving = true;
		});
		try {
			const id = item.id;
			this.remove(item);
			await this.sdk.apiClient.fetch(this.apiEndpoint, {
				method: "DELETE",
				body: JSON.stringify({
					id,
					...options
				})
			});
			if (this.constructor.persisted) await this.sdk.storage.remove(this.tableName, id);
		} catch (err) {
			this.sdk.logger.error(err, {
				context: "Store:delete",
				metadata: {
					message: `Failed to delete ${this.modelName}`,
					error: err
				}
			});
			throw err;
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	}
	async rollback(tx) {
		this.sdk.logger.warn("Store: no rollback handler, optimistic state kept", {
			context: "Store:rollback",
			metadata: {
				table: tx.table,
				modelId: tx.modelId,
				op: tx.op,
				transactionId: tx.id,
				attempt: tx.attempt,
				engineContext: {
					kind: "mutationSend",
					table: tx.table,
					action: tx.op
				}
			}
		});
	}
};
//#endregion
//#region src/core/sync/stores/utils.ts
const parseDate = (dateString) => {
	if (!dateString) return /* @__PURE__ */ new Date();
	const date = new Date(dateString);
	return isNaN(date.getTime()) ? /* @__PURE__ */ new Date() : date;
};
/**
* A list endpoint that answers with something other than a list has failed, and
* must read as a failure rather than as an empty account: the callers turn an
* empty result into a first-run empty state, which invites the user to recreate
* rows they already have.
*/
function expectList(value, what) {
	if (!Array.isArray(value)) throw new Error(`The ${what} response was not a list`);
	return value;
}
const operationKey = (operation, referenceId) => referenceId === void 0 ? operation : `${operation}:${referenceId}`;
function createOperationTracker() {
	const pendingOperations = observable.set();
	const operationErrors = observable.map();
	return {
		isOperationPending(operation, referenceId) {
			return pendingOperations.has(operationKey(operation, referenceId));
		},
		getOperationError(operation, referenceId) {
			return operationErrors.get(operationKey(operation, referenceId)) ?? null;
		},
		clearOperationErrors(referenceId) {
			const id = String(referenceId);
			runInAction(() => {
				for (const key of Array.from(operationErrors.keys())) if (key.slice(key.indexOf(":") + 1) === id) operationErrors.delete(key);
			});
		},
		reset() {
			runInAction(() => {
				pendingOperations.clear();
				operationErrors.clear();
			});
		},
		async withOperation(operation, referenceId, fn) {
			const key = operationKey(operation, referenceId);
			runInAction(() => {
				pendingOperations.add(key);
				operationErrors.delete(key);
			});
			try {
				return await fn();
			} catch (error) {
				runInAction(() => {
					operationErrors.set(key, error instanceof Error ? error : new Error(String(error)));
				});
				throw error;
			} finally {
				runInAction(() => {
					pendingOperations.delete(key);
				});
			}
		}
	};
}
async function updateIndexedProjectAssignment({ item, projectId, applyProjectId, sync, persist }) {
	const previousProjectId = item.projectId ?? null;
	if (previousProjectId === projectId) return;
	applyProjectId(projectId);
	try {
		await sync();
		await persist();
	} catch (error) {
		applyProjectId(previousProjectId);
		throw error;
	}
}
/**
* Exhaustiveness check.
*/
function assertNever(value) {
	throw Error(`This codepath was supposed to be unreachable: but we received: ${value}`);
}
function pruneAbsentRows(store, presentReferenceIds, isProtected = () => false) {
	for (const row of Array.from(store.data.values())) {
		if (presentReferenceIds.has(row.referenceId)) continue;
		if (isProtected(row.referenceId)) continue;
		store.remove(row);
	}
}
/**
* How long a row this client just created or deleted is protected from a list
* response that was already in flight when the mutation landed.
*
* ponytail: a time window, because neither the list endpoints nor the rows
* carry a revision to compare against. Swap it for request/store revisions if
* the API grows them.
*/
const LOCAL_MUTATION_GRACE_MS = 3e4;
/**
* Remembers rows this client just created or deleted. A list request that
* started before the mutation answers without it (or still with it), and
* applying that older response would prune the new row or resurrect the deleted
* one until the next poll.
*/
function createLocalMutationLog() {
	const mutatedAt = /* @__PURE__ */ new Map();
	return {
		record(referenceId) {
			const now = Date.now();
			for (const [id, at] of mutatedAt) if (now - at > LOCAL_MUTATION_GRACE_MS) mutatedAt.delete(id);
			mutatedAt.set(referenceId, now);
		},
		isRecent(referenceId) {
			const at = mutatedAt.get(referenceId);
			if (at === void 0) return false;
			if (Date.now() - at <= LOCAL_MUTATION_GRACE_MS) return true;
			mutatedAt.delete(referenceId);
			return false;
		},
		reset() {
			mutatedAt.clear();
		}
	};
}
//#endregion
//#region src/core/sync/stores/action-card-store.ts
const ACTION_CARDS_NUDGE_DEBOUNCE_MS = 50;
const HISTORY_REFRESH_DEBOUNCE_MS = 250;
const IMPRESSION_BATCH_MS = 1e3;
const SCREEN_RELEASE_DELAY_MS = 2e3;
const RUN_POLL_MS = [2e3, 5e3];
const RUN_POLLS_AT_FIRST_PACE = 3;
const RUN_LEASE_MS = 3e5;
const FIELD_PATH_PREFIX = "fields.";
const TEMPLATES_META_ID = "action_card_templates";
const MAX_SEEN_MS = 2147483647;
const rowKey = (cardId, placement) => `${cardId}:${placement.surface}:${placement.position}`;
const showingKey = (cardId, placement, now) => `${rowKey(cardId, placement)}:${now.toISOString().slice(0, 10)}`;
const RUN_REFUSAL_CODES = [
	"already_running",
	"already_done",
	"card_changed",
	"no_such_action",
	"not_runnable"
];
function isRunRefusalCode(code) {
	return RUN_REFUSAL_CODES.some((known) => known === code);
}
const childSnapshotOf = (child) => ({
	id: child.id,
	status: child.status,
	dismissedAt: child.dismissedAt,
	dismissedWithParent: child.dismissedWithParent,
	syncId: child.syncId
});
const restoredStatus = (card) => card.result?.error ? "failed" : "draft";
const codingAgentRuns = (card) => {
	if (card.actionType !== "coding_agent") return false;
	const run = card.content?.run;
	if (!run || typeof run !== "object" || Array.isArray(run)) return false;
	return run.state === "starting" || run.state === "working";
};
const onlyTheUserAnswers = (card) => card.actionType === "question" || card.actionType === "routine_offer" || card.reportId !== null && (card.actionType === "ask_user" || card.actionType === "routine_reach_out");
const isDismissedWithParent = (child, now) => (child.status === "draft" || child.status === "failed") && !child.pinnedAt && !(child.snoozedUntil && child.snoozedUntil > now) && child.actionType !== "tool_approval" && !onlyTheUserAnswers(child) && !codingAgentRuns(child);
const OPEN_CHAT_CARD_STATUSES = /* @__PURE__ */ new Set([
	"draft",
	"failed",
	"running"
]);
const isOpenCard = (card) => card.status !== void 0 && OPEN_CHAT_CARD_STATUSES.has(card.status) || !!card.followUpRequestId;
var ActionCardStore = class ActionCardStore extends Store {
	static persisted = true;
	static loadStrategy = "eager";
	static schemaVersion = 5;
	ModelClass = ActionCard;
	actionCardsChangedUnsub = null;
	nudgeFlushTimer = null;
	nudgeSyncInFlight = false;
	nudgePendingResync = false;
	executionLeases = /* @__PURE__ */ new Map();
	settledFieldValues = /* @__PURE__ */ new Map();
	cardIdsByLocalId = observable.map();
	missingLocalIds = /* @__PURE__ */ new Set();
	pendingLocalIds = /* @__PURE__ */ new Set();
	localIdsLoad = null;
	localIdLoads = /* @__PURE__ */ new Map();
	cardIdsByChat = observable.map();
	cardIdsByMeeting = observable.map();
	cardIdsByAssistantMessage = observable.map();
	cardIdsByParent = observable.map();
	historyRefreshTimer = null;
	queuedImpressions = [];
	showings = /* @__PURE__ */ new Map();
	shownRows = /* @__PURE__ */ new Set();
	queuedTimesOnScreen = /* @__PURE__ */ new Map();
	impressionSendTimer = null;
	impressionSend = Promise.resolve();
	openCardsLoad = null;
	historyBounded = null;
	loadedForScreens = /* @__PURE__ */ new Set();
	screenHolds = /* @__PURE__ */ new Map();
	screenReleaseTimer = null;
	/** Bumped when stored history may have changed, so Home counts it again. */
	historyVersion = 0;
	templates = null;
	templatesFailed = false;
	templatesFetch = null;
	templatesWanted = false;
	latestTemplatesHash;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("action-cards");
		observe(this.data, (change) => {
			if (change.type === "add") this.indexCard(change.newValue);
			else if (change.type === "delete") this.unindexCard(change.oldValue);
		});
		makeObservable(this, {
			autoStartedChatIds: computed,
			historyVersion: observable,
			templates: observable.ref,
			templatesFailed: observable,
			persistAndHydrateActionCards: action,
			markFollowUpRequested: action.bound,
			updateContent: action.bound,
			updateContentNow: action.bound,
			dismiss: action.bound,
			pin: action.bound,
			snooze: action.bound,
			acknowledge: action.bound,
			completeExternally: action.bound,
			keepWatched: action.bound,
			moveWatchedToWorking: action.bound,
			notWorkingOnWatched: action.bound,
			claim: action.bound,
			complete: action.bound,
			sendOnServer: action.bound,
			stopWaiting: action.bound,
			replaceFields: action.bound,
			run: action.bound,
			getFresh: action.bound,
			runToCompletion: action.bound,
			pollUntilRunEnds: action.bound,
			restore: action.bound
		});
		this.actionCardsChangedUnsub = globalEventBus.on(EventNames.ACTION_CARDS_CHANGED, ({ data }) => this.handleActionCardsChanged(data?.card_id));
	}
	/**
	* Reconcile server state after the backend nudges us that a card changed.
	* Debounced so a burst of nudges collapses into a single sync.
	*/
	handleActionCardsChanged(cardId) {
		if (cardId) this.refreshCard(cardId);
		if (this.nudgeFlushTimer) clearTimeout(this.nudgeFlushTimer);
		this.nudgeFlushTimer = setTimeout(() => {
			this.nudgeFlushTimer = null;
			this.runNudgeSync();
		}, ACTION_CARDS_NUDGE_DEBOUNCE_MS);
	}
	async refreshCard(cardId) {
		try {
			const row = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${cardId}`);
			const localCard = this.data.get(cardId);
			if (localCard && localCard.syncId > row.sync_id) return;
			await this.persistAndHydrateActionCards([row]);
		} catch (error) {
			this.sdk.logger.warn("Failed to fetch a nudged action card", {
				metadata: {
					cardId,
					error
				},
				context: "ActionCardStore:refreshCard"
			});
		}
	}
	/** If a sync is already in flight, queue exactly one follow-up instead of overlapping */
	async runNudgeSync() {
		if (this.nudgeSyncInFlight) {
			this.nudgePendingResync = true;
			return;
		}
		this.nudgeSyncInFlight = true;
		try {
			await this.sdk.syncNow();
		} catch (err) {
			this.sdk.logger.error("Sync failed on action cards changed nudge", {
				metadata: { error: err },
				context: "ActionCardStore:handleActionCardsChanged"
			});
		} finally {
			this.nudgeSyncInFlight = false;
			if (this.nudgePendingResync) {
				this.nudgePendingResync = false;
				this.runNudgeSync();
			}
		}
	}
	/**
	* Test-only cleanup. Unsubscribes the ACTION_CARDS_CHANGED listener so it
	* doesn't leak when the SDK singleton is replaced between tests.
	*/
	teardownForTesting() {
		this.actionCardsChangedUnsub?.unsubscribe();
		this.actionCardsChangedUnsub = null;
		this.cancelScheduledWork();
	}
	/** Logout drops syncs and releases still waiting to run, which belong to the old account */
	reset() {
		this.cancelScheduledWork();
		super.reset();
	}
	cancelScheduledWork() {
		if (this.nudgeFlushTimer) {
			clearTimeout(this.nudgeFlushTimer);
			this.nudgeFlushTimer = null;
		}
		this.cancelHistoryRefresh();
		this.cancelScreenRelease();
		if (this.impressionSendTimer) {
			clearTimeout(this.impressionSendTimer);
			this.impressionSendTimer = null;
		}
	}
	get isHistoryBounded() {
		this.historyBounded ??= !!this.sdk.options.isActionCardHistoryBounded?.();
		return this.historyBounded;
	}
	/** The newest page alone misses older drafts; this loads every open card. */
	ensureOpenCardsLoaded() {
		this.openCardsLoad ??= this.loadOpenCards().catch((error) => {
			this.openCardsLoad = null;
			throw error;
		});
		return this.openCardsLoad;
	}
	async loadOpenCards() {
		const queries = this.sdk.storage.entities.actionCards;
		if (!queries?.listOpenIds) return;
		const generation = this.generation;
		await this.loadCardsById(await queries.listOpenIds(), generation);
	}
	async loadCardsById(ids, generation = this.generation) {
		const queries = this.sdk.storage.entities.actionCards;
		const missing = ids.filter((id) => !this.data.has(id));
		if (!queries || missing.length === 0) return;
		const records = await this.fetchRecordsByIdChunks(queries.getByIds.bind(queries), missing);
		if (!this.isCurrentGeneration(generation)) return;
		this.addLoadedRecords(records);
	}
	async refreshOrReloadFromStorage() {
		await super.refreshOrReloadFromStorage();
		if (!this.isLoaded) await this.refreshResidentCardsFromStorage();
	}
	async refreshResidentCardsFromStorage() {
		const queries = this.sdk.storage.entities.actionCards;
		const ids = [...this.data.keys()];
		if (!queries || ids.length === 0) return;
		const generation = this.generation;
		const records = await this.fetchRecordsByIdChunks(queries.getByIds.bind(queries), ids);
		if (!this.isCurrentGeneration(generation)) return;
		this.applyStoredRecords(records);
	}
	addLoadedRecords(records) {
		this.applyStoredRecords(records);
	}
	applyStoredRecords(records) {
		runInAction(() => {
			for (const record of records) {
				const existing = this.data.get(record.id);
				if (existing && existing.syncId >= (record.syncId ?? 0)) continue;
				const card = this.addRecord(record);
				this.rebaseSettledFields(card);
				if (!existing && this.isHistoryBounded && !isOpenCard(record)) this.loadedForScreens.add(record.id);
			}
		});
	}
	/** Loads cards for a screen and keeps the closed ones until the returned release. */
	holdCards(ids) {
		return this.holdForScreen(() => this.loadCardsById(ids), () => ids);
	}
	holdChatCards(chatId) {
		return this.holdForScreen(() => this.loadCardsForChat(chatId), () => [...this.cardIdsByChat.get(chatId) ?? []]);
	}
	holdCardsByLocalIds(localIds) {
		return this.holdForScreen(() => this.loadCardsByLocalIds(localIds), () => localIds.flatMap((localId) => {
			const id = this.cardIdsByLocalId.get(localId);
			return id ? [id] : [];
		}));
	}
	holdForScreen(load, idsOf) {
		const screen = Symbol("screen");
		const held = /* @__PURE__ */ new Set();
		let released = false;
		const claim = () => {
			if (released) return;
			for (const id of idsOf()) {
				if (!this.loadedForScreens.has(id) || held.has(id)) continue;
				const screens = this.screenHolds.get(id) ?? /* @__PURE__ */ new Set();
				screens.add(screen);
				this.screenHolds.set(id, screens);
				held.add(id);
			}
		};
		claim();
		load().then(claim, (error) => {
			this.sdk.logger.error("Failed to load action cards for a screen", {
				metadata: { error },
				context: "ActionCardStore:holdForScreen"
			});
		}).then(() => {
			if (released) this.scheduleScreenRelease();
		});
		return () => {
			if (released) return;
			released = true;
			for (const id of held) this.screenHolds.get(id)?.delete(screen);
			this.scheduleScreenRelease();
		};
	}
	scheduleScreenRelease() {
		if (this.screenReleaseTimer) return;
		this.screenReleaseTimer = setTimeout(() => {
			this.screenReleaseTimer = null;
			this.releaseUnheldCards();
		}, SCREEN_RELEASE_DELAY_MS);
	}
	cancelScreenRelease() {
		if (!this.screenReleaseTimer) return;
		clearTimeout(this.screenReleaseTimer);
		this.screenReleaseTimer = null;
	}
	releaseUnheldCards() {
		let waitingOnWrites = false;
		runInAction(() => {
			for (const id of [...this.loadedForScreens]) {
				if (this.screenHolds.get(id)?.size) continue;
				const card = this.data.get(id);
				if (card && isOpenCard(card)) {
					this.loadedForScreens.delete(id);
					this.screenHolds.delete(id);
					continue;
				}
				if (card && (this.executionLeases.has(id) || this.sdk.transactionQueue.getTransactionForModel(this.tableName, id))) {
					waitingOnWrites = true;
					continue;
				}
				this.loadedForScreens.delete(id);
				this.screenHolds.delete(id);
				if (card) this.data.delete(id);
			}
		});
		if (waitingOnWrites) this.scheduleScreenRelease();
	}
	/** Coalesced, so a bootstrap's pages bump the version once. */
	refreshHistory() {
		this.cancelHistoryRefresh();
		this.historyRefreshTimer = setTimeout(() => {
			this.historyRefreshTimer = null;
			runInAction(() => {
				this.historyVersion += 1;
			});
		}, HISTORY_REFRESH_DEBOUNCE_MS);
	}
	cancelHistoryRefresh() {
		if (!this.historyRefreshTimer) return;
		clearTimeout(this.historyRefreshTimer);
		this.historyRefreshTimer = null;
	}
	/** History storage holds outside memory, per bucket; null when it can't be counted. */
	async countStoredHistory(query) {
		const queries = this.sdk.storage.entities.actionCards;
		if (!queries?.countHistory) return null;
		try {
			return await queries.countHistory(query);
		} catch (error) {
			this.sdk.logger.error("Failed to count action card history", {
				metadata: { error },
				context: "ActionCardStore:countStoredHistory"
			});
			return null;
		}
	}
	/** The newest history storage holds outside memory, in one bucket. */
	async listStoredHistoryPage(query, bucket, limit) {
		const queries = this.sdk.storage.entities.actionCards;
		if (!queries?.listHistoryPage) return null;
		try {
			return await queries.listHistoryPage(query, bucket, limit);
		} catch (error) {
			this.sdk.logger.error("Failed to read action card history", {
				metadata: { error },
				context: "ActionCardStore:listStoredHistoryPage"
			});
			return null;
		}
	}
	async hydrate() {
		const bounded = this.isHistoryBounded;
		if (bounded) await this.hydrateOpenCards();
		else await super.hydrate();
		await this.revokeOpenCardsForChatsMissingLocally();
		if (bounded) this.refreshHistory();
	}
	/** Home needs only open cards up front; screens load the rest when they ask. */
	async hydrateOpenCards() {
		await this.ensureOpenCardsLoaded();
		if (this.effectiveLoadStrategy !== "eager" || !ActionCardStore.persisted) return;
		runInAction(() => {
			this.isLoaded = true;
		});
	}
	/** A reply can link a card from another chat, which that chat's load misses. */
	loadCardsByLocalIds(localIds) {
		if (!this.sdk.storage.entities.actionCards?.listIdsByLocalIds) return Promise.resolve();
		const loads = /* @__PURE__ */ new Set();
		for (const localId of localIds) {
			const loading = this.localIdLoads.get(localId);
			if (loading) loads.add(loading);
			else if (!this.cardIdsByLocalId.has(localId) && !this.missingLocalIds.has(localId)) this.pendingLocalIds.add(localId);
		}
		if (this.pendingLocalIds.size > 0) {
			this.localIdsLoad ??= Promise.resolve().then(() => this.loadPendingLocalIds());
			loads.add(this.localIdsLoad);
		}
		return loads.size === 0 ? Promise.resolve() : Promise.all(loads).then(() => void 0);
	}
	async loadPendingLocalIds() {
		const wanted = [...this.pendingLocalIds];
		this.pendingLocalIds.clear();
		this.localIdsLoad = null;
		for (const localId of wanted) this.missingLocalIds.add(localId);
		const load = this.readCardsByLocalIds(wanted);
		for (const localId of wanted) this.localIdLoads.set(localId, load);
		await load;
		for (const localId of wanted) if (this.localIdLoads.get(localId) === load) this.localIdLoads.delete(localId);
	}
	async readCardsByLocalIds(wanted) {
		const generation = this.generation;
		try {
			const ids = await this.sdk.storage.entities.actionCards?.listIdsByLocalIds?.(wanted) ?? [];
			await this.loadCardsById(ids, generation);
			for (const localId of wanted) if (this.cardIdsByLocalId.has(localId)) this.missingLocalIds.delete(localId);
		} catch (error) {
			for (const localId of wanted) this.missingLocalIds.delete(localId);
			this.sdk.logger.error("Failed to load linked action cards", {
				metadata: { error },
				context: "ActionCardStore:loadCardsByLocalIds"
			});
		}
	}
	async loadCardsForChat(chatId) {
		const generation = this.generation;
		const records = await this.sdk.storage.getManyByIndex(this.tableName, "chatId", chatId);
		if (!this.isCurrentGeneration(generation)) return;
		this.addLoadedRecords(records);
	}
	async loadCardsForMeeting(meetingId) {
		const generation = this.generation;
		const records = await this.sdk.storage.getManyByIndex(this.tableName, "sourceMeetingId", meetingId);
		if (!this.isCurrentGeneration(generation)) return;
		this.addLoadedRecords(records);
	}
	/**
	* Drop open cards whose chat is gone locally and gone on the server.
	*
	* A chat missing from the threads table is not proof it was deleted.
	* Messaging-channel chats and hidden chats are never stored there, and a
	* local revoke is not written back, so this only applies a server 404 or
	* an already-revoked row. A meeting's assist chat is always hidden, so
	* this skips meeting cards.
	*/
	async revokeOpenCardsForChatsMissingLocally() {
		try {
			await this.reconcileOrphanedOpenCards();
		} catch (error) {
			this.sdk.logger.error("Failed to reconcile action cards for missing chats", {
				metadata: { error },
				context: "ActionCardStore:revokeOpenCardsForChatsMissingLocally"
			});
		}
	}
	/**
	* Look up the chat of each open card that has no local thread. Revoke the card when the chat is gone.
	* When the window turns off, this can run before the refill lands. It then sends one request per card and loses no data.
	*/
	async reconcileOrphanedOpenCards() {
		if (this.sdk.storage.fullBootstrapPending) return;
		if (this.sdk.options.syncChatWindow?.()) return;
		const threadQueries = this.sdk.storage.entities.threads;
		if (!threadQueries) return;
		const openCards = [...this.data.values()].filter((card) => card.chatId && !card.sourceMessagingChannelType && !card.sourceMeetingId && OPEN_CHAT_CARD_STATUSES.has(card.status));
		if (openCards.length === 0) return;
		const chatIds = [...new Set(openCards.map((card) => card.chatId))];
		const storedIds = new Set(await threadQueries.existingIds(chatIds));
		const candidates = openCards.filter((card) => this.isChatGone(card.chatId, storedIds));
		if (candidates.length === 0) return;
		const missingOnServer = [];
		const revokedOnServer = [];
		await Promise.all(candidates.map(async (card) => {
			const lookup = await this.lookupOrphanedCard(card);
			if (lookup === "missing") missingOnServer.push(card);
			else if (lookup) revokedOnServer.push(lookup);
		}));
		this.revokeCards(missingOnServer);
		await this.persistCards([...missingOnServer, ...revokedOnServer]);
	}
	async lookupOrphanedCard(card) {
		try {
			const row = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${card.id}`);
			if (row.status !== "revoked") return null;
			return this.add(this.mapDbActionCard(row));
		} catch (error) {
			if (error instanceof HttpError && error.status === 404) return "missing";
			return null;
		}
	}
	revokeOpenCardsForDeletedChat(chatId) {
		const cards = this.getByChatId(chatId).filter((card) => OPEN_CHAT_CARD_STATUSES.has(card.status));
		const restores = cards.map((card) => ({
			card,
			status: card.status,
			resolvedAt: card.resolvedAt
		}));
		this.revokeCards(cards);
		return restores;
	}
	restoreRevokedCards(restores) {
		runInAction(() => {
			for (const restore of restores) {
				restore.card.status = restore.status;
				restore.card.resolvedAt = restore.resolvedAt;
			}
		});
	}
	async persistRevokedCards(restores) {
		await this.persistCards(restores.map((restore) => restore.card));
	}
	isChatGone(chatId, storedIds) {
		const thread = this.sdk.stores.threads.getById(chatId);
		if (thread && !thread.deletedAt) return false;
		if (!thread && storedIds.has(chatId)) return false;
		return true;
	}
	revokeCards(cards) {
		if (cards.length === 0) return;
		const now = /* @__PURE__ */ new Date();
		runInAction(() => {
			for (const card of cards) {
				card.status = "revoked";
				card.resolvedAt = card.resolvedAt ?? now;
			}
		});
	}
	async persistCards(cards) {
		if (cards.length === 0 || !ActionCardStore.persisted) return;
		await this.sdk.storage.bulkSet(this.tableName, [...cards]);
	}
	mapDbActionCard(row) {
		return {
			id: row.id,
			localId: row.local_id,
			chatId: row.chat_id,
			..."source_messaging_channel_type" in row ? { sourceMessagingChannelType: row.source_messaging_channel_type } : {},
			..."source_meeting_id" in row ? { sourceMeetingId: row.source_meeting_id ?? null } : {},
			assistantMessageId: row.assistant_message_id,
			toolUseIndex: row.tool_use_index,
			entityIndex: row.entity_index ?? 0,
			origin: row.origin,
			actionType: row.action_type,
			status: row.status,
			content: row.content,
			result: row.result,
			externalRef: row.external_ref,
			context: row.context ?? null,
			importance: row.importance ?? null,
			expiresAt: row.expires_at ? parseDate(row.expires_at) : null,
			dueAt: row.due_at ? parseDate(row.due_at) : null,
			dueAtEstimated: row.due_at_estimated ?? false,
			autoReview: row.auto_review ?? null,
			resolvedAt: row.resolved_at ? parseDate(row.resolved_at) : null,
			dismissedAt: row.dismissed_at ? parseDate(row.dismissed_at) : null,
			pinnedAt: row.pinned_at ? parseDate(row.pinned_at) : null,
			snoozedUntil: row.snoozed_until ? parseDate(row.snoozed_until) : null,
			followUpChatId: row.follow_up_chat_id ?? null,
			followUpRequestId: row.follow_up_request_id ?? null,
			followUpRepliedAt: row.follow_up_replied_at ?? null,
			followUpReplyKind: row.follow_up_reply_kind ?? null,
			reportId: row.report_id ?? null,
			reportTemplateId: row.report_template_id ?? null,
			bootstrapDecision: row.bootstrap_decision ?? null,
			watch: row.watch ?? null,
			..."parent_id" in row ? { parentId: row.parent_id ?? null } : {},
			..."dismissed_with_parent" in row ? { dismissedWithParent: !!row.dismissed_with_parent } : {},
			backend: parseBackendName(row.backend),
			syncId: row.sync_id,
			computed: row.computed ?? null,
			createdAt: parseDate(row.created_at),
			updatedAt: parseDate(row.updated_at),
			deletedAt: row.deleted_at ? parseDate(row.deleted_at) : void 0
		};
	}
	async persistActionCard(card) {
		if (!ActionCardStore.persisted) return;
		await this.sdk.storage.set(this.tableName, card);
	}
	async persistAndHydrateActionCards(rows) {
		try {
			const deletedIds = rows.filter((row) => row.deleted_at).map((row) => row.id);
			const toAdd = rows.filter((row) => !row.deleted_at).map((row) => this.mapDbActionCard(row));
			if (toAdd.length > 0) {
				const bounded = this.isHistoryBounded;
				const addedCards = (bounded ? toAdd.filter((card) => isOpenCard(card) || this.data.has(card.id)) : toAdd).map((card) => this.add(card));
				for (const card of addedCards) this.rebaseSettledFields(card);
				if (ActionCardStore.persisted) {
					const storedOnly = toAdd.filter((card) => !this.data.has(card.id)).map((card) => new this.ModelClass(card, this, this.sdk.logger));
					await this.sdk.storage.bulkSet(this.tableName, [...addedCards, ...storedOnly]);
					this.missingLocalIds.clear();
				}
				if (bounded) this.refreshHistory();
			}
			if (deletedIds.length > 0) {
				runInAction(() => {
					for (const id of deletedIds) this.data.delete(id);
				});
				if (ActionCardStore.persisted) await this.sdk.storage.bulkRemove(this.tableName, deletedIds);
			}
		} catch (error) {
			const caughtError = error instanceof Error ? error : new Error(String(error));
			this.sdk.logger.error(caughtError, {
				metadata: { message: "Error processing action cards:" },
				context: "ActionCardStore:persistAndHydrateActionCards"
			});
			throw caughtError;
		}
	}
	markFollowUpRequested(cardIds, requestId) {
		const before = /* @__PURE__ */ new Map();
		runInAction(() => {
			for (const cardId of cardIds) {
				const card = this.data.get(cardId);
				if (!card) continue;
				before.set(cardId, card.followUpRequestId);
				card.followUpRequestId = requestId;
			}
		});
		return () => {
			runInAction(() => {
				for (const [cardId, requestIdBefore] of before) {
					const card = this.data.get(cardId);
					if (card?.followUpRequestId !== requestId) continue;
					card.followUpRequestId = requestIdBefore;
				}
			});
		};
	}
	async updateContent(id, content) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const snapshot = {
			content: card.content,
			syncId: card.syncId
		};
		runInAction(() => {
			card.content = content;
		});
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: { content },
				snapshot,
				handlerOverride: {
					endpoint: `${this.apiEndpoint}/${id}`,
					method: "PATCH"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: { content },
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		await this.persistActionCard(card);
		return card;
	}
	/**
	* Unqueued content PATCH that resolves on server ack, not enqueue. Callers that
	* must know the edit landed before doing something else with the card (e.g.
	* claiming it) need this instead of `updateContent`, whose queued write can settle
	* after that next step and get rejected once the card is no longer editable.
	*/
	async updateContentNow(id, content) {
		const response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}`, {
			method: "PATCH",
			body: JSON.stringify({ content })
		});
		const card = this.add(this.mapDbActionCard(response));
		await this.persistActionCard(card);
		return card;
	}
	async dismiss(id, dismissal) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const now = /* @__PURE__ */ new Date();
		const children = this.getChildren(id).filter((child) => isDismissedWithParent(child, now));
		const snapshot = {
			status: card.status,
			dismissedAt: card.dismissedAt,
			syncId: card.syncId,
			changedChildren: children.map(childSnapshotOf)
		};
		runInAction(() => {
			card.status = "dismissed";
			card.dismissedAt = now;
			for (const child of children) {
				child.status = "dismissed";
				child.dismissedAt = now;
				child.dismissedWithParent = true;
			}
		});
		await this.enqueuePost(id, "dismiss", dismissal ?? {}, snapshot);
		await this.persistCards([card, ...children]);
		return card;
	}
	async pin(id, pinned) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const pinnedAt = pinned ? card.pinnedAt ?? /* @__PURE__ */ new Date() : null;
		await this.postSettledField(card, "pinnedAt", pinnedAt, "pin", { pinned });
		return card;
	}
	async snooze(id, until) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		await this.postSettledField(card, "snoozedUntil", until, "snooze", { until: until?.toISOString() ?? null });
		return card;
	}
	/**
	* Sets a pin or snooze field at once and waits for the server.
	* Writes to one field that overlap, such as a snooze and its Undo, roll back to the value the
	* server last accepted, never to an earlier write's unconfirmed value. When the server accepted an
	* earlier write while a later one was waiting, the later write's failure puts the accepted value back.
	*/
	async postSettledField(card, field, value, action, payload) {
		const key = `${field}:${card.id}`;
		const settled = this.settledFieldValues.get(key) ?? {
			value: card[field],
			writes: 0,
			latest: 0
		};
		settled.writes += 1;
		settled.latest += 1;
		const write = settled.latest;
		this.settledFieldValues.set(key, settled);
		const snapshot = {
			[field]: settled.value,
			syncId: card.syncId
		};
		runInAction(() => {
			card[field] = value;
		});
		try {
			await this.persistActionCard(card);
			await this.enqueuePost(card.id, action, payload, snapshot, { waitForServerAck: true });
			if (card.syncId === snapshot.syncId) settled.value = value;
			if (write === settled.latest && card.syncId === snapshot.syncId && card[field] !== value) {
				runInAction(() => {
					card[field] = value;
				});
				await this.persistActionCard(card);
			}
		} catch (error) {
			if (write === settled.latest && card.syncId === snapshot.syncId) {
				runInAction(() => {
					card[field] = settled.value;
				});
				await this.persistActionCard(card);
			}
			throw error;
		} finally {
			settled.writes -= 1;
			if (settled.writes === 0) this.settledFieldValues.delete(key);
		}
	}
	rebaseSettledFields(card) {
		for (const field of ["pinnedAt", "snoozedUntil"]) {
			const settled = this.settledFieldValues.get(`${field}:${card.id}`);
			if (settled) settled.value = card[field];
		}
	}
	async recordDismissalReason(id, dismissal) {
		await this.enqueuePost(id, "dismissal-reason", dismissal, {});
	}
	/**
	* A feed list has the card's row on screen at `placement`. Tells the server so once
	* per row and UTC day in an app session: a card that moves to another row is sent again. The rows
	* a list shows together leave in one request. The request is sent once, so a failed
	* one is dropped and the stats can miss impressions
	*/
	recordImpression(id, placement) {
		const now = /* @__PURE__ */ new Date();
		this.shownRows.add(rowKey(id, placement));
		if (this.showings.has(showingKey(id, placement, now))) return;
		this.startShowing(id, placement, now);
	}
	startShowing(id, placement, now) {
		const showing = {
			id: v4(),
			seenMs: 0
		};
		this.showings.set(showingKey(id, placement, now), showing);
		this.queuedImpressions.push({
			id: showing.id,
			card_id: id,
			surface: placement.surface,
			position: placement.position,
			list_size: placement.listSize
		});
		this.sendImpressionsSoon();
		return showing;
	}
	/**
	* The card's row has been on screen for `ms` more. The time is added to the row's
	* showing and sent as the showing's total, so a card that scrolls back onto the row
	* keeps counting. A row that was never sent as shown has no showing to add to
	*/
	recordTimeOnScreen(id, placement, ms) {
		if (!this.shownRows.has(rowKey(id, placement))) return;
		const now = /* @__PURE__ */ new Date();
		const showing = this.showings.get(showingKey(id, placement, now)) ?? this.startShowing(id, placement, now);
		showing.seenMs = Math.min(showing.seenMs + Math.max(0, ms), MAX_SEEN_MS);
		this.queuedTimesOnScreen.set(showing.id, Math.round(showing.seenMs));
		this.sendImpressionsSoon();
	}
	sendImpressionsSoon() {
		this.impressionSendTimer ??= setTimeout(() => {
			this.impressionSendTimer = null;
			this.impressionSend = this.impressionSend.then(() => this.sendQueuedImpressions());
		}, IMPRESSION_BATCH_MS);
	}
	async sendQueuedImpressions() {
		if (!this.queuedImpressions.length && !this.queuedTimesOnScreen.size) return;
		const impressions = this.queuedImpressions.splice(0);
		const timeOnScreen = [...this.queuedTimesOnScreen].map(([impressionId, seenMs]) => ({
			impression_id: impressionId,
			seen_ms: seenMs
		}));
		this.queuedTimesOnScreen.clear();
		try {
			await this.sdk.apiClient.fetch(`${this.apiEndpoint}/impressions`, {
				method: "POST",
				body: JSON.stringify({
					impressions,
					time_on_screen: timeOnScreen
				}),
				backoff: NO_BACKOFF
			});
		} catch (error) {
			this.sdk.logger.warn("Failed to record action card impressions", { error });
		}
	}
	async enqueuePost(id, action, payload, snapshot, options = {}) {
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload,
				snapshot,
				handlerOverride: {
					endpoint: `${this.apiEndpoint}/${id}/${action}`,
					method: "POST"
				}
			}, {
				strictPersist: true,
				...options
			});
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload,
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
	}
	async acknowledge(id) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const snapshot = {
			status: card.status,
			resolvedAt: card.resolvedAt,
			result: card.result,
			syncId: card.syncId
		};
		runInAction(() => {
			card.status = "completed";
			card.resolvedAt = /* @__PURE__ */ new Date();
			card.result = { acknowledged: true };
		});
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: {},
				snapshot,
				handlerOverride: {
					endpoint: `${this.apiEndpoint}/${id}/acknowledge`,
					method: "POST"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: {},
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		await this.persistActionCard(card);
		return card;
	}
	/** Keep a card its watcher suggested resolving: the suggestion goes and the watcher stops */
	async keepWatched(id) {
		const response = await this.sdk.apiClient.fetch(`card-watchers/cards/${id}/keep`, { method: "POST" });
		return this.upsert(response);
	}
	async moveWatchedToWorking(id) {
		const response = await this.sdk.apiClient.fetch(`card-watchers/cards/${id}/working`, { method: "POST" });
		return this.upsert(response);
	}
	async notWorkingOnWatched(id) {
		const response = await this.sdk.apiClient.fetch(`card-watchers/cards/${id}/not-working`, { method: "POST" });
		return this.upsert(response);
	}
	async completeExternally(id) {
		const response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}/complete-externally`, { method: "POST" });
		const card = this.add(this.mapDbActionCard(response));
		await this.persistActionCard(card);
		return card;
	}
	/** The raw body; the caller parses it */
	async fetchPlayground(catalogVersion, timezone) {
		const query = new URLSearchParams({
			catalog_version: String(catalogVersion),
			timezone
		});
		return this.sdk.apiClient.fetch(`${this.apiEndpoint}/playground?${query}`, { method: "GET" });
	}
	async claim(id) {
		const response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}/claim`, {
			method: "POST",
			body: JSON.stringify({ processing_request_id: this.executionLeases.get(id) ?? null })
		});
		this.executionLeases.set(id, response.processing_request_id);
		const card = this.add(this.mapDbActionCard(response.card));
		await this.persistActionCard(card);
		return card;
	}
	async complete(id, params) {
		const processingRequestId = this.executionLeases.get(id);
		try {
			const response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}/complete`, {
				method: "POST",
				body: JSON.stringify({
					...params,
					processing_request_id: processingRequestId
				})
			});
			const card = this.add(this.mapDbActionCard(response));
			await this.persistActionCard(card);
			return card;
		} finally {
			this.executionLeases.delete(id);
		}
	}
	/** POSTs the version a press names (`RunVersion`) to the card's server endpoint, its `/run` or Hive Mind's send */
	async sendOnServer(endpoint, version) {
		const response = await this.sdk.apiClient.fetch(endpoint, {
			method: "POST",
			backoff: NO_BACKOFF,
			body: JSON.stringify("expectedSyncId" in version ? { expected_sync_id: version.expectedSyncId } : { seen_sync_id: version.seenSyncId })
		});
		return this.upsert(response);
	}
	async stopWaiting(id, dismiss) {
		const response = await this.sdk.apiClient.fetch(`hivemind/threads/cards/${id}/stop-waiting`, {
			method: "POST",
			body: JSON.stringify({ dismiss })
		});
		return this.upsert(response);
	}
	async replaceFields(id, fields) {
		let response;
		try {
			response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}`, {
				method: "PUT",
				body: JSON.stringify({ fields })
			});
		} catch (error) {
			const refusal = fieldsRefusalOf(error);
			if (refusal) return refusal;
			throw error;
		}
		return {
			card: await this.upsert(response),
			savedSyncId: response.sync_id
		};
	}
	/**
	* The row a 202 answer carries is still running; the caller polls for its end.
	* With `seenSyncId`, an agent write after that version (`agent_written_sync_id`) has the server refuse it as
	* `card_changed`.
	*/
	async run(id, key, seenSyncId) {
		let response;
		try {
			response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}/run`, {
				method: "POST",
				body: JSON.stringify({
					action_key: key,
					seen_sync_id: seenSyncId
				}),
				backoff: NO_BACKOFF
			});
		} catch (error) {
			const refusal = runRefusalOf(error);
			if (refusal) return refusal;
			throw error;
		}
		const card = await this.upsert(response);
		return {
			card,
			terminal: card.status !== "running"
		};
	}
	/**
	* Where to open the card in its provider's app. A pending Gmail card is first saved as a Gmail draft, or its
	* draft brought up to date with it; the answer's card holds the draft, or the text the user changed in Gmail
	*/
	async openInApp(id) {
		let response;
		try {
			response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}/open`, {
				method: "POST",
				backoff: NO_BACKOFF
			});
		} catch (error) {
			const refusal = openRefusalOf(error);
			if (refusal) return refusal;
			throw error;
		}
		return {
			url: response.url,
			card: await this.upsert(response.card)
		};
	}
	async getFresh(id) {
		let response;
		try {
			response = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${id}`);
		} catch (error) {
			if (error instanceof HttpError && error.status === 404) return null;
			throw error;
		}
		return this.upsert(response);
	}
	/**
	* Runs the action and, when the server answers before the run ends, polls the card
	* until the run ends or `leaseMs` passes; returns the last card seen either way.
	*/
	async runToCompletion(id, key, options = {}) {
		const outcome = await this.run(id, key, options.seenSyncId);
		if (!("card" in outcome)) return outcome;
		if (outcome.terminal) return outcome.card;
		return this.pollUntilRunEnds(outcome.card, options);
	}
	/**
	* Polls a card the server is still running until its run ends, the lease
	* runs out, or the card is gone. A failed poll is logged and polled again.
	* Returns the last card seen; it is still running only when the deadline passed first.
	*/
	async pollUntilRunEnds(running, options = {}) {
		const [firstPollMs, laterPollMs] = options.pollMs ?? RUN_POLL_MS;
		let card = running;
		const deadline = Date.now() + (options.leaseMs ?? RUN_LEASE_MS);
		for (let poll = 0; !runHasEnded(card) && Date.now() < deadline; poll++) {
			await sleep(Math.min(poll < RUN_POLLS_AT_FIRST_PACE ? firstPollMs : laterPollMs, deadline - Date.now()));
			try {
				const fresh = await this.getFresh(card.id);
				if (!fresh) return card;
				card = fresh;
			} catch (error) {
				this.sdk.logger.warn("Polling a running action card failed", {
					metadata: {
						cardId: card.id,
						error
					},
					context: "ActionCardStore:pollUntilRunEnds"
				});
			}
		}
		return card;
	}
	templatesFor(actionType) {
		return this.templates?.types[actionType] ?? null;
	}
	/** A host that draws cards from the server's documents asks for them; no other client reads or fetches the set */
	wantTemplates() {
		if (this.templatesWanted) return;
		this.templatesWanted = true;
		this.loadStoredTemplates().then(() => this.noteTemplatesHash(this.latestTemplatesHash));
	}
	noteTemplatesHash(hash) {
		if (!hash) return;
		this.latestTemplatesHash = hash;
		if (!this.templatesWanted || hash === this.templates?.templates_hash || this.templatesFetch) return;
		const requested = this.latestTemplatesHash;
		this.templatesFetch = this.fetchTemplates().then((ok) => {
			this.templatesFetch = null;
			if (ok && this.latestTemplatesHash !== requested) this.noteTemplatesHash(this.latestTemplatesHash);
		});
	}
	async fetchTemplates() {
		try {
			const templateSet = await this.sdk.apiClient.fetch(`${this.apiEndpoint}/templates?catalog_version=${CATALOG_VERSION}`);
			runInAction(() => {
				this.templates = templateSet;
				this.templatesFailed = false;
			});
			await this.sdk.storage.setMetaRow?.(TEMPLATES_META_ID, {
				...templateSet,
				requested_catalog_version: CATALOG_VERSION
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.templatesFailed = true;
			});
			this.sdk.logger.warn("Fetching the action card templates failed", {
				metadata: { error },
				context: "ActionCardStore:fetchTemplates"
			});
			return false;
		}
	}
	async loadStoredTemplates() {
		const stored = await this.sdk.storage.getMetaRow?.(TEMPLATES_META_ID).catch((error) => {
			this.sdk.logger.warn("Reading the stored action card templates failed", {
				metadata: { error },
				context: "ActionCardStore:loadStoredTemplates"
			});
		});
		if (this.templates) return;
		if (typeof stored?.templates_hash !== "string" || stored.requested_catalog_version !== CATALOG_VERSION) return;
		const { requested_catalog_version: _requested, ...templateSet } = stored;
		runInAction(() => {
			this.templates = templateSet;
		});
	}
	async restore(id) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const children = this.getChildren(id).filter((child) => child.status === "dismissed" && child.dismissedWithParent);
		const snapshot = {
			status: card.status,
			dismissedAt: card.dismissedAt,
			resolvedAt: card.resolvedAt,
			result: card.result,
			snoozedUntil: card.snoozedUntil,
			syncId: card.syncId,
			changedChildren: children.map(childSnapshotOf)
		};
		runInAction(() => {
			if (card.status === "completed") card.result = null;
			card.status = restoredStatus(card);
			card.dismissedAt = null;
			card.resolvedAt = null;
			card.snoozedUntil = null;
			for (const child of children) {
				child.status = restoredStatus(child);
				child.dismissedAt = null;
				child.dismissedWithParent = false;
			}
		});
		await this.enqueuePost(id, "restore", {}, snapshot);
		await this.persistCards([card, ...children]);
		return card;
	}
	/** Takes the card out from under its parent, so it stands on its own in the Feed. */
	async detach(id) {
		const card = this.data.get(id);
		if (!card) throw new Error(`ActionCard ${id} not found`);
		const snapshot = {
			parentId: card.parentId,
			syncId: card.syncId
		};
		runInAction(() => this.reindexing(card, () => {
			card.parentId = null;
		}));
		await this.enqueuePost(id, "detach", {}, snapshot);
		await this.persistActionCard(card);
		return card;
	}
	async upsert(row) {
		const localCard = this.data.get(row.id);
		if (localCard && localCard.syncId > row.sync_id) return localCard;
		const card = this.add(this.mapDbActionCard(row));
		await this.persistActionCard(card);
		return card;
	}
	indexCard(card) {
		this.cardIdsByLocalId.set(card.localId, card.id);
		addToIndex(this.cardIdsByChat, card.chatId, card.id);
		addToIndex(this.cardIdsByMeeting, card.sourceMeetingId, card.id);
		addToIndex(this.cardIdsByAssistantMessage, card.assistantMessageId, card.id);
		addToIndex(this.cardIdsByParent, card.parentId, card.id);
	}
	unindexCard(card) {
		this.cardIdsByLocalId.delete(card.localId);
		removeFromIndex(this.cardIdsByChat, card.chatId, card.id);
		removeFromIndex(this.cardIdsByMeeting, card.sourceMeetingId, card.id);
		removeFromIndex(this.cardIdsByAssistantMessage, card.assistantMessageId, card.id);
		removeFromIndex(this.cardIdsByParent, card.parentId, card.id);
	}
	updateExistingModel(existingModel, item) {
		this.reindexing(existingModel, () => existingModel.updateData(item));
		return existingModel;
	}
	reindexing(card, change) {
		const indexedKeys = [
			"id",
			"localId",
			"chatId",
			"sourceMeetingId",
			"assistantMessageId",
			"parentId"
		];
		const before = pick(card, ...indexedKeys);
		change();
		if (indexedKeys.every((key) => before[key] === card[key])) return;
		this.unindexCard(before);
		this.indexCard(card);
	}
	clear() {
		this.cardIdsByLocalId.clear();
		this.cardIdsByChat.clear();
		this.cardIdsByMeeting.clear();
		this.cardIdsByAssistantMessage.clear();
		this.cardIdsByParent.clear();
		this.missingLocalIds.clear();
		this.pendingLocalIds.clear();
		this.localIdsLoad = null;
		this.localIdLoads.clear();
		this.cancelHistoryRefresh();
		this.openCardsLoad = null;
		this.cancelScreenRelease();
		this.loadedForScreens.clear();
		this.screenHolds.clear();
		runInAction(() => {
			this.templatesFailed = false;
			this.historyVersion += 1;
		});
		super.clear();
	}
	cardsFromIndex(index, key) {
		const cards = [];
		for (const id of index.get(key) ?? []) {
			const card = this.data.get(id);
			if (card) cards.push(card);
		}
		return cards;
	}
	getByAssistantMessageId = computedFn(function(assistantMessageId) {
		return orderBy(this.cardsFromIndex(this.cardIdsByAssistantMessage, assistantMessageId), [(card) => card.toolUseIndex ?? Number.MAX_SAFE_INTEGER, (card) => card.entityIndex]);
	});
	getByChatId = computedFn(function(chatId) {
		return orderBy(this.cardsFromIndex(this.cardIdsByChat, chatId), [
			(card) => card.createdAt.getTime(),
			(card) => card.toolUseIndex ?? Number.MAX_SAFE_INTEGER,
			(card) => card.entityIndex
		]);
	});
	getByMeetingId = computedFn(function(meetingId) {
		return orderBy(this.cardsFromIndex(this.cardIdsByMeeting, meetingId), [(card) => card.createdAt.getTime()]);
	});
	/** The cards under `parentId` that are in memory, oldest first. */
	getChildren = computedFn(function(parentId) {
		return orderBy(this.cardsFromIndex(this.cardIdsByParent, parentId), [(card) => card.createdAt.getTime(), (card) => card.localId]);
	});
	/** The chats Littlebird started from to-dos in the user's place that the user has not written in yet */
	get autoStartedChatIds() {
		const chatIds = /* @__PURE__ */ new Set();
		for (const card of this.data.values()) if (card.followUpChatId && card.facts.chat_auto_started === true) chatIds.add(card.followUpChatId);
		return chatIds;
	}
	getByLocalId(localId) {
		const id = this.cardIdsByLocalId.get(localId);
		return id ? this.data.get(id) : void 0;
	}
	/**
	* Local ids are small sequential integers, so one written into a reply — by a confused
	* model, or by content it was reading — can easily name a real card from elsewhere.
	* Resolving within the chat keeps a card reachable only where it belongs.
	*/
	getByLocalIdInChat = computedFn(function(chatId, localId) {
		return this.cardsFromIndex(this.cardIdsByChat, chatId).find((card) => card.localId === localId);
	});
	rehydrateSnapshot(snap) {
		const out = {};
		for (const key of [
			"resolvedAt",
			"dismissedAt",
			"pinnedAt",
			"snoozedUntil"
		]) if (key in snap) {
			const v = snap[key];
			out[key] = v === null ? null : typeof v === "string" ? parseDate(v) : v;
		}
		for (const key of Object.keys(snap)) if (!(key in out)) out[key] = snap[key];
		return out;
	}
	async rollback(tx) {
		if (tx.op !== "update" || !tx.snapshot) return;
		const { changedChildren = [], ...ownSnapshot } = tx.snapshot;
		const restored = [{
			...ownSnapshot,
			id: tx.modelId
		}, ...changedChildren].flatMap(({ id, ...before }) => {
			const card = this.data.get(id);
			return card && (typeof before.syncId !== "number" || before.syncId === card.syncId) ? [{
				card,
				before
			}] : [];
		});
		if (restored.length === 0) return;
		runInAction(() => {
			for (const { card, before } of restored) this.reindexing(card, () => Object.assign(card, this.rehydrateSnapshot(before)));
		});
		await this.persistCards(restored.map(({ card }) => card));
	}
};
function addToIndex(index, key, id) {
	if (!key) return;
	let set = index.get(key);
	if (!set) {
		set = observable.set();
		index.set(key, set);
	}
	set.add(id);
}
function removeFromIndex(index, key, id) {
	if (!key) return;
	const set = index.get(key);
	set?.delete(id);
	if (set?.size === 0) index.delete(key);
}
const TERMINAL_STATUSES = /* @__PURE__ */ new Set([
	"completed",
	"completed_externally",
	"failed",
	"dismissed",
	"revoked"
]);
function runHasEnded(card) {
	return TERMINAL_STATUSES.has(card.status);
}
function refusalMessage(error) {
	return error.userErrorMessage ?? error.message;
}
function isProblem(value) {
	return typeof value === "object" && value !== null && "message" in value && typeof value.message === "string";
}
function fieldsRefusalOf(error) {
	if (!(error instanceof HttpError)) return null;
	if (error.status === 404) return {
		kind: "not_found",
		message: refusalMessage(error)
	};
	const code = error.detail?.error_code;
	if (code === "card_changed") return {
		kind: "card_changed",
		message: refusalMessage(error)
	};
	if (code !== "invalid_fields") return null;
	const fields = {};
	const cardMessages = [];
	const problems = error.detail?.problems;
	for (const problem of Array.isArray(problems) ? problems : []) {
		if (!isProblem(problem)) continue;
		const { path, message } = problem;
		const key = typeof path === "string" && path.startsWith(FIELD_PATH_PREFIX) ? path.slice(7).split(".")[0] : "";
		if (!key) cardMessages.push(message);
		else if (!(key in fields)) fields[key] = message;
	}
	return {
		kind: "validation",
		fields,
		card: cardMessages.length > 0 ? cardMessages.join(" ") : Object.keys(fields).length === 0 ? refusalMessage(error) : null
	};
}
function openRefusalOf(error) {
	if (!(error instanceof HttpError)) return null;
	const message = refusalMessage(error);
	if (error.status === 404) return {
		kind: "not_found",
		message
	};
	const code = error.detail?.error_code;
	return code === "cannot_open" || code === "card_changed" ? {
		kind: code,
		message
	} : null;
}
function runRefusalOf(error) {
	if (!(error instanceof HttpError)) return null;
	const message = refusalMessage(error);
	if (error.status === 404) return {
		kind: "not_found",
		message
	};
	const code = error.detail?.error_code;
	if (code === "required_empty") {
		const fields = error.detail?.fields;
		return {
			kind: "required_empty",
			fields: Array.isArray(fields) ? fields.filter((field) => typeof field === "string") : []
		};
	}
	return isRunRefusalCode(code) ? {
		kind: code,
		message
	} : null;
}
//#endregion
//#region src/core/sync/stores/agent-store.ts
var AgentStore = class AgentStore extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	ModelClass = Agent;
	pendingCreates = /* @__PURE__ */ new Map();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("agents");
		makeObservable(this, {
			createAgent: action.bound,
			updateAgent: action.bound,
			deleteAgent: action.bound,
			updateAgentLifecycle: action.bound,
			persistAndHydrateAgents: action
		});
	}
	getById = computedFn(function(id) {
		return this.data.get(id);
	});
	mapDbAgent(dbAgent) {
		return {
			id: dbAgent.id,
			name: dbAgent.name,
			label: dbAgent.label ?? null,
			description: dbAgent.description ?? null,
			icon: parseIconConfig(dbAgent.icon),
			agentSlug: dbAgent.agent_slug ?? null,
			chatId: dbAgent.chat_id ?? null,
			pinnedAt: dbAgent.pinned_at ? parseDate(dbAgent.pinned_at) : null,
			archivedAt: dbAgent.archived_at ? parseDate(dbAgent.archived_at) : null,
			syncId: dbAgent.sync_id,
			createdAt: parseDate(dbAgent.created_at),
			updatedAt: parseDate(dbAgent.updated_at),
			deletedAt: dbAgent.deleted_at ? parseDate(dbAgent.deleted_at) : void 0
		};
	}
	async persistAndHydrateAgents(rows) {
		try {
			const toPersist = [];
			const toDelete = [];
			for (const dbAgent of rows) {
				if (!dbAgent.id) continue;
				if (dbAgent.deleted_at) {
					toDelete.push(dbAgent.id);
					continue;
				}
				toPersist.push(this.mapDbAgent(dbAgent));
			}
			if (toDelete.length > 0) {
				toDelete.forEach((id) => this.data.delete(id));
				if (AgentStore.persisted) await this.sdk.storage.bulkRemove(this.tableName, toDelete);
			}
			if (toPersist.length > 0) {
				const added = runInAction(() => toPersist.map((data) => this.add(data)));
				if (AgentStore.persisted) await this.sdk.storage.bulkSet(this.tableName, added);
			}
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error processing agents:" },
				context: "AgentStore:persistAndHydrateAgents"
			});
			throw error;
		}
	}
	async acceptTeamProposal(cardId, selectedIds, expectedSyncId) {
		try {
			const card = await this.sdk.apiClient.fetch(`agents/team-proposals/${cardId}/accept`, {
				method: "POST",
				body: JSON.stringify({
					selected_ids: selectedIds,
					expected_sync_id: expectedSyncId
				})
			});
			await this.sdk.stores.actionCards.persistAndHydrateActionCards([card]);
		} catch (error) {
			this.sdk.syncNow().catch((syncError) => {
				this.sdk.logger.error(syncError, { context: "AgentStore:acceptTeamProposal" });
			});
			throw error;
		}
	}
	shouldRetainLocal(agent) {
		return agent.syncId === 0;
	}
	/**
	* Create the agent server-side with its thread and intro turn, then
	* hydrate the row (and a thread stub) locally so navigation is instant.
	*/
	async createAgentWithIntro(options) {
		const response = await this.sdk.apiClient.fetch("agents", {
			method: "POST",
			body: JSON.stringify({
				name: options.name,
				label: null,
				icon: options.icon ?? null,
				initial_instructions: options.initialInstructions ?? null,
				pinned: options.pinned ?? false,
				start_intro: true
			})
		});
		return this.hydrateCreatedAgent(response);
	}
	async createLaunchedAgent(launchKind, launchQuery) {
		const response = await this.sdk.apiClient.fetch("agents", {
			method: "POST",
			body: JSON.stringify({
				id: v4(),
				name: "New agent",
				label: null,
				icon: null,
				launch_kind: launchKind,
				launch_query: launchQuery
			})
		});
		return this.hydrateCreatedAgent(response);
	}
	async addDefaultTeam() {
		const created = await this.sdk.apiClient.fetch("agents/default-team", { method: "POST" });
		return Promise.all(created.map((row) => this.hydrateCreatedAgent(row)));
	}
	routeLaunch(query) {
		return this.sdk.apiClient.fetch("agents/route", {
			method: "POST",
			body: JSON.stringify({ query }),
			timeoutMs: 8e3
		});
	}
	async hydrateCreatedAgent(response) {
		const agent = this.add(this.mapDbAgent(response));
		await this.persistAgent(agent);
		if (agent.chatId) await this.sdk.stores.threads.persistAndHydrateThreads([{
			id: agent.chatId,
			title: agent.name,
			agent_id: agent.id,
			starred: false,
			archived: false,
			finished: false,
			sync_id: response.sync_id,
			created_at: response.created_at,
			updated_at: response.updated_at
		}]);
		return agent;
	}
	createAgent(options) {
		const id = v4();
		const now = /* @__PURE__ */ new Date();
		const agent = this.add({
			id,
			name: options.name,
			chatId: options.chatId ?? null,
			syncId: 0,
			createdAt: now,
			updatedAt: now
		});
		const payload = {
			id,
			name: options.name,
			chat_id: options.chatId ?? null
		};
		const queued = this.persistAgent(agent).then(() => this.enqueueOrRollback({
			id: v4(),
			table: this.tableName,
			op: "create",
			modelId: id,
			payload,
			handlerOverride: {
				endpoint: this.apiEndpoint,
				method: "POST"
			}
		}));
		this.pendingCreates.set(id, queued.finally(() => this.pendingCreates.delete(id)));
		queued.catch((error) => {
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to enqueue agent create" },
				context: "AgentStore:createAgent"
			});
		});
		return agent;
	}
	applyAgentUpdates(agent, params, snapshot, payload) {
		if (params.name !== void 0 && params.name !== agent.name) {
			snapshot.name = agent.name;
			agent.name = params.name;
			payload.name = params.name;
		}
		if (params.label !== void 0 && params.label !== agent.label) {
			snapshot.label = agent.label;
			agent.label = params.label;
			payload.label = params.label;
		}
		if (params.description !== void 0 && params.description !== agent.description) {
			snapshot.description = agent.description;
			agent.description = params.description;
			payload.description = params.description;
		}
		if (params.icon !== void 0) {
			snapshot.icon = agent.icon;
			agent.icon = params.icon;
			payload.icon = params.icon;
		}
	}
	async updateAgent(agent, params, options) {
		const snapshot = {};
		const payload = {};
		runInAction(() => {
			this.applyAgentUpdates(agent, params, snapshot, payload);
		});
		if (Object.keys(payload).length === 0) return;
		await this.persistAgent(agent);
		await this.enqueueOrRollback({
			id: v4(),
			table: this.tableName,
			op: "update",
			modelId: agent.id,
			payload,
			snapshot,
			handlerOverride: {
				endpoint: options?.notifyAgent ? `${this.apiEndpoint}/${agent.id}?notify=true` : `${this.apiEndpoint}/${agent.id}`,
				method: "PATCH"
			}
		});
	}
	async updateAgentLifecycle(agent, params) {
		const snapshot = {
			pinnedAt: agent.pinnedAt,
			archivedAt: agent.archivedAt
		};
		const now = /* @__PURE__ */ new Date();
		runInAction(() => {
			if (params.pinned !== void 0) {
				agent.pinnedAt = params.pinned ? now : null;
				if (params.pinned) agent.archivedAt = null;
			}
			if (params.archived !== void 0) {
				agent.archivedAt = params.archived ? now : null;
				if (params.archived) agent.pinnedAt = null;
			}
		});
		await this.persistAgent(agent);
		await this.enqueueOrRollback({
			id: v4(),
			table: this.tableName,
			op: "update",
			modelId: agent.id,
			payload: params,
			snapshot,
			handlerOverride: {
				endpoint: `${this.apiEndpoint}/${agent.id}`,
				method: "PATCH"
			}
		});
	}
	/**
	* Hides the agent and its chat at once and queues the delete, like thread
	* deletion, so the sidebar responds immediately. The queue runs it behind
	* any pending create of the same row, so a never-synced agent is created
	* and then deleted on the server instead of resurrected by its queued POST.
	* A delete the server finally refuses is rolled back from the snapshot.
	*/
	async deleteAgent(id) {
		const agent = this.getById(id);
		if (!agent) return;
		const thread = this.sdk.stores.threads.getThreadForAgent(id);
		const snapshot = {
			agent: agent.toJSON(),
			threadId: thread?.id ?? null
		};
		runInAction(() => {
			this.data.delete(id);
			if (thread) thread.deletedAt = /* @__PURE__ */ new Date();
		});
		await this.pendingCreates.get(id)?.catch(() => void 0);
		await this.enqueueOrRollback({
			id: v4(),
			table: this.tableName,
			op: "delete",
			modelId: id,
			payload: {},
			snapshot,
			handlerOverride: {
				endpoint: `${this.apiEndpoint}/${id}`,
				method: "DELETE"
			}
		});
		if (AgentStore.persisted) await this.sdk.storage.remove(this.tableName, id);
	}
	async persistAgent(agent) {
		if (!AgentStore.persisted) return;
		await this.sdk.storage.set(this.tableName, agent);
	}
	rehydrateAgentSnapshot(snap) {
		const rehydrated = { ...snap };
		for (const key of [
			"createdAt",
			"updatedAt",
			"deletedAt",
			"pinnedAt",
			"archivedAt"
		]) {
			const value = rehydrated[key];
			if (typeof value === "string") rehydrated[key] = parseDate(value);
		}
		return rehydrated;
	}
	/** Queue a mutation, undoing its optimistic local write when it cannot be queued */
	async enqueueOrRollback(tx) {
		try {
			await this.sdk.transactionQueue.enqueue(tx, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				...tx,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
	}
	async rollback(tx) {
		switch (tx.op) {
			case "create":
				runInAction(() => this.data.delete(tx.modelId));
				if (AgentStore.persisted) await this.sdk.storage.remove(this.tableName, tx.modelId);
				return;
			case "update": {
				const agent = this.getById(tx.modelId);
				if (!agent || !tx.snapshot) return;
				runInAction(() => Object.assign(agent, tx.snapshot));
				await this.persistAgent(agent);
				return;
			}
			case "delete": {
				if (!tx.snapshot) return;
				const { agent, threadId } = tx.snapshot;
				const restored = this.add(this.rehydrateAgentSnapshot(agent));
				await this.persistAgent(restored);
				const thread = threadId ? this.sdk.stores.threads.getById(threadId) : void 0;
				if (thread) runInAction(() => thread.deletedAt = void 0);
				return;
			}
		}
	}
};
//#endregion
//#region src/core/sync/stores/arena-store.ts
var ArenaStore = class extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	ModelClass = Arena;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			createSession: action.bound,
			updateArena: action.bound,
			stopStreaming: action.bound,
			sendMessage: action.bound
		});
	}
	getArenaMessages = computedFn(function(arenaId) {
		const arena = this.data.get(arenaId);
		if (!arena) return [];
		const { agents, exchanges } = arena;
		const arenaMessages = [];
		for (const exchange of exchanges) {
			let userMessage;
			for (const agent of agents) {
				const userMessageId = exchange.userMessageIds[agent];
				if (!userMessageId) continue;
				const msg = this.sdk.stores.messages.get(userMessageId);
				if (!msg) continue;
				userMessage = msg;
				break;
			}
			if (userMessage) arenaMessages.push({
				role: "user",
				message: userMessage
			});
			const assistantMessages = agents.map((agent) => {
				const responses = exchange.agentResponses[agent] || [];
				if (responses.length === 0) return null;
				const msgs = responses.map((response) => this.sdk.stores.messages.get(response.messageId)).filter((msg) => !!msg);
				if (msgs.length === 0) return null;
				const threadId = arena.threadIds[agent];
				let chatStatus = void 0;
				if (threadId) {
					const thread = this.sdk.stores.threads.get(threadId);
					if (thread) chatStatus = thread.status;
				}
				return {
					role: "assistant",
					messages: msgs,
					chatStatus: chatStatus || "done",
					agentName: agent
				};
			});
			arenaMessages.push({
				role: "assistants",
				messages: assistantMessages
			});
		}
		return arenaMessages;
	});
	createSession({ agents, userMessage }) {
		try {
			const threadsData = this.createThreadsAndMessages(agents, userMessage);
			const exchange = new Exchange({
				id: v4(),
				userMessageIds: Object.fromEntries(threadsData.map((t) => [t.agentName, t.message.id])),
				agentResponses: {}
			});
			const arena = this.add({
				id: v4(),
				agents,
				exchanges: [exchange],
				threadIds: Object.fromEntries(threadsData.map((t) => [t.agentName, t.thread.id])),
				createdAt: /* @__PURE__ */ new Date(),
				updatedAt: /* @__PURE__ */ new Date()
			});
			this.setThreadsToThinking(arena.threadIds);
			this.syncThreads({
				threadsData,
				arenaData: {
					exchangeId: exchange.id,
					sessionId: arena.id
				}
			}).then(async () => {
				await this.sdk.storage.set(this.tableName, arena);
			});
			return arena;
		} catch (err) {
			this.sdk.logger.error(err, {
				context: "ArenaStore:createSession",
				metadata: {
					message: "Failed to create session",
					error: err
				}
			});
			throw err;
		}
	}
	updateArena({ agentName, sessionId, exchangeId, messageId, threadId }) {
		const arena = this.data.get(sessionId);
		if (arena) {
			const exchange = arena.exchanges.find((e) => e.id === exchangeId);
			if (exchange) {
				const agentResponses = exchange.agentResponses[agentName] || [];
				agentResponses.push({
					agentName,
					messageId,
					threadId
				});
				exchange.agentResponses[agentName] = agentResponses;
			}
		}
	}
	async stopStreaming(arenaId) {
		const arena = this.data.get(arenaId);
		if (arena) {
			await Promise.all(Object.values(arena.threadIds).map(async (threadId) => {
				await this.sdk.stores.threads.stopStreamingForThread(threadId);
			}));
			await this.sdk.storage.set(this.tableName, arena);
		}
	}
	async streamAgentResponses(userMessages, exchange, arena, useTodos) {
		const results = await Promise.allSettled(userMessages.map((m) => this.sdk.stores.messages.streamAgentResponse({
			threadId: m.threadId,
			mode: m.mode,
			content: m.content,
			fileIds: m.fileIds,
			arenaData: {
				exchangeId: exchange.id,
				sessionId: arena.id,
				agentName: m.mode ?? ""
			},
			userMessageId: m.id,
			isMetaSlashCommand: false,
			useTodos: useTodos ?? false
		})));
		const failedThreadIds = [];
		results.forEach((result, index) => {
			if (result.status === "rejected") failedThreadIds.push(userMessages[index].threadId);
		});
		return failedThreadIds;
	}
	handleFailedThreads(failedThreadIds) {
		if (failedThreadIds.length === 0) return;
		failedThreadIds.forEach((threadId) => {
			this.sdk.stores.threads.updateThreadStatus(threadId, "done");
		});
		this.sdk.logger.error("Some agent streams failed", {
			context: "ArenaStore:sendMessage",
			metadata: { failedThreadIds }
		});
	}
	async sendMessage({ arenaId, content, useTodos }) {
		const arena = this.data.get(arenaId);
		if (!arena || Object.keys(arena.threadIds).length === 0) {
			if (arena && Object.keys(arena.threadIds).length === 0) this.sdk.logger.error("Arena has no agents", {
				context: "ArenaStore:sendMessage",
				metadata: { message: "Arena has no agents" }
			});
			return;
		}
		try {
			const userMessages = this.createUserMessages(arena, content);
			this.setThreadsToThinking(arena.threadIds);
			const exchange = new Exchange({
				id: v4(),
				userMessageIds: Object.fromEntries(userMessages.map((m) => [m.mode, m.id])),
				agentResponses: {}
			});
			arena.exchanges.push(exchange);
			const failedThreadIds = await this.streamAgentResponses(userMessages, exchange, arena, useTodos);
			this.handleFailedThreads(failedThreadIds);
			await this.sdk.storage.set(this.tableName, arena);
		} catch (err) {
			this.sdk.logger.error("Failed to send message", {
				metadata: {
					message: "Failed to send message",
					error: err
				},
				context: "ArenaStore:sendMessage"
			});
			throw err;
		}
	}
	isBusy = computedFn(function(arenaId) {
		const arena = this.data.get(arenaId);
		if (arena) return Object.values(arena.threadIds).some((threadId) => {
			const thread = this.sdk.stores.threads.get(threadId);
			return thread && (thread.status === "streaming" || thread.status === "thinking");
		});
		return false;
	});
	createThreadsAndMessages(agents, userMessage) {
		return agents.map((agentName) => {
			const threadId = v4();
			const messageId = v4();
			return {
				agentName,
				message: this.sdk.stores.messages.add({
					content: userMessage,
					role: "user",
					mode: agentName,
					fileIds: [],
					threadId,
					createdAt: /* @__PURE__ */ new Date(),
					updatedAt: /* @__PURE__ */ new Date(),
					status: "done",
					id: messageId
				}),
				thread: this.sdk.stores.threads.add({
					id: threadId,
					title: "New Chat",
					createdAt: /* @__PURE__ */ new Date(),
					updatedAt: /* @__PURE__ */ new Date(),
					lastMessageAt: /* @__PURE__ */ new Date(),
					synced: false,
					lastMessageId: messageId,
					model: agentName,
					starred: false,
					status: "done"
				})
			};
		});
	}
	async syncThreads({ arenaData, threadsData }) {
		const results = await Promise.allSettled(threadsData.map((t) => this.sdk.stores.threads.syncThreadToServer({
			thread: t.thread,
			userMessage: t.message,
			mode: t.agentName,
			isSlashCommand: false,
			useTodos: false,
			arenaData: {
				...arenaData,
				agentName: t.agentName
			}
		})));
		const failedThreadIds = [];
		results.forEach((result, index) => {
			if (result.status === "rejected") {
				failedThreadIds.push(threadsData[index].thread.id);
				this.sdk.stores.threads.updateThreadStatus(threadsData[index].thread.id, "done");
			}
		});
		if (failedThreadIds.length > 0) this.sdk.logger.error("Some threads failed to sync", {
			metadata: { failedThreadIds },
			context: "ArenaStore:syncThreads"
		});
	}
	createUserMessages(arena, content) {
		const userMessages = [];
		Object.entries(arena.threadIds).forEach(([agentName, threadId]) => {
			const messageId = v4();
			const message = this.sdk.stores.messages.add({
				content,
				role: "user",
				mode: agentName,
				fileIds: [],
				threadId,
				createdAt: /* @__PURE__ */ new Date(),
				updatedAt: /* @__PURE__ */ new Date(),
				status: "done",
				id: messageId
			});
			userMessages.push(message);
		});
		return userMessages;
	}
	setThreadsToThinking(threadIds) {
		Object.values(threadIds).forEach((threadId) => {
			this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
		});
	}
};
//#endregion
//#region src/core/sync/stores/calendar-event-store.ts
var CalendarEventStore = class extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	static schemaVersion = 1;
	ModelClass = CalendarEvent;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("calendar/events");
		makeObservable(this, {
			fetchUpcomingEvents: action.bound,
			orderedByStartTime: computed
		});
	}
	get orderedByStartTime() {
		return orderBy(Array.from(this.data.values()), "startDatetime", "asc");
	}
	async persistSyncedEvents(events, removedIds) {
		if (this.effectiveLoadStrategy === "ephemeral") return;
		try {
			if (removedIds.length > 0) await this.sdk.storage.bulkRemove(this.tableName, removedIds);
			if (events.length > 0) await this.sdk.storage.bulkSet(this.tableName, events);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			this.sdk.logger.warn(err.message, {
				context: "CalendarEventStore:persistSyncedEvents",
				metadata: {
					message: "Failed to cache calendar events",
					error: err
				}
			});
		}
	}
	async syncEventsFromResponse(apiEvents) {
		const responseIds = new Set(apiEvents.map((e) => e.id));
		const events = [];
		const removedIds = Array.from(this.data.keys()).filter((id) => !responseIds.has(id));
		runInAction(() => {
			for (const id of removedIds) {
				const existing = this.data.get(id);
				if (existing) this.remove(existing);
			}
			for (const apiEvent of apiEvents) events.push(this.add({
				id: apiEvent.id,
				status: apiEvent.status,
				summary: apiEvent.summary,
				description: apiEvent.description ?? null,
				startDatetime: apiEvent.start_datetime,
				endDatetime: apiEvent.end_datetime,
				htmlLink: apiEvent.htmlLink ?? null,
				attendees: apiEvent.attendees ?? [],
				hangoutLink: apiEvent.hangoutLink ?? null,
				conferenceData: apiEvent.conferenceData ?? null,
				eventType: apiEvent.eventType ?? null
			}));
		});
		await this.persistSyncedEvents(events, removedIds);
		return events;
	}
	async fetchUpcomingEvents() {
		if (this.isFetching) return [];
		runInAction(() => {
			this.isFetching = true;
		});
		try {
			const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
			const response = await this.sdk.apiClient.fetch("calendar/events", { headers: { TZ: timeZone } });
			return this.syncEventsFromResponse(response.events);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			const logContext = {
				metadata: { message: "Error fetching calendar events" },
				context: "CalendarEventStore:fetchUpcomingEvents"
			};
			if (isTransientFetchError(error)) this.sdk.logger.warn(err.message, {
				...logContext,
				metadata: {
					...logContext.metadata,
					reason: "network_failure",
					error: err
				}
			});
			else this.sdk.logger.error(err, logContext);
			throw error;
		} finally {
			runInAction(() => {
				this.isFetching = false;
			});
		}
	}
};
//#endregion
//#region src/core/sync/stores/draft-store.ts
function clearStaleDraftDocument(partial) {
	return "content" in partial && !("draftDocument" in partial) ? {
		...partial,
		draftDocument: void 0
	} : partial;
}
function getErrorMessage(error) {
	return error instanceof Error ? error.message : "Unknown error";
}
function newFileId() {
	return v4();
}
var DraftStore = class extends Store {
	static persisted = false;
	static loadStrategy = "localOnly";
	ModelClass = Draft;
	uploadDisposers = /* @__PURE__ */ new Map();
	uploadCancelFns = /* @__PURE__ */ new Map();
	transcriptionStates = /* @__PURE__ */ new Map();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			transcriptionStates: true,
			removeFile: action,
			createDraft: action,
			updateDraft: action,
			clearProjectAssignmentsForProject: action,
			addFileAndUpload: action,
			replaceFileAndUpload: action,
			appendSelectedText: action,
			removeSelectedText: action,
			clearSelectedText: action,
			setupDraftUploadReaction: action,
			cleanupDraftUploadReaction: action,
			transcribeIntoDraft: action,
			setTranscriptionState: action,
			clearTranscriptionState: action,
			clear: override
		});
	}
	isTranscribing = computedFn(function(draftId) {
		return this.transcriptionStates.get(draftId) || false;
	});
	setTranscriptionState = (draftId, isTranscribing) => {
		if (isTranscribing) this.transcriptionStates.set(draftId, true);
		else this.transcriptionStates.delete(draftId);
	};
	clearAllTranscriptionStates = () => {
		this.transcriptionStates.clear();
	};
	clearTranscriptionState = (draftId) => {
		this.transcriptionStates.delete(draftId);
	};
	getDraft = computedFn(function(draftId) {
		return this.data.get(draftId);
	});
	createDraft = (partial) => {
		const draftData = clearStaleDraftDocument(partial);
		return this.add({
			id: draftData.draftId,
			files: [],
			...draftData
		});
	};
	updateDraft = async (partial) => {
		const update = clearStaleDraftDocument(partial);
		let draft = this.data.get(update.draftId);
		if (!draft) draft = this.createDraft(update);
		draft.updateData({
			...update,
			updatedAt: /* @__PURE__ */ new Date()
		});
	};
	/**
	* Drop a deleted project from every draft holding it. Drafts are few and
	* unindexed by project, so this scans them rather than keeping an index.
	*/
	clearProjectAssignmentsForProject = async (projectId) => {
		const staleDrafts = Array.from(this.data.values()).filter((draft) => draft.projectId === projectId);
		await Promise.all(staleDrafts.map((draft) => this.updateDraft({
			draftId: draft.draftId,
			projectId: null
		})));
	};
	/** Move a draft to another chat without interrupting active file uploads */
	moveDraft = async (sourceDraftId, targetDraftId) => {
		if (sourceDraftId === targetDraftId) return;
		const sourceDraft = this.getDraft(sourceDraftId);
		if (!sourceDraft) return;
		const hasActiveUploads = sourceDraft.files.some((file) => file.uploadState === "pending" || file.uploadState === "uploading");
		await this.updateDraft({
			draftId: targetDraftId,
			content: sourceDraft.content,
			draftDocument: sourceDraft.draftDocument,
			files: [...sourceDraft.files],
			selectedTexts: [...sourceDraft.selectedTexts],
			projectId: sourceDraft.projectId
		});
		runInAction(() => {
			const targetDraft = this.getDraft(targetDraftId);
			if (hasActiveUploads && targetDraft) this.setupDraftUploadReaction(targetDraft);
			this.removeDraft(sourceDraft);
		});
	};
	appendSelectedText = (draftId, selectedText) => {
		if (!selectedText.trim()) return;
		let draft = this.data.get(draftId);
		if (!draft) draft = this.createDraft({ draftId });
		if (draft.selectedTexts.some((selection) => selection.text === selectedText)) return;
		draft.selectedTexts = [...draft.selectedTexts, {
			id: v4(),
			text: selectedText
		}];
	};
	removeSelectedText = (draftId, id) => {
		const draft = this.data.get(draftId);
		if (!draft) return;
		draft.selectedTexts = draft.selectedTexts.filter((s) => s.id !== id);
	};
	clearSelectedText = (draftId) => {
		const draft = this.data.get(draftId);
		if (draft) draft.selectedTexts = [];
	};
	setupDraftUploadReaction = (draft) => {
		if (this.uploadDisposers.has(draft.id)) return;
		const disposer = reaction(() => draft.files.map((f) => f.uploadState), async () => {
			for (const file of draft.files) if (file.uploadState === "pending") await this.uploadFile(draft, file);
		}, { fireImmediately: true });
		this.uploadDisposers.set(draft.id, disposer);
	};
	cleanupDraftUploadReaction = (draftId) => {
		const disposer = this.uploadDisposers.get(draftId);
		if (disposer) {
			disposer();
			this.uploadDisposers.delete(draftId);
		}
	};
	/**
	* Transcribe a recording and append its text to the draft's existing
	* content. The draft is marked as transcribing while the request runs.
	* Resolves with the text that was appended.
	*/
	transcribeIntoDraft = async (draftId, file, localTranscript) => {
		try {
			this.setTranscriptionState(draftId, true);
			const transcript = localTranscript ?? await this.sdk.transcribeSpeech(file);
			const existingContent = this.data.get(draftId)?.content?.trim() || "";
			const newContent = transcript.trim();
			const combinedContent = existingContent ? `${existingContent} ${newContent}` : newContent;
			await this.updateDraft({
				draftId,
				content: combinedContent
			});
			return newContent;
		} catch (error) {
			this.sdk.logger.error("Speech-to-text conversion failed:", {
				metadata: {
					message: "Speech-to-text conversion failed:",
					error
				},
				context: "DraftStore:transcribeIntoDraft"
			});
			throw error;
		} finally {
			this.clearTranscriptionState(draftId);
		}
	};
	async uploadFile(draft, file) {
		const uploadId = file.id;
		const isStale = () => file.id !== uploadId;
		runInAction(() => {
			file.uploadState = "uploading";
		});
		try {
			const fileDescriptor = this.prepareFileDescriptor(file, draft);
			const uploadUrl = await this.fetchUploadUrl(fileDescriptor);
			if (isStale()) return;
			await this.uploadFileWithProgress(uploadUrl, file, isStale);
			if (isStale()) return;
			await this.finalizeFileUpload(uploadId);
			runInAction(() => {
				if (isStale()) return;
				file.uploadState = "uploaded";
				file.progress = 100;
			});
		} catch (error) {
			if (isStale()) return;
			this.handleUploadError(file, error);
		}
	}
	validateDraft = (draftId, options = {}) => {
		const draft = this.data.get(draftId);
		if (!draft) throw new Error("Draft not found");
		const error = getDraftSendabilityError(draft, options);
		if (error) throw new Error(getDraftSendabilityMessage(error) ?? "Draft is invalid");
	};
	removeDraft = (draft) => {
		this.data.delete(draft.id);
		this.cleanupDraftUploadReaction(draft.id);
		this.clearTranscriptionState(draft.id);
	};
	addFileAndUpload = async (draftId, fileInput, dimensions) => {
		let draft = this.data.get(draftId);
		if (!draft) draft = this.createDraft({ draftId });
		this.validateAttachmentLimits(draft, fileInput);
		const draftFile = new DraftFile({
			id: newFileId(),
			file: fileInput,
			uploadState: "pending",
			progress: 0,
			fileName: fileInput.name,
			contentType: fileInput.type,
			size: fileInput.size,
			height: dimensions?.height,
			width: dimensions?.width
		});
		draft.files.push(draftFile);
		this.setupDraftUploadReaction(draft);
	};
	/**
	* Swaps a draft file's content in place, keeping the same DraftFile
	* instance (and localKey) so UI keyed on it does not remount. The server
	* soft-deletes files, so the old id cannot be reused; the replacement
	* uploads under a fresh id, and the old server file is only deleted once
	* that upload succeeds. A failed replacement rolls the swap back so the
	* draft still references the original, then throws.
	*
	* Scoped to same-kind content swaps (pasted-text edits): it validates
	* through the same validateAttachmentLimits as addFileAndUpload. Type and
	* extension rules live at the call sites, as they do for addFileAndUpload.
	*/
	replaceFileAndUpload = async (draftId, file, newFile) => {
		const draft = this.data.get(draftId);
		if (!draft || !draft.files.some((f) => f.id === file.id)) return;
		this.validateAttachmentLimits(draft, newFile, { replacing: file });
		const original = file.captureState();
		if (original.uploadState === "uploading" || original.uploadState === "pending") {
			const cancel = this.uploadCancelFns.get(original.id);
			if (cancel) {
				cancel();
				this.uploadCancelFns.delete(original.id);
			}
		}
		runInAction(() => {
			file.id = newFileId();
			file.file = newFile;
			file.fileName = newFile.name;
			file.contentType = newFile.type;
			file.size = newFile.size;
			file.height = void 0;
			file.width = void 0;
			file.progress = 0;
			file.error = void 0;
			file.uploadState = "uploading";
		});
		this.setupDraftUploadReaction(draft);
		let uploadThrew = false;
		try {
			await this.uploadFile(draft, file);
		} catch {
			uploadThrew = true;
		}
		if (uploadThrew || file.uploadState !== "uploaded") {
			this.rollbackFileReplacement(file, original);
			throw new Error("Replacement upload failed; original file restored");
		}
		if (original.uploadState === "uploaded") try {
			await this.sdk.apiClient.fetch(`files/${original.id}`, { method: "DELETE" });
		} catch (err) {
			this.sdk.logger.error("Failed to delete replaced file", {
				metadata: {
					error: err,
					fileId: original.id
				},
				context: "DraftStore:replaceFileAndUpload"
			});
		}
	};
	/** Rolls a failed in-place replacement back to the captured original. */
	rollbackFileReplacement(file, original) {
		runInAction(() => {
			file.restoreState(original);
			file.error = void 0;
			if (original.uploadState !== "uploaded") {
				file.id = newFileId();
				file.uploadState = "pending";
				file.progress = 0;
			}
		});
	}
	getFileUrl = async (file) => {
		const { url: downloadUrl } = await this.sdk.apiClient.fetch(`files/${file.id}`, { method: "GET" });
		return downloadUrl;
	};
	removeFile = async (draftId, file) => {
		const draft = this.data.get(draftId);
		if (draft) {
			draft.files = draft.files.filter((f) => f.id !== file.id);
			if (draft.files.length === 0) this.cleanupDraftUploadReaction(draftId);
			if (file.uploadState === "uploading" || file.uploadState === "pending") {
				const cancel = this.uploadCancelFns.get(file.id);
				if (cancel) {
					cancel();
					this.uploadCancelFns.delete(file.id);
				}
			} else if (file.uploadState === "uploaded") try {
				await this.sdk.apiClient.fetch(`files/${file.id}`, { method: "DELETE" });
			} catch (err) {
				this.sdk.logger.error("Failed to delete file", {
					metadata: {
						message: "Failed to delete file",
						error: err
					},
					context: "DraftStore:removeFile"
				});
			}
		}
	};
	/**
	* Re-drives a failed upload for a file the user already picked, without
	* removing and re-selecting it. Resets the file to a clean pending state so
	* the upload reaction picks it up and runs uploadFile again. A fresh id is
	* minted because the failed attempt may have registered its id server-side
	* (same reasoning as rollbackFileReplacement).
	*/
	retryFileUpload = (draftId, file) => {
		const draft = this.data.get(draftId);
		if (!draft || !draft.files.some((f) => f.id === file.id)) return;
		if (file.uploadState !== "failed") return;
		runInAction(() => {
			file.id = newFileId();
			file.uploadState = "pending";
			file.error = void 0;
			file.progress = 0;
		});
		this.setupDraftUploadReaction(draft);
	};
	clear() {
		for (const cancel of this.uploadCancelFns.values()) cancel();
		this.uploadCancelFns.clear();
		for (const disposer of this.uploadDisposers.values()) disposer();
		this.uploadDisposers.clear();
		this.clearAllTranscriptionStates();
		this.data.clear();
	}
	prepareFileDescriptor(file, draft) {
		return {
			filename: normalizeFilename(file.fileName),
			content_type: file.contentType,
			size: file.size,
			height: file.height,
			width: file.width,
			id: file.id,
			entity: "chat",
			entity_id: draft.id
		};
	}
	async fetchUploadUrl(fileDescriptor) {
		const { url: uploadUrl } = await this.sdk.apiClient.fetch("get_upload_url", {
			method: "POST",
			body: JSON.stringify({ file_metadata: fileDescriptor })
		});
		return uploadUrl;
	}
	async uploadFileWithProgress(uploadUrl, file, isStale) {
		const cancelKey = file.id;
		const onProgress = ({ loaded, total }) => {
			if (isStale?.()) return;
			runInAction(() => {
				file.progress = Math.round(loaded / total * 100);
			});
		};
		if (!file.file) {
			runInAction(() => {
				file.uploadState = "failed";
				file.error = "File is missing";
			});
			return;
		}
		const { promise, cancel } = this.sdk.apiClient.uploadFileWithProgress(uploadUrl, file.file, onProgress);
		this.uploadCancelFns.set(cancelKey, cancel);
		try {
			await promise;
		} finally {
			this.uploadCancelFns.delete(cancelKey);
		}
	}
	async finalizeFileUpload(id) {
		await this.sdk.apiClient.fetch(`finalize_upload/${id}`, { method: "POST" });
	}
	handleUploadError(file, error) {
		this.uploadCancelFns.delete(file.id);
		runInAction(() => {
			file.uploadState = "failed";
			file.error = getErrorMessage(error) || "Upload failed";
		});
	}
	/**
	* Shared by add and replace so the edit path cannot create attachments
	* the add path would reject. A replacement excludes the file it swaps out
	* and skips the count check, which a one-for-one swap cannot change.
	*/
	validateAttachmentLimits(draft, newFile, options) {
		const replacedId = options?.replacing?.id;
		const files = replacedId ? draft.files.filter((f) => f.id !== replacedId) : draft.files;
		const currentSize = files.reduce((sum, f) => sum + attachmentTotalBytes({
			name: f.fileName,
			size: f.size,
			mimeType: f.contentType
		}), 0);
		if (!replacedId && files.length >= 10) throw new DraftAttachmentLimitError("max_count", `Maximum 10 attachments allowed`);
		if (isVideoUpload(newFile.name, newFile.type) && newFile.size > 104857600) throw new DraftAttachmentLimitError("max_video_bytes", `A video can be at most ${Math.round(MAX_VIDEO_FILE_BYTES / 1048576)} MB`);
		if (currentSize + attachmentTotalBytes(newFile) > 31457280) throw new DraftAttachmentLimitError("max_total_bytes", `Total attachment size would exceed ${Math.round(MAX_TOTAL_ATTACHMENT_BYTES / 1048576)} MB limit`);
	}
};
//#endregion
//#region src/core/sync/stores/file-store.ts
const URL_TTL_SAFETY_FACTOR = .9;
var FileStore = class extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	ModelClass = File$1;
	pendingFileIds = /* @__PURE__ */ new Set();
	urlSession = 0;
	hydratingFileIds = /* @__PURE__ */ new Set();
	/**
	* Download URLs by file id, valid until `deadline` (epoch ms).
	* Not observable on purpose: callers ask for a URL when they need one,
	* nothing reacts to this map.
	*/
	fileUrlCache = /* @__PURE__ */ new Map();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			addFile: action,
			persistAndHydrateFiles: action,
			hydrateByIds: action,
			pendingFileIds: observable
		});
	}
	getById = computedFn(function(fileId) {
		return this.data.get(fileId);
	});
	getFileUrl = async (file) => {
		const cached = this.fileUrlCache.get(file.id);
		if (cached && Date.now() < cached.deadline) return cached.url;
		try {
			const session = this.urlSession;
			if (cached?.refresh) {
				const renewed = await cached.refresh();
				this.cacheUrl(file.id, renewed, cached.refresh, session);
				return renewed.url;
			}
			const { url, expires_in } = await this.sdk.apiClient.fetch(`files/${file.id}`, { method: "GET" });
			if (expires_in) this.cacheUrl(file.id, {
				url,
				expiresIn: expires_in
			}, void 0, session);
			return url;
		} catch (error) {
			this.sdk.logger.error(`Failed to fetch file URL for file ${file.id}`, {
				metadata: {
					message: `Failed to fetch file URL for file ${file.id}`,
					error
				},
				context: "FileStore:getFileUrl"
			});
			throw error;
		}
	};
	registerFileUrl(fileId, source) {
		this.cacheUrl(fileId, source, source.refresh);
	}
	cacheUrl(fileId, source, refresh, session = this.urlSession) {
		if (session !== this.urlSession) return;
		const deadline = source.expiresIn ? Date.now() + source.expiresIn * 1e3 * URL_TTL_SAFETY_FACTOR : 0;
		this.fileUrlCache.set(fileId, {
			url: source.url,
			deadline,
			refresh
		});
	}
	clear() {
		super.clear();
		this.urlSession += 1;
		this.fileUrlCache.clear();
		this.pendingFileIds.clear();
		this.hydratingFileIds.clear();
	}
	addFile(file) {
		return this.add(file);
	}
	async flushPersistedFileBatch(added, toPersistOnly) {
		if (added.length > 0) await this.sdk.storage.bulkSet(this.tableName, added);
		if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
	}
	async hydrateByIds(fileIds) {
		const missing = [...new Set(fileIds)].filter((id) => !this.data.has(id) && !this.hydratingFileIds.has(id));
		if (missing.length === 0) return;
		runInAction(() => {
			for (const id of missing) {
				this.pendingFileIds.add(id);
				this.hydratingFileIds.add(id);
			}
		});
		const session = this.urlSession;
		try {
			await this.loadStoredFiles(missing, session);
			if (session !== this.urlSession) return;
			const stillMissing = missing.filter((id) => this.hydratingFileIds.has(id));
			if (stillMissing.length === 0) return;
			await this.fetchMissingFilesFromApi(stillMissing, session);
		} catch (error) {
			this.sdk.logger.error("Error hydrating files by IDs", {
				metadata: {
					error,
					fileIds: missing
				},
				context: "FileStore:hydrateByIds"
			});
			if (session === this.urlSession) runInAction(() => {
				for (const id of missing) this.hydratingFileIds.delete(id);
			});
		}
	}
	async loadStoredFiles(ids, session) {
		const entityQueries = this.getEntityQueries();
		if (!entityQueries) return;
		try {
			const found = await this.fetchRecordsByIdChunks(entityQueries.getByIds.bind(entityQueries), ids);
			if (session !== this.urlSession) return;
			runInAction(() => {
				for (const record of found) {
					if (!this.hydratingFileIds.has(record.id)) continue;
					this.add(record);
					this.pendingFileIds.delete(record.id);
					this.hydratingFileIds.delete(record.id);
				}
			});
		} catch (error) {
			this.sdk.logger.error("Error hydrating files from storage", {
				metadata: {
					error,
					fileIds: ids
				},
				context: "FileStore:hydrateByIds"
			});
		}
	}
	async fetchMissingFilesFromApi(fileIds, session) {
		const results = await Promise.all(fileIds.map((fileId) => this.fetchPresentedFile(fileId)));
		if (session !== this.urlSession) return;
		runInAction(() => {
			for (const result of results) {
				if (!this.hydratingFileIds.has(result.fileId)) continue;
				switch (result.status) {
					case "found": {
						const file = result.file;
						this.add({
							id: file.id,
							filename: file.filename,
							contentType: file.content_type,
							size: file.size,
							width: file.width,
							height: file.height
						});
						if (file.expires_in) this.cacheUrl(file.id, {
							url: file.url,
							expiresIn: file.expires_in
						}, void 0, session);
						this.pendingFileIds.delete(result.fileId);
						this.hydratingFileIds.delete(result.fileId);
						break;
					}
					case "deferred":
						this.hydratingFileIds.delete(result.fileId);
						break;
					case "failed": this.hydratingFileIds.delete(result.fileId);
				}
			}
		});
	}
	async fetchPresentedFile(fileId) {
		try {
			return {
				status: "found",
				fileId,
				file: await this.sdk.apiClient.fetch(`files/${fileId}`, { method: "GET" })
			};
		} catch (error) {
			if (error instanceof HttpError && (error.status === 404 || error.status === 410)) {
				this.sdk.logger.warn(`Presented file ${fileId} is not available yet`, {
					metadata: {
						fileId,
						status: error.status
					},
					context: "FileStore:hydrateByIds"
				});
				return {
					status: "deferred",
					fileId
				};
			}
			this.sdk.logger.error(`Failed to fetch presented file ${fileId}`, {
				metadata: {
					error,
					fileId
				},
				context: "FileStore:hydrateByIds"
			});
			return {
				status: "failed",
				fileId
			};
		}
	}
	async persistAndHydrateFiles(dbFiles) {
		try {
			const toPersist = [];
			const toDelete = [];
			for (const dbFile of dbFiles) {
				if (!dbFile.id) continue;
				if (dbFile.deleted_at) {
					toDelete.push(dbFile.id);
					continue;
				}
				toPersist.push({
					id: dbFile.id,
					filename: dbFile.filename,
					contentType: dbFile.content_type,
					size: dbFile.size,
					width: dbFile.width,
					height: dbFile.height,
					syncId: dbFile.sync_id,
					createdAt: parseDate(dbFile.created_at),
					updatedAt: parseDate(dbFile.updated_at),
					deletedAt: dbFile.deleted_at ? parseDate(dbFile.deleted_at) : void 0
				});
			}
			if (toDelete.length > 0) {
				toDelete.forEach((id) => {
					this.data.delete(id);
					this.pendingFileIds.delete(id);
					this.hydratingFileIds.delete(id);
				});
				await this.sdk.storage.bulkRemove(this.tableName, toDelete);
			}
			if (toPersist.length > 0) {
				const toHydrate = toPersist.filter((d) => this.data.has(d.id) || this.pendingFileIds.has(d.id));
				const toPersistOnly = toPersist.filter((d) => !this.data.has(d.id) && !this.pendingFileIds.has(d.id));
				const addedFiles = toHydrate.map((data) => {
					this.pendingFileIds.delete(data.id);
					this.hydratingFileIds.delete(data.id);
					return this.add(data);
				});
				await this.flushPersistedFileBatch(addedFiles, toPersistOnly);
			}
			return {
				deletedSearchDocIds: toDelete,
				upsertedRecords: toPersist
			};
		} catch (error) {
			this.sdk.logger.error("Error processing files", {
				metadata: { error },
				context: "FileStore:persistAndHydrateFiles"
			});
			throw error;
		}
	}
};
//#endregion
//#region src/core/utils/text.ts
/**
* Slices a string to a maximum character length while preserving word boundaries.
* If the string is longer than the limit, it cuts at the last space before the limit
* and appends an optional suffix.
*
* @param str - The input string to slice
* @param n - Maximum number of characters (excluding suffix)
* @param suffix - Optional string to append when text is truncated (default: '')
* @returns The sliced string with suffix if truncated, or original string if within limit
*
* @example
* sliceWords("Hello world this is a test", 15, "...")
* // Returns: "Hello world..."
*/
function sliceWords(str, n, suffix = "") {
	if (str.length <= n) return str;
	let sliced = str.slice(0, n);
	const lastSpace = sliced.lastIndexOf(" ");
	if (lastSpace > 0) sliced = sliced.slice(0, lastSpace);
	return sliced + suffix;
}
//#endregion
//#region src/core/sync/stores/journal-store.ts
var JournalStore = class JournalStore extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	ModelClass = Journal;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			persistAndHydrateJournals: action.bound,
			markViewed: action.bound,
			markAllViewed: action.bound,
			fetchAll: action.bound
		});
	}
	get(id) {
		return this.data.get(id);
	}
	async fetchById(journalId) {
		try {
			const dbJournal = await this.sdk.apiClient.fetch(`journal/${journalId}`);
			if (!dbJournal?.id) return void 0;
			await this.persistAndHydrateJournals([dbJournal]);
			return this.data.get(dbJournal.id);
		} catch {
			return;
		}
	}
	async flushPersistedJournalBatch(added, toPersistOnly) {
		if (added.length > 0) await this.sdk.storage.bulkSet(this.tableName, added);
		if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
	}
	async markViewed(journalId) {
		const journal = this.data.get(journalId);
		if (!journal || journal.viewedAt) return;
		const snapshot = { viewedAt: null };
		runInAction(() => {
			journal.viewedAt = /* @__PURE__ */ new Date();
		});
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: journalId,
				payload: { uuid: journalId },
				snapshot,
				handlerOverride: {
					endpoint: `journal/mark_viewed?uuid=${journalId}`,
					method: "POST"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: journalId,
				payload: { uuid: journalId },
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		if (JournalStore.persisted) await this.sdk.storage.set(this.tableName, journal);
	}
	async markAllViewed() {
		const unreadJournals = Array.from(this.data.values()).filter((journal) => !journal.viewedAt);
		runInAction(() => {
			unreadJournals.forEach((journal) => {
				journal.viewedAt = /* @__PURE__ */ new Date();
			});
		});
		const txId = v4();
		const snapshot = { markAllViewedIds: unreadJournals.map((j) => j.id) };
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: v4(),
				payload: {},
				snapshot,
				handlerOverride: {
					endpoint: "journal/mark_all_viewed",
					method: "POST"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: txId,
				payload: {},
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		if (JournalStore.persisted && unreadJournals.length > 0) await this.sdk.storage.bulkSet(this.tableName, unreadJournals);
	}
	async fetchAll() {
		await this.ensureListLoadedFromStorage();
	}
	delete = async (item, options) => {
		this.isSaving = true;
		try {
			const id = item.id;
			await this.sdk.apiClient.fetch(`journal/${id}`, {
				method: "DELETE",
				body: JSON.stringify(options)
			});
			runInAction(() => {
				this.data.delete(id);
			});
			if (JournalStore.persisted) await this.sdk.storage.remove(this.tableName, id);
		} catch (err) {
			this.sdk.logger.error("Failed to delete journal", {
				metadata: {
					message: "Failed to delete journal",
					error: err
				},
				context: "JournalStore:delete"
			});
			throw err;
		} finally {
			this.isSaving = false;
		}
	};
	async persistAndHydrateJournals(dbJournals) {
		try {
			const toPersist = [];
			const toDelete = [];
			for (const dbJournal of dbJournals) {
				if (!dbJournal.id) continue;
				if (dbJournal.deleted_at) {
					toDelete.push(dbJournal.id);
					continue;
				}
				const existingJournal = this.data.get(dbJournal.id);
				const journalData = {
					...existingJournal ? existingJournal.toJSON() : {},
					id: dbJournal.id,
					text: dbJournal.text,
					document: dbJournal.document,
					sections: dbJournal.sections,
					date: parseDate(dbJournal.date),
					createdAt: parseDate(dbJournal.created_at),
					updatedAt: parseDate(dbJournal.updated_at),
					viewedAt: dbJournal.viewed_at ? parseDate(dbJournal.viewed_at) : void 0
				};
				toPersist.push(journalData);
			}
			if (toDelete.length > 0) {
				toDelete.forEach((id) => this.data.delete(id));
				await this.sdk.storage.bulkRemove(this.tableName, toDelete);
			}
			if (toPersist.length > 0) {
				const toHydrate = toPersist.filter((d) => this.data.has(d.id));
				const toPersistOnly = toPersist.filter((d) => !this.data.has(d.id));
				const addedJournals = runInAction(() => toHydrate.map((data) => this.add(data)));
				await this.flushPersistedJournalBatch(addedJournals, toPersistOnly);
			}
			return {
				deletedSearchDocIds: toDelete,
				upsertedRecords: toPersist
			};
		} catch (error) {
			this.sdk.logger.error("Error processing journals", {
				metadata: { error },
				context: "JournalStore:persistAndHydrateJournals"
			});
			throw error;
		}
	}
	async restoreJournal(journalId, toDocumentFrom) {
		const journal = this.data.get(journalId);
		if (!journal) return;
		const document = toDocumentFrom(journal.text);
		const title = journal.sections?.original_title ?? journal.sections?.title ?? "Untitled";
		runInAction(() => {
			journal.description = extractDescriptionFrom(journal.text);
			journal.document = document;
			journal.isEdited = false;
			journal.restoreCount += 1;
			if (journal.sections) journal.sections.title = title;
		});
		if (JournalStore.persisted) await this.sdk.storage.update(this.tableName, journalId, journal);
	}
	updateJournal(journalId, data, getText) {
		const journal = this.data.get(journalId);
		if (!journal) return;
		if (!data?.document && !data?.title) return;
		const document = data?.document ?? journal?.document;
		const title = data?.title ?? journal?.sections?.title;
		runInAction(() => {
			journal.updatedAt = /* @__PURE__ */ new Date();
			journal.document = document;
			journal.isEdited = true;
			if (document) journal.description = extractDescriptionFrom(getText(document));
			if (title?.length && journal.sections) journal.sections.title = title;
		});
	}
	async saveJournal(journalId) {
		const journal = this.data.get(journalId);
		if (!journal) return;
		const document = journal?.document;
		const title = journal?.sections?.title;
		if (!document || !title) return;
		try {
			await this.sdk.apiClient.fetch(`journal/${journalId}`, {
				method: "PUT",
				body: JSON.stringify({
					document,
					title
				})
			});
		} catch (error) {
			this.sdk.logger.error("Failed to save journal document and title", {
				metadata: {
					message: "Failed to save journal document and title",
					error
				},
				context: "JournalStore:saveJournal"
			});
			throw error;
		}
	}
	async migrateJournals(toDocumentFrom, getText) {
		if (!JournalStore.persisted) throw new Error("Journals Store is yet to be persisted!");
		const journalsToUpdate = [];
		for (const journalId of this.data.keys()) {
			const journal = this.data.get(journalId);
			if (!journal || journal.document && journal.description) continue;
			const document = journal?.document ?? toDocumentFrom(journal.text) ?? {
				type: "doc",
				content: []
			};
			const description = extractDescriptionFrom(getText(document));
			runInAction(() => {
				journal.document = document;
				journal.description = description;
			});
			journalsToUpdate.push(journal);
		}
		if (journalsToUpdate.length > 0) await this.sdk.storage.bulkSet(this.tableName, journalsToUpdate);
	}
	rehydrateSnapshot(snap) {
		const out = {};
		if ("viewedAt" in snap) {
			const v = snap.viewedAt;
			out.viewedAt = v === null ? void 0 : typeof v === "string" ? parseDate(v) : v;
		}
		for (const key of [
			"createdAt",
			"updatedAt",
			"date",
			"deletedAt"
		]) if (key in snap) {
			const v = snap[key];
			out[key] = typeof v === "string" ? parseDate(v) : v;
		}
		for (const key of Object.keys(snap)) if (!(key in out)) out[key] = snap[key];
		return out;
	}
	async rollback(tx) {
		if (tx.op !== "update") return;
		const bulkIds = tx.snapshot?.markAllViewedIds;
		if (Array.isArray(bulkIds)) {
			const reverted = [];
			runInAction(() => {
				for (const id of bulkIds) {
					const journal = this.data.get(id);
					if (journal) {
						journal.viewedAt = void 0;
						reverted.push(journal);
					}
				}
			});
			if (JournalStore.persisted && reverted.length > 0) await this.sdk.storage.bulkSet(this.tableName, reverted);
			return;
		}
		const journal = this.data.get(tx.modelId);
		if (!journal || !tx.snapshot) return;
		runInAction(() => Object.assign(journal, this.rehydrateSnapshot(tx.snapshot)));
		if (JournalStore.persisted) await this.sdk.storage.set(this.tableName, journal);
	}
};
function extractDescriptionFrom(text) {
	return stripMarkdown(sliceWords(text, 120, "..."));
}
//#endregion
//#region src/core/sync/stores/mcp-client-grant-store.ts
var McpClientGrantStore = class McpClientGrantStore extends Store {
	static persisted = true;
	ModelClass = McpClientGrant;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			persistAndHydrateMcpClientGrants: action,
			revokeClientGrant: action
		});
	}
	mapDbMcpClientGrant(grant) {
		return {
			id: grant.id,
			clientId: grant.client_id,
			clientName: grant.client_name,
			authorizedAt: parseDate(grant.authorized_at),
			syncId: grant.sync_id,
			createdAt: parseDate(grant.created_at),
			updatedAt: parseDate(grant.updated_at),
			deletedAt: grant.deleted_at ? parseDate(grant.deleted_at) : void 0
		};
	}
	async persistAndHydrateMcpClientGrants(rows) {
		try {
			const toAdd = [];
			const toDelete = [];
			for (const row of rows) {
				if (row.deleted_at) {
					toDelete.push(row.id);
					continue;
				}
				toAdd.push(this.mapDbMcpClientGrant(row));
			}
			if (toDelete.length > 0) {
				runInAction(() => {
					toDelete.forEach((id) => this.data.delete(id));
				});
				if (McpClientGrantStore.persisted) await this.sdk.storage.bulkRemove(this.tableName, toDelete);
			}
			if (toAdd.length > 0) {
				const addedGrants = runInAction(() => toAdd.map((grant) => this.add(grant)));
				if (McpClientGrantStore.persisted) await this.sdk.storage.bulkSet(this.tableName, addedGrants);
			}
		} catch (error) {
			const caughtError = error instanceof Error ? error : new Error(String(error));
			this.sdk.logger.error(caughtError, {
				metadata: { message: "Error processing MCP client grants:" },
				context: "McpClientGrantStore:persistAndHydrateMcpClientGrants"
			});
			throw caughtError;
		} finally {
			runInAction(() => {
				this.isLoaded = true;
			});
		}
	}
	async revokeClientGrant(grant) {
		runInAction(() => {
			this.isSaving = true;
		});
		try {
			await this.sdk.apiClient.fetch(`mcp_clients/${encodeURIComponent(grant.clientId)}/revoke`, { method: "POST" });
			runInAction(() => {
				this.data.delete(grant.id);
			});
			if (McpClientGrantStore.persisted) await this.sdk.storage.remove(this.tableName, grant.id);
		} catch (error) {
			const caughtError = error instanceof Error ? error : new Error(String(error));
			this.sdk.logger.error(caughtError, {
				metadata: { message: "Error revoking MCP client grant:" },
				context: "McpClientGrantStore:revokeClientGrant"
			});
			throw caughtError;
		} finally {
			runInAction(() => {
				this.isSaving = false;
			});
		}
	}
};
//#endregion
//#region src/meetings/transcript-term-correction-error.ts
let TranscriptTermCorrectionErrorCode = /* @__PURE__ */ function(TranscriptTermCorrectionErrorCode) {
	TranscriptTermCorrectionErrorCode["MeetingRecording"] = "meeting_is_recording";
	TranscriptTermCorrectionErrorCode["TranscriptProcessing"] = "transcript_is_processing";
	TranscriptTermCorrectionErrorCode["Unavailable"] = "transcript_correction_unavailable";
	TranscriptTermCorrectionErrorCode["TranscriptChanged"] = "transcript_changed_since_correction";
	TranscriptTermCorrectionErrorCode["RequestFailed"] = "request_failed";
	return TranscriptTermCorrectionErrorCode;
}({});
const displayMessages = {
	["meeting_is_recording"]: "Stop recording before correcting a word.",
	["transcript_is_processing"]: "Wait for transcript processing to finish before correcting a word.",
	["transcript_correction_unavailable"]: "Transcript correction is temporarily unavailable. Try again later.",
	["transcript_changed_since_correction"]: "The transcript changed. Review it and select the word again.",
	["request_failed"]: "Couldn't confirm the correction"
};
var TranscriptTermCorrectionError = class TranscriptTermCorrectionError extends Error {
	code;
	constructor(code, cause) {
		super(code, { cause });
		this.code = code;
		this.name = "TranscriptTermCorrectionError";
	}
	get displayMessage() {
		return displayMessages[this.code];
	}
	get displayDescription() {
		if (this.code === "request_failed") return "The latest available transcript is shown. Review it before trying again.";
	}
	get requiresReselection() {
		return this.code !== "request_failed";
	}
	static from(error) {
		if (error instanceof TranscriptTermCorrectionError) return error;
		let code = "request_failed";
		if (error instanceof HttpError && error.status === 409) switch (error.detail?.error_code) {
			case "meeting_is_recording":
			case "transcript_is_processing":
			case "transcript_correction_unavailable":
				code = error.detail.error_code;
				break;
			default: code = "transcript_changed_since_correction";
		}
		return new TranscriptTermCorrectionError(code, error);
	}
};
//#endregion
//#region src/meetings/transcript-word-ranges.ts
const intlWithSegmenter = Intl;
const wordSegmenter = typeof intlWithSegmenter.Segmenter === "function" ? new intlWithSegmenter.Segmenter(void 0, { granularity: "word" }) : null;
function tokenizeWords(text) {
	const tokens = [];
	let offset = 0;
	const pushToken = (tokenText) => {
		const core = tokenText.trim();
		const leadingLength = tokenText.indexOf(core);
		tokens.push({
			leading: tokenText.slice(0, leadingLength),
			core,
			trailing: tokenText.slice(leadingLength + core.length),
			start: offset,
			end: offset + tokenText.length
		});
		offset += tokenText.length;
	};
	if (wordSegmenter) {
		let pendingText = "";
		let pendingHasWord = false;
		for (const { segment, isWordLike } of wordSegmenter.segment(text)) {
			if (isWordLike && pendingHasWord) {
				pushToken(pendingText);
				pendingText = "";
				pendingHasWord = false;
			}
			pendingText += segment;
			pendingHasWord ||= Boolean(isWordLike);
		}
		if (pendingText) pushToken(pendingText);
		return tokens;
	}
	for (const run of text.match(/\s*\S+\s*/g) ?? []) pushToken(run);
	return tokens;
}
function getCorrectableTermRanges(text) {
	return tokenizeWords(text).flatMap((word) => {
		const term = word.core.replace(/^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu, "");
		if (!term) return [];
		const tokenStart = word.start + word.leading.length;
		const start = tokenStart + word.core.indexOf(term);
		return [{
			term,
			start,
			end: start + term.length,
			tokenStart,
			tokenEnd: tokenStart + word.core.length
		}];
	});
}
/** Match selectable words, then convert their UTF-16 offsets to Python code points */
function getTranscriptTermMatchOffsets(transcript, originalTerm) {
	const pattern = new RegExp(`^(?:${escapeRegExp(originalTerm)})$`, "iu");
	const offsets = [];
	let lineStart = 0;
	let previousUtf16Offset = 0;
	let codePointOffset = 0;
	for (const line of transcript.split("\n")) {
		const headerLength = /^\[[^\]\n]*\]: ?/.exec(line)?.[0].length ?? 0;
		for (const range of getCorrectableTermRanges(line.slice(headerLength))) {
			if (!pattern.test(range.term)) continue;
			const utf16Offset = lineStart + headerLength + range.start;
			codePointOffset += Array.from(transcript.slice(previousUtf16Offset, utf16Offset)).length;
			previousUtf16Offset = utf16Offset;
			offsets.push(codePointOffset);
		}
		lineStart += line.length + 1;
	}
	return offsets;
}
//#endregion
//#region src/core/search/search-item-builders.ts
const MEETING_NOTE_SEARCH_ID_SUFFIX = ":meeting_note";
const MEETING_SUMMARY_SEARCH_ID_SUFFIX = ":meeting_summary";
function getMeetingNoteSearchId(meetingId) {
	return `${meetingId}${MEETING_NOTE_SEARCH_ID_SUFFIX}`;
}
function getMeetingSummarySearchId(meetingId) {
	return `${meetingId}${MEETING_SUMMARY_SEARCH_ID_SUFFIX}`;
}
function getMeetingIdFromMeetingNoteSearchId(searchId) {
	if (!searchId.endsWith(":meeting_note")) return;
	return searchId.slice(0, -13);
}
function getMeetingIdFromMeetingSummarySearchId(searchId) {
	if (!searchId.endsWith(":meeting_summary")) return;
	return searchId.slice(0, -16);
}
function getMeetingSearchDocIds(meetingId) {
	return [getMeetingSummarySearchId(meetingId), getMeetingNoteSearchId(meetingId)];
}
function toIso(value) {
	if (value instanceof Date) return value.toISOString();
	if (typeof value === "string" && value) return value;
	return (/* @__PURE__ */ new Date(0)).toISOString();
}
function toText(value) {
	return typeof value === "string" ? value : String(value ?? "");
}
function buildMeetingSearchItems(meeting) {
	const items = [];
	const updatedAt = toIso(meeting.updatedAt);
	const summary = meeting.summary?.trim();
	if (summary) items.push({
		id: getMeetingSummarySearchId(meeting.id),
		type: "meeting_summary",
		title: toText(meeting.name),
		content: summary,
		updatedAt
	});
	const notesText = meeting.notesText?.trim() ?? "";
	if (notesText) items.push({
		id: getMeetingNoteSearchId(meeting.id),
		type: "meeting_note",
		title: toText(meeting.name),
		content: notesText,
		updatedAt
	});
	return items;
}
function buildThreadItems(threads, options = {}) {
	const requireTitle = options.requireTitle ?? false;
	return threads.filter((thread) => requireTitle ? thread.id && thread.title : thread.id).map((thread) => {
		const reportTemplateId = thread.reportTemplateId ?? void 0;
		return {
			id: thread.id,
			type: "thread",
			title: toText(thread.title),
			updatedAt: toIso(thread.updatedAt),
			...reportTemplateId === void 0 ? {} : { reportTemplateId }
		};
	});
}
function buildMessageItems(messages, options = {}) {
	const requireContent = options.requireContent ?? false;
	return messages.filter((message) => Boolean(message.id && message.threadId && (!requireContent || message.content))).map((message) => ({
		id: message.id,
		type: "message",
		content: toText(message.content),
		threadId: message.threadId,
		updatedAt: toIso(message.updatedAt)
	}));
}
function buildFileItems(files) {
	return files.filter((file) => file.id && file.filename).map((file) => ({
		id: file.id,
		type: "file",
		title: file.filename,
		contentType: file.contentType,
		updatedAt: toIso(file.updatedAt)
	}));
}
function buildJournalItems(journals) {
	return journals.filter((journal) => journal.id).map((journal) => ({
		id: journal.id,
		type: "journal",
		content: toText(journal.text),
		title: toText(journal.sections?.title),
		updatedAt: toIso(journal.updatedAt)
	}));
}
function buildMeetingItems(meetings, options = {}) {
	if (!options.notesToText) return meetings.filter((m) => m.id).flatMap((m) => buildMeetingSearchItems({
		id: m.id,
		name: m.name,
		summary: m.summary,
		updatedAt: m.updatedAt
	}));
	const notesToText = options.notesToText;
	return meetings.filter((m) => m.id).flatMap((m) => buildMeetingSearchItems({
		id: m.id,
		name: m.name,
		summary: m.summary,
		notesText: m.notes ? notesToText(m.notes)?.trim() : "",
		updatedAt: m.updatedAt
	}));
}
//#endregion
//#region src/core/utils/notes.ts
const JSONContentSchema = z.lazy(() => z.object({
	type: z.string().optional(),
	attrs: z.record(z.string(), z.any()).optional(),
	content: z.array(JSONContentSchema).optional(),
	marks: z.array(z.object({
		type: z.string(),
		attrs: z.record(z.string(), z.any()).optional()
	}).catchall(z.any())).optional(),
	text: z.string().optional()
}).catchall(z.any()));
const EMPTY_JSON_CONTENT = {
	type: "doc",
	content: []
};
function normalizeJsonValue(value) {
	if (value === void 0 || value === null) return void 0;
	if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
	if (Array.isArray(value)) return value.map((item) => normalizeJsonValue(item)).filter((item) => item !== void 0);
	if (typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, entryValue]) => [key, normalizeJsonValue(entryValue)]).filter(([, entryValue]) => entryValue !== void 0));
}
function normalizeNotes(notes) {
	return normalizeJsonValue(notes);
}
function parseNotes(notes) {
	if (notes === null || notes === void 0) return null;
	let parsed = notes;
	if (typeof notes === "string") try {
		parsed = JSON.parse(notes);
	} catch {
		return null;
	}
	const validated = JSONContentSchema.safeParse(parsed);
	return validated.success ? normalizeNotes(validated.data) : null;
}
//#endregion
//#region src/core/sync/stores/meeting-store.ts
const GROUPING_FIELDS = [
	"name",
	"eventId",
	"calendarSeriesId",
	"projectId",
	"attendees"
];
const mapPreps = (preps) => (preps ?? []).map((variant) => ({
	id: variant.id,
	agentName: variant.agent_name,
	content: variant.content,
	createdAt: parseDate(variant.created_at)
}));
const parseOptionalDate = (value) => value ? parseDate(value) : void 0;
/**
* 5 h 5 min, the same as `prepTimeout` on iOS and `PREP_TIMEOUT_SECONDS` on Android: 3 minutes over
* `_PREP_TOTAL_TIMEOUT` in backend/lb/features/meetings/meetings_workflows.py, the longest the backend works on a prep
* before it marks it failed
*/
const PREP_TIMEOUT_MS = 183e5;
var MeetingStore = class MeetingStore extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	ModelClass = Meeting;
	meetingIdsByProject = /* @__PURE__ */ new Map();
	meetingIdsByEvent = observable.map();
	projectIndexVersion = observable.box(0);
	groupingRevisionBox = observable.box(0);
	knownGrouping = /* @__PURE__ */ new Map();
	pendingCreates = /* @__PURE__ */ new Map();
	failedCreates = /* @__PURE__ */ new Map();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("meetings");
		observe(this.data, (change) => {
			if (change.type === "add") {
				this.addToProjectIndex(change.newValue);
				this.indexEvent(change.newValue.id, change.newValue.eventId);
			} else if (change.type === "delete") {
				this.removeFromProjectIndex(change.oldValue);
				this.unindexEvent(change.oldValue.id, change.oldValue.eventId);
			}
		});
		makeObservable(this, {
			sortedMeetings: computed,
			meetingsByLastActivity: computed,
			fetchAll: action.bound,
			persistAndHydrateMeetings: action.bound,
			createMeeting: action.bound,
			createPrepMeeting: action.bound,
			updateMeeting: action.bound,
			updateMeetingSummary: action.bound,
			setProjectId: action.bound,
			clearProjectAssignmentsForProject: action.bound,
			removeLocally: action.bound,
			deleteMeeting: action.bound,
			summarizeMeeting: action.bound,
			fetchMeetingTranscript: action.bound,
			refreshCalendarAccountsStatus: action.bound,
			syncCalendarNow: action.bound
		});
	}
	get sortedMeetings() {
		const disabledSources = this.sdk.userConfig.meetings.get("disabled_note_sources") ?? [];
		return orderBy(Array.from(this.data.values()).filter((item) => !item.deletedAt && !(item.externalSource && disabledSources.includes(item.externalSource))), [(meeting) => meeting.lastTranscribedAt ?? meeting.eventStartAt ?? meeting.createdAt], ["desc"]);
	}
	get meetingsByLastActivity() {
		return orderBy(Array.from(this.data.values()).filter((item) => !item.deletedAt), [(meeting) => meeting.lastTranscribedAt ?? meeting.createdAt], ["desc"]);
	}
	getMeetingsForFilter = computedFn(function(filter) {
		const meetings = this.sortedMeetings;
		switch (filter.kind) {
			case "all-meetings": return meetings;
			case "no-project": return meetings.filter((meeting) => !meeting.projectId);
			case "project": return meetings.filter((meeting) => meeting.projectId === filter.projectId);
		}
	});
	getMeetingByEventId(eventId) {
		let newest;
		for (const id of this.meetingIdsByEvent.get(eventId) ?? []) {
			const meeting = this.data.get(id);
			if (!meeting || meeting.deletedAt) continue;
			if (!newest || meeting.updatedAt > newest.updatedAt) newest = meeting;
		}
		return newest;
	}
	indexEvent(meetingId, eventId) {
		if (!eventId) return;
		let set = this.meetingIdsByEvent.get(eventId);
		if (!set) {
			set = observable.set();
			this.meetingIdsByEvent.set(eventId, set);
		}
		set.add(meetingId);
	}
	unindexEvent(meetingId, eventId) {
		if (!eventId) return;
		const set = this.meetingIdsByEvent.get(eventId);
		set?.delete(meetingId);
		if (set?.size === 0) this.meetingIdsByEvent.delete(eventId);
	}
	moveEventIndex(meetingId, previous, next) {
		if (previous === next) return;
		this.unindexEvent(meetingId, previous);
		this.indexEvent(meetingId, next);
	}
	/** Reuses the meeting already attached to the calendar event, on disk or in memory, before creating one */
	async ensurePrepMeeting(eventId, title, options) {
		return await this.findByEventId(eventId) ?? this.createPrepMeeting(eventId, title, options);
	}
	async findByEventId(eventId) {
		const inMemory = this.getMeetingByEventId(eventId);
		if (inMemory) return inMemory;
		const record = await this.requireMeetingQueries().getByEventId(eventId);
		if (!record) return void 0;
		return this.add({
			...record,
			deletedAt: record.deletedAt ? record.deletedAt : void 0,
			notes: parseNotes(record.notes)
		});
	}
	getNotesText(notes) {
		if (!notes) return "";
		return (this.sdk.options.meetingNotesToText?.(notes) ?? notesToText(notes)).trim();
	}
	async loadInitialPage(options) {
		const meetingQueries = this.requireMeetingQueries();
		this.clear();
		const generation = this.generation;
		const page = await meetingQueries.listBySortAt(options);
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of page.records) this.add({
				...record,
				deletedAt: record.deletedAt ? record.deletedAt : void 0,
				notes: parseNotes(record.notes)
			});
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
			this.isLoaded = true;
		});
	}
	async loadNextPage(options) {
		const meetingQueries = this.requireMeetingQueries();
		if (!this.currentPageCursor || !this.hasMorePages) return;
		const generation = this.generation;
		const page = await meetingQueries.listBySortAtAfter(this.currentPageCursor, options);
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of page.records) this.add({
				...record,
				deletedAt: record.deletedAt ? record.deletedAt : void 0,
				notes: parseNotes(record.notes)
			});
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
		});
	}
	async queryInitialListPage(filter) {
		const meetingQueries = this.requireMeetingQueries();
		const generation = this.generation;
		const page = await meetingQueries.listBySortAt({ filter });
		if (!this.isCurrentGeneration(generation)) return {
			meetings: [],
			state: {
				cursor: void 0,
				hasMore: false
			},
			hasMore: false
		};
		return {
			meetings: this.hydrateListRecords(page.records),
			state: {
				cursor: page.nextCursor,
				hasMore: page.hasMore
			},
			hasMore: page.hasMore
		};
	}
	async queryNextListPage(filter, state) {
		if (!state.hasMore || !state.cursor) return {
			meetings: [],
			state,
			hasMore: false
		};
		const generation = this.generation;
		const page = await this.requireMeetingQueries().listBySortAtAfter(state.cursor, { filter });
		if (!this.isCurrentGeneration(generation)) return {
			meetings: [],
			state,
			hasMore: false
		};
		return {
			meetings: this.hydrateListRecords(page.records),
			state: {
				cursor: page.nextCursor,
				hasMore: page.hasMore
			},
			hasMore: page.hasMore
		};
	}
	async loadProjectMeetings(projectId) {
		const queries = this.requireMeetingQueries();
		const generation = this.generation;
		const filter = {
			kind: "project",
			projectId
		};
		const records = await drainEntityPages((options) => queries.listBySortAt({
			...options,
			filter
		}), (cursor, options) => queries.listBySortAtAfter(cursor, {
			...options,
			filter
		}));
		if (!this.isCurrentGeneration(generation)) return;
		this.hydrateListRecords(records);
	}
	get groupingRevision() {
		return this.groupingRevisionBox.get();
	}
	rememberGrouping(records) {
		let changed = false;
		for (const record of records) {
			const grouping = hashString(JSON.stringify(GROUPING_FIELDS.map((field) => record[field])));
			if (this.knownGrouping.get(record.id) !== grouping) changed = true;
			this.knownGrouping.set(record.id, grouping);
		}
		return changed;
	}
	loadAttendeeSummaries() {
		return this.requireMeetingQueries().listAttendeeSummaries();
	}
	async loadByIds(ids) {
		const missing = ids.filter((id) => !this.data.has(id));
		if (missing.length === 0) return;
		const generation = this.generation;
		const queries = this.requireMeetingQueries();
		const records = await this.fetchRecordsByIdChunks(queries.getByIds.bind(queries), missing);
		if (!this.isCurrentGeneration(generation)) return;
		this.hydrateListRecords(records.filter((record) => !this.data.has(record.id)));
	}
	hydrateListRecords(records) {
		return runInAction(() => records.map((record) => this.add({
			...record,
			deletedAt: record.deletedAt ? record.deletedAt : void 0,
			notes: parseNotes(record.notes)
		})));
	}
	async runRefreshFromStorage() {
		const meetingQueries = this.requireMeetingQueries();
		const residentBefore = new Set(this.data.keys());
		if (residentBefore.size === 0) {
			await this.loadInitialPage();
			this.bumpGroupingRevision();
			return;
		}
		const page = await meetingQueries.listBySortAt();
		const freshIds = /* @__PURE__ */ new Set();
		const fresh = [];
		for (const record of page.records) {
			fresh.push(record);
			freshIds.add(record.id);
		}
		const detailOnlyIds = Array.from(residentBefore).filter((id) => !freshIds.has(id));
		if (detailOnlyIds.length > 0) {
			const detailRecords = await this.fetchRecordsByIdChunks(meetingQueries.getByIds.bind(meetingQueries), detailOnlyIds);
			for (const record of detailRecords) {
				fresh.push(record);
				freshIds.add(record.id);
			}
		}
		if (freshIds.size === 0 && residentBefore.size > 0) {
			this.sdk.logger.warn("Skipping meeting refresh wipe: storage returned no rows for non-empty resident set", {
				context: "MeetingStore:runRefreshFromStorage",
				metadata: { residentBefore: residentBefore.size }
			});
			return;
		}
		let removed = false;
		runInAction(() => {
			for (const record of fresh) this.applyRefreshedRecord({
				...record,
				notes: parseNotes(record.notes)
			});
			for (const id of residentBefore) {
				if (freshIds.has(id)) continue;
				const existing = this.data.get(id);
				if (!existing) continue;
				if (this.shouldRetainLocal(existing)) continue;
				this.data.delete(id);
				removed = true;
			}
			this.currentPageCursor = page.nextCursor;
			this.hasMorePages = page.hasMore;
		});
		if (this.rememberGrouping(fresh) || removed) this.bumpGroupingRevision();
	}
	requireMeetingQueries() {
		const meetingQueries = this.sdk.storage.entities.meetings;
		if (!meetingQueries) throw new MissingEntityQueriesError("meetings");
		return meetingQueries;
	}
	async fetchAll() {
		await this.ensureListLoadedFromStorage();
	}
	async getStoredPersonifiedTranscript(meetingId) {
		const record = await this.sdk.storage.get(this.tableName, meetingId);
		if (!record || record.deletedAt) return;
		return record.personifiedTranscript ?? void 0;
	}
	mapDbMeeting(dbMeeting) {
		const existingMeeting = this.data.get(dbMeeting.id);
		const incomingPrepStatus = dbMeeting.prep_status ?? null;
		const prepStatus = existingMeeting?.prepStatus === "in_progress" && (incomingPrepStatus === null || incomingPrepStatus === "initial") ? existingMeeting.prepStatus : incomingPrepStatus;
		return {
			...existingMeeting ? existingMeeting.toJSON() : {},
			id: dbMeeting.id,
			name: dbMeeting.name,
			summary: dbMeeting.summary,
			tldr: dbMeeting.tldr,
			personifiedTranscript: dbMeeting.personified_transcript,
			notes: parseNotes(dbMeeting.notes),
			prep: dbMeeting.prep,
			..."prep_id" in dbMeeting ? { prepId: dbMeeting.prep_id ?? null } : {},
			..."preps" in dbMeeting ? { preps: mapPreps(dbMeeting.preps) } : {},
			prepStatus,
			sourceApp: dbMeeting.source_app,
			..."external_source" in dbMeeting ? { externalSource: dbMeeting.external_source ?? null } : {},
			eventId: dbMeeting.event_id,
			..."calendar_series_id" in dbMeeting ? { calendarSeriesId: dbMeeting.calendar_series_id ?? null } : {},
			status: dbMeeting.status,
			summaryStatus: dbMeeting.summary_status,
			attendees: dbMeeting.attendees,
			speakers: dbMeeting.speakers,
			..."project_id" in dbMeeting ? { projectId: dbMeeting.project_id ?? null } : {},
			..."custom_vocabulary" in dbMeeting ? { customVocabulary: dbMeeting.custom_vocabulary ?? null } : {},
			eventStartAt: parseOptionalDate(dbMeeting.event_start_at),
			endedAt: parseOptionalDate(dbMeeting.ended_at),
			firstChunkTimestamp: parseOptionalDate(dbMeeting.first_chunk_timestamp),
			lastTranscribedAt: parseOptionalDate(dbMeeting.last_transcribed_at),
			firstTranscriptAt: parseOptionalDate(dbMeeting.first_transcript_at),
			transcriptDeletedAt: parseOptionalDate(dbMeeting.transcript_deleted_at),
			createdAt: parseDate(dbMeeting.created_at),
			updatedAt: parseDate(dbMeeting.updated_at)
		};
	}
	async flushPersistedMeetingBatch(added, toPersistOnly) {
		if (!MeetingStore.persisted) return;
		if (added.length > 0) await this.sdk.storage.bulkSet(this.tableName, added);
		if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
	}
	async persistAndHydrateMeetings(dbMeetings) {
		try {
			const toPersist = [];
			const toDelete = [];
			for (const dbMeeting of dbMeetings) {
				if (!dbMeeting.id) continue;
				if (dbMeeting.deleted_at) {
					toDelete.push(dbMeeting.id);
					continue;
				}
				toPersist.push(this.mapDbMeeting(dbMeeting));
			}
			if (toDelete.length > 0) {
				toDelete.forEach((id) => this.data.delete(id));
				if (MeetingStore.persisted) await this.sdk.storage.bulkRemove(this.tableName, toDelete);
			}
			if (toPersist.length > 0) {
				const toHydrate = toPersist.filter((d) => this.data.has(d.id));
				const toPersistOnly = toPersist.filter((d) => !this.data.has(d.id));
				const addedMeetings = runInAction(() => toHydrate.map((data) => this.add(data)));
				await this.flushPersistedMeetingBatch(addedMeetings, toPersistOnly);
			}
			toDelete.forEach((id) => this.knownGrouping.delete(id));
			if (this.rememberGrouping(toPersist) || toDelete.length > 0) this.bumpGroupingRevision();
			return {
				deletedSearchDocIds: toDelete.flatMap((id) => getMeetingSearchDocIds(id)),
				upsertedRecords: toPersist
			};
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error processing meetings:" },
				context: "MeetingStore:persistAndHydrateMeetings"
			});
			throw error;
		}
	}
	createMeeting(options) {
		const meetingId = options?.id ?? v4();
		const now = /* @__PURE__ */ new Date();
		const meeting = this.add({
			id: meetingId,
			name: options?.name ?? null,
			eventId: options?.eventId,
			sourceApp: options?.sourceApp,
			projectId: options?.projectId ?? null,
			status: "active",
			summaryStatus: "initial",
			createdAt: now,
			updatedAt: now
		});
		this.persistCreate(meetingId, {
			id: meetingId,
			autostart_meeting: false,
			event_id: options?.eventId,
			name: options?.name,
			source_app: options?.sourceApp,
			...options?.projectId !== void 0 ? { project_id: options.projectId } : {},
			...options?.recordingDeviceId !== void 0 ? { recording_device_id: options.recordingDeviceId } : {},
			...options?.recordingPlatform !== void 0 ? { recording_platform: options.recordingPlatform } : {}
		}, options, "Failed to create meeting.", "MeetingStore:createMeeting");
		return meeting;
	}
	createPrepMeeting(eventId, title, options) {
		const meetingId = v4();
		const now = /* @__PURE__ */ new Date();
		const meeting = this.add({
			id: meetingId,
			name: title,
			eventId,
			status: "active",
			summaryStatus: "initial",
			createdAt: now,
			updatedAt: now
		});
		this.persistCreate(meetingId, {
			id: meetingId,
			event_id: eventId,
			name: title,
			...options?.recordingDeviceId !== void 0 ? { recording_device_id: options.recordingDeviceId } : {},
			...options?.recordingPlatform !== void 0 ? { recording_platform: options.recordingPlatform } : {}
		}, options, "Failed to create prep meeting.", "MeetingStore:createPrepMeeting");
		return meeting;
	}
	persistCreate(meetingId, body, options, failureMessage, context) {
		const controller = new AbortController();
		const generation = this.generation;
		const creation = {
			controller,
			updates: {},
			ready: this.postMeeting(body, controller.signal).then((response) => {
				controller.signal.throwIfAborted();
				if (!this.isCurrentGeneration(generation)) throw new MeetingCreationCancelledError("Meeting creation was cancelled");
				if (!response) throw new Error("Meeting creation returned no meeting");
				this.applyCreateResponse(meetingId, response, options?.onPersist);
			}).catch((error) => this.handleCreateFailure(meetingId, creation, controller, generation, error, options, {
				body,
				failureMessage,
				context
			})).finally(() => {
				if (this.pendingCreates.get(meetingId) === creation) this.pendingCreates.delete(meetingId);
			})
		};
		this.pendingCreates.set(meetingId, creation);
		creation.ready.catch(() => {});
	}
	handleCreateFailure(meetingId, creation, controller, generation, caughtError, options, failedCreate) {
		const cancelled = controller.signal.aborted || !this.isCurrentGeneration(generation);
		const error = cancelled ? new MeetingCreationCancelledError("Meeting creation was cancelled") : caughtError;
		if (cancelled) {
			options?.onError?.(error);
			throw error;
		}
		const { failureMessage, context } = failedCreate;
		if (Object.keys(creation.updates).length > 0) {
			this.failedCreates.set(meetingId, failedCreate);
			this.logCreateFailure(error, failureMessage, context);
		} else this.rollbackCreate(meetingId, error, failureMessage, context);
		options?.onError?.(error);
		throw error;
	}
	postMeeting(body, signal) {
		return this.sdk.apiClient.fetch("meetings", {
			method: "POST",
			body: JSON.stringify(body),
			signal,
			backoff: {
				numOfAttempts: 10,
				startingDelay: 150,
				maxDelay: 2e3
			}
		});
	}
	applyCreateResponse(meetingId, response, onPersist) {
		runInAction(() => {
			const meeting = this.get(meetingId);
			if (!meeting) return;
			meeting.name = response.name;
			this.moveEventIndex(meeting.id, meeting.eventId, response.event_id);
			meeting.eventId = response.event_id;
			if ("calendar_series_id" in response) meeting.calendarSeriesId = response.calendar_series_id ?? null;
			if ("event_start_at" in response) meeting.eventStartAt = parseOptionalDate(response.event_start_at) ?? null;
			if ("source_app" in response) meeting.sourceApp = response.source_app ?? null;
			if ("project_id" in response) this.applyProjectId(meeting, response.project_id ?? null);
			meeting.status = response.status;
			meeting.summaryStatus = response.summary_status;
			if ("attendees" in response) meeting.attendees = response.attendees ?? null;
			if ("custom_vocabulary" in response) meeting.customVocabulary = response.custom_vocabulary ?? null;
			meeting.createdAt = parseDate(response.created_at);
			meeting.updatedAt = parseDate(response.updated_at);
			if (MeetingStore.persisted) this.sdk.storage.set(this.tableName, meeting);
		});
		this.bumpGroupingRevision();
		onPersist?.();
	}
	rollbackCreate(meetingId, error, message, context) {
		runInAction(() => {
			this.data.delete(meetingId);
		});
		this.logCreateFailure(error, message, context);
	}
	logCreateFailure(error, message, context) {
		this.sdk.logger.error(error instanceof Error || typeof error === "string" ? error : String(error), {
			metadata: { message },
			context
		});
	}
	retryFailedCreate(meetingId) {
		const failedCreate = this.failedCreates.get(meetingId);
		if (!failedCreate) return;
		this.failedCreates.delete(meetingId);
		this.persistCreate(meetingId, failedCreate.body, void 0, failedCreate.failureMessage, failedCreate.context);
	}
	buildUpdatePayload(data) {
		const payload = {};
		if (data.name !== void 0) payload.name = data.name;
		if (data.sourceApp !== void 0) payload.source_app = data.sourceApp;
		if (data.summary !== void 0) payload.summary = data.summary;
		if (data.tldr !== void 0) payload.tldr = data.tldr;
		if (data.notes !== void 0) payload.notes = data.notes;
		if (data.prep !== void 0) payload.prep = data.prep;
		if (data.status !== void 0) payload.status = data.status;
		if (data.summaryStatus !== void 0) payload.summary_status = data.summaryStatus;
		if (data.attendees !== void 0) payload.attendees = data.attendees;
		if (data.speakers !== void 0) payload.speakers = data.speakers;
		if (data.projectId !== void 0) payload.project_id = data.projectId;
		if (data.recordingDeviceId !== void 0) payload.recording_device_id = data.recordingDeviceId;
		if (data.recordingPlatform !== void 0) payload.recording_platform = data.recordingPlatform;
		return payload;
	}
	applyProjectId(meeting, projectId) {
		this.removeFromProjectIndex(meeting);
		meeting.projectId = projectId;
		this.addToProjectIndex(meeting);
	}
	async tryUpdateMeetingViaProjectIndex(meetingId, meeting, data, payload, hasProjectIdUpdate) {
		if (!hasProjectIdUpdate || Object.keys(payload).length !== 1) return false;
		try {
			await updateIndexedProjectAssignment({
				item: meeting,
				projectId: data.projectId ?? null,
				applyProjectId: (nextProjectId) => {
					runInAction(() => {
						this.applyProjectId(meeting, nextProjectId);
					});
				},
				sync: () => this.sdk.apiClient.fetch(`meetings/${meetingId}`, {
					method: "PATCH",
					body: JSON.stringify(payload)
				}),
				persist: async () => {
					if (MeetingStore.persisted) await this.sdk.storage.set(this.tableName, meeting);
					this.bumpGroupingRevision();
				}
			});
			return true;
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to update meeting." },
				context: "MeetingStore:updateMeeting"
			});
			throw error;
		}
	}
	updateMeetingDuringCreate(meetingId, meeting, data, creation) {
		const definedUpdates = Object.fromEntries(Object.entries(data).filter(([, value]) => value !== void 0));
		Object.assign(creation.updates, definedUpdates);
		runInAction(() => {
			if (data.projectId !== void 0) this.applyProjectId(meeting, data.projectId ?? null);
			Object.assign(meeting, definedUpdates);
		});
		const generation = this.generation;
		creation.save ??= creation.ready.then(() => {
			if (!this.isCurrentGeneration(generation)) throw new MeetingCreationCancelledError("Meeting creation was cancelled");
			return this.updateMeeting(meetingId, creation.updates);
		});
		return creation.save;
	}
	async updateMeeting(meetingId, data) {
		const payload = this.buildUpdatePayload(data);
		if (Object.keys(payload).length === 0) return;
		const meeting = this.get(meetingId);
		if (!meeting) throw new Error("Meeting not found");
		this.retryFailedCreate(meetingId);
		const creation = this.pendingCreates.get(meetingId);
		if (creation) return this.updateMeetingDuringCreate(meetingId, meeting, data, creation);
		const originalData = meeting.toJSON();
		const originalProjectId = meeting.projectId ?? null;
		const hasProjectIdUpdate = data.projectId !== void 0 && (meeting.projectId ?? null) !== data.projectId;
		if (await this.tryUpdateMeetingViaProjectIndex(meetingId, meeting, data, payload, hasProjectIdUpdate)) return;
		runInAction(() => {
			if (hasProjectIdUpdate) this.applyProjectId(meeting, data.projectId ?? null);
			Object.assign(meeting, data);
		});
		try {
			await this.sdk.apiClient.fetch(`meetings/${meetingId}`, {
				method: "PATCH",
				body: JSON.stringify(payload)
			});
			if (MeetingStore.persisted) await this.sdk.storage.set(this.tableName, meeting);
			if (GROUPING_FIELDS.some((field) => field in data)) this.bumpGroupingRevision();
		} catch (error) {
			runInAction(() => {
				if (hasProjectIdUpdate) this.applyProjectId(meeting, originalProjectId);
				Object.assign(meeting, originalData);
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to update meeting." },
				context: "MeetingStore:updateMeeting"
			});
			throw error;
		}
	}
	async updateMeetingSummary(meetingId, summary) {
		const meeting = this.get(meetingId);
		if (!meeting) return;
		const originalSummary = meeting.summary;
		runInAction(() => {
			meeting.summary = summary;
		});
		try {
			await this.sdk.apiClient.fetch(`meetings/${meetingId}/summary`, {
				method: "PATCH",
				body: JSON.stringify({ summary })
			});
			if (MeetingStore.persisted) this.sdk.storage.set(this.tableName, meeting);
		} catch (error) {
			runInAction(() => {
				meeting.summary = originalSummary;
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to update meeting summary." },
				context: "MeetingStore:updateMeetingSummary"
			});
			throw error;
		}
	}
	async setProjectId(meetingId, projectId) {
		const meeting = this.get(meetingId);
		if (!meeting) return;
		if ((meeting.projectId ?? null) === projectId) return;
		await this.updateMeeting(meetingId, { projectId });
	}
	async clearProjectAssignmentsForProject(projectId) {
		const clearedIds = new Set(MeetingStore.persisted ? await this.requireMeetingQueries().clearProjectId(projectId) : []);
		for (const id of this.meetingIdsByProject.get(projectId) ?? []) clearedIds.add(id);
		runInAction(() => {
			for (const meetingId of clearedIds) {
				const meeting = this.data.get(meetingId);
				if (!meeting || meeting.projectId !== projectId) continue;
				this.applyProjectId(meeting, null);
			}
		});
		if (clearedIds.size > 0) this.bumpGroupingRevision();
		return [...clearedIds];
	}
	addToProjectIndex(meeting) {
		if (!meeting.projectId) return;
		let set = this.meetingIdsByProject.get(meeting.projectId);
		if (!set) {
			set = /* @__PURE__ */ new Set();
			this.meetingIdsByProject.set(meeting.projectId, set);
		}
		set.add(meeting.id);
		this.bumpProjectIndexVersion();
	}
	removeMeetingIdFromProjectIndex(meetingId, projectId) {
		const set = this.meetingIdsByProject.get(projectId);
		if (!set) return;
		set.delete(meetingId);
		if (set.size === 0) this.meetingIdsByProject.delete(projectId);
		this.bumpProjectIndexVersion();
	}
	removeFromProjectIndex(meeting) {
		if (!meeting.projectId) return;
		this.removeMeetingIdFromProjectIndex(meeting.id, meeting.projectId);
	}
	bumpGroupingRevision() {
		runInAction(() => this.groupingRevisionBox.set(this.groupingRevisionBox.get() + 1));
	}
	bumpProjectIndexVersion() {
		runInAction(() => {
			this.projectIndexVersion.set(this.projectIndexVersion.get() + 1);
		});
		this.sdk.stores.projects.scheduleItemCountRefresh();
	}
	getMeetingsForProject(projectId) {
		this.projectIndexVersion.get();
		const ids = this.meetingIdsByProject.get(projectId);
		if (!ids) return [];
		const result = [];
		for (const id of ids) {
			const meeting = this.data.get(id);
			if (meeting && !meeting.deletedAt) result.push(meeting);
		}
		return result;
	}
	updateExistingModel(existingModel, item) {
		const oldProjectId = existingModel.projectId ?? null;
		const projectIdIsPresent = "projectId" in item;
		const newProjectId = projectIdIsPresent ? item.projectId ?? null : oldProjectId;
		const projectChanged = projectIdIsPresent && oldProjectId !== newProjectId;
		if (projectChanged && oldProjectId) this.removeMeetingIdFromProjectIndex(existingModel.id, oldProjectId);
		const previousEventId = existingModel.eventId;
		existingModel.updateData(item);
		if ("eventId" in item) this.moveEventIndex(existingModel.id, previousEventId, existingModel.eventId);
		if (projectChanged && newProjectId) this.addToProjectIndex(existingModel);
		return existingModel;
	}
	clear() {
		for (const creation of this.pendingCreates.values()) creation.controller.abort();
		this.pendingCreates.clear();
		this.failedCreates.clear();
		this.meetingIdsByProject.clear();
		this.meetingIdsByEvent.clear();
		this.bumpProjectIndexVersion();
		super.clear();
	}
	removeLocally(meetingId) {
		this.data.delete(meetingId);
		if (MeetingStore.persisted) this.sdk.storage.remove(this.tableName, meetingId).then(() => this.bumpGroupingRevision()).catch(() => {});
	}
	async deleteMeeting(meetingId) {
		const meeting = this.get(meetingId);
		if (!meeting) return;
		const originalMeeting = meeting.toJSON();
		runInAction(() => {
			this.data.delete(meetingId);
		});
		try {
			await this.sdk.apiClient.fetch(`meetings/${meetingId}`, { method: "DELETE" });
			if (MeetingStore.persisted) await this.sdk.storage.remove(this.tableName, meetingId);
			this.bumpGroupingRevision();
		} catch (error) {
			if (error instanceof HttpError && error.status === 404) {
				this.sdk.logger.error(error, {
					metadata: { message: "Got a 404" },
					context: "MeetingStore:deleteMeeting"
				});
				if (MeetingStore.persisted) await this.sdk.storage.remove(this.tableName, meetingId);
				this.bumpGroupingRevision();
				return;
			}
			runInAction(() => {
				this.add(originalMeeting);
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to delete meeting." },
				context: "MeetingStore:deleteMeeting"
			});
			throw error;
		}
	}
	async summarizeMeeting(meetingId, options) {
		const meeting = this.get(meetingId);
		const automatic = options?.automatic ?? false;
		if (automatic && meeting?.summaryStatus === "skipped") return;
		const originalStatus = meeting?.summaryStatus;
		const regenerate = !automatic;
		if (meeting) runInAction(() => {
			meeting.summaryStatus = "in_progress";
		});
		try {
			await this.sdk.apiClient.fetch(`meetings/${meetingId}/summarize`, {
				method: "POST",
				body: JSON.stringify({
					block_on_summary: false,
					automatic,
					regenerate
				})
			});
		} catch (error) {
			if (meeting && originalStatus) runInAction(() => {
				meeting.summaryStatus = originalStatus;
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to summarize meeting." },
				context: "MeetingStore:summarizeMeeting"
			});
			throw error;
		}
	}
	requestPrepContent(eventId, callbacks) {
		const existingMeeting = this.getMeetingByEventId(eventId);
		if (existingMeeting?.prepStatus === "in_progress" && !callbacks?.skipIfCompleted) return;
		if (callbacks?.automatic && existingMeeting?.prepStatus === "skipped") {
			this._reportPrepOutcome(existingMeeting, callbacks);
			return;
		}
		if (existingMeeting) {
			const hasCompletedPrep = Boolean(existingMeeting.prep || existingMeeting.prepStatus === "completed");
			if (!callbacks?.skipIfCompleted || !hasCompletedPrep) this._setPrepStatus(existingMeeting, "in_progress");
		}
		const cleanup = this._watchBootstrapForPrep(eventId, callbacks);
		this.sdk.apiClient.fetch(`calendar/events/${eventId}/prep`, {
			method: "POST",
			body: JSON.stringify({
				dont_block_on_completion: true,
				skip_if_completed: callbacks?.skipIfCompleted ?? false,
				automatic: callbacks?.automatic ?? false
			})
		}).then((response) => {
			if (response.in_progress) return;
			cleanup();
			const meeting = this.add(this.mapDbMeeting(response.meeting));
			this._reportPrepOutcome(meeting, callbacks, response.skipped_existing_prep ?? false);
		}).catch((error) => {
			cleanup();
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to start meeting prep generation." },
				context: "MeetingStore:requestPrepContent"
			});
			if (existingMeeting) {
				this._setPrepStatus(existingMeeting, "failed");
				this._reportPrepOutcome(existingMeeting, callbacks);
			}
			callbacks?.onRequestFailed?.();
		});
	}
	/** Reports a prep request's terminal outcome, whether seen locally, over HTTP, or in /bootstrap */
	_reportPrepOutcome(meeting, callbacks, existingPrep = false) {
		if (meeting.prepStatus === "skipped") callbacks?.onSkipped?.(meeting);
		else if (existingPrep) callbacks?.onAlreadyCompleted?.(meeting);
		else if (meeting.prepStatus === "failed") callbacks?.onFailed?.(meeting);
		else callbacks?.onCompleted?.(meeting);
	}
	_setPrepStatus(meeting, status) {
		runInAction(() => {
			meeting.prepStatus = status;
		});
		if (MeetingStore.persisted) this.sdk.storage.set(this.tableName, meeting);
	}
	/**
	* Watches /bootstrap polling for prep completion/failure and sets up a
	* safety-net timeout. Returns a cleanup function.
	*/
	_watchBootstrapForPrep(eventId, callbacks) {
		const timeoutId = setTimeout(() => {
			unlisten();
			const meeting = this.getMeetingByEventId(eventId);
			if (meeting && meeting.prepStatus === "in_progress") {
				this._setPrepStatus(meeting, "failed");
				this._reportPrepOutcome(meeting, callbacks);
			}
		}, PREP_TIMEOUT_MS);
		const unlisten = this.sdk.onBootstrap(() => {
			const meeting = this.getMeetingByEventId(eventId);
			if (!meeting) return;
			switch (meeting.prepStatus) {
				case "completed":
					unlistenAndCancelTimeout();
					runInAction(() => {
						meeting.hasNotification = true;
						meeting.prepContentCollapsed = false;
					});
					this._reportPrepOutcome(meeting, callbacks);
					break;
				case "skipped":
				case "failed":
					unlistenAndCancelTimeout();
					this._reportPrepOutcome(meeting, callbacks);
					break;
				case "initial":
				case "in_progress":
				case null: break;
				default: assertNever(meeting.prepStatus);
			}
		});
		const unlistenAndCancelTimeout = () => {
			unlisten();
			clearTimeout(timeoutId);
		};
		return unlistenAndCancelTimeout;
	}
	async fetchMeetingTranscript(meetingId) {
		const generation = this.generation;
		const meeting = this.get(meetingId);
		if (!meeting) {
			this.sdk.logger.error(/* @__PURE__ */ new Error("Meeting not found"), {
				metadata: { message: "Failed to fetch meeting transcript: meeting not found" },
				context: "MeetingStore:fetchMeetingTranscript"
			});
			throw new Error("Meeting not found");
		}
		try {
			const response = await this.sdk.apiClient.fetch(`meetings/${meetingId}/transcript`);
			if (response) {
				const transcript = response.personified_transcript;
				const transcriptState = {
					personifiedTranscript: transcript,
					currentLength: transcript ? Array.from(transcript).length : 0
				};
				if (this.isCurrentGeneration(generation)) await this.cacheTranscript(meeting, transcript);
				return transcriptState;
			}
			return {
				personifiedTranscript: null,
				currentLength: 0
			};
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to fetch meeting transcript." },
				context: "MeetingStore:fetchMeetingTranscript"
			});
			throw error;
		}
	}
	async deleteTranscriptChunks(meetingId, expectedTranscript, chunkIndices) {
		const meeting = this.get(meetingId);
		if (!meeting) throw new Error("Meeting not found");
		let response;
		try {
			response = await this.sdk.apiClient.fetch(`meetings/${meetingId}/transcript/delete_chunks`, {
				method: "POST",
				body: JSON.stringify({
					expected_transcript: expectedTranscript,
					chunk_indices: chunkIndices
				})
			});
			if (!response) throw new Error("Missing transcript deletion response");
		} catch (error) {
			await this.fetchMeetingTranscript(meetingId).catch(() => void 0);
			throw error;
		}
		const transcript = response.personified_transcript;
		await this.cacheTranscript(meeting, transcript);
		return {
			personifiedTranscript: transcript,
			searchRefreshStatus: response.search_refresh_status
		};
	}
	async correctTranscriptTerm(meetingId, expectedTranscript, originalTerm, correctedTerm) {
		const generation = this.generation;
		const meeting = this.get(meetingId);
		if (!meeting) throw new Error("Meeting not found");
		let response;
		try {
			const body = await this.sdk.apiClient.fetch(`meetings/${meetingId}/transcript/correct_term`, {
				method: "POST",
				body: JSON.stringify({
					expected_transcript: expectedTranscript,
					original_term: originalTerm,
					corrected_term: correctedTerm,
					add_to_vocabulary: true,
					match_offsets: getTranscriptTermMatchOffsets(expectedTranscript, originalTerm)
				})
			});
			response = z.object({
				personified_transcript: z.string(),
				replaced_count: z.number().int().nonnegative(),
				custom_transcription_vocabulary: z.array(z.string()),
				vocabulary_limit_reached: z.boolean(),
				search_refresh_status: z.enum(["pending", "complete"])
			}).parse(body);
		} catch (error) {
			if (this.isCurrentGeneration(generation)) await this.fetchMeetingTranscript(meetingId).catch(() => void 0);
			throw TranscriptTermCorrectionError.from(error);
		}
		if (this.isCurrentGeneration(generation)) await this.cacheTranscript(meeting, response.personified_transcript);
		return {
			personifiedTranscript: response.personified_transcript,
			replacedCount: response.replaced_count,
			customTranscriptionVocabulary: response.custom_transcription_vocabulary,
			vocabularyLimitReached: response.vocabulary_limit_reached,
			searchRefreshStatus: response.search_refresh_status
		};
	}
	async cacheTranscript(meeting, transcript) {
		runInAction(() => {
			meeting.personifiedTranscript = transcript;
		});
		if (MeetingStore.persisted) try {
			await this.sdk.storage.set(this.tableName, meeting);
		} catch (error) {
			this.sdk.logger.error(error instanceof Error ? error : String(error), {
				context: "MeetingStore:cacheTranscript",
				metadata: { message: "Failed to cache the committed transcript." }
			});
		}
	}
	async setMeetingAsPublic(meetingId, visibility = "public") {
		const res = await this.sdk.apiClient.fetch(`meetings/${meetingId}/share`, {
			method: "PATCH",
			body: JSON.stringify({ visibility })
		});
		return MeetingShareResponseSchema.parseAsync(res);
	}
	async revokeMeetingShare(meetingId) {
		const res = await this.sdk.apiClient.fetch(`meetings/${meetingId}/revoke_share`, { method: "PATCH" });
		return z.object({ success: z.boolean() }).parseAsync(res);
	}
	async getMeetingShareStatus(meetingId) {
		const res = await this.sdk.apiClient.fetch(`meetings/${meetingId}/share_status`);
		return MeetingShareStatusSchema.parseAsync(res);
	}
	async addMeetingShareInvite(meetingId, email) {
		const res = await this.sdk.apiClient.fetch(`meetings/${meetingId}/share_invites`, {
			method: "POST",
			body: JSON.stringify({ email })
		});
		return (await MeetingShareInvitesSchema.parseAsync(res)).invited_emails;
	}
	async removeMeetingShareInvite(meetingId, email) {
		const res = await this.sdk.apiClient.fetch(`meetings/${meetingId}/share_invites?email=${encodeURIComponent(email)}`, { method: "DELETE" });
		return (await MeetingShareInvitesSchema.parseAsync(res)).invited_emails;
	}
	async refreshCalendarAccountsStatus() {
		try {
			return await this.sdk.apiClient.fetch("calendar/accounts/refresh-status", { method: "POST" }) ?? { accounts: [] };
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to refresh calendar accounts status." },
				context: "MeetingStore:refreshCalendarAccountsStatus"
			});
			throw error;
		}
	}
	async syncCalendarNow() {
		try {
			return await this.sdk.apiClient.fetch("calendar/sync", { method: "POST" }) ?? {
				accounts: [],
				sync_started: false,
				workflow_id: null
			};
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to sync calendar now." },
				context: "MeetingStore:syncCalendarNow"
			});
			throw error;
		}
	}
	setProjectIdLocal = (meetingId, projectId) => {
		const meeting = this.data.get(meetingId);
		if (!meeting || meeting.projectId === projectId) return;
		runInAction(() => this.applyProjectId(meeting, projectId));
	};
	restoreProjectAssignments = async (ids, projectId) => {
		if (ids.length === 0) return;
		for (const id of ids) this.setProjectIdLocal(id, projectId);
		if (MeetingStore.persisted) await this.requireMeetingQueries().restoreProjectId(ids, projectId);
	};
};
//#endregion
//#region src/core/chat-engine.ts
/** What differs between the engines. Every decision that depends on a chat's engine reads this table */
const ENGINES = {
	v4: {
		onApiV2: false,
		sendEndpoint: "chat_v4",
		sendsFullForm: true,
		steerPath: (threadId) => `chat/${threadId}/steer`,
		editsMessages: true
	},
	v5: {
		onApiV2: true,
		sendEndpoint: "chat_v5",
		sendsFullForm: false,
		steerPath: (threadId) => `chat_v5/${threadId}/steer`,
		editsMessages: false
	}
};
/** A chat runs on `v4` until it is moved to `v5` */
const engineOf = (options, threadId) => options.apiV2?.getChatEngine(threadId) ?? "v4";
const baseUrlOf = (options, engine) => ENGINES[engine].onApiV2 ? options.apiV2?.baseUrl ?? options.apiBaseUrl : options.apiBaseUrl;
/** Where a chat's turns are sent, and whether they carry the full `/chat_v4` form */
function chatSendRoute(options, threadId) {
	const engine = engineOf(options, threadId);
	return {
		endpoint: ENGINES[engine].sendEndpoint,
		baseUrl: baseUrlOf(options, engine),
		sendsFullForm: ENGINES[engine].sendsFullForm
	};
}
/** The chat's steer route, on the backend that runs its turns, so the route can see the running reply */
function chatSteerUrl(options, threadId) {
	const engine = engineOf(options, threadId);
	return new URL(`${baseUrlOf(options, engine)}/${ENGINES[engine].steerPath(threadId)}`);
}
/** Whether a chat on this engine lets the user edit a sent message. Undefined is `v4` */
const engineEditsMessages = (engine) => ENGINES[engine ?? "v4"].editsMessages;
/** The form a `chat_v5` turn is sent with */
function chatV5FormData(payload) {
	const formData = new FormData();
	formData.append("chat_id", payload.threadId);
	formData.append("question", payload.content);
	if (payload.mode) formData.append("mode", payload.mode);
	if (payload.agent) formData.append("agent", payload.agent);
	formData.append("user_message_id", payload.userMessageId);
	return formData;
}
//#endregion
//#region src/core/utils/paywall.ts
/**
* Emit a paywall banner event based on a chat notification payload.
*
* This is the shared entry point for all paywall banner emissions:
* - Streamed chat notifications (websocket)
* - HTTP 400 rate-limit responses
*
* @param notification - The structured notification payload from chat events
*/
function emitPaywallBannerFromNotification(notification) {
	if (notification.kind !== "rate_limit_alert") {
		console.warn("[paywall] Unknown notification kind:", notification.kind);
		return;
	}
	globalEventBus.emit(EventNames.PAYWALL_SHOW_BANNER, { data: {
		kind: "generic",
		title: "Experience Littlebird without limits",
		subtitle: notification.message || "You've hit your daily chat limit, but we don't want to stop your momentum. We've given you extra chats for today, on us. Ready for this every day? Upgrade now."
	} });
}
/**
* Emit a paywall banner event from legacy content-only notification.
*
* Used for backward compatibility with older servers that only send content.
*
* @param content - The notification message content
*/
function emitPaywallBannerFromLegacyContent(content) {
	globalEventBus.emit(EventNames.PAYWALL_SHOW_BANNER, { data: {
		kind: "generic",
		title: "Experience Littlebird without limits",
		subtitle: content || "You've hit your daily chat limit, but we don't want to stop your momentum. We've given you extra chats for today, on us. Ready for this every day? Upgrade now."
	} });
}
//#endregion
//#region src/core/sync/send-error.ts
/**
* Chat status after a failed send. The free-message limit ends the chat as
* completed - the paywall banner explains the stop - while any other failure
* marks it errored.
*/
function chatStatusForSendError(error) {
	const endedByFreeLimit = error instanceof FreeLimitExceededError;
	return {
		status: endedByFreeLimit ? "done" : "errored",
		endedByFreeLimit
	};
}
/** The server's busy refusal of a send, also when the connection error of a down websocket wraps it */
function busyRefusalOf(error) {
	const refusal = error instanceof Error && error.cause instanceof ChatBusyError ? error.cause : error;
	return refusal instanceof ChatBusyError ? refusal : null;
}
function editRefusalMessage(error) {
	const httpError = error instanceof Error && error.cause instanceof HttpError ? error.cause : error;
	if (!(httpError instanceof HttpError)) return null;
	if (![
		400,
		404,
		409
	].includes(httpError.status)) return null;
	return httpError.userErrorMessage ?? "This message can no longer be edited.";
}
//#endregion
//#region src/core/sync/slash-commands.ts
const META_SLASH_COMMANDS = [
	"/info",
	"/mode",
	"/debug",
	"/set",
	"/call",
	"/all",
	"/",
	"/ctx",
	"/sys",
	"/history",
	"/hist",
	"/echo"
];
function parseSlashCommand(input) {
	const [cmdPrefix, ...rest] = input.trim().split(" ", 2);
	return {
		command: cmdPrefix,
		body: rest.length > 0 ? rest[0].trim() : ""
	};
}
function isMetaSlashCommand(input) {
	if (!input.startsWith("/")) return false;
	const { command } = parseSlashCommand(input);
	return META_SLASH_COMMANDS.includes(command);
}
//#endregion
//#region src/core/sync/stores/message-store.ts
const TAIL_SCAN_PAGE_SIZE = 200;
function timeMs(value) {
	const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
	return Number.isNaN(ms) ? void 0 : ms;
}
const CLIENT_TOOL_SERVER_ID = "local";
const SUBAGENT_LIVE_TRANSCRIPT_KEY = "subagent_live_transcript";
const AppsUsedStampSchema = z.object({ apps_used: z.array(z.string()) });
const ReviewFilesStampSchema = z.object({ review_files: z.array(z.object({
	modality_index: z.number().int(),
	file_ids: z.array(z.string())
})) });
function messageSyncFingerprint(dbMessage) {
	const toolCalls = dbMessage.tool_calls ?? [];
	const modalityKeyCounts = dbMessage.modalities_used?.map((modality) => Object.keys(modality).length).join(",") ?? "";
	let toolResultLength = 0;
	let toolArgumentKeyCount = 0;
	let toolStatuses = "";
	for (const toolCall of toolCalls) {
		toolResultLength += toolCall.result?.length ?? 0;
		toolArgumentKeyCount += Object.keys(toolCall.arguments ?? {}).length;
		toolStatuses += toolCall.status ?? "";
	}
	return hashString([
		dbMessage.thread_id ?? "",
		dbMessage.updated_at ?? "",
		dbMessage.content?.length ?? 0,
		dbMessage.thinking_content?.length ?? 0,
		dbMessage.hidden_content?.length ?? 0,
		toolCalls.length,
		toolResultLength,
		toolArgumentKeyCount,
		toolStatuses,
		dbMessage.chat_mode ?? "",
		dbMessage.role ?? "",
		dbMessage.version_ids?.join(",") ?? "",
		dbMessage.interrupted ? "interrupted" : "",
		dbMessage.joined_turn ? "joined_turn" : "",
		modalityKeyCounts,
		dbMessage.context_apps_used?.join(",") ?? ""
	].join("|"));
}
function withoutReplacedMessages(threadMessages) {
	const replacedMessageIds = new Set(threadMessages.flatMap((message) => message.replacedMessageIds));
	if (replacedMessageIds.size === 0) return threadMessages;
	return threadMessages.filter((message) => !replacedMessageIds.has(message.id));
}
var MessageStore = class MessageStore extends Store {
	static persisted = true;
	static loadStrategy = "partial";
	ModelClass = Message;
	arenaThreads = /* @__PURE__ */ new Map();
	messageIdsByThread = observable.map();
	syncFingerprints = /* @__PURE__ */ new Map();
	syncRowsProcessed = 0;
	syncRowsSkipped = 0;
	updateContentControllers = /* @__PURE__ */ new Map();
	activeResponseStreams = /* @__PURE__ */ new Map();
	constructor(sdk) {
		super(sdk);
		observe(this.data, (change) => {
			if (change.type === "add") this.addToThreadIndex(change.name, change.newValue.threadId);
			else if (change.type === "delete") this.removeFromThreadIndex(change.name, change.oldValue.threadId);
		});
		this.initializeMetadata("chats");
		makeObservable(this, {
			onMessage: action,
			streamAgentResponse: action,
			hydrate: action,
			sendMessage: action,
			editMessage: action,
			getStreamingMessageForThread: false,
			persistAndHydrateMessages: action,
			persistMessagesFromSync: action,
			hydrateMessagesIfLoaded: action,
			deleteByThreadId: action,
			updateMessageContent: action,
			loadOlderMessages: action,
			setThreadsSyncingHistory: action
		});
	}
	addToThreadIndex(messageId, threadId) {
		if (!threadId) return;
		let set = this.messageIdsByThread.get(threadId);
		if (!set) {
			set = observable.set();
			this.messageIdsByThread.set(threadId, set);
		}
		set.add(messageId);
	}
	removeFromThreadIndex(messageId, threadId) {
		if (!threadId) return;
		const set = this.messageIdsByThread.get(threadId);
		if (set) {
			set.delete(messageId);
			if (set.size === 0) this.messageIdsByThread.delete(threadId);
		}
	}
	remove(item) {
		this.removeFromThreadIndex(item.id, item.threadId);
		this.syncFingerprints.delete(item.id);
		super.remove(item);
	}
	clear() {
		this.loadedThreadIds.clear();
		this.threadPageState.clear();
		this.threadsWithOlderPageLoads.clear();
		this.messageIdsByThread.clear();
		this.syncFingerprints.clear();
		this.threadsSyncingHistory.clear();
		super.clear();
	}
	/**
	* Start the assistant request for an existing chat.
	*/
	async streamAgentResponse(payload) {
		const missing = [
			!payload.threadId && "threadId",
			!payload.content && !payload.fileIds?.length && !payload.askUserAnswers && "content",
			!payload.userMessageId && "userMessageId"
		].filter(Boolean);
		if (missing.length > 0) throw new Error(`Missing required fields: ${missing.join(", ")}`);
		const controller = new AbortController();
		const forwardCallerAbort = () => controller.abort();
		if (payload.signal?.aborted) controller.abort();
		payload.signal?.addEventListener("abort", forwardCallerAbort, { once: true });
		let markSettled = () => void 0;
		const activeStream = {
			abort: () => controller.abort(),
			settled: new Promise((resolve) => {
				markSettled = resolve;
			})
		};
		const streams = this.activeResponseStreams.get(payload.threadId) ?? /* @__PURE__ */ new Set();
		streams.add(activeStream);
		this.activeResponseStreams.set(payload.threadId, streams);
		let undoFollowUpRequested;
		try {
			undoFollowUpRequested = payload.followUpActionCardIds?.length ? this.sdk.stores.actionCards.markFollowUpRequested(payload.followUpActionCardIds, payload.userMessageId) : void 0;
			const { endpoint, baseUrl, sendsFullForm } = chatSendRoute(this.sdk.options, payload.threadId);
			const request = {
				endpoint,
				baseUrl,
				body: sendsFullForm ? await this.buildChatV4FormData(payload) : chatV5FormData(payload)
			};
			if (payload.arenaData) this.arenaThreads.set(payload.threadId, payload.arenaData);
			await this.sdk.transport.sendChat({
				...request,
				signal: controller.signal
			});
		} catch (err) {
			undoFollowUpRequested?.();
			const busyRefusal = busyRefusalOf(err);
			if (busyRefusal) {
				this.handleBusyRefusal(payload.threadId, payload.userMessageId, busyRefusal.conflictOwnerMessageId);
				return;
			}
			this.arenaThreads.delete(payload.threadId);
			throw err;
		} finally {
			payload.signal?.removeEventListener("abort", forwardCallerAbort);
			streams.delete(activeStream);
			if (streams.size === 0) this.activeResponseStreams.delete(payload.threadId);
			markSettled();
		}
	}
	abortActiveStreamsForThread = async (threadId) => {
		const streams = [...this.activeResponseStreams.get(threadId) ?? []];
		for (const stream of streams) stream.abort();
		await Promise.all(streams.map(({ settled }) => settled));
	};
	async buildChatV4FormData(payload) {
		const formData = new FormData();
		formData.append("chat_id", payload.threadId);
		formData.append("question", payload.content);
		if (payload.mode) formData.append("mode", payload.mode);
		if (payload.agent) formData.append("agent", payload.agent);
		formData.append("user_message_id", payload.userMessageId);
		formData.append("busy_refusal_in_response", "true");
		if (payload.replacesMessageId) formData.append("replaces_message_id", payload.replacesMessageId);
		payload.fileIds?.forEach((fileId) => formData.append("file_ids", fileId));
		if (payload.context) formData.append("context", payload.context);
		formData.append("use_todos", payload.useTodos.toString());
		payload.collaboratorIds?.forEach((id) => formData.append("collaborator_ids", String(id)));
		payload.followUpActionCardIds?.forEach((cardId) => formData.append("follow_up_action_card_ids", cardId));
		payload.personIds?.forEach((personId) => formData.append("person_ids", personId));
		payload.groupNames?.forEach((groupName) => formData.append("group_names", groupName));
		if (payload.askUserAnswers) formData.append("ask_user_answers", JSON.stringify(payload.askUserAnswers));
		if (payload.responseAudioVoiceId) formData.append("response_audio_voice_id", payload.responseAudioVoiceId);
		const deviceId = await this.sdk.options.getDeviceId?.();
		if (deviceId) formData.append("device_id", deviceId);
		const screenContextSearchDisabled = this.sdk.options.getScreenContextSearchDisabled?.(payload.threadId);
		if (screenContextSearchDisabled !== void 0) formData.append("disable_screen_context_search", String(screenContextSearchDisabled));
		return formData;
	}
	unregisterArenaThread(threadId) {
		this.arenaThreads.delete(threadId);
	}
	/**
	* Handles incoming streamed message events for a thread.
	* Ensures only one streaming message per thread at any time.
	*/
	onMessage(event) {
		if (!event.arenaData) {
			const arenaData = this.arenaThreads.get(event.threadId);
			if (arenaData) event = {
				...event,
				arenaData
			};
		}
		if (event.response.interrupted) {
			this.handleInterruptedEvent(event);
			return;
		}
		switch (event.response.event_type) {
			case "user_message":
				this.handleUserMessageEvent(event);
				break;
			case "token":
				this.handleTokenEvent(event);
				break;
			case "finish_one_assistant_message":
				this.handleFinishAssistantMessageEvent(event);
				break;
			case "thought":
				this.handleThoughtEvent(event);
				break;
			case "hidden_text":
				this.handleHiddenTextEvent(event);
				break;
			case "new_assistant_message":
				this.handleNewAssistantMessageEvent(event);
				break;
			case "progress_message":
				this.handleProgressEvent(event);
				break;
			case "done":
				this.handleDoneEvent(event);
				break;
			case "error":
				this.handleErrorEvent(event);
				break;
			case "tool_result":
				this.handleToolResultEvent(event);
				break;
			case "modality_apps_used":
				this.handleModalityAppsUsedEvent(event);
				break;
			case "modality_review_files":
				this.handleModalityReviewFilesEvent(event);
				break;
			case "notification":
				this.handleNotificationEvent(event);
				break;
			case "heartbeat":
				this.resumeThreadFromWaitingForClientTools(event.threadId);
				break;
			case "subagent_progress":
				this.handleSubagentProgressEvent(event);
				break;
			case "client_tool_call_request":
				this.handleClientToolCallRequest(event);
				break;
			case "client_tool_content_chunk":
				this.handleClientToolContentChunk(event);
				break;
			case "waiting_for_client_tools":
				this.handleWaitingForClientToolsEvent(event);
				break;
			case "waiting_for_device_tools":
				this.handleWaitingForDeviceToolsEvent(event);
				break;
			case "ask_user_questions":
				this.handleAskUserQuestionsEvent(event);
				break;
			case "audio_chunk": this.handleResponseAudioEvent(event);
		}
	}
	/** The receipt syncs, so a card finished on another device is recognised here */
	hasConnectionCardCompletion(threadId, cardKey) {
		return Boolean(this.get(connectionCardCompletionMessageId(threadId, cardKey)));
	}
	getStreamingMessageForThread(threadId) {
		const ids = this.messageIdsByThread.get(threadId);
		if (!ids) return void 0;
		for (const id of ids) {
			const msg = this.data.get(id);
			if (msg && msg.status === "streaming") return msg;
		}
	}
	hasActiveResponseStreams() {
		return this.activeResponseStreams.size > 0;
	}
	async updateMessageContent(threadId, messageId, content) {
		const message = this.get(messageId);
		if (!message) return;
		this.updateContentControllers.get(messageId)?.abort();
		const controller = new AbortController();
		this.updateContentControllers.set(messageId, controller);
		const originalContent = message.content;
		runInAction(() => {
			message.content = content;
		});
		try {
			await this.sdk.apiClient.fetch(`chat/${threadId}/messages/${messageId}`, {
				method: "PATCH",
				body: JSON.stringify({ content }),
				signal: controller.signal
			});
			if (MessageStore.persisted) this.sdk.storage.set(this.tableName, message);
		} catch (error) {
			if (controller.signal.aborted) return;
			runInAction(() => {
				if (message.content === content) message.content = originalContent;
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Failed to update message content." },
				context: "MessageStore:updateMessageContent"
			});
			throw error;
		} finally {
			if (this.updateContentControllers.get(messageId) === controller) this.updateContentControllers.delete(messageId);
		}
	}
	mergeToolCalls(local, server) {
		if (local.length === 0) return server;
		const localTerminalByRequestId = /* @__PURE__ */ new Map();
		for (const tc of local) if (tc.request_id && tc.status !== "pending") localTerminalByRequestId.set(tc.request_id, tc);
		const merged = server.map((tc) => {
			if (!tc.request_id || !localTerminalByRequestId.has(tc.request_id)) return tc;
			const localTc = localTerminalByRequestId.get(tc.request_id);
			if (tc.status === "pending") return localTc;
			if (tc.status === "completed" && (localTc.status === "denied" || localTc.status === "error")) return localTc;
			return tc;
		});
		const serverRequestIds = new Set(server.map((tc) => tc.request_id).filter((id) => id != null));
		const serverToolIds = new Set(server.map((tc) => tc.tool_id).filter((id) => id != null));
		const localPending = local.filter((tc) => tc.status === "pending" && tc.request_id && !serverRequestIds.has(tc.request_id) && !(tc.tool_id && serverToolIds.has(tc.tool_id)));
		if (localPending.length > 0) merged.push(...localPending);
		return merged;
	}
	groupWebSearchModalities(modalities) {
		if (!modalities) return [];
		const modalitiesFinal = [];
		const webSearchModalityCombined = {
			type: "chat_with_web_search_multi",
			search_queries: []
		};
		for (const modality of modalities) if (modality.type === "chat_with_web_search") {
			if (webSearchModalityCombined.search_queries.length === 0) modalitiesFinal.push(webSearchModalityCombined);
			webSearchModalityCombined.search_queries.push(modality.search_query);
		} else modalitiesFinal.push(modality);
		return modalitiesFinal;
	}
	appendHiddenContentToMessage(message, content) {
		if (!content || !message) return;
		runInAction(() => {
			message.hiddenContent = (message.hiddenContent || "") + content;
			message.updatedAt = /* @__PURE__ */ new Date();
		});
	}
	hydrate = async () => {
		runInAction(() => {
			this.isLoaded = true;
		});
	};
	/**
	* Re-load messages for the most recently active threads of the previous
	* session, based on persisted partial-index coverage. Called after
	* partialIndexCoverage has been hydrated from IDB. Goes through
	* `loadThreadsMessages` so a thread opened mid-restore is read only once.
	*/
	async restorePersistedSlices() {
		const candidates = this.sdk.partialIndexCoverage.getSlicesForTable("messages").filter((slice) => slice.indexName === "threadId" && !this.loadedThreadIds.has(slice.value)).map((slice) => slice.value);
		if (candidates.length === 0) return;
		const threadIds = orderBy(candidates, (threadId) => {
			const thread = this.sdk.stores.threads.data.get(threadId);
			const ts = thread?.updatedAt ?? thread?.createdAt;
			return ts ? ts.getTime() : 0;
		}, ["desc"]).slice(0, 20);
		await this.loadThreadsMessages(threadIds, { recordCoverage: false });
	}
	async loadInitialPage() {
		runInAction(() => {
			this.isLoaded = true;
		});
	}
	async loadOlderMessages(threadId, cursor, options) {
		const messageQueries = this.requireMessageQueries();
		const pageState = this.threadPageState.get(threadId);
		const effectiveCursor = cursor ?? pageState?.cursor;
		if (!effectiveCursor) return {
			records: [],
			hasMore: false
		};
		this.threadsWithOlderPageLoads.add(threadId);
		const page = await messageQueries.listOlderForThread(threadId, effectiveCursor, options);
		runInAction(() => {
			for (const record of page.records) this.add({
				...record,
				deletedAt: record.deletedAt ? record.deletedAt : void 0
			});
		});
		this.threadPageState.set(threadId, {
			cursor: page.nextCursor,
			hasMore: page.hasMore
		});
		return {
			records: page.records,
			nextCursor: page.nextCursor,
			hasMore: page.hasMore
		};
	}
	loadedThreadIds = /* @__PURE__ */ new Set();
	threadPageState = /* @__PURE__ */ new Map();
	/** Threads a sync walk has served only in part, by walk; their older messages are still on the way */
	threadsSyncingHistory = observable.map(void 0, { deep: false });
	setThreadsSyncingHistory(walk, threadIds) {
		if (threadIds.length === 0) {
			this.threadsSyncingHistory.delete(walk);
			return;
		}
		const current = new Set(this.threadsSyncingHistory.get(walk));
		const next = new Set(threadIds);
		if (!(current.size === next.size && [...next].every((id) => current.has(id)))) this.threadsSyncingHistory.set(walk, threadIds);
	}
	isThreadHistorySyncing(threadId) {
		for (const threadIds of this.threadsSyncingHistory.values()) if (threadIds.includes(threadId)) return true;
		return false;
	}
	/** User has called `loadOlderMessages` at least once for this thread. */
	threadsWithOlderPageLoads = /* @__PURE__ */ new Set();
	threadLoads = /* @__PURE__ */ new Map();
	async loadThreadMessages(threadId) {
		await this.loadThreadsMessages([threadId]);
	}
	/** `recordCoverage: false` for threads already read out of the ledger. */
	async loadThreadsMessages(threadIds, options) {
		const recordCoverage = options?.recordCoverage ?? true;
		const fresh = threadIds.filter((id) => !this.loadedThreadIds.has(id) && !this.threadLoads.has(id));
		if (fresh.length > 0) {
			const load = this.refreshThreadsFromStorage(fresh).then(async () => {
				for (const threadId of fresh) this.loadedThreadIds.add(threadId);
				if (!recordCoverage) return;
				await Promise.all(fresh.map((threadId) => this.sdk.partialIndexCoverage.record("messages", "threadId", threadId)));
			}).finally(() => {
				for (const threadId of fresh) this.threadLoads.delete(threadId);
			});
			for (const threadId of fresh) this.threadLoads.set(threadId, load);
		}
		await Promise.all(threadIds.flatMap((id) => {
			const load = this.threadLoads.get(id);
			return load ? [load] : [];
		}));
	}
	async refreshLoadedThreadsFromStorage() {
		await this.refreshThreadsFromStorage(Array.from(this.loadedThreadIds));
	}
	isThreadLoaded(threadId) {
		return this.loadedThreadIds.has(threadId);
	}
	shouldHydrateMessage(data) {
		if (this.data.has(data.id)) return true;
		const threadId = data.threadId;
		return Boolean(threadId && (this.loadedThreadIds.has(threadId) || this.messageIdsByThread.has(threadId)));
	}
	async runRefreshFromStorage() {
		await this.refreshLoadedThreadsFromStorage();
	}
	/** One windowed query for every thread, then one existence check for rows that fell out of the window */
	async refreshThreadsFromStorage(threadIds) {
		if (threadIds.length === 0) return;
		const messageQueries = this.requireMessageQueries();
		const pages = await messageQueries.listLatestForThreads(threadIds);
		const olderIds = [];
		for (const threadId of threadIds) {
			const currentIds = this.messageIdsByThread.get(threadId);
			if (!currentIds?.size || this.isThreadBusy(threadId)) continue;
			const freshIds = new Set((pages.get(threadId)?.records ?? []).map((r) => r.id));
			for (const id of currentIds) if (!freshIds.has(id)) olderIds.push(id);
		}
		if (olderIds.length > 0) await this.evictMissingMessages(olderIds, messageQueries);
		const modified = [];
		for (const threadId of threadIds) {
			const page = pages.get(threadId) ?? {
				records: [],
				hasMore: false
			};
			const addedMessages = runInAction(() => this.mergeThreadRecords(page.records));
			if (addedMessages.length > 0) modified.push(...this.reconcileToolCallOwnership(addedMessages));
			this.updateThreadPageState(threadId, page);
		}
		if (modified.length > 0) await this.sdk.storage.bulkSet(this.tableName, modified);
	}
	updateThreadPageState(threadId, page) {
		const prev = this.threadPageState.get(threadId);
		const freshCursorValue = page.nextCursor?.value ?? null;
		const prevCursorValue = prev?.cursor?.value ?? null;
		const userHasScrolledOlder = this.threadsWithOlderPageLoads.has(threadId) && prev?.cursor !== void 0 && freshCursorValue !== null && prevCursorValue !== null && String(prevCursorValue) < String(freshCursorValue);
		this.threadPageState.set(threadId, {
			cursor: userHasScrolledOlder ? prev.cursor : page.nextCursor,
			hasMore: userHasScrolledOlder ? prev?.hasMore ?? page.hasMore : page.hasMore
		});
	}
	isThreadBusy(threadId) {
		const thread = this.sdk.stores.threads.data.get(threadId);
		return thread?.status === "thinking" || thread?.status === "streaming";
	}
	isMessageActive(id) {
		const msg = this.data.get(id);
		return msg?.status === "streaming" || msg?.status === "thinking";
	}
	async evictMissingMessages(olderIds, messageQueries) {
		const existingRecords = await messageQueries.getByIds(olderIds);
		const stillExistById = new Map(existingRecords.map((r) => [r.id, r]));
		runInAction(() => {
			if (existingRecords.length > 0) this.mergeThreadRecords(existingRecords);
			for (const id of olderIds) {
				if (stillExistById.has(id)) continue;
				if (this.isMessageActive(id)) continue;
				this.data.delete(id);
			}
		});
	}
	mergeThreadRecords(records) {
		const added = [];
		for (const record of records) {
			const existing = this.data.get(record.id);
			if (existing?.status === "streaming" || existing?.status === "thinking") continue;
			added.push(this.add({
				...record,
				deletedAt: record.deletedAt ? record.deletedAt : void 0
			}));
		}
		return added;
	}
	async deleteByThreadId(threadId) {
		const loadedMessages = this.getLoadedThreadMessages(threadId);
		await this.requireMessageQueries().deleteManyByThread(threadId);
		runInAction(() => {
			loadedMessages.forEach((message) => this.data.delete(message.id));
			this.loadedThreadIds.delete(threadId);
			this.threadPageState.delete(threadId);
			this.threadsWithOlderPageLoads.delete(threadId);
			this.arenaThreads.delete(threadId);
		});
	}
	requireMessageQueries() {
		const messageQueries = this.sdk.storage.entities.messages;
		if (!messageQueries) throw new MissingEntityQueriesError("messages");
		return messageQueries;
	}
	hasOlderMessages(threadId) {
		return this.threadPageState.get(threadId)?.hasMore ?? false;
	}
	/**
	* Finds the local messages of a thread that the server deleted, from a
	* range of its messages. A local message that the range lacks is gone when
	* it lies after the anchor, and, for an older slice, before `before`.
	* Nothing is deleted when the device lacks the anchor, because the range
	* then does not show what lies before it. Nothing is deleted while a reply
	* runs either; that rule and its cost are in
	* ai-docs/architecture/frontend/sync.md.
	*/
	async findChatTailGhosts(tail) {
		const { threadId, after, before, keptIds, rowUnfinished } = tail;
		const anchorHeld = after === null || this.data.has(after.id) || (await this.requireMessageQueries().existingIds([after.id])).length > 0;
		if (!anchorHeld) return {
			ghostIds: [],
			anchorHeld
		};
		if (rowUnfinished || isChatStatusBusy(this.sdk.stores.threads.data.get(threadId)?.status)) return {
			ghostIds: [],
			anchorHeld
		};
		const lowerMs = after ? timeMs(after.createdAt) : void 0;
		const upperMs = before ? timeMs(before.createdAt) : void 0;
		return {
			ghostIds: (await this.scanThreadMessages(threadId, lowerMs)).filter((row) => (lowerMs === void 0 || row.ms > lowerMs) && (upperMs === void 0 || row.ms < upperMs) && !keptIds.has(row.id) && !isChatStatusBusy(row.status) && !row.refusedAsBusy).map((row) => row.id),
			anchorHeld
		};
	}
	/** Whether the device holds a message of the thread older than `position` */
	async holdsMessageBefore(threadId, position) {
		const positionMs = timeMs(position.createdAt);
		if (positionMs === void 0) return false;
		return (await this.scanThreadMessages(threadId, positionMs - 1)).some((row) => row.ms < positionMs);
	}
	/** Newest first, down to the first message at or before `floorMs`; memory and storage together */
	async scanThreadMessages(threadId, floorMs) {
		const rows = /* @__PURE__ */ new Map();
		for (const id of this.messageIdsByThread.get(threadId) ?? []) {
			const message = this.data.get(id);
			if (message) rows.set(id, {
				id,
				ms: timeMs(message.createdAt) ?? 0,
				status: message.status,
				refusedAsBusy: message.refusedAsBusy === true
			});
		}
		const queries = this.requireMessageQueries();
		let page = await queries.listLatestForThread(threadId, { limit: TAIL_SCAN_PAGE_SIZE });
		for (;;) {
			for (const record of page.records) if (!rows.has(record.id)) rows.set(record.id, {
				id: record.id,
				ms: timeMs(record.createdAt) ?? 0,
				status: record.status,
				refusedAsBusy: record.refusedAsBusy === true
			});
			const oldest = page.records.at(-1);
			const reachedFloor = floorMs !== void 0 && oldest !== void 0 && (timeMs(oldest.createdAt) ?? 0) <= floorMs;
			if (!page.hasMore || !page.nextCursor || reachedFloor) break;
			page = await queries.listOlderForThread(threadId, page.nextCursor, { limit: TAIL_SCAN_PAGE_SIZE });
		}
		return [...rows.values()];
	}
	getMessagesByThreadIdComputed = computedFn(function(threadId, inverted) {
		return orderBy(withoutReplacedMessages(this.getLoadedThreadMessages(threadId)), "createdAt", inverted ? "desc" : "asc");
	});
	getLoadedThreadMessages(threadId) {
		const messages = [];
		for (const id of this.messageIdsByThread.get(threadId) ?? []) {
			const msg = this.data.get(id);
			if (msg) messages.push(msg);
		}
		return messages;
	}
	/**
	* Lists messages for a thread. `computedFn` requires a fixed arity, so the
	* public method normalizes defaults and always forwards two args internally.
	*/
	getMessagesByThreadId(threadId, inverted = false) {
		return this.getMessagesByThreadIdComputed(threadId, inverted);
	}
	/**
	* The questions a thread is currently waiting on, if any.
	*
	* Only the most recent ask is answerable, and only until the user sends
	* anything — after that the conversation has moved on and the backend would
	* refuse the answers anyway.
	*/
	getLiveAskUserRequest = computedFn(function(threadId) {
		const messages = this.getMessagesByThreadId(threadId, true);
		for (const message of messages) {
			if (message.role === "user" && !message.refusedAsBusy) return null;
			if (message.askUserQuestions) {
				const request = message.askUserQuestions;
				const card = this.sdk.stores.actionCards.getByChatId(threadId).find((candidate) => !candidate.deletedAt && candidate.actionType === "ask_user" && candidate.content.request_id === request.request_id);
				return card && card.status !== "draft" ? null : request;
			}
		}
		return null;
	});
	prepareFilesFromDraft(draft) {
		return draft.files.filter((file) => file.uploadState === "uploaded").map((file) => this.sdk.stores.files.addFile({
			id: file.id,
			filename: file.fileName,
			contentType: file.contentType,
			size: file.size,
			width: file.width,
			height: file.height,
			createdAt: /* @__PURE__ */ new Date(),
			updatedAt: /* @__PURE__ */ new Date(),
			deletedAt: void 0
		}));
	}
	/**
	* Create and persist one local user message, then start its assistant
	* request. Rejects only while validating input, before the local message is
	* added; every later failure is reported to Voice Mode and UI listeners by
	* `handleMessageSendError` instead of rethrown. The result is false when
	* the response could not be sent, so callers that sequence sends can hold
	* their queue.
	*/
	sendMessage = async (payload) => {
		const { content, draft } = resolveOutgoingSendInput(payload.content, () => this.sdk.stores.drafts.getDraft(payload.draftId ?? payload.threadId), Boolean(payload.askUserAnswers));
		const files = draft ? this.prepareFilesFromDraft(draft) : [];
		const now = /* @__PURE__ */ new Date();
		const message = this.add({
			id: payload.userMessageId ?? v4(),
			threadId: payload.threadId,
			content,
			status: "done",
			role: "user",
			fileIds: files.map((file) => file.id),
			askUserAnswers: payload.askUserAnswers ?? null,
			mode: payload.mode,
			createdAt: now,
			updatedAt: now
		});
		for (const edit of this.getPendingEdits(payload.threadId)) await this.rollBackPendingEdit(edit);
		return this.startUserMessageTurn(message, {
			files,
			mode: payload.mode,
			agent: payload.agent,
			useTodos: payload.useTodos,
			context: payload.context,
			followUpActionCardIds: payload.followUpActionCardIds,
			personIds: payload.personIds,
			groupNames: payload.groupNames,
			responseAudioVoiceId: payload.responseAudioVoiceId,
			onPersisted: () => {
				if (draft) this.sdk.stores.drafts.removeDraft(draft);
			}
		});
	};
	editMessage = async (payload) => {
		const replacedMessage = this.get(payload.messageId);
		if (!replacedMessage) throw new Error("This message is no longer available.");
		if (isChatStatusBusy(this.sdk.stores.threads.getById(payload.threadId)?.status)) throw new Error("Wait for the reply to finish before editing.");
		const content = composeDraftMessageContent(payload.body, replacedMessage.quotes.map((text) => ({ text })));
		if (isMetaSlashCommand(content)) throw new Error("Commands can't be sent as an edit. Send the command as a new message instead.");
		const replacedCreatedAt = replacedMessage.createdAt.getTime();
		const replacedMessageIds = this.getMessagesByThreadId(payload.threadId).filter((message) => message.createdAt.getTime() >= replacedCreatedAt).map((message) => message.id);
		const previousVersionIds = replacedMessage.versionIds.length > 0 ? replacedMessage.versionIds : [replacedMessage.id];
		const messageId = payload.userMessageId ?? v4();
		const now = /* @__PURE__ */ new Date();
		const message = this.add({
			id: messageId,
			threadId: payload.threadId,
			content,
			status: "done",
			role: "user",
			fileIds: replacedMessage.fileIds,
			collaboratorIds: replacedMessage.collaboratorIds,
			versionIds: [...previousVersionIds, messageId],
			replacesMessageId: replacedMessage.id,
			replacedMessageIds,
			mode: payload.mode,
			createdAt: now,
			updatedAt: now
		});
		await this.startUserMessageTurn(message, {
			files: [],
			mode: payload.mode,
			useTodos: payload.useTodos
		});
	};
	async startUserMessageTurn(message, options) {
		const isSlashCommand = isMetaSlashCommand(message.content);
		if (!isSlashCommand) {
			this.sdk.stores.threads.updateThreadStatus(message.threadId, "thinking");
			this.sdk.stores.threads.updateThreadLastMessageId(message.threadId, message.id);
		}
		try {
			await this.persistMessageAndFiles(message, options.files, isSlashCommand);
			options.onPersisted?.();
			await this.streamAgentResponse({
				threadId: message.threadId,
				content: message.content,
				mode: options.mode,
				agent: options.agent,
				fileIds: message.fileIds,
				userMessageId: message.id,
				replacesMessageId: message.replacesMessageId ?? void 0,
				isMetaSlashCommand: isSlashCommand,
				useTodos: options.useTodos,
				context: options.context,
				collaboratorIds: message.collaboratorIds,
				askUserAnswers: message.askUserAnswers ?? void 0,
				followUpActionCardIds: options.followUpActionCardIds,
				personIds: options.personIds,
				groupNames: options.groupNames,
				responseAudioVoiceId: options.responseAudioVoiceId
			});
		} catch (error) {
			if (error instanceof StreamCancellationError) return "cancelled";
			await this.handleMessageSendError(message, error, isSlashCommand);
			return "failed";
		}
		return "sent";
	}
	async activateMessageVersion(threadId, messageId) {
		await this.sdk.apiClient.fetch(`chat/${threadId}/messages/${messageId}/activate`, { method: "POST" });
		try {
			for (const edit of this.getPendingEdits(threadId)) await this.rollBackPendingEdit(edit);
			await this.sdk.syncNow();
			if (!this.getMessagesByThreadId(threadId).some((message) => message.id === messageId)) await this.sdk.syncNow();
		} catch (error) {
			this.sdk.logger.error(error, { context: "MessageStore:activateMessageVersion" });
		}
		this.restoreThreadLastMessageId(threadId);
	}
	restoreThreadLastMessageId(threadId) {
		const lastUserMessage = this.getMessagesByThreadId(threadId, true).find((message) => message.role === "user");
		if (lastUserMessage) this.sdk.stores.threads.updateThreadLastMessageId(threadId, lastUserMessage.id);
	}
	async acceptPendingEdit(edit) {
		runInAction(() => {
			edit.replacesMessageId = null;
		});
		await this.sdk.storage.set(this.tableName, edit);
	}
	getPendingEdits(threadId) {
		return this.getLoadedThreadMessages(threadId).filter((message) => message.replacesMessageId);
	}
	async rollBackPendingEdit(edit) {
		await this.removeLocalMessages([edit.id]);
		this.restoreThreadLastMessageId(edit.threadId);
		await this.sdk.stores.threads.updateThreadStatus(edit.threadId, "done");
	}
	/**
	* Retry the thread's last user message after its response failed. If that
	* message no longer exists locally, return the thread to an idle state so
	* the user can send a new message instead.
	*
	* A retry never requests spoken response audio.
	*/
	retryMessage = async (payload) => {
		const message = this.get(payload.messageId);
		if (!message) {
			this.sdk.stores.threads.updateThreadStatus(payload.threadId, "done");
			throw new Error("Something went wrong. Please send a new message");
		}
		const isSlashCommand = isMetaSlashCommand(message.content);
		if (!isSlashCommand) this.sdk.stores.threads.updateThreadStatus(message.threadId, "thinking");
		globalEventBus.emit(EventNames.CHAT_NEW_ASSISTANT_THOUGHT, { data: { threadId: message.threadId } });
		try {
			await this.streamAgentResponse({
				threadId: message.threadId,
				content: message.content,
				mode: payload.mode,
				fileIds: message.fileIds,
				userMessageId: message.id,
				replacesMessageId: message.replacesMessageId ?? void 0,
				isMetaSlashCommand: isSlashCommand,
				useTodos: payload.useTodos,
				collaboratorIds: message.collaboratorIds,
				askUserAnswers: message.askUserAnswers ?? void 0
			});
		} catch (error) {
			if (error instanceof StreamCancellationError) return;
			await this.handleMessageSendError(message, error, isSlashCommand);
		}
	};
	async handleMessageSendError(message, error, isSlashCommand) {
		this.sdk.voiceMode.notifyPendingReplyFailure(message.threadId, message.id);
		globalEventBus.emit(EventNames.CHAT_SEND_FAILED, { data: {
			threadId: message.threadId,
			userMessageId: message.id
		} });
		const editRefusal = message.replacesMessageId ? editRefusalMessage(error) : null;
		if (editRefusal !== null) {
			await this.rollBackPendingEdit(message);
			throw new Error(editRefusal);
		}
		if (isSlashCommand) return;
		const errorMessage = error instanceof Error ? error.message : "Unknown error";
		const { status, endedByFreeLimit } = chatStatusForSendError(error);
		try {
			await this.sdk.stores.threads.updateThreadStatus(message.threadId, status, endedByFreeLimit ? void 0 : errorMessage);
		} catch (statusError) {
			this.sdk.logger.error("Failed to persist chat send error status", {
				context: "MessageStore:handleMessageSendError",
				metadata: {
					error: statusError,
					originalError: error,
					threadId: message.threadId,
					userMessageId: message.id
				}
			});
		}
		if (shouldTrackMessageError(error)) globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_ERROR_EVENT, { data: {
			chat_id: message.threadId,
			...messageErrorAnalyticsFields(error, errorMessage)
		} });
	}
	resolveIncomingTimestamps(dbMessage, fallbackNow, localMessage) {
		const incomingUpdatedAt = dbMessage.updated_at ? parseDate(dbMessage.updated_at) : localMessage?.updatedAt ?? fallbackNow;
		return {
			incomingCreatedAt: dbMessage.created_at ? parseDate(dbMessage.created_at) : localMessage?.createdAt ?? incomingUpdatedAt,
			incomingUpdatedAt
		};
	}
	shouldSkipIncomingMessage(localMessage, threadId) {
		if (!localMessage || localMessage.status !== "streaming" && localMessage.status !== "thinking") return false;
		return !this.sdk.stores.threads.isThreadFinished(threadId);
	}
	isLocalMessageUnsettled(localMessage) {
		if (!localMessage) return false;
		return localMessage.status !== "done" || localMessage.toolCalls.some((toolCall) => toolCall.status === "pending");
	}
	async persistAndHydrateMessages(dbMessages) {
		return this.persistMessagesFromSync(dbMessages);
	}
	/**
	* Persist server messages to IndexedDB. Always runs regardless of which
	* threads are loaded in memory.
	*/
	async persistMessagesFromSync(dbMessages) {
		try {
			const toPersist = [];
			const toDelete = [];
			const pendingFingerprints = /* @__PURE__ */ new Map();
			const batchFallbackNow = /* @__PURE__ */ new Date();
			const fingerprintSkipEnabled = this.sdk.options.isSyncFingerprintSkipEnabled?.() ?? false;
			let skippedRowCount = 0;
			for (const dbMessage of dbMessages) {
				const { id } = dbMessage;
				if (!id) continue;
				if (dbMessage.deleted_at) {
					toDelete.push(id);
					continue;
				}
				const localMessage = this.data.get(id);
				if (this.shouldSkipIncomingMessage(localMessage, dbMessage.thread_id)) continue;
				const fingerprint = fingerprintSkipEnabled && !this.isLocalMessageUnsettled(localMessage) ? messageSyncFingerprint(dbMessage) : null;
				if (fingerprint !== null && this.syncFingerprints.get(id) === fingerprint) {
					skippedRowCount++;
					continue;
				}
				if (fingerprint !== null) pendingFingerprints.set(id, fingerprint);
				toPersist.push(this.mapApiMessageToLocal({
					...dbMessage,
					id
				}, batchFallbackNow, localMessage));
			}
			const persistedMessages = await this.filterOrphanThreadMessages(toPersist);
			if (toDelete.length > 0) await this.removeLocalMessages(toDelete);
			if (persistedMessages.length > 0) {
				const toHydrate = persistedMessages.filter((data) => this.shouldHydrateMessage(data));
				const toPersistOnly = persistedMessages.filter((data) => !this.shouldHydrateMessage(data));
				const addedMessages = runInAction(() => toHydrate.map((data) => this.add(data)));
				const modifiedMessages = this.reconcileToolCallOwnership(addedMessages);
				if (addedMessages.length > 0) await this.sdk.storage.bulkSet(this.tableName, addedMessages);
				if (modifiedMessages.length > 0) await this.sdk.storage.bulkSet(this.tableName, modifiedMessages);
				if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
				this.emitPendingToolCallsFromBootstrap(toHydrate);
			}
			for (const record of persistedMessages) {
				const fingerprint = pendingFingerprints.get(record.id);
				if (fingerprint !== void 0) this.syncFingerprints.set(record.id, fingerprint);
			}
			this.syncRowsProcessed += persistedMessages.length;
			this.syncRowsSkipped += skippedRowCount;
			if (skippedRowCount > 0) this.sdk.logger.debug("Skipped unchanged sync messages", {
				context: "MessageStore:persistMessagesFromSync",
				metadata: {
					skippedRowCount,
					processedRowCount: persistedMessages.length
				}
			});
			return {
				deletedSearchDocIds: toDelete,
				upsertedRecords: persistedMessages
			};
		} catch (error) {
			this.sdk.logger.error("Error processing messages", {
				metadata: { error },
				context: "MessageStore:persistMessagesFromSync"
			});
			throw error;
		}
	}
	async filterOrphanThreadMessages(messages) {
		if (messages.length === 0) return messages;
		const threadQueries = this.sdk.storage.entities.threads;
		if (!threadQueries) return messages;
		const threadIds = [...new Set(messages.map((msg) => msg.threadId).filter((id) => Boolean(id)))];
		if (threadIds.length === 0) return messages;
		const existingThreadIds = new Set(await threadQueries.existingIds(threadIds));
		if (existingThreadIds.size === threadIds.length) return messages;
		const filtered = messages.filter((msg) => !msg.threadId || existingThreadIds.has(msg.threadId));
		this.sdk.logger.warn("Skipping sync messages for threads missing from local DB", {
			context: "MessageStore:persistMessagesFromSync",
			metadata: {
				threadIds: threadIds.filter((id) => !existingThreadIds.has(id)),
				skippedCount: messages.length - filtered.length
			}
		});
		return filtered;
	}
	/**
	* Hydrate persisted rows into memory only for threads that are either
	* loaded (via loadThreadMessages) or already have messages in memory
	* (e.g. from active streaming). Runs reconciliation for the hydrated subset.
	*/
	hydrateMessagesIfLoaded(messages) {
		if (messages.length === 0) return;
		const toHydrate = [];
		for (const msg of messages) {
			if (!msg.threadId) continue;
			const isLoaded = this.loadedThreadIds.has(msg.threadId);
			const hasInMemoryMessages = this.messageIdsByThread.has(msg.threadId);
			if (isLoaded || hasInMemoryMessages) toHydrate.push(msg);
		}
		if (toHydrate.length === 0) return;
		const addedMessages = runInAction(() => toHydrate.map((messageData) => this.add(messageData)));
		const modifiedMessages = this.reconcileToolCallOwnership(addedMessages);
		if (modifiedMessages.length > 0) this.sdk.storage.bulkSet(this.tableName, modifiedMessages).catch(() => {});
	}
	/**
	* After bootstrap adds server-authoritative messages, remove any tool calls
	* from OTHER messages in the same thread that share a request_id with the
	* server data. This handles the race where the stream pushed a tool call
	* onto a local message before bootstrap established the authoritative owner.
	*/
	/** Each thread is read once: a packet of a long chat holds many tool calls, and a read per call made the apply quadratic */
	reconcileToolCallOwnership(serverMessages) {
		const ownerIdsByThread = /* @__PURE__ */ new Map();
		for (const msg of serverMessages) {
			if (!msg.toolCalls?.length || !msg.threadId) continue;
			for (const tc of msg.toolCalls) {
				if (!tc.request_id) continue;
				const ownerIds = ownerIdsByThread.get(msg.threadId) ?? /* @__PURE__ */ new Map();
				ownerIds.set(tc.request_id, msg.id);
				ownerIdsByThread.set(msg.threadId, ownerIds);
			}
		}
		if (ownerIdsByThread.size === 0) return [];
		const modifiedMessages = [];
		runInAction(() => {
			for (const [threadId, ownerIds] of ownerIdsByThread) {
				const threadMessages = withoutReplacedMessages(this.getLoadedThreadMessages(threadId));
				for (const msg of threadMessages) {
					const ownedToolCalls = msg.toolCalls.filter((tc) => {
						const ownerId = tc.request_id && ownerIds.get(tc.request_id);
						return !ownerId || ownerId === msg.id;
					});
					if (ownedToolCalls.length === msg.toolCalls.length) continue;
					msg.toolCalls.splice(0, msg.toolCalls.length, ...ownedToolCalls);
					modifiedMessages.push(msg);
				}
			}
		});
		return modifiedMessages;
	}
	/**
	* After bootstrap hydration, emit events for any pending tool calls so
	* the desktop hook can pick them up and execute them.
	*/
	emitPendingToolCallsFromBootstrap(messages) {
		const threadsWithPending = /* @__PURE__ */ new Set();
		for (const message of messages) {
			if (message.role !== "assistant" || !message.toolCalls?.length) continue;
			const threadId = message.threadId;
			if (!threadId) continue;
			if (this.sdk.stores.threads.data.get(threadId)?.finished) continue;
			for (const tc of message.toolCalls) {
				if (!this.isPendingClientToolCall(tc)) continue;
				globalEventBus.emit(EventNames.CLIENT_TOOL_CALL_REQUEST, { data: {
					threadId,
					requestId: tc.request_id,
					toolId: tc.tool_id ?? null,
					namespace: tc.server_id,
					toolName: tc.tool_name,
					args: tc.arguments,
					timeoutSeconds: 0
				} });
				threadsWithPending.add(threadId);
			}
		}
		for (const threadId of threadsWithPending) {
			this.sdk.stores.threads.updateThreadStatus(threadId, "waiting_for_client_tools");
			globalEventBus.emit(EventNames.CHAT_WAITING_FOR_CLIENT_TOOLS, { data: { threadId } });
		}
	}
	isPendingClientToolCall(toolCall) {
		return toolCall.status === "pending" && toolCall.server_id === CLIENT_TOOL_SERVER_ID && typeof toolCall.request_id === "string" && toolCall.request_id.length > 0;
	}
	mapApiMessageToLocal(dbMessage, batchFallbackNow, localMessage) {
		const { incomingCreatedAt, incomingUpdatedAt } = this.resolveIncomingTimestamps(dbMessage, batchFallbackNow, localMessage);
		return {
			id: dbMessage.id,
			threadId: dbMessage.thread_id,
			role: dbMessage.role,
			content: dbMessage.content || "",
			thinkingContent: dbMessage.thinking_content || "",
			modalitiesUsed: this.groupWebSearchModalities(dbMessage.modalities_used) || null,
			mode: dbMessage.chat_mode,
			hiddenContent: dbMessage.hidden_content || "",
			status: dbMessage.interrupted ? "stopped" : "done",
			agent: dbMessage.agent || "",
			fileIds: dbMessage.file_ids ?? [],
			todoResults: dbMessage.todo_results ?? [],
			toolCalls: this.mergeToolCalls(localMessage?.toolCalls ?? [], dbMessage.tool_calls ?? []),
			contextAppsUsed: dbMessage.context_apps_used ?? localMessage?.contextAppsUsed ?? [],
			collaboratorIds: dbMessage.collaborator_ids ?? [],
			askUserQuestions: dbMessage.ask_user_questions ?? null,
			askUserAnswers: dbMessage.ask_user_answers ?? null,
			joinedTurn: dbMessage.joined_turn ?? false,
			refusedAsBusy: false,
			versionIds: dbMessage.version_ids ?? [],
			replacesMessageId: null,
			createdAt: incomingCreatedAt,
			updatedAt: incomingUpdatedAt,
			deletedAt: dbMessage.deleted_at ? parseDate(dbMessage.deleted_at) : void 0
		};
	}
	toggleMessageExpansion(messageId) {
		const message = this.get(messageId);
		if (message) message.isExpanded = !message.isExpanded;
	}
	clearAllExpansions() {
		Array.from(this.data.values()).forEach((message) => {
			message.isExpanded = false;
		});
	}
	async vote(threadId, messageId, direction, reasoning) {
		await this.sdk.apiClient.fetch("vote", {
			method: "POST",
			body: JSON.stringify({
				chat_id: threadId,
				message_id: messageId,
				direction,
				reasoning
			})
		});
	}
	handleInterruptedEvent(event) {
		const { threadId, response } = event;
		const streamingMsg = this.getStreamingMessageForThread(threadId);
		this.sdk.logger.warn("Chat stream interrupted - clearing streaming message and waiting for retry", {
			context: "MessageStore:handleInterruptedEvent",
			metadata: {
				threadId,
				messageId: streamingMsg?.id,
				eventType: response.event_type,
				hasStreamingMessage: !!streamingMsg
			}
		});
		globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_RESPONSE_EVENT, { data: {
			chat_context_type: "interrupted",
			chat_id: threadId,
			message_id: streamingMsg?.id ?? ""
		} });
		if (streamingMsg) {
			runInAction(() => {
				this.data.delete(streamingMsg.id);
			});
			this.sdk.storage.remove(this.tableName, streamingMsg.id);
		}
	}
	handleUserMessageEvent(event) {
		const { response, threadId, isMetaSlashCommand } = event;
		const { message_id, content, role, file_ids, collaborator_ids, modalities_used, joined_turn } = response;
		if (!message_id) {
			this.sdk.logger.warn("User message event missing message_id", {
				context: "MessageStore:handleUserMessageEvent",
				metadata: { response }
			});
			return;
		}
		const now = /* @__PURE__ */ new Date();
		const message = this.add({
			id: message_id,
			threadId,
			role: role === "user" ? "user" : "user",
			content: content ?? "",
			status: "done",
			joinedTurn: joined_turn ?? false,
			refusedAsBusy: false,
			fileIds: file_ids ?? [],
			collaboratorIds: collaborator_ids ?? [],
			modalitiesUsed: modalities_used,
			createdAt: now,
			updatedAt: now
		});
		if (message.replacesMessageId) this.acceptPendingEdit(message).catch((error) => {
			this.sdk.logger.error("Failed to store an accepted edit", {
				context: "MessageStore:handleUserMessageEvent",
				metadata: {
					error,
					threadId,
					messageId: message_id
				}
			});
		});
		if (!isMetaSlashCommand) {
			this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
			this.sdk.stores.threads.updateThreadLastMessageId(threadId, message_id);
		}
	}
	handleNotificationEvent(event) {
		const { response } = event;
		if (response.notification) {
			const { kind } = response.notification;
			if (kind === "rate_limit_alert") emitPaywallBannerFromNotification(response.notification);
			else if (kind === "chat_compaction") {
				const notification = response.notification;
				const status = notification.status;
				const isAsync = notification.is_async ?? false;
				this.sdk.logger.info("MessageStore: received chat compaction notification", {
					threadId: response.chat_id,
					status,
					isAsync,
					afterUserMessageId: notification.after_user_message_id
				});
				globalEventBus.emit(EventNames.CHAT_COMPACTION_STATUS, { data: {
					threadId: response.chat_id,
					status,
					isAsync,
					afterUserMessageId: notification.after_user_message_id,
					summary: notification.message
				} });
			} else if (kind === "turn_cap_hit") {
				const notification = response.notification;
				this.sdk.logger.info("MessageStore: received turn cap-hit notification", {
					threadId: response.chat_id,
					reason: notification.reason,
					assistantMessageId: notification.assistant_message_id
				});
				globalEventBus.emit(EventNames.CHAT_TURN_CAP_HIT, { data: {
					threadId: response.chat_id,
					reason: notification.reason,
					assistantMessageId: notification.assistant_message_id ?? null
				} });
			} else if (kind === "image_generation_quota_exceeded") {
				const notification = response.notification;
				this.sdk.logger.info("MessageStore: received image-generation quota notification", {
					threadId: response.chat_id,
					afterUserMessageId: notification.after_user_message_id,
					messageLimit: notification.message_limit,
					nextAllowedTime: notification.next_allowed_time
				});
				globalEventBus.emit(EventNames.CHAT_IMAGE_GENERATION_QUOTA_EXCEEDED, { data: {
					threadId: response.chat_id,
					afterUserMessageId: notification.after_user_message_id ?? null,
					messageLimit: notification.message_limit ?? null,
					nextAllowedTime: notification.next_allowed_time ?? null,
					userErrorMessage: notification.user_error_message ?? null
				} });
			} else console.warn("[notification] Unknown kind:", kind, response.notification);
		} else if (response.content) emitPaywallBannerFromLegacyContent(response.content);
	}
	/**
	* Attach questions to the assistant message that just asked them, so the
	* picker appears as the turn ends rather than after the message returns
	* through sync. Sync later re-hydrates the same payload from the message.
	*/
	handleAskUserQuestionsEvent(event) {
		const questions = event.response.ask_user_questions;
		if (!questions) return;
		const message = this.getStreamingMessageForThread(event.threadId) ?? this.getMessagesByThreadId(event.threadId, true).find((candidate) => candidate.role === "assistant");
		if (!message) return;
		runInAction(() => {
			message.askUserQuestions = questions;
		});
	}
	handleWaitingForClientToolsEvent(event) {
		const { threadId, isMetaSlashCommand } = event;
		if (this.sdk.stores.threads.data.get(threadId)?.finished) {
			this.sdk.logger.warn("Ignoring waiting_for_client_tools for finished thread", {
				context: "MessageStore:handleWaitingForClientToolsEvent",
				metadata: { threadId }
			});
			return;
		}
		this.finishAllStreamingMessages(threadId, !isMetaSlashCommand);
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "waiting_for_client_tools");
		globalEventBus.emit(EventNames.CHAT_WAITING_FOR_CLIENT_TOOLS, { data: { threadId } });
	}
	handleWaitingForDeviceToolsEvent(event) {
		const { threadId, isMetaSlashCommand } = event;
		if (this.sdk.stores.threads.data.get(threadId)?.finished) {
			this.sdk.logger.warn("Ignoring waiting_for_device_tools for finished thread", {
				context: "MessageStore:handleWaitingForDeviceToolsEvent",
				metadata: { threadId }
			});
			return;
		}
		this.finishAllStreamingMessages(threadId, !isMetaSlashCommand);
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "waiting_for_client_tools");
		globalEventBus.emit(EventNames.CHAT_WAITING_FOR_DEVICE_TOOLS, { data: { threadId } });
	}
	/**
	* `subagent_progress` and `heartbeat` frames are advisory: a detached
	* subagent keeps streaming them to a paused parent thread, but no client
	* state may depend on receiving them. This only upgrades the presentation
	* of an already-paused thread so its loader stays on — a client that
	* never sees these frames is still fully correct.
	*/
	resumeThreadFromWaitingForClientTools(threadId) {
		const thread = this.sdk.stores.threads.data.get(threadId);
		if (!thread || thread.finished) return;
		if (thread.status !== "waiting_for_client_tools") return;
		this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
	}
	handleClientToolContentChunk(event) {
		const { response, threadId } = event;
		if (!response.content) return;
		try {
			const data = JSON.parse(response.content);
			globalEventBus.emit(EventNames.CLIENT_TOOL_CONTENT_CHUNK, { data: {
				threadId,
				requestId: data.request_id,
				filePath: data.file_path ?? null,
				charsSoFar: data.chars_so_far ?? 0,
				funcName: data.func_name ?? null
			} });
		} catch {}
	}
	/** Convert one audio_chunk wire event and hand it to this SDK's Voice Mode store */
	handleResponseAudioEvent(event) {
		const { response, threadId } = event;
		const userMessageId = response.user_message_id;
		if (!userMessageId) return;
		const common = {
			threadId,
			userMessageId
		};
		if (response.audio_is_reset) {
			this.sdk.voiceMode.receiveResponseAudio({
				...common,
				kind: "reset"
			});
			return;
		}
		if (response.audio_is_final) {
			this.sdk.voiceMode.receiveResponseAudio({
				...common,
				kind: "final",
				synthesisFailed: response.audio_incomplete === true
			});
			return;
		}
		const audioBase64 = response.audio_base64;
		if (!audioBase64) return;
		this.sdk.voiceMode.receiveResponseAudio({
			...common,
			kind: "audio",
			mimeType: response.audio_mime_type ?? "audio/mpeg",
			audioBase64
		});
	}
	handleClientToolCallRequest(event) {
		const { response, threadId } = event;
		if (!response.content) return;
		try {
			const data = JSON.parse(response.content);
			if (!data.request_id || !data.namespace || !data.tool_name) {
				this.sdk.logger.error("Missing required fields in client tool call", {
					context: "MessageStore:handleClientToolCallRequest",
					metadata: {
						hasRequestId: !!data.request_id,
						hasNamespace: !!data.namespace,
						hasToolName: !!data.tool_name
					}
				});
				return;
			}
			if (this.getMessagesByThreadId(threadId).some((msg) => msg.toolCalls.some((tc) => tc.request_id === data.request_id))) return;
			const lastAssistantMsg = this.findCurrentTurnAssistantMessage(threadId);
			runInAction(() => {
				if (lastAssistantMsg) lastAssistantMsg.toolCalls.push({
					server_id: data.namespace,
					tool_name: data.tool_name,
					arguments: data.args ?? {},
					result: null,
					status: "pending",
					tool_id: data.tool_id ?? null,
					request_id: data.request_id
				});
				globalEventBus.emit(EventNames.CLIENT_TOOL_CALL_REQUEST, { data: {
					threadId,
					requestId: data.request_id,
					toolId: data.tool_id ?? null,
					namespace: data.namespace,
					toolName: data.tool_name,
					args: data.args,
					timeoutSeconds: data.timeout_seconds
				} });
			});
		} catch (error) {
			this.sdk.logger.error("Error parsing client tool call request", {
				context: "MessageStore:handleClientToolCallRequest",
				metadata: { error }
			});
		}
	}
	findLastUserMessageId(threadId) {
		const messages = this.getMessagesByThreadId(threadId);
		for (let index = messages.length - 1; index >= 0; index -= 1) {
			const message = messages[index];
			if (message.role === "user" && !message.refusedAsBusy) return message.id;
		}
		return null;
	}
	/**
	* Find the last assistant message in the current turn (after the user
	* message that opened it). Returns undefined when no such message exists
	* yet, preventing tool calls from being attached to a previous turn's message.
	*/
	findCurrentTurnAssistantMessage(threadId) {
		const messages = this.getMessagesByThreadId(threadId);
		let turnStartIdx = -1;
		for (let i = messages.length - 1; i >= 0; i--) if (opensTurn(messages[i])) {
			turnStartIdx = i;
			break;
		}
		return [...messages].slice(turnStartIdx + 1).reverse().find((m) => m.role === "assistant");
	}
	/**
	* Mark all pending tool calls on messages in this thread as 'error'.
	* Called when the client/server state is irreconcilable (e.g. /continue
	* returns 404) to prevent bootstrap from re-emitting them.
	*/
	failPendingToolCalls(threadId, reason) {
		const messages = this.getMessagesByThreadId(threadId);
		runInAction(() => {
			for (const msg of messages) {
				let changed = false;
				for (const tc of msg.toolCalls) if (tc.status === "pending") {
					tc.status = "error";
					tc.result = reason;
					changed = true;
				}
				if (changed) this.sdk.storage.set(this.tableName, msg).catch(() => {});
			}
		});
	}
	updateToolCallOnMessage(threadId, requestId, status, result) {
		const messages = this.getMessagesByThreadId(threadId);
		for (const msg of messages) {
			const tc = msg.toolCalls.find((tc) => tc.request_id === requestId);
			if (tc) {
				runInAction(() => {
					tc.status = status;
					if (result !== void 0) tc.result = result;
				});
				this.sdk.storage.set(this.tableName, msg).catch(() => {});
				return;
			}
		}
	}
	handleSubagentProgressEvent(event) {
		const { response, threadId } = event;
		this.resumeThreadFromWaitingForClientTools(threadId);
		if (!response.content) return;
		try {
			const data = JSON.parse(response.content);
			const message = this.findCurrentTurnAssistantMessage(threadId);
			if (!message) return;
			runInAction(() => {
				this.upsertSubagentProgress(message, data);
			});
		} catch (error) {
			this.sdk.logger.error("Error parsing subagent progress event", {
				context: "MessageStore:handleSubagentProgressEvent",
				metadata: { error }
			});
		}
	}
	upsertSubagentProgress(message, data) {
		if (!data.subagent_chat_id || !data.child) return;
		const liveText = this.formatSubagentProgressText(data.child);
		const status = data.child.event_type === "done" ? "completed" : "pending";
		const toolCall = message.toolCalls.find((tc) => {
			if (data.tool_id && tc.tool_id === data.tool_id) return true;
			const extra = tc.extra;
			return extra !== null && typeof extra === "object" && extra.subagent_chat_id === data.subagent_chat_id;
		});
		if (toolCall) {
			const existingExtra = toolCall.extra && typeof toolCall.extra === "object" ? toolCall.extra : {};
			const previous = typeof existingExtra[SUBAGENT_LIVE_TRANSCRIPT_KEY] === "string" ? existingExtra[SUBAGENT_LIVE_TRANSCRIPT_KEY] : "";
			toolCall.status = status;
			toolCall.extra = {
				...existingExtra,
				subagent_chat_id: data.subagent_chat_id,
				[SUBAGENT_LIVE_TRANSCRIPT_KEY]: previous + liveText
			};
			return;
		}
		message.toolCalls.push({
			server_id: "server",
			tool_name: "run_subagent",
			arguments: {},
			result: null,
			status,
			tool_id: data.tool_id ?? null,
			request_id: data.tool_index ? `subagent:${data.tool_index}` : null,
			extra: {
				subagent_chat_id: data.subagent_chat_id,
				[SUBAGENT_LIVE_TRANSCRIPT_KEY]: liveText
			}
		});
	}
	formatSubagentProgressText(data) {
		if (!data.content) return data.event_type === "finish_one_assistant_message" ? "\n\n---\n\n" : "";
		if (data.event_type === "token") return data.content;
		return `\n\n[${data.event_type ?? "event"}]\n${data.content}`;
	}
	handleHiddenTextEvent(event) {
		const { response, threadId, isMetaSlashCommand } = event;
		const target = response.message_id ? this.streamingTargetFor(response.message_id) : void 0;
		if (target === null) return;
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
		if (!response.message_id) return;
		if (target) this.appendHiddenContentToMessage(target, response.content ?? "");
		else {
			const newStreamingMsg = this.createStreamingMessage({
				...event,
				response: {
					...event.response,
					content: ""
				}
			});
			this.appendHiddenContentToMessage(newStreamingMsg, response.content ?? "");
		}
		globalEventBus.emit(EventNames.CHAT_NEW_ASSISTANT_HIDDEN_TEXT, { data: {
			threadId,
			messageId: response.message_id
		} });
	}
	handleThoughtEvent(event) {
		const { response, threadId, isMetaSlashCommand } = event;
		const target = response.message_id ? this.streamingTargetFor(response.message_id) : void 0;
		if (target === null) return;
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
		if (response.message_id) {
			if (target) this.appendThinkingContentToMessage(target, response.content ?? "");
			else {
				const newStreamingMsg = this.createStreamingMessage({
					...event,
					response: {
						...event.response,
						content: ""
					}
				});
				this.appendThinkingContentToMessage(newStreamingMsg, response.content ?? "");
			}
			globalEventBus.emit(EventNames.CHAT_NEW_ASSISTANT_THOUGHT, { data: { threadId } });
		}
	}
	handleTokenEvent(event) {
		const { threadId, response, isMetaSlashCommand } = event;
		const { agent } = response;
		const target = response.message_id ? this.streamingTargetFor(response.message_id) : void 0;
		if (target === null) return;
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "streaming");
		if (!response.message_id) {
			const now = /* @__PURE__ */ new Date();
			this.add({
				id: v4(),
				threadId,
				role: "assistant",
				content: response.content ?? "",
				status: "done",
				mode: agent,
				createdAt: now,
				updatedAt: now
			});
		} else if (target) this.appendContentToMessage(target, response.content ?? "");
		else this.createStreamingMessage(event);
	}
	/**
	* The message a token, thought, or hidden-text event extends.
	*
	* Events name their assistant message, so a known id wins over "whatever is
	* streaming in this thread": an interrupted turn can leave a message
	* `Streaming` (its late `done` is discarded as stale), and the next turn's
	* tokens must not land in it. `undefined` means create a new streaming
	* message; `null` means drop the event, because its message already finished
	* or was stopped and late tokens would only duplicate or extend it.
	*/
	streamingTargetFor(messageId) {
		const named = this.data.get(messageId);
		if (!named) return void 0;
		return named.status === "streaming" ? named : null;
	}
	/**
	* Attaches the file ids the server reported for a just-finished assistant message, so its
	* attachments render without waiting for the next sync poll.
	*
	* The hydrate call matters even when the metadata has not landed locally yet: `FileStore` keeps
	* unresolved ids in `pendingFileIds`, and that is what makes a later sync tick put their metadata
	* into MobX rather than only into IndexedDB.
	*/
	applyFinishedAssistantFileIds(threadId, fileIds) {
		if (!fileIds || fileIds.length === 0) return;
		const target = this.getStreamingMessageForThread(threadId) ?? this.getNewestAssistantMessage(threadId);
		if (!target) return;
		const merged = [.../* @__PURE__ */ new Set([...target.fileIds, ...fileIds])];
		if (merged.length !== target.fileIds.length) {
			runInAction(() => {
				target.fileIds = merged;
			});
			if (MessageStore.persisted) this.sdk.storage.set(this.tableName, target);
		}
		this.sdk.stores.files.hydrateByIds(fileIds);
	}
	getNewestAssistantMessage(threadId) {
		return this.getMessagesByThreadId(threadId, true).find((message) => message.role === "assistant");
	}
	async handleFinishAssistantMessageEvent(event) {
		const { threadId, isMetaSlashCommand, response } = event;
		if (!isMetaSlashCommand) {
			this.sdk.stores.threads.updateThreadStatus(threadId, "thinking");
			globalEventBus.emit(EventNames.CHAT_ASSISTANT_DID_FINISH_STEP, { data: { threadId } });
		}
		this.applyFinishedAssistantFileIds(threadId, response.file_ids);
		await this.finishStreamingMessage(threadId, isMetaSlashCommand);
	}
	handleNewAssistantMessageEvent(event) {
		const { threadId, isMetaSlashCommand } = event;
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "streaming");
		this.createStreamingMessage(event);
	}
	handleDoneEvent(event) {
		const { threadId, isMetaSlashCommand, response } = event;
		const lastUserMessageId = this.findLastUserMessageId(threadId);
		/**
		* Following guard helps prevent ownership conflicts caused by late done events.
		* Example of a scenario where this guard is needed:
		* 1. User stops a reply and quickly sends a new message.
		* 2. The stopped turn's done event lands late, after the next turn has already started.
		* 3. The client treated any done as "turn over", so it unlocked the composer.
		* 4. Send again in that window and since another turn still owns the chat, the user gets an error.
		*/
		if (lastUserMessageId !== null && response.message_id !== null && response.message_id !== void 0 && lastUserMessageId !== response.message_id) {
			if (this.sdk.options.isChatTurnOwnershipEnabled?.()) {
				this.sdk.logger.warn("Ignoring stale done event", {
					context: "MessageStore:handleDoneEvent",
					metadata: {
						lastUserMessageId,
						eventMessageId: response.message_id,
						threadId
					}
				});
				return;
			}
			this.sdk.logger.warn("Stale done event encountered, but chat turn ownership is disabled", {
				context: "MessageStore:handleDoneEvent",
				metadata: {
					lastUserMessageId,
					eventMessageId: response.message_id,
					threadId
				}
			});
		}
		this.arenaThreads.delete(threadId);
		if (response.sync_id !== void 0) this.sdk.stores.threads.recordFinishSyncId(threadId, response.sync_id);
		this.applyFinishedAssistantFileIds(threadId, response.file_ids);
		this.finishAllStreamingMessages(threadId, !isMetaSlashCommand);
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "done");
		if (response.is_legacy_chat !== void 0) this.sdk.stores.threads.setIsLegacyChat(threadId, response.is_legacy_chat);
	}
	handleErrorEvent(event) {
		const { threadId, isMetaSlashCommand, response } = event;
		const ownerId = response.conflict_owner_message_id;
		if (response.chat_busy || ownerId) {
			this.handleBusyRefusal(threadId, response.user_message_id, ownerId);
			return;
		}
		this.arenaThreads.delete(threadId);
		if (response.sync_id !== void 0) this.sdk.stores.threads.recordFinishSyncId(threadId, response.sync_id);
		this.finishAllStreamingMessages(threadId, !isMetaSlashCommand);
		this.createErrorMessage(event);
		if (!isMetaSlashCommand) globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_ERROR_EVENT, { data: {
			chat_id: threadId,
			error: response.content?.trim() || "Agent error"
		} });
	}
	/**
	* Settles a message the server refused because a reply holds the chat, whether the refusal came as the send's
	* response or over the socket. The running reply goes on, whether or not this device knows it, so only the
	* refused message fails
	*/
	handleBusyRefusal(threadId, userMessageId, ownerId) {
		if (ownerId && ownerId === userMessageId) return;
		this.sdk.logger.info("MessageStore: a message was refused because a reply holds the chat", {
			threadId,
			userMessageId,
			conflictOwnerMessageId: ownerId
		});
		this.sdk.voiceMode.notifyPendingReplyFailure(threadId, userMessageId ?? void 0);
		const sentHere = !!userMessageId && !!this.get(userMessageId);
		this.failRefusedUserMessage(threadId, userMessageId);
		if (sentHere) this.sdk.stores.threads.forgetReplyStart(threadId);
		this.sdk.checkRunningChats([threadId]);
	}
	/**
	* Reads the server's rows of a chat this client shows running, and applies them like a delta when the thread row
	* ends the reply (`ThreadStore.rowEndsShownReply`) or the chat was deleted. It is for a chat whose finished row this
	* client missed, or whose finished row another browser tab, sharing this local database, overwrote with its running
	* copy. No later sync brings that row. Only a sync run calls this
	* (`BootstrapManager.checkMarkedRunningChats`), so no other sync packet applies during it, and a logout waits for it.
	*/
	async checkRunningChatInSyncRun(threadId, signal) {
		if (!this.sdk.stores.threads.showsRunning(threadId)) return;
		await this.sdk.stores.threads.ensureThreadIsReady(threadId);
		const rows = await readChatRows(this.sdk, threadId, signal);
		if (!rows || signal?.aborted) return;
		const endsReply = this.sdk.stores.threads.rowEndsShownReply(rows.thread);
		if (!endsReply && !rows.thread.deleted_at) return;
		const endedMessages = endsReply ? this.markMessagesDone(this.streamingMessagesOf(threadId)) : [];
		try {
			await applyChatRows(this.sdk, rows);
			if (!this.sdk.stores.threads.showsRunning(threadId)) this.sdk.logger.info("A chat shown running shows done after its check", {
				context: "MessageStore:checkRunningChatInSyncRun",
				metadata: { threadId }
			});
		} finally {
			if (endedMessages.length > 0) await this.saveAndAnnounceEndedMessages(threadId, endedMessages);
		}
	}
	/** Saves the messages a check ended, and tells finish listeners, who read the server's text */
	async saveAndAnnounceEndedMessages(threadId, endedMessages) {
		try {
			await this.sdk.storage.bulkSet(this.tableName, endedMessages);
		} finally {
			this.emitFinishedMessages(threadId, endedMessages.map(({ id }) => id));
		}
	}
	async failRefusedUserMessage(threadId, userMessageId) {
		if (!userMessageId) return;
		const message = this.get(userMessageId);
		if (!message || message.role !== "user") return;
		const failed = {
			threadId,
			userMessageId,
			reason: "chat_busy"
		};
		if (message.replacesMessageId) {
			try {
				await this.removeLocalMessages([message.id]);
			} catch (error) {
				this.sdk.logger.error("Failed to remove the refused edit", {
					context: "MessageStore:failRefusedUserMessage",
					metadata: {
						error,
						threadId,
						userMessageId
					}
				});
			}
			this.restoreThreadLastMessageId(threadId);
			globalEventBus.emit(EventNames.CHAT_SEND_FAILED, { data: failed });
			return;
		}
		runInAction(() => {
			message.status = "errored";
			message.refusedAsBusy = true;
			message.updatedAt = /* @__PURE__ */ new Date();
		});
		globalEventBus.emit(EventNames.CHAT_SEND_FAILED, { data: failed });
		try {
			await this.sdk.storage.set(this.tableName, message);
		} catch (error) {
			this.sdk.logger.error("Failed to persist the refused user message", {
				context: "MessageStore:failRefusedUserMessage",
				metadata: {
					error,
					threadId,
					userMessageId
				}
			});
		}
	}
	handleProgressEvent(event) {
		const { threadId, response } = event;
		if (!response.content) return;
		let progressData;
		try {
			progressData = JSON.parse(response.content);
		} catch (_error) {
			this.sdk.logger.warn("Failed to parse progress message content:", { metadata: {
				message: "Failed to parse progress message content:",
				error: _error
			} });
			return;
		}
		if (!progressData.modalities) return;
		let streamingMsg = this.getStreamingMessageForThread(threadId);
		if (!streamingMsg) {
			if (!response.message_id) return;
			streamingMsg = this.createStreamingMessage({
				...event,
				response: {
					...response,
					content: ""
				}
			});
		}
		this.appendModalitiesToMessage(streamingMsg, progressData.modalities);
		if (!progressData.is_partial) this.trackAllModalities(threadId, progressData.modalities);
	}
	trackAllModalities(threadId, modalities) {
		const streamingMsg = this.getStreamingMessageForThread(threadId);
		for (const modality of modalities) {
			const toolExecute = modality.type === "tool_execute" ? modality : null;
			globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_RESPONSE_EVENT, { data: {
				chat_context_type: modality.type,
				chat_id: threadId,
				message_id: streamingMsg?.id ?? "",
				tool_slug: toolExecute?.tool_slug,
				integration_name: toolExecute?.integration_name,
				account_label: toolExecute?.account_label
			} });
		}
	}
	handleModalityAppsUsedEvent(event) {
		this.applyPostToolStamp(event, AppsUsedStampSchema, (target, stamp) => {
			const seen = new Set(target.contextAppsUsed.map((a) => a.toLowerCase()));
			for (const app of stamp.apps_used) {
				const key = app.toLowerCase();
				if (!app || seen.has(key)) continue;
				seen.add(key);
				target.contextAppsUsed.push(app);
			}
		});
	}
	handleModalityReviewFilesEvent(event) {
		const fileIds = [];
		this.applyPostToolStamp(event, ReviewFilesStampSchema, (target, stamp) => {
			const modalities = target.modalitiesUsed;
			if (!modalities) return;
			for (const { modality_index: index, file_ids } of stamp.review_files) {
				const modality = modalities[index];
				if (modality?.type !== "run_sandbox") continue;
				modality.review_file_ids = file_ids;
				fileIds.push(...file_ids);
			}
			target.modalitiesUsed = [...modalities];
		});
		if (fileIds.length > 0) this.sdk.stores.files.hydrateByIds(fileIds);
	}
	applyPostToolStamp(event, schema, patch) {
		const { response, threadId } = event;
		if (!response.content) return;
		let stamp;
		try {
			stamp = schema.parse(JSON.parse(response.content));
		} catch (error) {
			const message = `Failed to parse ${response.event_type} content`;
			this.sdk.logger.warn(message, { metadata: {
				message,
				error
			} });
			return;
		}
		const target = response.message_id ? this.data.get(response.message_id) : this.getStreamingMessageForThread(threadId);
		if (!target) return;
		runInAction(() => {
			patch(target, stamp);
			target.updatedAt = /* @__PURE__ */ new Date();
		});
		if (MessageStore.persisted) this.sdk.storage.set(this.tableName, target).catch(() => {});
	}
	handleToolResultEvent(event) {
		const { response, threadId } = event;
		if (!response.content) return;
		try {
			const parsedContent = JSON.parse(response.content);
			if (parsedContent.type === "todo_results") {
				this.handleTodoResult(parsedContent, threadId);
				return;
			}
		} catch (error) {
			this.sdk.logger.error("Error parsing tool result content", {
				context: "MessageStore:handleToolResultEvent",
				metadata: {
					message: "Error parsing tool result content",
					error
				}
			});
		}
	}
	handleTodoResult(parsedContent, threadId) {
		const streamingMsg = this.getStreamingMessageForThread(threadId);
		if (!streamingMsg) return;
		runInAction(() => {
			const todoData = parsedContent.content.function_result.data;
			const newTodoResults = (Array.isArray(todoData) ? todoData : [todoData]).map((data) => ({
				todoData: data,
				functionName: parsedContent.modality.function_name,
				modality: parsedContent.modality
			}));
			streamingMsg.todoResults.push(...newTodoResults);
			streamingMsg.updatedAt = /* @__PURE__ */ new Date();
		});
	}
	async removeLocalMessages(ids) {
		runInAction(() => {
			for (const id of ids) if (this.data.has(id)) this.data.delete(id);
		});
		for (const id of ids) this.syncFingerprints.delete(id);
		await this.sdk.storage.bulkRemove(this.tableName, ids);
	}
	async persistMessageAndFiles(message, files, isSlashCommand) {
		await this.sdk.storage.withTransaction([this.tableName, "files"], "readwrite", async () => {
			if (!isSlashCommand) {
				await this.sdk.storage.set(this.tableName, message);
				await this.sdk.storage.bulkSet("files", files);
			}
		});
	}
	appendModalitiesToMessage(message, modalities) {
		if (!modalities || !message) return;
		runInAction(() => {
			message.modalitiesUsed = this.groupWebSearchModalities(modalities);
			message.updatedAt = /* @__PURE__ */ new Date();
		});
	}
	appendContentToMessage(message, content) {
		if (!content || !message) return;
		runInAction(() => {
			message.content = (message.content || "") + content;
			message.updatedAt = /* @__PURE__ */ new Date();
		});
	}
	appendThinkingContentToMessage(message, content) {
		if (!content || !message) return;
		runInAction(() => {
			message.thinkingContent = (message.thinkingContent || "") + content;
			message.updatedAt = /* @__PURE__ */ new Date();
		});
	}
	createStreamingMessage(event) {
		const { threadId, isMetaSlashCommand, response, arenaData } = event;
		const { agent } = response;
		this.finishAllStreamingMessages(threadId, !isMetaSlashCommand);
		const now = /* @__PURE__ */ new Date();
		const message = this.add({
			id: response.message_id ?? v4(),
			threadId,
			role: "assistant",
			content: response.content ?? "",
			status: "streaming",
			mode: agent,
			createdAt: now,
			updatedAt: now
		});
		if (arenaData && response.message_id) this.sdk.stores.arenas.updateArena({
			agentName: arenaData.agentName,
			messageId: response.message_id,
			threadId,
			sessionId: arenaData.sessionId,
			exchangeId: arenaData.exchangeId
		});
		return message;
	}
	createErrorMessage(event) {
		const { threadId, response, isMetaSlashCommand } = event;
		const now = /* @__PURE__ */ new Date();
		const message = this.add({
			id: v4(),
			threadId,
			role: "assistant",
			content: response.content?.trim() ?? "",
			status: "done",
			mode: response.agent,
			createdAt: now,
			updatedAt: now
		});
		if (!isMetaSlashCommand) this.sdk.stores.threads.updateThreadStatus(threadId, "errored");
		const userMessageId = response.user_message_id ?? void 0;
		this.sdk.voiceMode.notifyPendingReplyFailure(threadId, userMessageId);
		globalEventBus.emit(EventNames.CHAT_ERROR, { data: {
			threadId,
			messageId: message.id,
			userMessageId
		} });
	}
	async finishStreamingMessage(threadId, isMetaSlashCommand) {
		const streamingMsg = this.getStreamingMessageForThread(threadId);
		if (!streamingMsg) return;
		runInAction(() => {
			streamingMsg.updatedAt = /* @__PURE__ */ new Date();
			streamingMsg.status = "done";
		});
		if (!isMetaSlashCommand) await this.sdk.storage.set(this.tableName, streamingMsg);
	}
	/** Ends the chat's streaming messages and tells finish listeners */
	async finishAllStreamingMessages(threadId, saveMessage) {
		const ended = this.markMessagesDone(this.streamingMessagesOf(threadId));
		if (ended.length === 0) return;
		if (saveMessage) await this.sdk.storage.bulkSet(this.tableName, ended);
		this.emitFinishedMessages(threadId, ended.map(({ id }) => id));
	}
	streamingMessagesOf(threadId) {
		return Array.from(this.data.values()).filter((msg) => msg.threadId === threadId && msg.status === "streaming");
	}
	/** Marks the messages done in memory, without telling listeners, and returns them */
	markMessagesDone(messages) {
		runInAction(() => {
			for (const msg of messages) {
				msg.status = "done";
				msg.updatedAt = /* @__PURE__ */ new Date();
			}
		});
		return messages;
	}
	emitFinishedMessages(threadId, messageIds) {
		globalEventBus.emit(EventNames.CHAT_ASSISTANT_DID_FINISH_MESSAGES, { data: {
			threadId,
			messageIds
		} });
	}
};
//#endregion
//#region src/core/sync/types/project-list.ts
const OWNER_PROJECT_CAPABILITIES = {
	canEditContent: true,
	canInvite: true,
	canManageMembers: true,
	canManageLink: true,
	canRename: true,
	canDelete: true,
	canLeave: false
};
//#endregion
//#region src/core/sync/stores/project-store.ts
function assertProjectIcon(icon) {
	if (icon === null) return null;
	const parsed = parseIconConfig(icon);
	if (!parsed) throw new Error(`Invalid project icon: ${JSON.stringify(icon)}`);
	return parsed;
}
const SERVER_ACKED_SYNC_ID = 1;
const NO_ITEMS = {
	threads: 0,
	meetings: 0
};
const PROJECTS_NUDGE_DEBOUNCE_MS = 50;
const PROJECTS_NUDGE_JITTER_MS = 500;
var ProjectStore = class ProjectStore extends Store {
	static persisted = true;
	static loadStrategy = "lazy";
	static schemaVersion = 2;
	ModelClass = Project;
	projectsChangedUnsub = null;
	nudgeFlushTimer = null;
	nudgeRefreshInFlight = false;
	nudgePendingRefresh = false;
	/** Thread and meeting counts per project from SQL, so they cover rows that are not resident */
	itemCounts = observable.map();
	countsFlushTimer = null;
	countsRefreshInFlight = false;
	countsRefreshPending = false;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("projects");
		makeObservable(this, {
			pinnedProjects: computed,
			recentProjects: computed,
			createProject: action.bound,
			renameProject: action.bound,
			updateProject: action.bound,
			deleteProject: action.bound,
			persistAndHydrateProjects: action,
			refreshProjects: action.bound
		});
		this.projectsChangedUnsub = globalEventBus.on(EventNames.PROJECTS_CHANGED, () => this.handleProjectsChanged());
	}
	handleProjectsChanged() {
		if (!this.sdk.usesRestProjects) return;
		if (this.nudgeFlushTimer) clearTimeout(this.nudgeFlushTimer);
		this.nudgeFlushTimer = setTimeout(() => {
			this.nudgeFlushTimer = null;
			this.runNudgeRefresh();
		}, 50 + Math.random() * 500);
	}
	async runNudgeRefresh() {
		if (this.nudgeRefreshInFlight) {
			this.nudgePendingRefresh = true;
			return;
		}
		this.nudgeRefreshInFlight = true;
		try {
			await this.refreshProjects();
		} catch (err) {
			this.sdk.logger.error("Refresh failed on projects changed nudge", {
				metadata: { error: err },
				context: "ProjectStore:handleProjectsChanged"
			});
		} finally {
			this.nudgeRefreshInFlight = false;
			if (this.nudgePendingRefresh) {
				this.nudgePendingRefresh = false;
				this.runNudgeRefresh();
			}
		}
	}
	getItemCounts(projectId) {
		return this.itemCounts.get(projectId) ?? NO_ITEMS;
	}
	/** Coalesces bursts of assignment changes into one pair of count queries */
	scheduleItemCountRefresh() {
		if (this.countsFlushTimer) return;
		this.countsFlushTimer = setTimeout(() => {
			this.countsFlushTimer = null;
			this.refreshItemCounts();
		}, 50);
	}
	async refreshItemCounts() {
		if (!this.sdk.isHydrated) return;
		if (this.countsRefreshInFlight) {
			this.countsRefreshPending = true;
			return;
		}
		this.countsRefreshInFlight = true;
		try {
			const { threads, meetings } = this.sdk.storage.entities;
			const none = {};
			const [threadCounts, meetingCounts] = await Promise.all([threads?.countByProject() ?? none, meetings?.countByProject() ?? none]);
			const next = /* @__PURE__ */ new Map();
			for (const id of /* @__PURE__ */ new Set([...Object.keys(threadCounts), ...Object.keys(meetingCounts)])) next.set(id, {
				threads: threadCounts[id] ?? 0,
				meetings: meetingCounts[id] ?? 0
			});
			runInAction(() => this.itemCounts.replace(next));
		} catch (err) {
			this.sdk.logger.error("Failed to refresh project item counts", {
				metadata: { error: err },
				context: "ProjectStore:refreshItemCounts"
			});
		} finally {
			this.countsRefreshInFlight = false;
			if (this.countsRefreshPending) {
				this.countsRefreshPending = false;
				this.refreshItemCounts();
			}
		}
	}
	/** Logout drops refreshes still waiting to run and counts, which belong to the old account */
	reset() {
		this.cancelScheduledRefreshes();
		this.nudgePendingRefresh = false;
		this.countsRefreshPending = false;
		runInAction(() => this.itemCounts.clear());
		super.reset();
	}
	teardownForTesting() {
		this.projectsChangedUnsub?.unsubscribe();
		this.projectsChangedUnsub = null;
		this.cancelScheduledRefreshes();
	}
	cancelScheduledRefreshes() {
		if (this.nudgeFlushTimer) {
			clearTimeout(this.nudgeFlushTimer);
			this.nudgeFlushTimer = null;
		}
		if (this.countsFlushTimer) {
			clearTimeout(this.countsFlushTimer);
			this.countsFlushTimer = null;
		}
	}
	getById = computedFn(function(id) {
		return this.data.get(id);
	});
	get pinnedProjects() {
		return this.orderedData.filter((project) => project.starred);
	}
	get recentProjects() {
		return this.orderedData.filter((project) => !project.starred);
	}
	getThreads(projectId) {
		return this.sdk.stores.threads.getThreadsForProject(projectId);
	}
	getMeetings(projectId) {
		return this.sdk.stores.meetings.getMeetingsForProject(projectId);
	}
	getProjectForThread = computedFn(function(threadId) {
		const thread = this.sdk.stores.threads.getById(threadId);
		if (!thread?.projectId) return null;
		return this.getById(thread.projectId) ?? null;
	});
	getProjectForMeeting = computedFn(function(meetingId) {
		const meeting = this.sdk.stores.meetings.get(meetingId);
		if (!meeting?.projectId) return null;
		return this.getById(meeting.projectId) ?? null;
	});
	mapDbProject(dbProject) {
		return {
			id: dbProject.id,
			name: dbProject.name,
			instructions: dbProject.instructions ?? null,
			icon: parseIconConfig(dbProject.icon),
			starred: dbProject.starred ?? false,
			limitChatContextToProject: dbProject.limit_chat_context_to_project ?? true,
			disableScreenContextSearch: dbProject.disable_screen_context_search ?? false,
			syncId: dbProject.sync_id,
			createdAt: parseDate(dbProject.created_at),
			updatedAt: parseDate(dbProject.updated_at),
			deletedAt: dbProject.deleted_at ? parseDate(dbProject.deleted_at) : void 0
		};
	}
	mapProjectResource(res) {
		return {
			id: res.id,
			name: res.name,
			instructions: res.instructions,
			icon: parseIconConfig(res.icon),
			starred: res.starred,
			limitChatContextToProject: res.limitChatContextToProject,
			disableScreenContextSearch: res.disableScreenContextSearch,
			role: res.role,
			hasShareLink: res.hasShareLink,
			capabilities: res.capabilities,
			syncId: SERVER_ACKED_SYNC_ID,
			createdAt: parseDate(res.createdAt),
			updatedAt: parseDate(res.updatedAt)
		};
	}
	async refreshProjects() {
		const idsBeforeFetch = new Set(this.data.keys());
		const resources = (await this.sdk.apiClient.fetch(this.apiEndpoint, { method: "GET" })).projects ?? [];
		const seenIds = new Set(resources.map((r) => r.id));
		const added = resources.map((res) => this.mapProjectResource(res)).map((data) => this.add(data));
		if (ProjectStore.persisted && added.length > 0) await this.sdk.storage.bulkSet(this.tableName, added);
		const removedIds = Array.from(idsBeforeFetch).filter((id) => !seenIds.has(id) && this.data.get(id)?.syncId !== 0);
		if (ProjectStore.persisted && removedIds.length > 0) await this.sdk.storage.bulkRemove(this.tableName, removedIds);
		for (const id of removedIds) {
			this.data.delete(id);
			await this.clearLocalProjectAssignments(id);
		}
	}
	normalizeProjectName(name) {
		const trimmedName = name.trim();
		if (!trimmedName) throw new Error("Project name cannot be empty");
		return trimmedName;
	}
	removeProjectLocally(id) {
		runInAction(() => {
			this.data.delete(id);
		});
	}
	async persistProject(project) {
		if (!ProjectStore.persisted) return;
		await this.sdk.storage.set(this.tableName, project);
	}
	/** Clear cached references after a project disappears locally or through sync */
	async clearLocalProjectAssignments(projectId) {
		const [threadIds, meetingIds] = await Promise.all([
			this.sdk.stores.threads.clearProjectAssignmentsForProject(projectId),
			this.sdk.stores.meetings.clearProjectAssignmentsForProject(projectId),
			this.sdk.stores.drafts.clearProjectAssignmentsForProject(projectId)
		]);
		return {
			threadIds,
			meetingIds
		};
	}
	async flushPersistedProjectBatch(added, toPersistOnly) {
		if (!ProjectStore.persisted) return;
		if (added.length > 0) await this.sdk.storage.bulkSet(this.tableName, added);
		if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
	}
	async persistAndHydrateProjects(rows) {
		try {
			const toPersist = [];
			const toDelete = [];
			for (const dbProject of rows) {
				if (!dbProject.id) continue;
				if (dbProject.deleted_at) {
					toDelete.push(dbProject.id);
					continue;
				}
				toPersist.push(this.mapDbProject(dbProject));
			}
			if (toDelete.length > 0) {
				toDelete.forEach((id) => this.data.delete(id));
				if (ProjectStore.persisted) await this.sdk.storage.bulkRemove(this.tableName, toDelete);
				await Promise.all(toDelete.map((id) => this.clearLocalProjectAssignments(id)));
			}
			if (toPersist.length > 0) {
				const toHydrate = toPersist.filter((d) => this.data.has(d.id));
				const toPersistOnly = toPersist.filter((d) => !this.data.has(d.id));
				const addedProjects = runInAction(() => toHydrate.map((data) => this.add(data)));
				await this.flushPersistedProjectBatch(addedProjects, toPersistOnly);
			}
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error processing projects:" },
				context: "ProjectStore:persistAndHydrateProjects"
			});
			throw error;
		}
	}
	/**
	* Clear assignments for a project another renderer deleted from shared storage.
	*
	* One gap, deliberately left: a read returning no projects at all is treated as
	* a failed read by the base class, so deleting the very last project is not seen
	* here. Sync reports that within its next poll, exactly as it reports a deletion
	* made on another device, which every deletion also travels through.
	*/
	async runRefreshFromStorage() {
		const residentBefore = new Set(this.data.keys());
		await super.runRefreshFromStorage();
		const removedProjectIds = Array.from(residentBefore).filter((id) => !this.data.has(id));
		await Promise.all(removedProjectIds.map((id) => this.clearLocalProjectAssignments(id)));
		await this.refreshItemCounts();
	}
	/** A project created here is not in storage until its create is persisted */
	shouldRetainLocal(project) {
		return project.syncId === 0;
	}
	async createProject(name, options = {}) {
		const normalizedName = this.normalizeProjectName(name);
		const id = v4();
		const now = /* @__PURE__ */ new Date();
		const icon = assertProjectIcon(options.icon ?? null);
		const project = this.add({
			id,
			name: normalizedName,
			icon,
			syncId: 0,
			capabilities: OWNER_PROJECT_CAPABILITIES,
			createdAt: now,
			updatedAt: now
		});
		const payload = {
			id,
			name: normalizedName,
			icon
		};
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "create",
				modelId: id,
				payload,
				handlerOverride: {
					endpoint: this.apiEndpoint,
					method: "POST"
				}
			}, {
				strictPersist: true,
				waitForServerAck: options.waitForServerAck
			});
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "create",
				modelId: id,
				payload,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		await this.persistProject(project);
		return project;
	}
	async renameProject(id, name) {
		const normalizedName = this.normalizeProjectName(name);
		const project = this.getById(id);
		if (!project) throw new Error(`Project ${id} not found`);
		const previousName = project.name;
		runInAction(() => {
			project.name = normalizedName;
		});
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: { name: normalizedName },
				snapshot: { name: previousName },
				handlerOverride: {
					endpoint: `${this.apiEndpoint}/${id}`,
					method: "PATCH"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: id,
				payload: { name: normalizedName },
				snapshot: { name: previousName },
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		await this.persistProject(project);
		return project;
	}
	applyProjectUpdates(project, params, snapshot, payload) {
		const icon = params.icon !== void 0 ? assertProjectIcon(params.icon) : void 0;
		if (params.name !== void 0) {
			snapshot.name = project.name;
			project.name = this.normalizeProjectName(params.name);
			payload.name = project.name;
		}
		if (params.instructions !== void 0) {
			snapshot.instructions = project.instructions;
			project.instructions = params.instructions;
			payload.instructions = params.instructions;
		}
		if (icon !== void 0) {
			snapshot.icon = project.icon;
			project.icon = icon;
			payload.icon = icon;
		}
		if (params.starred !== void 0) {
			snapshot.starred = project.starred;
			project.starred = params.starred;
			payload.starred = params.starred;
		}
		if (params.limitChatContextToProject !== void 0) {
			snapshot.limitChatContextToProject = project.limitChatContextToProject;
			project.limitChatContextToProject = params.limitChatContextToProject;
			payload.limit_chat_context_to_project = params.limitChatContextToProject;
		}
		if (params.disableScreenContextSearch !== void 0) {
			snapshot.disableScreenContextSearch = project.disableScreenContextSearch;
			project.disableScreenContextSearch = params.disableScreenContextSearch;
			payload.disable_screen_context_search = params.disableScreenContextSearch;
		}
		project.updatedAt = /* @__PURE__ */ new Date();
	}
	async updateProject(project, params) {
		const snapshot = {};
		const payload = {};
		runInAction(() => {
			this.applyProjectUpdates(project, params, snapshot, payload);
		});
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: project.id,
				payload,
				snapshot,
				handlerOverride: {
					endpoint: `${this.apiEndpoint}/${project.id}`,
					method: "PATCH"
				}
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "update",
				modelId: project.id,
				payload,
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		await this.persistProject(project);
	}
	async deleteProject(id) {
		const project = this.getById(id);
		if (!project) return;
		const { threadIds, meetingIds } = await this.clearLocalProjectAssignments(id);
		const snapshot = {
			project: project.toJSON(),
			assignedThreadIds: threadIds,
			assignedMeetingIds: meetingIds
		};
		this.removeProjectLocally(id);
		const payload = { id };
		const txId = v4();
		try {
			await this.sdk.transactionQueue.enqueue({
				id: txId,
				table: this.tableName,
				op: "delete",
				modelId: id,
				payload,
				snapshot
			}, { strictPersist: true });
		} catch (enqueueError) {
			await this.rollback({
				id: txId,
				table: this.tableName,
				op: "delete",
				modelId: id,
				payload,
				snapshot,
				state: "pending",
				attempt: 0,
				createdAt: Date.now()
			});
			throw enqueueError;
		}
		if (ProjectStore.persisted) await this.sdk.storage.remove(this.tableName, id);
	}
	rehydrateProjectSnapshot(snap) {
		const out = {};
		for (const key of [
			"createdAt",
			"updatedAt",
			"deletedAt"
		]) if (key in snap) {
			const v = snap[key];
			out[key] = typeof v === "string" ? parseDate(v) : v;
		}
		for (const key of Object.keys(snap)) if (!(key in out)) out[key] = snap[key];
		return out;
	}
	async rollback(tx) {
		switch (tx.op) {
			case "create":
				this.removeProjectLocally(tx.modelId);
				if (ProjectStore.persisted) await this.sdk.storage.remove(this.tableName, tx.modelId);
				return;
			case "update": {
				const project = this.getById(tx.modelId);
				if (!project || !tx.snapshot) return;
				runInAction(() => Object.assign(project, this.rehydrateProjectSnapshot(tx.snapshot)));
				if (ProjectStore.persisted) await this.sdk.storage.set(this.tableName, project);
				return;
			}
			case "delete": {
				if (!tx.snapshot) return;
				const snap = tx.snapshot;
				const projectData = this.rehydrateProjectSnapshot(snap.project);
				const restored = this.add(projectData);
				if (ProjectStore.persisted) await this.sdk.storage.set(this.tableName, restored);
				await this.sdk.stores.threads.restoreProjectAssignments(snap.assignedThreadIds ?? [], tx.modelId);
				await this.sdk.stores.meetings.restoreProjectAssignments(snap.assignedMeetingIds ?? [], tx.modelId);
				return;
			}
		}
	}
};
//#endregion
//#region src/core/sync/stores/report-store.ts
const REPORT_SUMMARIES_ENDPOINT = "report_summaries";
/** Preview text in the shape the list endpoints used to send, so `parseStatus` keeps working on it */
const summaryPreviewText = (summary) => {
	if (summary.status === "generating") return "GENERATING";
	if (summary.status === "no_data") return "NO DATA";
	return summary.summary;
};
/** Whether `report` sorts at or before the page's last row, so the page would have listed it */
const isCoveredByPage = (report, page) => {
	const lastRow = page.items.at(-1);
	if (page.next_cursor === null || !lastRow) return true;
	const lastScheduledAt = parseDate(lastRow.scheduled_at).getTime();
	const scheduledAt = report.scheduledAt.getTime();
	return scheduledAt > lastScheduledAt || scheduledAt === lastScheduledAt && report.referenceId >= lastRow.id;
};
const toWaitingDevice = (device) => ({
	deviceSlug: device.device_slug,
	platform: device.platform,
	waitingSince: parseDate(device.waiting_since),
	waitsUntil: parseDate(device.waits_until)
});
const toRanWithoutDevice = (device) => ({
	deviceSlug: device.device_slug,
	platform: device.platform
});
const toRunReachOut = (reachOut) => ({
	reason: reachOut.reason,
	answered: reachOut.answered,
	deadline: parseDate(reachOut.deadline),
	waiting: reachOut.waiting && {
		kind: reachOut.waiting.kind,
		reason: reachOut.waiting.reason,
		since: parseDate(reachOut.waiting.since)
	}
});
const waitingDeviceKey = (device) => [
	device.deviceSlug,
	device.platform,
	device.waitingSince.getTime(),
	device.waitsUntil.getTime()
].join("|");
const ranWithoutDeviceKey = (device) => `${device.deviceSlug}|${device.platform}`;
const sameDevices = (incoming, held, keyOf) => incoming.length === held.length && incoming.every((device, index) => keyOf(device) === keyOf(held[index]));
const applyDevices = (report, waitingOn, ranWithout) => {
	if (waitingOn && !sameDevices(waitingOn, report.waitingOn, waitingDeviceKey)) report.waitingOn = waitingOn;
	if (ranWithout && !sameDevices(ranWithout, report.ranWithout, ranWithoutDeviceKey)) report.ranWithout = ranWithout;
};
var ReportStore = class extends Store {
	static persisted = false;
	static loadStrategy = "lazy";
	ModelClass = Report;
	/** Template IDs fetched at least once. Distinguishes "loading" from "none exist". */
	fetchedTemplateIds = /* @__PURE__ */ new Set();
	/** Template IDs whose last fetch failed. Distinguishes an error from an empty list. */
	failedTemplateIds = /* @__PURE__ */ new Set();
	/** Pending/error bookkeeping keyed by operation + referenceId. Its Set/Map
	* are already `observable.set`/`observable.map`, so it must not be listed in
	* `makeObservable` below. */
	operationTracker = createOperationTracker();
	detailFetches = /* @__PURE__ */ new Map();
	/** Per routine: the cursor for its next older page, or null once its history is fully loaded */
	routineRunCursors = observable.map();
	/** Reports added or deleted here, so an older list response cannot undo them. */
	recentMutations = createLocalMutationLog();
	/**
	* Every report in `data` by its `referenceId`, which a report never changes.
	* Updated one entry at a time, on each add and remove. Not a MobX computed
	* like `reportsByChatId`: `fetchReports` looks a report up before each add,
	* and a computed would be rebuilt in full on every one of those lookups.
	*/
	reportsByReferenceId = observable.map();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("reports");
		observe(this.data, (change) => {
			if (change.type !== "add") this.reportsByReferenceId.delete(change.oldValue.referenceId);
			if (change.type !== "delete") this.reportsByReferenceId.set(change.newValue.referenceId, change.newValue);
		});
		makeObservable(this, {
			fetchReports: action.bound,
			fetchReportsByTemplateId: action.bound,
			addApiReport: action.bound,
			markReportViewed: action.bound,
			deleteReport: action.bound,
			submitFeedback: action.bound,
			continueWithoutDevice: action.bound,
			skipWaitingDevice: action.bound,
			finishWithoutMe: action.bound,
			persistAndHydrateReports: action.bound,
			orderedReports: computed,
			latestByTemplateId: computed,
			reportsByChatId: computed,
			fetchAll: action.bound,
			fetchedTemplateIds: observable,
			failedTemplateIds: observable
		});
	}
	isOperationPending(operation, referenceId) {
		return this.operationTracker.isOperationPending(operation, referenceId);
	}
	getOperationError(operation, referenceId) {
		return this.operationTracker.getOperationError(operation, referenceId);
	}
	withOperation(operation, referenceId, fn) {
		return this.operationTracker.withOperation(operation, referenceId, fn);
	}
	/** Logs `message` and rethrows, so every request failure reads the same */
	failed(context, message, error) {
		this.sdk.logger.error(error instanceof Error ? error : new Error(message), {
			metadata: { message: `${message}.` },
			context: `ReportStore:${context}`
		});
		throw error;
	}
	/**
	* Logout clears every store. The base only empties `data`, so the per-template
	* read state has to be dropped here too, or the next account inherits it.
	*/
	clear() {
		this.fetchedTemplateIds.clear();
		this.failedTemplateIds.clear();
		this.detailFetches.clear();
		this.routineRunCursors.clear();
		this.operationTracker.reset();
		this.recentMutations.reset();
		super.clear();
	}
	/** For callers that already hold a fresh report payload, such as a manual run */
	addApiReport(apiReport) {
		this.recentMutations.record(apiReport.id);
		return runInAction(() => this.apiReportToReport(apiReport, "detail"));
	}
	hasFetchedTemplate(templateId) {
		return this.fetchedTemplateIds.has(templateId);
	}
	hasTemplateError(templateId) {
		return this.failedTemplateIds.has(templateId);
	}
	/** Reports are not persisted, so `fetchAll` always hits the server. */
	async fetchAll() {
		return this.fetchReports();
	}
	getByReferenceId(referenceId) {
		return this.reportsByReferenceId.get(referenceId);
	}
	/** A MobX computed, because a report can be added before it has a chat */
	get reportsByChatId() {
		const byChatId = /* @__PURE__ */ new Map();
		for (const report of this.data.values()) if (report.chatId && !report.deletedAt && !byChatId.has(report.chatId)) byChatId.set(report.chatId, report);
		return byChatId;
	}
	getByChatId(chatId) {
		return this.reportsByChatId.get(chatId);
	}
	async fetchByReferenceId(referenceId) {
		const versionKey = `${referenceId}:${this.getByReferenceId(referenceId)?.updatedAt.getTime() ?? 0}`;
		const inFlight = this.detailFetches.get(versionKey);
		if (inFlight) return inFlight;
		const detailFetch = this.requestReportDetail(referenceId).finally(() => {
			this.detailFetches.delete(versionKey);
		});
		this.detailFetches.set(versionKey, detailFetch);
		return detailFetch;
	}
	async requestReportDetail(referenceId) {
		const generation = this.generation;
		try {
			const apiReport = await this.sdk.apiClient.fetch(`reports/${referenceId}`);
			if (!apiReport?.id) return void 0;
			if (!this.isCurrentGeneration(generation)) return void 0;
			return runInAction(() => this.apiReportToReport(apiReport, "detail"));
		} catch (error) {
			this.sdk.logger.warn(`Failed to fetch report ${referenceId}`, {
				context: "ReportStore:requestReportDetail",
				metadata: { error: String(error) }
			});
			return;
		}
	}
	getReportsByTemplateId = computedFn(function(templateId) {
		return Array.from(this.data.values()).filter((report) => report.reportTemplateId === templateId).sort((a, b) => b.scheduledAt.getTime() - a.scheduledAt.getTime());
	});
	/**
	* Loads the newest page of runs across all routines, plus each routine's
	* latest run, which a busy routine can push off that page.
	*/
	async fetchReports() {
		runInAction(() => {
			this.isFetching = true;
		});
		const generation = this.generation;
		try {
			const [newestPage, latestPerRoutine] = await Promise.all([this.requestSummaryPage(REPORT_SUMMARIES_ENDPOINT), this.requestSummaryPage(`${REPORT_SUMMARIES_ENDPOINT}?latest_per_routine=true`)]);
			if (!this.isCurrentGeneration(generation)) return [];
			const newestIds = new Set(newestPage.items.map((summary) => summary.id));
			const summaries = [...newestPage.items, ...latestPerRoutine.items.filter((summary) => !newestIds.has(summary.id))];
			return runInAction(() => {
				const reports = this.applySummaries(summaries);
				const routinesWithRuns = new Set(latestPerRoutine.items.map((summary) => summary.report_template_id));
				this.pruneAbsentSummaries(summaries, (report) => !this.sdk.stores.routines.getByReferenceId(report.reportTemplateId)?.isSystem && (!routinesWithRuns.has(report.reportTemplateId) || isCoveredByPage(report, newestPage)));
				return reports;
			});
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error fetching reports" },
				context: "ReportStore:fetchReports"
			});
			throw error;
		} finally {
			runInAction(() => {
				this.isFetching = false;
			});
		}
	}
	hasMoreRoutineRuns(templateId) {
		return typeof this.routineRunCursors.get(templateId) === "string";
	}
	/** Loads a routine's newest runs, and drops its runs that page shows are gone */
	loadRoutineRuns(templateId) {
		return this.loadRoutineRunsPage(templateId, null);
	}
	/** Loads the next older page of a routine's runs */
	async loadMoreRoutineRuns(templateId) {
		const cursor = this.routineRunCursors.get(templateId);
		if (typeof cursor !== "string") return;
		return this.loadRoutineRunsPage(templateId, cursor);
	}
	async loadRoutineRunsPage(templateId, cursor) {
		if (this.isOperationPending("loadRuns", templateId)) return;
		const generation = this.generation;
		const params = new URLSearchParams({ report_template_id: String(templateId) });
		if (cursor) params.set("cursor", cursor);
		return this.withOperation("loadRuns", templateId, async () => {
			try {
				const page = await this.requestSummaryPage(`${REPORT_SUMMARIES_ENDPOINT}?${params}`);
				if (!this.isCurrentGeneration(generation)) return;
				runInAction(() => {
					this.applySummaries(page.items);
					if (cursor === null) this.pruneAbsentSummaries(page.items, (report) => report.reportTemplateId === templateId && isCoveredByPage(report, page));
					this.routineRunCursors.set(templateId, page.next_cursor);
				});
			} catch (error) {
				this.failed("loadRoutineRuns", "Failed to load routine runs", error);
			}
		});
	}
	async requestSummaryPage(path) {
		const page = await this.sdk.apiClient.fetch(path);
		return {
			items: expectList(page?.items, "reports"),
			next_cursor: page.next_cursor ?? null
		};
	}
	applySummaries(summaries) {
		const reports = [];
		for (const summary of summaries) {
			const existingReport = this.getByReferenceId(summary.id);
			if (!existingReport && this.recentMutations.isRecent(summary.id)) continue;
			const apiReport = {
				...summary,
				title: existingReport?.title ?? "",
				date: existingReport?.date.toISOString() ?? summary.scheduled_at,
				text: summaryPreviewText(summary),
				run_context: summary.run_context
			};
			reports.push(this.apiReportToReport(apiReport, "preview"));
		}
		return reports;
	}
	/** Removes stored runs in scope that the response left out; the server deletes runs outright */
	pruneAbsentSummaries(summaries, isInScope) {
		const presentIds = new Set(summaries.map((summary) => summary.id));
		for (const report of Array.from(this.data.values())) {
			if (presentIds.has(report.referenceId)) continue;
			if (this.recentMutations.isRecent(report.referenceId)) continue;
			if (isInScope(report)) this.remove(report);
		}
	}
	async fetchReportsByTemplateId(templateId) {
		runInAction(() => {
			this.isFetching = true;
			this.failedTemplateIds.delete(templateId);
		});
		const generation = this.generation;
		try {
			const apiReports = expectList(await this.sdk.apiClient.fetch(`report_templates/${templateId}/reports`), "routine runs");
			if (!this.isCurrentGeneration(generation)) return [];
			const reports = [];
			runInAction(() => {
				for (const apiReport of apiReports) {
					const report = this.apiReportToReport(apiReport, "detail");
					reports.push(report);
				}
			});
			return reports;
		} catch (error) {
			runInAction(() => {
				this.failedTemplateIds.add(templateId);
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Error fetching reports by template" },
				context: "ReportStore:fetchReportsByTemplateId"
			});
			throw error;
		} finally {
			runInAction(() => {
				this.isFetching = false;
				this.fetchedTemplateIds.add(templateId);
			});
		}
	}
	async markReportViewed(referenceId) {
		const report = this.getByReferenceId(referenceId);
		const previousLastViewed = report?.lastViewed ?? null;
		if (report) runInAction(() => {
			report.lastViewed = /* @__PURE__ */ new Date();
		});
		try {
			if (!await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${referenceId}/seen`, { method: "POST" })) throw new Error("Failed to mark report as viewed");
		} catch (error) {
			if (report) runInAction(() => {
				report.lastViewed = previousLastViewed;
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Error marking report as viewed" },
				context: "ReportStore:markReportViewed"
			});
			throw error;
		}
	}
	async deleteReport(referenceId) {
		const report = this.getByReferenceId(referenceId);
		if (!report) throw new Error(`Report ${referenceId} not found`);
		return this.withOperation("delete", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${referenceId}`, { method: "DELETE" });
				runInAction(() => {
					this.remove(report);
				});
				this.recentMutations.record(referenceId);
				this.operationTracker.clearOperationErrors(referenceId);
			} catch (error) {
				this.failed("deleteReport", "Failed to delete report", error);
			}
		});
	}
	/** Stays pending until the refreshed reports replace the waiting run */
	async continueWithoutDevice(referenceId) {
		return this.postContinueWithoutDevice("continueWithoutDevice", referenceId, "Failed to continue the run without the device");
	}
	/**
	* Skips one device; the run keeps waiting for any others. Stays pending
	* until the refreshed reports arrive
	*/
	async skipWaitingDevice(referenceId, deviceSlug) {
		return this.postContinueWithoutDevice("skipWaitingDevice", referenceId, "Failed to skip the waiting device", deviceSlug);
	}
	async postContinueWithoutDevice(operation, referenceId, failureMessage, deviceSlug) {
		return this.withOperation(operation, referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${referenceId}/continue_without_device`, deviceSlug === void 0 ? { method: "POST" } : {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ device_slug: deviceSlug })
				});
			} catch (error) {
				this.failed(operation, failureMessage, error);
			}
			await this.fetchReports().catch(() => void 0);
		});
	}
	/**
	* Ends a run that waits on its user with what it has. `stopAsking` also stops
	* the routine reaching out from now on. Stays pending until the refreshed
	* reports and routines arrive
	*/
	async finishWithoutMe(referenceId, { stopAsking = false } = {}) {
		return this.withOperation("finishWithoutMe", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`${this.apiEndpoint}/${referenceId}/finish_without_me`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ stop_asking: stopAsking })
				});
			} catch (error) {
				this.failed("finishWithoutMe", "Failed to finish the run without the user", error);
			}
			await Promise.all([this.fetchReports().catch(() => void 0), this.sdk.stores.routines.fetchRoutines().catch(() => void 0)]);
		});
	}
	async submitFeedback(referenceId, direction, reasoning) {
		return this.withOperation("feedback", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch("vote_entity", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						entity_type: "routine_report",
						entity_id: String(referenceId),
						direction,
						reasoning
					})
				});
			} catch (error) {
				this.failed("submitFeedback", "Failed to submit report feedback", error);
			}
		});
	}
	async persistAndHydrateReports(dbReports) {
		try {
			runInAction(() => {
				for (const dbReport of dbReports) {
					if (!dbReport.id) continue;
					if (dbReport.deleted_at) {
						const existingReport = this.getByReferenceId(dbReport.id);
						if (existingReport) this.remove(existingReport);
						continue;
					}
					this.apiReportToReport(dbReport, "detail");
				}
			});
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error processing reports" },
				context: "ReportStore:persistAndHydrateReports"
			});
			throw error;
		}
	}
	get orderedReports() {
		return Array.from(this.data.values()).filter((report) => !report.deletedAt).sort((a, b) => b.scheduledAt.getTime() - a.scheduledAt.getTime());
	}
	get latestByTemplateId() {
		const latest = /* @__PURE__ */ new Map();
		for (const report of this.orderedReports) if (!latest.has(report.reportTemplateId)) latest.set(report.reportTemplateId, report);
		return latest;
	}
	apiReportToReport(apiReport, source) {
		const existingReport = this.getByReferenceId(apiReport.id);
		const updatedAt = parseDate(apiReport.updated_at);
		const fields = {
			title: apiReport.title,
			date: parseDate(apiReport.date),
			scheduledAt: parseDate(apiReport.scheduled_at),
			reportTemplateId: apiReport.report_template_id,
			chatId: apiReport.chat_id ?? null,
			lastViewed: apiReport.last_viewed ? parseDate(apiReport.last_viewed) : null,
			updatedAt,
			artifactFileIds: apiReport.artifact_file_ids ?? [],
			...apiReport.waiting_on && { waitingOn: apiReport.waiting_on.map(toWaitingDevice) },
			...apiReport.ran_without && { ranWithout: apiReport.ran_without.map(toRanWithoutDevice) },
			...apiReport.reach_out !== void 0 && { reachOut: apiReport.reach_out && toRunReachOut(apiReport.reach_out) },
			...apiReport.backend !== void 0 && { backend: parseBackendName(apiReport.backend) },
			...apiReport.run_context !== void 0 && { runContext: apiReport.run_context }
		};
		if (existingReport) return this.mergeApiReport(existingReport, apiReport, fields, source, updatedAt);
		return this.add({
			id: v4(),
			referenceId: apiReport.id,
			createdAt: parseDate(apiReport.created_at),
			text: apiReport.text,
			detailUpdatedAt: source === "detail" ? updatedAt : null,
			...fields
		});
	}
	mergeApiReport(existingReport, apiReport, fields, source, updatedAt) {
		if (updatedAt.getTime() < existingReport.updatedAt.getTime()) return existingReport;
		const incoming = existingReport.parseStatus(apiReport.text?.trim() ?? "");
		const held = existingReport.parseStatus(existingReport.text?.trim() ?? "");
		if (incoming === "GENERATING" && held !== "GENERATING") return existingReport;
		this.applyApiReportFields(existingReport, apiReport, fields, source);
		return existingReport;
	}
	applyApiReportFields(existingReport, apiReport, fields, source) {
		const { lastViewed, updatedAt } = fields;
		const staleAgainstDetail = source === "preview" && existingReport.detailUpdatedAt !== null && updatedAt.getTime() <= existingReport.detailUpdatedAt.getTime();
		const { lastViewed: _incomingLastViewed, date, scheduledAt, updatedAt: incomingUpdatedAt, artifactFileIds, waitingOn, ranWithout, reachOut, ...restFields } = fields;
		Object.assign(existingReport, restFields);
		applyDevices(existingReport, waitingOn, ranWithout);
		if (reachOut !== void 0 && !isEqual(reachOut, existingReport.reachOut)) existingReport.reachOut = reachOut;
		if (date.getTime() !== existingReport.date.getTime()) existingReport.date = date;
		if (scheduledAt.getTime() !== existingReport.scheduledAt.getTime()) existingReport.scheduledAt = scheduledAt;
		if (incomingUpdatedAt.getTime() !== existingReport.updatedAt.getTime()) existingReport.updatedAt = incomingUpdatedAt;
		if (artifactFileIds.length !== existingReport.artifactFileIds.length || artifactFileIds.some((fileId, index) => fileId !== existingReport.artifactFileIds[index])) existingReport.artifactFileIds = artifactFileIds;
		if (existingReport.lastViewed === null || lastViewed !== null && lastViewed.getTime() > existingReport.lastViewed.getTime()) existingReport.lastViewed = lastViewed;
		if (!staleAgainstDetail) {
			existingReport.text = apiReport.text;
			existingReport.detailUpdatedAt = source === "detail" ? updatedAt : null;
		}
	}
};
//#endregion
//#region src/core/sync/stores/routine-draft-store.ts
const getSessionId = (ownerId, draftKey) => JSON.stringify([ownerId, draftKey]);
const cloneDraft = (draft) => ({
	...draft,
	icon: draft.icon ? { ...draft.icon } : null,
	weekDays: [...draft.weekDays]
});
/**
* Holds unsaved routine forms in memory. Callers provide an owner id, such as
* a desktop tab id.
*/
var RoutineDraftStore = class extends Store {
	static persisted = false;
	static loadStrategy = "localOnly";
	ModelClass = RoutineDraft;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			initializeDraft: action,
			setDraft: action,
			updateDraft: action,
			setCustomBackup: action,
			clearCustomBackup: action,
			clearDraft: action,
			clearOwner: action,
			clearRoutine: action,
			restoreSessions: action
		});
	}
	getDraft = computedFn(function(ownerId, draftKey) {
		const draft = this.data.get(getSessionId(ownerId, draftKey))?.draft;
		return draft ? cloneDraft(draft) : null;
	});
	/**
	* A kept draft is rebased rather than replaced: the routine can have moved on
	* since — its icon has editors outside this form — and a field nobody touched
	* must not submit a stale value back over it.
	*/
	initializeDraft(ownerId, draftKey, draft) {
		const nextDraft = RoutineFormDraftSchema.parse(draft);
		const existing = this.data.get(getSessionId(ownerId, draftKey));
		if (existing?.baselineDraft) this.setDraft(ownerId, draftKey, rebaseRoutineFormDraft(existing.draft, existing.baselineDraft, nextDraft));
		else if (!existing) this.setDraft(ownerId, draftKey, nextDraft);
		const session = this.data.get(getSessionId(ownerId, draftKey));
		if (session) session.baselineDraft = nextDraft;
	}
	setDraft(ownerId, draftKey, draft) {
		const id = getSessionId(ownerId, draftKey);
		const currentSession = this.data.get(id);
		this.add({
			id,
			ownerId,
			draftKey,
			draft: RoutineFormDraftSchema.parse(draft),
			baselineDraft: currentSession?.baselineDraft,
			customBackup: currentSession?.customBackup
		});
	}
	/**
	* Whether the draft moved off the values it was initialized with — a form that
	* writes one on mount holds a draft whether or not anyone typed. A session set
	* without `initializeDraft` has no baseline and counts as edited.
	*/
	isDraftEdited = computedFn(function(ownerId, draftKey) {
		const session = this.data.get(getSessionId(ownerId, draftKey));
		if (!session) return false;
		return !session.baselineDraft || !isSameRoutineFormDraft(session.draft, session.baselineDraft);
	});
	updateDraft(ownerId, draftKey, initialDraft, updates) {
		this.initializeDraft(ownerId, draftKey, initialDraft);
		const draft = this.getDraft(ownerId, draftKey) ?? initialDraft;
		this.setDraft(ownerId, draftKey, {
			...draft,
			...updates
		});
	}
	getCustomBackup = computedFn(function(ownerId) {
		const backup = this.data.get(getSessionId(ownerId, "create"))?.customBackup;
		return backup ? cloneDraft(backup) : null;
	});
	setCustomBackup(ownerId, draft) {
		const session = this.data.get(getSessionId(ownerId, "create"));
		if (!session) return;
		session.customBackup = RoutineFormDraftSchema.parse(draft);
	}
	clearCustomBackup(ownerId) {
		const session = this.data.get(getSessionId(ownerId, "create"));
		if (session) session.customBackup = void 0;
	}
	clearDraft(ownerId, draftKey) {
		const session = this.data.get(getSessionId(ownerId, draftKey));
		if (session) this.remove(session);
	}
	clearOwner(ownerId) {
		for (const session of this.data.values()) if (session.ownerId === ownerId) this.remove(session);
	}
	clearRoutine(routineId) {
		for (const session of this.data.values()) if (session.draftKey === routineId) this.remove(session);
	}
	exportSessions() {
		return Array.from(this.data.values()).map((session) => ({
			ownerId: session.ownerId,
			draftKey: session.draftKey,
			draft: cloneDraft(session.draft),
			baselineDraft: session.baselineDraft && cloneDraft(session.baselineDraft),
			customBackup: session.customBackup && cloneDraft(session.customBackup)
		}));
	}
	restoreSessions(sessions) {
		for (const session of sessions) this.add({
			id: getSessionId(session.ownerId, session.draftKey),
			...session
		});
	}
};
//#endregion
//#region src/core/sync/types/routine-suggestions.ts
const GeneratedRoutineSuggestionApiSchema = z.object({
	id: z.number(),
	title: z.string(),
	prompt: z.string(),
	recurrence_rule: z.string(),
	schedule: z.string(),
	rationale: z.string(),
	integrations: z.array(z.string()).default([]),
	created_at: z.string()
});
function parseGeneratedRoutineSuggestions(data) {
	return z.array(GeneratedRoutineSuggestionApiSchema).parse(data).map((item) => ({
		id: item.id,
		title: item.title,
		prompt: item.prompt,
		recurrenceRule: item.recurrence_rule,
		schedule: item.schedule,
		rationale: item.rationale,
		integrations: item.integrations,
		createdAt: item.created_at
	}));
}
//#endregion
//#region src/core/sync/stores/routine-store.ts
/** `v=3` adds the tabs and team-template fields; older shapes still parse. */
const ROUTINE_LIBRARY_ENDPOINT = "routine_library?v=3";
const CHANNEL_CONNECTION_ENDPOINT = {
	imessage: "imessage/connection",
	whatsapp_business: "whatsapp/connection"
};
const ChannelConnectionResponseSchema = z.object({
	identifier: z.string(),
	has_active_conversation: z.boolean().optional().default(false)
});
const ShareRoutineWithTeamResponseSchema = z.object({ team_share: z.object({
	id: z.string(),
	is_stale: z.boolean()
}) });
const triggerFromApi = (trigger) => ({
	...routineTriggerInputFromApi(trigger),
	id: trigger.id,
	isEnabled: trigger.is_enabled,
	lastFiredAt: trigger.last_fired_at ? parseDate(trigger.last_fired_at) : null
});
const toRoutineWireBody = (data) => ({
	title: data.title,
	icon: data.icon,
	prompt: data.prompt,
	recurrence_type: data.recurrenceType,
	recurrence_rule: data.recurrenceRule,
	notifications_enabled: data.notificationsEnabled,
	email_notifications_enabled: data.emailNotificationsEnabled,
	delivery_channel: data.deliveryChannel,
	auto_pause_enabled: data.autoPauseEnabled,
	may_reach_out: data.mayReachOut,
	agent_mode: data.agentMode,
	kind: data.kind,
	triggers: data.triggers?.map(routineTriggerInputToApi)
});
const WEBHOOK_ENDPOINTS_PATH = "routine_webhook_endpoints";
const toWebhookEndpoint = (endpoint) => ({
	id: endpoint.id,
	name: endpoint.name,
	description: endpoint.description ?? "",
	url: endpoint.url,
	payloadFields: endpoint.payload_fields ?? null,
	createdAt: parseDate(endpoint.created_at),
	teamId: endpoint.team_id ?? null,
	canManage: endpoint.can_manage ?? true,
	hasSecret: endpoint.has_secret ?? true
});
const permissionsFromApi = (permissions) => ({
	canEditDefinition: permissions.can_edit_definition,
	canChangeAgentMode: permissions.can_change_agent_mode,
	canChangeDeliverySettings: permissions.can_change_delivery_settings,
	canPause: permissions.can_pause,
	canResume: permissions.can_resume,
	canRun: permissions.can_run,
	canDelete: permissions.can_delete,
	canShare: permissions.can_share,
	canImprove: permissions.can_improve
});
/** The two POSTs that start a run and answer with its report */
const GENERATION_REQUESTS = {
	generate: {
		endpoint: "generate",
		context: "generateReport",
		label: "Generate report"
	},
	restartWaitingRun: {
		endpoint: "restart_waiting_run",
		context: "restartWaitingRun",
		label: "Restart waiting run"
	}
};
var RoutineStore = class extends Store {
	static persisted = false;
	static loadStrategy = "ephemeral";
	ModelClass = Routine;
	/** Observable so a remounted action still renders the run it started. */
	manualGenerations = /* @__PURE__ */ new Map();
	/** True when the last fetch failed. Distinguishes an error from an empty list. */
	loadError = false;
	/**
	* True once the server's list has been applied since the store was last
	* cleared, so a routine missing from the store is known to be deleted.
	* `isLoaded` cannot tell this: local hydration and failed fetches set it too.
	*/
	hasLoadedFromServer = false;
	routineLibrary = [];
	routineLibraryTabs = [];
	routineSuggestions = [];
	deliveryChannelConnections = /* @__PURE__ */ new Map();
	webhookEndpoints = [];
	webhookPresets = [];
	/** The update in flight per routine, so the next one waits for it. */
	updateQueues = /* @__PURE__ */ new Map();
	/** Star requests per routine, sent in order, with the last star the server confirmed. */
	starQueues = /* @__PURE__ */ new Map();
	/** Pending/error bookkeeping keyed by operation (+ optional referenceId).
	* Its Set/Map are already `observable.set`/`observable.map`, so it must not
	* be listed in `makeObservable` below. */
	operationTracker = createOperationTracker();
	/** Routines created or deleted here, so an older list response cannot undo them. */
	recentMutations = createLocalMutationLog();
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata();
		makeObservable(this, {
			loadError: observable,
			hasLoadedFromServer: observable,
			routineLibrary: observable,
			routineLibraryTabs: observable,
			routineSuggestions: observable,
			deliveryChannelConnections: observable,
			webhookEndpoints: observable,
			webhookPresets: observable,
			starredRoutines: computed,
			userRoutines: computed,
			fetchRoutines: action.bound,
			createRoutine: action.bound,
			updateRoutine: action.bound,
			generateReport: action.bound,
			restartWaitingRun: action.bound,
			manualGenerations: observable,
			deleteRoutine: action.bound,
			pauseRoutine: action.bound,
			unpauseRoutine: action.bound,
			setRoutineStarred: action.bound,
			fetchRoutineLibrary: action.bound,
			fetchRoutineSuggestions: action.bound,
			dismissRoutineSuggestion: action.bound,
			shareRoutineWithTeam: action.bound,
			removeTeamRoutineTemplate: action.bound,
			fetchDeliveryChannelConnections: action.bound,
			fetchWebhookEndpoints: action.bound,
			fetchWebhookPresets: action.bound,
			createWebhookEndpoint: action.bound,
			rotateWebhookEndpointSecret: action.bound,
			setWebhookEndpointSecret: action.bound,
			editWebhookEndpoint: action.bound,
			deleteWebhookEndpoint: action.bound,
			persistAndHydrateRoutines: action.bound
		});
	}
	/**
	* Logout clears every store. The base only empties `data`, so everything this
	* store holds alongside it has to be dropped here too: the team library, the
	* generated suggestions, the delivery-channel identifiers and the webhook
	* endpoints all belong to the account signing out.
	*/
	clear() {
		this.routineLibrary = [];
		this.routineLibraryTabs = [];
		this.routineSuggestions = [];
		this.deliveryChannelConnections.clear();
		this.webhookEndpoints = [];
		this.manualGenerations.clear();
		this.updateQueues.clear();
		this.starQueues.clear();
		this.operationTracker.reset();
		this.recentMutations.reset();
		this.loadError = false;
		this.hasLoadedFromServer = false;
		super.clear();
	}
	get starredRoutines() {
		return this.userRoutines.filter((routine) => routine.starred);
	}
	/** The user's own routines, without the ones Littlebird runs for its own work */
	get userRoutines() {
		return this.orderedData.filter((routine) => !routine.isSystem);
	}
	getByReferenceId = computedFn(function(referenceId) {
		for (const routine of this.data.values()) if (routine.referenceId === referenceId) return routine;
	});
	isOperationPending(operation, referenceId) {
		return this.operationTracker.isOperationPending(operation, referenceId);
	}
	getOperationError(operation, referenceId) {
		return this.operationTracker.getOperationError(operation, referenceId);
	}
	withOperation(operation, referenceId, fn) {
		return this.operationTracker.withOperation(operation, referenceId, fn);
	}
	/** Logs `message` and rethrows, so every request failure reads the same */
	failed(context, message, error) {
		this.sdk.logger.error(error instanceof Error ? error : new Error(message), {
			metadata: { message: `${message}.` },
			context: `RoutineStore:${context}`
		});
		throw error;
	}
	async fetchRoutines() {
		runInAction(() => {
			this.isFetching = true;
			this.loadError = false;
		});
		const generation = this.generation;
		try {
			const apiRoutines = expectList(await this.sdk.apiClient.fetch("report_templates"), "routines");
			if (!this.isCurrentGeneration(generation)) return [];
			const routines = [];
			runInAction(() => {
				for (const apiRoutine of apiRoutines) {
					if (!this.getByReferenceId(apiRoutine.id) && this.recentMutations.isRecent(apiRoutine.id)) continue;
					const routine = this.apiRoutineToRoutine(apiRoutine);
					routines.push(routine);
				}
				pruneAbsentRows(this, new Set(apiRoutines.map((apiRoutine) => apiRoutine.id)), (referenceId) => this.recentMutations.isRecent(referenceId));
				this.hasLoadedFromServer = true;
			});
			return routines;
		} catch (error) {
			runInAction(() => {
				this.loadError = true;
			});
			this.sdk.logger.error(error, {
				metadata: { message: "Error fetching routines" },
				context: "RoutineStore:fetchRoutines"
			});
			throw error;
		} finally {
			runInAction(() => {
				this.isFetching = false;
				this.isLoaded = true;
			});
		}
	}
	async createRoutine(data) {
		return this.withOperation("create", void 0, async () => {
			const generation = this.generation;
			try {
				const response = await this.sdk.apiClient.fetch("report_templates", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(toRoutineWireBody(data))
				});
				if (!response?.report_template) throw new Error("Create routine response is missing the routine");
				if (!this.isCurrentGeneration(generation)) throw new Error("Signed out before the routine was created");
				let routine;
				runInAction(() => {
					routine = this.apiRoutineToRoutine(response.report_template);
				});
				this.recentMutations.record(routine.referenceId);
				return {
					routine,
					initialReport: response.initial_report ?? null,
					hasReachedFreeLimit: response.has_reached_free_limit ?? false
				};
			} catch (error) {
				this.failed("createRoutine", "Failed to create routine", error);
			}
		});
	}
	/**
	* Writes `routineLibrary` / `routineLibraryTabs` and returns the parsed
	* response.
	*/
	async fetchRoutineLibrary() {
		return this.withOperation("library", void 0, async () => {
			const generation = this.generation;
			try {
				const { items, tabs, droppedCount } = parseRoutineLibraryResponse(await this.sdk.apiClient.fetch(ROUTINE_LIBRARY_ENDPOINT));
				if (droppedCount > 0) this.sdk.logger.warn(`Dropped ${droppedCount} routine library entries the schema did not recognize`, { context: "RoutineStore:fetchRoutineLibrary" });
				if (!this.isCurrentGeneration(generation)) return {
					items: [],
					tabs: []
				};
				runInAction(() => {
					this.routineLibrary = items;
					this.routineLibraryTabs = tabs;
				});
				return {
					items,
					tabs
				};
			} catch (error) {
				this.failed("fetchRoutineLibrary", "Failed to fetch the routine library", error);
			}
		});
	}
	/** The flag-gated catalog is separate from the legacy form picker's cache. */
	async fetchExpandedRoutineLibrary() {
		return parseRoutineLibraryResponse(await this.sdk.apiClient.fetch("routine_library?v=4"));
	}
	async fetchRoutineSuggestions() {
		return this.withOperation("suggestions", void 0, async () => {
			const generation = this.generation;
			try {
				const suggestions = parseGeneratedRoutineSuggestions(await this.sdk.apiClient.fetch("routine_suggestions"));
				if (!this.isCurrentGeneration(generation)) return [];
				runInAction(() => {
					this.routineSuggestions = suggestions;
				});
				return suggestions;
			} catch (error) {
				this.failed("fetchRoutineSuggestions", "Failed to fetch routine suggestions", error);
			}
		});
	}
	async dismissRoutineSuggestion(suggestionId) {
		return this.withOperation("dismiss-suggestion", suggestionId, async () => {
			try {
				await this.sdk.apiClient.fetch(`routine_suggestions/${suggestionId}/dismiss`, {
					method: "POST",
					headers: { "Content-Type": "application/json" }
				});
				runInAction(() => {
					this.routineSuggestions = this.routineSuggestions.filter((suggestion) => suggestion.id !== suggestionId);
				});
			} catch (error) {
				this.failed("dismissRoutineSuggestion", "Failed to dismiss routine suggestion", error);
			}
		});
	}
	async shareRoutineWithTeam(referenceId) {
		const routine = this.getByReferenceId(referenceId);
		if (!routine) throw new Error(`Routine ${referenceId} not found`);
		return this.withOperation("share-team", referenceId, async () => {
			try {
				const data = await this.sdk.apiClient.fetch(`report_templates/${referenceId}/share_with_team`, {
					method: "POST",
					headers: { "Content-Type": "application/json" }
				});
				const response = ShareRoutineWithTeamResponseSchema.parse(data);
				runInAction(() => {
					routine.teamShare = {
						id: response.team_share.id,
						isStale: response.team_share.is_stale
					};
				});
				this.recentMutations.record(referenceId);
				this.fetchRoutineLibrary().catch(() => void 0);
				return routine;
			} catch (error) {
				this.failed("shareRoutineWithTeam", "Failed to share routine with team", error);
			}
		});
	}
	async removeTeamRoutineTemplate(teamTemplateId) {
		return this.withOperation("remove-team-template", teamTemplateId, async () => {
			try {
				await this.sdk.apiClient.fetch(`team_routine_templates/${teamTemplateId}`, { method: "DELETE" });
				runInAction(() => {
					for (const routine of this.data.values()) if (routine.teamShare?.id === teamTemplateId) {
						routine.teamShare = null;
						this.recentMutations.record(routine.referenceId);
					}
					this.routineLibrary = this.routineLibrary.filter((item) => item.team_template_id !== teamTemplateId);
				});
			} catch (error) {
				this.failed("removeTeamRoutineTemplate", "Failed to remove team routine template", error);
			}
		});
	}
	async fetchDeliveryChannelConnections(enabledChannels) {
		const channels = enabledChannels.filter((channel) => channel !== "off");
		return this.withOperation("delivery-connections", void 0, async () => {
			const generation = this.generation;
			const settled = await Promise.allSettled(channels.map(async (channel) => [channel, await this.fetchChannelConnection(channel)]));
			if (!this.isCurrentGeneration(generation)) return;
			runInAction(() => {
				for (const result of settled) {
					if (result.status !== "fulfilled") continue;
					const [channel, connection] = result.value;
					this.deliveryChannelConnections.set(channel, connection);
				}
			});
			const firstRejection = settled.find((result) => result.status === "rejected");
			if (firstRejection) this.failed("fetchDeliveryChannelConnections", "Failed to fetch delivery channel connections", firstRejection.reason);
		});
	}
	async fetchChannelConnection(channel) {
		try {
			const data = await this.sdk.apiClient.fetch(CHANNEL_CONNECTION_ENDPOINT[channel]);
			const parsed = ChannelConnectionResponseSchema.parse(data);
			return {
				identifier: parsed.identifier,
				hasActiveConversation: parsed.has_active_conversation
			};
		} catch (error) {
			if (error instanceof HttpError && error.status === 404) return null;
			throw error;
		}
	}
	/**
	* Serializes writes per routine. Two concurrent PUTs can otherwise land out
	* of order, leaving the backend on the older value.
	*/
	async updateRoutine(referenceId, data) {
		const routine = this.getByReferenceId(referenceId);
		if (!routine) throw new Error(`Routine ${referenceId} not found`);
		const queued = (this.updateQueues.get(referenceId) ?? Promise.resolve()).catch(() => {}).then(() => this.sendRoutineUpdate(referenceId, routine, data));
		this.updateQueues.set(referenceId, queued);
		const releaseQueue = () => {
			if (this.updateQueues.get(referenceId) === queued) this.updateQueues.delete(referenceId);
		};
		queued.then(releaseQueue, releaseQueue);
		return queued;
	}
	async sendRoutineUpdate(referenceId, routine, data) {
		return this.withOperation("update", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`report_templates/${referenceId}`, {
					method: "PUT",
					body: JSON.stringify({
						...toRoutineWireBody(data),
						skip_initial_report: true
					})
				});
				runInAction(() => {
					Object.assign(routine, data);
				});
				this.recentMutations.record(referenceId);
				return routine;
			} catch (error) {
				this.failed("updateRoutine", "Failed to update routine", error);
			}
		});
	}
	async fetchWebhookEndpoints() {
		return this.withOperation("webhook-endpoints", void 0, async () => {
			const generation = this.generation;
			try {
				const endpoints = (await this.sdk.apiClient.fetch(WEBHOOK_ENDPOINTS_PATH)).map(toWebhookEndpoint);
				if (this.isCurrentGeneration(generation)) runInAction(() => {
					this.webhookEndpoints = endpoints;
				});
				return endpoints;
			} catch (error) {
				this.failed("fetchWebhookEndpoints", "Failed to fetch webhook endpoints", error);
			}
		});
	}
	/** The same for every user, so a later call returns the presets already fetched. */
	async fetchWebhookPresets() {
		if (this.webhookPresets.length > 0) return this.webhookPresets;
		return this.withOperation("webhook-presets", void 0, async () => {
			try {
				const presets = (await this.sdk.apiClient.fetch("routine_webhook_presets")).map(({ sample_payload, ...preset }) => ({
					...preset,
					samplePayload: sample_payload
				}));
				runInAction(() => {
					this.webhookPresets = presets;
				});
				return presets;
			} catch (error) {
				this.failed("fetchWebhookPresets", "Failed to fetch webhook presets", error);
			}
		});
	}
	/**
	* The secret is in this result and nowhere else: the backend never returns it again.
	*
	* With `teamId`, the endpoint belongs to that team, so every active member can attach triggers to it.
	*/
	async createWebhookEndpoint(name, samplePayload = null, teamId = null, description = "") {
		return this.withOperation("create-webhook-endpoint", void 0, async () => {
			try {
				const response = await this.sdk.apiClient.fetch(WEBHOOK_ENDPOINTS_PATH, {
					method: "POST",
					body: JSON.stringify({
						name,
						description,
						sample_payload: samplePayload,
						team_id: teamId
					})
				});
				const endpoint = toWebhookEndpoint(response);
				runInAction(() => {
					this.webhookEndpoints = [...this.webhookEndpoints, endpoint];
				});
				return {
					...endpoint,
					secret: response.secret
				};
			} catch (error) {
				this.failed("createWebhookEndpoint", "Failed to create webhook endpoint", error);
			}
		});
	}
	/** Invalidates the old secret for every routine that uses the endpoint. */
	async rotateWebhookEndpointSecret(endpointId) {
		return this.withOperation("rotate-webhook-endpoint-secret", endpointId, async () => {
			try {
				const credentials = await this.sdk.apiClient.fetch(`${WEBHOOK_ENDPOINTS_PATH}/${endpointId}/rotate_secret`, { method: "POST" });
				this.markHasSecret(endpointId);
				return credentials;
			} catch (error) {
				this.failed("rotateWebhookEndpointSecret", "Failed to rotate webhook endpoint secret", error);
			}
		});
	}
	/** Replaces the secret with one the sender issued, such as Stripe's signing secret, invalidating the old one. */
	async setWebhookEndpointSecret(endpointId, secret) {
		return this.withOperation("set-webhook-endpoint-secret", endpointId, async () => {
			try {
				await this.sdk.apiClient.fetch(`${WEBHOOK_ENDPOINTS_PATH}/${endpointId}/secret`, {
					method: "PUT",
					body: JSON.stringify({ secret })
				});
				this.markHasSecret(endpointId);
			} catch (error) {
				this.failed("setWebhookEndpointSecret", "Failed to set webhook endpoint secret", error);
			}
		});
	}
	markHasSecret(endpointId) {
		runInAction(() => {
			this.webhookEndpoints = this.webhookEndpoints.map((endpoint) => endpoint.id === endpointId ? {
				...endpoint,
				hasSecret: true
			} : endpoint);
		});
	}
	/** Its URL and secret stay the same. Fields `edit` leaves out are not sent, so the backend keeps them. */
	async editWebhookEndpoint(endpointId, edit) {
		return this.withOperation("edit-webhook-endpoint", endpointId, async () => {
			try {
				const response = await this.sdk.apiClient.fetch(`${WEBHOOK_ENDPOINTS_PATH}/${endpointId}`, {
					method: "PATCH",
					body: JSON.stringify({
						name: edit.name,
						description: edit.description,
						team_id: edit.teamId,
						sample_payload: edit.samplePayload
					})
				});
				const endpoint = toWebhookEndpoint(response);
				runInAction(() => {
					this.webhookEndpoints = this.webhookEndpoints.map((existing) => existing.id === endpointId ? endpoint : existing);
				});
				return endpoint;
			} catch (error) {
				this.failed("editWebhookEndpoint", "Failed to edit webhook endpoint", error);
			}
		});
	}
	/** The backend deletes every trigger on the endpoint with it, so this drops them locally too. */
	async deleteWebhookEndpoint(endpointId) {
		return this.withOperation("delete-webhook-endpoint", endpointId, async () => {
			try {
				await this.sdk.apiClient.fetch(`${WEBHOOK_ENDPOINTS_PATH}/${endpointId}`, { method: "DELETE" });
				runInAction(() => {
					this.webhookEndpoints = this.webhookEndpoints.filter((endpoint) => endpoint.id !== endpointId);
					for (const routine of this.data.values()) routine.triggers = routine.triggers.filter((trigger) => trigger.kind !== "generic_webhook" || trigger.webhookEndpointId !== endpointId);
				});
			} catch (error) {
				this.failed("deleteWebhookEndpoint", "Failed to delete webhook endpoint", error);
			}
		});
	}
	async pauseRoutine(referenceId) {
		return this.setRoutinePaused(referenceId, true);
	}
	async unpauseRoutine(referenceId) {
		const routine = this.getByReferenceId(referenceId);
		if (routine?.isAdminPaused) return routine;
		return this.setRoutinePaused(referenceId, false);
	}
	async setRoutineStarred(referenceId, starred) {
		const routine = this.getByReferenceId(referenceId);
		if (!routine) throw new Error(`Routine ${referenceId} not found`);
		const queue = this.starQueues.get(referenceId) ?? {
			latest: Promise.resolve(),
			confirmedStarred: routine.starred
		};
		runInAction(() => {
			routine.starred = starred;
		});
		this.recentMutations.record(referenceId);
		const request = queue.latest.catch(() => {}).then(() => this.withOperation("star", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`report_templates/${referenceId}/star`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ starred })
				});
				queue.confirmedStarred = starred;
				return routine;
			} catch (error) {
				if (queue.latest === request) runInAction(() => {
					routine.starred = queue.confirmedStarred;
				});
				this.failed("setRoutineStarred", "Failed to star routine", error);
			}
		}));
		queue.latest = request;
		this.starQueues.set(referenceId, queue);
		const releaseQueue = () => {
			if (queue.latest === request) this.starQueues.delete(referenceId);
		};
		request.then(releaseQueue, releaseQueue);
		return request;
	}
	async generateReport(referenceId) {
		if (!this.getByReferenceId(referenceId)) throw new Error(`Routine ${referenceId} not found`);
		const inFlight = this.manualGenerations.get(referenceId);
		if (inFlight) return inFlight;
		const generation = this.withOperation("generate", referenceId, () => this.requestGeneration(referenceId, GENERATION_REQUESTS.generate)).finally(() => {
			runInAction(() => {
				this.manualGenerations.delete(referenceId);
			});
		});
		runInAction(() => {
			this.manualGenerations.set(referenceId, generation);
		});
		return generation;
	}
	/**
	* Cancels the run waiting for an offline device and starts a new one with the
	* routine's current instructions. The cancelled run is deleted server-side, so
	* the routine's runs are reloaded to drop it.
	*/
	async restartWaitingRun(referenceId) {
		return this.withOperation("restart-waiting-run", referenceId, async () => {
			const report = await this.requestGeneration(referenceId, GENERATION_REQUESTS.restartWaitingRun);
			await this.sdk.stores.reports.loadRoutineRuns(referenceId).catch(() => void 0);
			return report;
		});
	}
	/** Whether a manual run for this routine is still in flight */
	isGeneratingReport(referenceId) {
		return this.manualGenerations.has(referenceId);
	}
	async deleteRoutine(referenceId) {
		const routine = this.getByReferenceId(referenceId);
		if (!routine) throw new Error(`Routine ${referenceId} not found`);
		return this.withOperation("delete", referenceId, async () => {
			try {
				await this.sdk.apiClient.fetch(`report_templates/${referenceId}`, { method: "DELETE" });
				runInAction(() => {
					this.remove(routine);
				});
				this.recentMutations.record(referenceId);
				this.operationTracker.clearOperationErrors(referenceId);
				this.sdk.syncNow().catch(() => {});
			} catch (error) {
				this.failed("deleteRoutine", "Failed to delete routine", error);
			}
		});
	}
	async requestGeneration(referenceId, request) {
		const generation = this.generation;
		try {
			const response = await this.sdk.apiClient.fetch(`report_templates/${referenceId}/${request.endpoint}`, {
				method: "POST",
				headers: { "Content-Type": "application/json" }
			});
			if (!response?.report) throw new Error(`${request.label} response is missing the report`);
			if (!this.isCurrentGeneration(generation)) throw new Error("Signed out before the run was created");
			const routine = this.getByReferenceId(referenceId);
			if (routine && response.last_manual_generation_at) runInAction(() => {
				routine.lastManualGenerationAt = parseDate(response.last_manual_generation_at);
			});
			return this.sdk.stores.reports.addApiReport(response.report);
		} catch (error) {
			this.failed(request.context, `Failed to ${request.label.toLowerCase()}`, error);
		}
	}
	async persistAndHydrateRoutines(dbRoutines) {
		try {
			runInAction(() => {
				for (const dbRoutine of dbRoutines) {
					if (!dbRoutine.id) continue;
					this.apiRoutineToRoutine(dbRoutine);
				}
			});
		} catch (error) {
			this.sdk.logger.error(error, {
				metadata: { message: "Error processing routines" },
				context: "RoutineStore:persistAndHydrateRoutines"
			});
			throw error;
		}
	}
	/** Resuming may start a run straight away, which the response carries back. */
	async requestPauseChange(referenceId, isPaused) {
		const generation = this.generation;
		if (isPaused) {
			await this.sdk.apiClient.fetch(`report_templates/${referenceId}/pause`, {
				method: "POST",
				headers: { "Content-Type": "application/json" }
			});
			return;
		}
		const response = await this.sdk.apiClient.fetch(`report_templates/${referenceId}/unpause`, {
			method: "POST",
			headers: { "Content-Type": "application/json" }
		});
		if (response?.report && this.isCurrentGeneration(generation)) this.sdk.stores.reports.addApiReport(response.report);
	}
	async setRoutinePaused(referenceId, isPaused) {
		const routine = this.getByReferenceId(referenceId);
		if (!routine) throw new Error(`Routine ${referenceId} not found`);
		const operation = isPaused ? "pause" : "resume";
		const snapshot = {
			isPaused: routine.isPaused,
			pauseReason: routine.pauseReason
		};
		runInAction(() => {
			routine.isPaused = isPaused;
			routine.pauseReason = isPaused ? "manual" : null;
		});
		this.recentMutations.record(referenceId);
		return this.withOperation(operation, referenceId, async () => {
			try {
				await this.requestPauseChange(referenceId, isPaused);
				return routine;
			} catch (error) {
				runInAction(() => {
					Object.assign(routine, snapshot);
				});
				this.failed("setRoutinePaused", `Failed to ${operation} routine`, error);
			}
		});
	}
	apiRoutineToRoutine(apiRoutine) {
		const existingRoutine = this.getByReferenceId(apiRoutine.id);
		const fields = this.routineFieldsFromApi(apiRoutine);
		if (existingRoutine) return this.mergeApiRoutine(existingRoutine, apiRoutine, fields);
		return this.add({
			id: v4(),
			referenceId: apiRoutine.id,
			createdAt: parseDate(apiRoutine.created_at),
			icon: parseIconConfig(apiRoutine.icon),
			...fields
		});
	}
	routineFieldsFromApi(apiRoutine) {
		return {
			title: apiRoutine.title,
			kind: apiRoutine.kind ?? "digest",
			completedAt: apiRoutine.completed_at ? parseDate(apiRoutine.completed_at) : null,
			projectId: apiRoutine.project_id ?? null,
			createdInChatId: apiRoutine.created_in_chat_id ?? null,
			recurrenceType: apiRoutine.recurrence_type,
			recurrenceRule: apiRoutine.recurrence_rule,
			prompt: apiRoutine.prompt,
			isPaused: apiRoutine.is_paused ?? false,
			isAdminPaused: apiRoutine.is_admin_paused ?? false,
			isExperimental: apiRoutine.is_experimental ?? false,
			origin: apiRoutine.origin ?? "user",
			hiddenFromUser: apiRoutine.hidden_from_user ?? false,
			starred: apiRoutine.starred ?? false,
			permissions: apiRoutine.permissions ? permissionsFromApi(apiRoutine.permissions) : null,
			pauseReason: apiRoutine.pause_reason ?? null,
			autoPauseEnabled: apiRoutine.auto_pause_enabled ?? true,
			mayReachOut: apiRoutine.may_reach_out ?? true,
			waitingOnYou: apiRoutine.waiting_on_you ? {
				reportId: apiRoutine.waiting_on_you.report_id,
				chatId: apiRoutine.waiting_on_you.chat_id,
				kind: apiRoutine.waiting_on_you.kind,
				reason: apiRoutine.waiting_on_you.reason
			} : null,
			notificationsEnabled: apiRoutine.notifications_enabled ?? true,
			emailNotificationsEnabled: apiRoutine.email_notifications_enabled ?? false,
			deliveryChannel: apiRoutine.delivery_channel ?? "off",
			deliveryAgentId: apiRoutine.delivery_agent_id ?? null,
			agentMode: apiRoutine.agent_mode ?? "regular",
			lastManualGenerationAt: apiRoutine.last_manual_generation_at ? parseDate(apiRoutine.last_manual_generation_at) : null,
			teamShare: apiRoutine.team_share ? {
				id: apiRoutine.team_share.id,
				isStale: apiRoutine.team_share.is_stale
			} : null,
			triggers: (apiRoutine.triggers ?? []).map(triggerFromApi),
			updatedAt: parseDate(apiRoutine.updated_at)
		};
	}
	mergeApiRoutine(existingRoutine, apiRoutine, fields) {
		const { waitingOnYou, ...guardedFields } = fields;
		if (!isEqual(waitingOnYou, existingRoutine.waitingOnYou)) existingRoutine.waitingOnYou = waitingOnYou;
		if (this.recentMutations.isRecent(apiRoutine.id)) return existingRoutine;
		const icon = parseIconConfig(apiRoutine.icon);
		if (!isSameIconConfig(icon, existingRoutine.icon)) existingRoutine.icon = icon;
		const pauseInFlight = this.isOperationPending("pause", apiRoutine.id) || this.isOperationPending("resume", apiRoutine.id);
		const starInFlight = this.isOperationPending("star", apiRoutine.id);
		const { isPaused, pauseReason, starred, lastManualGenerationAt, teamShare, permissions, updatedAt, ...restFields } = guardedFields;
		Object.assign(existingRoutine, restFields);
		if (!isEqual(permissions, existingRoutine.permissions)) existingRoutine.permissions = permissions;
		if (lastManualGenerationAt?.getTime() !== existingRoutine.lastManualGenerationAt?.getTime()) existingRoutine.lastManualGenerationAt = lastManualGenerationAt;
		if (teamShare?.id !== existingRoutine.teamShare?.id || teamShare?.isStale !== existingRoutine.teamShare?.isStale) existingRoutine.teamShare = teamShare;
		if (updatedAt.getTime() !== existingRoutine.updatedAt.getTime()) existingRoutine.updatedAt = updatedAt;
		if (!pauseInFlight) Object.assign(existingRoutine, {
			isPaused,
			pauseReason
		});
		if (!starInFlight) existingRoutine.starred = starred;
		return existingRoutine;
	}
};
//#endregion
//#region src/core/sync/stores/thread-store.ts
const DEFAULT_TITLE = "New Chat";
const PROVISIONAL_TITLE_MAX_LENGTH = 20;
const PLACEHOLDER_TITLES = /* @__PURE__ */ new Set([DEFAULT_TITLE, "Chat with Littlebird"]);
const ARCHIVED_STATE_NOT_CONFIRMED_ERROR = "Archived state was not confirmed by the server";
const CHATS_NUDGE_DEBOUNCE_MS = 50;
const CHATS_NUDGE_JITTER_MS = 500;
/**
* True when a thread title is empty or matches one of the auto-rename
* placeholders the SDK uses before a server-generated title arrives.
*/
function isPlaceholderThreadTitle(title) {
	const t = title?.trim();
	return !t || PLACEHOLDER_TITLES.has(t);
}
/**
* Derive the short placeholder title shown in the sidebar until the
* server-generated title arrives.
*/
function provisionalThreadTitle(messageContent) {
	const normalized = messageContent.trim().replace(/\s+/g, " ");
	if (!normalized) return DEFAULT_TITLE;
	if (normalized.length <= PROVISIONAL_TITLE_MAX_LENGTH) return normalized;
	return `${normalized.slice(0, PROVISIONAL_TITLE_MAX_LENGTH).trimEnd()}…`;
}
/**
* How long a steer that is not accepted waits for its check of the chat, so the Steer button stops loading. A check
* that takes longer, as one behind a backfill, still runs, and if the server finished the chat, the queued message
* sends then.
*/
const STEER_CHECK_WAIT_MS = 1e4;
/**
* The refusals of a steer that can come from a reply that ended. The server runs its other checks, such as credits or
* the message's length, only once it found a turn holding the chat, so those refusals show the reply had not ended.
*/
const REPLY_STATE_STEER_REFUSALS = /* @__PURE__ */ new Set(["no_reply_running", "reply_ended"]);
const STEER_CHAT_STATUSES = {
	accepted: true,
	delivered: true,
	not_accepted: true
};
const isDefiniteRefusal = (error) => error instanceof HttpError && error.status >= 400 && error.status < 500 && error.status !== 408;
var ThreadStore = class ThreadStore extends Store {
	static persisted = true;
	static loadStrategy = "eager";
	ModelClass = Thread;
	activeStreamControllers = /* @__PURE__ */ new Map();
	wsReconnectedUnsub = null;
	chatModePending = /* @__PURE__ */ new Map();
	chatModeInFlight = /* @__PURE__ */ new Set();
	chatsChangedUnsub = null;
	chatsNudgeTimer = null;
	stopChatVisibilityRefresh;
	hydratedOrigins = /* @__PURE__ */ new Set();
	threadIdsByProject = /* @__PURE__ */ new Map();
	threadIdsByMeeting = observable.map();
	projectIndexVersion = observable.box(0);
	/**
	* Side metadata mirrored for every thread persisted during bootstrap, even
	* when the Thread model is not hydrated into MobX. Lets consumers like
	* MessageStore check `finished` for unloaded threads without paying the
	* cost of materializing the full Thread.
	*/
	bootstrapThreadMeta = /* @__PURE__ */ new Map();
	pinnedCursor;
	hasMorePinned = true;
	hasMoreRecent = true;
	batchedLoadIds = /* @__PURE__ */ new Set();
	/** Chats whose `new_chat` request has not answered; their reply starts after it, so no finished row ends it */
	chatsBeingCreated = /* @__PURE__ */ new Set();
	batchedLoad = null;
	constructor(sdk) {
		super(sdk);
		this.initializeMetadata("chats");
		observe(this.data, (change) => {
			if (change.type === "add") {
				this.addToProjectIndex(change.newValue);
				this.addToMeetingIndex(change.newValue);
			} else if (change.type === "delete") {
				this.removeFromProjectIndex(change.oldValue);
				this.removeFromMeetingIndex(change.oldValue);
			}
		});
		makeObservable(this, {
			visibleChats: computed,
			pinnedChats: computed,
			recentChats: computed,
			archivedChats: computed,
			updateThread: action,
			archiveThread: action,
			unarchiveThread: action,
			hydrate: action,
			delete: action,
			createEmptyThread: action,
			discardFreshDraftThread: action,
			initializeThreadWithMessage: action,
			updateThreadStatus: action,
			updateThreadLastMessageId: action,
			setIsLegacyChat: action,
			setProjectId: action,
			setProjectIdLocal: action,
			clearProjectAssignmentsForProject: action,
			persistAndHydrateThreads: action,
			retryThreadInitialization: action,
			createThreadFromRoutine: action
		});
		this.wsReconnectedUnsub = globalEventBus.on(EventNames.WS_RECONNECTED, () => void this.handleWSReconnect());
		this.chatsChangedUnsub = globalEventBus.on(EventNames.CHATS_CHANGED, () => this.handleChatsChanged());
		this.stopChatVisibilityRefresh = reaction(() => sdk.userConfig.config.chat.show_messaging_channel_chats, () => {
			sdk.refreshLoadedListStores(/* @__PURE__ */ new Set([this.tableName]));
		});
	}
	/**
	* Reconcile server state after reconnect. A reply that ended while the connection was down sent its end
	* to no one here, so the chats still shown running are marked for the check of the sync run that this starts.
	*/
	async handleWSReconnect() {
		this.sdk.markRunningChatsForCheck(this.data.keys());
		try {
			await this.sdk.syncNow();
		} catch (err) {
			this.sdk.logger.error("Sync failed on websocket reconnect", {
				metadata: { error: err },
				context: "ThreadStore:handleWSReconnect"
			});
		}
	}
	handleChatsChanged() {
		if (this.chatsNudgeTimer) clearTimeout(this.chatsNudgeTimer);
		this.chatsNudgeTimer = setTimeout(() => {
			this.chatsNudgeTimer = null;
			this.sdk.syncNow().catch((err) => {
				this.sdk.logger.error("Sync failed on chats changed nudge", {
					metadata: { error: err },
					context: "ThreadStore:handleChatsChanged"
				});
			});
		}, CHATS_NUDGE_DEBOUNCE_MS + Math.random() * CHATS_NUDGE_JITTER_MS);
	}
	/** Releases subscriptions and the nudge timer before an SDK instance is replaced in tests */
	teardownForTesting() {
		this.wsReconnectedUnsub?.unsubscribe();
		this.wsReconnectedUnsub = null;
		this.stopChatVisibilityRefresh();
		this.chatsChangedUnsub?.unsubscribe();
		this.chatsChangedUnsub = null;
		if (this.chatsNudgeTimer) {
			clearTimeout(this.chatsNudgeTimer);
			this.chatsNudgeTimer = null;
		}
	}
	get orderedData() {
		return orderBy(Array.from(this.data.values()).filter((item) => !item.deletedAt), [(item) => {
			const ts = item.updatedAt ?? item.createdAt;
			return ts ? ts.getTime() : 0;
		}], ["desc"]);
	}
	/** User-facing chat-list threads. */
	get visibleChats() {
		return this.orderedData.filter((thread) => thread.synced && thread.title !== "temp" && (!thread.isMessagingChannel || this.sdk.userConfig.config.chat.show_messaging_channel_chats) && !isRoutineRunThread(thread) && !isAgentThread(thread));
	}
	get chatListOptions() {
		return { includeMessagingChannelChats: this.sdk.userConfig.config.chat.show_messaging_channel_chats };
	}
	getThreadForAgent = computedFn(function(agentId) {
		for (const thread of this.data.values()) if (thread.agentId === agentId && !thread.deletedAt) return thread;
	});
	get pinnedChats() {
		return this.visibleChats.filter((chat) => chat.starred && chat.isAvailableForInteraction);
	}
	get recentChats() {
		return this.visibleChats.filter((chat) => !chat.starred && chat.isAvailableForInteraction);
	}
	get archivedChats() {
		return this.visibleChats.filter((chat) => chat.archived);
	}
	recentChatsByOrigin = computedFn(function(origin, limit) {
		return this.recentChats.filter((chat) => chat.origin === origin).sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()).slice(0, limit);
	});
	getChatsForFilter = computedFn(function(filter) {
		return this.visibleChats.filter((chat) => {
			if (chat.archived) return filter === "archived";
			switch (filter) {
				case "archived": return false;
				case "favorites": return chat.starred;
				case "in-project": return chat.projectId !== null;
				case "no-project": return chat.projectId === null;
				case "from-meeting": return chat.meetingId !== null;
				case "all": return true;
			}
		});
	});
	getById = computedFn(function(threadId) {
		return this.data.get(threadId);
	});
	addToProjectIndex(thread) {
		if (!thread.projectId) return;
		let set = this.threadIdsByProject.get(thread.projectId);
		if (!set) {
			set = /* @__PURE__ */ new Set();
			this.threadIdsByProject.set(thread.projectId, set);
		}
		set.add(thread.id);
		this.bumpProjectIndexVersion();
	}
	removeThreadIdFromProjectIndex(threadId, projectId) {
		const set = this.threadIdsByProject.get(projectId);
		if (!set) return;
		set.delete(threadId);
		if (set.size === 0) this.threadIdsByProject.delete(projectId);
		this.bumpProjectIndexVersion();
	}
	removeFromProjectIndex(thread) {
		if (!thread.projectId) return;
		this.removeThreadIdFromProjectIndex(thread.id, thread.projectId);
	}
	bumpProjectIndexVersion() {
		runInAction(() => {
			this.projectIndexVersion.set(this.projectIndexVersion.get() + 1);
		});
		this.sdk.stores.projects.scheduleItemCountRefresh();
	}
	addToMeetingIndex(thread) {
		if (!thread.meetingId) return;
		let set = this.threadIdsByMeeting.get(thread.meetingId);
		if (!set) {
			set = observable.set();
			this.threadIdsByMeeting.set(thread.meetingId, set);
		}
		set.add(thread.id);
	}
	removeFromMeetingIndex(thread) {
		if (!thread.meetingId) return;
		this.removeThreadIdFromMeetingIndex(thread.id, thread.meetingId);
	}
	removeThreadIdFromMeetingIndex(threadId, meetingId) {
		const set = this.threadIdsByMeeting.get(meetingId);
		if (!set) return;
		set.delete(threadId);
		if (set.size === 0) this.threadIdsByMeeting.delete(meetingId);
	}
	getThreadsForProject(projectId) {
		this.projectIndexVersion.get();
		const ids = this.threadIdsByProject.get(projectId);
		if (!ids) return [];
		const result = [];
		for (const id of ids) {
			const thread = this.data.get(id);
			if (thread && !thread.deletedAt) result.push(thread);
		}
		return result;
	}
	updateExistingModel(existingModel, item) {
		const oldProjectId = existingModel.projectId ?? null;
		const projectIdIsPresent = "projectId" in item;
		const newProjectId = projectIdIsPresent ? item.projectId ?? null : oldProjectId;
		const projectChanged = projectIdIsPresent && oldProjectId !== newProjectId;
		const oldMeetingId = existingModel.meetingId ?? null;
		const meetingIdIsPresent = "meetingId" in item;
		const newMeetingId = meetingIdIsPresent ? item.meetingId ?? null : oldMeetingId;
		const meetingChanged = meetingIdIsPresent && oldMeetingId !== newMeetingId;
		if (projectChanged && oldProjectId) this.removeThreadIdFromProjectIndex(existingModel.id, oldProjectId);
		if (meetingChanged && oldMeetingId) this.removeThreadIdFromMeetingIndex(existingModel.id, oldMeetingId);
		existingModel.updateData(item);
		if (projectChanged && newProjectId) this.addToProjectIndex(existingModel);
		if (meetingChanged && newMeetingId) this.addToMeetingIndex(existingModel);
		return existingModel;
	}
	clear() {
		this.threadIdsByProject.clear();
		this.threadIdsByMeeting.clear();
		this.bootstrapThreadMeta.clear();
		this.pinnedCursor = void 0;
		this.hasMorePinned = true;
		this.hasMoreRecent = true;
		this.bumpProjectIndexVersion();
		super.clear();
	}
	isThreadFinished(threadId) {
		const thread = this.data.get(threadId);
		if (thread) return thread.finished;
		return this.bootstrapThreadMeta.get(threadId)?.finished;
	}
	async loadThread(threadId) {
		const existing = this.getById(threadId);
		if (existing) return existing;
		const generation = this.generation;
		const record = await this.sdk.storage.get(this.tableName, threadId);
		if (!this.isCurrentGeneration(generation)) return void 0;
		if (!record) return void 0;
		return this.add(this.normalizePersistedThread(record));
	}
	async loadInitialPage(options) {
		const threadQueries = this.requireThreadQueries();
		this.clear();
		this.hydratedOrigins.clear();
		const generation = this.generation;
		const [pinnedPage, recentPage] = await Promise.all([threadQueries.listPinned({
			...options,
			...this.chatListOptions
		}), threadQueries.listRecent({
			...options,
			...this.chatListOptions
		})]);
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of [...pinnedPage.records, ...recentPage.records]) this.add(this.normalizePersistedThread(record));
			this.pinnedCursor = pinnedPage.nextCursor;
			this.hasMorePinned = pinnedPage.hasMore;
			this.currentPageCursor = recentPage.nextCursor;
			this.hasMoreRecent = recentPage.hasMore;
			this.hasMorePages = pinnedPage.hasMore || recentPage.hasMore;
			this.isLoaded = true;
		});
	}
	async loadNextPage(options) {
		const threadQueries = this.requireThreadQueries();
		if (!this.hasMorePages) return;
		const generation = this.generation;
		if (this.hasMorePinned && this.pinnedCursor) {
			const page = await threadQueries.listPinnedAfter(this.pinnedCursor, {
				...options,
				...this.chatListOptions
			});
			if (!this.isCurrentGeneration(generation)) return;
			runInAction(() => {
				for (const record of page.records) this.add(this.normalizePersistedThread(record));
				this.pinnedCursor = page.nextCursor;
				this.hasMorePinned = page.hasMore;
				this.hasMorePages = this.hasMorePinned || this.hasMoreRecent;
			});
			return;
		}
		if (!this.hasMoreRecent || !this.currentPageCursor) {
			runInAction(() => {
				this.hasMorePages = this.hasMorePinned || this.hasMoreRecent;
			});
			return;
		}
		const page = await threadQueries.listRecentAfter(this.currentPageCursor, {
			...options,
			...this.chatListOptions
		});
		if (!this.isCurrentGeneration(generation)) return;
		runInAction(() => {
			for (const record of page.records) this.add(this.normalizePersistedThread(record));
			this.currentPageCursor = page.nextCursor;
			this.hasMoreRecent = page.hasMore;
			this.hasMorePages = this.hasMorePinned || this.hasMoreRecent;
		});
	}
	async queryInitialListPage(filter) {
		const threadQueries = this.requireThreadQueries();
		const generation = this.generation;
		const options = {
			filter,
			...this.chatListOptions
		};
		const [pinnedPage, recentPage] = await Promise.all([threadQueries.listPinned(options), threadQueries.listRecent(options)]);
		if (!this.isCurrentGeneration(generation)) return {
			pinned: [],
			recent: [],
			state: {
				pinnedCursor: void 0,
				recentCursor: void 0,
				hasMorePinned: false,
				hasMoreRecent: false
			},
			hasMore: false
		};
		return {
			pinned: this.hydrateListRecords(pinnedPage.records),
			recent: this.hydrateListRecords(recentPage.records),
			state: {
				pinnedCursor: pinnedPage.nextCursor,
				recentCursor: recentPage.nextCursor,
				hasMorePinned: pinnedPage.hasMore,
				hasMoreRecent: recentPage.hasMore
			},
			hasMore: pinnedPage.hasMore || recentPage.hasMore
		};
	}
	async queryNextListPage(filter, state) {
		const threadQueries = this.requireThreadQueries();
		const options = {
			filter,
			...this.chatListOptions
		};
		const generation = this.generation;
		if (state.hasMorePinned && state.pinnedCursor) {
			const pinnedPage = await threadQueries.listPinnedAfter(state.pinnedCursor, options);
			if (!this.isCurrentGeneration(generation)) return {
				pinned: [],
				recent: [],
				state,
				hasMore: false
			};
			const nextState = {
				...state,
				pinnedCursor: pinnedPage.nextCursor,
				hasMorePinned: pinnedPage.hasMore
			};
			return {
				pinned: this.hydrateListRecords(pinnedPage.records),
				recent: [],
				state: nextState,
				hasMore: nextState.hasMorePinned || nextState.hasMoreRecent
			};
		}
		if (state.hasMoreRecent && state.recentCursor) {
			const recentPage = await threadQueries.listRecentAfter(state.recentCursor, options);
			if (!this.isCurrentGeneration(generation)) return {
				pinned: [],
				recent: [],
				state,
				hasMore: false
			};
			const nextState = {
				...state,
				recentCursor: recentPage.nextCursor,
				hasMoreRecent: recentPage.hasMore
			};
			return {
				pinned: [],
				recent: this.hydrateListRecords(recentPage.records),
				state: nextState,
				hasMore: nextState.hasMorePinned || nextState.hasMoreRecent
			};
		}
		return {
			pinned: [],
			recent: [],
			state,
			hasMore: false
		};
	}
	async loadProjectThreads(projectId) {
		await this.sdk.waitForFullBootstrap();
		const generation = this.generation;
		const queries = this.requireThreadQueries();
		const filter = {
			kind: "project",
			projectId
		};
		const [pinned, recent] = await Promise.all([drainEntityPages((options) => queries.listPinned({
			...options,
			filter
		}), (cursor, options) => queries.listPinnedAfter(cursor, {
			...options,
			filter
		})), drainEntityPages((options) => queries.listRecent({
			...options,
			filter
		}), (cursor, options) => queries.listRecentAfter(cursor, {
			...options,
			filter
		}))]);
		if (!this.isCurrentGeneration(generation)) return;
		this.hydrateListRecords([...pinned, ...recent]);
	}
	async loadThreadForAgent(agentId) {
		const resident = this.getThreadForAgent(agentId);
		if (resident) return resident;
		const queries = this.requireThreadQueries();
		const generation = this.generation;
		const filter = {
			kind: "agent",
			agentId
		};
		const [pinned, recent] = await Promise.all([drainEntityPages((options) => queries.listPinned({
			...options,
			filter
		}), (cursor, options) => queries.listPinnedAfter(cursor, {
			...options,
			filter
		})), drainEntityPages((options) => queries.listRecent({
			...options,
			filter
		}), (cursor, options) => queries.listRecentAfter(cursor, {
			...options,
			filter
		}))]);
		if (!this.isCurrentGeneration(generation)) return void 0;
		this.hydrateListRecords([...pinned, ...recent]);
		return this.getThreadForAgent(agentId);
	}
	hydrateListRecords(records) {
		return runInAction(() => records.map((record) => this.add(this.normalizePersistedThread(record))));
	}
	/** Calls in the same tick share one read and one store change, so a list of many chats redraws once */
	async ensureLoaded(threadId) {
		const existing = this.getById(threadId);
		if (existing) return existing;
		this.batchedLoadIds.add(threadId);
		this.batchedLoad ??= Promise.resolve().then(() => this.loadBatchedThreads());
		const failures = await this.batchedLoad;
		if (failures.has(threadId)) throw failures.get(threadId);
		return this.getById(threadId);
	}
	/** Each chunk is its own read, so a failed read rejects only the callers of its chats */
	async loadBatchedThreads() {
		const threadIds = [...this.batchedLoadIds];
		const generation = this.generation;
		this.batchedLoadIds.clear();
		this.batchedLoad = null;
		const threadQueries = this.requireThreadQueries();
		const chunks = chunkIds(threadIds);
		const results = await Promise.allSettled(chunks.map((ids) => threadQueries.getByIds(ids)));
		const failures = /* @__PURE__ */ new Map();
		if (!this.isCurrentGeneration(generation)) return failures;
		runInAction(() => {
			results.forEach((result, index) => {
				if (result.status === "rejected") {
					for (const id of chunks[index]) failures.set(id, result.reason);
					return;
				}
				for (const record of result.value) {
					if (this.data.has(record.id)) continue;
					this.add(this.normalizePersistedThread(record));
				}
			});
		});
		return failures;
	}
	shouldRetainLocal(thread) {
		return thread.synced === false;
	}
	normalizePersistedThread(record) {
		return {
			...record,
			deletedAt: record.deletedAt ? record.deletedAt : void 0,
			synced: record.synced !== false,
			...record.finished ? {
				status: "done",
				errorMessage: void 0
			} : {}
		};
	}
	applyRefreshedRecord(record) {
		const existing = this.data.get(record.id);
		if (existing && record.updatedAt instanceof Date && existing.updatedAt instanceof Date && record.updatedAt.getTime() <= existing.updatedAt.getTime()) return;
		this.add(this.normalizePersistedThread(record));
	}
	async runRefreshFromStorage() {
		if (!this.isLoaded) return;
		const threadQueries = this.requireThreadQueries();
		const residentBefore = new Set(this.data.keys());
		const [pinnedPage, recentPage] = await Promise.all([threadQueries.listPinned(this.chatListOptions), threadQueries.listRecent(this.chatListOptions)]);
		const fresh = [...pinnedPage.records, ...recentPage.records];
		const freshIds = new Set(fresh.map((record) => record.id));
		const detailOnlyIds = Array.from(residentBefore).filter((id) => !freshIds.has(id));
		if (detailOnlyIds.length > 0) {
			const detailRecords = await this.fetchRecordsByIdChunks(threadQueries.getByIds.bind(threadQueries), detailOnlyIds);
			for (const record of detailRecords) {
				fresh.push(record);
				freshIds.add(record.id);
			}
		}
		if (freshIds.size === 0 && residentBefore.size > 0) {
			this.sdk.logger.warn("Skipping thread refresh wipe: storage returned no rows for non-empty resident set", {
				context: "ThreadStore:runRefreshFromStorage",
				metadata: { residentBefore: residentBefore.size }
			});
			return;
		}
		runInAction(() => {
			for (const record of fresh) this.applyRefreshedRecord(record);
			for (const id of residentBefore) {
				if (freshIds.has(id)) continue;
				const existing = this.data.get(id);
				if (!existing) continue;
				if (this.shouldRetainLocal(existing)) continue;
				this.data.delete(id);
				this.bootstrapThreadMeta.delete(id);
			}
			this.pinnedCursor = pinnedPage.nextCursor;
			this.hasMorePinned = pinnedPage.hasMore;
			this.currentPageCursor = recentPage.nextCursor;
			this.hasMoreRecent = recentPage.hasMore;
			this.hasMorePages = this.hasMorePinned || this.hasMoreRecent;
		});
	}
	requireThreadQueries() {
		const threadQueries = this.sdk.storage.entities.threads;
		if (!threadQueries) throw new MissingEntityQueriesError("threads");
		return threadQueries;
	}
	async getRecentThreads(limit) {
		if (this.isLoaded) return this.recentChats.slice(0, limit);
		return (await this.requireThreadQueries().listRecent({
			limit,
			...this.chatListOptions
		})).records.slice(0, limit).map((record) => {
			const existing = this.getById(record.id);
			if (existing) return existing;
			return this.add(this.normalizePersistedThread(record));
		});
	}
	async loadRecentThreadsByOrigin(origin, limit) {
		if (this.hydratedOrigins.has(origin)) return;
		this.hydratedOrigins.add(origin);
		try {
			const page = await this.requireThreadQueries().listRecent({
				limit,
				origin,
				...this.chatListOptions
			});
			for (const record of page.records.slice(0, limit)) if (!this.getById(record.id)) this.add(this.normalizePersistedThread(record));
		} catch (err) {
			this.hydratedOrigins.delete(origin);
			throw err;
		}
	}
	/**
	* Loads a chat without checking it with the server, as for a prefetch. A view that shows the chat calls
	* `openThread` instead.
	*/
	async ensureThreadIsReady(threadId) {
		const thread = await this.loadThread(threadId);
		await this.sdk.stores.messages.loadThreadMessages(threadId);
		return thread ?? this.getById(threadId);
	}
	/**
	* Loads a chat that a view opens. A chat shown running may have missed its reply's end, so it is checked
	* with the server. This does not wait for the check.
	*/
	async openThread(threadId) {
		const thread = await this.ensureThreadIsReady(threadId);
		this.sdk.checkRunningChats([threadId]);
		return thread;
	}
	async deleteChatOnServer(id, options) {
		try {
			await this.sdk.apiClient.fetch(`chat/${id}`, {
				method: "DELETE",
				body: JSON.stringify(options)
			});
		} catch (err) {
			if (err instanceof HttpError && err.status === 404) {
				this.sdk.logger.info("Chat not found on server, proceeding with local deletion", {
					metadata: { chatId: id },
					context: "ThreadStore:delete"
				});
				return;
			}
			throw err;
		}
	}
	delete = async (item, options) => {
		this.isSaving = true;
		try {
			const id = item.id;
			item.deletedAt = /* @__PURE__ */ new Date();
			const revokedCards = this.sdk.stores.actionCards.revokeOpenCardsForDeletedChat(id);
			if (item.synced) try {
				await this.deleteChatOnServer(id, options);
			} catch (err) {
				item.deletedAt = void 0;
				this.sdk.stores.actionCards.restoreRevokedCards(revokedCards);
				this.sdk.logger.error("Failed to delete chat", {
					metadata: {
						message: "Failed to delete chat",
						error: err
					},
					context: "ThreadStore:delete"
				});
				throw err;
			}
			const searchDocIds = await this.collectThreadSearchDocIds([id]);
			await this.sdk.storage.withTransaction([
				this.tableName,
				"messages",
				"_partialIndexes"
			], "readwrite", async () => {
				await this.sdk.storage.remove(this.tableName, id);
				await this.sdk.stores.messages.deleteByThreadId(id);
				await this.sdk.partialIndexCoverage.removePersisted("messages", "threadId", id);
			});
			await this.sdk.stores.actionCards.persistRevokedCards(revokedCards);
			await this.sdk.applySearchSync("threads", {
				deletedSearchDocIds: searchDocIds,
				upsertedRecords: []
			}, "ThreadStore:delete");
		} finally {
			this.isSaving = false;
		}
	};
	async createThreadFromRoutine(params) {
		try {
			const threadId = v4();
			const userMessageId = v4();
			const assistantMessageId = v4();
			const nowMs = Date.now();
			const userMessageDate = /* @__PURE__ */ new Date(nowMs - 2e3);
			const assistantMessageDate = /* @__PURE__ */ new Date(nowMs - 1e3);
			const thread = this.createThread({
				threadId,
				userMessageId,
				now: /* @__PURE__ */ new Date(),
				status: "done",
				title: params.title
			});
			thread.origin = params.origin;
			const userMessage = this.createMessage({
				content: params.userMessage,
				now: userMessageDate,
				role: "user",
				mode: MAIN_CHAT_MODE,
				fileIds: [],
				messageId: userMessageId,
				threadId
			});
			const assistantMessage = this.createMessage({
				threadId,
				messageId: assistantMessageId,
				content: params.assistantMessage,
				now: assistantMessageDate,
				role: "assistant",
				mode: MAIN_CHAT_MODE,
				fileIds: []
			});
			await this.createThreadWithMessages({
				thread,
				userMessage,
				assistantMessage
			});
			return threadId;
		} catch (err) {
			this.sdk.logger.error("Failed to create thread from routine", {
				metadata: {
					message: "Failed to create thread from routine",
					error: err
				},
				context: "ThreadStore:createThreadFromRoutine"
			});
			throw err;
		}
	}
	async createThreadWithMessages(params) {
		await this.sdk.apiClient.fetch("create_chat_with_messages", {
			method: "POST",
			body: JSON.stringify({
				chat_id: params.thread.id,
				title: params.thread.title,
				user_message_id: params.userMessage.id,
				assistant_message_id: params.assistantMessage.id,
				user_message: params.userMessage.content,
				assistant_message: params.assistantMessage.content,
				origin: params.thread.origin
			})
		});
		runInAction(() => {
			params.thread.synced = true;
		});
		await this.sdk.storage.set(this.tableName, params.thread);
		await this.sdk.storage.bulkSet("messages", [params.userMessage, params.assistantMessage]);
	}
	prepareFilesFromDraft(draft) {
		return draft.files.filter((file) => file.uploadState === "uploaded").map((file) => this.sdk.stores.files.addFile({
			id: file.id,
			filename: file.fileName,
			contentType: file.contentType,
			size: file.size,
			width: file.width,
			height: file.height,
			createdAt: /* @__PURE__ */ new Date(),
			updatedAt: /* @__PURE__ */ new Date(),
			deletedAt: void 0
		}));
	}
	/**
	* Sets a busy status. A chat that turns busy records the sync id this client holds as its reply's start, for
	* the reply rule (`isAfterReplyStart`), whether a send or a retry from this client or a reply's event turns it.
	*/
	setBusyStatus(thread, status) {
		if (!isChatStatusBusy(thread.status)) thread.replyStartSyncId = thread.sync_id;
		thread.completedAt = void 0;
		thread.status = status;
	}
	/** Whether the chat shows a reply running, that is, its status is busy */
	showsRunning(threadId) {
		return isChatStatusBusy(this.data.get(threadId)?.status);
	}
	/**
	* Holds a finished row's sync id before the row syncs. The done or error event that ends a reply carries it, and
	* so does the answer to Stop. A reply that starts next, as a queued message sent then, records it as its start, so
	* that finish cannot end the new reply. The finished row still syncs: only rows older than the held one are
	* skipped (`isOlderThanHeld`).
	*/
	recordFinishSyncId(threadId, syncId) {
		const thread = this.data.get(threadId);
		if (!thread || thread.sync_id !== void 0 && thread.sync_id >= syncId) return;
		runInAction(() => thread.sync_id = syncId);
	}
	/**
	* Forgets the reply start that a send recorded, once the server refuses that send as busy: the send started no
	* reply, and the reply that holds the chat may already have a finished row with the sync id this device holds.
	* The reply rule then passes any finished row. The chat is saved, since the send saved the start.
	*/
	forgetReplyStart(threadId) {
		const thread = this.data.get(threadId);
		if (!thread) return;
		runInAction(() => thread.replyStartSyncId = void 0);
		this.sdk.storage.set(this.tableName, thread).catch((error) => {
			this.sdk.logger.error("Failed to save a forgotten reply start", {
				metadata: {
					error,
					threadId
				},
				context: "ThreadStore:forgetReplyStart"
			});
		});
	}
	createThread(params) {
		return this.add({
			id: params.threadId,
			title: params.title || DEFAULT_TITLE,
			status: params.status,
			createdAt: params.now,
			updatedAt: params.now,
			lastMessageAt: params.now,
			synced: false,
			lastMessageId: params.userMessageId,
			projectId: params.projectId ?? null
		});
	}
	createMessage({ role = "user", ...rest }) {
		return this.sdk.stores.messages.add({
			role,
			content: rest.content,
			fileIds: rest.fileIds,
			threadId: rest.threadId,
			createdAt: new Date(rest.now),
			updatedAt: new Date(rest.now),
			status: "done",
			id: rest.messageId,
			mode: rest.mode
		});
	}
	async persistThreadData(thread, userMessage, files, isSlashCommand) {
		await this.sdk.storage.withTransaction([
			this.tableName,
			"messages",
			"files"
		], "readwrite", async () => {
			await this.sdk.storage.set(this.tableName, thread);
			if (!isSlashCommand) {
				await this.sdk.storage.set("messages", userMessage);
				await this.sdk.storage.bulkSet("files", files);
			}
		});
	}
	getThreadForMeeting(meetingId) {
		let fallback;
		for (const id of this.threadIdsByMeeting.get(meetingId) ?? []) {
			const thread = this.data.get(id);
			if (!thread || thread.deletedAt) continue;
			if (thread.synced) return thread;
			fallback ??= thread;
		}
		return fallback;
	}
	async findThreadForMeeting(meetingId) {
		const inMemory = this.getThreadForMeeting(meetingId);
		if (inMemory) return inMemory;
		const record = await this.requireThreadQueries().getByMeetingId(meetingId);
		return record ? this.add(record) : void 0;
	}
	isFreshDraftThread(threadId) {
		const thread = this.data.get(threadId);
		if (thread?.synced || thread?.lastMessageId) return false;
		const draft = this.sdk.stores.drafts.getDraft(threadId);
		if (draft && !isDraftEmpty(draft)) return false;
		return !this.sdk.stores.messages.getMessagesByThreadId(threadId).some((message) => message.role === "user");
	}
	discardFreshDraftThread(threadId) {
		if (!this.isFreshDraftThread(threadId)) return;
		const thread = this.data.get(threadId);
		if (thread) this.remove(thread);
		const draft = this.sdk.stores.drafts.getDraft(threadId);
		if (draft) this.sdk.stores.drafts.removeDraft(draft);
	}
	createEmptyThread = (params) => {
		const existingMeetingThread = params?.meetingId ? this.getThreadForMeeting(params.meetingId) : void 0;
		if (existingMeetingThread) return existingMeetingThread;
		const now = /* @__PURE__ */ new Date();
		const threadId = v4();
		return this.add({
			id: threadId,
			title: params?.title?.trim() || DEFAULT_TITLE,
			status: "done",
			createdAt: now,
			updatedAt: now,
			lastMessageAt: now,
			synced: false,
			projectId: params?.projectId ?? null,
			meetingId: params?.meetingId ?? null,
			origin: params?.origin ?? (params?.meetingId ? "meeting" : null)
		});
	};
	buildInitialThreadData(params) {
		const now = /* @__PURE__ */ new Date();
		const threadId = params.existingThreadId ?? v4();
		const userMessageId = params.userMessageId ?? v4();
		const { messageContent, files } = params;
		const fileIds = files.map((file) => file.id);
		const existingThread = params.existingThreadId ? this.get(params.existingThreadId) : null;
		const thread = existingThread ? runInAction(() => {
			if (params.projectId !== void 0 && existingThread.projectId !== params.projectId) {
				this.removeFromProjectIndex(existingThread);
				existingThread.projectId = params.projectId;
				this.addToProjectIndex(existingThread);
			}
			this.setBusyStatus(existingThread, "thinking");
			existingThread.lastMessageId = userMessageId;
			existingThread.lastMessageAt = now;
			existingThread.updatedAt = now;
			return existingThread;
		}) : this.createThread({
			threadId,
			userMessageId,
			now,
			status: "thinking",
			title: params.title ?? provisionalThreadTitle(messageContent),
			projectId: params.projectId
		});
		const userMessage = this.createMessage({
			threadId,
			messageId: userMessageId,
			role: "user",
			content: messageContent,
			fileIds,
			now,
			mode: params.mode
		});
		return {
			thread,
			userMessage,
			isSlashCommand: isMetaSlashCommand(userMessage.content)
		};
	}
	/**
	* Create a local chat and its first user message, then start server sync in
	* the background. Direct content is used for a voice transcript and leaves
	* the typed draft untouched; otherwise the draft supplies text, quotes,
	* and files.
	*/
	initializeThreadWithMessage = (params) => {
		this.isSaving = true;
		try {
			const draftId = params.draftId ?? params.existingThreadId ?? "NEW_CHAT";
			const sendInput = resolveOutgoingSendInput(params.content, () => this.sdk.stores.drafts.getDraft(draftId));
			const { draft } = sendInput;
			const files = draft ? this.prepareFilesFromDraft(draft) : [];
			const { thread, userMessage, isSlashCommand } = this.buildInitialThreadData({
				messageContent: sendInput.content,
				files,
				mode: params.mode,
				projectId: params.projectId,
				existingThreadId: params.existingThreadId,
				userMessageId: params.userMessageId,
				title: params.title
			});
			if (draft) this.sdk.stores.drafts.removeDraft(draft);
			this.persistThreadData(thread, userMessage, files, isSlashCommand).catch((err) => {
				this.sdk.logger.error("Failed to save thread in local db", {
					metadata: {
						message: "Failed to save thread in local db",
						error: err
					},
					context: "ThreadStore:initializeThreadWithMessage"
				});
			}).then(() => this.syncThreadToServer({
				thread,
				userMessage,
				mode: params.mode,
				agent: params.agent,
				sourceActionCardId: params.sourceActionCardId,
				followUpActionCardIds: params.followUpActionCardIds,
				personIds: params.personIds,
				groupNames: params.groupNames,
				isSlashCommand,
				useTodos: params.useTodos,
				context: params.context,
				projectId: params.projectId,
				title: params.title,
				responseAudioVoiceId: params.responseAudioVoiceId
			}));
			return thread;
		} finally {
			this.isSaving = false;
		}
	};
	/**
	* If we this function is called, it means the thread was created locally but was not synced to the server
	* so we need to retry with the `lastMessageId` of the thread. Incase the message is not found, we update the thread back to normal
	* so that user can resend another message
	*/
	retryThreadInitialization = async (payload) => {
		const message = payload.thread.lastMessageId ? this.sdk.stores.messages.get(payload.thread.lastMessageId) : void 0;
		if (!message) {
			runInAction(() => {
				payload.thread.status = "done";
				payload.thread.errorMessage = void 0;
			});
			throw new Error("Something went wrong. Please send a new message");
		}
		const isSlashCommand = isMetaSlashCommand(message.content);
		if (!isSlashCommand) runInAction(() => {
			this.setBusyStatus(payload.thread, "thinking");
			payload.thread.errorMessage = void 0;
		});
		await this.syncThreadToServer({
			thread: payload.thread,
			userMessage: message,
			mode: payload.mode,
			isSlashCommand,
			useTodos: payload.useTodos,
			projectId: payload.projectId
		});
	};
	/**
	* Do not use this method directly. Use `initializeThreadWithMessage` instead.
	*/
	syncThreadToServer = async (opts) => {
		try {
			await this.createNewChatOnServer(opts.thread, {
				projectId: opts.projectId,
				title: opts.title,
				sourceActionCardId: opts.sourceActionCardId
			});
			await this.streamAgentResponse(opts);
		} catch (err) {
			this.handleSendError(err, opts, "Failed to create new chat");
		} finally {
			await this.sdk.storage.set(this.tableName, opts.thread).catch((err) => {
				this.sdk.logger.error("Failed to persist thread sync result", {
					metadata: {
						error: err,
						threadId: opts.thread.id
					},
					context: "ThreadStore:syncThreadToServer"
				});
			});
			this.isSaving = false;
		}
	};
	async createNewChatOnServer(thread, opts) {
		if (thread.meetingId) throw new Error("Meeting threads must be prepared with prepareMeetingThread() before syncing");
		const requestBody = {
			chat_id: thread.id,
			title: opts.title ?? DEFAULT_TITLE
		};
		const resolvedProjectId = opts.projectId ?? thread.projectId;
		if (resolvedProjectId) requestBody.project_id = resolvedProjectId;
		if (thread.origin) requestBody.origin = thread.origin;
		if (opts.sourceActionCardId) requestBody.source_action_card_id = opts.sourceActionCardId;
		this.chatsBeingCreated.add(thread.id);
		try {
			const newChatResponse = await this.sdk.apiClient.fetch("new_chat", {
				method: "POST",
				body: JSON.stringify(requestBody)
			});
			runInAction(() => {
				thread.synced = true;
				const createdSyncId = newChatResponse?.sync_id;
				if (createdSyncId !== void 0) {
					thread.sync_id ??= createdSyncId;
					if (this.showsRunning(thread.id)) thread.replyStartSyncId ??= createdSyncId;
				}
			});
		} finally {
			this.chatsBeingCreated.delete(thread.id);
		}
	}
	async prepareMeetingThread(thread) {
		if (!thread.meetingId || thread.synced || thread.lastMessageId) return thread;
		const sourceThreadId = thread.id;
		const meetingId = thread.meetingId;
		const response = await this.sdk.apiClient.fetch("new_chat", {
			method: "POST",
			body: JSON.stringify({ meeting_id: meetingId })
		});
		const chatId = response.chat_id;
		const now = /* @__PURE__ */ new Date();
		const existing = this.data.get(chatId);
		const meeting = this.sdk.stores.meetings.get(meetingId);
		const title = meeting?.name ?? "";
		const projectId = meeting?.projectId ?? null;
		const preparedThread = existing ?? this.add({
			id: chatId,
			title,
			status: "done",
			createdAt: now,
			updatedAt: now,
			lastMessageAt: now,
			synced: true,
			meetingId,
			projectId,
			sync_id: response.sync_id
		});
		if (existing) this.add({
			id: chatId,
			deletedAt: void 0,
			meetingId,
			synced: true,
			projectId,
			title: isPlaceholderThreadTitle(existing.title) ? title : existing.title
		});
		await this.sdk.storage.set(this.tableName, preparedThread);
		if (preparedThread.id !== sourceThreadId) await this.sdk.stores.drafts.moveDraft(sourceThreadId, preparedThread.id);
		return preparedThread;
	}
	async streamAgentResponse(opts) {
		this.activeStreamControllers.get(opts.thread.id)?.abort();
		const controller = new AbortController();
		this.activeStreamControllers.set(opts.thread.id, controller);
		try {
			await this.sdk.stores.messages.streamAgentResponse({
				threadId: opts.thread.id,
				content: opts.userMessage.content,
				context: opts.context,
				mode: opts.mode,
				agent: opts.agent,
				fileIds: opts.userMessage.fileIds,
				userMessageId: opts.userMessage.id,
				isMetaSlashCommand: opts.isSlashCommand,
				arenaData: opts.arenaData,
				useTodos: opts.useTodos,
				followUpActionCardIds: opts.followUpActionCardIds,
				personIds: opts.personIds,
				groupNames: opts.groupNames,
				responseAudioVoiceId: opts.responseAudioVoiceId,
				signal: controller.signal
			});
		} catch (streamErr) {
			if (streamErr instanceof StreamCancellationError) return;
			this.handleSendError(streamErr, opts, "Failed to stream agent response");
		} finally {
			if (this.activeStreamControllers.get(opts.thread.id) === controller) this.activeStreamControllers.delete(opts.thread.id);
		}
	}
	handleSendError(error, opts, failureMessage) {
		this.sdk.logger.error(failureMessage, {
			metadata: {
				message: failureMessage,
				error
			},
			context: "ThreadStore:syncThreadToServer"
		});
		this.sdk.voiceMode.notifyPendingReplyFailure(opts.thread.id, opts.userMessage.id);
		globalEventBus.emit(EventNames.CHAT_SEND_FAILED, { data: {
			threadId: opts.thread.id,
			userMessageId: opts.userMessage.id
		} });
		if (opts.isSlashCommand) return;
		const errorMessage = error instanceof Error ? error.message : String(error) || failureMessage;
		const { status, endedByFreeLimit } = chatStatusForSendError(error);
		runInAction(() => {
			opts.thread.status = status;
			opts.thread.finished = endedByFreeLimit;
			opts.thread.errorMessage = endedByFreeLimit ? void 0 : errorMessage;
		});
		if (shouldTrackMessageError(error)) globalEventBus.emit(EventNames.ANALYTICS_TRACK_MESSAGE_ERROR_EVENT, { data: {
			chat_id: opts.thread.id,
			...messageErrorAnalyticsFields(error, errorMessage)
		} });
	}
	isThreadActivelyRunning(thread) {
		if (thread.status === "not_submitted") return false;
		return thread.status === "thinking" || thread.status === "streaming" || thread.status === "waiting_for_client_tools" || thread.finished === false;
	}
	archiveThread = async (thread) => {
		const wasRunning = this.isThreadActivelyRunning(thread);
		await this.setArchivedState(thread, true);
		if (wasRunning) try {
			await this.stopStreamingForThread(thread.id);
		} catch (err) {
			this.sdk.logger.error("Failed to stop stream after archive", {
				metadata: {
					error: err,
					threadId: thread.id
				},
				context: "ThreadStore:archiveThread"
			});
		}
	};
	unarchiveThread = async (thread) => {
		await this.setArchivedState(thread, false);
	};
	async setArchivedState(thread, archived) {
		if (thread.archived === archived) return;
		const previousArchived = thread.archived;
		const previousUpdatedAt = thread.updatedAt;
		runInAction(() => {
			thread.archived = archived;
			thread.updatedAt = /* @__PURE__ */ new Date();
		});
		try {
			if ((await this.sdk.apiClient.fetch(`update_chat/${thread.id}`, {
				method: "PUT",
				body: JSON.stringify({ archived })
			}))?.archived !== archived) throw new Error(ARCHIVED_STATE_NOT_CONFIRMED_ERROR);
		} catch (err) {
			runInAction(() => {
				thread.archived = previousArchived;
				thread.updatedAt = previousUpdatedAt;
			});
			this.sdk.logger.error("Failed to update thread archived state", {
				metadata: {
					error: err,
					threadId: thread.id,
					archived
				},
				context: "ThreadStore:setArchivedState"
			});
			throw err;
		}
		this.sdk.storage.set(this.tableName, thread).catch((err) => {
			this.sdk.logger.error("Failed to persist thread archived state", {
				metadata: {
					error: err,
					threadId: thread.id,
					archived
				},
				context: "ThreadStore:setArchivedState"
			});
		});
	}
	updateThread = async (thread, params) => {
		this.isSaving = true;
		const preUpdateSnapshot = thread.toJSON();
		try {
			thread.updateData({
				...params,
				updatedAt: /* @__PURE__ */ new Date()
			});
			await this.sdk.apiClient.fetch(`update_chat/${thread.id}`, {
				method: "PUT",
				body: JSON.stringify({ ...params })
			});
			await this.sdk.storage.set(this.tableName, thread);
		} catch (err) {
			this.sdk.logger.error("Failed to update thread title", {
				metadata: {
					message: "Failed to update thread title",
					error: err
				},
				context: "ThreadStore:updateThread"
			});
			thread.updateData(preUpdateSnapshot);
		} finally {
			this.isSaving = false;
		}
	};
	updateThreadStatus = async (threadId, status, errorMessage) => {
		const thread = this.data.get(threadId);
		if (!thread) return;
		if (thread.status === status && thread.errorMessage === errorMessage) return;
		if (isChatStatusBusy(status)) this.setBusyStatus(thread, status);
		else {
			if (isChatStatusBusy(thread.status) && (status === "done" || status === "errored" || status === "stopped")) thread.completedAt = Date.now();
			thread.status = status;
		}
		thread.finished = status === "done";
		if (errorMessage) thread.errorMessage = errorMessage;
		else thread.errorMessage = void 0;
		await this.sdk.storage.set(this.tableName, thread);
	};
	updateThreadLastMessageId = async (threadId, messageId) => {
		const thread = this.data.get(threadId);
		if (!thread) return;
		thread.lastMessageId = messageId;
		await this.sdk.storage.set(this.tableName, thread);
	};
	setIsLegacyChat = async (threadId, isLegacy) => {
		const thread = this.data.get(threadId);
		if (!thread) return;
		thread.isLegacyChat = isLegacy;
		await this.sdk.storage.set(this.tableName, thread);
	};
	setProjectIdLocal = (threadId, projectId) => {
		const thread = this.data.get(threadId);
		if (!thread) return;
		if (thread.projectId === projectId) return;
		this.removeFromProjectIndex(thread);
		thread.projectId = projectId;
		this.addToProjectIndex(thread);
	};
	setProjectId = async (threadId, projectId) => {
		const thread = this.data.get(threadId);
		if (!thread) return;
		try {
			await updateIndexedProjectAssignment({
				item: thread,
				projectId,
				applyProjectId: (nextProjectId) => {
					runInAction(() => {
						this.removeFromProjectIndex(thread);
						thread.projectId = nextProjectId;
						this.addToProjectIndex(thread);
					});
				},
				sync: () => this.sdk.apiClient.fetch(`update_chat/${threadId}`, {
					method: "PUT",
					body: JSON.stringify({ project_id: projectId })
				}),
				persist: () => this.sdk.storage.set(this.tableName, thread)
			});
		} catch (err) {
			this.sdk.logger.error("Failed to update thread project assignment", {
				metadata: {
					error: err,
					threadId,
					projectId
				},
				context: "ThreadStore:setProjectId"
			});
			throw err;
		}
	};
	clearProjectSuggestion = async (threadId) => {
		const thread = this.data.get(threadId);
		if (!thread || !thread.suggestedProject) return;
		const previous = thread.suggestedProject;
		runInAction(() => {
			thread.suggestedProject = null;
		});
		await this.sdk.storage.set(this.tableName, thread);
		try {
			await this.sdk.apiClient.fetch(`update_chat/${threadId}`, {
				method: "PUT",
				body: JSON.stringify({ clear_suggested_project: true })
			});
		} catch (err) {
			runInAction(() => {
				thread.suggestedProject = previous;
			});
			await this.sdk.storage.set(this.tableName, thread);
			this.sdk.logger.error("Failed to clear project suggestion", {
				metadata: {
					error: err,
					threadId
				},
				context: "ThreadStore:clearProjectSuggestion"
			});
			throw err;
		}
	};
	setChatMode = async (threadId, mode) => {
		const thread = this.data.get(threadId);
		if (!thread || thread.model === mode) return;
		runInAction(() => {
			thread.model = mode;
		});
		if (thread.synced) this.chatModePending.set(threadId, mode);
		await this.sdk.storage.set(this.tableName, thread);
		if (!thread.synced) return;
		await this.flushChatMode(threadId);
	};
	async flushChatMode(threadId) {
		if (this.chatModeInFlight.has(threadId)) return;
		this.chatModeInFlight.add(threadId);
		try {
			let mode;
			while ((mode = this.chatModePending.get(threadId)) !== void 0) {
				this.chatModePending.delete(threadId);
				try {
					await this.sdk.apiClient.fetch(`update_chat/${threadId}`, {
						method: "PUT",
						body: JSON.stringify({ chat_mode: mode })
					});
				} catch (err) {
					this.chatModePending.delete(threadId);
					this.sdk.logger.error("Failed to update chat mode", {
						metadata: {
							error: err,
							threadId,
							mode
						},
						context: "ThreadStore:setChatMode"
					});
					return;
				}
			}
		} finally {
			this.chatModeInFlight.delete(threadId);
		}
	}
	clearProjectAssignmentsForProject = async (projectId) => {
		const clearedIds = new Set(ThreadStore.persisted ? await this.requireThreadQueries().clearProjectId(projectId) : []);
		for (const id of this.threadIdsByProject.get(projectId) ?? []) clearedIds.add(id);
		runInAction(() => {
			for (const threadId of clearedIds) {
				const thread = this.data.get(threadId);
				if (!thread || thread.projectId !== projectId) continue;
				this.removeFromProjectIndex(thread);
				thread.projectId = null;
			}
		});
		return [...clearedIds];
	};
	stopChat = async (threadId, lastMessage) => {
		const result = await this.sdk.apiClient.fetch(`stop_chat_with_context/${threadId}`, {
			method: "POST",
			body: JSON.stringify(lastMessage ? { last_message: lastMessage } : {})
		});
		const finishSyncId = result?.sync_id;
		if (finishSyncId !== void 0 && finishSyncId !== null) this.recordFinishSyncId(threadId, finishSyncId);
		await globalEventBus.emit(EventNames.CHAT_DID_STOP, { data: {
			threadId,
			cancelledSignInRequestId: result?.cancelled_sign_in_request_id
		} });
	};
	/**
	* Hands a message to the chat's running turn. `accepted`: the server stored it and writes it
	* into the history, so the caller does not send it again. `delivered`: it is already in the
	* history. `not_accepted`: the server did not take it. When the reason says the reply may have
	* ended (`REPLY_STATE_STEER_REFUSALS`), a chat shown running is then checked with the server, for
	* up to `STEER_CHECK_WAIT_MS` before this returns (`checkRunningChats` says when the check waits
	* for a later sync run). When the check ends the chat, it shows done, and the
	* caller sends the message as a normal message; otherwise it stays running. Throws
	* `SteerRefusedError` on a 4xx other than 408, and any other error when the outcome is unclear.
	* A 401 or 429 throws the API client's own error type, not `SteerRefusedError`.
	*/
	steerChat = async (threadId, payload) => {
		try {
			const body = await this.sdk.apiClient.fetch(chatSteerUrl(this.sdk.options, threadId), {
				method: "POST",
				body: JSON.stringify({
					user_message_id: payload.userMessageId,
					question: payload.question,
					context: payload.context,
					file_ids: payload.fileIds ?? []
				}),
				backoff: NO_BACKOFF,
				timeoutMs: 15e3
			});
			if (!Object.hasOwn(STEER_CHAT_STATUSES, body.status)) throw new Error(`Unknown steer status: ${String(body.status)}`);
			if (body.status === "not_accepted" && body.reason !== void 0 && REPLY_STATE_STEER_REFUSALS.has(body.reason)) {
				let waitId;
				await Promise.race([this.sdk.checkRunningChats([threadId]), new Promise((resolve) => {
					waitId = setTimeout(resolve, STEER_CHECK_WAIT_MS);
				})]);
				clearTimeout(waitId);
			}
			return body.status;
		} catch (error) {
			if (isDefiniteRefusal(error)) throw new SteerRefusedError(error.status);
			throw error;
		}
	};
	/**
	* Stops the running turn. Resolves `true` once the server acknowledged the
	* stop. Resolves `false` when the stop request failed: local state is still
	* reset so the composer is not stuck busy, but the turn may still be running
	* server-side, so callers that sequence work after the stop (steering) must
	* not treat resolution alone as the acknowledgement.
	*/
	stopStreamingForThread = async (threadId) => {
		if (!threadId) return false;
		const streamingMsg = this.sdk.stores.messages.getStreamingMessageForThread(threadId);
		const payload = streamingMsg ? [
			`${streamingMsg.role}`,
			`text:${streamingMsg.content}`,
			STOP_CHAT_WITH_CONTEXT_PAYLOAD
		] : void 0;
		try {
			await this.stopChat(threadId, payload);
			this.sdk.stores.messages.unregisterArenaThread(threadId);
			this.updateThreadStatus(threadId, "done");
			if (streamingMsg) {
				runInAction(() => {
					streamingMsg.status = "stopped";
				});
				await this.sdk.storage.set("messages", streamingMsg);
			}
			return true;
		} catch (err) {
			this.sdk.logger.error("Failed to stop chat", {
				metadata: {
					message: "Failed to stop chat",
					error: err
				},
				context: "ThreadStore:stopStreamingForThread"
			});
			this.sdk.stores.messages.unregisterArenaThread(threadId);
			this.updateThreadStatus(threadId, "done");
			return false;
		}
	};
	abortActiveStream(threadId) {
		const controller = this.activeStreamControllers.get(threadId);
		if (controller) {
			controller.abort();
			this.activeStreamControllers.delete(threadId);
		}
	}
	buildThreadDataFromApi(dbThread) {
		const existingThread = this.data.get(dbThread.id);
		const keepsReplyRunning = dbThread.finished === true && this.showsRunning(dbThread.id) && !this.rowEndsShownReply(dbThread);
		const startsReply = dbThread.finished !== true && dbThread.reply_running === true && !this.showsRunning(dbThread.id);
		const serverTitle = dbThread.title || "Untitled";
		const keepLocalTitle = existingThread && !PLACEHOLDER_TITLES.has(existingThread.title) && PLACEHOLDER_TITLES.has(serverTitle);
		return {
			...existingThread ? existingThread.toJSON() : {},
			...dbThread,
			id: dbThread.id,
			title: keepLocalTitle ? existingThread.title : serverTitle,
			starred: dbThread.starred || false,
			archived: dbThread.archived ?? existingThread?.archived ?? false,
			..."project_id" in dbThread ? { projectId: dbThread.project_id ?? null } : {},
			..."meeting_id" in dbThread ? { meetingId: dbThread.meeting_id ?? null } : {},
			..."report_template_id" in dbThread ? { reportTemplateId: dbThread.report_template_id ?? null } : {},
			..."agent_id" in dbThread ? { agentId: dbThread.agent_id ?? null } : {},
			...dbThread.chat_mode && !this.chatModePending.has(dbThread.id) && !this.chatModeInFlight.has(dbThread.id) ? { model: dbThread.chat_mode } : {},
			..."last_imported_message_id" in dbThread ? { lastImportedMessageId: dbThread.last_imported_message_id ?? null } : {},
			..."origin" in dbThread ? { origin: dbThread.origin ?? null } : {},
			..."engine" in dbThread ? { backend: parseBackendName(dbThread.engine) } : {},
			..."hidden_from_user" in dbThread ? { hiddenFromUser: dbThread.hidden_from_user ?? false } : {},
			..."suggested_project" in dbThread ? { suggestedProject: dbThread.suggested_project ? {
				name: dbThread.suggested_project.name,
				projectId: dbThread.suggested_project.project_id
			} : null } : {},
			createdAt: parseDate(dbThread.created_at),
			updatedAt: parseDate(dbThread.updated_at),
			isNew: false,
			lastMessageAt: parseDate(dbThread.updated_at || dbThread.created_at),
			synced: true,
			isMessagingChannel: dbThread.is_messaging_channel ?? existingThread?.isMessagingChannel ?? false,
			finished: keepsReplyRunning ? existingThread?.finished : dbThread.finished,
			...dbThread.finished && !keepsReplyRunning ? {
				status: "done",
				errorMessage: void 0
			} : {},
			...startsReply ? {
				status: "thinking",
				replyStartSyncId: dbThread.sync_id,
				completedAt: void 0,
				errorMessage: void 0
			} : {}
		};
	}
	/**
	* Whether the row is older than the sync id held in memory for the chat: the last row applied, or the finished row
	* a done or error event or Stop named (`recordFinishSyncId`). A chat only in storage has none. This relies on the
	* server giving a chat's rows sync ids in the order it writes them; a write that draws its id first, as a Python
	* rename does, can break that, and its row is then skipped.
	*/
	isOlderThanHeld(row) {
		const heldSyncId = this.data.get(row.id)?.sync_id;
		return heldSyncId !== void 0 && row.sync_id !== void 0 && row.sync_id < heldSyncId;
	}
	/**
	* Whether applying the row ends the reply the chat shows running: the row is finished, not deleted and not
	* older than the one held, the chat's `new_chat` has answered (`chatsBeingCreated`), and the row passes the reply
	* rule (`isAfterReplyStart`).
	*
	* A finished row that is not deleted, for a chat shown running, has one of these outcomes:
	* - It is skipped on apply (`persistAndHydrateThreads`) when it is below the held id (`Thread.sync_id`), since it
	*   would roll the chat back.
	* - It applies and leaves the reply running while `new_chat` has not answered, since the reply starts after the
	*   answer.
	* - It applies and leaves the reply running when it is not above the reply's start (`Thread.replyStartSyncId`):
	*   for example the finish that a done or error event or Stop recorded before a queued message started this reply.
	* - Otherwise it applies and ends the reply.
	*/
	rowEndsShownReply(row) {
		const thread = this.data.get(row.id);
		return row.finished === true && !row.deleted_at && this.showsRunning(row.id) && !this.chatsBeingCreated.has(row.id) && !this.isOlderThanHeld(row) && this.isAfterReplyStart(thread, row);
	}
	/**
	* The reply rule: a finished row ends the reply shown here only when its sync id is greater than the one this
	* client held when that reply started (`Thread.replyStartSyncId`). Every finish takes a new sync id, so a finish
	* written after the reply started is newer than its start, and a finish this client held before the reply started
	* is not.
	*
	* A chat whose start recorded no sync id, as one stuck before this device recorded starts, takes any finished row;
	* a row without a sync id passes too. A chat created here can start at the sync id `new_chat` returns
	* (see `createNewChatOnServer`).
	*/
	isAfterReplyStart(thread, row) {
		const replyStartSyncId = thread?.replyStartSyncId;
		return replyStartSyncId === void 0 || row.sync_id === void 0 || row.sync_id > replyStartSyncId;
	}
	shouldHydrateThread(id) {
		return this.data.has(id) || this.sdk.stores.messages.isThreadLoaded(id);
	}
	async collectThreadSearchDocIds(threadIds) {
		const docIds = [...threadIds];
		const messageQueries = this.sdk.storage.entities.messages;
		if (messageQueries) docIds.push(...await messageQueries.listIdsByThreads(threadIds));
		return docIds;
	}
	async persistAndHydrateThreads(dbThreads) {
		try {
			const liveRows = [];
			const toDelete = [];
			for (const dbThread of dbThreads) {
				if (!dbThread.id) continue;
				if (dbThread.deleted_at) {
					toDelete.push(dbThread.id);
					continue;
				}
				liveRows.push(dbThread);
			}
			let deletedSearchDocIds = [];
			if (toDelete.length > 0) {
				runInAction(() => {
					toDelete.forEach((id) => {
						this.sdk.voiceMode.stopSessionWithHangupCue(id);
					});
				});
				deletedSearchDocIds = await this.collectThreadSearchDocIds(toDelete);
				const revokedCards = runInAction(() => toDelete.flatMap((id) => {
					this.data.delete(id);
					this.bootstrapThreadMeta.delete(id);
					return this.sdk.stores.actionCards.revokeOpenCardsForDeletedChat(id);
				}));
				await this.sdk.storage.withTransaction([
					this.tableName,
					"messages",
					"_partialIndexes"
				], "readwrite", async () => {
					await this.sdk.storage.bulkRemove(this.tableName, toDelete);
					for (const id of toDelete) {
						await this.sdk.stores.messages.deleteByThreadId(id);
						await this.sdk.partialIndexCoverage.removePersisted("messages", "threadId", id);
					}
				});
				await this.sdk.stores.actionCards.persistRevokedCards(revokedCards);
			}
			const toPersist = liveRows.filter((row) => !this.isOlderThanHeld(row)).map((row) => this.buildThreadDataFromApi(row));
			if (toPersist.length > 0) {
				runInAction(() => {
					for (const data of toPersist) this.bootstrapThreadMeta.set(data.id, { finished: data.finished === true });
				});
				const toHydrate = toPersist.filter((data) => this.shouldHydrateThread(data.id));
				const toPersistOnly = toPersist.filter((data) => !this.shouldHydrateThread(data.id));
				const hydratedModels = runInAction(() => toHydrate.map((data) => this.add(data)));
				if (hydratedModels.length > 0) await this.sdk.storage.bulkSet(this.tableName, hydratedModels);
				if (toPersistOnly.length > 0) await this.persistMany(toPersistOnly);
			}
			return {
				deletedSearchDocIds,
				upsertedRecords: toPersist
			};
		} catch (error) {
			this.sdk.logger.error("Error processing threads", {
				metadata: { error },
				context: "ThreadStore:persistAndHydrateThreads"
			});
			throw error;
		}
	}
	async setChatVisibility(threadId, visibility = "public") {
		try {
			const res = await this.sdk.apiClient.fetch(`set_chat_as_public/${threadId}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ visibility })
			});
			return {
				publicToken: res.public_token,
				sharedTeamId: res.shared_team_id ?? null,
				sharedMsgLength: res.shared_msg_length
			};
		} catch (error) {
			this.sdk.logger.error("Error setting chat visibility", {
				metadata: {
					message: "Error setting chat visibility",
					error
				},
				context: "ThreadStore:setChatVisibility"
			});
			throw error;
		}
	}
	async getShareStatus(threadId) {
		try {
			const res = await this.sdk.apiClient.fetch(`chat_share_status/${threadId}`, { method: "GET" });
			return {
				isShared: res.is_shared,
				publicToken: res.public_token,
				visibility: res.visibility,
				sharedTeamId: res.shared_team_id ?? null,
				sharedMsgLength: res.shared_msg_length,
				currentMsgLength: res.current_msg_length,
				hasNewMessages: res.has_new_messages,
				invitedEmails: res.invited_emails ?? []
			};
		} catch (error) {
			this.sdk.logger.error("Error getting share status", {
				metadata: {
					message: "Error getting share status",
					error
				},
				context: "ThreadStore:getShareStatus"
			});
			throw error;
		}
	}
	async revokeShareToken(threadId) {
		try {
			await this.sdk.apiClient.fetch(`revoke_chat_share/${threadId}`, { method: "PATCH" });
		} catch (error) {
			this.sdk.logger.error("Error revoking share token", {
				metadata: {
					message: "Error revoking share token",
					error
				},
				context: "ThreadStore:revokeShareToken"
			});
			throw error;
		}
	}
	async addChatShareInvite(threadId, email) {
		return (await this.sdk.apiClient.fetch(`chat_share_invites/${threadId}`, {
			method: "POST",
			body: JSON.stringify({ email })
		})).invited_emails;
	}
	async removeChatShareInvite(threadId, email) {
		return (await this.sdk.apiClient.fetch(`chat_share_invites/${threadId}?email=${encodeURIComponent(email)}`, { method: "DELETE" })).invited_emails;
	}
	persistThreadsByIds = async (ids) => {
		if (!ThreadStore.persisted || ids.length === 0) return;
		const items = ids.map((id) => this.data.get(id)).filter((t) => !!t);
		if (items.length > 0) await this.sdk.storage.bulkSet(this.tableName, items);
	};
	restoreProjectAssignments = async (ids, projectId) => {
		if (ids.length === 0) return;
		for (const id of ids) this.setProjectIdLocal(id, projectId);
		if (ThreadStore.persisted) await this.requireThreadQueries().restoreProjectId(ids, projectId);
	};
};
//#endregion
//#region src/core/sync/sync-line-types.ts
/** Client handling for each supported row-carrying bootstrap line type. */
const SYNC_LINE_TYPES = {
	threads: {
		producer: "threads",
		storageTable: "threads",
		packetTable: "threads",
		syncId: "zero"
	},
	messages: {
		producer: "threads",
		storageTable: "messages",
		packetTable: "messages",
		syncId: "zero"
	},
	routine_threads: {
		producer: "routine_threads",
		storageTable: "threads",
		packetTable: "threads",
		syncId: "zero"
	},
	routine_messages: {
		producer: "routine_threads",
		storageTable: "messages",
		packetTable: "messages",
		syncId: "zero"
	},
	files: {
		producer: "files",
		storageTable: "files",
		packetTable: "files",
		syncId: "zero"
	},
	journals: {
		producer: "journals",
		storageTable: "journals",
		packetTable: "journals",
		syncId: "zero"
	},
	meetings: {
		producer: "meetings",
		storageTable: "meetings",
		packetTable: "meetings",
		syncId: "zero"
	},
	projects: {
		producer: "projects",
		storageTable: "projects",
		packetTable: "projects",
		syncId: "zero"
	},
	agents: {
		producer: "agents",
		storageTable: "agents",
		packetTable: "agents",
		syncId: "zero"
	},
	mcp_client_grants: {
		producer: "mcp_client_grants",
		storageTable: "mcpClientGrants",
		packetTable: "mcpClientGrants",
		syncId: "zero"
	},
	user_configs: {
		producer: "user_configs",
		storageTable: "userConfigs",
		packetTable: "user_configs",
		syncId: "derive"
	},
	action_cards: {
		producer: "action_cards",
		storageTable: "actionCards",
		packetTable: "actionCards",
		syncId: "zero"
	}
};
/** Spec for a line type, or undefined when this client has no handler for it. */
function syncLineTypeSpec(lineType) {
	return Object.hasOwn(SYNC_LINE_TYPES, lineType) ? SYNC_LINE_TYPES[lineType] : void 0;
}
/** Storage table each packet writes to, which is not always its own discriminator. */
const STORAGE_TABLE_BY_PACKET_TABLE = Object.fromEntries(Object.values(SYNC_LINE_TYPES).map((spec) => [spec.packetTable, spec.storageTable]));
/** Storage tables a producer needs, keyed by producer key. */
const STORAGE_TABLES_BY_PRODUCER = Object.values(SYNC_LINE_TYPES).reduce((byProducer, spec) => {
	const tables = byProducer.get(spec.producer) ?? [];
	if (!tables.includes(spec.storageTable)) tables.push(spec.storageTable);
	return byProducer.set(spec.producer, tables);
}, /* @__PURE__ */ new Map());
//#endregion
//#region src/core/sync/root-store.ts
function rescopeBackfillCheckpoint(checkpoint, clearedProducers) {
	if (clearedProducers.length === 0) return checkpoint;
	const cursors = { ...checkpoint.cursors };
	const hasMore = { ...checkpoint.hasMore };
	for (const producer of clearedProducers) {
		delete cursors[producer];
		hasMore[producer] = true;
	}
	return {
		...checkpoint,
		cursors,
		hasMore,
		updatedAt: (/* @__PURE__ */ new Date()).toISOString()
	};
}
function isSupersededBy(job, refillJob) {
	return job.producerKeys.every((key) => refillJob.producerKeys.includes(key));
}
function mergeQueuedJobs(currentJobs, queued) {
	if (queued.length === 0) return currentJobs;
	return [...currentJobs.filter((job) => !queued.some((queuedJob) => queuedJob.id === job.id || queuedJob.trigger === "schema_change" && isSupersededBy(job, queuedJob))), ...queued];
}
function producerKeysOf(entries) {
	return [...new Set(entries.flatMap((entry) => entry.policy.producerKeys))].sort();
}
function buildBackfillJob(entries, currentManifest) {
	const tables = entries.map((entry) => entry.table).sort();
	return {
		id: tables.map((table) => {
			const schema = currentManifest[table];
			return `${table}:${schema.schemaVersion}:${schema.fields.join(",")}`;
		}).join("|"),
		runId: v4(),
		tables,
		producerKeys: producerKeysOf(entries),
		updatedAt: (/* @__PURE__ */ new Date()).toISOString()
	};
}
var RootStore = class {
	threads;
	messages;
	drafts;
	files;
	journals;
	arenas;
	routines;
	routineDrafts;
	reports;
	meetings;
	projects;
	agents;
	mcpClientGrants;
	calendarEvents;
	actionCards;
	sdk;
	constructor(sdk) {
		this.sdk = sdk;
		this.threads = this.registerStore(ThreadStore, "threads");
		this.messages = this.registerStore(MessageStore, "messages");
		this.drafts = this.registerStore(DraftStore, "drafts");
		this.files = this.registerStore(FileStore, "files");
		this.journals = this.registerStore(JournalStore, "journals");
		this.arenas = this.registerStore(ArenaStore, "arenas");
		this.routines = this.registerStore(RoutineStore, "routines");
		this.routineDrafts = this.registerStore(RoutineDraftStore, "routineDrafts");
		this.reports = this.registerStore(ReportStore, "reports");
		this.meetings = this.registerStore(MeetingStore, "meetings");
		this.projects = this.registerStore(ProjectStore, "projects");
		this.agents = this.registerStore(AgentStore, "agents");
		this.mcpClientGrants = this.registerStore(McpClientGrantStore, "mcpClientGrants");
		this.calendarEvents = this.registerStore(CalendarEventStore, "calendarEvents");
		this.actionCards = this.registerStore(ActionCardStore, "actionCards");
		if (this.sdk._mode === "partial") {
			const partialKeep = /* @__PURE__ */ new Set([
				"threads",
				"messages",
				"projects",
				"agents"
			]);
			for (const store of this.getStoreInstances()) if (!partialKeep.has(store.tableName)) store.runtimeLoadStrategy = "ephemeral";
		}
	}
	clearAll() {
		this.getStoreInstances().forEach((store) => {
			store.reset();
		});
	}
	getStoreInstances() {
		const stores = [];
		Object.keys({
			threads: true,
			messages: true,
			drafts: true,
			files: true,
			journals: true,
			arenas: true,
			routines: true,
			routineDrafts: true,
			reports: true,
			meetings: true,
			projects: true,
			agents: true,
			mcpClientGrants: true,
			calendarEvents: true,
			actionCards: true
		}).forEach((key) => {
			const store = this[key];
			stores.push(store);
		});
		return stores;
	}
	getStoreByTableName(tableName) {
		return this.getStoreInstances().find((store) => store.tableName === tableName);
	}
	migrationSourceFor(key) {
		if (key === "userConfigs") return {
			table: "userConfigs",
			schemaVersion: UserConfigStore.schemaVersion,
			fields: () => this.sdk.userConfig.getPersistedFieldNames()
		};
		const store = this[key];
		if (!store.tableName) return void 0;
		return {
			table: store.tableName,
			schemaVersion: store.constructor.schemaVersion,
			fields: () => store.getPersistedFieldNames()
		};
	}
	/** Returns current persisted store schemas and versions. */
	buildSchemaSnapshot() {
		const currentManifest = {};
		const currentVersions = {};
		for (const [key, policy] of Object.entries(STORE_MIGRATION_POLICIES)) {
			const source = this.migrationSourceFor(key);
			if (!source) continue;
			currentVersions[source.table] = source.schemaVersion;
			if (policy.persistence === "none") continue;
			if (!this.sdk.storage.hasTable(source.table)) continue;
			currentManifest[source.table] = {
				schemaVersion: source.schemaVersion,
				persisted: true,
				refill: policy.refill,
				fields: source.fields()
			};
		}
		return {
			currentManifest,
			currentVersions
		};
	}
	/** Re-persist schema metadata after `clearAll()` drops the manifest. */
	async persistSchemaSnapshot() {
		const { currentManifest, currentVersions } = this.buildSchemaSnapshot();
		await this.sdk.storage.updateMetaData((meta) => ({
			...meta ?? {},
			modelSchemaVersions: currentVersions,
			modelSchemaManifest: currentManifest
		}));
	}
	/** Returns sorted producer keys whose tables are all registered locally. */
	supportedProducerKeys() {
		const supported = [];
		for (const [producer, tables] of STORAGE_TABLES_BY_PRODUCER) if (tables.every((table) => this.sdk.storage.hasTable(table))) supported.push(producer);
		return supported.sort();
	}
	/**
	* Compare the persisted schema manifest against the current model shapes
	* and apply the required migration. A new bootstrap-backed table is filled
	* by a resumable `EntityBackfillJob`. A changed one is refilled by a job
	* too, or by restarting its producers in the backfill checkpoint when one is
	* pending. A changed table keeps its rows unless the change is breaking: a
	* removed field or a version bump. A changed `userConfigs` always keeps its
	* row, marked unconfirmed until the refill delivers the server's copy.
	* Changed source-loaded tables clear without a refill. Memory-only stores
	* and tables the active adapter did not register never touch storage.
	*
	* Must be called after `storage.init()` but before store hydration.
	*/
	async migrateSchemaVersions() {
		const meta = await this.sdk.storage.getMetaData();
		const plan = this.planSchemaMigration(meta);
		const changedBootstrapProducers = producerKeysOf(plan.changedBootstrapTables);
		if (changedBootstrapProducers.length > 0) await this.sdk.storage.updateMetaData((current) => current?.bootstrapBackfill && {
			...current,
			bootstrapBackfill: rescopeBackfillCheckpoint(current.bootstrapBackfill, changedBootstrapProducers)
		});
		const refillsUserConfig = plan.changedBootstrapTables.some((entry) => entry.table === "userConfigs");
		const breakingBootstrapTables = plan.changedBootstrapTables.filter((entry) => entry.change === "breaking" && entry.table !== "userConfigs").map((entry) => entry.table);
		for (const table of [...breakingBootstrapTables, ...plan.changedSourceTables]) {
			await this.sdk.storage.clear(table);
			await this.sdk.partialIndexCoverage.clearTable(table);
			this.getStoreByTableName(table)?.clear();
		}
		for (const table of plan.removedTables) await this.sdk.partialIndexCoverage.clearTable(table);
		const queued = plan.nextJobs.filter((job) => plan.newBackfillJobIds.includes(job.id));
		await this.sdk.storage.updateMetaData((current) => {
			const nextMeta = {
				...current ?? {},
				modelSchemaVersions: plan.currentVersions,
				modelSchemaManifest: plan.currentManifest,
				entityBackfillJobs: mergeQueuedJobs(current?.entityBackfillJobs ?? [], queued)
			};
			if (changedBootstrapProducers.length > 0 || queued.length > 0) {
				delete nextMeta.deltaCheckpoint;
				const pin = current?.bootstrapBackfill?.maxSyncId;
				if (pin !== void 0 && (nextMeta.lastSyncId ?? 0) > pin) nextMeta.lastSyncId = pin;
			}
			if (refillsUserConfig) nextMeta.userConfigRefillPending = true;
			return nextMeta;
		});
	}
	/**
	* Pure schema diff against persisted state.
	* Reads table metadata only; no writes before the apply phase.
	*/
	planSchemaMigration(meta) {
		const diff = this.diffStoreSchemas(meta);
		const previousManifest = meta?.modelSchemaManifest;
		const previousVersions = meta?.modelSchemaVersions ?? {};
		const removedTables = previousManifest ? Object.keys(previousManifest).filter((table) => !(table in diff.currentManifest)) : Object.keys(previousVersions).filter((table) => !(table in diff.currentVersions));
		let nextJobs = [...meta?.entityBackfillJobs ?? []];
		const newBackfillJobIds = [];
		if (diff.changedBootstrapTables.length > 0 && !meta?.bootstrapBackfill) {
			const refillJob = {
				...buildBackfillJob(diff.changedBootstrapTables, diff.currentManifest),
				trigger: "schema_change"
			};
			nextJobs = nextJobs.filter((job) => !isSupersededBy(job, refillJob));
			nextJobs.push(refillJob);
			newBackfillJobIds.push(refillJob.id);
		}
		if (diff.newBootstrapTables.length > 0) {
			const job = buildBackfillJob(diff.newBootstrapTables, diff.currentManifest);
			if (!nextJobs.some((existing) => existing.id === job.id)) {
				nextJobs.push(job);
				newBackfillJobIds.push(job.id);
			}
		}
		return {
			currentManifest: diff.currentManifest,
			currentVersions: diff.currentVersions,
			changedBootstrapTables: diff.changedBootstrapTables,
			changedSourceTables: diff.changedSourceTables,
			removedTables,
			nextJobs,
			newBackfillJobIds
		};
	}
	/** Diff persisted schemas against current models. */
	diffStoreSchemas(meta) {
		const previousManifest = meta?.modelSchemaManifest;
		const previousVersions = meta?.modelSchemaVersions ?? {};
		const hasPersistedState = previousManifest !== void 0 || Object.keys(previousVersions).length > 0;
		const { currentManifest, currentVersions } = this.buildSchemaSnapshot();
		const changedBootstrapTables = [];
		const changedSourceTables = [];
		const newBootstrapTables = [];
		for (const [key, policy] of Object.entries(STORE_MIGRATION_POLICIES)) {
			const table = this.migrationSourceFor(key)?.table;
			if (!table) continue;
			const entry = currentManifest[table];
			if (!entry) continue;
			if (!hasPersistedState) continue;
			const previousEntry = previousManifest?.[table];
			const previousVersion = previousManifest ? previousEntry?.schemaVersion : previousVersions[table];
			if (previousVersion === void 0) {
				if (policy.refill === "bootstrap") newBootstrapTables.push({
					table,
					policy
				});
				continue;
			}
			if (!(previousEntry !== void 0 && previousEntry.fields.join(",") !== entry.fields.join(",")) && entry.schemaVersion <= previousVersion) continue;
			if (entry.refill !== "bootstrap") {
				changedSourceTables.push(table);
				continue;
			}
			const breaking = (previousEntry?.fields.some((field) => !entry.fields.includes(field)) ?? false) || entry.schemaVersion > previousVersion;
			changedBootstrapTables.push({
				table,
				policy,
				change: breaking ? "breaking" : "additive"
			});
		}
		return {
			currentManifest,
			currentVersions,
			changedBootstrapTables,
			changedSourceTables,
			newBootstrapTables
		};
	}
	registerStore(StoreClass, name) {
		const store = new StoreClass(this.sdk);
		const storeName = name ?? this.getStoreNameForModelName(store.modelName);
		invariant(storeName, `No store found for model name "${store.modelName}"`);
		return store;
	}
	getStoreNameForModelName(modelName) {
		for (const key of Object.keys(this)) {
			const store = this[key];
			if (store && typeof store === "object" && "modelName" in store && store.modelName === modelName) return key;
		}
		const storeName = getTableName(modelName);
		if (storeName) return storeName;
	}
};
/** Exhaustive per-store migration and refill policies. */
const STORE_MIGRATION_POLICIES = {
	threads: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["threads", "routine_threads"]
	},
	messages: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["threads", "routine_threads"]
	},
	drafts: {
		persistence: "none",
		refill: "none",
		producerKeys: []
	},
	files: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["files"]
	},
	journals: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["journals"]
	},
	arenas: {
		persistence: "required",
		refill: "source",
		producerKeys: []
	},
	routines: {
		persistence: "none",
		refill: "none",
		producerKeys: []
	},
	routineDrafts: {
		persistence: "none",
		refill: "none",
		producerKeys: []
	},
	reports: {
		persistence: "none",
		refill: "none",
		producerKeys: []
	},
	meetings: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["meetings"]
	},
	projects: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["projects"]
	},
	agents: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["agents"]
	},
	mcpClientGrants: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["mcp_client_grants"]
	},
	calendarEvents: {
		persistence: "platform",
		refill: "source",
		producerKeys: []
	},
	actionCards: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["action_cards"]
	},
	userConfigs: {
		persistence: "required",
		refill: "bootstrap",
		producerKeys: ["user_configs"]
	}
};
//#endregion
//#region src/core/sync/sync-plan.ts
function planSync(state) {
	const { cursor } = state;
	if (!cursor) return {
		kind: "full_bootstrap",
		reason: triggerWithoutCursor(state)
	};
	if (state.backfill) return {
		kind: "resume_backfill",
		backfill: state.backfill
	};
	if (state.fullBootstrapPending) return {
		kind: "full_bootstrap",
		reason: state.pendingTrigger ?? "unknown"
	};
	if (state.pendingRefillProducers.length > 0) return {
		kind: "scoped_refill",
		fromSyncId: cursor.lastSyncId,
		producers: state.pendingRefillProducers,
		...deltaResume(state)
	};
	return {
		kind: "delta",
		fromSyncId: cursor.lastSyncId,
		...deltaResume(state)
	};
}
/** The checkpoint a delta from `fromSyncId` resumes. Any other cursor means a later cursor write ended the walk this checkpoint belongs to */
function resumableDeltaCheckpoint(checkpoint, fromSyncId) {
	return checkpoint && checkpoint.clientSyncId === fromSyncId ? checkpoint : void 0;
}
function deltaResume(state) {
	const resume = resumableDeltaCheckpoint(state.deltaCheckpoint, state.cursor?.lastSyncId);
	return resume ? { resume } : {};
}
function triggerWithoutCursor(state) {
	if (state.hasEntityRows) return "meta_lost_with_data";
	const reason = state.lastReset?.reason;
	switch (reason) {
		case void 0: return "first_sync";
		case "server_reset":
		case "db_unrecognised": return reason;
		case "user_logout":
		case "auth_expired_logout":
		case "profile_missing":
		case "onboarding_exit":
		case "force_refresh": return "after_logout";
		default: return reason;
	}
}
//#endregion
//#region src/core/sync/bootstrap-manager.ts
function syncRunHeaders(run) {
	if (!run) return {};
	return {
		"x-sync-run-id": run.runId,
		"x-sync-page-index": String(run.pageIndex),
		"x-sync-trigger": run.trigger
	};
}
function entityBackfillTrigger(job) {
	return job.trigger ?? "new_tables";
}
function entityBackfillRunContext(job, drain) {
	return {
		runId: job.runId ?? job.id,
		pageIndex: drain.pages,
		trigger: entityBackfillTrigger(job)
	};
}
function recordStreamLine(state, line) {
	const { type } = line;
	if (type === "_meta" || type === "_error" || type === "_page") return;
	state.received.add(type);
	if (type === "threads" || type === "routine_threads") state.tails.addThreads(line.data);
	else if (type === "messages" || type === "routine_messages") state.tails.addMessages(line.data);
	if (!Array.isArray(line.data)) return;
	state.rows += line.data.length;
	const producer = syncLineTypeSpec(type)?.producer;
	if (producer) state.rowsByProducer[producer] = (state.rowsByProducer[producer] ?? 0) + line.data.length;
}
/** Settles with the value, or `null` as soon as the signal aborts. */
function unlessAborted(pending, signal) {
	if (signal.aborted) return Promise.resolve(null);
	return new Promise((resolve, reject) => {
		const onAbort = () => resolve(null);
		signal.addEventListener("abort", onAbort, { once: true });
		pending.then((value) => {
			signal.removeEventListener("abort", onAbort);
			resolve(value);
		}, (error) => {
			signal.removeEventListener("abort", onAbort);
			reject(error);
		});
	});
}
function incrementalSyncOutcomeFor(error, signal) {
	if (signal.aborted) return "aborted";
	if (error instanceof BootstrapInterruptedError || isTransientFetchError(error)) return "interrupted";
	return "failed";
}
function startEntityBackfillDrain(job) {
	return {
		startedAtMs: Date.now(),
		attempt: (job.attempts ?? 0) + 1,
		priorMs: job.activeMs ?? 0,
		pages: job.pages ?? 0,
		rows: job.rows ?? 0,
		applyTiming: {
			...NO_APPLY_TIMING,
			...job.applyTiming
		}
	};
}
function countEntityBackfillPage(drain, result) {
	drain.pages += 1;
	drain.rows += result.rows;
	drain.applyTiming = addPageApplyTiming(drain.applyTiming, result.applyTiming);
}
function nextSearchRebuildOwed(meta, trigger) {
	return {
		trigger,
		generation: (meta?.searchRebuildOwed?.generation ?? 0) + 1
	};
}
function withSearchRebuildOwed(next, meta, owed) {
	return owed ? {
		...next,
		searchRebuildOwed: nextSearchRebuildOwed(meta, owed)
	} : next;
}
function tablesWrittenBy(producers) {
	return [...new Set(producers.flatMap((producer) => STORAGE_TABLES_BY_PRODUCER.get(producer) ?? []))].sort();
}
const USER_CONFIG_PRODUCER = SYNC_LINE_TYPES.user_configs.producer;
const KNOWN_BACKFILL_PRODUCER_KEYS = Object.fromEntries(Object.values(STORE_MIGRATION_POLICIES).flatMap((policy) => policy.producerKeys.map((key) => [key, true])));
function createBackfillStoreRefresher(refreshLists, refreshAll, refreshIntervalMs) {
	let lastRefreshAt = performance.now();
	let owedTables = /* @__PURE__ */ new Set();
	let fullRefreshOwed = false;
	return {
		markPageApplied(rowsByProducer) {
			const producers = Object.keys(rowsByProducer).filter((producer) => (rowsByProducer[producer] ?? 0) > 0);
			for (const table of tablesWrittenBy(producers)) owedTables.add(table);
			fullRefreshOwed = true;
		},
		async refreshIfDue(isLastPage) {
			if (isLastPage) {
				await refreshAll();
				owedTables = /* @__PURE__ */ new Set();
				fullRefreshOwed = false;
				return;
			}
			if (refreshIntervalMs === null) return;
			const dueAt = lastRefreshAt + refreshIntervalMs;
			if (owedTables.size === 0 || performance.now() < dueAt) return;
			const tables = owedTables;
			owedTables = /* @__PURE__ */ new Set();
			await refreshLists(tables);
			lastRefreshAt = performance.now();
		},
		async flushOwed() {
			if (fullRefreshOwed) await refreshAll();
		}
	};
}
const ROUTINE_CHAT_SCOPE = "routine";
const MAX_CURSOR_HOLD_CYCLES = 3;
/** A `supports` token: this client can take one thread across several pages */
const THREAD_MESSAGE_PAGING = "thread_message_paging";
/** A `supports` token: rows and bodies for a window of chats on pages without a floor */
const CHAT_WINDOW = "chat_window";
/** A `supports` token: rows for the newest past meetings and every unended meeting on pages without a floor */
const MEETING_WINDOW = "meeting_window";
/** A `supports` token: a changed chat on a delta page carries the rows written since this client saw it */
const CHAT_DELTA_INCREMENTAL = "chat_delta_incremental";
const WINDOW_TOKENS = [
	CHAT_WINDOW,
	CHAT_DELTA_INCREMENTAL,
	MEETING_WINDOW
];
const MAX_ENTITY_BACKFILL_STREAM_ERRORS = 10;
const FLOOR_FALLBACK_MEMORY_MS = 9e5;
const PRUNE_REQUEST_TIMEOUT_MS = 3e4;
/** Only a run over wiped tables cannot hold a row the server deleted without a tombstone */
const STARTS_FROM_EMPTY_TABLES = /* @__PURE__ */ new Set([
	"first_sync",
	"after_logout",
	"server_reset",
	"stale_cache",
	"db_unrecognised"
]);
function newPruneProgress(epoch) {
	return {
		epoch,
		runId: v4(),
		table: PRUNE_TABLES[0],
		afterId: null,
		requests: 0,
		startedAt: (/* @__PURE__ */ new Date()).toISOString(),
		counts: {}
	};
}
function countPruneRequest(progress, ids, absent) {
	const counts = progress.counts[progress.table] ?? {
		checked: 0,
		absent: 0,
		pruned: 0
	};
	return {
		...progress,
		afterId: ids[ids.length - 1],
		requests: progress.requests + 1,
		counts: {
			...progress.counts,
			[progress.table]: {
				...counts,
				checked: counts.checked + ids.length,
				absent: counts.absent + absent
			}
		}
	};
}
function countPruned(progress, pruned) {
	const counts = progress.counts[progress.table];
	return {
		...progress,
		counts: {
			...progress.counts,
			[progress.table]: {
				...counts,
				pruned: counts.pruned + pruned
			}
		}
	};
}
function cursorsRepeat(seenCursorKeys, cursors) {
	const cursorKey = JSON.stringify(cursors);
	if (seenCursorKeys.has(cursorKey)) return true;
	seenCursorKeys.add(cursorKey);
	return false;
}
function deltaRunContext(resume) {
	return {
		runId: resume?.runId ?? v4(),
		pageIndex: resume?.pages ?? 0,
		trigger: "delta"
	};
}
const NO_DELTA_WALK_TIMING = {
	...NO_APPLY_TIMING,
	pages: 0,
	rows: 0,
	producers: /* @__PURE__ */ new Set()
};
/** Producers of a stream result that wrote rows. A zero-count key is a line type that carried none */
function producersWithRows(rowsByProducer) {
	return Object.keys(rowsByProducer).filter((producer) => (rowsByProducer[producer] ?? 0) > 0);
}
/** Also counts the page's rows into the cycle's report */
function addDeltaWalkPage(walk, result, stream) {
	stream.rows += result.rows;
	return {
		...addPageApplyTiming(walk, result.applyTiming),
		pages: walk.pages + 1,
		rows: walk.rows + result.rows,
		producers: /* @__PURE__ */ new Set([...walk.producers, ...producersWithRows(result.rowsByProducer)])
	};
}
function deltaCheckpointAfter(pageData, fromSyncId, run, startedAt, windowTokens) {
	return {
		clientSyncId: fromSyncId,
		maxSyncId: pageData.max_sync_id,
		cursors: { ...pageData.cursors },
		windowTokens: [...windowTokens],
		runId: run.runId,
		pages: run.pageIndex,
		startedAt
	};
}
/** A checkpoint with a cursor for a producer this client no longer syncs is dropped, and the delta walks again from its floor */
function usableDeltaCheckpoint(checkpoint, supportedProducers) {
	if (!checkpoint) return void 0;
	return Object.keys(checkpoint.cursors).every((producer) => supportedProducers.includes(producer)) ? checkpoint : void 0;
}
function failedRowCount(persistFailures) {
	return persistFailures.reduce((sum, failure) => sum + failure.rowCount, 0);
}
function persistFailureReport(persistFailures) {
	return {
		failureCount: persistFailures.length,
		failures: persistFailures.map((f) => ({
			entityType: f.entityType,
			message: f.error.message,
			syncId: f.syncId,
			rowCount: f.rowCount
		})),
		engineContext: {
			kind: "bootstrap",
			phase: "writingToDatabase"
		}
	};
}
function isFullBootstrapPlan(plan) {
	return plan.kind === "full_bootstrap" || plan.kind === "resume_backfill";
}
function asSyncResetRequired(error) {
	if (error instanceof SyncResetRequiredError) return error;
	if (!(error instanceof HttpError) || error.status !== 409) return void 0;
	if (error.detail?.code !== "sync-reset-required") return void 0;
	const reason = typeof error.detail.reason === "string" ? error.detail.reason : "unknown";
	return new SyncResetRequiredError(`Sync reset required by server (${reason})`, reason);
}
/**
* Exponential backoff delay for the Nth (1-indexed) interrupted-sync retry,
* capped at `SYNC_RETRY_MAX_DELAY_MS`: 1s, 2s, 4s, 8s, 16s, then 30s.
*/
function interruptedRetryDelayMs(attempt) {
	const exponential = SYNC_RETRY_INITIAL_DELAY_MS * 2 ** (attempt - 1);
	return Math.min(exponential, SYNC_RETRY_MAX_DELAY_MS);
}
/** For entities synced row by row, the packet's syncId is the highest sync_id among its rows */
function deriveMaxSyncId(rows) {
	return rows.reduce((max, row) => Math.max(max, row.sync_id ?? 0), 0);
}
function emptyStreamResult(source) {
	return {
		received: /* @__PURE__ */ new Set(),
		persistFailures: [],
		meta: void 0,
		streamError: void 0,
		pageData: void 0,
		rows: 0,
		rowsByProducer: {},
		bytes: 0,
		source,
		durationMs: 0,
		parseMs: 0,
		tails: new ChatTailCollector()
	};
}
function deltaEndReason(error, signal) {
	if (signal.aborted) return "cancelled";
	return isTransientFetchError(error) ? "interrupted" : void 0;
}
/** Keep only cursors for producers requested by the job. */
function scopeCursorsToJob(job, pageData) {
	return Object.fromEntries(job.producerKeys.filter((producerKey) => producerKey in pageData.cursors).map((producerKey) => [producerKey, pageData.cursors[producerKey]]));
}
/** Where a walk stopped inside a thread it has served only in part: a chat producer's cursor that stops inside a thread holds it */
function threadPositionsInProgress(...cursorSets) {
	return cursorSets.flatMap((cursors) => Object.values(cursors ?? {}).flatMap((cursor) => typeof cursor === "object" && cursor?.thread ? [cursor.thread] : []));
}
function threadsInProgress(...cursorSets) {
	return threadPositionsInProgress(...cursorSets).map((position) => position.pk);
}
function lacksNextCursor(job, pageData) {
	return job.producerKeys.some((producerKey) => pageData.has_more[producerKey] === true && (pageData.cursors[producerKey] ?? null) === null);
}
/**
* BootstrapManager handles hydrating stores from local storage and syncing with the server.
*/
var BootstrapManager = class {
	sdk;
	bootstrapPromise = null;
	bootstrapAbortController;
	backgroundBootstrapPromise = null;
	_isBackfilling = false;
	/** Tables a delta wrote whose list stores are not refreshed yet. A walk that stops mid-way keeps them */
	pendingDeltaRefreshTables = /* @__PURE__ */ new Set();
	/** Set while a catch-up runs, which logs its own `cycle done` mode and leaves the network-failure count and lifecycle recovery alone. The backfill holds the sync slot meanwhile, so no other sync can finish while it is set */
	catchingUp = false;
	/**
	* Shared by the `bootstrap()` calls made during a backfill until a catch-up
	* takes it. It resolves when that catch-up ends, or, if the catch-up gets a
	* sync reset or none takes it, when the cycle it was made in ends.
	* ai-docs/architecture/frontend/sync.md says when each call returns.
	*/
	catchUpRequest = null;
	/**
	* Chats shown running that the next sync run checks with the server. `markRunningChatsForCheck` marks
	* them, `checkRunningChats` marks them and waits for a sync run, and the run checks them in
	* `checkMarkedRunningChats`
	*/
	runningChatsToCheck = /* @__PURE__ */ new Set();
	stalledEntityBackfillJobIds = /* @__PURE__ */ new Set();
	entityBackfillJobHolds = /* @__PURE__ */ new Map();
	entityBackfillJobStreamErrors = /* @__PURE__ */ new Map();
	streamDeltasUntilMs = 0;
	/** From the last `_page` this process read; undefined until then, and on a server without the check */
	serverPruneEpoch;
	/** Set on a 404 so a server without the endpoint is asked once per launch */
	pruneCheckUnavailable = false;
	/** The full bootstrap attempt running in this process */
	fullBootstrapRun;
	/** The unawaited rebuild after a one-page full bootstrap, which outlives its cycle's signal */
	detachedRebuild;
	/**
	* Current lifecycle phase of the bootstrap process.
	*/
	phase = "idle";
	/**
	* Consecutive sync cycles interrupted by a network/connectivity failure,
	* reset to 0 on any successful cycle. Drives both the bounded auto-retry loop
	* and the user-facing `syncNetworkFailing` signal.
	*/
	consecutiveNetworkFailures = 0;
	retryTimer;
	retriesSuspended = false;
	/** Set when a cycle is planned: `full` for a full bootstrap or a resumed one, `incremental` for a delta or a refill. When a full bootstrap's backfill completes, its cycle sets `incremental` for the delta that ends the cycle. Otherwise it keeps its value until the next cycle is planned, so a one-page full bootstrap, or a full bootstrap whose backfill stops, leaves `full`. Catch-ups do not change it */
	lastBootstrapMode = "unknown";
	currentSyncRequestId;
	consecutiveFailingCycles = 0;
	/** Never reset in this process, so startup telemetry sees a refill that already ended */
	refillPlanned = false;
	streamCapture = null;
	bootstrapListeners = /* @__PURE__ */ new Set();
	nextBootstrapListeners = /* @__PURE__ */ new Set();
	constructor(sdk) {
		this.sdk = sdk;
		makeObservable(this, {
			phase: observable,
			consecutiveNetworkFailures: observable,
			_isBackfilling: observable,
			isHydrated: computed,
			syncNetworkFailing: computed,
			reset: action,
			recordSyncSuccess: action,
			recordSyncInterruption: action,
			recordNonNetworkFailure: action
		});
		this.setPhase = action(this.setPhase.bind(this));
	}
	get isHydrated() {
		return this.phase === "ready";
	}
	startStreamCapture() {
		this.streamCapture = [];
	}
	stopStreamCapture() {
		const lines = this.streamCapture ?? [];
		this.streamCapture = null;
		return lines;
	}
	async replayLines(lines) {
		const state = emptyStreamResult("live");
		const signal = new AbortController().signal;
		for (const line of lines) await this.handleBootstrapStreamLine(state, line, signal);
		await this.sdk.syncProcessor.idle();
		await this.applyStreamTails(state);
		this.warnStreamBootstrapPersistFailures(state.persistFailures);
	}
	/**
	* True once sync has failed for `SYNC_NETWORK_FAILURE_THRESHOLD` consecutive
	* cycles due to network/connectivity — a persistent failure the user should
	* be told about, not a one-off interruption.
	*/
	get syncNetworkFailing() {
		return this.consecutiveNetworkFailures >= 2;
	}
	/**
	* True during a backfill, as ai-docs/architecture/frontend/sync.md defines
	* it. Meanwhile a `bootstrap()` call starts no sync of its own: it asks the
	* backfill for a catch-up.
	*/
	get isBackfilling() {
		return this._isBackfilling;
	}
	reset() {
		if (this.bootstrapAbortController) this.bootstrapAbortController.abort();
		this.cancelScheduledRetry();
		this.phase = "idle";
		this.consecutiveNetworkFailures = 0;
		this.consecutiveFailingCycles = 0;
		this.bootstrapPromise = null;
		this.bootstrapAbortController = void 0;
		this._isBackfilling = false;
		this.pendingDeltaRefreshTables.clear();
		this.runningChatsToCheck.clear();
		this.endFullBootstrapRun("aborted");
		this.stalledEntityBackfillJobIds.clear();
		this.entityBackfillJobHolds.clear();
		this.entityBackfillJobStreamErrors.clear();
	}
	/** Clear failure tracking after a sync cycle completes successfully. */
	recordSyncSuccess() {
		this.clearNetworkFailureTracking();
	}
	/**
	* Clear failure tracking after a sync cycle fails with a genuine (non-network)
	* error. Such a cycle breaks the network-failure streak, so consecutive
	* counting and the user-facing signal must not carry across it.
	*/
	recordNonNetworkFailure() {
		this.clearNetworkFailureTracking();
	}
	clearNetworkFailureTracking() {
		this.cancelScheduledRetry();
		this.consecutiveNetworkFailures = 0;
	}
	/**
	* Record a network-interrupted sync cycle and queue a bounded backoff retry
	* that resumes from the durable checkpoint, rather than waiting for the next
	* natural poll.
	*/
	recordSyncInterruption() {
		this.consecutiveNetworkFailures += 1;
		this.scheduleInterruptedRetry();
	}
	scheduleInterruptedRetry() {
		if (this.retriesSuspended) return;
		if (this.retryTimer) return;
		if (this.consecutiveNetworkFailures >= 5) return;
		const delay = interruptedRetryDelayMs(this.consecutiveNetworkFailures);
		this.retryTimer = setTimeout(() => {
			this.retryTimer = void 0;
			if (this.retriesSuspended) return;
			this.bootstrap();
		}, delay);
	}
	/** Cancel any pending interrupted-sync retry (on success or reset). */
	cancelScheduledRetry() {
		if (this.retryTimer) {
			clearTimeout(this.retryTimer);
			this.retryTimer = void 0;
		}
	}
	/**
	* Stop arming interrupted-sync retries and cancel any pending one. Called when
	* polling stops so an in-flight bootstrap that fails afterwards can't schedule
	* a fresh retry behind the scenes.
	*/
	suspendRetries() {
		this.retriesSuspended = true;
		this.cancelScheduledRetry();
	}
	/** Allow interrupted-sync retries again (called when polling resumes). */
	resumeRetries() {
		this.retriesSuspended = false;
	}
	/** Register a callback that fires after every bootstrap cycle. Returns an unlisten function. */
	onBootstrap(cb) {
		this.bootstrapListeners.add(cb);
		return () => {
			this.bootstrapListeners.delete(cb);
		};
	}
	/** Register a callback that fires once after the next bootstrap cycle, then auto-removes. */
	onNextBootstrap(cb) {
		this.nextBootstrapListeners.add(cb);
	}
	fireBootstrapListeners() {
		const bootstrapListenersSnapshot = [...this.bootstrapListeners];
		const nextBootstrapListenersSnapshot = [...this.nextBootstrapListeners];
		this.nextBootstrapListeners.clear();
		for (const cb of bootstrapListenersSnapshot) try {
			cb();
		} catch (err) {
			this.sdk.logger.error("onBootstrap listener threw", {
				metadata: {
					error: err,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				},
				context: "BootstrapManager:fireBootstrapListeners"
			});
		}
		for (const cb of nextBootstrapListenersSnapshot) try {
			cb();
		} catch (err) {
			this.sdk.logger.error("onNextBootstrap listener threw", {
				metadata: {
					error: err,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				},
				context: "BootstrapManager:fireBootstrapListeners"
			});
		}
	}
	setPhase(phase) {
		this.phase = phase;
		if (phase === "ready") this.sdk.stores.projects?.scheduleItemCountRefresh();
	}
	async cancelInFlight() {
		if (this.bootstrapAbortController) this.bootstrapAbortController.abort();
		const foreground = this.bootstrapPromise;
		const background = this.backgroundBootstrapPromise;
		try {
			if (foreground) await foreground.catch(() => void 0);
			if (background) await background.catch(() => void 0);
		} catch (error) {
			if (error instanceof Error && error.name !== "AbortError") this.sdk.logger.error("Error draining in-flight bootstrap", {
				metadata: {
					error,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				},
				context: "BootstrapManager:cancelInFlight"
			});
		}
		await this.stopDetachedRebuild();
	}
	async bootstrap(options) {
		await this.requestSync(options).returnsAfter;
	}
	/**
	* Starts a sync cycle, or, while one runs, finds what serves the call:
	* during a backfill the catch-up the call asks for (`requestCatchUp`),
	* otherwise the cycle running. `returnsAfter` is what `bootstrap()` waits
	* for: that catch-up, or the cycle's foreground part, or all of a cycle
	* that already goes on in the background.
	*/
	requestSync(options) {
		if (this.bootstrapPromise) {
			this.sdk.logger.info("Bootstrap call skipped: previous call still in progress.");
			return {
				servedBy: "cycle",
				returnsAfter: this.bootstrapPromise
			};
		}
		if (this.backgroundBootstrapPromise) {
			if (this._isBackfilling) return {
				servedBy: "catch_up",
				returnsAfter: this.requestCatchUp(this.backgroundBootstrapPromise)
			};
			this.sdk.logger.debug("Bootstrap call skipped: previous background sync still in progress.");
			return {
				servedBy: "cycle",
				returnsAfter: this.backgroundBootstrapPromise
			};
		}
		return {
			servedBy: "cycle",
			returnsAfter: this.runCycle(options)
		};
	}
	/** Asks for a sync as `bootstrap()` does, and waits for all of what serves the call: the catch-up, or the whole cycle, background part included */
	async waitForWholeSync() {
		const sync = this.requestSync();
		await sync.returnsAfter;
		if (sync.servedBy === "cycle") await this.backgroundBootstrapPromise;
	}
	async runCycle(options) {
		const startedAt = Date.now();
		this.bootstrapPromise = this.performBootstrapWithTimeout();
		try {
			await this.bootstrapPromise;
		} catch (error) {
			if (error instanceof BootstrapInterruptedError) {
				this.recordSyncInterruption();
				if (this.phase !== "ready") this.setPhase("idle");
				if (options?.surfaceSoftInterrupt) throw error;
				return;
			}
			if (!(error instanceof ReportedBootstrapError)) this.sdk.logger.error("Bootstrap failed", {
				context: "BootstrapManager:bootstrap",
				metadata: {
					durationMs: Date.now() - startedAt,
					phase: this.phase,
					error,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				}
			});
			throw error;
		} finally {
			this.bootstrapPromise = null;
		}
	}
	async performBootstrapWithTimeout() {
		let isFullBootstrap = true;
		const controller = new AbortController();
		this.bootstrapAbortController = controller;
		let timeoutId;
		const timeoutPromise = new Promise((_, reject) => {
			timeoutId = setTimeout(() => {
				const message = `Bootstrap timed out after ${BOOTSTRAP_TIMEOUT_MS}ms`;
				reject(isFullBootstrap ? new Error(message) : new BootstrapInterruptedError(message));
				controller.abort();
			}, BOOTSTRAP_TIMEOUT_MS);
		});
		const hydration = this.phase === "ready" ? void 0 : this.loadEagerStores();
		let running;
		try {
			const plan = await Promise.race([unlessAborted(this.planCycle(controller.signal), controller.signal), timeoutPromise]);
			if (plan === null) {
				if (this.bootstrapAbortController === controller) this.bootstrapAbortController = void 0;
				return;
			}
			isFullBootstrap = isFullBootstrapPlan(plan);
			running = this.performBootstrap(plan, controller.signal, hydration);
			await Promise.race([running, timeoutPromise]);
		} finally {
			clearTimeout(timeoutId);
			if (hydration) await this.joinStoppedCycle(hydration);
			if (running) await this.joinStoppedCycle(running);
		}
	}
	/** Bounded, so a local call that ignores the abort cannot hold every later cycle and the logout */
	async joinStoppedCycle(running) {
		let graceId;
		const graceExpired = new Promise((resolve) => {
			graceId = setTimeout(() => {
				this.sdk.logger.error("Bootstrap cycle did not stop after its timeout", {
					context: "BootstrapManager:joinStoppedCycle",
					metadata: {
						graceMs: BOOTSTRAP_JOIN_GRACE_MS,
						phase: this.phase,
						syncRequestId: this.currentSyncRequestId
					}
				});
				resolve();
			}, BOOTSTRAP_JOIN_GRACE_MS);
		});
		await Promise.race([running.catch(() => void 0), graceExpired]);
		clearTimeout(graceId);
	}
	/** Not joined after a timeout, unlike the cycle: the metadata read ignores the signal, and a hung read would hold every later cycle */
	async planCycle(signal) {
		const plan = planSync(await this.observeLocalSyncState());
		await this.markPlannedThreads(plan);
		if (signal.aborted) return plan;
		if (plan.kind !== "delta") this.refillPlanned = true;
		this.lastBootstrapMode = isFullBootstrapPlan(plan) ? "full" : "incremental";
		this.sdk.logger.debug("BootstrapManager: cycle start", {
			context: "BootstrapManager:cycle",
			metadata: {
				mode: this.lastBootstrapMode,
				plan,
				phase: this.phase,
				syncRequestId: this.currentSyncRequestId
			}
		});
		return plan;
	}
	async performBootstrap(plan, signal, hydration) {
		const wasAlreadyReady = hydration === void 0;
		try {
			if (signal.aborted) return;
			if (hydration) {
				this.setPhase("connectingDatabase");
				this.setPhase("hydratingFromCache");
				await this.completeHydration(hydration);
				this.sdk.reportStartupStage("stores_hydrated");
				this.restorePersistedSlicesInBackground();
				await this.sdk.transactionQueue.resendCached();
				this.sdk.reportStartupStage("queue_resent");
				if (signal.aborted) return;
			}
			if (plan.kind === "resume_backfill") {
				this.resumePagedBackfill(plan.backfill, signal, wasAlreadyReady);
				return;
			}
			if (plan.kind === "full_bootstrap") {
				await this.performInitialPagedBootstrap(plan.reason, wasAlreadyReady, signal);
				return;
			}
			this.performBackgroundSync(plan, signal, wasAlreadyReady);
			if (!signal.aborted && !wasAlreadyReady) this.setPhase("ready");
		} catch (error) {
			await this.handlePerformBootstrapError(error);
		} finally {
			this.cleanupAfterBootstrap(signal);
		}
	}
	startIncrementalSearchIndexing() {
		const start = () => {
			this.sdk.searchStore.startIndexing().catch((err) => {
				this.sdk.logger.warn("Incremental search indexing failed", { metadata: {
					error: err,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				} });
			});
		};
		const schedule = this.sdk.options.scheduleIncrementalSearchIndexing;
		if (schedule) schedule(start);
		else start();
	}
	async handlePerformBootstrapError(error) {
		if (error instanceof SyncResetRequiredError) {
			await this.recoverFromSyncReset(error, "BootstrapManager:performBootstrap");
			return;
		}
		if (error instanceof BootstrapInterruptedError) {
			this.recordSyncInterruption();
			if (this.phase !== "ready") this.setPhase("idle");
			return;
		}
		this.setPhase("errored");
		throw error;
	}
	async clearSyncCheckpoint(reason, context) {
		this.sdk.logger.warn("Sync reset required by server; clearing synced local state", {
			context,
			metadata: {
				reason,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		this.endFullBootstrapRun("aborted");
		await this.sdk.storage.markBootstrapStarted();
		await this.stopDetachedRebuild();
		await this.sdk.resetLocalData("server_reset");
	}
	resumePagedBackfill(pendingBackfill, signal, wasAlreadyReady) {
		if (!signal.aborted && !wasAlreadyReady) this.setPhase("ready");
		this.performPagedBackfill(pendingBackfill, signal, wasAlreadyReady);
	}
	async performInitialPagedBootstrap(trigger, wasAlreadyReady, signal) {
		if (!wasAlreadyReady) this.setPhase("fetchingFromServer");
		await this.sdk.storage.markBootstrapStarted();
		await this.beginFullBootstrapRun(false, trigger);
		let backfillCheckpoint;
		const windowTokens = this.windowTokens();
		try {
			const firstPageResult = await this.consumePagedBootstrapPage(void 0, signal, {
				run: this.fullBootstrapRunContext(),
				windowTokens
			});
			if (signal.aborted || !firstPageResult) return;
			this.countFullBootstrapPage(firstPageResult);
			backfillCheckpoint = this.createBackfillCheckpoint(firstPageResult.pageData, windowTokens);
			await this.finalizeStreamBootstrap(firstPageResult, Date.now(), void 0, signal, backfillCheckpoint);
			await this.confirmUserConfigIfDelivered(firstPageResult.pageData);
			this.markBackfillThreads(backfillCheckpoint?.cursors);
		} catch (error) {
			if (!signal.aborted) await this.failFullBootstrapRun(error);
			throw error;
		}
		if (!wasAlreadyReady) this.setPhase("ready");
		if (backfillCheckpoint) this.performPagedBackfill(backfillCheckpoint, signal);
		else {
			await this.completeFullBootstrap();
			if (signal.aborted) return;
			const controller = new AbortController();
			const done = this.rebuildSearchIndex("full_bootstrap", controller.signal).finally(() => {
				if (this.detachedRebuild?.done === done) this.detachedRebuild = void 0;
			});
			this.detachedRebuild = {
				controller,
				done
			};
		}
	}
	/** Awaited before every wipe, or the rebuild could land writes and `hasIndexed` over the emptied index */
	async stopDetachedRebuild() {
		const rebuild = this.detachedRebuild;
		if (!rebuild) return;
		rebuild.controller.abort();
		await rebuild.done;
	}
	async completeFullBootstrap() {
		await this.sdk.storage.updateMetaData((meta) => {
			const nextMeta = {
				...meta ?? {},
				entityBackfillJobs: [],
				searchRebuildOwed: nextSearchRebuildOwed(meta, "full_bootstrap")
			};
			const trigger = meta?.fullBootstrapRun?.trigger;
			if (this.serverPruneEpoch !== void 0 && trigger !== void 0 && STARTS_FROM_EMPTY_TABLES.has(trigger)) nextMeta.pruneEpoch = this.serverPruneEpoch;
			delete nextMeta.fullBootstrapRun;
			return nextMeta;
		});
		await this.sdk.stores.persistSchemaSnapshot();
		await this.sdk.storage.markBootstrapComplete();
		await this.sdk.userConfig.confirmServerCopy();
		this.endFullBootstrapRun("completed");
		await this.sdk.stores.actionCards.revokeOpenCardsForChatsMissingLocally();
	}
	/** An in-process attempt stays open across transient retries */
	async beginFullBootstrapRun(resumed, trigger) {
		if (this.fullBootstrapRun) return;
		try {
			const previous = (await this.sdk.storage.getMetaData())?.fullBootstrapRun;
			if (previous?.open) this.reportFullBootstrap(previous, "interrupted");
			const run = startRunRecord(trigger, previous, resumed);
			run.runId = resumed && previous?.runId ? previous.runId : v4();
			run.activeMs = resumed ? previous?.activeMs ?? 0 : 0;
			if (resumed && previous?.applyTiming) run.applyTiming = {
				...NO_APPLY_TIMING,
				...previous.applyTiming
			};
			this.fullBootstrapRun = run;
			const record = { ...run };
			await this.sdk.storage.updateMetaData((current) => current && {
				...current,
				fullBootstrapRun: record
			});
		} catch (error) {
			this.sdk.logger.warn("Could not record full bootstrap attempt", {
				context: "BootstrapManager:beginFullBootstrapRun",
				metadata: { error }
			});
		}
	}
	countFullBootstrapPage(result) {
		if (!this.fullBootstrapRun) return;
		this.fullBootstrapRun.pages += 1;
		this.fullBootstrapRun.rows += result.rows;
		this.fullBootstrapRun.applyTiming = addPageApplyTiming(this.fullBootstrapRun.applyTiming ?? NO_APPLY_TIMING, result.applyTiming);
		this.fullBootstrapRun.activeMs = (this.fullBootstrapRun.activeMs ?? 0) + result.durationMs;
	}
	fullBootstrapRunContext() {
		const run = this.fullBootstrapRun;
		if (!run?.runId) return void 0;
		return {
			runId: run.runId,
			pageIndex: run.pages,
			trigger: run.trigger
		};
	}
	endFullBootstrapRun(outcome) {
		const run = this.fullBootstrapRun;
		if (!run) return;
		this.fullBootstrapRun = void 0;
		run.updatedAtMs = Date.now();
		run.open = false;
		this.reportFullBootstrap(run, outcome);
	}
	/** Transient errors and resets don't end the attempt: the retry or reset path owns it */
	async failFullBootstrapRun(error) {
		if (error instanceof BootstrapInterruptedError || error instanceof SyncResetRequiredError || isTransientFetchError(error)) return;
		const run = this.fullBootstrapRun;
		if (!run) return;
		this.endFullBootstrapRun("failed");
		try {
			await this.sdk.storage.updateMetaData((meta) => meta && {
				...meta,
				fullBootstrapRun: { ...run }
			});
		} catch (storeError) {
			this.sdk.logger.warn("Could not close failed full bootstrap attempt", {
				context: "BootstrapManager:failFullBootstrapRun",
				metadata: { error: storeError }
			});
		}
	}
	reportFullBootstrap(run, outcome) {
		const durationMs = attemptDurationMs(run);
		this.sdk.reportFullBootstrap({
			runId: run.runId,
			outcome,
			trigger: run.trigger,
			scope: "full",
			resumed: run.resumed,
			attempt: run.attempt,
			durationMs,
			totalDurationMs: run.priorMs + durationMs,
			pages: run.pages,
			rows: run.rows,
			...NO_APPLY_TIMING,
			...run.applyTiming,
			activeMs: run.activeMs
		});
	}
	/** Compares by signal: after a reset, a newer cycle can own the controller by the time this one ends */
	cleanupAfterBootstrap(signal) {
		const backgroundPromise = this.backgroundBootstrapPromise;
		const finalize = () => {
			if (this.backgroundBootstrapPromise === backgroundPromise) this.backgroundBootstrapPromise = null;
			if (this.bootstrapAbortController?.signal === signal) this.bootstrapAbortController = void 0;
		};
		if (backgroundPromise) backgroundPromise.finally(finalize);
		else finalize();
	}
	performBackgroundSync(plan, signal, wasAlreadyReady) {
		this.backgroundBootstrapPromise = this.runBackgroundDelta(signal, async () => {
			let start = plan;
			if (plan.kind === "scoped_refill") {
				await this.drainEntityBackfillJobs(plan.fromSyncId, signal);
				if (signal.aborted) return void 0;
				start = await this.readDeltaStartAndMarkThreads(plan.fromSyncId);
			}
			await this.confirmUserConfigUnlessRefillOwed();
			if (!wasAlreadyReady) this.sdk.reportStartupStage("backfill_drained");
			return start;
		});
	}
	/**
	* The delta that ends a cycle, in its background part, reported through
	* `onIncrementalSyncFinished`. `prepare` runs first and returns where the
	* delta starts, or undefined once the cycle is cancelled. The reported time
	* includes `prepare`: for a refill that is the whole drain of its entity
	* backfill jobs, and for a delta, or after a full bootstrap, only short
	* steps on local data.
	*/
	async runBackgroundDelta(signal, prepare) {
		const startedAt = Date.now();
		const stream = emptyStreamResult("live");
		let outcome = "completed";
		let endedAt;
		try {
			const start = await prepare();
			if (!start) {
				outcome = "aborted";
				return;
			}
			this.startIncrementalSearchIndexing();
			const completed = await this.runDelta(start.fromSyncId, start.resume, signal, stream);
			endedAt = Date.now();
			if (signal.aborted) outcome = "aborted";
			if (completed) await this.continuePruneCheck(signal);
		} catch (error) {
			endedAt ??= Date.now();
			outcome = incrementalSyncOutcomeFor(error, signal);
			await this.handleBackgroundSyncError(error, signal);
		} finally {
			this.sdk.reportIncrementalSync({
				outcome,
				durationMs: (endedAt ?? Date.now()) - startedAt,
				rows: stream.rows
			});
		}
	}
	/** `lastSyncId` moves to the pin only after the last page, so a lost walk never skips a row. True once the walk reached the pin without a held page */
	async runDelta(fromSyncId, resume, signal, stream) {
		const startTime = Date.now();
		const run = deltaRunContext(resume);
		const windowTokens = resume ? resume.windowTokens ?? [] : this.windowTokens();
		if (Date.now() < this.streamDeltasUntilMs) return this.tryStreamingBootstrap(fromSyncId, run, windowTokens, signal, stream);
		const startedAt = resume?.startedAt ?? new Date(startTime).toISOString();
		return this.walkDeltaPages({
			fromSyncId,
			run,
			windowTokens,
			startedAt,
			signal
		}, resume, startTime, stream);
	}
	async walkDeltaPages(delta, resume, startTime, stream) {
		const { fromSyncId, run, windowTokens, signal } = delta;
		let checkpoint = resume;
		const seenCursorKeys = /* @__PURE__ */ new Set([JSON.stringify(resume?.cursors ?? {})]);
		let walk = NO_DELTA_WALK_TIMING;
		for (;;) {
			const result = await this.consumePagedBootstrapPage(checkpoint?.cursors, signal, {
				clientSyncId: fromSyncId,
				maxSyncId: checkpoint?.maxSyncId,
				run: { ...run },
				windowTokens
			});
			if (!result || signal.aborted) return false;
			const meta = this.ensureStreamBootstrapMeta(result.received, result.streamError, result.meta);
			const pageData = result.pageData;
			if (pageData?.client_sync_id !== fromSyncId) return this.streamDeltaInstead(fromSyncId, run, windowTokens, signal, stream);
			walk = addDeltaWalkPage(walk, result, stream);
			for (const table of tablesWrittenBy(producersWithRows(result.rowsByProducer))) this.pendingDeltaRefreshTables.add(table);
			const hold = this.cursorPolicy(result.persistFailures, {
				clientSyncId: fromSyncId,
				maxSyncId: pageData.max_sync_id
			});
			const done = !hold && Object.values(pageData.has_more).every((more) => !more);
			if (hold || done) return this.endDeltaWalk(meta, hold, startTime, walk, signal);
			checkpoint = await this.advanceDeltaWalk(delta, pageData, seenCursorKeys);
			if (!checkpoint) return false;
		}
	}
	/** Saves the walk's next checkpoint and yields to the UI. Undefined when the walk must stop: its cursors repeat, or it was cancelled */
	async advanceDeltaWalk(delta, pageData, seenCursorKeys) {
		const { fromSyncId, run, startedAt, windowTokens, signal } = delta;
		if (cursorsRepeat(seenCursorKeys, pageData.cursors)) {
			await this.abandonStalledDelta(run, fromSyncId, pageData);
			return;
		}
		run.pageIndex += 1;
		const checkpoint = deltaCheckpointAfter(pageData, fromSyncId, run, startedAt, windowTokens);
		if (signal.aborted) return void 0;
		await this.saveDeltaCheckpoint(checkpoint);
		await this.markDeltaThreads(checkpoint.cursors);
		await this.sdk.yieldPolicy.betweenPages();
		if (signal.aborted) {
			this.warnDeltaEndedEarly(emptyStreamResult("live"), run, "cancelled", Date.now());
			return;
		}
		return checkpoint;
	}
	async streamDeltaInstead(fromSyncId, run, windowTokens, signal, stream) {
		this.sdk.logger.warn("Server ignored the delta floor on bootstrap/page; streaming the delta instead", {
			context: "BootstrapManager:runDelta",
			metadata: {
				runId: run.runId,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		this.streamDeltasUntilMs = Date.now() + FLOOR_FALLBACK_MEMORY_MS;
		this.sdk.stores.messages.setThreadsSyncingHistory("delta", []);
		return this.tryStreamingBootstrap(fromSyncId, run, windowTokens, signal, stream);
	}
	async endDeltaWalk(meta, hold, startTime, walk, signal) {
		if (hold) await this.noteHeldSyncTime();
		else {
			await this.processMeta(meta);
			this.sdk.stores.messages.setThreadsSyncingHistory("delta", []);
		}
		await this.refreshDeltaStores();
		await this.fillRecordedChatGaps(signal);
		await this.checkMarkedRunningChats();
		await this.finishBootstrapSuccess(startTime, walk);
		return !hold;
	}
	/**
	* A delta applies its rows through the store handlers, which leave rows for
	* contexts nobody has open in SQLite only, so the list stores read the written
	* tables back after the walk. The tables survive a walk that stopped after
	* it saved its checkpoint, so the next cycle refreshes them, and the refresh
	* clears them. A full refresh stays for stream bootstraps, full
	* bootstraps, and backfills.
	*/
	async refreshDeltaStores() {
		if (this.pendingDeltaRefreshTables.size === 0) return;
		const tables = new Set(this.pendingDeltaRefreshTables);
		await this.refreshListStores(tables);
		for (const table of tables) this.pendingDeltaRefreshTables.delete(table);
	}
	async saveDeltaCheckpoint(checkpoint) {
		await this.sdk.storage.updateMetaData((current) => ({
			...current ?? {},
			deltaCheckpoint: checkpoint
		}), { notifyCrossWindowEntitySync: false });
	}
	/** One cycle's share of the check; its checkpoint carries the rest to later cycles. Best-effort: a failure pauses the check and never fails the cycle */
	async continuePruneCheck(signal) {
		const epoch = this.serverPruneEpoch;
		if (signal.aborted || this.pruneCheckUnavailable || epoch === void 0) return;
		let progress;
		try {
			const meta = await this.sdk.storage.getMetaData();
			if (!isPruneDue(epoch, meta?.pruneEpoch)) return;
			if (!this.isQuietForPruneCheck(meta)) return;
			progress = meta?.pruneProgress?.epoch === epoch ? meta.pruneProgress : newPruneProgress(epoch);
			for (let sent = 0; sent < 5;) {
				if (sent > 0) await this.sdk.yieldPolicy.betweenPages();
				if (signal.aborted) return;
				const ids = await this.pruneEntities(progress.table).listIdsAfter(progress.afterId ?? void 0, PRUNE_IDS_PER_REQUEST);
				const next = PRUNE_TABLES[PRUNE_TABLES.indexOf(progress.table) + 1];
				if (ids.length === 0 && next) {
					progress = {
						...progress,
						table: next,
						afterId: null
					};
					continue;
				}
				if (ids.length === 0) return await this.finishPruneCheck(progress, "completed");
				const absent = await this.fetchAbsentIds(progress, ids, signal);
				sent += 1;
				progress = countPruneRequest(progress, ids, absent.length);
				if (isPruneBraked(ids.length, absent.length)) {
					this.sdk.logger.warn("Prune check braked", {
						context: "BootstrapManager:continuePruneCheck",
						metadata: {
							table: progress.table,
							checked: ids.length,
							absent: absent.length,
							epoch
						}
					});
					return await this.finishPruneCheck(progress, "braked");
				}
				if (signal.aborted) return;
				const pruned = await this.pruneAbsentRows(progress.table, absent);
				progress = countPruned(progress, pruned);
				if (signal.aborted) return;
				await this.savePruneProgress(progress);
			}
		} catch (error) {
			this.pausePruneCheckOnError(error, epoch, progress, signal);
		}
	}
	pruneEntities(table) {
		return this.sdk.storage.entities[syncLineTypeSpec(table).storageTable];
	}
	isQuietForPruneCheck(meta) {
		const queue = this.sdk.transactionQueue;
		return meta?.lastSyncId !== void 0 && !meta.deltaCheckpoint && !meta.bootstrapBackfill && !(meta.entityBackfillJobs?.length ?? 0) && queue.getPendingCount() + queue.getExecutingCount() + queue.getAwaitingSyncCount() === 0;
	}
	async fetchAbsentIds(progress, ids, signal) {
		const run = {
			runId: progress.runId,
			pageIndex: progress.requests,
			trigger: "prune_check"
		};
		return (await this.sdk.apiClient.fetch("bootstrap/absent_ids", {
			method: "POST",
			headers: {
				"content-type": "application/json",
				...syncRunHeaders(run)
			},
			body: JSON.stringify({
				table: progress.table,
				ids
			}),
			signal,
			timeoutMs: PRUNE_REQUEST_TIMEOUT_MS,
			backoff: NO_BACKOFF
		})).absent_ids;
	}
	/** Reads each absent row now, not when its id was read, so a row that became unsettled in between is kept */
	async pruneAbsentRows(table, absent) {
		if (absent.length === 0) return 0;
		const { storageTable } = syncLineTypeSpec(table);
		const stored = await this.pruneEntities(table).getByIds(absent);
		const loaded = this.sdk.stores.getStoreByTableName(storageTable);
		const localRows = new Map(stored.map((row) => [row.id, loaded?.get(row.id) ?? row]));
		const isHeld = (id) => Boolean(this.sdk.transactionQueue.getTransactionForModel(storageTable, id)) || table === "threads" && this.sdk.stores.messages.isThreadHistorySyncing(id);
		const ids = prunableIds(table, absent, localRows, Date.now(), isHeld);
		if (ids.length === 0) return 0;
		const deletedAt = (/* @__PURE__ */ new Date()).toISOString();
		await this.handleNdjsonLine({
			type: table,
			data: ids.map((id) => ({
				id,
				deleted_at: deletedAt
			}))
		}, "live");
		return ids.length;
	}
	async savePruneProgress(progress) {
		await this.sdk.storage.updateMetaData((meta) => meta?.lastSyncId === void 0 ? void 0 : {
			...meta,
			pruneProgress: progress
		}, { notifyCrossWindowEntitySync: false });
	}
	async finishPruneCheck(progress, outcome) {
		await this.sdk.storage.updateMetaData((meta) => {
			if (meta?.lastSyncId === void 0) return void 0;
			const nextMeta = {
				...meta,
				pruneEpoch: progress.epoch
			};
			delete nextMeta.pruneProgress;
			return nextMeta;
		}, { notifyCrossWindowEntitySync: false });
		if ((await this.sdk.storage.getMetaData())?.pruneEpoch !== progress.epoch) return;
		this.sdk.reportPruneCheck({
			outcome,
			epoch: progress.epoch,
			runId: progress.runId,
			requests: progress.requests,
			durationMs: Date.now() - Date.parse(progress.startedAt),
			counts: progress.counts
		});
	}
	pausePruneCheckOnError(error, epoch, progress, signal) {
		if (signal.aborted) return;
		const status = error instanceof HttpError ? error.status : void 0;
		if (status === 404 || status === 422) this.pruneCheckUnavailable = true;
		const errorShape = describeError(error);
		this.sdk.logger.warn("Prune check paused", {
			context: "BootstrapManager:continuePruneCheck",
			metadata: {
				table: progress?.table,
				afterId: progress?.afterId,
				requests: progress?.requests,
				epoch,
				runId: progress?.runId,
				errorName: errorShape.name ?? "unknown",
				message: errorShape.message,
				status
			}
		});
	}
	/** As after a held stream, the status check sees a recent sync and other windows refresh, while `lastSyncId` stays at the floor */
	async noteHeldSyncTime() {
		await this.sdk.storage.updateMetaData((current) => current && {
			...current,
			lastSyncTime: (/* @__PURE__ */ new Date()).toISOString()
		});
	}
	/** The next cycle starts the delta again from its floor, so a server that stops advancing cannot loop inside one cycle */
	async abandonStalledDelta(run, fromSyncId, pageData) {
		this.sdk.logger.error("Incremental sync page did not advance its cursor", {
			context: "BootstrapManager:runDelta",
			metadata: {
				runId: run.runId,
				pageIndex: run.pageIndex,
				clientSyncId: fromSyncId,
				maxSyncId: pageData.max_sync_id,
				cursors: pageData.cursors,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		await this.sdk.storage.updateMetaData((current) => {
			if (!current?.deltaCheckpoint) return void 0;
			const next = { ...current };
			delete next.deltaCheckpoint;
			return next;
		}, { notifyCrossWindowEntitySync: false });
		this.sdk.stores.messages.setThreadsSyncingHistory("delta", []);
	}
	async handleBackgroundSyncError(error, signal) {
		if (signal.aborted) this.sdk.logger.warn("Background bootstrap aborted", {
			context: "BootstrapManager:performBackgroundSync",
			metadata: { engineContext: {
				kind: "bootstrap",
				phase: "fetchingFromServer"
			} }
		});
		else if (error instanceof SyncResetRequiredError) await this.recoverFromSyncReset(error, "BootstrapManager:performBackgroundSync");
		else if (error instanceof BootstrapInterruptedError) this.recordSyncInterruption();
		else if (isTransientFetchError(error)) {
			this.recordSyncInterruption();
			this.sdk.logger.warn("Background bootstrap interrupted by transient error", {
				context: "BootstrapManager:performBackgroundSync",
				metadata: {
					reason: "network_failure",
					error,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				}
			});
		} else {
			this.recordNonNetworkFailure();
			this.logUnlessAlreadyReported(error, "Error during background bootstrap API call", "BootstrapManager:performBackgroundSync");
		}
	}
	logUnlessAlreadyReported(error, message, context) {
		if (error instanceof ReportedBootstrapError) return;
		this.sdk.logger.error(message, this.bootstrapErrorReport(error, context));
	}
	async recoverFromSyncReset(error, context) {
		await this.clearSyncCheckpoint(error.reason, context);
		this.setPhase("idle");
		setTimeout(() => {
			if (this.retriesSuspended) return;
			this.bootstrap();
		}, 0);
	}
	/** Tell the server which producers this client can persist. */
	declareSupportedModels(params, windowTokens) {
		const supported = this.sdk.stores.supportedProducerKeys();
		if (supported.length === 0) return;
		params.set("supports", [
			...supported,
			THREAD_MESSAGE_PAGING,
			...windowTokens
		].join(","));
	}
	/** The window tokens a new walk declares. The host's answer is read once per walk, never per page. Every walk declares the delta token: without it, a client that holds every chat downloads a long chat again on each new turn */
	windowTokens() {
		return this.sdk.options.declareSyncWindows?.() ? [...WINDOW_TOKENS] : [CHAT_DELTA_INCREMENTAL];
	}
	declareLazyTranscripts(params) {
		if (this.sdk.options.lazyMeetingTranscripts) params.set("lazy_transcripts", "true");
	}
	async consumePagedBootstrapPage(cursors, signal, options) {
		const params = new URLSearchParams();
		if (cursors) params.set("cursor", JSON.stringify(cursors));
		if (options.maxSyncId !== void 0) params.set("max_sync_id", String(options.maxSyncId));
		if (options.clientSyncId !== void 0) params.set("client_sync_id", String(options.clientSyncId));
		if (options.onlyModels && options.onlyModels.length > 0) params.set("only_models", options.onlyModels.join(","));
		params.set("chat_scopes", ROUTINE_CHAT_SCOPE);
		const { pageSize } = this.sdk.yieldPolicy;
		if (pageSize !== void 0) params.set("page_size", String(pageSize));
		this.declareSupportedModels(params, options.windowTokens);
		this.declareLazyTranscripts(params);
		const qs = params.toString();
		const url = `bootstrap/page${qs ? `?${qs}` : ""}`;
		let result;
		let applyTiming;
		try {
			result = await this.consumeBootstrapStream(url, signal, options.clientSyncId === void 0 ? "bulk" : "live", options.run);
		} finally {
			await this.sdk.syncProcessor.idle();
			applyTiming = this.sdk.syncProcessor.takeApplyTiming();
		}
		const pageData = result?.pageData;
		if (typeof pageData?.prune_epoch === "number") this.serverPruneEpoch = pageData.prune_epoch;
		return result && {
			...result,
			applyTiming: {
				...applyTiming,
				parseMs: result.parseMs
			}
		};
	}
	performPagedBackfill(firstPageData, signal, refreshStoresFirst = false) {
		if (!firstPageData) return;
		runInAction(() => {
			this._isBackfilling = true;
		});
		this.backgroundBootstrapPromise = (async () => {
			const initialCursors = firstPageData.cursors ? { ...firstPageData.cursors } : {};
			let completed = false;
			try {
				await this.beginFullBootstrapRun(true, "unknown");
				if (refreshStoresFirst && !signal.aborted) await this.refreshLoadedStoresPostBootstrap(signal);
				completed = await this.runPagedBackfillLoop(initialCursors, firstPageData.maxSyncId, firstPageData.windowTokens ?? [], signal);
			} catch (error) {
				if (!signal.aborted && error instanceof SyncResetRequiredError) await this.recoverFromSyncReset(error, "BootstrapManager:performPagedBackfill");
				else {
					if (!signal.aborted && (error instanceof BootstrapInterruptedError || isTransientFetchError(error))) this.recordSyncInterruption();
					if (!signal.aborted) await this.failFullBootstrapRun(error);
					this.logPagedBackfillError(error, signal);
				}
			} finally {
				runInAction(() => {
					this._isBackfilling = false;
				});
			}
			if (!completed || signal.aborted) return;
			this.lastBootstrapMode = "incremental";
			await this.runBackgroundDelta(signal, () => this.readDeltaStartAndMarkThreads(firstPageData.maxSyncId));
		})();
	}
	/** True once every page is in and the search reindex after them has run */
	async runPagedBackfillLoop(initialCursors, initialMaxSyncId, windowTokens, signal) {
		let currentCursors = initialCursors;
		let maxSyncId = initialMaxSyncId;
		const seenCursorKeys = /* @__PURE__ */ new Set([JSON.stringify(initialCursors)]);
		const storeRefresher = this.backfillStoreRefresher(signal);
		try {
			while (true) {
				await this.betweenBackfillPages(maxSyncId, signal);
				if (signal.aborted) return false;
				const run = this.fullBootstrapRunContext();
				const result = await this.consumePagedBootstrapPage(currentCursors, signal, {
					maxSyncId,
					run,
					windowTokens
				});
				if (!result || signal.aborted) return false;
				storeRefresher.markPageApplied(result.rowsByProducer);
				this.warnFullBootstrapPagePersistFailures(result.persistFailures, "BootstrapManager:runPagedBackfillLoop");
				this.countFullBootstrapPage(result);
				const pageData = result.pageData;
				if (!pageData) {
					this.warnBackfillPageDataMissing();
					return false;
				}
				await this.confirmUserConfigIfDelivered(pageData);
				const allComplete = Object.values(pageData.has_more).every((v) => !v);
				await storeRefresher.refreshIfDue(allComplete);
				if (allComplete) {
					this.sdk.stores.messages.setThreadsSyncingHistory("backfill", []);
					await this.finalizeBackfillCompletion(result, signal);
					return true;
				}
				if (this.backfillCursorsRepeat(seenCursorKeys, pageData.cursors, run)) return false;
				if (result.meta) await this.processMeta(result.meta, {
					kind: "full_bootstrap_page",
					next: this.createBackfillCheckpoint(pageData, windowTokens)
				});
				this.markBackfillThreads(pageData.cursors);
				currentCursors = { ...pageData.cursors };
				maxSyncId = pageData.max_sync_id;
			}
		} finally {
			if (!signal.aborted) await storeRefresher.flushOwed();
		}
	}
	/**
	* ai-docs/architecture/frontend/sync.md says when `fullBootstrapPending` is
	* on and when this returns. While it is on during a backfill, this waits on
	* the background part itself, because `requestSync` would ask for a catch-up.
	*/
	async waitForFullBootstrap() {
		if (!this.sdk.storage.fullBootstrapPending) return;
		if (this._isBackfilling) await this.backgroundBootstrapPromise;
		else await this.waitForWholeSync();
	}
	/** During a backfill, a `bootstrap()` call waits on the shared `catchUpRequest`; `cycle` settles it if no catch-up does */
	requestCatchUp(cycle) {
		if (this.catchUpRequest) return this.catchUpRequest.promise;
		let resolve;
		const promise = new Promise((done) => {
			resolve = done;
		});
		const request = {
			promise,
			resolve
		};
		this.catchUpRequest = request;
		const settle = () => {
			if (this.catchUpRequest === request) this.catchUpRequest = null;
			resolve();
		};
		cycle.then(settle, settle);
		return promise;
	}
	/** The gap a backfill leaves between its pages: the host's pause, then the catch-up a `bootstrap()` call asked for, if any */
	async betweenBackfillPages(pin, signal) {
		await this.sdk.yieldPolicy.betweenPages();
		const request = this.catchUpRequest;
		if (!request || signal.aborted) return;
		this.catchUpRequest = null;
		await this.catchUpPastPin(pin, signal);
		request.resolve();
	}
	/**
	* Pages cover rows up to the pin, so anything changed after the backfill
	* began stays invisible until it finishes. Fetch those rows through the
	* incremental path from the stored cursor, never from below the pin. An
	* entity backfill job's pin is at or below the cursor, and every page of a full
	* bootstrap raises the cursor to its pin, but a failed metadata save can
	* leave the cursor below it. Runs between pages, never beside one, because
	* a page in flight could carry an older version of a row the catch-up
	* wrote.
	*/
	async catchUpPastPin(pin, signal) {
		this.catchingUp = true;
		try {
			const { fromSyncId, resume } = await this.readDeltaStartAndMarkThreads(pin);
			await this.runDelta(fromSyncId, resume, signal, emptyStreamResult("live"));
		} catch (error) {
			if (error instanceof SyncResetRequiredError) throw error;
			if (signal.aborted) return;
			if (error instanceof BootstrapInterruptedError || isTransientFetchError(error)) this.sdk.logger.warn("Catch-up sync cut off; the next sync fetches its rows", {
				context: "BootstrapManager:catchUpPastPin",
				metadata: {
					error,
					reason: "network_failure"
				}
			});
			else this.logUnlessAlreadyReported(error, "Error during catch-up sync", "BootstrapManager:catchUpPastPin");
		} finally {
			this.catchingUp = false;
			await this.sdk.syncProcessor.idle();
			this.sdk.syncProcessor.takeApplyTiming();
		}
	}
	/** `readDeltaStart`, with the threads that the resumed checkpoint stops inside marked as still syncing history */
	async readDeltaStartAndMarkThreads(floor) {
		const start = await this.readDeltaStart(floor);
		await this.markDeltaThreads(start.resume?.cursors);
		return start;
	}
	/**
	* Where a delta that follows other walks starts: the stored cursor, never
	* below `floor`. It resumes the checkpoint that a delta cut short at that
	* cursor left, as the planner does.
	*/
	async readDeltaStart(floor) {
		const meta = await this.sdk.storage.getMetaData();
		const fromSyncId = Math.max(meta?.lastSyncId ?? 0, floor);
		return {
			fromSyncId,
			resume: resumableDeltaCheckpoint(usableDeltaCheckpoint(meta?.deltaCheckpoint, this.sdk.stores.supportedProducerKeys()), fromSyncId)
		};
	}
	warnBackfillPageDataMissing() {
		this.sdk.logger.warn("Paged backfill: no _page data received, stopping", {
			context: "BootstrapManager:performPagedBackfill",
			metadata: { engineContext: {
				kind: "bootstrap",
				phase: this.phase
			} }
		});
	}
	/** On a repeat the checkpoint stays at the page before, so the next cycle asks for this page once more instead of looping inside this one */
	backfillCursorsRepeat(seenCursorKeys, cursors, run) {
		if (!cursorsRepeat(seenCursorKeys, cursors)) return false;
		this.sdk.logger.error("Paged backfill page did not advance its cursor", {
			context: "BootstrapManager:performPagedBackfill",
			metadata: {
				runId: run?.runId,
				pageIndex: run?.pageIndex,
				cursors,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		return true;
	}
	backfillStoreRefresher(signal) {
		return createBackfillStoreRefresher((tables) => this.refreshListStores(tables), () => this.refreshLoadedStoresPostBootstrap(signal), this.sdk.yieldPolicy.refreshDuringBulkMs);
	}
	async finalizeBackfillCompletion(result, signal) {
		if (result.meta) await this.processMeta(result.meta, {
			kind: "full_bootstrap_page",
			next: void 0
		});
		await this.completeFullBootstrap();
		this.sdk.logger.info("Paged backfill complete: all entities fully replicated", {
			context: "BootstrapManager:performPagedBackfill",
			metadata: { engineContext: {
				kind: "bootstrap",
				phase: this.phase
			} }
		});
		await this.rebuildSearchIndex("full_bootstrap", signal);
	}
	/** Stops with `signal`, so logout and resume do not wait out the rebuild */
	async rebuildSearchIndex(trigger, signal) {
		const abortIndexing = () => this.sdk.searchAdapter.abortIndexing();
		signal?.addEventListener("abort", abortIndexing, { once: true });
		if (signal?.aborted) {
			abortIndexing();
			return;
		}
		try {
			await this.sdk.searchStore.startIndexing({
				fullReindex: true,
				trigger
			});
		} catch (err) {
			if (err instanceof Error && err.name === "AbortError") return;
			this.sdk.logger.warn("Post-backfill search reindex failed", { metadata: {
				error: err,
				trigger,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			} });
		} finally {
			signal?.removeEventListener("abort", abortIndexing);
		}
	}
	logPagedBackfillError(error, signal) {
		if (signal.aborted) this.sdk.logger.warn("Paged backfill aborted", {
			context: "BootstrapManager:performPagedBackfill",
			metadata: { engineContext: {
				kind: "bootstrap",
				phase: "fetchingFromServer"
			} }
		});
		else if (!(error instanceof BootstrapInterruptedError)) {
			if (isTransientFetchError(error)) this.sdk.logger.warn("Paged backfill interrupted by transient error", {
				context: "BootstrapManager:performPagedBackfill",
				metadata: {
					reason: "network_failure",
					error,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				}
			});
			else if (!(error instanceof ReportedBootstrapError)) this.sdk.logger.error("Error during paged backfill", {
				metadata: {
					error,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				},
				context: "BootstrapManager:performPagedBackfill"
			});
		}
	}
	async drainEntityBackfillJobs(lastSyncId, signal) {
		const jobs = (await this.sdk.storage.getMetaData())?.entityBackfillJobs;
		if (!jobs || jobs.length === 0 || signal.aborted) return;
		runInAction(() => {
			this._isBackfilling = true;
		});
		try {
			for (const job of jobs.filter((job) => !this.stalledEntityBackfillJobIds.has(job.id) && this.canDrainEntityBackfillJob(job))) {
				if (signal.aborted) return;
				await this.drainEntityBackfillJob(job, lastSyncId, signal).catch((error) => {
					if (error instanceof SyncResetRequiredError) throw error;
					this.logEntityBackfillJobFailure(job, error);
				});
				await this.markRefillThreadsFromSavedJobs();
			}
			const owed = signal.aborted ? void 0 : (await this.sdk.storage.getMetaData())?.searchRebuildOwed;
			if (owed) await this.rebuildSearchIndex(owed.trigger, signal);
		} finally {
			runInAction(() => {
				this._isBackfilling = false;
			});
		}
	}
	async pinEntityBackfillJob(job, lastSyncId) {
		if (job.maxSyncId !== void 0) return {
			...job,
			maxSyncId: job.maxSyncId
		};
		const pinned = {
			maxSyncId: lastSyncId,
			cursors: {},
			windowTokens: job.trigger === "chat_window_off" ? [] : this.windowTokens(),
			startedAt: job.startedAt ?? (/* @__PURE__ */ new Date()).toISOString(),
			updatedAt: (/* @__PURE__ */ new Date()).toISOString()
		};
		await this.updateEntityBackfillJob(job.id, pinned);
		return {
			...job,
			...pinned
		};
	}
	async drainEntityBackfillJob(job, lastSyncId, signal) {
		const currentJob = await this.pinEntityBackfillJob(job, lastSyncId);
		const owed = tablesWrittenBy(currentJob.producerKeys).some(isIndexableTable) ? "scoped_refill" : void 0;
		let cursors = currentJob.cursors ?? {};
		const seenCursorKeys = /* @__PURE__ */ new Set([JSON.stringify(cursors)]);
		const drain = startEntityBackfillDrain(currentJob);
		while (true) {
			await this.betweenBackfillPages(currentJob.maxSyncId, signal);
			if (signal.aborted) return;
			const result = await this.consumePagedBootstrapPage(cursors, signal, {
				maxSyncId: currentJob.maxSyncId,
				onlyModels: currentJob.producerKeys,
				run: entityBackfillRunContext(currentJob, drain),
				windowTokens: currentJob.windowTokens ?? []
			});
			if (!result || signal.aborted) return;
			const pageData = this.usableEntityBackfillPage(currentJob, result);
			if (!pageData) return;
			countEntityBackfillPage(drain, result);
			await this.confirmUserConfigIfDelivered(pageData, currentJob);
			if (!currentJob.producerKeys.some((producerKey) => pageData.has_more[producerKey] === true)) {
				await this.removeEntityBackfillJob(currentJob.id, owed);
				this.reportEntityBackfillJob(currentJob, drain);
				return;
			}
			const scopedCursors = scopeCursorsToJob(currentJob, pageData);
			const cursorKey = JSON.stringify(scopedCursors);
			if (lacksNextCursor(currentJob, pageData) || seenCursorKeys.has(cursorKey)) {
				if (owed) await this.noteSearchRebuildOwed(owed);
				this.markEntityBackfillJobStalled(currentJob, "page did not advance its cursor", { cursors: scopedCursors });
				return;
			}
			seenCursorKeys.add(cursorKey);
			await this.saveEntityBackfillProgress(currentJob, scopedCursors, drain, owed);
			await this.markRefillThreadsFromSavedJobs();
			cursors = scopedCursors;
		}
	}
	async saveEntityBackfillProgress(job, cursors, drain, owed) {
		await this.updateEntityBackfillJob(job.id, {
			cursors,
			updatedAt: (/* @__PURE__ */ new Date()).toISOString(),
			attempts: drain.attempt,
			activeMs: drain.priorMs + (Date.now() - drain.startedAtMs),
			pages: drain.pages,
			rows: drain.rows,
			applyTiming: drain.applyTiming
		}, owed);
	}
	/** The page's `_page` data, or undefined after holding a job that the page cannot advance */
	usableEntityBackfillPage(job, result) {
		if (result.streamError) {
			const errors = (this.entityBackfillJobStreamErrors.get(job.id) ?? 0) + 1;
			this.entityBackfillJobStreamErrors.set(job.id, errors);
			if (errors >= MAX_ENTITY_BACKFILL_STREAM_ERRORS) {
				this.markEntityBackfillJobStalled(job, "page ended in a server error", {
					errors,
					streamError: result.streamError
				});
				return;
			}
			this.handleStreamError(result.streamError, "BootstrapManager:drainEntityBackfillJobs");
		}
		if (result.persistFailures.length > 0) {
			this.warnStreamBootstrapPersistFailures(result.persistFailures);
			this.holdEntityBackfillJob(job, "rows failed to persist");
			return;
		}
		if (!result.pageData) {
			this.sdk.logger.warn("Entity backfill page carried no _page data", {
				context: "BootstrapManager:drainEntityBackfillJobs",
				metadata: {
					jobId: job.id,
					engineContext: {
						kind: "bootstrap",
						phase: this.phase
					}
				}
			});
			this.holdEntityBackfillJob(job, "page carried no _page data");
			return;
		}
		if (!this.pageAnsweredEveryProducer(job, result.pageData)) {
			this.holdEntityBackfillJob(job, "page omitted a requested producer");
			return;
		}
		this.entityBackfillJobHolds.delete(job.id);
		this.entityBackfillJobStreamErrors.delete(job.id);
		return result.pageData;
	}
	/** Each poll re-requests a held page, so the same limit as the delta's cursor hold ends the retries */
	holdEntityBackfillJob(job, reason) {
		const holds = (this.entityBackfillJobHolds.get(job.id) ?? 0) + 1;
		this.entityBackfillJobHolds.set(job.id, holds);
		if (holds >= MAX_CURSOR_HOLD_CYCLES) this.markEntityBackfillJobStalled(job, reason, { holds });
	}
	markEntityBackfillJobStalled(job, reason, details) {
		this.stalledEntityBackfillJobIds.add(job.id);
		this.entityBackfillJobHolds.delete(job.id);
		this.entityBackfillJobStreamErrors.delete(job.id);
		this.sdk.logger.warn(`Entity backfill job stalled until the next launch: ${reason}; job retained`, {
			context: "BootstrapManager:drainEntityBackfillJobs",
			metadata: {
				jobId: job.id,
				...details,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
	}
	/** Completion only: an aborted drain resumes next launch, so stopping isn't abandonment */
	reportEntityBackfillJob(job, drain) {
		const durationMs = Date.now() - drain.startedAtMs;
		this.sdk.reportFullBootstrap({
			runId: job.runId ?? job.id,
			outcome: "completed",
			trigger: entityBackfillTrigger(job),
			scope: "partial",
			tables: [...job.tables],
			resumed: drain.attempt > 1,
			attempt: drain.attempt,
			durationMs,
			totalDurationMs: drain.priorMs + durationMs,
			pages: drain.pages,
			rows: drain.rows,
			...drain.applyTiming
		});
	}
	/** Whether the page reported a state for every requested producer. */
	pageAnsweredEveryProducer(job, pageData) {
		const unanswered = job.producerKeys.filter((producerKey) => pageData.has_more[producerKey] === void 0);
		if (unanswered.length === 0) return true;
		this.sdk.logger.warn("Entity backfill page omitted a requested producer; job retained", {
			context: "BootstrapManager:drainEntityBackfillJobs",
			metadata: {
				jobId: job.id,
				unanswered,
				answered: Object.keys(pageData.has_more),
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		return false;
	}
	isKnownEntityBackfillJob(job) {
		const producerKeysKnown = job.producerKeys.length > 0 && job.producerKeys.every((key) => Object.hasOwn(KNOWN_BACKFILL_PRODUCER_KEYS, key));
		const tablesKnown = job.tables.every((table) => this.sdk.storage.hasTable(table));
		return producerKeysKnown && tablesKnown;
	}
	canDrainEntityBackfillJob(job) {
		if (this.isKnownEntityBackfillJob(job)) return true;
		this.sdk.logger.error(`Entity backfill job "${job.id}" names tables [${job.tables.join(", ")}] or producer keys [${job.producerKeys.join(", ")}] the current client no longer recognizes; the job is retained. Clear local sync metadata deliberately if this job is stale.`, {
			context: "BootstrapManager:drainEntityBackfillJobs",
			metadata: {
				jobId: job.id,
				tables: job.tables,
				producerKeys: job.producerKeys,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		return false;
	}
	logEntityBackfillJobFailure(job, error) {
		const transient = error instanceof BootstrapInterruptedError || isTransientFetchError(error);
		const report = {
			context: "BootstrapManager:drainEntityBackfillJobs",
			metadata: {
				jobId: job.id,
				tables: job.tables,
				producerKeys: job.producerKeys,
				...transient ? { reason: "network_failure" } : {},
				error,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		};
		const message = "Entity backfill job did not complete; job retained to resume on the next sync";
		if (transient) this.sdk.logger.warn(message, report);
		else this.sdk.logger.error(message, report);
	}
	/** `owed` is bumped in the same write, so a kill never leaves the cursor advanced without it */
	async updateEntityBackfillJob(jobId, patch, owed) {
		await this.sdk.storage.updateMetaData((meta) => {
			const jobs = meta?.entityBackfillJobs ?? [];
			const index = jobs.findIndex((job) => job.id === jobId);
			if (index === -1 && !owed) return void 0;
			const next = { ...meta ?? {} };
			if (index !== -1) next.entityBackfillJobs = jobs.map((job, i) => i === index ? {
				...job,
				...patch
			} : job);
			return withSearchRebuildOwed(next, meta, owed);
		});
	}
	async noteSearchRebuildOwed(trigger) {
		await this.sdk.storage.updateMetaData((meta) => withSearchRebuildOwed({ ...meta ?? {} }, meta, trigger), { notifyCrossWindowEntitySync: false });
	}
	/** `owed` is bumped in the same write, so a kill never leaves the job removed without it */
	async removeEntityBackfillJob(jobId, owed) {
		await this.sdk.storage.updateMetaData((meta) => {
			const jobs = meta?.entityBackfillJobs ?? [];
			const found = jobs.some((job) => job.id === jobId);
			if (!found && !owed) return void 0;
			const next = { ...meta ?? {} };
			if (found) next.entityBackfillJobs = jobs.filter((job) => job.id !== jobId);
			return withSearchRebuildOwed(next, meta, owed);
		});
	}
	async observeLocalSyncState() {
		let meta;
		let cursor;
		let hasEntityRows;
		try {
			meta = await this.sdk.storage.getMetaData();
			cursor = meta?.lastSyncId !== void 0 && meta.lastSyncTime !== void 0 ? { lastSyncId: meta.lastSyncId } : void 0;
			hasEntityRows = cursor ? void 0 : await this.hasEntityRows();
		} catch (error) {
			this.sdk.logger.warn("Could not read local sync state; retrying the cycle", {
				context: "BootstrapManager:observeLocalSyncState",
				metadata: {
					error,
					engineContext: {
						kind: "bootstrap",
						phase: "connectingDatabase"
					}
				}
			});
			throw new BootstrapInterruptedError("Could not read local sync state");
		}
		if (!cursor) this.sdk.logger.warn("Bootstrap metadata missing in local storage", {
			context: "BootstrapManager:observeLocalSyncState",
			lastResetReason: meta?.lastReset?.reason ?? "none",
			metadata: { engineContext: {
				kind: "bootstrap",
				phase: "connectingDatabase"
			} }
		});
		const deltaCheckpoint = usableDeltaCheckpoint(meta?.deltaCheckpoint, this.sdk.stores.supportedProducerKeys());
		const refillJobs = (meta?.entityBackfillJobs ?? []).filter((job) => !this.stalledEntityBackfillJobIds.has(job.id));
		this.markRefillThreads(meta?.entityBackfillJobs);
		return {
			cursor,
			hasEntityRows,
			lastReset: meta?.lastReset,
			fullBootstrapPending: this.sdk.storage.fullBootstrapPending,
			pendingTrigger: meta?.fullBootstrapRun?.trigger,
			backfill: meta?.bootstrapBackfill,
			deltaCheckpoint,
			pendingRefillProducers: [...new Set(refillJobs.flatMap((job) => job.producerKeys))].sort()
		};
	}
	markBackfillThreads(cursors) {
		this.sdk.stores.messages.setThreadsSyncingHistory("backfill", threadsInProgress(cursors));
	}
	/** A delta serves every message of a changed thread, so older messages already on the device mean the thread's start is here and only its newer part is sent again */
	async markDeltaThreads(cursors) {
		const positions = threadPositionsInProgress(cursors);
		const olderMessagesHeld = await Promise.all(positions.map((position) => this.holdsOlderMessages(position)));
		const threadIds = positions.filter((_, index) => !olderMessagesHeld[index]).map((position) => position.pk);
		this.sdk.stores.messages.setThreadsSyncingHistory("delta", threadIds);
	}
	/** A failed read counts as none held: the mark must not fail the cycle, and a wrong mark only shows the thread as still syncing */
	async holdsOlderMessages(position) {
		try {
			return !!(await this.sdk.storage.entities.messages?.listOlderForThread(position.pk, {
				value: new Date(position.created_at).toISOString(),
				id: position.message_id
			}, { limit: 1 }))?.records.length;
		} catch (error) {
			const errorShape = describeError(error);
			this.sdk.logger.warn("Could not read local messages for a delta thread mark; marking the thread", {
				context: "BootstrapManager:markDeltaThreads",
				metadata: {
					threadId: position.pk,
					errorName: errorShape.name ?? "unknown",
					errorMessage: errorShape.message
				}
			});
			return false;
		}
	}
	/** Set before the first page request, so a thread the walk will fill shows as syncing from the start */
	async markPlannedThreads(plan) {
		if (plan.kind === "resume_backfill") {
			this.markBackfillThreads(plan.backfill.cursors);
			const { resume } = await this.readDeltaStart(plan.backfill.maxSyncId);
			await this.markDeltaThreads(resume?.cursors);
			return;
		}
		this.markBackfillThreads(void 0);
		await this.markDeltaThreads(plan.kind === "full_bootstrap" ? void 0 : plan.resume?.cursors);
	}
	/** One mark set for every job, since jobs drain one after another and each page would otherwise clear the others' marks */
	markRefillThreads(jobs) {
		const drainableJobs = (jobs ?? []).filter((job) => !this.stalledEntityBackfillJobIds.has(job.id) && this.isKnownEntityBackfillJob(job));
		this.sdk.stores.messages.setThreadsSyncingHistory("refill", threadsInProgress(...drainableJobs.map((job) => job.cursors)));
	}
	async markRefillThreadsFromSavedJobs() {
		const meta = await this.sdk.storage.getMetaData();
		this.markRefillThreads(meta?.entityBackfillJobs);
	}
	async hasEntityRows() {
		const entities = this.sdk.storage.entities;
		const tables = tablesWrittenBy([...STORAGE_TABLES_BY_PRODUCER.keys()]);
		for (const table of tables) {
			const page = await entities[table]?.listPage({ limit: 1 });
			if (page && page.records.length > 0) return true;
		}
		return false;
	}
	/** Stream a delta from GET /bootstrap?client_sync_id=X. Used only when a server ignores the floor on /bootstrap/page. */
	async tryStreamingBootstrap(syncId, run, windowTokens, signal, state) {
		const startTime = Date.now();
		const params = new URLSearchParams({
			client_sync_id: String(syncId),
			chat_scopes: ROUTINE_CHAT_SCOPE
		});
		this.declareSupportedModels(params, windowTokens);
		this.declareLazyTranscripts(params);
		const url = `bootstrap?${params.toString()}`;
		try {
			const result = await this.consumeBootstrapStream(url, signal, "live", run, state);
			if (!result) return false;
			return await this.finalizeStreamBootstrap(result, startTime, syncId, signal);
		} finally {
			await this.sdk.syncProcessor.idle();
			this.sdk.syncProcessor.takeApplyTiming();
		}
	}
	async consumeBootstrapStream(url, signal, source, run, state = emptyStreamResult(source)) {
		const syncRequestId = v4();
		this.currentSyncRequestId = syncRequestId;
		const startedAt = Date.now();
		try {
			await this.sdk.apiClient.fetchNdjson(url, {
				signal,
				headers: {
					"x-sync-request-id": syncRequestId,
					...syncRunHeaders(run)
				},
				bodyIdleTimeoutMs: BOOTSTRAP_BODY_IDLE_TIMEOUT_MS,
				onBodyBytes: (byteCount) => {
					state.bytes += byteCount;
				},
				onParseMs: (ms) => {
					state.parseMs += ms;
				},
				onLine: (line) => this.handleBootstrapStreamLine(state, line, signal)
			});
		} catch (error) {
			const endReason = deltaEndReason(error, signal);
			if (endReason) this.warnDeltaEndedEarly(state, run, endReason, startedAt, error);
			return this.handleConsumeError(error, signal);
		}
		state.durationMs = Date.now() - startedAt;
		if (signal.aborted) {
			this.warnDeltaEndedEarly(state, run, "cancelled", startedAt);
			return;
		}
		if (!state.meta && !state.streamError) {
			this.warnDeltaEndedEarly(state, run, "truncated", startedAt);
			throw this.interruptedBootstrap("Bootstrap stream truncated before completion");
		}
		await this.applyStreamTails(state);
		return state;
	}
	/** A stream that was not cut short carried every message of its ranges, so the deletes they imply are safe to apply */
	async applyStreamTails(state) {
		const tails = state.tails.take();
		if (tails.length === 0 || state.streamError) return;
		await this.sdk.syncProcessor.idle();
		const servedInPart = new Set(threadsInProgress(state.pageData?.cursors));
		const gaps = [];
		for (const tail of tails) try {
			if (await applyChatTail(this.sdk, tail) || servedInPart.has(tail.threadId)) continue;
			const gap = await chatGapBefore(this.sdk, tail.threadId, tail.rangeOldest);
			if (gap) gaps.push(gap);
		} catch (error) {
			this.sdk.logger.warn("Could not apply a range of newest messages", {
				metadata: {
					error,
					threadId: tail.threadId
				},
				context: "BootstrapManager:applyStreamTails"
			});
		}
		if (gaps.length > 0) await writeChatGaps(this.sdk, { add: gaps });
	}
	/** Fetch the messages that a device missed before the recorded gaps. A gap left for a later sync keeps its thread reading as still syncing. */
	async fillRecordedChatGaps(signal) {
		const messages = this.sdk.stores.messages;
		try {
			const gaps = (await this.sdk.storage.getMetaData())?.chatGaps ?? [];
			messages.setThreadsSyncingHistory("gap", gaps.map((gap) => gap.threadId));
			await fillChatGaps(this.sdk, gaps, signal);
			const remaining = (await this.sdk.storage.getMetaData())?.chatGaps ?? [];
			messages.setThreadsSyncingHistory("gap", remaining.map((gap) => gap.threadId));
		} catch (error) {
			this.sdk.logger.warn("Could not read or update the recorded gaps", {
				metadata: { error },
				context: "BootstrapManager:fillRecordedChatGaps"
			});
		}
	}
	/**
	* Asks the server, in a sync run, about each chat that shows running now
	* (`MessageStore.checkRunningChatInSyncRun`), and waits for that run (`waitForWholeSync`). A delta applies its rows
	* first, as for `fillRecordedChatGaps`, so the check reads what sync did not bring. When the call joins a run already
	* past its check, or the run fails, the chats wait for the next sync.
	*/
	async checkRunningChats(threadIds) {
		if (this.markRunningChatsForCheck(threadIds).length === 0) return;
		try {
			await this.waitForWholeSync();
		} catch (error) {
			this.sdk.logger.warn("Sync failed before checking running chats", {
				metadata: { error },
				context: "BootstrapManager:checkRunningChats"
			});
		}
	}
	/** Marks the chats that show running now for the next sync run's check, and returns them */
	markRunningChatsForCheck(threadIds) {
		const toCheck = [...threadIds].filter((threadId) => this.sdk.stores.threads.showsRunning(threadId));
		for (const threadId of toCheck) this.runningChatsToCheck.add(threadId);
		return toCheck;
	}
	/** Checks the chats marked for a check (`markRunningChatsForCheck`); a chat marked during the checks waits */
	async checkMarkedRunningChats() {
		const signal = this.bootstrapAbortController?.signal;
		const threadIds = [...this.runningChatsToCheck];
		this.runningChatsToCheck.clear();
		const keepUnchecked = (from) => {
			for (const unchecked of threadIds.slice(from)) this.runningChatsToCheck.add(unchecked);
		};
		for (const [index, threadId] of threadIds.entries()) {
			try {
				await this.sdk.stores.messages.checkRunningChatInSyncRun(threadId, signal);
			} catch (error) {
				if (!signal?.aborted) {
					this.runningChatsToCheck.add(threadId);
					this.sdk.logger.warn("Could not check a running chat with the server", {
						metadata: {
							error,
							threadId
						},
						context: "BootstrapManager:checkMarkedRunningChats"
					});
				}
			}
			if (signal?.aborted) {
				keepUnchecked(index);
				return;
			}
		}
	}
	/**
	* The warnings beside this one are dropped by the Sentry network filter. This one has no
	* `reason` or `errorType` key and no network phrase in its message, so Sentry keeps it
	*/
	warnDeltaEndedEarly(state, run, endReason, startedAt, error) {
		if (state.source !== "live" || state.meta || state.streamError) return;
		this.sdk.logger.warn("Incremental sync ended before completion", {
			context: "BootstrapManager:consumeBootstrapStream",
			metadata: {
				endReason,
				rowsHandedOff: state.rows - failedRowCount(state.persistFailures),
				bytes: state.bytes,
				elapsedMs: Date.now() - startedAt,
				syncRequestId: this.currentSyncRequestId,
				errorName: error instanceof Error ? error.name : void 0,
				runId: run?.runId,
				pageIndex: run?.pageIndex,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
	}
	async handleBootstrapStreamLine(state, line, signal) {
		if (signal.aborted) return;
		const { type } = line;
		recordStreamLine(state, line);
		try {
			if (this.streamCapture && type !== "_meta" && type !== "_page" && type !== "_error") this.streamCapture.push(line);
			await this.handleNdjsonLine(line, state.source);
		} catch (err) {
			state.persistFailures.push({
				entityType: type,
				error: err instanceof Error ? err : new Error(String(err)),
				rowCount: Array.isArray(line.data) ? line.data.length : 0,
				syncId: deriveMaxSyncId(Array.isArray(line.data) ? line.data : []) || void 0
			});
			this.sdk.logger.error("Persistence operation failed during bootstrap", {
				metadata: {
					reason: err,
					engineContext: {
						kind: "bootstrap",
						phase: "writingToDatabase"
					}
				},
				context: "BootstrapManager:consumeBootstrapStream"
			});
		}
		if (type === "_meta") {
			state.meta = line.data;
			this.sdk.stores.actionCards.noteTemplatesHash(line.data.action_cards_template_hash);
		} else if (type === "_page") {
			state.pageData = line.data;
			this.sdk.stores.actionCards.noteTemplatesHash(line.data.action_cards_template_hash);
			if (!state.meta) state.meta = {
				last_sync_id: line.data.max_sync_id,
				emitting: line.data.emitting
			};
		} else if (type === "_error") state.streamError = line.data.message;
	}
	/**
	* Log metadata for a genuine (non-transient) bootstrap error. The underlying
	* error's shape goes into Sentry `tags` (aggregatable) so we can slice the
	* bucket by error name / platform, with the full shape kept in `metadata`.
	* Tag values are coerced to strings: the Sentry transport only sets tags
	* whose value is a string, so a boolean or null would be silently dropped.
	*/
	bootstrapErrorReport(error, context) {
		const errorShape = describeError(error);
		return {
			context,
			tags: {
				errorName: errorShape.name ?? "unknown",
				errorConstructor: errorShape.constructorName ?? "unknown",
				hasCause: String(errorShape.hasCause),
				platform: this.sdk.options.platform
			},
			metadata: {
				error,
				errorShape,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		};
	}
	handleConsumeError(error, signal) {
		if (signal.aborted) {
			this.sdk.logger.warn("Bootstrap aborted", {
				context: "BootstrapManager:consumeBootstrapStream",
				metadata: { engineContext: {
					kind: "bootstrap",
					phase: this.phase
				} }
			});
			return;
		}
		if (isTransientFetchError(error)) throw this.interruptedBootstrap("Streaming bootstrap interrupted", error);
		const resetRequired = asSyncResetRequired(error);
		if (resetRequired) throw resetRequired;
		this.sdk.logger.error("Error during streaming bootstrap", this.bootstrapErrorReport(error, "BootstrapManager:consumeBootstrapStream"));
		throw new ReportedBootstrapError("Error during streaming bootstrap", error);
	}
	/**
	* Log a transient interruption as a warning tagged `network_failure` (suppressed
	* by the Sentry network filter) and return a marker error for callers to skip.
	*/
	interruptedBootstrap(message, cause) {
		this.sdk.logger.warn(message, {
			context: "BootstrapManager",
			metadata: {
				reason: "network_failure",
				error: cause,
				engineContext: {
					kind: "bootstrap",
					phase: this.phase
				}
			}
		});
		return new BootstrapInterruptedError(message);
	}
	warnFullBootstrapPagePersistFailures(persistFailures, context) {
		if (persistFailures.length === 0) return;
		this.sdk.logger.warn(`Full bootstrap page dropped ${failedRowCount(persistFailures)} row(s) that failed to persist`, {
			context,
			metadata: persistFailureReport(persistFailures)
		});
	}
	warnStreamBootstrapPersistFailures(persistFailures) {
		if (persistFailures.length === 0) return;
		this.sdk.logger.warn(`Bootstrap completed with ${persistFailures.length} persistence failure(s) - some data was dropped`, {
			context: "BootstrapManager:finalizeStreamBootstrap",
			metadata: persistFailureReport(persistFailures)
		});
	}
	ensureStreamBootstrapMeta(received, streamError, meta) {
		if (streamError) this.handleStreamError(streamError);
		if (!meta) throw this.interruptedBootstrap("Bootstrap stream truncated before completion");
		this.assertBootstrapCompleteness(received, meta);
		return meta;
	}
	handleStreamError(streamError, context = "BootstrapManager:finalizeStreamBootstrap") {
		this.sdk.logger.error("Server reported bootstrap error", {
			metadata: {
				streamError,
				engineContext: {
					kind: "bootstrap",
					phase: "writingToDatabase"
				}
			},
			context
		});
		throw new ReportedBootstrapError(`Bootstrap stream error: ${streamError}`);
	}
	/** Validates that the response delivered every promised model. */
	assertBootstrapCompleteness(received, meta) {
		const promised = meta.emitting ?? REQUIRED_ENTITY_TYPES;
		const missing = promised.filter((lineType) => !received.has(lineType));
		if (missing.length === 0) return;
		this.sdk.logger.error("Incomplete bootstrap stream", {
			metadata: {
				missing,
				promised,
				negotiated: meta.emitting !== void 0,
				received: Array.from(received),
				engineContext: {
					kind: "bootstrap",
					phase: "writingToDatabase"
				}
			},
			context: "BootstrapManager:finalizeStreamBootstrap"
		});
		throw new ReportedBootstrapError(`Bootstrap stream incomplete, missing: ${missing.join(", ")}`);
	}
	async finishBootstrapSuccess(startTime, deltaWalk) {
		const cycleStartIso = new Date(startTime).toISOString();
		const lastSyncId = (await this.sdk.storage.getMetaData())?.lastSyncId ?? 0;
		this.sdk.logger.info("BootstrapManager: cycle done", {
			context: "BootstrapManager:cycle",
			metadata: {
				...deltaWalk,
				...deltaWalk ? { producers: [...deltaWalk.producers] } : {},
				syncRequestId: this.currentSyncRequestId,
				mode: this.catchingUp ? "catch_up" : this.lastBootstrapMode,
				durationMs: Date.now() - startTime,
				lastSyncId,
				packets: this.sdk.syncProcessor.recentPackets().filter((p) => p.at >= cycleStartIso).length,
				downloadErrors: this.sdk.syncProcessor.downloadErrors,
				queuePending: this.sdk.transactionQueue.getPendingCount()
			}
		});
		globalEventBus.emit(EventNames.BOOTSTRAP_COMPLETE, { data: {
			duration: Date.now() - startTime,
			hadCachedData: this.phase === "ready"
		} }, "BootstrapManager");
		if (!this.catchingUp) {
			this.recordSyncSuccess();
			this.sdk.lifecycle.notifySyncRecovered();
		}
		this.fireBootstrapListeners();
	}
	/** `backfill` is the checkpoint of the second page, when this is the first page of a multi-page full bootstrap */
	async finalizeStreamBootstrap(streamResult, startTime, clientSyncId, signal, backfill) {
		await this.sdk.syncProcessor.idle();
		let write;
		if (backfill) {
			this.warnFullBootstrapPagePersistFailures(streamResult.persistFailures, "BootstrapManager:finalizeStreamBootstrap");
			write = {
				kind: "full_bootstrap_page",
				next: backfill
			};
		} else write = { kind: this.cursorPolicy(streamResult.persistFailures, {
			clientSyncId,
			maxSyncId: streamResult.meta?.last_sync_id
		}) ? "hold" : "advance" };
		const meta = this.ensureStreamBootstrapMeta(streamResult.received, streamResult.streamError, streamResult.meta);
		await this.processMeta(meta, write);
		await this.refreshLoadedStoresPostBootstrap(signal);
		await this.finishBootstrapSuccess(startTime);
		return write.kind !== "hold";
	}
	cursorPolicy(persistFailures, walk) {
		if (persistFailures.length === 0) {
			this.consecutiveFailingCycles = 0;
			return false;
		}
		this.consecutiveFailingCycles += 1;
		const hold = this.consecutiveFailingCycles <= MAX_CURSOR_HOLD_CYCLES;
		(hold ? this.sdk.logger.warn : this.sdk.logger.error).call(this.sdk.logger, hold ? `Bootstrap: ${persistFailures.length} packet(s) failed to persist; holding lastSyncId so the next delta re-sends them` : `Bootstrap: giving up on rows that failed to persist for ${this.consecutiveFailingCycles} cycles; advancing lastSyncId past them`, {
			context: "BootstrapManager:finalizeStreamBootstrap",
			metadata: {
				failingCycles: this.consecutiveFailingCycles,
				clientSyncId: walk.clientSyncId,
				maxSyncId: walk.maxSyncId,
				...persistFailureReport(persistFailures)
			}
		});
		if (!hold) this.consecutiveFailingCycles = 0;
		return hold;
	}
	/** The narrow store refresh: re-reads the loaded list stores of the given tables */
	async refreshListStores(tables) {
		try {
			await this.sdk.refreshLoadedListStores(tables);
		} catch (error) {
			this.sdk.logger.warn("List store refresh failed", {
				metadata: { error },
				context: "BootstrapManager:refreshListStores"
			});
		}
	}
	async refreshLoadedStoresPostBootstrap(signal) {
		try {
			await this.sdk.refreshLoadedStoresAfterBootstrap();
		} catch (error) {
			this.sdk.logger.warn("Post-bootstrap store refresh failed", {
				metadata: { error },
				context: "BootstrapManager:refreshLoadedStoresPostBootstrap"
			});
		}
		await this.fillRecordedChatGaps(signal);
		await this.checkMarkedRunningChats();
	}
	handleNdjsonLine(line, source) {
		if (this.isEphemeralLine(line.type)) return Promise.resolve();
		if (line.type === "_meta" || line.type === "_error" || line.type === "_page") return Promise.resolve();
		const packet = this.buildSyncPacket(line, source);
		if (packet) {
			this.sdk.syncProcessor.enqueue(packet);
			return this.sdk.syncProcessor.waitForIdle();
		}
		return Promise.resolve();
	}
	buildSyncPacket(line, source) {
		const spec = syncLineTypeSpec(line.type);
		if (!spec) {
			this.sdk.logger.warn(`Discarding unsupported NDJSON bootstrap line type: ${line.type}`, { metadata: { engineContext: {
				kind: "bootstrap",
				phase: this.phase
			} } });
			return;
		}
		const rows = line.data;
		return {
			table: spec.packetTable,
			syncId: spec.syncId === "derive" ? deriveMaxSyncId(rows) : 0,
			rows,
			source
		};
	}
	isEphemeralLine(lineType) {
		if (lineType === "_meta" || lineType === "_error" || lineType === "_page") return false;
		const stores = this.sdk.stores;
		return {
			threads: stores.threads,
			messages: stores.messages,
			routine_threads: stores.threads,
			routine_messages: stores.messages,
			files: stores.files,
			journals: stores.journals,
			meetings: stores.meetings,
			projects: stores.projects,
			agents: stores.agents
		}[lineType]?.effectiveLoadStrategy === "ephemeral";
	}
	/** Settings are one row, so the first page that finishes their producer carries the server's copy if one exists */
	async confirmUserConfigIfDelivered(pageData, job) {
		if (job && !job.producerKeys.includes(USER_CONFIG_PRODUCER)) return;
		if (pageData?.has_more[USER_CONFIG_PRODUCER] === false) await this.sdk.userConfig.confirmServerCopy();
	}
	/** Clears a flag that outlived its refill, such as one left by a kill between the last page and the confirm */
	async confirmUserConfigUnlessRefillOwed() {
		if (!this.sdk.userConfig.awaitingServerCopy) return;
		if (!((await this.sdk.storage.getMetaData())?.entityBackfillJobs ?? []).some((job) => job.producerKeys.includes(USER_CONFIG_PRODUCER))) await this.sdk.userConfig.confirmServerCopy();
	}
	createBackfillCheckpoint(pageData, windowTokens, existingStartedAt) {
		if (!pageData) return;
		if (!Object.values(pageData.has_more).some(Boolean)) return;
		const now = (/* @__PURE__ */ new Date()).toISOString();
		return {
			status: "in_progress",
			maxSyncId: pageData.max_sync_id,
			cursors: { ...pageData.cursors },
			hasMore: { ...pageData.has_more },
			windowTokens: [...windowTokens],
			startedAt: existingStartedAt ?? now,
			updatedAt: now
		};
	}
	getNextLastSyncId(existingMeta, serverSyncId, kind) {
		const currentLastSyncId = existingMeta?.lastSyncId ?? 0;
		if (kind === "full_bootstrap_page") return Math.max(currentLastSyncId, serverSyncId);
		if (kind === "hold") {
			this.sdk.logger.warn("Bootstrap: holding lastSyncId", {
				context: "BootstrapManager:processMeta",
				metadata: {
					heldAt: currentLastSyncId,
					serverSyncId,
					failingCycles: this.consecutiveFailingCycles
				}
			});
			return currentLastSyncId;
		}
		return Math.max(currentLastSyncId, this.sdk.syncProcessor.getLastAppliedSyncId(), serverSyncId);
	}
	/** Saves `next`, keeping when the full bootstrap started, or clears the checkpoint after the last page */
	applyBootstrapBackfill(nextMeta, existingMeta, next) {
		if (!next) {
			delete nextMeta.bootstrapBackfill;
			return;
		}
		nextMeta.bootstrapBackfill = this.createBackfillCheckpoint({
			max_sync_id: next.maxSyncId,
			cursors: next.cursors,
			has_more: next.hasMore
		}, next.windowTokens ?? [], existingMeta?.bootstrapBackfill?.startedAt);
	}
	async processMeta(meta, write = { kind: "advance" }) {
		try {
			await this.sdk.storage.updateMetaData((existingMeta) => {
				const nextMeta = {
					...existingMeta ?? {},
					lastSyncId: this.getNextLastSyncId(existingMeta, meta.last_sync_id, write.kind),
					lastSyncTime: (/* @__PURE__ */ new Date()).toISOString()
				};
				if (write.kind === "full_bootstrap_page") this.applyBootstrapBackfill(nextMeta, existingMeta, write.next);
				else delete nextMeta.deltaCheckpoint;
				if (this.fullBootstrapRun) {
					this.fullBootstrapRun.updatedAtMs = Date.now();
					nextMeta.fullBootstrapRun = { ...this.fullBootstrapRun };
				}
				return nextMeta;
			});
		} catch (error) {
			this.sdk.logger.error("Error storing meta data", {
				metadata: {
					error,
					engineContext: {
						kind: "storageWrite",
						table: "threads",
						op: "set"
					}
				},
				context: "BootstrapManager:processMeta"
			});
		}
	}
	async hydrateStores() {
		if (this.bootstrapAbortController?.signal.aborted) return;
		await this.completeHydration(this.loadEagerStores());
	}
	async loadEagerStores() {
		await Promise.all(this.sdk.stores.getStoreInstances().map((store) => store.hydrate().catch((error) => {
			this.sdk.logger.warn("Eager store hydration failed", {
				metadata: {
					store: store.tableName,
					error
				},
				context: "BootstrapManager:hydrateStores"
			});
		})));
	}
	async completeHydration(loading) {
		const signal = this.bootstrapAbortController?.signal;
		await loading;
		if (signal?.aborted) return;
		this.sdk.logger.info("Storage is ready and eager stores are hydrated");
		this.setPhase("ready");
	}
	/** Reports on settle, not on start: the stage it marks times the restore. */
	restorePersistedSlicesInBackground() {
		this.sdk.stores.messages.restorePersistedSlices().catch((error) => {
			this.sdk.logger.warn("Restoring persisted message slices failed", {
				metadata: { error },
				context: "BootstrapManager:restorePersistedSlices"
			});
		}).finally(() => {
			this.sdk.reportStartupStage("slices_restored");
		}).catch(() => void 0);
	}
};
//#endregion
//#region src/core/sync/client-capabilities.ts
const SHARED_PROJECTS_REST = "0.84.19";
function usesSharedProjectsRest(clientVersion) {
	if (!clientVersion) return false;
	return clientVersion.localeCompare(SHARED_PROJECTS_REST, void 0, { numeric: true }) >= 0;
}
//#endregion
//#region src/core/sync/partial-index-coverage.ts
/**
* Tracks which index slices are currently loaded in memory for partial-load stores.
*
* Two layers:
* - **In-memory**: `loadedSlices` Map keyed by `${table}:${indexName}:${value}`.
* - **Persisted**: IDB `_partialIndexes` table, hydrated on init and updated on
*   record/removePersisted.
*
* Consumers (SyncProcessor, MessageStore) check `isLoaded` to decide whether to
* hydrate a sync-packet row into memory or just persist it.
*/
var PartialIndexCoverage = class {
	sdk;
	loadedSlices = /* @__PURE__ */ new Map();
	constructor(sdk) {
		this.sdk = sdk;
	}
	isLoaded(table, indexName, value) {
		return this.loadedSlices.has(this.makeKey(table, indexName, value));
	}
	getAllSlices() {
		return Array.from(this.loadedSlices.values());
	}
	getSlicesForTable(table) {
		const result = [];
		for (const slice of this.loadedSlices.values()) if (slice.table === table) result.push(slice);
		return result;
	}
	async record(table, indexName, value, firstSyncId = 0) {
		const key = this.makeKey(table, indexName, value);
		const slice = {
			table,
			indexName,
			value,
			firstSyncId
		};
		this.loadedSlices.set(key, slice);
		await this.persistSlice(slice);
	}
	/**
	* Remove a slice from both memory and IDB. Used when a thread is deleted.
	*/
	async removePersisted(table, indexName, value) {
		this.loadedSlices.delete(this.makeKey(table, indexName, value));
		await this.deletePersistedSlice(table, indexName, value);
	}
	/**
	* Remove all slices for a given table from both memory and IDB.
	* Used when a schema version bump clears a table.
	*/
	async clearTable(table) {
		const keysToRemove = [];
		for (const [key, slice] of this.loadedSlices) if (slice.table === table) keysToRemove.push(key);
		for (const key of keysToRemove) this.loadedSlices.delete(key);
		await this.deletePersistedSlicesForTable(table);
	}
	async hydrate() {
		try {
			const rows = await this.sdk.storage.getAll("_partialIndexes");
			for (const row of rows) {
				const key = this.makeKey(row.table, row.indexName, row.value);
				this.loadedSlices.set(key, {
					table: row.table,
					indexName: row.indexName,
					value: row.value,
					firstSyncId: row.firstSyncId
				});
			}
			if (rows.length > 0) this.sdk.logger.info(`PartialIndexCoverage: hydrated ${rows.length} slice(s) from IDB`);
		} catch (error) {
			this.sdk.logger.warn("PartialIndexCoverage: failed to hydrate from IDB", { metadata: { error } });
		}
	}
	makeKey(table, indexName, value) {
		return `${table}:${indexName}:${value}`;
	}
	async persistSlice(slice) {
		try {
			const id = this.makeKey(slice.table, slice.indexName, slice.value);
			await this.sdk.storage.set("_partialIndexes", {
				id,
				...slice
			});
		} catch (error) {
			this.sdk.logger.warn("PartialIndexCoverage: failed to persist slice", { metadata: {
				error,
				slice
			} });
		}
	}
	async deletePersistedSlice(table, indexName, value) {
		try {
			const id = this.makeKey(table, indexName, value);
			await this.sdk.storage.remove("_partialIndexes", id);
		} catch (error) {
			this.sdk.logger.warn("PartialIndexCoverage: failed to delete persisted slice", { metadata: { error } });
		}
	}
	async deletePersistedSlicesForTable(table) {
		try {
			await this.sdk.storage.deleteManyByIndex("_partialIndexes", "table", table);
		} catch (error) {
			this.sdk.logger.warn("PartialIndexCoverage: failed to delete persisted slices for table", { metadata: {
				error,
				table
			} });
		}
	}
};
//#endregion
//#region src/core/sync/sync-processor.ts
const SYNC_ACTION_PRUNE_MARGIN = 1e4;
const SYNC_ACTION_PRUNE_STRIDE = 1e3;
const SYNC_RECENT_PACKETS = 200;
function noApplyTiming() {
	return {
		applyMs: 0,
		yieldMs: 0,
		maxSliceMs: 0,
		parseMs: 0,
		persistMs: 0,
		searchMs: 0
	};
}
const NO_YIELD = {
	sliceRows: Number.POSITIVE_INFINITY,
	betweenSlices: () => Promise.resolve(),
	betweenPages: () => Promise.resolve(),
	refreshDuringBulkMs: 3e3
};
function validYieldPolicy(policy) {
	const { sliceRows, pageSize } = policy;
	invariant(sliceRows === Number.POSITIVE_INFINITY || Number.isInteger(sliceRows) && sliceRows >= 1, `Yield policy slice size must be a positive integer or Infinity, got ${sliceRows}`);
	invariant(pageSize === void 0 || Number.isInteger(pageSize) && pageSize >= 1, `Yield policy page size must be a positive integer, got ${pageSize}`);
	return policy;
}
/**
* SyncProcessor is the single ordered pipe through which all inbound sync
* data must pass. It guarantees:
*
* 1. **Stale-sync guard** — a packet whose syncId is above 0 and at or below
*    lastSyncId is dropped, unless it is `bulk` or user config. No producer
*    sends such a packet; `SyncPacket` says which packets carry a syncId.
* 2. **Sequential application** — two packets never write storage / memory /
*    meta concurrently.
* 3. **Storage-first** — IDB writes happen before memory mutation (delegated
*    to per-store persistAndHydrate* methods).
* 4. **Tombstone-wins** — upserts are applied before deletes within a packet.
* 5. **In-memory high-water mark only** — `lastAppliedSyncId` tracks progress;
*    `_meta.lastSyncId` is written by `BootstrapManager.processMeta` alone, so
*    the cursor policy (hold on persist failure) lives in one place.
*
* The processor is producer-agnostic: BootstrapManager constructs packets from
* NDJSON lines; a future SSE delta endpoint would construct them the same way.
*/
var SyncProcessor = class {
	sdk;
	queue = [];
	processing = false;
	/**
	* The highest syncId that has been successfully applied.
	* Tracked in-memory so mid-stream _error can report it without touching _meta.
	*/
	lastAppliedSyncId = 0;
	/**
	* Holds any error that occurred during the most recent drain.
	* Cleared at the start of each drain cycle.
	*/
	lastError = void 0;
	_bestEffortWriteFailures = 0;
	_downloadErrors = 0;
	_lastPacketError;
	recent = [];
	idleResolvers = [];
	lastPrunedSyncId = 0;
	applyTiming = noApplyTiming();
	constructor(sdk) {
		this.sdk = sdk;
	}
	get bestEffortWriteFailures() {
		return this._bestEffortWriteFailures;
	}
	get downloadErrors() {
		return this._downloadErrors;
	}
	get lastPacketError() {
		return this._lastPacketError;
	}
	recentPackets() {
		return [...this.recent];
	}
	takeApplyTiming() {
		const timing = this.applyTiming;
		this.applyTiming = noApplyTiming();
		return timing;
	}
	enqueue(packet) {
		this.queue.push(packet);
		this.drain();
	}
	/**
	* Wait for the queue to drain and throw if the last drain produced an error.
	* Used by BootstrapManager's handleNdjsonLine to ensure errors bubble up
	* through consumeBootstrapStream's error tracking.
	*/
	async waitForIdle() {
		if (!this.processing && this.queue.length === 0) {
			const error = this.lastError;
			this.lastError = void 0;
			if (error) throw error;
			return;
		}
		await new Promise((resolve, reject) => {
			this.idleResolvers.push((error) => {
				if (error) reject(error);
				else resolve();
			});
		});
	}
	async drain() {
		if (this.processing) return;
		this.processing = true;
		this.lastError = void 0;
		try {
			while (this.queue.length > 0) {
				const packet = this.queue.shift();
				const startedAt = Date.now();
				try {
					await this.applyTimed(packet, startedAt);
					this.remember(packet, startedAt);
				} catch (error) {
					this.remember(packet, startedAt, error);
					this.lastError ??= error;
					this._lastPacketError = {
						at: (/* @__PURE__ */ new Date()).toISOString(),
						table: packet.table,
						syncId: packet.syncId,
						rowCount: packet.rows.length,
						message: error instanceof Error ? error.message : String(error)
					};
					this._downloadErrors += 1;
					this.sdk.logger.error("SyncProcessor: packet failed", {
						context: "SyncProcessor:drain",
						fingerprint: [`sync-packet-failed:${packet.table}`],
						tags: { sync_request_id: this.sdk.currentSyncRequestId ?? "" },
						metadata: {
							error,
							engineContext: this.packetContext(packet)
						}
					});
				}
			}
		} finally {
			this.processing = false;
			for (const resolve of this.idleResolvers) resolve(this.lastError);
			if (this.idleResolvers.length > 0) this.lastError = void 0;
			this.idleResolvers = [];
		}
	}
	async applyTimed(packet, startedAt) {
		const timing = this.applyTiming;
		const yieldMsBefore = timing.yieldMs;
		const timedFrom = performance.now();
		try {
			await this.apply(packet, startedAt, timing);
		} finally {
			const yieldMs = timing.yieldMs - yieldMsBefore;
			timing.applyMs += performance.now() - timedFrom - yieldMs;
		}
	}
	/**
	* Apply a single sync packet through the canonical pipeline:
	*
	* 1. Stale-sync guard (see the class doc).
	* 2. Delegate to the per-store handler (persistAndHydrate*), which handles
	*    storage writes, memory hydration, domain-specific logic.
	* 3. Advance lastAppliedSyncId.
	*
	* Tombstone ordering (upserts before deletes) is handled inside each store's
	* persistAndHydrate* method.
	*/
	async apply(packet, startedAt, timing) {
		if (!this.canPersist(packet)) return;
		const guardsOwnSyncId = packet.table === "user_configs";
		if (packet.syncId > 0 && !guardsOwnSyncId && packet.source !== "bulk") {
			const currentLastSyncId = (await this.sdk.storage.getMetaData())?.lastSyncId ?? 0;
			if (packet.syncId <= currentLastSyncId) return;
		}
		await this.persistInSlices(packet, timing);
		if (packet.table === "threads" || packet.table === "meetings") this.sdk.stores.projects.scheduleItemCountRefresh();
		if (packet.syncId > 0 && packet.syncId > this.lastAppliedSyncId) this.lastAppliedSyncId = packet.syncId;
		const observedSyncIds = await this.recordSyncActions(packet);
		const maxObservedSyncId = Math.max(packet.syncId, ...observedSyncIds);
		if (maxObservedSyncId > 0) await this.sdk.transactionQueue.resolveBySync(maxObservedSyncId);
		if (maxObservedSyncId > 0) await this.maybePruneSyncActions(maxObservedSyncId);
		this.sdk.logger.debug("SyncProcessor: applied packet", {
			context: "SyncProcessor:apply",
			metadata: {
				engineContext: this.packetContext(packet),
				durationMs: Date.now() - startedAt,
				maxObservedSyncId
			}
		});
	}
	/** `timing` is the object current when the packet started, so a take in the middle of a packet does not split it */
	async persistInSlices(packet, timing) {
		const { sliceRows } = this.sdk.yieldPolicy;
		if (packet.rows.length <= sliceRows) {
			await this.persistSlice(packet, timing);
			return;
		}
		for (let start = 0; start < packet.rows.length; start += sliceRows) {
			const rows = packet.rows.slice(start, start + sliceRows);
			await this.persistSlice({
				...packet,
				rows
			}, timing);
		}
	}
	async persistSlice(packet, timing) {
		const startedAt = performance.now();
		let persistResult;
		try {
			const persistStartedAt = performance.now();
			try {
				persistResult = await this.dispatchToStoreHandler(packet);
			} finally {
				timing.persistMs += performance.now() - persistStartedAt;
			}
			if (packet.source !== "bulk") {
				const searchStartedAt = performance.now();
				try {
					await this.sdk.applySearchSync(packet.table, persistResult, "SyncProcessor:apply");
				} finally {
					timing.searchMs += performance.now() - searchStartedAt;
				}
			}
		} finally {
			const sliceMs = performance.now() - startedAt;
			timing.maxSliceMs = Math.max(timing.maxSliceMs, sliceMs);
		}
		const yieldStartedAt = performance.now();
		try {
			await this.sdk.yieldPolicy.betweenSlices();
		} finally {
			timing.yieldMs += performance.now() - yieldStartedAt;
		}
	}
	remember(packet, startedAt, error) {
		this.recent.push({
			at: new Date(startedAt).toISOString(),
			table: packet.table,
			syncId: packet.syncId,
			rowCount: packet.rows.length,
			durationMs: Date.now() - startedAt,
			...error !== void 0 ? { error: error instanceof Error ? error.message : String(error) } : {}
		});
		if (this.recent.length > SYNC_RECENT_PACKETS) this.recent.shift();
	}
	async dispatchToStoreHandler(packet) {
		const stores = this.sdk.stores;
		switch (packet.table) {
			case "threads": return await stores.threads.persistAndHydrateThreads(packet.rows);
			case "messages": return await stores.messages.persistAndHydrateMessages(packet.rows);
			case "files":
				if (stores.files.effectiveLoadStrategy === "ephemeral") return EMPTY_SYNC_PERSIST_RESULT;
				return await stores.files.persistAndHydrateFiles(packet.rows);
			case "journals":
				if (stores.journals.effectiveLoadStrategy === "ephemeral") return EMPTY_SYNC_PERSIST_RESULT;
				return await stores.journals.persistAndHydrateJournals(packet.rows);
			case "meetings":
				if (stores.meetings.effectiveLoadStrategy === "ephemeral") return EMPTY_SYNC_PERSIST_RESULT;
				return await stores.meetings.persistAndHydrateMeetings(packet.rows);
			case "projects":
				if (this.sdk.usesRestProjects) return EMPTY_SYNC_PERSIST_RESULT;
				await stores.projects.persistAndHydrateProjects(packet.rows);
				return EMPTY_SYNC_PERSIST_RESULT;
			case "agents":
				await stores.agents.persistAndHydrateAgents(packet.rows);
				return EMPTY_SYNC_PERSIST_RESULT;
			case "mcpClientGrants":
				await stores.mcpClientGrants.persistAndHydrateMcpClientGrants(packet.rows);
				return EMPTY_SYNC_PERSIST_RESULT;
			case "user_configs":
				await this.sdk.userConfig.persistAndHydrate(packet.rows);
				return EMPTY_SYNC_PERSIST_RESULT;
			case "actionCards":
				if (stores.actionCards.effectiveLoadStrategy === "ephemeral") return EMPTY_SYNC_PERSIST_RESULT;
				await stores.actionCards.persistAndHydrateActionCards(packet.rows);
				return EMPTY_SYNC_PERSIST_RESULT;
			default:
				this.warnUnknownSyncPacketTable(packet);
				return EMPTY_SYNC_PERSIST_RESULT;
		}
	}
	/** Whether this platform has the packet's local storage table. */
	canPersist(packet) {
		const storageTable = STORAGE_TABLE_BY_PACKET_TABLE[packet.table];
		if (storageTable && this.sdk.storage.hasTable(storageTable)) return true;
		this.sdk.logger.warn(`Discarding sync packet for unavailable table: ${packet.table}`, { metadata: {
			storageTable,
			rowCount: packet.rows.length,
			engineContext: this.packetContext(packet)
		} });
		return false;
	}
	warnUnknownSyncPacketTable(packet) {
		this.sdk.logger.warn(`Unknown sync packet table: ${packet.table}`, { metadata: { engineContext: this.packetContext(packet) } });
	}
	packetContext(packet) {
		return {
			kind: "syncPacket",
			table: packet.table,
			syncId: packet.syncId,
			rowCount: packet.rows.length
		};
	}
	buildSyncActions(packet) {
		const appliedAt = Date.now();
		const actions = [];
		for (const row of packet.rows) {
			if (!row.id || row.sync_id === void 0 || row.sync_id <= 0) continue;
			actions.push({
				id: `sync_${row.sync_id}_${packet.table}_${row.id}`,
				syncId: row.sync_id,
				appliedAt,
				table: packet.table,
				modelId: row.id,
				action: row.deleted_at ? "delete" : "upsert"
			});
		}
		return actions;
	}
	/**
	* Record sync action headers in _syncActions for transaction-queue catchup.
	* This allows resendCached() to determine which transactions have already
	* been completed by the time the app restarts.
	*/
	async recordSyncActions(packet) {
		const actions = this.buildSyncActions(packet);
		if (actions.length === 0) return [];
		try {
			await this.sdk.storage.bulkSet("_syncActions", actions);
		} catch (error) {
			this._bestEffortWriteFailures += 1;
			this.sdk.logger.warn("SyncProcessor: failed to record sync actions", { metadata: {
				error,
				syncIds: actions.map((a) => a.syncId)
			} });
		}
		return actions.map((a) => a.syncId);
	}
	async maybePruneSyncActions(syncId) {
		if (syncId <= SYNC_ACTION_PRUNE_MARGIN || syncId - this.lastPrunedSyncId < SYNC_ACTION_PRUNE_STRIDE) return;
		this.lastPrunedSyncId = syncId;
		try {
			await this.sdk.storage.entities._syncActions?.pruneBelow(syncId - SYNC_ACTION_PRUNE_MARGIN);
		} catch (error) {
			this._bestEffortWriteFailures += 1;
			this.sdk.logger.warn("SyncProcessor: failed to prune sync actions", { metadata: {
				error,
				syncId
			} });
		}
	}
	/**
	* Returns the last successfully applied syncId.
	* Used by BootstrapManager for the partial-failure invariant
	* (mid-stream _error: rows already applied stay, lastSyncId reflects
	* the last successful packet, not the server's _meta.last_sync_id).
	*/
	getLastAppliedSyncId() {
		return this.lastAppliedSyncId;
	}
	/** Forgets the wiped data's high-water marks, which would otherwise become the next cursor */
	reset() {
		this.lastAppliedSyncId = 0;
		this.lastPrunedSyncId = 0;
		this.applyTiming = noApplyTiming();
		this._downloadErrors = 0;
		this._bestEffortWriteFailures = 0;
		this._lastPacketError = void 0;
		this.recent.length = 0;
	}
	/**
	* Wait for any in-flight packet processing to complete.
	* Unlike waitForIdle(), this does not throw on errors — it simply
	* waits for the queue to drain.
	*/
	async idle() {
		while (this.processing || this.queue.length > 0) await new Promise((resolve) => setTimeout(resolve, 0));
	}
};
//#endregion
//#region src/core/sync/transaction-queue.ts
const MAX_RETRY_ATTEMPTS = 5;
const RETRY_BASE_MS = 2e3;
const FLUSH_DEBOUNCE_MS = 50;
/**
* Durable mutation queue implementing the two-phase completion model.
*
* State machine:
*   Pending → Executing → CompletedButUnsynced → Completed
*                       ↘ Failed
*
* Stores wire through this queue by:
* 1. Optimistically applying mutations to MobX memory.
* 2. Calling enqueue() with the mutation details.
* 3. The queue flushes to the server with 50ms debounce.
* 4. On ACK with sync_id: CompletedButUnsynced.
* 5. SyncProcessor observes the sync_id: Completed.
*/
var TransactionQueue = class {
	sdk;
	pending = [];
	executing = [];
	awaitingSync = [];
	persistingCount = 0;
	flushTimer = null;
	handler = null;
	rollbackHandler = null;
	lifecycleHandlers = null;
	serverAckWaiters = /* @__PURE__ */ new Map();
	isDraining = false;
	requestController = new AbortController();
	retryNotBefore = 0;
	lastCreatedAt = 0;
	latestObservedSyncId = 0;
	_lastFailure;
	_bestEffortWriteFailures = 0;
	_uploadErrors = 0;
	get lastFailure() {
		return this._lastFailure;
	}
	get bestEffortWriteFailures() {
		return this._bestEffortWriteFailures;
	}
	get uploadErrors() {
		return this._uploadErrors;
	}
	/** Transactions whose durable persist is still in flight (strict mode) */
	getPersistingCount() {
		return this.persistingCount;
	}
	snapshot() {
		const summarize = (tx, state) => ({
			id: tx.id,
			table: tx.table,
			op: tx.op,
			modelId: tx.modelId,
			state,
			attempt: tx.attempt,
			createdAt: new Date(tx.createdAt).toISOString(),
			syncIdNeededForCompletion: tx.syncIdNeededForCompletion
		});
		return [
			...this.pending.map((tx) => summarize(tx, "pending")),
			...this.executing.map((tx) => summarize(tx, "executing")),
			...this.awaitingSync.map((tx) => summarize(tx, "completedButUnsynced"))
		];
	}
	logTransition(tx) {
		this.sdk.logger.debug("TransactionQueue: transition", {
			context: "TransactionQueue:transition",
			metadata: {
				transactionId: tx.id,
				table: tx.table,
				op: tx.op,
				modelId: tx.modelId,
				state: tx.state,
				attempt: tx.attempt,
				syncIdNeededForCompletion: tx.syncIdNeededForCompletion
			}
		});
	}
	constructor(sdk) {
		this.sdk = sdk;
	}
	/**
	* Register the handler that executes mutations against the server.
	* Called once during SDK init by the wiring layer (not by stores).
	*/
	setHandler(handler) {
		this.handler = handler;
	}
	setRollbackHandler(handler) {
		this.rollbackHandler = handler;
	}
	setLifecycleHandlers(handlers) {
		this.lifecycleHandlers = handlers;
	}
	async clear() {
		this.requestController.abort(new DOMException("SDK logged out", "AbortError"));
		this.requestController = new AbortController();
		if (this.flushTimer) clearTimeout(this.flushTimer);
		this.flushTimer = null;
		this.pending = [];
		this.executing = [];
		this.awaitingSync = [];
		this.retryNotBefore = 0;
		this.lastCreatedAt = 0;
		this.latestObservedSyncId = 0;
		this._lastFailure = void 0;
		this._bestEffortWriteFailures = 0;
		this._uploadErrors = 0;
		for (const id of this.serverAckWaiters.keys()) this.rejectServerAckWaiter(id, new DOMException("SDK logged out", "AbortError"));
		await this.sdk.storage.clear("_transactions");
	}
	/**
	* Enqueue a new transaction. Persists to IDB in Pending state.
	*
	* When `strictPersist` is set, the persist is performed before queuing
	* and failures are surfaced to the caller (who can then roll back
	* optimistic state). Without it, the legacy best-effort behavior applies:
	* persist failures are swallowed and the tx is queued regardless.
	*
	* When `waitForServerAck` is set, the transaction still moves through the
	* queue normally; only this caller waits until the queued server mutation
	* succeeds or fails terminally.
	*/
	async enqueue(tx, options = {}) {
		if (options.waitForServerAck && !this.handler) throw new Error("Transaction queue handler is not set");
		this.lastCreatedAt = Math.max(Date.now(), this.lastCreatedAt + 1);
		const fullTx = {
			...tx,
			state: "pending",
			attempt: 0,
			createdAt: this.lastCreatedAt
		};
		if (options.strictPersist) {
			this.persistingCount += 1;
			let serverAckPromise;
			try {
				await this.persistTransactionStrict(fullTx);
				serverAckPromise = options.waitForServerAck ? this.createServerAckPromise(fullTx.id) : void 0;
				this.pending.push(fullTx);
				this.logTransition(fullTx);
				this.scheduleFlush();
			} finally {
				this.persistingCount -= 1;
			}
			await serverAckPromise;
			return;
		}
		const serverAckPromise = options.waitForServerAck ? this.createServerAckPromise(fullTx.id) : void 0;
		this.pending.push(fullTx);
		this.logTransition(fullTx);
		await this.persistTransaction(fullTx);
		this.scheduleFlush();
		await serverAckPromise;
	}
	/**
	* Debounced batch flush. Moves Pending transactions to Executing,
	* calls the handler, and transitions state based on the result.
	*/
	flush = () => {
		if (this.flushTimer) clearTimeout(this.flushTimer);
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			this.drainPending();
		}, Math.max(FLUSH_DEBOUNCE_MS, this.retryNotBefore - Date.now()));
	};
	async drainPending() {
		if (!this.handler || this.isDraining) return;
		this.isDraining = true;
		try {
			while (this.pending.length > 0) {
				if (this.retryNotBefore > Date.now()) {
					this.scheduleFlush();
					break;
				}
				const tx = this.pending.shift();
				if (tx) await this.executeTransaction(tx);
			}
		} finally {
			this.isDraining = false;
		}
	}
	async executeTransaction(tx) {
		if (!this.handler) return;
		const signal = this.requestController.signal;
		this.executing.push(tx);
		tx.state = "executing";
		this.logTransition(tx);
		await this.persistTransaction(tx);
		try {
			signal.throwIfAborted();
			const result = await this.handler(tx.table, tx.op, tx.modelId, tx.payload, tx.handlerOverride, signal);
			if (!signal.aborted) await this.handleExecutionSuccess(tx, result);
		} catch (error) {
			if (!signal.aborted) await this.handleExecutionFailure(tx, error);
		}
	}
	async handleExecutionSuccess(tx, result) {
		if (result.sync_id !== void 0) {
			tx.state = "completedButUnsynced";
			tx.syncIdNeededForCompletion = result.sync_id;
			this.logTransition(tx);
			this.executing.splice(this.executing.indexOf(tx), 1);
			this.awaitingSync.push(tx);
		} else {
			tx.state = "completed";
			this.logTransition(tx);
			this.executing.splice(this.executing.indexOf(tx), 1);
			await this.deleteTransaction(tx.id);
		}
		if (tx.state === "completedButUnsynced") await this.persistTransaction(tx);
		this.lifecycleHandlers?.onServerAck(tx);
		this.resolveServerAckWaiter(tx.id);
		await this.resolveBySync(this.latestObservedSyncId);
	}
	async handleExecutionFailure(tx, error) {
		tx.attempt += 1;
		if (tx.attempt >= MAX_RETRY_ATTEMPTS) await this.failTransactionTerminally(tx, error);
		else {
			tx.state = "pending";
			this.logTransition(tx);
			this.executing.splice(this.executing.indexOf(tx), 1);
			this.pending.unshift(tx);
			await this.persistTransaction(tx);
			const delay = RETRY_BASE_MS * Math.pow(2, tx.attempt - 1);
			this.retryNotBefore = Date.now() + delay;
		}
	}
	async failTransactionTerminally(tx, error) {
		tx.state = "failed";
		this.logTransition(tx);
		this._lastFailure = {
			at: (/* @__PURE__ */ new Date()).toISOString(),
			table: tx.table,
			transactionId: tx.id,
			op: tx.op,
			attempt: tx.attempt,
			message: error instanceof Error ? error.message : String(error)
		};
		this._uploadErrors += 1;
		await this.persistTransaction(tx);
		this.executing.splice(this.executing.indexOf(tx), 1);
		if (this.rollbackHandler) try {
			await this.rollbackHandler(tx);
		} catch (rollbackErr) {
			this.sdk.logger.error("Rollback failed for terminally-failed transaction", {
				metadata: {
					rollbackErr,
					transactionId: tx.id,
					table: tx.table,
					op: tx.op
				},
				context: "TransactionQueue:drainPending"
			});
		}
		this.sdk.logger.error("Transaction failed after max retries", {
			metadata: {
				error,
				transactionId: tx.id,
				table: tx.table,
				attempt: tx.attempt,
				engineContext: {
					kind: "mutationSend",
					table: tx.table,
					action: tx.op
				}
			},
			context: "TransactionQueue:drainPending"
		});
		this.rejectServerAckWaiter(tx.id, error);
	}
	scheduleFlush() {
		this.flush();
	}
	createServerAckPromise(txId) {
		return new Promise((resolve, reject) => {
			this.serverAckWaiters.set(txId, {
				resolve,
				reject
			});
		});
	}
	resolveServerAckWaiter(txId) {
		const waiter = this.serverAckWaiters.get(txId);
		if (!waiter) return;
		this.serverAckWaiters.delete(txId);
		waiter.resolve();
	}
	rejectServerAckWaiter(txId, error) {
		const waiter = this.serverAckWaiters.get(txId);
		if (!waiter) return;
		this.serverAckWaiters.delete(txId);
		waiter.reject(error);
	}
	/**
	* Called by SyncProcessor when it applies a packet with the given syncId.
	* Transitions all CompletedButUnsynced transactions with
	* syncIdNeededForCompletion <= syncId to Completed.
	*/
	async resolveBySync(syncId) {
		this.latestObservedSyncId = Math.max(this.latestObservedSyncId, syncId);
		const resolved = [];
		for (let i = this.awaitingSync.length - 1; i >= 0; i--) {
			const tx = this.awaitingSync[i];
			if (this.isSyncConfirmed(tx, this.latestObservedSyncId)) {
				tx.state = "completed";
				this.logTransition(tx);
				resolved.push(tx);
				this.awaitingSync.splice(i, 1);
				await this.deleteTransaction(tx.id);
			}
		}
		if (resolved.length > 0) this.sdk.logger.info(`TransactionQueue: resolved ${resolved.length} transaction(s) via syncId ${syncId}`);
	}
	isSyncConfirmed(tx, observedSyncId) {
		return this.lifecycleHandlers ? this.lifecycleHandlers.isSyncConfirmed(tx, observedSyncId) : tx.syncIdNeededForCompletion !== void 0 && tx.syncIdNeededForCompletion <= observedSyncId;
	}
	/**
	* Load persisted transactions from IDB and rehydrate the queues.
	* Called by BootstrapManager after SyncProcessor init but before Ready.
	*
	* For each transaction:
	* - If syncIdNeededForCompletion is already in _syncActions → drop (already done).
	* - Otherwise: re-add to pending (for non-ACKed) or awaitingSync (ACKed-but-unsynced).
	*/
	async loadObservedSyncIds(rows) {
		const syncActions = this.sdk.storage.entities?._syncActions;
		if (syncActions) {
			const needed = rows.map((row) => row.syncIdNeededForCompletion).filter((id) => typeof id === "number");
			return {
				observedSyncIds: await syncActions.hasSyncIds(needed),
				latestObservedSyncId: await syncActions.maxSyncId() ?? 0
			};
		}
		const actions = await this.sdk.storage.getAll("_syncActions");
		return {
			observedSyncIds: new Set(actions.map((a) => a.syncId)),
			latestObservedSyncId: actions.reduce((latest, action) => Math.max(latest, action.syncId), 0)
		};
	}
	async resendCached() {
		const rows = await this.sdk.storage.getAll("_transactions");
		if (rows.length === 0) return;
		const { observedSyncIds, latestObservedSyncId } = await this.loadObservedSyncIds(rows);
		this.latestObservedSyncId = Math.max(this.latestObservedSyncId, latestObservedSyncId);
		let pruned = 0;
		let restored = 0;
		for (const partial of rows.sort((a, b) => a.createdAt - b.createdAt)) {
			if (!partial.table || !partial.state || !partial.op || !partial.modelId) {
				await this.deleteTransaction(partial.id);
				pruned++;
				continue;
			}
			const tx = partial;
			this.deserializeJsonFields(tx);
			this.lastCreatedAt = Math.max(this.lastCreatedAt, tx.createdAt);
			if (tx.state === "completed" || tx.state === "failed") {
				await this.deleteTransaction(tx.id);
				pruned++;
				continue;
			}
			this.lifecycleHandlers?.onRestore(tx);
			const observedSyncId = tx.syncIdNeededForCompletion !== void 0 && observedSyncIds.has(tx.syncIdNeededForCompletion) ? Math.max(latestObservedSyncId, tx.syncIdNeededForCompletion) : latestObservedSyncId;
			if (this.isSyncConfirmed(tx, observedSyncId)) {
				await this.deleteTransaction(tx.id);
				pruned++;
				continue;
			}
			if (tx.state === "completedButUnsynced") this.awaitingSync.push(tx);
			else {
				tx.state = "pending";
				this.pending.push(tx);
				this.logTransition(tx);
			}
			restored++;
		}
		if (restored > 0 || pruned > 0) this.sdk.logger.info(`TransactionQueue.resendCached: restored ${restored}, pruned ${pruned}`);
		if (this.pending.length > 0) this.scheduleFlush();
	}
	deserializeJsonFields(tx) {
		if (typeof tx.payload === "string") tx.payload = JSON.parse(tx.payload);
		if (typeof tx.snapshot === "string") tx.snapshot = JSON.parse(tx.snapshot);
		if (tx.handlerOverride && typeof tx.handlerOverride === "string") tx.handlerOverride = JSON.parse(tx.handlerOverride);
	}
	getPendingCount() {
		return this.pending.length;
	}
	getExecutingCount() {
		return this.executing.length;
	}
	getAwaitingSyncCount() {
		return this.awaitingSync.length;
	}
	getTransactionForModel(table, modelId) {
		return this.executing.find((tx) => tx.table === table && tx.modelId === modelId) ?? this.awaitingSync.find((tx) => tx.table === table && tx.modelId === modelId) ?? this.pending.find((tx) => tx.table === table && tx.modelId === modelId);
	}
	async persistTransaction(tx) {
		try {
			await this.sdk.storage.set("_transactions", {
				id: tx.id,
				table: tx.table,
				op: tx.op,
				modelId: tx.modelId,
				payload: JSON.stringify(tx.payload),
				snapshot: tx.snapshot ? JSON.stringify(tx.snapshot) : void 0,
				state: tx.state,
				syncIdNeededForCompletion: tx.syncIdNeededForCompletion,
				handlerOverride: tx.handlerOverride ? JSON.stringify(tx.handlerOverride) : void 0,
				attempt: tx.attempt,
				createdAt: tx.createdAt
			});
		} catch (error) {
			this._bestEffortWriteFailures += 1;
			this.sdk.logger.warn("TransactionQueue: failed to persist transaction", { metadata: {
				error,
				transactionId: tx.id
			} });
		}
	}
	/**
	* same write as persistTransaction, but rethrows on failure.
	* used by the strictPersist enqueue path so callers can roll back
	* optimistic state when the durable write fails.
	*/
	async persistTransactionStrict(tx) {
		await this.sdk.storage.set("_transactions", {
			id: tx.id,
			table: tx.table,
			op: tx.op,
			modelId: tx.modelId,
			payload: JSON.stringify(tx.payload),
			snapshot: tx.snapshot ? JSON.stringify(tx.snapshot) : void 0,
			state: tx.state,
			syncIdNeededForCompletion: tx.syncIdNeededForCompletion,
			handlerOverride: tx.handlerOverride ? JSON.stringify(tx.handlerOverride) : void 0,
			attempt: tx.attempt,
			createdAt: tx.createdAt
		});
	}
	async deleteTransaction(id) {
		try {
			await this.sdk.storage.remove("_transactions", id);
		} catch (error) {
			this._bestEffortWriteFailures += 1;
			this.sdk.logger.warn("TransactionQueue: failed to delete transaction", { metadata: {
				error,
				transactionId: id
			} });
		}
	}
};
//#endregion
//#region src/core/utils/device-id.ts
const DEVICE_ID_TIMEOUT_MS = 500;
/** A device id that cannot be read, or never arrives, must not stop the wipe that logs it */
async function readDeviceId(getDeviceId) {
	if (!getDeviceId) return "unknown";
	let timer;
	try {
		const timeout = new Promise((resolve) => {
			timer = setTimeout(() => resolve(null), DEVICE_ID_TIMEOUT_MS);
		});
		return await Promise.race([getDeviceId(), timeout]) ?? "unknown";
	} catch {
		return "unknown";
	} finally {
		clearTimeout(timer);
	}
}
//#endregion
//#region src/core/voice-mode/recording-status.ts
/**
* The shared recording lifecycle (`RecordingStatus`) reported by a platform's
* recording service and displayed by app recording controls.
*/
let RecordingStatus = /* @__PURE__ */ function(RecordingStatus) {
	/** Neither recording nor waiting for transcription */
	RecordingStatus[RecordingStatus["None"] = 0] = "None";
	/** The microphone is currently recording */
	RecordingStatus[RecordingStatus["Recording"] = 1] = "Recording";
	/** Finalizing recorded audio or waiting for its transcript */
	RecordingStatus[RecordingStatus["Processing"] = 2] = "Processing";
	return RecordingStatus;
}({});
//#endregion
//#region src/core/voice-mode/response-audio.ts
/**
* Plays one playlist of audio and final markers per reply, in the order the
* `options.playbackOrder` dictates. A final marker waits behind its reply's
* earlier audio before calling `onResponseFinished`.
*/
var ResponseAudioQueue = class {
	options;
	/**
	* Each reply's queued audio events, keyed by its user message ID. The reply
	* that plays is always the first ID in `options.playbackOrder`; the map's
	* own insertion order never matters.
	*/
	buffers = /* @__PURE__ */ new Map();
	/** Prevents concurrent enqueue calls from starting overlapping processing loops */
	isProcessingAudioEvents = false;
	/** Permanent shutdown flag for this queue instance */
	isStopped = false;
	constructor(options) {
		this.options = options;
	}
	/** Buffer one playable or final event and process it if its turn permits */
	enqueue(event) {
		if (this.isStopped) return;
		this.options.onActivity?.(event.userMessageId, event.kind);
		const buffer = this.buffers.get(event.userMessageId);
		if (buffer) buffer.push(event);
		else this.buffers.set(event.userMessageId, [event]);
		this.processAvailableAudioEvents();
	}
	/**
	* Process eligible audio and final events serially until the queue is blocked
	* or empty. A call during an active pass returns without starting another.
	*/
	async processAvailableAudioEvents() {
		if (this.isProcessingAudioEvents) return;
		this.isProcessingAudioEvents = true;
		try {
			while (!this.isStopped) {
				const event = this.currentBuffer()?.shift();
				if (!event) break;
				if (event.kind === "final") {
					this.options.debug?.("final-processed", {
						userMessageId: event.userMessageId,
						synthesisFailed: event.synthesisFailed
					});
					await this.options.onResponseFinished(event.userMessageId, event.synthesisFailed);
					continue;
				}
				await this.playAudio(event);
			}
		} finally {
			this.isProcessingAudioEvents = false;
		}
	}
	/** Remove one reply's buffered or playing audio without changing reply order */
	discard(userMessageId) {
		const queuedItems = this.buffers.get(userMessageId)?.length ?? 0;
		this.buffers.delete(userMessageId);
		this.options.debug?.("reply-discarded", {
			userMessageId,
			queuedItems
		});
		if (this.options.playbackOrder()[0] === userMessageId) this.options.playback.stop();
	}
	/** Discard queued audio and stop the current piece without reporting completion */
	stop() {
		this.isStopped = true;
		this.options.debug?.("queue-stopped", { queuedItems: [...this.buffers.values()].flat().length });
		this.buffers.clear();
		this.options.playback.stop();
	}
	/**
	* Return the buffered events of the first reply in playback order, or null
	* when it has none. Also deletes buffers for replies no longer in
	* `playbackOrder`.
	*/
	currentBuffer() {
		const order = this.options.playbackOrder();
		for (const key of [...this.buffers.keys()]) if (!order.includes(key)) this.buffers.delete(key);
		if (order.length === 0) return null;
		return this.buffers.get(order[0]) ?? null;
	}
	/**
	* Play one piece, reporting activity before and after it. A playback
	* rejection is handed to the awaited `onPlaybackError` callback.
	*/
	async playAudio(event) {
		try {
			this.options.onActivity?.(event.userMessageId, "audio");
			this.options.debug?.("play-start", {
				mimeType: event.mimeType,
				audioChars: event.audioBase64.length,
				queuedItems: [...this.buffers.values()].flat().length
			});
			await this.options.playback.play({
				mimeType: event.mimeType,
				audioBase64: event.audioBase64
			});
			if (this.isStopped) return;
			this.options.onActivity?.(event.userMessageId, "audio");
			this.options.debug?.("play-ended");
		} catch (error) {
			this.options.debug?.("play-error", { message: error instanceof Error ? error.message : String(error) });
			if (!this.isStopped) await this.options.onPlaybackError(error, event.userMessageId);
		}
	}
};
//#endregion
//#region src/core/voice-mode/voice-mode-machine.ts
/**
* One Voice Mode conversation, from first recording to disposal.
*
* `VoiceModeStore` creates one machine per session and disposes it at the
* session boundary; nothing else may hold one. The machine runs the
* conversation loop: record an utterance, send the transcript, play the
* assistant's spoken reply, listen again. Everything hardware- or
* app-specific - microphone, playback, cues, the actual chat send - arrives
* through the `VoiceModePlatform` contract, so this file holds conversation
* policy only.
*
* The central record is `pendingReplies`, keyed by user-message ID in send
* order. A reply is registered before its send goes out, incoming audio is
* accepted only for registered replies of the current session, and replies
* play in registration order. The microphone reopens only when the owner
* chat has finished its text and tool work and no reply is pending. Per-reply
* deadlines (`handleResponseStall`) recover replies whose audio stops
* flowing; failure cues and a consecutive-failure cap
* (`VOICE_INPUT_FAILURES_BEFORE_HANGUP`) handle input failures.
* Async continuations re-check `isCurrent()` (supplied by the store) after
* resuming, so late results from a replaced session are discarded instead of
* leaking into its replacement.
*/
/**
* How long a reply may go with no audio arriving or playing, once its chat
* has finished its text work, before the machine gives up on it
*/
const VOICE_RESPONSE_STALL_MS = 6e4;
const VOICE_THINKING_CUE_DELAY_MS = 1e3;
/** Consecutive voice-input failures at which the machine stops retrying and hangs up */
const VOICE_INPUT_FAILURES_BEFORE_HANGUP = 3;
/**
* Create the machine's empty reply registry. The wrapper forces every
* mutation through a MobX action. Insertion order records user-message send
* order; playback and the stall check (`handleResponseStall`) inspect the
* first entry.
*/
const createPendingReplies = () => {
	const replies = observable.map(void 0, { deep: false });
	return {
		get size() {
			return replies.size;
		},
		get: (userMessageId) => replies.get(userMessageId),
		has: (userMessageId) => replies.has(userMessageId),
		keys: () => replies.keys(),
		[Symbol.iterator]: () => replies[Symbol.iterator](),
		set: action((userMessageId, state) => {
			replies.set(userMessageId, state);
		}),
		delete: action((userMessageId) => replies.delete(userMessageId))
	};
};
const asError = (error) => error instanceof Error ? error : new Error(String(error));
/**
* Create the runtime for one Voice Mode conversation. The caller must install
* it as current before scheduled recording or platform hooks may take effect.
*/
function createVoiceModeMachine(draftId, deps) {
	const { platform, logger } = deps;
	const session = observable({ draftId });
	const recordingStatus = observable.box(0);
	const pendingReplies = createPendingReplies();
	const debug = platform.logDebugEvent ?? (() => {});
	let voiceInputFailureCount = 0;
	let submittedSoundTimer = null;
	let submittedSoundPlayed = false;
	let listeningCuePlayedThisTurn = false;
	let responseStallTimer = null;
	const replyAudioPlaying = observable.box(false);
	let thinkingCueTimer = null;
	let stopThinkingCue = null;
	/** Return this machine's current owner only while it remains installed */
	const getActiveDraftId = () => deps.isCurrent() ? session.draftId : null;
	/** Whether this machine is current and its chat is neither generating nor running tools */
	const isActiveChatIdle = () => {
		const activeDraftId = getActiveDraftId();
		return activeDraftId !== null && !deps.isThreadBusy(activeDraftId);
	};
	/** Whether the microphone may start the next user turn */
	const isChatReadyForVoiceInput = () => isActiveChatIdle() && pendingReplies.size === 0;
	/**
	* First message ID in insertion order. Later replies cannot play until this
	* entry is removed; returns `undefined` when none remain.
	*/
	const getOldestPendingReplyId = () => pendingReplies.keys().next().value;
	/** Cancel only the scheduled stall check; each reply keeps its state and deadline */
	const clearResponseStallTimer = () => {
		if (responseStallTimer === null) return;
		clearTimeout(responseStallTimer);
		responseStallTimer = null;
	};
	/**
	* Re-arm the stall check. While the owner chat is still generating text or
	* running tools, silence is normal and no deadline runs. Once the chat
	* finishes, audio becomes due for the first reply in playback order, so only
	* that reply is put on the clock. The ones behind it cannot play until it
	* finishes, and start their own deadline on reaching the head, so a long
	* reply ahead of them cannot expire them. Expiry runs `handleResponseStall`.
	*/
	function updateResponseStallTimer() {
		clearResponseStallTimer();
		if (getActiveDraftId() === null) return;
		const headReplyId = getOldestPendingReplyId();
		if (headReplyId === void 0) return;
		let headReply = pendingReplies.get(headReplyId);
		if (headReply?.status === "waiting-for-chat-idle" && isActiveChatIdle()) {
			headReply = {
				status: "waiting-for-audio",
				deadline: Date.now() + VOICE_RESPONSE_STALL_MS
			};
			pendingReplies.set(headReplyId, headReply);
		}
		if (headReply?.status !== "waiting-for-audio") return;
		responseStallTimer = setTimeout(handleResponseStall, Math.max(0, headReply.deadline - Date.now()));
	}
	/** Extend one active deadline when delivery or playback makes progress */
	function recordResponseActivity(userMessageId) {
		const reply = pendingReplies.get(userMessageId);
		if (!reply || reply.status === "recovering-from-stall") return;
		if (reply.status === "waiting-for-audio") pendingReplies.set(userMessageId, {
			status: "waiting-for-audio",
			deadline: Date.now() + VOICE_RESPONSE_STALL_MS
		});
		updateResponseStallTimer();
	}
	/** Drop the oldest stalled reply, play the failure cue, then allow the next */
	function handleResponseStall() {
		responseStallTimer = null;
		const threadId = getActiveDraftId();
		const userMessageId = getOldestPendingReplyId();
		const reply = userMessageId ? pendingReplies.get(userMessageId) : void 0;
		if (threadId === null || userMessageId === void 0 || reply?.status !== "waiting-for-audio" || reply.deadline > Date.now()) {
			updateResponseStallTimer();
			return;
		}
		debug("stall-watchdog-fired", {
			threadId,
			userMessageId,
			stallMs: VOICE_RESPONSE_STALL_MS
		});
		logger.error(/* @__PURE__ */ new Error("Voice-mode response audio stalled: no activity before the stall deadline; reopening the microphone"), {
			threadId,
			userMessageId
		});
		pendingReplies.set(userMessageId, { status: "recovering-from-stall" });
		responseAudioQueue.discard(userMessageId);
		platform.playFailureCue().finally(() => {
			if (getActiveDraftId() === null || pendingReplies.get(userMessageId)?.status !== "recovering-from-stall") return;
			pendingReplies.delete(userMessageId);
			responseAudioQueue.processAvailableAudioEvents();
			updateResponseStallTimer();
		});
	}
	/** Remove a completed or failed reply after any failure cue finishes */
	const finishSpokenReply = async (userMessageId, audioFailed) => {
		if (getActiveDraftId() === null || !pendingReplies.has(userMessageId)) return;
		if (audioFailed) await platform.playFailureCue();
		if (getActiveDraftId() === null) return;
		pendingReplies.delete(userMessageId);
	};
	const platformPlayback = platform.createPlayback();
	const responseAudioQueue = new ResponseAudioQueue({
		playback: {
			play: async (audio) => {
				runInAction(() => replyAudioPlaying.set(true));
				try {
					await platformPlayback.play(audio);
				} finally {
					runInAction(() => replyAudioPlaying.set(false));
				}
			},
			stop: () => platformPlayback.stop()
		},
		onResponseFinished: finishSpokenReply,
		onPlaybackError: async (error, userMessageId) => {
			logger.error(asError(error), {
				message: "Failed to play voice-mode response audio",
				threadId: getActiveDraftId()
			});
			await finishSpokenReply(userMessageId, true);
		},
		onActivity: recordResponseActivity,
		playbackOrder: () => [...pendingReplies.keys()],
		debug
	});
	/** Cancel a delayed submitted cue without changing whether it already played */
	const cancelSubmittedSound = () => {
		if (submittedSoundTimer !== null) clearTimeout(submittedSoundTimer);
		submittedSoundTimer = null;
	};
	/** Play the submitted cue at most once for the current recording */
	const playSubmittedSound = () => {
		cancelSubmittedSound();
		if (submittedSoundPlayed) return;
		submittedSoundPlayed = true;
		if (getActiveDraftId() !== null) platform.playSubmittedCue();
	};
	/**
	* Recover from microphone, recorder, upload, or transcription failure.
	* Failures below `VOICE_INPUT_FAILURES_BEFORE_HANGUP` cue and retry;
	* reaching it cues hangup and ends the session.
	*/
	const handleVoiceInputFailure = () => {
		if (getActiveDraftId() === null) return;
		cancelSubmittedSound();
		voiceInputFailureCount += 1;
		if (voiceInputFailureCount >= VOICE_INPUT_FAILURES_BEFORE_HANGUP) {
			platform.playHangupCue().finally(() => {
				const activeDraftId = getActiveDraftId();
				if (activeDraftId !== null) deps.stopSession(activeDraftId);
			});
			return;
		}
		platform.playFailureCue().finally(scheduleVoiceModeRecording);
	};
	/**
	* Resolve the draft ID a finished capture result should be sent to. Returns
	* null when the result must be dropped instead: this machine is no longer
	* current, or another send has already registered a pending reply.
	*/
	const getDraftIdForRecordingResult = () => {
		const activeDraftId = getActiveDraftId();
		if (activeDraftId === null) return null;
		if (pendingReplies.size > 0) {
			cancelSubmittedSound();
			return null;
		}
		return activeDraftId;
	};
	/**
	* Resume listening after a recording produced no usable speech. This is not
	* a failure: the no-speech cue plays only when the earlier submitted cue
	* told the user their speech was accepted; otherwise listening restarts
	* silently.
	*/
	const handleEmptyRecording = () => {
		if (getDraftIdForRecordingResult() === null) return;
		voiceInputFailureCount = 0;
		cancelSubmittedSound();
		if (!submittedSoundPlayed) {
			scheduleVoiceModeRecording();
			return;
		}
		platform.playNoSpeechCue().finally(scheduleVoiceModeRecording);
	};
	/** Validate one capture result, acknowledge it, and send it to the owner chat */
	const handleTranscript = (transcript) => {
		const activeDraftId = getDraftIdForRecordingResult();
		if (activeDraftId === null) return;
		const voiceModeContent = transcript.trim();
		if (!voiceModeContent) return;
		playSubmittedSound();
		voiceInputFailureCount = 0;
		platform.sendTranscript(activeDraftId, voiceModeContent).catch((error) => {
			logger.error(asError(error), {
				message: "Voice-mode send failed",
				draftId: activeDraftId
			});
		});
	};
	/** Reusable recorder and transcription uploader owned by this machine */
	const capture = platform.createCapture({
		onTranscript: handleTranscript,
		onEmptyTranscript: handleEmptyRecording,
		onTranscriptionStart: () => {
			submittedSoundTimer = setTimeout(playSubmittedSound, platform.getSubmittedCueDelayMs());
		},
		onFailure: handleVoiceInputFailure,
		onStatusChange: (status) => {
			runInAction(() => recordingStatus.set(status));
		}
	});
	/** Start listening after the chat, spoken replies, and prior capture are done */
	const startVoiceModeRecording = () => {
		if (!isChatReadyForVoiceInput()) return;
		if (capture.isCapturing() || capture.hasPendingTranscription()) return;
		submittedSoundPlayed = false;
		if (!listeningCuePlayedThisTurn) {
			listeningCuePlayedThisTurn = true;
			platform.playListeningCue();
		}
		capture.startRecordingWithAutomaticStop();
	};
	/**
	* Schedule `startVoiceModeRecording` for the next event-loop turn, after
	* capture callbacks and MobX reactions settle
	*/
	const scheduleVoiceModeRecording = () => {
		setTimeout(startVoiceModeRecording, 0);
	};
	/**
	* Resume recording whenever assistant audio and any tool work finish. The
	* saved value is the reaction's disposer; `dispose()` calls it to stop the
	* watching. Empty recordings and transcription errors schedule their own
	* retry because they do not change the watched condition (chat no longer
	* busy, no pending replies).
	*/
	const stopWatchingForReadyChat = reaction(isChatReadyForVoiceInput, (isUsersTurn) => {
		if (!isUsersTurn) return;
		listeningCuePlayedThisTurn = false;
		scheduleVoiceModeRecording();
	}, { fireImmediately: true });
	const isAssistantTurnSilent = () => {
		const activeDraftId = getActiveDraftId();
		if (activeDraftId === null || replyAudioPlaying.get()) return false;
		return deps.isThreadBusy(activeDraftId) || pendingReplies.size > 0;
	};
	const endThinkingCue = () => {
		if (thinkingCueTimer !== null) clearTimeout(thinkingCueTimer);
		thinkingCueTimer = null;
		stopThinkingCue?.();
		stopThinkingCue = null;
	};
	const stopWatchingForSilentTurn = reaction(isAssistantTurnSilent, (isSilent) => {
		endThinkingCue();
		if (!isSilent || !platform.startThinkingCue) return;
		thinkingCueTimer = setTimeout(() => {
			thinkingCueTimer = null;
			stopThinkingCue = platform.startThinkingCue?.() ?? null;
		}, VOICE_THINKING_CUE_DELAY_MS);
	}, { fireImmediately: true });
	/**
	* Run a platform lifecycle hook (e.g. `onSessionStart`) after the store's
	* session change completes (as a microtask) - so a hook may replace the session without
	* reentering that change. A hook that throws is logged with session context
	* and contained, because the microtask has no caller that could handle it.
	*/
	const notifyPlatform = (hook) => {
		queueMicrotask(() => {
			try {
				hook();
			} catch (error) {
				logger.error(asError(error), {
					message: "A VoiceModePlatform session hook threw",
					draftId: startedDraftId
				});
			}
		});
	};
	const startedDraftId = session.draftId;
	let sessionStartHookRan = false;
	notifyPlatform(() => {
		if (getActiveDraftId() === null) return;
		sessionStartHookRan = true;
		platform.onSessionStart?.(startedDraftId);
	});
	/**
	* Re-run `updateResponseStallTimer` when chat activity or reply order
	* changes. The saved value is the reaction's disposer; `dispose()` calls it
	* to stop the watching.
	*/
	const stopWatchingForStalledAudio = reaction(() => ({
		activeChatIdle: isActiveChatIdle(),
		pendingReplyIds: [...pendingReplies.keys()]
	}), updateResponseStallTimer, {
		equals: comparer.structural,
		fireImmediately: true
	});
	/**
	* Buffer one response-audio event for ordered playback, or discard the
	* reply's queued audio when the event is a reset marker. Events are
	* accepted only for this machine's owner chat and registered replies.
	*/
	const receiveResponseAudio = (event) => {
		const activeDraftId = getActiveDraftId();
		const reply = pendingReplies.get(event.userMessageId);
		if (activeDraftId !== event.threadId || !reply || reply.status === "recovering-from-stall") {
			debug("audio-event-filtered", {
				reason: activeDraftId !== event.threadId ? "thread-mismatch" : reply?.status === "recovering-from-stall" ? "reply-stalled" : "reply-not-pending",
				expectedThreadId: activeDraftId,
				receivedThreadId: event.threadId,
				userMessageId: event.userMessageId,
				kind: event.kind
			});
			return;
		}
		if (event.kind === "reset") {
			recordResponseActivity(event.userMessageId);
			responseAudioQueue.discard(event.userMessageId);
			return;
		}
		debug("audio-event-received", {
			userMessageId: event.userMessageId,
			kind: event.kind,
			mimeType: event.kind === "audio" ? event.mimeType : void 0,
			audioChars: event.kind === "audio" ? event.audioBase64.length : 0
		});
		responseAudioQueue.enqueue(event);
	};
	/**
	* End the session and play the hangup cue when a reported failure names a
	* reply this session still awaits. The ownership check rejects unattributed
	* errors and late failures from an older Voice Mode session that used the
	* same chat.
	*/
	const handlePendingReplyFailure = (threadId, userMessageId) => {
		if (getActiveDraftId() !== threadId || userMessageId === void 0 || !pendingReplies.has(userMessageId)) return;
		deps.stopSession(threadId);
		platform.playHangupCue();
	};
	/**
	* Cancel voice recording or transcription when typed text is sent in the
	* same chat. Cancelling transcription first marks its upload as obsolete, so
	* a transcript that arrives during cancellation is ignored.
	*/
	const discardActiveCaptureForTypedSend = (targetDraftId) => {
		if (getActiveDraftId() !== targetDraftId) return;
		if (capture.isCapturing()) capture.cancelRecording();
		else if (capture.hasPendingTranscription()) {
			capture.cancelPendingTranscription();
			cancelSubmittedSound();
		}
	};
	/**
	* Remove the reply registered for a send that failed before the SDK created
	* its local message, then let listening resume. The failure cue plays only
	* when no other reply could be interrupted; outstanding replies keep
	* control of playback and recording.
	*/
	const handleVoiceSendFailure = (userMessageId) => {
		if (getActiveDraftId() === null) return;
		if (pendingReplies.has(userMessageId)) {
			finishSpokenReply(userMessageId, pendingReplies.size === 1).then(() => void responseAudioQueue.processAvailableAudioEvents());
			return;
		}
		if (pendingReplies.size > 0) return;
		platform.playFailureCue().finally(() => {
			if (getActiveDraftId() === null) return;
			scheduleVoiceModeRecording();
		});
	};
	/**
	* Tear down this machine's runtime: stop the reactions and timers, stop the
	* audio queue, reset recording state, abort capture and transcription, then
	* run the platform's end hook. The store calls this exactly once, when it
	* replaces this machine.
	*/
	const dispose = () => {
		stopWatchingForReadyChat();
		stopWatchingForStalledAudio();
		stopWatchingForSilentTurn();
		clearResponseStallTimer();
		endThinkingCue();
		responseAudioQueue.stop();
		cancelSubmittedSound();
		runInAction(() => recordingStatus.set(0));
		try {
			capture.disposeAndCancelTranscription();
		} finally {
			notifyPlatform(() => {
				if (sessionStartHookRan) platform.onSessionEnd?.(startedDraftId);
			});
		}
	};
	return {
		session,
		get recordingStatus() {
			return recordingStatus.get();
		},
		retargetSession(fromDraftId, toDraftId) {
			if (getActiveDraftId() === fromDraftId) session.draftId = toDraftId;
		},
		markSpokenReplyPending(targetThreadId, userMessageId) {
			if (getActiveDraftId() !== targetThreadId) return;
			pendingReplies.set(userMessageId, { status: "waiting-for-chat-idle" });
		},
		hasPendingSpokenReplies(targetThreadId) {
			return getActiveDraftId() === targetThreadId && pendingReplies.size > 0;
		},
		receiveResponseAudio,
		discardActiveCaptureForTypedSend,
		handleVoiceSendFailure,
		handlePendingReplyFailure,
		dispose
	};
}
//#endregion
//#region src/core/voice-mode/voice-mode-store.ts
/**
* The SDK's entry point for Voice Mode and the sole owner of machine lifetime.
*
* One `VoiceModeStore` lives as long as its SDK instance; each conversation
* runs in a short-lived `VoiceModeMachine`. The store holds the app's attached
* `VoiceModePlatform` (capture, playback, cues, sending) and at most one
* machine, and forwards commands and events to whichever machine is current.
* UI code observes the store and never holds a machine, so nothing outside
* this file can retain a conversation whose session has ended.
*
* Machines are created and disposed only in `replaceMachine`, which clears the
* current-machine reference before disposing the outgoing machine. Async work
* inside a machine re-checks `isCurrent()` when it resumes, so anything that
* completes after replacement sees it is stale and does nothing.
*/
/**
* The public Voice Mode surface for one SDK instance. The store owns the
* attached platform and one observable reference to the current machine;
* session state and runtime resources share the machine's lifetime.
*/
var VoiceModeStore = class {
	context;
	currentMachine = null;
	platform = null;
	constructor(context) {
		this.context = context;
		makeAutoObservable(this, {
			currentMachine: observable.ref,
			context: false,
			platform: false,
			attachPlatform: false,
			receiveResponseAudio: false,
			discardActiveCaptureForTypedSend: false,
			notifySendFailure: false,
			notifyPendingReplyFailure: false
		});
	}
	/** Current session object; its draft ID may change without replacing the object */
	get session() {
		return this.currentMachine?.session ?? null;
	}
	/** Recording state of the current machine's capture */
	get recordingStatus() {
		return this.currentMachine?.recordingStatus ?? 0;
	}
	/** Start a session, or leave the existing session untouched for the same draft */
	startSession(draftId) {
		if (this.session?.draftId === draftId) return;
		this.replaceMachine(draftId);
	}
	/** Stop the session only when the given draft still owns it */
	stopSession(draftId) {
		if (this.session?.draftId === draftId) this.replaceMachine(null);
	}
	/**
	* Stop an owned session with a hangup cue. The cue tells a user who is not
	* watching the screen that Voice Mode ended on its own.
	*/
	stopSessionWithHangupCue(draftId) {
		if (this.session?.draftId !== draftId) return;
		this.platform?.playHangupCue();
		this.replaceMachine(null);
	}
	/** Stop an owned session the user turned off; teardown paths use `stopSession` */
	stopSessionWithStopCue(draftId) {
		if (this.session?.draftId !== draftId) return;
		this.platform?.playStopCue();
		this.replaceMachine(null);
	}
	/** Whether the given draft currently owns the one Voice Mode machine */
	isActiveForDraft(draftId) {
		return this.session?.draftId === draftId;
	}
	/** Record that the owning chat owes a spoken reply for this user message */
	markSpokenReplyPending(threadId, userMessageId) {
		this.currentMachine?.markSpokenReplyPending(threadId, userMessageId);
	}
	/** Whether this thread has an unfinished spoken reply, including buffered playback */
	hasPendingSpokenReplies(threadId) {
		return this.currentMachine?.hasPendingSpokenReplies(threadId) ?? false;
	}
	/** Deliver one response-audio event to this SDK instance's current machine */
	receiveResponseAudio(event) {
		this.currentMachine?.receiveResponseAudio(event);
	}
	/** Replace a temporary draft ID without replacing the current session */
	retargetSession(fromDraftId, toDraftId) {
		this.currentMachine?.retargetSession(fromDraftId, toDraftId);
	}
	/** Clear the current session and dispose its machine */
	reset() {
		this.replaceMachine(null);
	}
	/**
	* Connect the platform's recording, playback, cue, and send implementations.
	* Attach before Voice Mode controls become interactive. The returned cleanup
	* ends any session and detaches the platform.
	*/
	attachPlatform(platform) {
		if (this.platform) throw new Error("A Voice Mode platform is already attached");
		this.platform = platform;
		return () => {
			if (this.platform !== platform) return;
			try {
				this.reset();
			} finally {
				this.platform = null;
			}
		};
	}
	/** Cancel recording or transcription before a typed send from the same draft */
	discardActiveCaptureForTypedSend(draftId) {
		this.currentMachine?.discardActiveCaptureForTypedSend(draftId);
	}
	/** Report a spoken send that failed before the SDK created its local message */
	notifySendFailure(userMessageId) {
		this.currentMachine?.handleVoiceSendFailure(userMessageId);
	}
	/** Deliver a failure for one pending spoken reply from this SDK instance */
	notifyPendingReplyFailure(threadId, userMessageId) {
		this.currentMachine?.handlePendingReplyFailure(threadId, userMessageId);
	}
	/**
	* Replace the current machine; MobX batches the writes, so reactions
	* never see the interim null
	*/
	replaceMachine(draftId) {
		const platform = this.platform;
		if (draftId !== null && !platform) throw new Error("Cannot start Voice Mode before a platform is attached");
		const outgoing = this.currentMachine;
		this.currentMachine = null;
		outgoing?.dispose();
		if (draftId !== null) this.currentMachine = this.createMachine(draftId, platform);
	}
	/** Build a machine whose `isCurrent` callback checks this store */
	createMachine(draftId, platform) {
		let machine = void 0;
		machine = createVoiceModeMachine(draftId, {
			platform,
			isCurrent: () => this.currentMachine === machine,
			stopSession: (ownerDraftId) => this.stopSession(ownerDraftId),
			isThreadBusy: this.context.isThreadBusy,
			logger: this.context.logger
		});
		return machine;
	}
};
//#endregion
//#region src/core/sdk.ts
const SpeechToTextResponseSchema = z.object({ text: z.string() });
const ReadAloudAudioWireEventSchema = z.object({
	audio_mime_type: z.string().nullable(),
	audio_base64: z.string().nullable(),
	audio_is_final: z.boolean(),
	audio_incomplete: z.boolean().default(false)
});
const SPEECH_TRANSCRIPTION_PROCESSING_ALLOWANCE_MS = 1e5;
const SPEECH_TRANSCRIPTION_MIN_UPLOAD_BYTES_PER_SECOND = 262144;
const READ_ALOUD_TIMEOUT_MS = 6e4;
const SYNC_STATUS_MAX_TRANSACTIONS = 50;
const SYNC_STALE_AFTER_MS = 3 * SYNC_INTERVAL_MS;
const READ_ALOUD_BACKOFF = {
	numOfAttempts: 4,
	startingDelay: 1e3,
	maxDelay: 4e3
};
/**
* Bound the whole STT request: upload, semaphore wait, provider work, and the
* backend's one provider retry. Larger supported uploads receive more upload time.
*/
function getSpeechTranscriptionTimeoutMs(audioBytes) {
	return SPEECH_TRANSCRIPTION_PROCESSING_ALLOWANCE_MS + Math.ceil(audioBytes / SPEECH_TRANSCRIPTION_MIN_UPLOAD_BYTES_PER_SECOND * 1e3);
}
/** Validate and convert one newline-delimited Read Aloud wire event */
function parseReadAloudAudioEvent(line) {
	const event = ReadAloudAudioWireEventSchema.parse(JSON.parse(line));
	if (event.audio_is_final) return {
		kind: "final",
		synthesisFailed: event.audio_incomplete
	};
	if (!event.audio_base64) throw new Error("Read-aloud audio event contained no audio");
	return {
		kind: "audio",
		mimeType: event.audio_mime_type ?? "audio/mpeg",
		audioBase64: event.audio_base64
	};
}
function isPersistedStore(store) {
	return store instanceof Store && "persisted" in store.constructor && store.constructor.persisted === true;
}
function forEachPersistedStore(stores, callback) {
	const keys = Object.keys(stores);
	for (const key of keys) {
		const store = stores[key];
		if (isPersistedStore(store)) callback(store, key);
	}
}
var LittlebirdSDK = class {
	apiClient;
	wsManager;
	storage;
	transport;
	stores;
	searchAdapter;
	searchStore;
	userConfig;
	voiceMode;
	options;
	logger;
	bootstrapManager;
	lifecycle;
	syncProcessor;
	yieldPolicy;
	partialIndexCoverage;
	transactionQueue;
	pollingCron;
	_state = "uninitialized";
	_mode = "full";
	_error = null;
	_isResuming = false;
	_initPromise = null;
	_usesRestProjects = false;
	projectCatalogController = new AbortController();
	constructor(options) {
		validateOptions(options);
		this.options = Object.freeze({ ...options });
		this.logger = options.logger;
		this.storage = options.createStorageAdapter();
		this.apiClient = new ApiClient(options, this);
		this.wsManager = options.webSocketManager ?? new WebsocketManager(this);
		this.transport = new TransportManager(this);
		this.userConfig = new UserConfigStore(this);
		this.voiceMode = new VoiceModeStore({
			isThreadBusy: (threadId) => isChatStatusBusy(this.stores.threads.get(threadId)?.status),
			logger: this.logger
		});
		this.stores = new RootStore(this);
		this.transport.start();
		this.searchAdapter = options.createSearchAdapter();
		this.searchStore = new SearchStore(this.searchAdapter, { logger: this.logger });
		this.searchAdapter.setReindexReporter?.((report) => this.reportSearchReindex(report));
		this.yieldPolicy = validYieldPolicy(options.yieldPolicy ?? NO_YIELD);
		this.syncProcessor = new SyncProcessor(this);
		this.partialIndexCoverage = new PartialIndexCoverage(this);
		this.transactionQueue = new TransactionQueue(this);
		this.bootstrapManager = new BootstrapManager(this);
		this.lifecycle = new ConnectionLifecycle(this.createLifecycleDeps());
		makeObservable(this, {
			_state: observable.ref,
			_mode: observable.ref,
			_error: observable,
			_isResuming: observable,
			isReady: computed,
			isLoading: computed,
			isServicesInitialized: computed,
			isHydrated: computed,
			error: computed,
			isResuming: computed,
			initializeServices: action.bound,
			initialize: action.bound,
			initializePartialSession: action.bound,
			logout: action.bound,
			reset: action.bound,
			forceRefresh: action.bound,
			setPollingInterval: action.bound,
			resetPollingInterval: action.bound
		});
	}
	createLifecycleDeps() {
		return {
			logger: this.logger,
			startPolling: (options) => this.startPolling(options),
			stopPolling: () => this.stopPolling(),
			cancelInFlightBootstrap: async () => {
				await this.bootstrapManager.cancelInFlight();
			},
			ensureWebSocketConnected: async () => {
				if (this._state === "ready") {
					await this.wsManager.resetIfStale();
					if (this.wsManager.isConnected()) return;
				}
				await this.connectWebSocketForLifecycle();
			},
			connectWebSocket: () => this.connectWebSocketForLifecycle(),
			disconnectWebSocket: () => {
				console.info("SDK: Disconnecting WebSocket");
				this.wsManager.disconnect();
			},
			setSdkReady: () => {
				runInAction(() => {
					this._state = "ready";
					this._error = null;
				});
				this.reportStartupStage("sdk_ready");
			},
			setSdkResuming: (isResuming) => {
				runInAction(() => {
					this._isResuming = isResuming;
				});
			},
			setSdkError: (error) => {
				runInAction(() => {
					this._state = "error";
					this._error = error;
				});
			},
			setSdkConnecting: () => {
				runInAction(() => {
					this._state = "connecting";
				});
			},
			onLongInactive: this.options.onLongInactive,
			isPartialMode: () => this._mode === "partial",
			getBootstrapMode: () => this.bootstrapManager.lastBootstrapMode,
			classifyBootstrapFailure: (error) => error instanceof BootstrapInterruptedError || isTransientFetchError(error) ? "soft" : "hard"
		};
	}
	async connectWebSocketForLifecycle() {
		console.info("SDK: Connecting WebSocket");
		try {
			await this.wsManager.connect();
		} catch (error) {
			console.error("SDK: WebSocket connection failed", { error });
		}
	}
	async initializeServices() {
		if (this._state === "services_initialized" || this._state === "ready") return;
		if (this._initPromise) return this._initPromise;
		console.info("SDK: State change", {
			from: this._state,
			to: "initializing"
		});
		this._state = "initializing";
		this._initPromise = this.performServicesInitialization();
		try {
			await this._initPromise;
			runInAction(() => {
				console.info("SDK: State change", {
					from: this._state,
					to: "services_initialized"
				});
				this._state = "services_initialized";
			});
			this.reportStartupStage("services_initialized");
			this.lifecycle.notifyServicesReady();
		} catch (error) {
			runInAction(() => {
				console.error("SDK: State change", {
					from: this._state,
					to: "error",
					error
				});
				this._state = "error";
				this._error = error instanceof Error ? error : new Error(String(error));
			});
			throw error;
		} finally {
			this._initPromise = null;
		}
	}
	async initialize() {
		if (this._state === "ready") return;
		if (this._state === "uninitialized" || this._state === "initializing") await this.initializeServices();
		await this.lifecycle.beginConnect();
		if (this._state === "error" && this._error) throw this._error;
	}
	async initializePartialSession() {
		if (this._state === "ready") return;
		await this.initializeServices();
		await this.stores.messages.hydrate();
		this.transport.start();
		runInAction(() => {
			this._mode = "partial";
			this._state = "ready";
		});
		this.bootstrapManager.setPhase("ready");
	}
	/** Upload recorded speech and return its transcription */
	async transcribeSpeech(file, signal) {
		if (file.size > 20971520) throw new Error("Recording exceeds the speech-to-text upload limit");
		const formData = new FormData();
		const filename = file instanceof File && file.name ? file.name : "recording.wav";
		formData.append("file", file, filename);
		const response = await this.apiClient.fetch("stt", {
			method: "POST",
			body: formData,
			signal,
			timeoutMs: getSpeechTranscriptionTimeoutMs(file.size),
			backoff: NO_BACKOFF
		});
		return SpeechToTextResponseSchema.parse(response).text;
	}
	/** Stream validated standalone text-to-speech events as they arrive */
	async streamReadAloudAudioEvents(text, voiceId, onEvent, signal) {
		try {
			await this.apiClient.fetchNdjson("tts/chunks", {
				method: "POST",
				body: JSON.stringify({
					text,
					voice_id: voiceId
				}),
				signal,
				requestTimeoutMs: READ_ALOUD_TIMEOUT_MS,
				bodyIdleTimeoutMs: READ_ALOUD_TIMEOUT_MS,
				backoff: READ_ALOUD_BACKOFF,
				parseLine: parseReadAloudAudioEvent,
				onLine: onEvent
			});
		} catch (error) {
			if (error instanceof RequestTimeoutError) throw new Error("Read-aloud stream stalled", { cause: error });
			throw error;
		}
	}
	async logout(reason) {
		console.info("SDK: Shutting down...");
		this.voiceMode.reset();
		const sdkState = this._state;
		if (this._state === "uninitialized") await this.initializeServices();
		await this.lifecycle.notifyLogout();
		await this.transactionQueue.clear();
		await this.resetLocalData(reason, sdkState);
		this.bootstrapManager.reset();
		this.projectCatalogController.abort();
		this.projectCatalogController = new AbortController();
		this.userConfig.reset();
		runInAction(() => {
			console.info("SDK: State change", {
				from: this._state,
				to: "services_initialized"
			});
			this._state = "services_initialized";
			this._mode = "full";
			this._error = null;
		});
		console.info("SDK: Shutdown complete");
	}
	async reset() {
		console.info("SDK: Performing full reset...");
		this.voiceMode.reset();
		await this.lifecycle.notifyLogout();
		await this.resetLocalData("force_refresh");
		this.bootstrapManager.reset();
		this.userConfig.reset();
		runInAction(() => {
			console.info("SDK: State change", {
				from: this._state,
				to: "services_initialized"
			});
			this._mode = "full";
			this._state = "services_initialized";
		});
		await this.initialize();
	}
	async forceRefresh() {
		console.info("SDK: Force refreshing data...");
		this.voiceMode.reset();
		await this.lifecycle.notifyLogout();
		await this.resetLocalData("force_refresh");
		this.bootstrapManager.reset();
		await this.lifecycle.beginConnect();
	}
	/**
	* The only wipe of synced local data, except the foreign-schema reset in
	* local-db, which runs before the SDK exists and deletes the database file.
	* It stops search indexing first, but it does not cancel in-flight sync: a
	* sync run that wipes its own data calls this, and waiting for that run
	* would wait on itself. Other callers stop sync first.
	*/
	async resetLocalData(reason, sdkState = this._state) {
		this.logger.warn("SDK: resetting local data", {
			context: "LittlebirdSDK:resetLocalData",
			reason,
			sdkState,
			deviceId: await readDeviceId(this.options.getDeviceId)
		});
		await this.searchAdapter.abortIndexing();
		this.stores.clearAll();
		this.syncProcessor.reset();
		this.searchStore.clearAll();
		await this.storage.clearAll();
		try {
			await this.searchAdapter.clear(true);
		} catch (error) {
			this.logger.warn("SDK: could not clear the search index during reset", {
				context: "LittlebirdSDK:resetLocalData",
				reason,
				errorMessage: error instanceof Error ? error.message : String(error),
				metadata: { error }
			});
		}
		const lastReset = {
			reason,
			at: (/* @__PURE__ */ new Date()).toISOString()
		};
		try {
			await this.storage.updateMetaData(() => ({ lastReset }), { notifyCrossWindowEntitySync: false });
		} catch (error) {
			this.logger.warn("SDK: could not record the reset", {
				context: "LittlebirdSDK:resetLocalData",
				reason,
				errorMessage: error instanceof Error ? error.message : String(error),
				metadata: { error }
			});
		}
	}
	async hydrateStores() {
		if (this._mode === "partial") return;
		await this.bootstrapManager.hydrateStores();
	}
	async refreshFromExternalStorageSync() {
		await this.refreshLoadedPersistedStores("SDK:refreshFromExternalStorageSync");
	}
	async refreshLoadedStoresAfterBootstrap() {
		await this.refreshLoadedPersistedStores("SDK:refreshLoadedStoresAfterBootstrap");
	}
	/**
	* Messages are left out: sync already adds rows for loaded threads, and
	* re-reading every open chat is what made a per-page refresh expensive.
	*/
	async refreshLoadedListStores(tables) {
		await this.refreshLoadedPersistedStores("SDK:refreshLoadedListStores", (store, key) => key !== "messages" && tables.has(store.tableName));
	}
	async refreshLoadedPersistedStores(context, include = () => true) {
		const tasks = [];
		forEachPersistedStore(this.stores, (store, key) => {
			if (!include(store, key)) return;
			tasks.push(store.refreshOrReloadFromStorage().catch((err) => {
				if (err instanceof MissingEntityQueriesError) {
					this.logger.warn("Store refresh skipped: version skew", {
						metadata: {
							storeName: key,
							reason: "version_skew",
							error: err.message
						},
						context
					});
					return;
				}
				const cause = err instanceof Error && err.cause instanceof Error ? err.cause : void 0;
				this.logger.error("Failed to refresh store from storage", {
					metadata: {
						storeName: key,
						error: err,
						errorMessage: err instanceof Error ? err.message : String(err),
						errorCause: cause?.message
					},
					context
				});
			}));
		});
		await Promise.all(tasks);
	}
	async startPolling(options) {
		console.info("SDK: Starting polling");
		this.transport.start();
		if (!this.pollingCron) this.pollingCron = new PeriodicCron(SYNC_INTERVAL_MS, () => this.pollTick());
		this.pollingCron.start();
		this.bootstrapManager.resumeRetries();
		await this.bootstrapManager.bootstrap(options);
	}
	pollTick() {
		this.bootstrapManager.bootstrap();
	}
	/** Starts a sync, or joins the one running. During a backfill it asks for a catch-up instead. ai-docs/architecture/frontend/sync.md defines a backfill and says when the call returns */
	async syncNow() {
		await this.bootstrapManager.bootstrap();
	}
	/** While the stored flag `fullBootstrapPending` is on, waits for the whole running sync cycle, background part included, and starts one if none runs. ai-docs/architecture/frontend/sync.md says when the flag is on */
	async waitForFullBootstrap() {
		await this.bootstrapManager.waitForFullBootstrap();
	}
	/** Asks the server, in a sync run, about each chat shown running; see `BootstrapManager.checkRunningChats` */
	async checkRunningChats(threadIds) {
		await this.bootstrapManager.checkRunningChats(threadIds);
	}
	/** Marks the chats shown running for the next sync run's check, without starting one */
	markRunningChatsForCheck(threadIds) {
		this.bootstrapManager.markRunningChatsForCheck(threadIds);
	}
	startStreamCapture() {
		this.bootstrapManager.startStreamCapture();
	}
	stopStreamCapture() {
		return this.bootstrapManager.stopStreamCapture();
	}
	replayLines(lines) {
		return this.bootstrapManager.replayLines(lines);
	}
	stopPolling() {
		console.info("SDK: Stopping polling");
		this.transport.stop();
		this.bootstrapManager.suspendRetries();
		if (this.pollingCron) {
			this.pollingCron.stop();
			this.pollingCron = void 0;
		}
	}
	setPollingInterval(intervalMs) {
		if (this.pollingCron) {
			this.pollingCron.stop();
			this.pollingCron = new PeriodicCron(intervalMs, () => this.pollTick());
			this.pollingCron.start();
		}
	}
	resetPollingInterval() {
		this.setPollingInterval(SYNC_INTERVAL_MS);
	}
	get state() {
		return this._state;
	}
	get isReady() {
		return this._state === "ready";
	}
	get isResuming() {
		return this._isResuming;
	}
	get isLoading() {
		return this._state !== "ready" && this._state !== "error";
	}
	get isServicesInitialized() {
		return this._state === "services_initialized" || this._state === "ready";
	}
	get isHydrated() {
		return this.bootstrapManager.phase === "ready";
	}
	get bootstrapPhase() {
		return this.bootstrapManager.phase;
	}
	/** True during the backfill of a full bootstrap or a refill, as ai-docs/architecture/frontend/sync.md defines it. `isReady` turns true after a full bootstrap's first page, before its backfill */
	get isBackfilling() {
		return this.bootstrapManager.isBackfilling;
	}
	get refillPlannedThisLaunch() {
		return this.bootstrapManager.refillPlanned;
	}
	/** @internal */
	reportFullBootstrap(report) {
		this.reportRun("Full bootstrap", report, this.options.onFullBootstrapFinished);
	}
	/** @internal */
	reportSearchReindex(report) {
		this.reportRun("Search reindex", report, this.options.onSearchReindexFinished);
	}
	/** @internal */
	reportPruneCheck(report) {
		this.reportRun("Prune check", report, this.options.onPruneCheckFinished);
	}
	/** @internal */
	reportIncrementalSync(report) {
		this.callHost("Incremental sync report", () => this.options.onIncrementalSyncFinished?.(report));
	}
	/** @internal */
	reportStartupStage(stage) {
		this.callHost("Startup stage report", () => this.options.onStartupStage?.(stage));
	}
	callHost(label, call) {
		try {
			call();
		} catch (error) {
			this.logger.warn(`${label} failed`, {
				context: "SyncRunReport",
				metadata: { error }
			});
		}
	}
	reportRun(label, report, onFinished) {
		try {
			this.logger.info(`${label} finished: ${report.outcome}`, {
				context: "SyncRunReport",
				metadata: { ...report }
			});
			onFinished?.(report);
		} catch (error) {
			this.logger.warn(`${label} report failed`, {
				context: "SyncRunReport",
				metadata: { error }
			});
		}
	}
	/** Resolved during service init, before any sync packet can be processed. */
	get usesRestProjects() {
		return this._usesRestProjects;
	}
	/**
	* True when sync has persistently failed due to network/connectivity, so the
	* UI can surface an actionable indicator (see FE-2245).
	*/
	get syncNetworkFailing() {
		return this.bootstrapManager.syncNetworkFailing;
	}
	get currentSyncRequestId() {
		return this.bootstrapManager.currentSyncRequestId;
	}
	async getSyncStatus() {
		const { meta, degraded, phase, fullBootstrapPending, staleForMs } = await this.getSyncStatusData();
		const queue = this.getSyncQueueSnapshot();
		return {
			phase,
			lastBootstrapMode: this.bootstrapManager.lastBootstrapMode,
			consecutiveNetworkFailures: this.bootstrapManager.consecutiveNetworkFailures,
			syncNetworkFailing: this.syncNetworkFailing,
			fullBootstrapPending,
			wsConnected: this.wsManager.isConnected(),
			uploading: this.transactionQueue.getExecutingCount() > 0,
			downloading: phase === "fetchingFromServer" || phase === "writingToDatabase" || phase === "hydrating",
			hasSynced: meta?.lastSyncTime !== void 0 && !fullBootstrapPending,
			staleForMs,
			downloadErrors: this.syncProcessor.downloadErrors,
			uploadErrors: this.transactionQueue.uploadErrors,
			cursorHeldForCycles: this.bootstrapManager.consecutiveFailingCycles,
			lastSyncRequestId: this.bootstrapManager.currentSyncRequestId,
			lastSyncId: meta?.lastSyncId ?? 0,
			lastSyncTime: meta?.lastSyncTime,
			lastAppliedSyncId: this.syncProcessor.getLastAppliedSyncId(),
			queue,
			lastPacketError: this.syncProcessor.lastPacketError,
			lastTransactionFailure: this.transactionQueue.lastFailure,
			degraded
		};
	}
	async getSyncStatusData() {
		const degraded = [];
		let meta;
		try {
			meta = await this.storage.getMetaData();
		} catch (error) {
			degraded.push(`meta unreadable: ${error instanceof Error ? error.message : String(error)}`);
		}
		const txFailures = this.transactionQueue.bestEffortWriteFailures;
		if (txFailures > 0) degraded.push(`${txFailures} _transactions write(s) failed since launch`);
		const syncActionFailures = this.syncProcessor.bestEffortWriteFailures;
		if (syncActionFailures > 0) degraded.push(`${syncActionFailures} _syncActions write(s) failed since launch`);
		const lastTransactionFailure = this.transactionQueue.lastFailure;
		if (lastTransactionFailure) degraded.push(`last terminal transaction failure: ${lastTransactionFailure.table} ${lastTransactionFailure.op} (${lastTransactionFailure.message})`);
		const lastPacketError = this.syncProcessor.lastPacketError;
		if (lastPacketError) degraded.push(`last packet failure: ${lastPacketError.table} syncId=${lastPacketError.syncId ?? "n/a"} (${lastPacketError.message})`);
		const heldCycles = this.bootstrapManager.consecutiveFailingCycles;
		if (heldCycles > 0) degraded.push(`lastSyncId held for ${heldCycles} cycle(s): rows are failing to persist`);
		const phase = this.bootstrapManager.phase;
		const fullBootstrapPending = this.storage.fullBootstrapPending;
		const staleForMs = meta?.lastSyncTime ? Date.now() - Date.parse(meta.lastSyncTime) : void 0;
		if (staleForMs !== void 0 && phase === "ready" && staleForMs > SYNC_STALE_AFTER_MS) degraded.push(`no successful sync for ${Math.round(staleForMs / 1e3)}s (poll interval ${SYNC_INTERVAL_MS / 1e3}s)`);
		return {
			meta,
			degraded,
			phase,
			fullBootstrapPending,
			staleForMs
		};
	}
	getSyncQueueSnapshot() {
		return {
			pending: this.transactionQueue.getPendingCount(),
			executing: this.transactionQueue.getExecutingCount(),
			awaitingSync: this.transactionQueue.getAwaitingSyncCount(),
			transactions: this.transactionQueue.snapshot().slice(0, SYNC_STATUS_MAX_TRANSACTIONS)
		};
	}
	/** Register a callback that fires after every bootstrap cycle. Returns an unlisten function. */
	onBootstrap(cb) {
		return this.bootstrapManager.onBootstrap(cb);
	}
	/** Register a callback that fires once after the next bootstrap cycle, then auto-removes. */
	onNextBootstrap(cb) {
		this.bootstrapManager.onNextBootstrap(cb);
	}
	/**
	* Apply incremental search index updates from a store persist result.
	* Non-indexable tables and empty results are no-ops. Search failures are logged
	* but do not propagate.
	*/
	async applySearchSync(table, persistResult, context) {
		if (!isIndexableTable(table)) return;
		if (persistResult.deletedSearchDocIds.length === 0 && persistResult.upsertedRecords.length === 0) return;
		try {
			await this.searchStore.syncChanges({
				table,
				upserted: persistResult.upsertedRecords,
				deletedDocIds: persistResult.deletedSearchDocIds
			});
		} catch (err) {
			this.logger.warn("Failed to sync search index", {
				metadata: {
					table,
					error: err
				},
				context
			});
		}
	}
	get error() {
		return this._error;
	}
	async performServicesInitialization() {
		console.info("SDK: Initializing services...");
		await this.storage.init();
		if (this.storage.unrecognisedReset) {
			const lastReset = {
				reason: "db_unrecognised",
				at: (/* @__PURE__ */ new Date()).toISOString()
			};
			await this.storage.updateMetaData((meta) => meta ? void 0 : { lastReset }, { notifyCrossWindowEntitySync: false });
		}
		await this.stores.migrateSchemaVersions();
		await this.partialIndexCoverage.hydrate();
		await this.userConfig.hydrateFromStorage();
		await this.searchStore.initIndexedState();
		this.wireTransactionQueue();
		await this.wireProjectListRefresh();
		console.info("SDK: Services initialization complete");
	}
	resolveMutationRoute(table, op, modelId, handlerOverride) {
		if (handlerOverride) return handlerOverride;
		const baseEndpoint = this.stores.getStoreByTableName(table)?.apiEndpoint ?? table;
		return {
			endpoint: op === "create" ? baseEndpoint : `${baseEndpoint}/${modelId}`,
			method: op === "create" ? "POST" : op === "delete" ? "DELETE" : "PUT"
		};
	}
	wireTransactionQueue() {
		this.transactionQueue.setHandler(async (table, op, modelId, payload, handlerOverride, signal) => {
			const { endpoint, method } = this.resolveMutationRoute(table, op, modelId, handlerOverride);
			try {
				return await this.apiClient.fetch(endpoint, {
					method,
					body: JSON.stringify(payload),
					signal
				});
			} catch (error) {
				if (op === "delete" && error instanceof HttpError && (error.status === 404 || error.status === 410)) return {};
				if (table === "projects" && error instanceof HttpError) {
					if (error.status === 403) throw new ProjectForbiddenError(error.message);
				}
				throw error;
			}
		});
		this.transactionQueue.setLifecycleHandlers({
			onRestore: (tx) => {
				if (tx.table === "user_configs") this.userConfig.restoreTransaction(tx);
			},
			onServerAck: (tx) => {
				if (tx.table === "user_configs") this.userConfig.acknowledgeTransaction(tx);
			},
			isSyncConfirmed: (tx, observedSyncId) => {
				const confirmedSyncId = tx.table === "user_configs" ? this.userConfig.syncId : observedSyncId;
				return tx.syncIdNeededForCompletion !== void 0 && tx.syncIdNeededForCompletion <= confirmedSyncId;
			}
		});
		this.wireTransactionQueueRollback();
	}
	wireTransactionQueueRollback() {
		this.transactionQueue.setRollbackHandler(async (tx) => {
			if (tx.table === "user_configs") {
				await this.userConfig.rollback(tx);
				return;
			}
			const store = this.stores.getStoreByTableName(tx.table);
			if (!store) return;
			await store.rollback(tx);
		});
	}
	async wireProjectListRefresh() {
		try {
			this._usesRestProjects = usesSharedProjectsRest(await this.options.getVersion());
		} catch {
			this._usesRestProjects = false;
		}
		if (!this._usesRestProjects) return;
		this.onBootstrap(() => {
			this.refreshProjectCatalog();
		});
	}
	async refreshProjectCatalog() {
		const { signal } = this.projectCatalogController;
		const attempts = this.stores.projects.orderedData.length === 0 ? 2 : 1;
		for (let attempt = 1; attempt <= attempts && !signal.aborted; attempt++) try {
			await this.stores.projects.refreshProjects();
			return;
		} catch (error) {
			this.logger.error(error, {
				context: "SDK:refreshProjectCatalog",
				metadata: { attempt }
			});
			if (attempt < attempts) await Effect.runPromiseExit(Effect.sleep(5e3), { signal });
		}
	}
};
function validateOptions(options) {
	for (const field of [
		"apiBaseUrl",
		"wsBaseUrl",
		"platform",
		"logger",
		"getAccessToken",
		"getVersion",
		"createStorageAdapter",
		"createSearchAdapter"
	]) if (options[field] === void 0 || options[field] === null) throw new Error(`SDK Configuration Error: '${field}' is required. Please provide this field when calling createSDK().`);
	if (![
		"web",
		"ios",
		"android",
		"desktop"
	].includes(options.platform)) throw new Error(`SDK Configuration Error: 'platform' must be one of 'web', 'ios', 'android', or 'desktop'. Received: '${options.platform}'`);
}
const sdkInstance = observable.box(null, { deep: false });
function createSDK(options) {
	const existingSDK = sdkInstance.get();
	if (existingSDK) return existingSDK;
	const createdSDK = new LittlebirdSDK(options);
	runInAction(() => {
		sdkInstance.set(createdSDK);
	});
	if (typeof globalThis !== "undefined") globalThis.__littlebirdSdk = createdSDK;
	return createdSDK;
}
function getSDK() {
	const currentSDK = sdkInstance.get();
	if (!currentSDK) throw new Error("SDK not created. Call createSDK() first.");
	return currentSDK;
}
function hasSDK() {
	return sdkInstance.get() !== null;
}
function getStores() {
	return getSDK().stores;
}
function getApiClient() {
	return getSDK().apiClient;
}
//#endregion
//#region src/core/published-sites/published-sites-store.ts
const SHARE_API_BACKOFF = {
	numOfAttempts: 2,
	startingDelay: 500,
	maxDelay: 2e3
};
function toStatus(res) {
	return {
		isShared: res.is_shared,
		publicToken: res.public_token,
		visibility: res.visibility,
		sharedTeamId: res.shared_team_id,
		hasNewMessages: res.has_update ?? false
	};
}
function toVisibilityResult(res) {
	return {
		publicToken: res.public_token,
		sharedTeamId: res.shared_team_id
	};
}
async function patchVisibility(path, visibility) {
	return getApiClient().fetch(path, {
		method: "PATCH",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ visibility }),
		backoff: SHARE_API_BACKOFF
	});
}
/**
* Thin wrapper over the published-sites API. Shares are keyed by the source file (file-preview) or
* the sprite (sprites section); the sprite variants surface staleness so the link can be refreshed.
*/
var PublishedSitesStore = class {
	async getStatus(fileId) {
		return toStatus(await getApiClient().fetch(`published_sites/status/${fileId}`, {
			method: "GET",
			backoff: SHARE_API_BACKOFF
		}));
	}
	async setVisibility(fileId, visibility) {
		return toVisibilityResult(await patchVisibility(`published_sites/${fileId}`, visibility));
	}
	async revoke(fileId) {
		await patchVisibility(`published_sites/${fileId}`, "private");
	}
	async getSpriteStatus(spriteId) {
		return toStatus(await getApiClient().fetch(`published_sites/sprite/${spriteId}/status`, {
			method: "GET",
			backoff: SHARE_API_BACKOFF
		}));
	}
	async setSpriteVisibility(spriteId, visibility) {
		return toVisibilityResult(await patchVisibility(`published_sites/sprite/${spriteId}`, visibility));
	}
	async revokeSprite(spriteId) {
		await patchVisibility(`published_sites/sprite/${spriteId}`, "private");
	}
};
const publishedSitesStore = new PublishedSitesStore();
//#endregion
//#region src/core/user-config/config-accessor.ts
function activeConfig() {
	return hasSDK() ? getSDK().userConfig.config : DEFAULT_USER_CONFIG;
}
/**
* Read-only, fully-defaulted view of the synced user config.
*
* Reads the live SDK config when one exists and falls back to the schema
* defaults otherwise, so call sites never need `hasSDK()` guards or
* `?? DEFAULT_USER_CONFIG.*` fallbacks — every field always resolves to a
* concrete, typed value (e.g. `config.chat.confirm_external_links`).
*
* Reads are reactive inside MobX `observer()` components and reactions: each
* column getter touches the observable `userConfig.config` reference, which is
* replaced wholesale on every write.
*
* Writes are intentionally not exposed here — mutate through the store
* (`useUserConfig().<column>.set(...)` / `.patch(...)`) so persistence and sync
* side effects stay explicit.
*/
const config = {
	get meetings() {
		return activeConfig().meetings;
	},
	get menubar() {
		return activeConfig().menubar;
	},
	get calendar() {
		return activeConfig().calendar;
	},
	get chat() {
		return activeConfig().chat;
	},
	get privacy() {
		return activeConfig().privacy;
	},
	get notifications() {
		return activeConfig().notifications;
	},
	get proactive_agents() {
		return activeConfig().proactive_agents;
	},
	get feed() {
		return activeConfig().feed;
	},
	get auto_review() {
		return activeConfig().auto_review;
	}
};
//#endregion
//#region src/core/user-config/automatic-meeting-prep.ts
/** Plans whose users get automatic meeting prep without opting in. */
const DEFAULT_ENABLED_PLANS = /* @__PURE__ */ new Set([
	"pro_monthly",
	"pro2_monthly",
	"power_monthly",
	"power_annual"
]);
/**
* The synced `automatic_meeting_prep_enabled` value is tri-state: `null`
* means the user never made a choice. Fall back to on-by-default for
* internal users and higher-tier plans, off for everyone else.
*/
function resolveAutomaticMeetingPrepEnabled(savedPreference, user) {
	return savedPreference ?? (user.isInternalUser || DEFAULT_ENABLED_PLANS.has(user.currentPlan ?? ""));
}
/**
* A long lead time can make many meetings eligible in the same pass, for
* example right after the user raises the setting. Each pass of a client
* poller requests prep for at most this many meetings, soonest first, so the
* rest start on later passes instead of all at once
*/
const MAX_AUTOMATIC_MEETING_PREPS_PER_PASS = 3;
const MEETING_PREP_LEAD_TIME_PRESETS_MINUTES = [
	30,
	60,
	120,
	360,
	720,
	1440
];
function formatMeetingPrepLeadTime(minutes) {
	const hours = Math.floor(minutes / 60);
	const remainingMinutes = minutes % 60;
	const parts = [];
	if (hours > 0) parts.push(hours === 1 ? "1 hour" : `${hours} hours`);
	if (remainingMinutes > 0 || hours === 0) parts.push(remainingMinutes === 1 ? "1 minute" : `${remainingMinutes} minutes`);
	return parts.join(" ");
}
//#endregion
//#region src/core/user-config/email-attribution.ts
function resolveEmailAttributionEnabled(savedPreference, user) {
	return savedPreference ?? !(user.isPaid || user.isInternalUser);
}
//#endregion
//#region src/core/user-config/meeting-assist.ts
/**
* The synced `meeting_assist_enabled` value is tri-state: `null` means the
* user never made a choice, and Meeting Assist is on for them. The backend's
* `is_meeting_assist_turned_on` applies the same default, so change the two
* together.
*/
function resolveMeetingAssistEnabled(savedPreference) {
	return savedPreference ?? true;
}
//#endregion
//#region src/core/user-config/meeting-intelligence.ts
/**
* The synced `meeting_intelligence_enabled` value is tri-state: `null` means
* the user never made a choice, and Meeting Intelligence is on for them. The
* backend's `is_meeting_intelligence_turned_on` applies the same default, so
* change the two together.
*/
function resolveMeetingIntelligenceEnabled(savedPreference) {
	return savedPreference ?? true;
}
//#endregion
//#region src/meetings/post-processing/upload-transcript.ts
var EmptyLocalTranscriptUploadError = class extends Error {
	constructor() {
		super("Local transcript upload has no non-empty transcript segments");
		this.name = "EmptyLocalTranscriptUploadError";
	}
};
var LocalTranscriptUploadStageError = class extends Error {
	stage;
	constructor(stage, cause) {
		super(`Local transcript ${stage} failed`, { cause });
		this.name = "LocalTranscriptUploadStageError";
		this.stage = stage;
	}
};
async function resolveStartOffset(deps, meetingId) {
	return (await deps.fetchMeetingTranscript(meetingId)).currentLength;
}
async function uploadLocalTranscript(deps, input) {
	const segments = normalizeSegments(input.segments);
	if (segments.length === 0) throw new EmptyLocalTranscriptUploadError();
	let currentOffset = input.startOffset;
	try {
		for (const segment of segments) currentOffset = (await deps.patchTranscript(input.meetingId, currentOffset, segment.text, segment.speaker)).current_length;
	} catch (error) {
		throw new LocalTranscriptUploadStageError("upload", error);
	}
	try {
		await deps.ingestRemainingTranscript(input.meetingId);
	} catch (error) {
		throw new LocalTranscriptUploadStageError("ingest", error);
	}
	return { currentLength: currentOffset };
}
function normalizeSegments(segments) {
	return segments.map((segment) => ({
		speaker: segment.speaker.trim() || "Unknown",
		text: segment.text.trim()
	})).filter((segment) => segment.text);
}
//#endregion
//#region src/meetings/post-processing/replace-transcript-suffix.ts
/** Backend rejected the round permanently for this watermark: the transcript
* changed (409 transcript_changed_since_round), another device holds it
* (409 meeting_recording_owned_by_other_device), or the watermark is beyond
* the transcript (422 start_offset_beyond_transcript). All mean "discard the
* round": no ack, keep the record, reprocess at the next stop. Retrying the
* same offset would just loop, so this is NOT a retryable failure. */
var TranscriptChangedError = class extends Error {
	constructor(cause) {
		super("Transcript changed/watermark stale for round; discard and resync", { cause });
		this.name = "TranscriptChangedError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
var TeamPolicyRejectedError = class extends Error {
	constructor(cause) {
		super("The team's data policy rejected the transcript write", { cause });
		this.name = "TeamPolicyRejectedError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
/** Retryable: ingestion-reset (503) or network failure. */
var ReplaceTranscriptSuffixFailedError = class extends Error {
	constructor(cause) {
		super("Replace transcript suffix failed", { cause });
		this.name = "ReplaceTranscriptSuffixFailedError";
		Object.setPrototypeOf(this, new.target.prototype);
	}
};
async function replaceTranscriptSuffix(deps, input) {
	try {
		const result = await deps.replaceTranscriptSuffix(input.meetingId, input.startOffset, input.expectedLength, input.segments, input.deviceId, input.processingToken);
		return {
			currentLength: result.current_length,
			currentSpeaker: result.current_speaker
		};
	} catch (error) {
		if (asTranscriptRequestFailure(error)?.errorCode === "transcript_removed_by_policy") throw new TeamPolicyRejectedError(error);
		if (isDiscardRoundFailure(error)) throw new TranscriptChangedError(error);
		throw new ReplaceTranscriptSuffixFailedError(error);
	}
}
/** Backend `error_code`s that mean the round is permanently stale for this
* watermark: discard it instead of retrying. */
const DISCARD_ROUND_ERROR_CODES = /* @__PURE__ */ new Set([
	"transcript_changed_since_round",
	"meeting_recording_owned_by_other_device",
	"start_offset_beyond_transcript"
]);
/** Both 409 causes (transcript changed, recording owned by another device)
* mean "discard the round", so any 409 qualifies. A 422 qualifies only with
* the stale-watermark error_code; unknown 422s (e.g. schema validation) and
* everything else stay retryable. */
function isDiscardRoundFailure(error) {
	const failure = asTranscriptRequestFailure(error);
	if (!failure) return false;
	if (failure.status === 409) return true;
	return failure.errorCode !== void 0 && DISCARD_ROUND_ERROR_CODES.has(failure.errorCode);
}
function asTranscriptRequestFailure(error) {
	if (typeof error !== "object" || error === null) return void 0;
	const { status, errorCode } = error;
	if (typeof status !== "number") return void 0;
	if (errorCode !== void 0 && typeof errorCode !== "string") return void 0;
	return {
		status,
		errorCode
	};
}
//#endregion
//#region src/meetings/meeting-note-sources.ts
/**
* Note-taking apps whose notes the backend syncs into Meeting Notes. `slug` is the app's remote MCP
* server slug, the value a synced meeting carries in `externalSource`. Mirrors `SOURCES` in
* backend/lb/features/external_meeting_notes/sources/__init__.py.
*/
const MEETING_NOTE_SOURCES = [
	{
		slug: "clickup",
		displayName: "ClickUp"
	},
	{
		slug: "fireflies",
		displayName: "Fireflies"
	},
	{
		slug: "granola",
		displayName: "Granola"
	},
	{
		slug: "otter_ai",
		displayName: "Otter.ai"
	},
	{
		slug: "plaud",
		displayName: "Plaud"
	},
	{
		slug: "sybill",
		displayName: "Sybill"
	}
];
function getMeetingNoteSource(slug) {
	return MEETING_NOTE_SOURCES.find((source) => source.slug === slug);
}
/** The `disabled_note_sources` config value after the user turns `slug` on or off */
function withMeetingNoteSourceEnabled(disabledSources, slug, enabled) {
	const others = disabledSources.filter((disabled) => disabled !== slug);
	return enabled ? others : [...others, slug];
}
//#endregion
//#region src/meetings/recording-rules.ts
function isMeetingBlocked(rules, title, attendeeEmails) {
	const foldedTitle = (title ?? "").toLowerCase();
	const emails = new Set(attendeeEmails.map((email) => email.toLowerCase()));
	const domains = [...emails].map((email) => email.split("@").pop() ?? "");
	return rules.some(({ kind, value }) => {
		switch (kind) {
			case "title_keyword": return foldedTitle.includes(value);
			case "attendee_email": return emails.has(value);
			case "attendee_domain": return domains.some((domain) => domain === value || domain.endsWith(`.${value}`));
		}
	});
}
//#endregion
//#region src/core/search/base-search-indexer.ts
function defineIndexableSource(table, toItems, options) {
	return {
		table,
		toItems: (rows) => toItems(rows),
		rowIdIsDocId: options?.rowIdIsDocId
	};
}
const DEFAULT_CHUNK_SIZE = 100;
/**
* Shared indexing orchestration for search adapters. Subclasses implement the
* platform-specific storage primitives (add / remove / clear / search) and
* declare which entity sources to index. The base owns: single-flight
* indexing, version-checked full/incremental reindex, paged fetching,
* cooperative chunked indexing, abort handling, and metadata writes.
*
* A full rebuild checkpoints itself into `_meta.searchIndexProgress` as it
* goes, so a pass cut short by a kill or a background resumes on the next
* launch rather than starting over.
*/
var BaseSearchIndexer = class {
	storage;
	config;
	logger;
	indexingPromise = null;
	abortController = null;
	reindexReporter;
	activeRebuild;
	constructor(storage, config, logger) {
		this.storage = storage;
		this.config = config;
		this.logger = logger;
	}
	async getLastIndexedAt() {
		return (await this.storage.getMetaData())?.lastIndexedAt;
	}
	/** True while the next pass would rebuild rather than index incrementally. */
	async isFullReindexPending() {
		const meta = await this.storage.getMetaData();
		if (meta?.searchRebuildOwed) return true;
		if (this.resumableProgress(meta?.searchIndexProgress)) return true;
		return (meta?.searchIndexVersion ?? 0) !== this.config.searchIndexVersion;
	}
	async indexData(options) {
		if (this.indexingPromise) return this.indexingPromise;
		this.indexingPromise = this.performIndexing(options);
		try {
			await this.indexingPromise;
		} finally {
			this.indexingPromise = null;
		}
	}
	async abortIndexing() {
		this.abortController?.abort();
		await this.indexingPromise?.catch(() => {});
	}
	setReindexReporter(reporter) {
		this.reindexReporter = reporter;
	}
	async add(items) {
		await this.indexingPromise;
		await this.indexItemsInChunks(items);
	}
	async remove(ids) {
		await this.indexingPromise;
		await this.removeItemsInChunks(ids);
	}
	async syncChanges(options) {
		await this.indexingPromise;
		const { table, upserted, deletedDocIds } = options;
		if (deletedDocIds.length === 0 && upserted.length === 0) return;
		if (deletedDocIds.length > 0) await this.removeItemsInChunks(deletedDocIds);
		const source = this.config.sources.find((s) => s.table === table);
		if (!source || upserted.length === 0) return;
		const rows = upserted;
		await this.onBeforeSourceIndex(source, rows, false);
		const items = source.toItems(rows);
		if (source.rowIdIsDocId) {
			const itemIds = new Set(items.map((item) => item.id));
			const staleIds = rows.map((row) => row.id).filter((id) => !itemIds.has(id));
			if (staleIds.length > 0) await this.removeItemsInChunks(staleIds);
		}
		if (items.length > 0) await this.indexItemsInChunks(items);
	}
	async clear(abortIndexing = false) {
		if (abortIndexing) await this.abortIndexing();
		await this.init();
		await this.discardProgressCheckpoint();
		await this.clearIndex();
		await this.commit();
	}
	async commit() {}
	async onBeforeSourceIndex(_source, _rows, _isFullReindex) {}
	async indexTableNatively(_table) {
		return false;
	}
	async indexItemsInChunks(items) {
		await this.init();
		if (items.length === 0) return;
		const chunkSize = this.config.chunkSize ?? DEFAULT_CHUNK_SIZE;
		for (let i = 0; i < items.length; i += chunkSize) {
			this.throwIfAborted();
			const chunk = items.slice(i, i + chunkSize);
			await this.addItems(chunk);
			if (i + chunkSize < items.length) await new Promise((resolve) => setTimeout(resolve, 0));
		}
		await this.commit();
	}
	async removeItemsInChunks(ids) {
		await this.init();
		if (ids.length === 0) return;
		const chunkSize = this.config.chunkSize ?? DEFAULT_CHUNK_SIZE;
		for (let i = 0; i < ids.length; i += chunkSize) {
			this.throwIfAborted();
			const chunk = ids.slice(i, i + chunkSize);
			await this.removeItems(chunk);
			if (i + chunkSize < ids.length) await new Promise((resolve) => setTimeout(resolve, 0));
		}
		await this.commit();
	}
	throwIfAborted() {
		if (this.abortController?.signal.aborted) {
			const error = /* @__PURE__ */ new Error("Indexing operation was aborted");
			error.name = "AbortError";
			throw error;
		}
	}
	async performIndexing(options) {
		this.abortController = new AbortController();
		try {
			await this.init();
			await this.storage.init();
			this.throwIfAborted();
			const plan = await this.resolveIndexingPlan(options);
			if (plan.kind === "incremental") await this.runIncrementalPass(plan.lastIndexedAt);
			else await this.runFullRebuild(plan);
			this.logger.debug(`${this.logLabel}: indexing complete`);
		} catch (error) {
			this.reportIndexingError(error);
			await this.endActiveRebuild(error);
			throw error;
		} finally {
			this.abortController = null;
			this.activeRebuild = void 0;
		}
	}
	/** Closed in storage so the next launch doesn't re-report it; `clear()` awaits this write */
	async endActiveRebuild(error) {
		const progress = this.activeRebuild;
		if (!progress?.run) return;
		const aborted = error instanceof Error && error.name === "AbortError";
		progress.run.updatedAtMs = Date.now();
		progress.run.open = false;
		this.reportRebuild(progress, aborted ? "aborted" : "failed");
		try {
			await this.storeProgress(progress);
		} catch (storeError) {
			this.logger.warn(`${this.logLabel}: could not close rebuild attempt`, { metadata: { error: storeError } });
		}
	}
	reportRebuild(progress, outcome) {
		const run = progress.run;
		if (!run || !this.reindexReporter) return;
		const durationMs = attemptDurationMs(run);
		this.reindexReporter({
			outcome,
			trigger: run.trigger,
			scope: "full",
			resumed: run.resumed,
			attempt: run.attempt,
			durationMs,
			totalDurationMs: run.priorMs + durationMs,
			tablesCompleted: progress.completedTables.length,
			tablesTotal: this.config.sources.length
		});
	}
	reportIndexingError(error) {
		if (error instanceof Error && error.name === "AbortError") {
			this.logger.info("Indexing was aborted");
			return;
		}
		this.logger.error(error instanceof Error ? error : new Error(String(error)), {
			context: `${this.logContext}_indexing_error`,
			metadata: { message: "Failed to index data" }
		});
	}
	async resolveIndexingPlan(options) {
		const meta = await this.storage.getMetaData();
		const previous = meta?.searchIndexProgress;
		const owed = meta?.searchRebuildOwed;
		const forced = options?.fullReindex || owed !== void 0;
		const resumable = options?.fullReindex && !owed ? void 0 : this.resumableProgress(previous);
		const resumed = owed && resumable?.owedGeneration !== owed.generation ? void 0 : resumable;
		const versionMatches = (meta?.searchIndexVersion ?? 0) === this.config.searchIndexVersion;
		if (!forced && !resumed && versionMatches && meta?.lastIndexedAt) return {
			kind: "incremental",
			lastIndexedAt: meta.lastIndexedAt
		};
		if (resumed) this.logger.info(`${this.logLabel}: resuming index rebuild`, { metadata: {
			startedAt: resumed.startedAt,
			completedTables: resumed.completedTables,
			table: resumed.table
		} });
		else this.logger.info(`${this.logLabel}: creating full index`);
		return {
			kind: "full",
			resumed,
			previous,
			trigger: this.reindexTrigger(meta, options, versionMatches),
			owedGeneration: owed?.generation
		};
	}
	reindexTrigger(meta, options, versionMatches) {
		if (options?.fullReindex) return options.trigger ?? "full_bootstrap";
		if (meta?.searchRebuildOwed) return meta.searchRebuildOwed.trigger;
		if (!meta?.lastIndexedAt) return "first_index";
		if (!versionMatches) return "index_version";
		return "unknown";
	}
	resumableProgress(progress) {
		if (!progress || progress.version !== this.config.searchIndexVersion) return;
		return {
			...progress,
			completedTables: [...progress.completedTables]
		};
	}
	async runIncrementalPass(lastIndexedAt) {
		const startedAt = (/* @__PURE__ */ new Date()).toISOString();
		for (const source of this.config.sources) {
			const rows = await this.storage.getAllAfter(source.table, lastIndexedAt) ?? [];
			this.throwIfAborted();
			await this.onBeforeSourceIndex(source, rows, false);
			await this.indexItemsInChunks(source.toItems(rows));
			this.throwIfAborted();
		}
		await this.writeIndexMeta((meta) => {
			meta.lastIndexedAt = startedAt;
			meta.searchIndexVersion = this.config.searchIndexVersion;
		});
	}
	async runFullRebuild(plan) {
		const { resumed, previous } = plan;
		if (previous?.run?.open) this.reportRebuild(previous, "interrupted");
		const progress = resumed ?? this.newProgressCheckpoint(plan.owedGeneration);
		progress.run = startRunRecord(plan.trigger, resumed?.run, resumed !== void 0);
		this.activeRebuild = progress;
		if (!progress.cleared) {
			progress.completedTables = [];
			delete progress.table;
			delete progress.cursor;
			await this.storeProgress(progress);
			await this.clearIndex();
			await this.commit();
			progress.cleared = true;
			await this.storeProgress(progress);
		} else await this.storeProgress(progress);
		for (const source of this.config.sources) {
			if (progress.completedTables.includes(source.table)) continue;
			await this.rebuildSource(source, progress);
			this.throwIfAborted();
		}
		await this.writeIndexMeta((meta) => {
			meta.lastIndexedAt = progress.startedAt;
			meta.searchIndexVersion = this.config.searchIndexVersion;
			delete meta.searchIndexProgress;
			if (meta.searchRebuildOwed?.generation === progress.owedGeneration) delete meta.searchRebuildOwed;
		});
		this.activeRebuild = void 0;
		progress.run.updatedAtMs = Date.now();
		this.reportRebuild(progress, "completed");
	}
	newProgressCheckpoint(owedGeneration) {
		const now = (/* @__PURE__ */ new Date()).toISOString();
		return {
			version: this.config.searchIndexVersion,
			completedTables: [],
			startedAt: now,
			updatedAt: now,
			...owedGeneration === void 0 ? {} : { owedGeneration }
		};
	}
	async rebuildSource(source, progress) {
		if (await this.indexTableNatively(source.table)) {
			await this.markTableComplete(source.table, progress);
			return;
		}
		const startCursor = progress.table === source.table ? progress.cursor : void 0;
		for await (const page of this.streamSourceRows(source.table, startCursor)) {
			const rows = page.records;
			await this.onBeforeSourceIndex(source, rows, true);
			await this.indexItemsInChunks(source.toItems(rows));
			progress.table = source.table;
			progress.cursor = page.nextCursor;
			await this.storeProgress(progress);
			this.throwIfAborted();
		}
		await this.markTableComplete(source.table, progress);
	}
	async markTableComplete(table, progress) {
		progress.completedTables = [...progress.completedTables, table];
		delete progress.table;
		delete progress.cursor;
		await this.storeProgress(progress);
	}
	streamSourceRows(table, startCursor) {
		const entities = this.storage.entities[table];
		if (!entities) throw new MissingEntityQueriesError(table);
		return streamEntityPages((options) => entities.listPage(options), (cursor, options) => entities.listPageAfter(cursor, options), {
			signal: this.abortController?.signal,
			pageSize: this.config.pageSize,
			startCursor,
			onYield: () => new Promise((resolve) => setTimeout(resolve, 0))
		});
	}
	async storeProgress(progress) {
		const now = Date.now();
		progress.updatedAt = new Date(now).toISOString();
		if (progress.run?.open) progress.run.updatedAtMs = now;
		const snapshot = {
			...progress,
			completedTables: [...progress.completedTables],
			run: progress.run && { ...progress.run }
		};
		await this.writeIndexMeta((meta) => {
			meta.searchIndexProgress = snapshot;
		});
	}
	/** No-op without a metadata row, so a wiped store is not resurrected */
	async discardProgressCheckpoint() {
		await this.storage.updateMetaData((meta) => {
			if (!meta?.searchIndexProgress) return void 0;
			const next = { ...meta };
			delete next.searchIndexProgress;
			return next;
		}, { notifyCrossWindowEntitySync: false });
	}
	/** Never writes a sync cursor: one invented for a wiped store would make it look synchronized */
	async writeIndexMeta(mutate) {
		await this.storage.updateMetaData((existing) => {
			const next = { ...existing };
			mutate(next);
			return next;
		}, { notifyCrossWindowEntitySync: false });
	}
	get logLabel() {
		return "SearchIndexer";
	}
	get logContext() {
		return "search_indexer";
	}
};
//#endregion
//#region src/core/search/types.ts
const SEARCH_FETCH_LIMIT = 1e3;
const SEARCH_PRESENTATION_LIMIT = 500;
//#endregion
//#region src/core/search/ranking.ts
const titleOf = (item) => "title" in item ? item.title?.toLowerCase() ?? "" : "";
const contentOf = (item) => "content" in item ? item.content?.toLowerCase() ?? "" : "";
function tokenizeSearchQuery(normalizedQuery) {
	return normalizedQuery.toLowerCase().split(" ").filter(Boolean);
}
const TYPE_PRIORITY = {
	thread: 0,
	meeting_summary: 1,
	meeting_note: 1,
	message: 2,
	journal: 2,
	file: 2
};
function typePriority(type) {
	return TYPE_PRIORITY[type];
}
function tierFor(item, searchQuery, tokens) {
	const title = titleOf(item);
	if (title.includes(searchQuery)) return 0;
	if (tokens.every((token) => title.includes(token))) return 1;
	if (contentOf(item).includes(searchQuery)) return 2;
	return 3;
}
function rankSearchItems(items, normalizedQuery) {
	const tokens = tokenizeSearchQuery(normalizedQuery);
	if (tokens.length === 0) return items;
	const phrase = normalizedQuery.toLowerCase();
	const decorated = items.map((item) => ({
		item,
		tier: tierFor(item, phrase, tokens)
	}));
	decorated.sort((a, b) => a.tier - b.tier || typePriority(a.item.type) - typePriority(b.item.type) || b.item.updatedAt.localeCompare(a.item.updatedAt));
	return decorated.map((d) => d.item);
}
//#endregion
//#region src/core/search/grouping.ts
const BUCKET_DISPLAY_ORDER = [
	"threads",
	"messages",
	"meetings",
	"journals",
	"others"
];
function groupSearchResultsByBucket(items, normalizedQuery) {
	const ranked = rankSearchItems(items, normalizedQuery);
	const byBucket = /* @__PURE__ */ new Map();
	for (const item of ranked) {
		const bucket = bucketForResultType(item.type);
		let group = byBucket.get(bucket);
		if (!group) {
			group = [];
			byBucket.set(bucket, group);
		}
		group.push(item);
	}
	return BUCKET_DISPLAY_ORDER.filter((b) => byBucket.has(b)).map((b) => ({
		bucket: b,
		label: labelForSearchResultBucket(b),
		items: byBucket.get(b)
	}));
}
/**
* Display bucket for a SearchItem type. meeting_note is grouped with
* meeting_summary under "meetings" (both describe the same meeting).
*/
function bucketForResultType(type) {
	if (type === "thread") return "threads";
	if (type === "message") return "messages";
	if (type === "meeting_summary" || type === "meeting_note") return "meetings";
	if (type === "journal") return "journals";
	return "others";
}
const BUCKET_LABEL = {
	threads: "Chats",
	messages: "Messages",
	meetings: "Meetings",
	journals: "Journals",
	others: "Others"
};
function labelForSearchResultBucket(bucket) {
	return BUCKET_LABEL[bucket];
}
//#endregion
//#region src/core/search/query.ts
/**
* Normalizes raw user input before tokenizing/indexing:
* strips surrounding whitespace, replaces ASCII control chars (including DEL)
* with a space, and collapses internal whitespace runs to a single space.
*
* Example: '  wee\x00kly\t\tsync\x7F  ' -> 'wee kly sync'
*/
function normalizeSearchQuery(raw) {
	return raw.trim().replace(/[\x00-\x1F\x7F]/g, " ").replace(/\s+/g, " ").trim();
}
/**
* Characters stripped from index tokens. We keep alphanumerics, underscore,
* and any non-ASCII char (\u0080-\uFFFF, e.g. accented letters) so unicode
* FTS5 barewords survive. Everything else (punctuation, symbols) becomes a
* split point so it can't be matched as part of a token.
*
* Examples of what gets dropped/split:
*   'week*ly' -> ['week', 'ly']     ( '*' is a split point )
*   'foo/bar' -> ['foo', 'bar']     ( '/' is a split point )
*   "O'Reilly" -> ['O', 'Reilly']   ( "'" is a split point )
*   'café' -> ['café']              ( 'é' kept — inside \u0080-\uFFFF )
*/
const SEARCH_TOKEN_KEEP = /[^A-Za-z0-9_\u0080-\uFFFF]/g;
/**
* Splits a normalized query into index/match tokens using SEARCH_TOKEN_KEEP.
* Output mirrors what an FTS5 unicode61 tokenizer would produce, so tokens
* built here line up with tokens stored in the search index.
*
* Example: tokenizeForSearchIndex('follow-up café') -> ['follow', 'up', 'café']
*/
function tokenizeForSearchIndex(normalized) {
	return normalized.replace(SEARCH_TOKEN_KEEP, " ").split(/\s+/).filter(Boolean);
}
//#endregion
//#region src/core/subscription/types.ts
const ResearchLimitsSchema = z.object({
	messages_used: z.number(),
	daily_limit: z.number(),
	messages_remaining: z.number(),
	is_limit_hit: z.boolean(),
	warning_threshold: z.number()
});
const RoutinesLimitsSchema = z.object({
	routines_created: z.number(),
	routines_limit: z.number(),
	max_enabled: z.boolean().optional(),
	ultra_enabled: z.boolean().optional(),
	god_enabled: z.boolean().optional(),
	argon_enabled: z.boolean().optional()
});
const CreditLimitsSchema = z.object({
	balance: z.number(),
	allowance_credits: z.number(),
	used_credits: z.number(),
	remaining_credits: z.number(),
	is_low: z.boolean(),
	is_blocked: z.boolean(),
	cycle_start: z.string().optional(),
	resets_at: z.string(),
	plan_grant_kind: z.enum(["monthly", "one_time"]).nullish()
});
/** An absent `routines_limits` means uncapped, not a cap of zero. */
function deriveRoutinesLimitState(limits) {
	const routinesCreated = limits?.routines_created ?? 0;
	const routinesLimit = limits?.routines_limit;
	const hasLimit = typeof routinesLimit === "number";
	return {
		hasLimit,
		hasReachedLimit: hasLimit && routinesCreated >= routinesLimit,
		routinesCreated,
		routinesLimit
	};
}
//#endregion
//#region src/core/subscription/limit-utils.ts
function shouldShowLimitWarning(limits, isNewThread) {
	if (!limits) return false;
	const { messages_remaining, warning_threshold, is_limit_hit } = limits;
	return !is_limit_hit && !isNewThread && messages_remaining !== void 0 && warning_threshold !== void 0 && messages_remaining <= warning_threshold;
}
/** Max (research) mode is the only mode still metered by message count. */
function formatLimitWarningMessage(remaining, researchLabel = "Max") {
	return `${remaining} ${researchLabel} ${remaining === 1 ? "message" : "messages"} remaining today.`;
}
//#endregion
//#region src/core/subscription/chat-mode-policy.ts
const TEAM_MODE_CHAT_MODES = {
	lite: DASH_CHAT_MODE,
	regular: MAIN_CHAT_MODE,
	max: RESEARCH_CHAT_MODE,
	ultra: ULTRA_CHAT_MODE
};
/** Steps a disallowed mode toward Default, which a team always allows */
function clampChatMode(mode, teamAllowedModes) {
	const allowed = teamAllowedModes?.map((teamMode) => TEAM_MODE_CHAT_MODES[teamMode]);
	const isTeamMode = Object.values(TEAM_MODE_CHAT_MODES).includes(mode);
	if (!allowed || !isTeamMode || allowed.includes(mode)) return mode;
	if (mode === "ultra" && allowed.includes("research")) return RESEARCH_CHAT_MODE;
	return MAIN_CHAT_MODE;
}
//#endregion
//#region src/core/utils/tts-chunker.ts
const PARAGRAPH_SPLIT = /\n\s*\n/;
const SENTENCE_SPLIT = /(?<=[.!?])\s+/;
const WORD_SPLIT = /\s+/;
/**
* Split text into speakable chunks that respect a maximum character length.
*
* Native speech engines silently truncate long content: Android's TextToSpeech
* caps each call at ~4000 chars, and Chromium's SpeechSynthesisUtterance cuts
* off utterances longer than ~15 seconds. This utility produces chunks the
* caller can queue and play sequentially.
*
* Boundary priority (most to least preferred): paragraph → sentence → word →
* hard character split. Chunks are packed — as much content as fits below the
* limit is kept together so playback has fewer seams.
*
* @param text - Plain text to chunk. Markdown should be stripped by the caller.
* @param options.maxChars - Max characters per chunk (e.g. 3500 for Android).
* @returns Ordered chunks. Empty or whitespace-only input returns an empty array.
*/
function chunkTextForSpeech(text, { maxChars }) {
	if (maxChars < 1) throw new Error(`chunkTextForSpeech: maxChars must be >= 1 (got ${maxChars})`);
	const normalized = text.replace(/\r\n/g, "\n").trim();
	if (!normalized) return [];
	const chunks = [];
	let current = "";
	for (const rawParagraph of normalized.split(PARAGRAPH_SPLIT)) {
		const paragraph = rawParagraph.trim();
		if (!paragraph) continue;
		const merged = current ? `${current}\n\n${paragraph}` : paragraph;
		if (merged.length <= maxChars) {
			current = merged;
			continue;
		}
		if (current) {
			chunks.push(current);
			current = "";
		}
		if (paragraph.length <= maxChars) {
			current = paragraph;
			continue;
		}
		for (const subChunk of splitOversizedParagraph(paragraph, maxChars)) chunks.push(subChunk);
	}
	if (current) chunks.push(current);
	return chunks;
}
function splitOversizedParagraph(paragraph, maxChars) {
	const chunks = [];
	let current = "";
	const flush = () => {
		if (current) chunks.push(current);
		current = "";
	};
	const tryAppend = (piece, separator) => {
		const candidate = current ? current + separator + piece : piece;
		if (candidate.length <= maxChars) {
			current = candidate;
			return true;
		}
		return false;
	};
	const emitWords = (piece) => {
		for (const word of piece.split(WORD_SPLIT)) {
			if (!word) continue;
			if (word.length > maxChars) {
				flush();
				for (let i = 0; i < word.length; i += maxChars) chunks.push(word.slice(i, i + maxChars));
				continue;
			}
			if (!tryAppend(word, " ")) {
				flush();
				current = word;
			}
		}
	};
	for (const rawSentence of paragraph.split(SENTENCE_SPLIT)) {
		const sentence = rawSentence.trim();
		if (!sentence) continue;
		if (tryAppend(sentence, " ")) continue;
		flush();
		if (sentence.length <= maxChars) {
			current = sentence;
			continue;
		}
		emitWords(sentence);
	}
	flush();
	return chunks;
}
//#endregion
//#region src/core/utils/parse-chat-history.ts
function modalitiesOf(json) {
	try {
		const parsed = JSON.parse(json);
		return (Array.isArray(parsed) ? parsed.flat() : [parsed]).filter((item) => typeof item === "object" && item !== null && "type" in item);
	} catch {
		return [];
	}
}
const MESSAGES_ROLES = [
	"user",
	"assistant",
	"tool",
	"system",
	"developer"
];
function parseMessageParts(msgParts) {
	let text = "";
	let thinkingContent = "";
	let hiddenContent = "";
	let agent = "";
	let role = "";
	let fileIds = [];
	const modalities = [];
	for (let i = 0; i < msgParts.length; i++) {
		const part = msgParts[i];
		if (i === 0 && MESSAGES_ROLES.includes(part)) {
			role = part;
			continue;
		}
		if (part.startsWith("text:")) text += part.slice(5);
		else if (part.startsWith("thought:")) thinkingContent += part.slice(8);
		else if (part.startsWith("hidden_text:")) hiddenContent += part.slice(12);
		else if (part.startsWith("agent:")) agent = part.slice(6);
		else if (part.startsWith("file_ids:")) try {
			fileIds = JSON.parse(part.slice(9));
		} catch (error) {
			console.warn("Failed to parse file_ids:", part, error);
		}
		else if (part.startsWith("modalities:")) modalities.push(...modalitiesOf(part.slice(11)));
		else if (part.startsWith("id:") || part.startsWith("created_at:") || part.startsWith("updated_at:") || part.startsWith("tool_use:")) continue;
		else console.warn("Unexpected message part:", part);
	}
	const isTeamChatPost = modalities.some((modality) => modality.type === "team_chat_post");
	return {
		role,
		text,
		thinkingContent,
		hiddenContent,
		agent,
		fileIds,
		modalities: isTeamChatPost ? modalities : []
	};
}
function chatHistoryToMessages(history, chatId, logger) {
	return history.map((entry, idx) => {
		const parsed = parseMessageParts(entry);
		return new Message({
			id: String(idx),
			threadId: chatId,
			role: parsed.role === "user" ? "user" : "assistant",
			content: parsed.text || "",
			status: "done",
			mode: MAIN_CHAT_MODE,
			fileIds: parsed.fileIds,
			thinkingContent: parsed.thinkingContent,
			hiddenContent: parsed.hiddenContent,
			createdAt: /* @__PURE__ */ new Date(Date.now() - 6e4),
			updatedAt: void 0,
			agent: parsed.agent,
			modalitiesUsed: parsed.modalities.length > 0 ? parsed.modalities : null,
			isExpanded: false,
			needsTruncation: void 0
		}, {}, logger);
	});
}
//#endregion
//#region src/react/index.tsx
const SDKContext = createContext(null);
/**
* Provides the SDK instance to the React tree.
*/
function SDKProvider({ children }) {
	if (!hasSDK()) throw new Error("SDK not created. Call createSDK() before rendering SDKProvider.");
	return /* @__PURE__ */ jsx(SDKContext.Provider, {
		value: getSDK(),
		children
	});
}
function useSDK() {
	const sdk = useContext(SDKContext);
	if (!sdk) throw new Error("useSDK must be used within a SDKProvider");
	return sdk;
}
function useStores() {
	return useSDK().stores;
}
function useSDKState() {
	const sdk = useSDK();
	return {
		state: sdk.state,
		isReady: sdk.isReady,
		isLoading: sdk.isLoading,
		isServicesInitialized: sdk.isServicesInitialized,
		isHydrated: sdk.isHydrated,
		bootstrapPhase: sdk.bootstrapPhase,
		syncNetworkFailing: sdk.syncNetworkFailing,
		error: sdk.error
	};
}
function useUserConfig() {
	return useSDK().userConfig;
}
//#endregion
//#region src/core/feed/feed-tabs.store.ts
const filterSections = [
	"statuses",
	"types",
	"apps"
];
const localPeopleSchema = z.record(z.string(), z.array(z.string()));
const legacyViewId = (id) => `legacy:${id}`;
const sameSelection = (left, right) => left.length === right.length && left.every((value) => right.includes(value));
const restoreLegacyTabs = (tabs, customTabs, peopleByTab) => DEFAULT_FEED_TABS.flatMap((defaults) => {
	const saved = tabs.find((tab) => tab.builtin === defaults.builtin);
	const id = legacyViewId(defaults.id);
	if (customTabs.some((tab) => tab.id === id)) return [];
	return saved && (saved.name !== defaults.name || filterSections.some((section) => !sameSelection(saved.configuration[section], defaults.configuration[section])) || saved.configuration.sort !== defaults.configuration.sort) || Boolean(peopleByTab.get(id)?.length) ? [{
		id,
		builtin: null,
		name: saved && saved.name !== defaults.name ? saved.name : `${defaults.name} filters`,
		configuration: feedConfigurationSchema.parse(saved?.configuration ?? defaults.configuration)
	}] : [];
});
var FeedTabsStore = class {
	config;
	storage;
	deviceKey;
	createId;
	selection;
	selectedId = "for_you";
	defaultId = "for_you";
	renamingId = null;
	menuSession = null;
	previousTabId = "for_you";
	peopleByTab = /* @__PURE__ */ new Map();
	constructor(config, storage, deviceKey, createId = v4, selection) {
		this.config = config;
		this.storage = storage;
		this.deviceKey = deviceKey;
		this.createId = createId;
		this.selection = selection;
		try {
			this.defaultId = storage.getItem(this.defaultStorageKey) ?? "for_you";
		} catch {}
		this.selectedId = selection?.selectedId ?? this.defaultId;
		try {
			const storedPeople = storage.getItem(this.peopleStorageKey);
			if (storedPeople) {
				const parsed = localPeopleSchema.safeParse(JSON.parse(storedPeople));
				if (parsed.success) this.peopleByTab = new Map(Object.entries(parsed.data));
			}
		} catch {}
		let movedPeople = false;
		for (const builtin of DEFAULT_FEED_TABS) {
			const selected = this.peopleByTab.get(builtin.id);
			if (!selected?.length) continue;
			const savedId = legacyViewId(builtin.id);
			if (!this.peopleByTab.has(savedId)) this.peopleByTab.set(savedId, selected);
			this.peopleByTab.delete(builtin.id);
			movedPeople = true;
		}
		if (movedPeople) this.persistPeople();
		makeAutoObservable(this, {}, { autoBind: true });
	}
	get tabs() {
		const ids = /* @__PURE__ */ new Set();
		const builtins = /* @__PURE__ */ new Set();
		const tabs = this.config.values.tabs.filter((tab) => {
			if (DEFAULT_FEED_TABS.some((builtin) => builtin.id === tab.id) && tab.builtin !== tab.id || tab.builtin && tab.id !== tab.builtin) return false;
			if (ids.has(tab.id) || tab.builtin && builtins.has(tab.builtin)) return false;
			ids.add(tab.id);
			if (tab.builtin) builtins.add(tab.builtin);
			return true;
		});
		const builtinTabs = DEFAULT_FEED_TABS.map((defaults) => {
			const saved = tabs.find((tab) => tab.builtin === defaults.builtin);
			return {
				...defaults,
				configuration: {
					...defaults.configuration,
					groupBy: saved?.configuration.groupBy ?? defaults.configuration.groupBy
				}
			};
		});
		const customTabs = tabs.filter((tab) => !tab.builtin);
		const legacyTabs = restoreLegacyTabs(tabs, customTabs, this.peopleByTab);
		return [
			...builtinTabs.filter((tab) => tab.builtin !== "history"),
			...legacyTabs,
			...customTabs,
			builtinTabs.find((tab) => tab.builtin === "history")
		];
	}
	get primaryTabs() {
		const tabs = this.tabs.filter((tab) => tab.builtin !== "history");
		const defaultIndex = tabs.findIndex((tab) => tab.id === this.defaultId);
		if (defaultIndex > 0) tabs.unshift(tabs.splice(defaultIndex, 1)[0]);
		return tabs;
	}
	get activeTab() {
		return this.tabs.find((tab) => tab.id === this.selectedId) ?? this.tabs.find((tab) => tab.builtin === "for_you");
	}
	get defaultTabId() {
		return this.primaryTabs.some((tab) => tab.id === this.defaultId) ? this.defaultId : "for_you";
	}
	setDefault(id) {
		if (!this.primaryTabs.some((tab) => tab.id === id)) return;
		this.defaultId = id;
		try {
			this.storage.setItem(this.defaultStorageKey, id);
		} catch {}
	}
	get canResetMenuTab() {
		const tab = this.menuTab;
		const session = this.menuSession;
		if (!tab || !session) return false;
		if (tab.builtin) return false;
		return this.menuChangedConfigurationKeys().length > 0 || session.editedPeople && this.sectionDiffers(tab, "people", session);
	}
	get menuTab() {
		return this.tabs.find((tab) => tab.id === this.menuSession?.tabId);
	}
	get clearBaseline() {
		return this.menuSession;
	}
	canClearSection(section) {
		const tab = this.menuTab;
		const baseline = this.clearBaseline;
		return tab && baseline ? this.sectionDiffers(tab, section, baseline) : false;
	}
	sectionDiffers(tab, section, baseline) {
		const current = new Set(section === "people" ? this.getPeople(tab.id) : tab.configuration[section]);
		const original = new Set(section === "people" ? baseline.people : baseline.configuration[section]);
		return current.size !== original.size || [...current].some((value) => !original.has(value));
	}
	getPeople(id) {
		return this.peopleByTab.get(id) ?? [];
	}
	setPeople(id, people) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab || tab.builtin) return;
		if (id.startsWith("legacy:") && !this.config.values.tabs.some((tab) => tab.id === id)) this.config.set("tabs", this.tabs);
		runInAction(() => {
			if (this.menuSession?.tabId === id) this.menuSession.editedPeople = true;
			if (people.length) this.peopleByTab.set(id, [...new Set(people)]);
			else this.peopleByTab.delete(id);
		});
		this.persistPeople();
	}
	get peopleStorageKey() {
		return this.deviceKey + ":people";
	}
	get defaultStorageKey() {
		return this.deviceKey + ":default";
	}
	persistPeople() {
		try {
			this.storage.setItem(this.peopleStorageKey, JSON.stringify(Object.fromEntries(this.peopleByTab)));
		} catch {}
	}
	select(id) {
		if (!this.tabs.some((tab) => tab.id === id)) return;
		runInAction(() => {
			if (this.activeTab.builtin !== "history") this.previousTabId = this.activeTab.id;
			this.selectedId = id;
			this.selection?.onSelect(id);
		});
	}
	toggleHistory() {
		this.closeMenu();
		if (this.activeTab.builtin !== "history") {
			this.select("history");
			return;
		}
		this.select(this.primaryTabs.some((tab) => tab.id === this.previousTabId) ? this.previousTabId : "for_you");
	}
	selectRelative(direction) {
		const tabs = this.primaryTabs;
		const index = tabs.findIndex((tab) => tab.id === this.activeTab.id);
		const next = index < 0 ? direction > 0 ? 0 : tabs.length - 1 : (index + direction + tabs.length) % tabs.length;
		this.select(tabs[next].id);
	}
	add() {
		this.startRename(this.createTab(DEFAULT_FEED_TABS[0], "Untitled"));
	}
	duplicate(id) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (tab) this.startRename(this.createTab(tab, `${tab.name} copy`, this.getPeople(id)));
	}
	saveAsNewView(id, name) {
		const trimmed = name.trim();
		const source = this.tabs.find((tab) => tab.id === id);
		if (!trimmed || !source) return;
		const people = [...this.getPeople(id)];
		if (this.menuSession?.tabId === id) this.restoreMenuChanges();
		this.createTab(source, trimmed, people);
		this.closeMenu();
		this.finishRename();
	}
	createTab(source, name, people = []) {
		const tab = {
			id: this.createId(),
			builtin: null,
			name,
			configuration: feedConfigurationSchema.parse(source.configuration)
		};
		this.config.set("tabs", [...this.tabs, tab]);
		this.setPeople(tab.id, people);
		this.select(tab.id);
		return tab.id;
	}
	startRename(id) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab || tab.builtin) return;
		runInAction(() => {
			this.menuSession = null;
			this.renamingId = id;
		});
	}
	finishRename() {
		runInAction(() => {
			this.renamingId = null;
		});
	}
	rename(id, name) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab || tab.builtin) return;
		const trimmed = name.trim();
		if (trimmed) this.config.set("tabs", this.tabs.map((tab) => tab.id === id ? {
			...tab,
			name: trimmed
		} : tab));
		this.finishRename();
	}
	remove(id) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab || tab.builtin) return;
		const removedDefault = this.defaultId === id;
		this.config.set("tabs", this.tabs.filter((candidate) => candidate.id !== id));
		runInAction(() => {
			this.peopleByTab.delete(id);
			if (removedDefault) this.defaultId = "for_you";
		});
		this.persistPeople();
		if (removedDefault) try {
			this.storage.setItem(this.defaultStorageKey, "for_you");
		} catch {}
		if (this.selectedId === id) this.select(this.activeTab.id);
		this.closeMenu();
	}
	reorder(id, overId) {
		const tabs = [...this.tabs];
		const from = tabs.findIndex((tab) => tab.id === id);
		const to = tabs.findIndex((tab) => tab.id === overId);
		if (from < 0 || to < 0 || from === to || tabs[from]?.builtin || tabs[to]?.builtin) return;
		const [tab] = tabs.splice(from, 1);
		tabs.splice(to, 0, tab);
		this.config.set("tabs", tabs);
	}
	move(id, direction) {
		const index = this.tabs.findIndex((tab) => tab.id === id);
		const target = this.tabs[index + direction];
		if (target) this.reorder(id, target.id);
	}
	openMenu(id) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab) return;
		runInAction(() => {
			this.menuSession = {
				tabId: id,
				configuration: feedConfigurationSchema.parse(tab.configuration),
				people: [...this.getPeople(id)],
				editedConfigurationKeys: [],
				editedPeople: false
			};
		});
	}
	closeMenu() {
		runInAction(() => {
			this.menuSession = null;
		});
	}
	configure(id, partial) {
		const tab = this.tabs.find((candidate) => candidate.id === id);
		if (!tab) return;
		const changes = tab.builtin ? partial.groupBy === void 0 ? {} : { groupBy: partial.groupBy } : partial;
		if (!Object.keys(changes).length) return;
		if (this.menuSession?.tabId === id) {
			for (const key of Object.keys(changes)) if (!this.menuSession.editedConfigurationKeys.includes(key)) this.menuSession.editedConfigurationKeys.push(key);
		}
		this.config.set("tabs", this.tabs.map((tab) => tab.id === id ? {
			...tab,
			configuration: {
				...tab.configuration,
				...changes
			}
		} : tab));
	}
	clearMenuSectionChanges(section) {
		const tab = this.menuTab;
		const baseline = this.clearBaseline;
		if (!tab || !baseline) return;
		if (section === "people") {
			this.setPeople(tab.id, baseline.people);
			return;
		}
		const configuration = feedConfigurationSchema.parse(baseline.configuration);
		this.configure(tab.id, { [section]: configuration[section] });
	}
	resetMenuTab() {
		const tab = this.menuTab;
		if (!tab) return;
		if (!tab.builtin) this.restoreMenuChanges();
	}
	restoreMenuChanges() {
		const tab = this.menuTab;
		const session = this.menuSession;
		if (!tab || !session) return;
		const original = feedConfigurationSchema.parse(session.configuration);
		const changed = Object.fromEntries(this.menuChangedConfigurationKeys().map((key) => [key, original[key]]));
		if (Object.keys(changed).length) this.configure(tab.id, changed);
		if (session.editedPeople && this.sectionDiffers(tab, "people", session)) this.setPeople(tab.id, session.people);
	}
	menuChangedConfigurationKeys() {
		const tab = this.menuTab;
		const session = this.menuSession;
		if (!tab || !session) return [];
		return session.editedConfigurationKeys.filter((key) => Array.isArray(session.configuration[key]) ? this.sectionDiffers(tab, key, session) : tab.configuration[key] !== session.configuration[key]);
	}
};
//#endregion
export { ARGON_CHAT_MODE, ASK_USER_FREE_TEXT_PLACEHOLDER, AUTO_REVIEW_RULE_BEHAVIORS, ActionCard, ActionCardIdSchema, Agent, AgentIdSchema, ApiClient, Arena, ArenaAgentResponse, BaseSearchIndexer, BootstrapPhase, CalendarEvent, CalendarEventIdSchema, ChatConnectionError, ChatStatus, ConfigColumn, CreditLimitsSchema, DASH_CHAT_MODE, DEFAULT_ENTITY_PAGE_LIMIT, DEFAULT_FEED_TABS, DEFAULT_GMAIL_TRIGGER_LABELS, DEFAULT_ROUTINE_ICON, DEFAULT_USER_CONFIG, Draft, DraftAttachmentLimitError, DraftFile, EMPTY_JSON_CONTENT, EMPTY_SYNC_PERSIST_RESULT, ENTITY_ICON_CATALOG, EmptyLocalTranscriptUploadError, EntityIdSchema, Exchange, FAST_CHAT_MODE, FEED_TYPE_LABELS, FeedTabsStore, File$1 as File, FileIdSchema, FileLinkMetaSchema, GMAIL_TRIGGER_LABELS, GOD_CHAT_MODE, HUMMINGBIRD_CHAT_MODE, HttpError, ICON_CODE_MAX_LENGTH, ICON_COLOR_KEYS, ICON_COLOR_LABELS, IntegrationActionType, JSONContentSchema, Journal, JournalIdSchema, LegacyRoutineLibraryResponseSchema, LittlebirdSDK, LocalTranscriptUploadStageError, MAIN_CHAT_MODE, MAX_ATTACHMENTS_PER_MESSAGE, MAX_AUTOMATIC_MEETING_PREPS_PER_PASS, MAX_AUTO_REVIEW_RULES, MAX_AUTO_REVIEW_RULE_CHARS, MAX_SPEECH_TO_TEXT_AUDIO_BYTES, MAX_TOTAL_ATTACHMENT_BYTES, MAX_VIDEO_FILE_BYTES, MAX_WEBHOOK_CONDITIONS, MEETING_NOTE_SEARCH_ID_SUFFIX, MEETING_NOTE_SOURCES, MEETING_PREP_LEAD_TIME_PRESETS_MINUTES, MEETING_SUMMARY_SEARCH_ID_SUFFIX, MEETING_TRIGGER_KINDS, McpClientGrant, McpClientGrantIdSchema, Meeting, MeetingCreationCancelledError, MeetingIdSchema, MeetingPrepIdSchema, MeetingQuotaExceededError, MeetingShareInvitesSchema, MeetingShareResponseSchema, MeetingShareStatusSchema, MeetingShareVisibilitySchema, Message, MessageIdSchema, ModalityType, Model, NEEDS_YOU_FEED_SECTION, NO_APPLY_TIMING, NO_BACKOFF, NoOpSearchAdapter, OUTLOOK_TRIGGER_FOLDERS, PINNED_FEED_GROUP, PROJECTS_NUDGE_DEBOUNCE_MS, PROJECTS_NUDGE_JITTER_MS, Project, ProjectIdSchema, ProjectNotEmptyError, READY_FEED_SECTION, REQUIRED_ENTITY_TYPES, RESEARCH_CHAT_MODE, ROUTINE_INTERVAL_END_ERROR, ROUTINE_TRIGGER_GMAIL, ROUTINE_TRIGGER_KINDS, ROUTINE_TRIGGER_MEETING_STARTED, ROUTINE_TRIGGER_MEETING_SUMMARIZED, ROUTINE_TRIGGER_OUTLOOK, ROUTINE_TRIGGER_WEBHOOK, RecordingStatus, ReplaceTranscriptSuffixFailedError, Report, ReportIdSchema, ReportStatus, ReportTemplateIdSchema, ResearchLimitsSchema, ResponseAudioQueue, RootStore, Routine, RoutineDraft, RoutineFormDraftSchema, RoutineFormSubmissionSchema, RoutineIdSchema, RoutineLibraryItemSchema, RoutineLibraryResponseSchema, RoutineLibraryTabSchema, RoutineTriggerDraftSchema, RoutinesLimitsSchema, SDKProvider, SEARCH_FETCH_LIMIT, SEARCH_PRESENTATION_LIMIT, SNOOZED_FEED_GROUP, STORE_MIGRATION_POLICIES, SearchStore, SteerRefusedError, SyncIdSchema, TeamPolicyRejectedError, Thread, ThreadIdSchema, TodoAction, TodoReferenceIdSchema, TodoStatus, TranscriptChangedError, TranscriptTermCorrectionError, TranscriptTermCorrectionErrorCode, ULTRA_CHAT_MODE, UNPINNED_FEED_GROUP, UPDATES_FEED_SECTION, USER_CONFIG_TABLE, UserConfigModel, UserConfigNotConfirmedError, UserConfigSchema, UserConfigStore, UserIdSchema, VoiceModeStore, WEBHOOK_CONDITION_OPERATORS, WEBSOCKET_CONFIG, WORKING_FEED_GROUP, WSMethods, WebhookConditionDraftSchema, adaptReportModelToSource, adaptRoutineModelToSource, addMember, addPageApplyTiming, attachmentTotalBytes, attemptDurationMs, bucketForResultType, buildEntityPageFromOverfetch, buildFileItems, buildJournalItems, buildMeetingItems, buildMeetingNotesSendContext, buildMeetingSearchItems, buildMessageItems, buildThreadItems, chatHistoryToMessages, chunkTextForSpeech, clampChatMode, composeDraftMessageContent, config, connectionCardCompletionMessageId, createDefaultRoutineFormDraft, createMeetingTriggerDraft, createOutlookTriggerDraft, createProjectFileUploadUrl, createRoutineFormDraftFromRoutine, createSDK, defineIndexableSource, delegateActionCard, delegationPersonName, deleteProjectFile, deriveRoutinesLimitState, discardTeamChatDraft, drainEntityPages, engineEditsMessages, extractBackendErrorCode, fetchDelegationTeammates, fetchInviteTeam, fetchMembers, fetchProjectChat, fetchProjectChatFile, fetchProjectChats, fetchProjectFiles, fetchProjectMeeting, fetchProjectMeetingFile, fetchProjectMeetings, fetchReceivedDelegations, fetchSentDelegations, fetchShareLink, fetchShareLinkPreview, fetchTeamChatDraft, finalizeProjectFile, formatLimitWarningMessage, formatMeetingPrepLeadTime, getApiClient, getCorrectableTermRanges, getDefaultRoutineTriggerTime, getDraftSendabilityError, getDraftSendabilityMessage, getMeetingIdFromMeetingNoteSearchId, getMeetingIdFromMeetingSummarySearchId, getMeetingNoteSearchId, getMeetingNoteSource, getMeetingSearchDocIds, getMeetingSummarySearchId, getRoutineTitleUpdate, getSDK, getStores, getTableName, getTranscriptTermMatchOffsets, groupSearchResultsByBucket, handleChatMessage, handleChatMessageBatch, handleServerError, hasNoUpcomingIntervalOccurrence, hasSDK, isAgentThread, isChatStatusBusy, isDraftEmpty, isDraftSendable, isIconColorKey, isIndexableTable, isMeetingBlocked, isPlaceholderThreadTitle, isRoutineRunThread, isSameIconConfig, isSameRoutineFormDraft, isVideoFileName, isVideoUpload, labelForSearchResultBucket, leaveProject, markTeamChatRead, mintShareLink, nextBackoff, normalizeSearchQuery, parseActionCardsChangedPayload, parseGeneratedRoutineSuggestions, parseIconConfig, parseMessageParts, parseNotes, parseRoutineLibraryResponse, postToTeamChat, publishedSitesStore, rankSearchItems, readDeviceId, rebaseRoutineFormDraft, redeemShareLink, removeMember, replaceTranscriptSuffix, requestTeamChatDraft, resolveAutomaticMeetingPrepEnabled, resolveEmailAttributionEnabled, resolveFileLinkMeta, resolveMeetingAssistEnabled, resolveMeetingIntelligenceEnabled, resolvePageLimit, resolveStartOffset, revokeShareLink, routineFormDraftsEqual, selectFeedItems, shouldShowLimitWarning, startRunRecord, streamEntityPages, suggestEntityIconSlug, suggestIconForTitle, tokenizeForSearchIndex, tokenizeSearchQuery, tokenizeWords, typePriority, updateMemberRole, uploadLocalTranscript, useSDK, useSDKState, useStores, useUserConfig, withAskUserFreeText, withAskUserPick, withMeetingNoteSourceEnabled };

//# sourceMappingURL=index.js.map